/**
 * Manager Worker — forked child process that polls the API for agent config
 * changes and local file drift. Communicates with the watchdog parent via IPC.
 *
 * This file is a standalone entry point built by tsup so `fork()` can target it.
 */

import { createHash } from 'node:crypto';
import { readFileSync, writeFileSync, mkdirSync, existsSync, rmSync, readdirSync, statSync, unlinkSync, copyFileSync, renameSync, utimesSync } from 'node:fs';
// ENG-9128 Part A: replace bundles atomically so no agent can read one
// half-written. `copyFileSync` truncates-then-writes into a directory that is
// bind-mounted into every container on the host.
import { atomicCopyFileSync } from './atomic-file-replace.js';
import { execFileSync as syncExecFile } from 'node:child_process';
import { join, dirname, delimiter as pathDelimiter } from 'node:path';
import { homedir, platform } from 'node:os';
import { fileURLToPath } from 'node:url';
import {
  extractFrontmatter,
  resolveChannels,
  getFramework,
  DEFAULT_FRAMEWORK,
  parseDeliveryTarget,
  isParseError,
  resolveDmTarget,
  isResolveError,
  appendDmFooter,
  wrapScheduledTaskPrompt,
  classifyOutput,
  SUPPRESS_SENTINEL,
  type PriorRun,
  type FrameworkAdapter,
  type CharterFrontmatter,
  type ToolsFrontmatter,
  type ChannelId,
  type ChannelPolicy,
  type OrgChannelPolicy,
  type DeploymentTarget,
  type ProvisionInput,
  type DeliveryTarget,
  type ResolverAgent,
  type ResolverPerson,
  KANBAN_CHECK_COMMAND,
  KANBAN_NUDGE_ACTIONABLE_STATUSES,
  formatRunMarker,
  classifyActor,
  resolveAvatarEnvUrl,
  MAX_AVATAR_ENV_URL_BYTES,
  FLAGS_SCHEMA_VERSION,
  type FlagValue,
  coerceOnboardingState,
  isOnboardingArea,
  laneTagFragment,
  resolveAgentTimezone,
  isAgentTimezoneOverrideExpired,
  isUnsetTimezone,
  isControlPlaneOnlyEnvVar,
  senderPolicyIsEnforcedOn,
} from '@augmented/core';
import { provision } from '@augmented/core/provisioning/provisioner.js';
import { extractCommandNotFound } from '@augmented/core/provisioning/hook-env.js';
// ENG-9802: which telegram env changes justify respawning a live session, and
// how to canonicalise them for hashing. Classified in core beside the adapter
// that emits the env so a new key cannot be silently inert.
import {
  telegramPolicyEnvFromInputs,
  telegramPolicyEnvBasis,
  isTelegramPolicyRestrictive,
} from '@augmented/core/provisioning/channel-policy-env.js';
// ENG-9861: the rules restart basis lives with the renderer that produces the
// section it hashes, so the two can never drift apart.
import {
  agentRulesRestartBasis,
  agentHasRules,
} from '@augmented/core/provisioning/frameworks/claudecode/agent-rules.js';
import { getIntegration } from '@augmented/core/integrations/registry.js';
import type { ConnectivityTestOverride, ToolkitSourceType } from '@augmented/core/integrations';
import type {
  IntegrationAuthType,
  IntegrationCredentials,
  IntegrationScope,
  RemoteMcpSpec,
} from '@augmented/core';
import { createSingleFlight, type SingleFlightRunResult } from './single-flight.js';
import {
  createPollRateLimiter,
  DEFAULT_POLL_INTERVAL_MS,
  type PollRateLimiter,
} from './poll-rate-limiter.js';
import {
  claudeCodeUpgradeMarkerPath,
  stampClaudeCodeUpgradeMarker,
  claudeCodeUpgradeThrottled,
} from './claude-code-upgrade-throttle.js';
import { reapOrphanChannelMcps, parsePsRows } from './orphan-channel-mcp-reaper.js';
import { agentRuntimeKey } from './agent-runtime-key.js';
import { flushAgentTranscriptsOnDrain, drainTranscriptFlushDeps } from './drain-transcript-flush.js';
import { decideMcpDriftAction, decideMcpRestartOnDrift, managedMcpStructureHash, managedMcpStructureHashFromFile, decideManagedMcpRestart, channelSecretValueHash } from './mcp-config-drift.js';
import {
  decidePostRestartVerification,
  shouldRemediateUnverifiedRestart,
  shouldDeferBindRemediationForFirstConnect,
  computeFirstConnectUnsettled,
  type PendingRestartVerification,
} from './mcp-restart-verify.js';
import { CHANNEL_SECRET_ENV_KEYS } from '@augmented/core/provisioning/env-integrations-file.js';
import { collectChannelSpawnSecrets } from '@augmented/core/provisioning/channel-env.js';
// ENG-9840: read the writer's stated cause instead of guessing at one.
import { describeChannelWriteFailure } from '@augmented/core/provisioning/channel-write-outcome.js';
import { computeCapabilitySurfaceHash, computeIntegrationsHash } from './integration-hash.js';
import { withResolvedRemoteMcp } from './host-integration-mapping.js';
import {
  classifyEnvIntegrationsDiff,
  diffEnvIntegrations,
  envOnlyRespawnVars,
  findMcpServersUsingVars,
  liveProxyExtraHeaderVars,
  liveProxyTokenVars,
  reapStaleMcpChildren,
} from './stale-mcp-reaper.js';
import { AnchorSessionLifecycle } from './anchor-session-lifecycle.js';
// ENG-5053: presence check for declared MCP children — restarts the
// session when a declared mcpServers entry has no live child under the
// agent's claude (e.g. xero-mcp-server crashed and Claude Code's
// transport didn't respawn it). See lib/mcp-presence-reaper.ts.
import {
  reapMissingMcpSessions,
  clearPresenceReaperState,
  clearPresenceReaperStateForKeys,
  givenUpMcpServerKeys,
  findMissingMcpServers,
} from './mcp-presence-reaper.js';
// ENG-10134: tunnel liveness for the open-computer-use stdio MCP. Corrects the
// reaper's INPUT rather than adding a second recovery mechanism — see the
// module header for why the reaper alone cannot identify this server (ENG-10258)
// and why a sleeping Mac would otherwise give up on it permanently.
import {
  COMPUTER_USE_MCP_KEY,
  TunnelWatcher,
  computerUseReaperView,
} from './computer-use-tunnel.js';
import { decideChannelRestart, launchableChannelIds } from './channel-restart-decision.js';
import { resolveChannelLaunchFlags } from './channel-launch-flags.js';
import {
  createEffectiveIntegrationObservations,
  forgetEffectiveIntegrations,
  recordEffectiveIntegrationIds,
  type EffectiveIntegrationObservations,
} from './channel-sync-precondition.js';
import { decideSenderPolicyRestart } from './sender-policy-restart-decision.js';
import { computeSenderPolicyRestartHash, isSenderPolicyRestrictive } from './sender-policy-normalize.js';
import {
  decideMsTeamsBehaviourRestart,
  extractMsTeamsBehaviourSubset,
  isMsTeamsBehaviourRestrictive,
} from './msteams-behaviour-restart.js';
import {
  decideSlackBehaviourRestart,
  extractSlackBehaviourSubset,
  isSlackBehaviourRestrictive,
} from './slack-behaviour-restart.js';
// ENG-6533 (folds ENG-6527): drive a mid-flow self-onboarding agent by injecting
// a one-shot "run onboarding_get and proceed" directive into its live session.
import {
  decideOnboardingDrive,
  buildOnboardingDirective,
  onboardingNudgeReachedSession,
  readOnboardingDriveMarker,
  writeOnboardingDriveMarker,
  clearOnboardingDriveMarker,
} from './onboarding-drive.js';
// ENG-5932 (Reaper Slice 1): criticality classification + local persistent
// channel-quarantine store. Quarantine a permanently-dead OPTIONAL channel
// (e.g. an orphaned msteams config) instead of restarting the whole session
// for it forever — without ever touching ESSENTIAL control-plane channels.
import {
  AUTO_RESUME_MAX_ATTEMPTS,
  resumeHistoryKey,
  shouldForgetResumeHistory,
  ChannelResumeHistoryStore,
  decideChannelAutoResume,
  defaultResumeHistoryPath,
  resolveAutoResumeEffectiveMode,
} from './channel-auto-resume.js';
import {
  ChannelQuarantineStore,
  classifyChannelCriticality,
  defaultQuarantinePath,
  isKnownChannelKey,
} from './channel-quarantine.js';
import {
  measureClaudeMd,
  logClaudeMdCeilingTransition,
  type ClaudeMdSizeReport,
} from './claude-md-size.js';
import {
  readForwardedToolsReport,
  type ForwardedToolsReport,
} from '@augmented/core/integrations/forwarded-tools-report.js';
import {
  RestartBreaker,
  reaperRestartBreakerReason,
  isProvisioningReloadReason,
  isIntegrationBucketedReason,
  readEnvNumber as readBreakerEnvNumber,
  tripClass,
  type RestartReason,
  type TripState,
} from './restart-breaker.js';
import {
  McpFlapDampener,
  membershipSignature,
  readEnvNumber as readFlapEnvNumber,
  DEFAULT_FLAP_BREAKER_COOLDOWN_MS,
  FLAP_CHANNEL_INTEGRATIONS,
  FLAP_CHANNEL_MANAGED_MCP,
  isManagedMcpServerKey,
  orphanedRemoteMcpKeys,
  expectedRemoteMcpKeys,
} from './mcp-flap-dampener.js';
import {
  AUTO_RESUME_FLAG_KEY,
  decideAutoResume,
  hydrateAutoResumeMarkers,
  readAutoResumeConfig,
  type AutoResumeRecord,
} from './auto-resume.js';
// ENG-7577: level-triggered reconcile of a held trip against the authoritative
// agent status, so a console Resume always reaches clear() — even across the
// manager restart that empties the edge-detector's knownStatuses map.
import { decideStaleTripClear, readStaleTripSettleMs } from './breaker-stale-trip.js';
import { queueSuppressionReport, takeSuppressionReports } from './pending-suppression-reports.js';
// ENG-6383 (ENG-6375 Slice C): health-gated auto-resume. Replaces the blind
// ENG-6088 quiet-timer with decideResumeReconcile when AGT_RESUME_RECONCILER_ENABLED
// is on (the blind path stays the flag-off fallback). The pure decision +
// input-assembly helpers live in their own modules so this hot-file edit is a
// thin call into tested code.
import {
  decideResumeReconcile,
  readResumeReconcilerConfig,
} from './resume-reconciler.js';
import {
  deriveMcpPresent,
  parseResumeHealthResponse,
} from './resume-reconciler-inputs.js';
import { shouldRespawnForModelChange, decideOpencodeModelRestart } from './model-change-respawn.js';
import { restartTargetsOpencodeServe } from './restart-stop-strategy.js';
import {
  stopAgentRuntime as stopAgentRuntimeWithDeps,
  type AgentRuntimeTeardownDeps,
} from './agent-runtime-teardown.js';
import {
  opencodeCredentialFingerprint,
  shouldRestartForCredentialChange,
} from './opencode-credential-fingerprint.js';
import { toOpencodeModel, mergeOpencodeConfigArtifact } from '@augmented/core/provisioning/frameworks/opencode/index.js';
// ENG-5641: host-side connectivity probing.
import { runConnectivityProbes } from './connectivity-probe-runner.js';
// ENG-8363: the first-connect window is owned beside the probe's verdict rule so
// the probe and bind-remediation cannot disagree about when a row has settled.
import { FIRST_CONNECT_WINDOW_MS, markQuarantinedForProbe } from './mcp-config-lookup.js';
// ENG-6392: per-agent down→ok diff feeding the auto-resume close-the-loop send.
import { DependencyRecoveryLedger } from './dependency-recovery-ledger.js';
import { parseEnvIntegrations } from './mcp-env-probe.js';
// ENG-9125: pre-spawn readiness gate for the host-shared `_mcp` bundle mount.
import { findMissingMcpBundles, formatMissingMcpBundles } from './mcp-assets-ready.js';
// ENG-9129: startup observability — a stalled boot must not be silent.
import { describeStartupCompletion } from './startup-telemetry.js';
import { safeWriteJsonAtomic } from '@augmented/core/provisioning/mcp-config-guards.js';
import {
  decideSelfUpdateCoalesce,
  formatCoalesceDeferLogLine,
  isAgtVersionSmokeOk,
  readLastSelfUpdateAppliedMs,
  resolveCoalesceWindowMs,
  stampLastSelfUpdateApplied,
} from './self-update-coalesce.js';
import {
  decideMaintenanceWindowGate,
  isUrgentUpgrade,
  decideRestartGate,
  isGateableRestartReason,
  isWindowExemptRestartReason,
  shouldConsumeForcedUpdate,
  decidePendingForcedUpdate,
  FORCED_UPDATE_HARD_DEADLINE_MS,
  RESTART_WINDOW_DEFER_ESCALATE_AFTER,
  nextWindowDeferStreak,
  type SelfUpdateOutcome,
} from './update-window-gate.js';
import { formatWindowDeferLogLine } from './maintenance-window.js';
import { decidePin, pinAllowsUrgent, resolveEffectivePinRaw } from './self-update-pin.js';
import { atomicWriteFileSync } from './atomic-write.js';
import { HostFlagStore, defaultFlagsCachePath } from './feature-flags-host.js';
import {
  classifyModelApiError,
  reportModelApiError,
  scrapeAgentRuntimeModelApiError,
  type ModelApiErrorSource,
} from './model-api-error.js';
import {
  addSpawn as addClaudePidSpawn,
  decideReaperActions as decideClaudeReaperActions,
  formatDrainShutdownLine as formatDrainLine,
  formatReaperBootLine as formatReaperLine,
  readPidFile as readClaudePidFile,
  removeSpawn as removeClaudePidSpawn,
  writePidFile as writeClaudePidFile,
} from './claude-pid-tracker.js';
import {
  decideDrainReleaseTargets,
  drainReleaseScanned,
  tallyDrainReleases,
} from './drain-card-release.js';
import { killSessionsConcurrently } from './drain-session-kill.js';
import {
  directChatMaxAgeMs,
  isDirectChatMessageExpired,
  directChatDoorbellPath,
} from './direct-chat-delivery.js';
import {
  buildProbeEnv,
  buildConnectivityProbeDeps,
  deriveMcpServerKey,
} from './connectivity-probe-context.js';
import { runSessionToolBindProbes } from './session-tool-bind-runner.js';
import { provisionReviewPoster } from './review-poster-asset.js';
import { gatherSessionToolBindProbe } from './session-tool-bind-probe-host.js';

// ENG-5794: sentinels around the side-effect-managed Integrations section
// in CLAUDE.md. See `buildIntegrationsSection` for the producer side and
// the diff strip below at the call site for the consumer rationale.
import {
  provisionStopHook,
  provisionIsolationHook,
  provisionOrientHook,
  provisionAutoKanbanProgressHook,
  provisionChannelProgressHook,
  provisionSessionStateHook,
  provisionPreCompactHook,
  estimateActiveTasksTokens,
  INTEGRATIONS_SECTION_START,
  INTEGRATIONS_SECTION_END,
  reconstructQuarantinedMcpServerEntry,
} from '@augmented/core/provisioning/frameworks/claudecode/index.js';
// ENG-7891 / ADR-0049 slice 3: the dark id-keyed migration engine (per-agent,
// flag-gated, ships dark). Invoked at the pre-spawn funnel below.
import { maybeMigrateAgentToIdKeyedLayout } from './id-keyed-migration.js';

/**
 * Escape a literal string for use inside a `RegExp` — the sentinel
 * constants contain `<`/`-` etc. that aren't regex-special today, but
 * pinning the escape makes the diff strip robust if the sentinel shape
 * ever changes.
 */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

import { api, getHostId, exchangeApiKey, forceNextExchange, mintAgentKey, getCachedClaudeAuthMode, ApiError, setConfigHash } from './api-client.js';
// ENG-8260: classify an auth key-exchange failure (transient platform fault vs
// a verdict on the credential) and decide what it means for a running session.
import { exchangeFailureKind } from './auth-exchange-error.js';
import { decideAuthResolveFailureAction } from './manager/auth-resolve-decision.js';
import { collectEnvGates } from '@augmented/core/host-config';
// ENG-8346: the same cursor-advance verdict the direct-chat channel MCP uses.
// Shared via @augmented/core precisely because this file cannot import from
// packages/mcp — the ADR-0047 opencode arm below is the ninth cursor-advancing
// call site and the only one outside that package.
import {
  classifyCursorAdvance,
  formatCursorAdvanceShortfall,
  type CursorAdvanceResponseBody,
} from '@augmented/core';
import { recordCursorAdvanceOutcome } from '@augmented/core/direct-chat/cursor-advance-telemetry.js';
import {
  ArtifactStreamScanner,
  nodeArtifactFs,
  artifactsDirFor,
} from './artifact-stream.js';
import { maybeReportUsageBanner } from './usage-banner-monitor.js';
// ENG-9394: the HOST-level usage poller that supersedes the per-agent banner
// scrape above as an alert source. See host-usage-poller.ts for why both exist
// during the transition.
import {
  maybePollHostUsage,
  HOST_USAGE_POLL_TIMEOUT_MS,
  USAGE_COMMAND_ARGS,
  type UsageCommandResult,
} from './host-usage-poller.js';
// ENG-8186: which claude.ai account the host is signed in as, so a re-login
// (the operator's fix for a usage limit) trips the auth-tuple restart.
import { getClaudeAccountFingerprint } from './claude-account-fingerprint.js';
import { diffAuthTuples, type AuthTupleDiff } from './auth-tuple-diff.js';
import { syncAccountEnforcementMarker } from './account-enforcement-marker.js';
import { maybeReportTokenUsage } from './token-usage-monitor.js';
import { maybeReconcileWorkflowRunTokens } from './workflow-run-reconciler.js';
import {
  maybeEvaluateConversations,
  meteredScorerSuppressed,
  selectEvalBackend,
  DEFAULT_CLAUDE_EVAL_MODEL,
  DEFAULT_LOCAL_EVAL_URL,
  DEFAULT_LOCAL_EVAL_MODEL,
  type ConversationEvalBackend,
} from './conversation-evaluator.js';
import { maybeExtractMemories } from './memory-extractor.js';
import { maybeScanToolCalls } from './tool-call-audit.js';
import { maybeReportActivityCache } from './activity-cache-monitor.js';
import { classifyPollError, currentPollBackoffMs } from './poll-backoff.js';
import {
  currentIdlePollBackoffMs,
  resolveIdlePollBackoffCapMs,
} from './idle-poll-backoff.js';
import { getApiKey, requireHost } from './config.js';
import { resolvePerAgentApiKey } from './per-agent-key.js';
import { sanitizeMcpJson } from './mcp-sanitize.js';
import { renderIntegrationSkillContent } from './integration-context-render.js';
import {
  buildIntegrationBundle,
  bundleFingerprint,
  groupSkillsByIntegration,
  planIntegrationSkillSweep,
  resolveIntegrationSkillLane,
  type IntegrationSkillInput,
} from './integration-skill-layout.js';
import { detectClaudeAuth, findClaudeCredentialsPaths } from './claude-auth-detect.js';
import { deviceStateHoldApplies } from './self-update-device-state-hold.js';
import { canonicalJson } from './canonical-json.js';
import { computeChannelConfigHash } from './channel-config-hash.js';
import {
  resolveKnowledgeFromManifest,
  type KnowledgeManifestEntry,
} from './knowledge-cache.js';
import {
  resolveMemoriesFromManifest,
  type MemoryContentManifestEntry,
} from './memory-cache.js';
import {
  resolveIntegrationSkillsFromManifest,
  resolveSkillsFromManifest,
  type IntegrationSkillManifestEntry,
  type SkillManifestEntry,
} from './skill-cache.js';
import { createTickBoardFetcher } from './kanban-board-fetch.js';
import { BoardTokenCache, type BoardResponse } from './kanban-board-token.js';
import { REFRESH_MANIFEST_FLAGS } from './refresh-manifest-flags.js';
import { createFetchGate } from './fetch-cadence.js';
import { resolveConditionalResponse, stillHeld } from './conditional-fetch.js';
import {
  loadChannelHashCache as loadChannelHashCacheFromDisk,
  saveChannelHashCache as saveChannelHashCacheToDisk,
} from './channel-hash-cache.js';
import {
  createDeliveryBaselineMaps,
  loadSenderPolicyBaseline as loadSenderPolicyBaselineFromDisk,
  saveSenderPolicyBaseline as saveSenderPolicyBaselineToDisk,
  type BaselineConcern,
} from './sender-policy-baseline.js';
import {
  createStuckStreakMaps,
  loadStuckStreaks as loadStuckStreaksFromDisk,
  pruneStuckStreaksToAgents,
  saveStuckStreaks as saveStuckStreaksToDisk,
  type StuckStreakMaps,
} from './stuck-streak-store.js';
import { DeviceStateRotationGate } from './claude-device-state.js';
import { SUPERVISOR_RESTART_EXIT_CODE } from '../commands/manager.js';
import { sweepChannelProcesses, killAgentChannelProcesses } from './channel-sweep.js';
import {
  checkChannelInputs,
  creditWatchdogGiveUpCount,
  creditWatchdogUnclaimedDialogCount,
  takeWatchdogGiveUpCount,
  takeWatchdogUnclaimedDialogCount,
} from './channel-input-watchdog.js';
import { hintProbability, pickHintVariant, shouldIncludeHint } from './delivery-hint.js';
import { withScheduleLinkFooter, getConsoleUrl } from './delivery-schedule-link.js';
// ADR-0011 PR-A: shared per-agent state, migrated out of module scope so
// the manager subsystems can lift in the stacked follow-up PRs without
// having to import ambient `const Map` references from this file.
import { pruneConfirmCycles } from './manager/managed-prune-streak.js';
import { agentState, clearAgentState } from './manager/agent-state.js';
import { planGlobalSkillSync } from './manager/global-skills.js';
import {
  rememberIntegrationSkill,
  removeIntegrationSkillFolder,
  shouldWriteIntegrationSkill,
} from './manager/integration-skill-cache.js';
import {
  managedSkillManifestPath,
  readManagedSkillManifest,
  writeManagedSkillManifest,
} from './manager/managed-skill-manifest.js';
import { pollCycleAndReschedule } from './poll-loop-guard.js';
import {
  log,
  redactForDiskLog,
  sha256,
  hashFile,
  execFilePromise,
  execFilePromiseLong,
  ChildProcessError,
} from './manager/runtime.js';
import {
  noteRestartRequested,
  beginRestartTiming,
  markRestartStopped,
  finishRestartTiming,
  clearRestartTiming,
} from './manager/restart-timing.js';
import { resolveModelChain } from './manager/model.js';
import { applyClaudeAuthToEnv } from './manager/claude-auth.js';
import {
  RUNTIME_AUTH_PROBE_PROMPT,
  classifyRuntimeAuthProbe,
  foldRuntimeAuthVerdict,
  heartbeatRuntimeAuthFields,
  type RuntimeAuthProbeResult,
} from './claude-runtime-auth-probe.js';
import {
  STANDUP_TEMPLATES,
  TASK_UPDATE_TEMPLATES,
  PLAN_TEMPLATES,
  KANBAN_WORK_TEMPLATES,
  BOARD_INJECT_TEMPLATES,
  isKanbanHybridEnabled,
  isKanbanHybridDryRun,
  isHybridActionable,
  parseStandupSummary,
  parsePlanItems,
  parseKanbanUpdates,
  sanitizeBoardItem,
  formatBoardForPrompt,
  kanbanActionableSignature,
  shouldNudgeUnchangedBoard,
  shouldAttemptNoticeEnqueue,
  KANBAN_NOTICE_BREAKER_THRESHOLD,
  resolveHybridNudgeAction,
} from './manager/kanban/parsers.js';
import type { BoardItem, KanbanNudgeBackoffState } from './manager/kanban/parsers.js';
import { loadKanbanNudgeState, saveKanbanNudgeState } from './manager/kanban/nudge-state-cache.js';
import { enqueueKanbanNotice, cancelKanbanNotice } from './manager/kanban/notify.js';
import {
  agentChannelTokens,
  getAlertSlackWebhook,
  setAlertSlackWebhook,
} from './manager/channels/state.js';
import { sendSlackWebhookMessage } from './manager/channels/slack.js';
import { telegramApiCall } from './manager/channels/telegram.js';
import { cacheAgentDeliveryMetadata } from './manager/delivery/state.js';
import {
  reportDeliveryStatus,
  buildDoneCardNotification,
  sendTaskNotification,
  resolveRequesterDmTarget,
  buildWaitingHumanNotification,
} from './manager/delivery/notifications.js';
import { deliverScheduledTaskOutput } from './manager/delivery/scheduled-output.js';
import {
  buildSchedulerTaskInput,
  startRun,
  finishRun,
  syncAndCheckClaudeScheduler,
  fireScheduledTaskViaKanban,
  reconcileScheduledRuns,
  deliverScheduledCardResult,
  isScheduledCardTracked,
  closeScheduledRunsForCode,
  closeSessionRunForCode,
  sessionRunsByCode,
  trackSessionRun,
  registerClaudeSpawn,
  unregisterClaudeSpawn,
  claudePidFilePath,
  claudeSchedulerStates,
  inFlightClaudeTasks,
  claudeTaskConcurrency,
  MAX_CLAUDE_CONCURRENCY,
  scheduledRunsByCode,
  inFlightClaudePids,
} from './manager/scheduler.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WorkerConfig {
  intervalMs: number;
  configDir: string;
}

interface AcpSessionSummary {
  sessionId: string;
  agentCommand: string;
  sessionName?: string;
  queueState: string;
  turnCount: number;
  startedAt: string;
}

export interface AgentState {
  agentId: string;
  codeName: string;
  status: string;
  charterVersion: string;
  toolsVersion: string;
  secretsHash: string | null;
  lastRefreshAt: string | null;
  lastProvisionAt: string | null;
  lastDriftCheckAt: string | null;
  lastSecretsProvisionAt: string | null;
  // ENG-6932: gateway is OpenClaw/NemoClaw-only and those frameworks are
  // removed; Claude Code has no gateway process so these are never set by the
  // manager. Kept optional (not removed) because sibling CLI surfaces
  // (commands/manager.ts, commands/manager-watch.tsx, lib/watchdog.ts) and the
  // persisted manager-state.json still read them; they render as "no gateway".
  gatewayPort?: number | null;
  gatewayPid?: number | null;
  gatewayRunning?: boolean;
  acpSessions: AcpSessionSummary[];
  // ENG-4947: latest `restart_requested_at` (from /host/agents) that this
  // manager has already acted on. Persists across manager restarts so a
  // dashboard-issued restart fires exactly once even if we crash between
  // poll and respawn.
  lastRestartProcessedAt?: string | null;
}

interface ManagerState {
  pid: number;
  startedAt: string;
  lastPollAt: string | null;
  pollCount: number;
  errorCount: number;
  agents: AgentState[];
  // ENG-5441: serialised circuit-breaker trips keyed by code_name. Only
  // tripped breakers are persisted — the in-window event log is per-
  // worker and rebuilds from scratch on manager restart. A previously-
  // tripped agent stays tripped across a manager restart so an operator
  // can rely on the trip surviving a `systemctl restart agt-manager`.
  circuitBreakerTrips?: Record<string, TripState>;
  // ENG-6088: spent auto-resume credits keyed by code_name. Persisted so
  // a manager restart can't hand a re-tripped agent a fresh credit (the
  // exact loophole that would turn "resume once" into a resume loop).
  circuitBreakerAutoResumes?: Record<string, AutoResumeRecord>;
  // ENG-6692: the last hosts.update_requested_at this manager has CONSUMED (ran
  // its one-shot "Update CLI now" self-update for). Host-level twin of
  // AgentState.lastRestartProcessedAt — persisted so the forced update fires
  // exactly once per distinct timestamp even across a manager restart (the
  // self-update itself restarts the manager, so this MUST survive it). Echoed
  // back on the heartbeat as update_request_processed_at so the API clears the
  // column.
  lastUpdateRequestProcessedAt?: string | null;
}

/**
 * ENG-5008: stamp `lastRestartProcessedAt` on the fresh AgentState rows
 * built this cycle, carrying forward any prior ack from `state.agents`
 * when no new restart signal arrived this poll.
 *
 * Pre-ENG-5008 only `restartAcks` (fresh signals) populated the field, so
 * every cycle where a previously-acked agent had no new signal dropped
 * the ack — and the very next cycle saw `prev?.lastRestartProcessedAt =
 * null`, the timestamp comparison failed, and the manager re-fired the
 * kill. That's the loop that pinned vigil/vera/stirling for hours on
 * 2026-05-12.
 *
 * Exported for unit testing — caller wires it into the poll loop after
 * `processAgent` has filled `agentStates`.
 */
export function applyRestartAcks(args: {
  agentStates: AgentState[];
  priorAgents: AgentState[];
  restartAcks: Map<string, string>;
}): boolean {
  const { agentStates, priorAgents, restartAcks } = args;
  if (agentStates.length === 0) return false;

  const priorAcks = new Map<string, string>();
  for (const prev of priorAgents) {
    if (prev.lastRestartProcessedAt) {
      priorAcks.set(prev.agentId, prev.lastRestartProcessedAt);
    }
  }
  // CodeRabbit (PR #953): return whether anything actually changed in
  // this cycle's agentStates rows so the caller can force a crash-safe
  // immediate write on EITHER a fresh ack OR a carry-forward repair.
  // Pre-fix, the immediate write was keyed off `restartAcks.size > 0`,
  // which missed the case where a prior write had failed silently and
  // this cycle is restoring the ack only via carry-forward.
  let changed = false;
  for (const ack of agentStates) {
    const freshAck = restartAcks.get(ack.agentId);
    const carryForward = priorAcks.get(ack.agentId);
    // Fresh ack from this cycle wins (caller only adds to restartAcks
    // when the requested timestamp is strictly newer than any prior).
    // When no fresh ack arrived, preserve the prior ack so the
    // already-acked restart doesn't re-fire next poll.
    if (freshAck) {
      if (ack.lastRestartProcessedAt !== freshAck) changed = true;
      ack.lastRestartProcessedAt = freshAck;
    } else if (carryForward) {
      if (ack.lastRestartProcessedAt !== carryForward) changed = true;
      ack.lastRestartProcessedAt = carryForward;
    }
  }
  return changed;
}

// Event types (formerly IPC messages, now just internal events)
type ManagerEvent =
  | { type: 'ready' }
  | { type: 'state-update'; state: ManagerState }
  | { type: 'provisioned'; agentId: string; codeName: string }
  | { type: 'drift-detected'; agentId: string; codeName: string; files: string[] }
  | { type: 'error'; message: string }
  | { type: 'shutdown' };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Channel-process zombie sweep (ENG-4453). Throttled to avoid shelling out
// to `ps eww` every poll. Default 5 min, overridable via env for tuning or
// stress tests. AGT_CHANNEL_SWEEP_DRY_RUN=1 logs kill targets without sending
// SIGTERM — operators should flip this on first and verify the targets before
// letting the sweep act.
const CHANNEL_SWEEP_INTERVAL_MS = (() => {
  const raw = parseInt(process.env['AGT_CHANNEL_SWEEP_INTERVAL_MS'] ?? '', 10);
  if (!Number.isFinite(raw)) return 5 * 60 * 1000;
  // Clamp overrides up to the 30s floor rather than falling back to default
  // — an operator who sets =10000 clearly intends a short interval, not the
  // five-minute silent fallback.
  return Math.max(raw, 30_000);
})();
const CHANNEL_SWEEP_DRY_RUN = process.env['AGT_CHANNEL_SWEEP_DRY_RUN'] === '1';
let lastChannelSweepAt = 0;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let config: WorkerConfig | null = null;
let running = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

// ENG-7273: the fast, independent liveness heartbeat timer. The poll cycle
// stamps liveness only once per iteration, so a busy host's long cycle (or the
// multi-minute re-provision after a restart) can starve last_seen_at past the
// webapp's 2-minute offline threshold and false-flag every agent offline. This
// setInterval posts a minimal /host/heartbeat on its own ~30s cadence, fully
// decoupled from the heavy cycle. `livenessInFlight` guards against a slow POST
// stacking on the next tick.
let livenessTimer: ReturnType<typeof setInterval> | null = null;
let livenessInFlight = false;
const LIVENESS_HEARTBEAT_INTERVAL_MS = 30_000;

// ENG-4659: when persistent-session reports a session unhealthy, the
// pane log tail is included in the operator-facing log line. Keep the
// inline preview short — operators reading the log don't need a full
// 20-line dump on every restart, just enough to identify the failure
// pattern. The full tail is still on disk at ~/.augmented/<codeName>/pane.log.
const PANE_TAIL_PREVIEW_LINES = 5;
/**
 * ENG-4923: extract `multi_agent.telegram_peers[]` from a CHARTER's raw
 * frontmatter so the manager can pass the resolved roster as the
 * TELEGRAM_PEERS env var on the per-agent MCP child. The shape on
 * disk is `{ code_name, bot_id }`; we add an empty `agent_id` field
 * because the classifier's parsePeersEnv requires it (downstream
 * runtime — ENG-4904 — can resolve agent_id from the API when
 * building the peer preamble).
 *
 * Lenient: any parse / shape error returns []. The CHARTER lint
 * rule from ENG-4901 already validates the shape at write time;
 * a runtime parse failure here just means the classifier sees no
 * declared peers, which falls through to its existing
 * `unknown_peer` drop path. Better than crashing the manager tick.
 */
/**
 * ENG-4935 / ENG-4929 §4.0: gate-path metadata for each CHARTER-declared
 * peer. The classifier in packages/mcp admits a peer message only when
 * the peer resolves AND `gate_path` is non-null; a null gate path
 * surfaces in telemetry as `cross_team_grant_missing` (cross-org pair
 * without a live grant, or an org in `consent_required` mode with a
 * peer that has neither same-team membership nor a grant).
 *
 *  - `same_team`               — peer's bot_id is on this team's roster.
 *  - `intra_org_unrestricted`  — peer is foreign-team, but the org's
 *                                cross_team_peer_intra_org toggle is
 *                                `unrestricted`, so no grant required.
 *  - `grant:<grant_id>`        — peer is foreign-team and a live grant
 *                                authorises it. Validated against the
 *                                inbound grants snapshot.
 *  - `null`                    — gate missing: cross-org without grant
 *                                or charter references a stale grant.
 *                                Charter still lists it (so the UI can
 *                                tell the user) but classifier drops.
 */
type PeerGatePath = 'same_team' | 'intra_org_unrestricted' | `grant:${string}` | null;

export interface CharterPeerWithGate {
  code_name: string;
  bot_id: number;
  agent_id: string;
  gate_path: PeerGatePath;
}

/**
 * ENG-4970 / ENG-4974: Slack mirror of CharterPeerWithGate. The runtime
 * Slack classifier (ENG-4936) expects entries keyed on `bot_user_id`
 * (Slack `U…` identity), so the manager extractor produces this shape.
 */
export interface CharterSlackPeerWithGate {
  code_name: string;
  bot_user_id: string;
  agent_id: string;
  gate_path: PeerGatePath;
}

export interface CharterPeerGateContext {
  /** Bot_ids of same-team agents (from /host/refresh team_peer_bot_ids). */
  teamPeerBotIds?: ReadonlyArray<number>;
  /**
   * ENG-4970 / ENG-4973: Slack `U…` user_ids of same-team agents (from
   * /host/refresh team_peer_slack_user_ids). Parallel to teamPeerBotIds.
   */
  teamPeerSlackUserIds?: ReadonlyArray<string>;
  /**
   * Inbound grants snapshot (from /host/refresh cross_team_grants.inbound).
   * `granted_agent_bot_id` is denormalised so we can match a grant by the
   * bot_id the CHARTER cites without a second lookup. ENG-4973 adds the
   * parallel `granted_agent_slack_user_id` so the Slack extractor can
   * match the same way.
   */
  crossTeamGrants?: ReadonlyArray<{
    grant_id: string;
    granted_agent_bot_id: number | null;
    /** ENG-4970 / ENG-4973: Slack `U…` user_id of granted_agent_id. */
    granted_agent_slack_user_id?: string | null;
    revoked_at: string | null;
    expires_at: string | null;
  }>;
  /** Org-level toggle from organization.cross_team_peer_intra_org. */
  crossTeamPeerIntraOrg?: 'unrestricted' | 'consent_required';
  /** Time source for expiry comparison. Defaults to new Date(). */
  now?: () => Date;
}

/**
 * ENG-4923 / ENG-4935: extract `multi_agent.telegram_peers[]` from a
 * CHARTER's raw frontmatter and resolve a gate_path per peer.
 *
 * Lenient parsing — any shape error returns []. The CHARTER lint rules
 * (ENG-4901 + ENG-4938) already validate at write time; a runtime parse
 * failure here just means the classifier sees no declared peers, which
 * falls through to its existing `unknown_peer` drop path.
 *
 * When `gateContext` is omitted (e.g. older /host/refresh response that
 * predates ENG-4935), every peer's gate_path is set to `same_team` —
 * which preserves the pre-ENG-4935 behaviour where every declared peer
 * was admitted unconditionally.
 */
export function extractCharterTelegramPeers(
  rawContent: string,
  gateContext?: CharterPeerGateContext,
): CharterPeerWithGate[] {
  if (!rawContent || rawContent.length === 0) return [];
  try {
    const parsed = extractFrontmatter(rawContent);
    const frontmatter = parsed.frontmatter as
      | { multi_agent?: { telegram_peers?: Array<{ code_name?: unknown; bot_id?: unknown; cross_team_grant_id?: unknown }> } }
      | null;
    const peers = frontmatter?.multi_agent?.telegram_peers;
    if (!peers || !Array.isArray(peers)) return [];
    const teamBotIds = new Set(gateContext?.teamPeerBotIds ?? []);
    const grants = gateContext?.crossTeamGrants ?? [];
    const intraOrg = gateContext?.crossTeamPeerIntraOrg ?? 'unrestricted';
    const now = (gateContext?.now ?? (() => new Date()))();
    const out: CharterPeerWithGate[] = [];
    for (const p of peers) {
      if (
        !p ||
        typeof p !== 'object' ||
        typeof p.code_name !== 'string' ||
        typeof p.bot_id !== 'number' ||
        !Number.isInteger(p.bot_id) ||
        p.bot_id <= 0
      ) {
        continue;
      }
      const grantId =
        typeof p.cross_team_grant_id === 'string' && p.cross_team_grant_id.length > 0
          ? p.cross_team_grant_id
          : null;
      let gate: PeerGatePath = null;

      if (gateContext === undefined) {
        // Backwards-compat: no gate context = pre-ENG-4935 manager; admit
        // every declared peer as same-team to preserve the old contract.
        gate = 'same_team';
      } else if (teamBotIds.has(p.bot_id)) {
        // CodeRabbit on PR #868: same-team wins over a CHARTER-cited
        // grant_id. Otherwise a stale cross_team_grant_id (left over from
        // when the peer was on another team) would downgrade a current
        // same-team peer to `null` on revoke/expiry. Team membership is
        // the strongest signal — trust it.
        gate = 'same_team';
      } else if (grantId) {
        // Charter cites a cross-team grant — must match a live grant in the
        // inbound snapshot, and the grant must authorise the bot_id the
        // charter declared. Lint covers this at write time; the runtime
        // re-checks because the grant may have been revoked since.
        const grant = grants.find((g) => g.grant_id === grantId);
        if (
          grant &&
          !grant.revoked_at &&
          (!grant.expires_at || new Date(grant.expires_at) > now) &&
          grant.granted_agent_bot_id === p.bot_id
        ) {
          gate = `grant:${grantId}` as const;
        } else {
          gate = null;
        }
      } else if (intraOrg === 'unrestricted') {
        // Foreign-team peer without a grant — admitted only because the
        // org is in unrestricted mode. Flipping the toggle to
        // consent_required will reduce this to `null` and the classifier
        // will start dropping with `cross_team_grant_missing`.
        gate = 'intra_org_unrestricted';
      } else {
        gate = null;
      }
      out.push({ code_name: p.code_name, bot_id: p.bot_id, agent_id: '', gate_path: gate });
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * ENG-4970 / ENG-4974: Slack parallel of `extractCharterTelegramPeers`.
 * Reads `multi_agent.slack_peers[]` from a CHARTER's frontmatter and
 * resolves a gate_path per peer using the same four-state model
 * (same_team / intra_org_unrestricted / grant:<id> / null).
 *
 * Slack-specific differences from the Telegram extractor:
 *   - Keyed on `bot_user_id` (string `U…`) instead of `bot_id` (integer)
 *   - Same-team detection uses `teamPeerSlackUserIds` from /host/refresh
 *   - Grant match compares `granted_agent_slack_user_id` (ENG-4973)
 *
 * Backwards-compat: when `gateContext` is omitted, every peer is admitted
 * as `same_team` — matches the Telegram extractor's pre-ENG-4935
 * behaviour for managers that haven't picked up the new refresh fields.
 */
export function extractCharterSlackPeers(
  rawContent: string,
  gateContext?: CharterPeerGateContext,
): CharterSlackPeerWithGate[] {
  if (!rawContent || rawContent.length === 0) return [];
  try {
    const parsed = extractFrontmatter(rawContent);
    const frontmatter = parsed.frontmatter as
      | {
          multi_agent?: {
            slack_peers?: Array<{
              code_name?: unknown;
              bot_user_id?: unknown;
              cross_team_grant_id?: unknown;
            }>;
          };
        }
      | null;
    const peers = frontmatter?.multi_agent?.slack_peers;
    if (!peers || !Array.isArray(peers)) return [];
    const teamUserIds = new Set(gateContext?.teamPeerSlackUserIds ?? []);
    const grants = gateContext?.crossTeamGrants ?? [];
    const intraOrg = gateContext?.crossTeamPeerIntraOrg ?? 'unrestricted';
    const now = (gateContext?.now ?? (() => new Date()))();
    const out: CharterSlackPeerWithGate[] = [];
    for (const p of peers) {
      if (
        !p ||
        typeof p !== 'object' ||
        typeof p.code_name !== 'string' ||
        typeof p.bot_user_id !== 'string' ||
        p.bot_user_id.length === 0
      ) {
        continue;
      }
      const grantId =
        typeof p.cross_team_grant_id === 'string' && p.cross_team_grant_id.length > 0
          ? p.cross_team_grant_id
          : null;
      let gate: PeerGatePath = null;

      if (gateContext === undefined) {
        gate = 'same_team';
      } else if (teamUserIds.has(p.bot_user_id)) {
        // Same-team wins over a stale CHARTER grant_id — same precedence
        // rule as the Telegram extractor (CodeRabbit on PR #868).
        gate = 'same_team';
      } else if (grantId) {
        const grant = grants.find((g) => g.grant_id === grantId);
        if (
          grant &&
          !grant.revoked_at &&
          (!grant.expires_at || new Date(grant.expires_at) > now) &&
          (grant.granted_agent_slack_user_id ?? null) === p.bot_user_id
        ) {
          gate = `grant:${grantId}` as const;
        } else {
          gate = null;
        }
      } else if (intraOrg === 'unrestricted') {
        gate = 'intra_org_unrestricted';
      } else {
        gate = null;
      }
      out.push({
        code_name: p.code_name,
        bot_user_id: p.bot_user_id,
        agent_id: '',
        gate_path: gate,
      });
    }
    return out;
  } catch {
    return [];
  }
}

function truncateForLog(s: string): string {
  const lines = s.split('\n').filter((l) => l.length > 0);
  return lines.slice(-PANE_TAIL_PREVIEW_LINES).map((l) => `    | ${l}`).join('\n');
}
// CodeRabbit (PR #618): only embed the raw pane tail in manager.log
// when the failure signature is one we recognise as a safe-to-quote
// Claude error string. For anything else (including 'unknown' tails)
// log only the sha256 prefix and point operators at the on-disk file.
// Add new signatures here only after auditing what Claude actually
// prints for that failure mode.
const KNOWN_SAFE_TAIL_SIGNATURES = new Set<string>(['session_id_in_use']);

/**
 * ENG-5090: gate the revoked-agent cleanup block in processAgent so it
 * only runs on the first tick after the status flips to `revoked`.
 * Extracted as an exported pure function so it has a regression-pin
 * test without dragging in the full processAgent surface (matches the
 * extract-and-test pattern in extractCharterTelegramPeers etc.).
 *
 * @param previousKnownStatus the value of `agentState.knownStatuses.get(agent.agent_id)`
 *   captured at the top of the current processAgent call — `undefined`
 *   means the manager has never seen this agent before this tick.
 * @returns true when the caller should skip the cleanup side effects
 *   (gateway stop, tmux kill, fs cleanup, cache purge) because they
 *   already ran on a prior tick.
 */
export function shouldSkipRevokedCleanup(previousKnownStatus: string | undefined): boolean {
  return previousKnownStatus === 'revoked';
}

/**
 * ENG-5090: residual check that runs after `shouldSkipRevokedCleanup`
 * says "yes, we already cleaned this up on a prior tick". A couple of
 * the cleanup ops in processAgent's revoked branch — `stopGatewayIfRunning`
 * (manager-worker.ts:2055) and `cleanupAgentFiles` (manager-worker.ts:8272)
 * — wrap their inner work in try/catch and log on failure rather than
 * rethrowing. That means a failure on the first revoked pass can still
 * reach `agentState.knownStatuses.set('revoked')` and trip the short-circuit on
 * subsequent ticks while real residuals (running gateway, allocated
 * port, surviving provision dir) remain.
 *
 * Treat the obvious residuals as a signal to fall back into the full
 * cleanup path. The check is cheap: an `isGatewayRunning` adapter call
 * (no-op `false` for adapters without a gateway, e.g. ClaudeCode), an
 * in-memory JSON read for the port file, and a single `existsSync` for
 * the provision dir.
 *
 * `gatewayRunning` is essential because the revoked branch calls
 * `freePort` and `cleanupAgentFiles` *after* `stopGatewayIfRunning`,
 * and those steps succeed even when the stop failed silently. Without
 * the live-gateway check the port and dir residuals are both wiped to
 * false on the next tick and the short-circuit would leave a zombie
 * gateway running forever — the exact bug CodeRabbit on PR #1054 caught.
 */
export function hasRevokedResiduals(state: {
  gatewayRunning: boolean;
  portAllocated: boolean;
  provisionDirExists: boolean;
}): boolean {
  return state.gatewayRunning || state.portAllocated || state.provisionDirExists;
}

// Track last-known versions + hashes per agent to detect changes.
// ADR-0011 PR-A: moved into `agentState` (./manager/agent-state.ts).

// ENG-4807 / CodeRabbit on PR #773: coalesce per-agent session-restart
// timers so two hot-reload triggers in the same tick (e.g. channel set
// changed AND MCP toolkit changed) don't schedule two stops back-to-back.
// Without this, the first stop kills the original session and the second
// stop fires a few seconds later — by which time the persistent-session
// healthcheck has already respawned, and the second stop kills the
// freshly-respawned session.
// ENG-6491: track the timer AND the reason it was scheduled with, so the
// coalescer can refuse to DOWNGRADE a window-exempt 'channel-set-change' restart
// to a window-gated 'hot-reload-mcp' one. A channel ADD also writes .mcp.json +
// channel secrets, so the same-poll drift watchers schedule a generic hot-reload
// restart that coalesces over the channel-set timer — without this, the exempt
// reason would be silently lost and the add would wait for the maintenance window
// after all (the exact coalescing seen in valai/gccm manager.log, ENG-6486).
interface PendingRestart {
  timer: ReturnType<typeof setTimeout>;
  reason: string;
  breakerReason: RestartReason;
  // ENG-6491: fire-time side effect (channel-add DM-notice marker write).
  beforeStop?: () => void;
  // Structured diagnosis for the audit row, kept on the pending entry so a
  // maintenance-deferred restart still knows what changed when it fires.
  auditDetail?: Record<string, unknown>;
}
const pendingSessionRestarts = new Map<string, PendingRestart>();

// ENG-8013 (storm-awareness): per-agent record of whether the session generation
// the manager most recently tore down + respawned was torn down by an EXTERNAL
// cause, anything but the mcp-presence-reaper's OWN recovery restart (gate reason
// 'mcp-presence-reaper'). The presence reaper reads this (as
// currentGenerationExternallyStarted) so a channel MCP that's missing purely as
// COLLATERAL of external respawn churn (integration-change / channel-set-change /
// day-rollover / wedge / operator restart, or a respawn storm) is NOT counted
// toward its quarantine budget: the durable fix for the ENG-8003 storm-quarantine
// incident (Sherlock's healthy Slack MCP quarantined by an integration-hash storm).
// Set only after an ACTUAL teardown proceeds (a maintenance-deferred restart never
// churned the generation, so it must not flip this). Absent → treated as false
// (first boot / no prior restart): count normally.
const lastRestartWasExternal = new Map<string, boolean>();

// ENG-10134: per-agent reverse-tunnel liveness for the open-computer-use stdio
// MCP. Module-scope so the cache (and the once-per-minute probe spacing) outlives
// any single poll; `log` and the recovery hook are passed per observation because
// `log` is function-scoped. Probing happens OUT OF BAND — `observe()` returns
// what is already known and never blocks the poll on a socket.
const computerUseTunnels = new TunnelWatcher();

// ENG-6174: per-agent record of a hot-reload-MCP restart whose respawn we are
// still trying to VERIFY. Populated when a hot-reload restart's stop fires (see
// scheduleSessionRestart) and consumed each poll by verifyPendingRestarts(),
// which confirms the session respawned healthy — or, if it never does, surfaces
// a bounded WARN→ERROR escalation instead of the pre-fix silent fire-and-forget.
// Keyed by code_name. Cleared on verification success, on escalation, and on any
// non-hot-reload teardown (cancelPendingSessionRestart).
const pendingRestartVerifications = new Map<string, PendingRestartVerification>();

// ENG-5441: per-manager circuit breaker for runaway session restart loops
// (today's Scout incident: day-rollover repeated every ~55s for hours).
// Trip state is mirrored into manager-state.json — see saveBreakerState /
// hydrateBreakerFromState below — so a manager restart can't accidentally
// clear a tripped breaker and revive the loop the operator stopped.
// ENG-7891 slice 5b: key the breaker's persisted trip state on the rename-stable
// runtime key (agent_id on an id-keyed host, code_name on the legacy fleet) so a
// codename rename doesn't strand a held trip and silently un-pause the very
// crash-looping agent the operator kept down. agentRuntimeKey is identity until
// the id-keyed layout is armed, so this is a no-op on today's fleet.
// ENG-8690: code_name → agent creation time (epoch ms), populated from the
// roster poll below. Module-level so it survives across polls within a worker
// generation, exactly like the breaker itself.
//
// NOT persisted to manager-state.json on purpose: it is re-populated by the very
// next poll, and a stale creation time is worse than an absent one - the breaker
// treats absent as "no grace" (armed), while a wrong value could hand a
// long-lived agent an exemption it should never have.
const agentCreatedAtMs = new Map<string, number>();

/**
 * ENG-8690: record an agent's creation time from a roster row.
 *
 * DELETES on a missing or unparseable value rather than returning early. The
 * first draft returned early, and CodeRabbit was right that it defeats the
 * fail-safe this whole feature rests on: the map survives across polls, so once
 * a timestamp was cached, a later roster row that no longer carries one (an API
 * rollback, a column made nullable, a bad row) would leave the STALE value in
 * place and keep granting grace. "Unknown age means governed, not exempt" has to
 * hold on the second poll too, not just the first.
 */
function noteAgentCreatedAt(codeName: string, createdAt: string | null | undefined): void {
  if (!createdAt) {
    agentCreatedAtMs.delete(codeName);
    return;
  }
  // Date.parse yields NaN on an unparseable value. Treated identically to
  // absent, so "we do not know" stays a single representable state.
  const ms = Date.parse(createdAt);
  if (Number.isFinite(ms)) agentCreatedAtMs.set(codeName, ms);
  else agentCreatedAtMs.delete(codeName);
}

const restartBreaker = new RestartBreaker({
  runtimeKey: (codeName) => agentRuntimeKey(codeName),
  agentCreatedAt: (codeName) => agentCreatedAtMs.get(codeName),
});
// ENG-8215 (CodeRabbit on PR #3854): if an operator's
// AGT_RESTART_BREAKER_PROVISIONING_MAX would have put the breaker's bar at or
// below the MCP quarantine threshold, the breaker raised it so quarantine still
// fires first. Say so once at boot - an override that is silently overruled is
// how an operator ends up debugging a threshold that isn't the one in their env
// file. Logged, not thrown: the ordering invariant matters more than honouring
// a tuning that would defeat it, but the operator still needs to see it.
{
  const clamp = restartBreaker.getQuarantineOrderingClamp();
  if (clamp) {
    log(
      `[circuit-breaker] provisioningMax=${clamp.requested} would trip before MCP quarantine ` +
        `(threshold=${clamp.quarantineThreshold}); raised to ${clamp.applied} so a failing integration ` +
        `is isolated before the whole agent is paused (ENG-8215)`,
    );
  }
}
// Tracks which trips we've already told the API about, so the same trip
// doesn't POST /host/circuit-breaker/trip on every poll.
const reportedTrips = new Map<string, number>();

// ENG-8679 (CodeRabbit, #4351): the cold-start queue for suppression reports
// lives in `./pending-suppression-reports.js`, where it can be unit-tested —
// see that module's header for why the report is deferred rather than dropped.

// ENG-7560: the provisioning-tally trip bar, mirrored here so
// recordRestartForBreaker can emit a single low-noise early-warning on the last
// provisioning-reload restart BEFORE the looser bar trips (count == max; the
// (max+1)th trips). A routine integration-add (3-4 restarts) stays below this
// and logs nothing; a churning integration prints one "about to auto-pause"
// line, complementing (a) the trip that still fires at this bar and (b) the
// McpConfigChurnRestartsHourly CloudWatch metric. Kept in sync with the
// breaker's own default (DEFAULT_PROVISIONING_MAX).
const RESTART_BREAKER_PROVISIONING_MAX = readBreakerEnvNumber('AGT_RESTART_BREAKER_PROVISIONING_MAX', 5);
// ENG-7812: freshness bound for the integration-membership attribution stash,
// kept in sync with the breaker's DEFAULT_PROVISIONING_WINDOW_MS (30 min). A
// provisioning restart within this window of a membership delta is attributed to
// the integration(s) that changed; older than this and it falls through to the
// shared unattributed bucket (so a stale onboarding add can't shield an unrelated
// reprovision fired much later).
const RESTART_BREAKER_PROVISIONING_WINDOW_MS = readBreakerEnvNumber(
  'AGT_RESTART_BREAKER_PROVISIONING_WINDOW_MS',
  1_800_000,
);

// ENG-6123: detects when an agent's managed desired-config set is OSCILLATING
// (flipping back and forth between membership states many times in a window)
// rather than changing once. While a channel flaps, the manager suppresses the
// integration-update / mcp-update notice injection and the session restart —
// the two per-flip token burns that took valai to ~1,550 opus sessions in 4
// days when two Composio integrations' `status` flapped active↔configured every
// ~60s. The `.mcp.json` itself still converges (file writes are cheap); only
// the opus-facing notice + reload are held until the set settles.
const mcpFlapDampener = new McpFlapDampener();

// ENG-7239: cool-off for the breaker exemption below. A 'managed-mcp-churn'
// restart fired while the agent's managed-MCP set is flapping, or within this
// window after it last flapped, restarts the session but does NOT count toward
// the ENG-5441 circuit breaker, so managed-MCP churn can't auto-pause the whole
// agent (Kylie/dig, 2026-06-29). Same posture as ENG-5547's single-dead-MCP
// reaper exemption.
const FLAP_BREAKER_COOLDOWN_MS = readFlapEnvNumber(
  'AGT_MCP_FLAP_BREAKER_COOLDOWN_MS',
  DEFAULT_FLAP_BREAKER_COOLDOWN_MS,
);

// ENG-6123: report a config-churn restart event to the API so the
// agent-heartbeat-monitor cron can aggregate per-agent counts into the
// `McpConfigChurnRestartsHourly` CloudWatch metric + `config-churn-flapping`
// alarm — the missing detection twin of the reaper-flapping alarm (which only
// counts DYING MCPs, never CHURNING membership, so valai ran 4 days unpaged).
// Fire-and-forget: a missed POST only under-counts the metric; suppression
// still works locally off the dampener's in-memory state.
function recordConfigChurnEvent(
  agentId: string,
  codeName: string,
  channel: string,
  signature: string,
): void {
  api.post('/host/mcp-config-churn', {
    agent_id: agentId,
    code_name: codeName,
    channel,
    signature,
  }).catch((err) => {
    log(
      `[mcp-flap-dampener] failed to record config-churn event for '${codeName}' (${channel}) (ENG-6123): ${(err as Error).message} — local suppression still active; CloudWatch metric will under-count`,
    );
  });
}

// ENG-6184 (epic ENG-6181): surface a single mcp-presence-reaper action (a
// declared-MCP-server restart, or an optional-channel quarantine) to the
// control plane, which opens a `reaper_action` alert via POST /host/reaper-event
// → alert-pager. Pre-fix a single kill was invisible — an operator learned of
// reaper activity only if restarts crossed the per-hour reaper-flapping
// CloudWatch alarm (ENG-5286).
//
// Scoped DELIBERATELY to the mcp-presence-reaper: its actions mean "a declared
// MCP died / a channel is permanently dead", a genuine incident. The
// stale-mcp-reaper (credential-rotation respawn) and orphan-channel-mcp-reaper
// (post-session-exit cleanup) are routine maintenance and intentionally do NOT
// alert — paging them would reintroduce the very noise epic ENG-6181 removes.
//
// Fire-and-forget: the reaper's correctness must not depend on this, and the
// endpoint dedupes per (agent, reaper) so a flapping reaper refreshes one open
// alert (the sweeper closes it a quiet window after the flapping stops) rather
// than storming pages.
async function reportReaperEvent(
  agentId: string,
  reaper: string,
  // ENG-9932: 'blocked' — the reaper HELD a restart because the pane is sitting
  // on a prompt no respawn can clear. Distinct from the other three because
  // nothing was killed; the point of the alert is that a human has to act.
  action: 'restart' | 'quarantine' | 'kill' | 'blocked',
  reason: string,
): Promise<void> {
  try {
    await api.post('/host/reaper-event', {
      agent_id: agentId,
      reaper,
      action,
      reason,
    });
  } catch (err) {
    log(
      `[${reaper}] failed to report reaper-event (ENG-6184): ${(err as Error).message} — local reap still proceeded; the alert may be missed`,
    );
  }
}

function recordRestartForBreaker(codeName: string, reason: RestartReason): void {
  // ENG-7239: a 'managed-mcp-churn' restart (a .mcp.json drift whose changed
  // server keys are all managed-MCP/brokered toolkits) that fires while the
  // agent's managed-MCP set is flapping, or within the cool-off after it last
  // flapped, restarts the session but is NOT counted toward the breaker. The
  // dampener already holds most flap-driven restarts (ENG-6123), but its eager
  // single-poll recovery (ENG-6512) can briefly clear `isFlapping`
  // mid-oscillation, letting one flip slip through; counting it tripped the
  // breaker and auto-paused Kylie (org dig) unreachable on every channel.
  // Scoped to the 'managed-mcp-churn' reason (CodeRabbit PR #2870) so a
  // co-occurring channel-credential rotation or channel-set drift, which shares
  // the cool-off but is a different cause, still counts toward the breaker.
  // Mirrors ENG-5547's single-dead-MCP reaper exemption: restart, don't count.
  if (
    reason === 'managed-mcp-churn' &&
    mcpFlapDampener.flappedWithin(codeName, FLAP_CHANNEL_MANAGED_MCP, FLAP_BREAKER_COOLDOWN_MS)
  ) {
    log(
      `[mcp-flap-dampener] '${codeName}' managed-MCP churn restart NOT counted toward circuit breaker, ` +
      `managed-MCP set flapped within ${Math.round(FLAP_BREAKER_COOLDOWN_MS / 1000)}s (ENG-7239)`,
    );
    return;
  }
  // ENG-7812: attribute a provisioning-reload restart to the integration whose
  // membership changed most recently (within the provisioning window), so the
  // breaker buckets its provisioning trip PER-INTEGRATION. An add/remove fired
  // this poll stashed the changed definition_id(s); a restart on an UNCHANGED set
  // (no fresh delta) resolves to `undefined` and shares the unattributed bucket
  // that still trips. Only provisioning reasons carry a key; the breaker ignores
  // it for crash / credential-rotation events.
  //
  // ENG-8388 (CodeRabbit on PR #4033): the operator-config class MUST resolve a
  // key too. `RestartBreaker.record` pins the key for provisioning AND
  // operator-config, but this caller resolved one only for the provisioning
  // half — and 'operator-integration-change' is NOT in
  // PROVISIONING_RELOAD_REASONS. So the breaker's operator bucketing was
  // unreachable: every operator reload arrived with `integrationKey ===
  // undefined` and shared the '' bucket, the exact failure the breaker's own
  // comment says the pin exists to prevent.
  //
  // It defeated this ticket's whole purpose. First-time setup adds integrations
  // one after another; with a single shared bucket, OPERATOR_MAX+1 DISTINCT adds
  // auto-pause the agent — the scenario ENG-8388 exists to stop.
  //
  // Both sides now call the SAME exported predicate, so they cannot drift apart
  // again; a new bucketed class is added in one place.
  let integrationKey: string | undefined;
  if (isIntegrationBucketedReason(reason)) {
    const attribution = agentState.provisioningAttribution.get(codeName);
    if (attribution && Date.now() - attribution.at <= RESTART_BREAKER_PROVISIONING_WINDOW_MS) {
      integrationKey = attribution.key;
    }
  }
  const result = restartBreaker.record(codeName, reason, integrationKey);

  // ENG-9138: a platform-scheduled restart class is LOOPING.
  //
  // Handled BEFORE the tripped/untripped split, and deliberately so: the
  // scheduled class never trips, but a co-occurring crash burst can trip on the
  // same call, and a rollover loop that only reported when nothing else went
  // wrong would go silent in exactly the incident where it matters most.
  //
  // This is the half of ENG-9138 that keeps the breaker's teeth. Excluding
  // day-rollover from the tally (restart-breaker.ts) stops the platform's own
  // maintenance from pausing a customer's agent; on its own that would also make
  // a genuinely broken rollover invisible. So the loop still pages a human — it
  // just pages them about the rollover instead of by killing the agent.
  if (result.scheduledLoop) {
    const { count, windowMs, reason: loopReason } = result.scheduledLoop;
    const windowMins = Math.round(windowMs / 60_000);
    log(
      `[restart-breaker] agent=${codeName} SCHEDULED-RESTART LOOP: ${count} '${loopReason}' restarts ` +
        `in ${windowMins}m. A correct day rollover fires ONCE per agent per day, so this is a platform ` +
        `bug, not an unhealthy agent. The agent is deliberately NOT paused (ENG-9138) — reporting it ` +
        `instead so somebody fixes the rollover.`,
    );
    queueSuppressionReport(codeName, 'scheduled_rollover_loop', {
      trip_class: 'scheduled',
      // No integration is involved in a scheduled restart, and the route renders
      // an empty key as "an unattributed integration" rather than inventing one.
      bucket_key: '',
      bucket_count: count,
      scheduled_window_ms: windowMs,
      scheduled_reason: loopReason,
      // Not applicable: this suppression has nothing to do with the agent's age
      // or the new-agent grace. Sent as null rather than 0, which would read as
      // "brand new" — the one wrong answer (same reasoning as ENG-8679 AC4).
      agent_age_ms: null,
      grace_expires_at: null,
    });
    maybeReportPendingSuppression(codeName);
  }

  // ENG-10066: operator-commanded restarts are LOOPING.
  //
  // Handled here for exactly the reasons the scheduled block above gives, and it
  // is the half of ENG-10066 that keeps the breaker's teeth. Excluding a channel
  // `/restart` from the tally stops the platform from pausing the agent an
  // operator is actively recovering; on its own that would also make "somebody
  // has restarted this agent nine times in eighteen minutes" invisible, which is
  // a real signal — it says the agent is unhealthy. So it still reports, it just
  // reports instead of pausing the agent mid-recovery.
  if (result.operatorCommandLoop) {
    const { count, windowMs, reason: loopReason } = result.operatorCommandLoop;
    const windowMins = Math.round(windowMs / 60_000);
    log(
      `[restart-breaker] agent=${codeName} OPERATOR-RESTART LOOP: ${count} '${loopReason}' restarts ` +
        `in ${windowMins}m. A person keeps restarting this agent, so the AGENT is likely unhealthy — ` +
        `the operator is not. The agent is deliberately NOT paused (ENG-10066), because pausing the ` +
        `agent somebody is recovering is how razorclaw-sales stayed down for 14h.`,
    );
    queueSuppressionReport(codeName, 'operator_command_loop', {
      trip_class: 'operator-command',
      // No integration is involved in a `/restart`; the route renders an empty
      // key as "an unattributed integration" rather than inventing one.
      bucket_key: '',
      bucket_count: count,
      // Reusing the scheduled_* wire fields rather than forking a parallel pair:
      // they carry "the loop's window" and "the loop's dominant reason", which is
      // exactly what these are, and a second pair would double the parsing on the
      // route for no added meaning.
      scheduled_window_ms: windowMs,
      scheduled_reason: loopReason,
      // Not applicable — nothing to do with the agent's age or the new-agent
      // grace. Null rather than 0, which would read as "brand new" (ENG-8679 AC4).
      agent_age_ms: null,
      grace_expires_at: null,
    });
    maybeReportPendingSuppression(codeName);
  }

  if (!result.tripped || !result.trip) {
    // ENG-7560/ENG-7812: not tripped. If a SINGLE integration's provisioning
    // bucket has reached the trip bar (one more restart of THAT integration will
    // auto-pause the agent), surface a single early-warning. Gating on the
    // per-integration bucket (not the raw provisioning total) is what keeps a
    // legitimate bulk onboard quiet (N distinct adds never fill one bucket),
    // while a churning integration prints this line right before it trips.
    // Complements the McpConfigChurnRestartsHourly CloudWatch metric.
    if (isProvisioningReloadReason(reason) && result.maxProvisioningBucket >= RESTART_BREAKER_PROVISIONING_MAX) {
      const culprit = result.maxProvisioningBucketKey ? ` [${result.maxProvisioningBucketKey}]` : '';
      log(
        `[restart-breaker] agent=${codeName} provisioning-reload churn at the trip bar: ` +
        `${result.maxProvisioningBucket} restarts for one integration${culprit} in window (reason=${reason}); ` +
        `one more will auto-pause the agent; that integration is likely churning (ENG-7560, ENG-7812)`,
      );
    }
    // ENG-8690: the grace actually changed the outcome - this agent WOULD have
    // auto-paused. Logged loudly and separately from the churn warning above,
    // because it is a different statement: not "you are approaching the bar" but
    // "you crossed it and were spared, and you will not be after <expiry>".
    //
    // Note this can fire alongside the warning above, and should: the operator
    // needs both the culprit integration and the deadline.
    if (result.newAgentGraceSuppressedTrip) {
      // ENG-8690 (review): read the SUPPRESSED class's own bucket. This used to
      // log `maxProvisioningBucketKey` unconditionally, so a suppressed
      // operator-config trip named the wrong integration or none at all - the
      // two classes have separate buckets with separate keys.
      const sup = result.newAgentGraceSuppressed;
      const tripClassName = sup?.class ?? 'provisioning';
      const bucketKey = sup?.bucketKey ?? result.maxProvisioningBucketKey ?? '';
      const bucketCount = sup?.bucketCount ?? result.maxProvisioningBucket;
      const culprit = bucketKey ? ` [${bucketKey}]` : '';
      // `toISOString()` throws RangeError on an out-of-range value. The breaker
      // clamps the grace so this should be unreachable, but formatting a log line
      // must never be able to kill the restart path it is describing.
      let expires = 'unknown';
      if (result.newAgentGraceExpiresAt !== undefined) {
        const d = new Date(result.newAgentGraceExpiresAt);
        if (!Number.isNaN(d.getTime())) expires = d.toISOString();
      }
      const ageMins = sup ? Math.round(sup.agentAgeMs / 60_000) : undefined;
      const agePart = ageMins === undefined ? '' : ` agent is ${ageMins}m old;`;
      log(
        `[restart-breaker] agent=${codeName} ${tripClassName} trip SUPPRESSED by the new-agent grace ` +
        `(reason=${reason}${culprit}, bucket=${bucketCount});${agePart} ` +
        `onboarding churn must not pause a new agent, but the grace ends ${expires} — ` +
        `if this churn is still going then, it WILL auto-pause (ENG-8690)`,
      );

      // ENG-8679 AC2 + AC4: report the suppression to the control plane.
      //
      // Until this, the line above was the ONLY trace. A real trip reports via
      // reportCircuitTripped; the suppressed trip — the more surprising outcome,
      // and the one an operator is likelier to be confused by — reported
      // nowhere, so "why did this agent NOT pause?" was answerable only over
      // SSM. The ticket calls that "swallowed" and it is the defect AC2/AC4
      // exist to close.
      //
      // FIRE-AND-FORGET, matching reportCircuitTripped. The suppression has
      // already happened locally and the agent is still running; a failed report
      // must never affect the restart path, and must never retry-storm the
      // endpoint. A dropped report costs one alert, not correctness.
      // COLD-START LOSS, now DEFERRED rather than dropped (CodeRabbit, #4351).
      //
      // The dashboard restart loop can reach this branch before processAgent has
      // populated codeNameToAgentId, so an integration-change restart on a cold
      // manager has no agent id to report against. The first cut dropped the
      // report silently; the second logged the drop. Both still lost the report,
      // which is the same swallowed-signal defect AC2 and AC4 exist to close,
      // reintroduced one layer down in the fix for it.
      //
      // "It recurs on the next poll" was the argument for dropping, and it does
      // not hold: the suppression fires on the RESTART, and the restart that
      // produced it may well be the last one of the episode — the grace works
      // precisely by not restarting again. A suppression at the tail of a flap
      // would then never be reported at all.
      //
      // The queue is the pattern this file already uses for exactly this race
      // (ENG-5441/PR #1302: a trip hydrated before the id lands is retried from
      // processAgent, not dropped). One entry per agent, flushed on the next
      // poll, so it costs a map slot rather than a lost operator signal.
      const suppressionPayload: Record<string, unknown> = {
        trip_class: tripClassName,
        bucket_key: bucketKey,
        bucket_count: bucketCount,
        // AC4 names the age specifically. `sup` is absent only on an older
        // breaker build; send null rather than a fabricated 0, which would
        // read as "brand new" and is the one wrong answer here.
        agent_age_ms: sup ? sup.agentAgeMs : null,
        grace_expires_at: result.newAgentGraceExpiresAt ?? null,
      };
      queueSuppressionReport(codeName, 'new_agent_grace', suppressionPayload);
      if (!agentState.codeNameToAgentId.has(codeName)) {
        log(
          `[restart-breaker] agent=${codeName} suppression report DEFERRED: no agent_id known yet ` +
            `(cold-start before processAgent). The local suppression still applied; the report is ` +
            `queued and will be sent on the next poll once the id is cached (ENG-8679).`,
        );
      }
      // Flushes immediately in the steady-state case, where the id is already
      // cached — so the warm path is unchanged, one POST at suppression time.
      maybeReportPendingSuppression(codeName);
    }
    return;
  }
  const trip = result.trip;
  log(
    `[persistent-session-decision] agent=${codeName} decision=circuit-tripped ` +
    `detail=${JSON.stringify(trip.statusMessage)} (ENG-5441)`,
  );
  // Fire-and-forget — a missed API call leaves the trip in `reportedTrips`
  // unset, so `maybeReportCurrentTrip` will retry on the next poll.
  // The breaker keeps gating spawns locally regardless.
  maybeReportCurrentTrip(codeName);
}

async function reportCircuitTripped(codeName: string, trip: TripState): Promise<void> {
  // Skip the API call if we've already reported this exact trip (same
  // trippedAt timestamp). Without this, every subsequent poll would
  // re-POST until the operator clears, spamming the audit log.
  if (reportedTrips.get(codeName) === trip.trippedAt) return;
  // Look up agent_id from the local cache (populated by processAgent on
  // every poll). Cold-start manager that hydrated a trip but hasn't
  // observed the agent in /host/agents yet will hit this — leave
  // `reportedTrips` untouched so `maybeReportCurrentTrip` retries once
  // the agent_id lands. CodeRabbit on PR #1302.
  const agentId = agentState.codeNameToAgentId.get(codeName);
  if (!agentId) throw new Error(`no agent_id known yet for '${codeName}' — will retry next poll`);
  // CodeRabbit on PR #1302: do NOT mark `reportedTrips` until the POST
  // succeeds. Pre-fix, a transient API failure (5xx, JWT expiry race)
  // would stamp the timestamp anyway and silently swallow the trip
  // forever; the breaker would still gate spawns locally but the
  // dashboard / status_message would stay stale.
  await api.post('/host/circuit-breaker/trip', {
    agent_id: agentId,
    status_message: trip.statusMessage,
    tripped_at: new Date(trip.trippedAt).toISOString(),
    events: trip.eventsAtTrip.map((e) => ({ reason: e.reason, at: new Date(e.at).toISOString() })),
  });
  reportedTrips.set(codeName, trip.trippedAt);
}

// Idempotent helper — call after `agentState.codeNameToAgentId` is populated for an
// agent to retry a pending trip report. Used both on the first trip and
// on the first poll after a manager restart (where the trip was
// hydrated from manager-state.json before any /host/agents call).
function maybeReportCurrentTrip(codeName: string): void {
  const trip = restartBreaker.getTrip(codeName);
  if (!trip) return;
  if (reportedTrips.get(codeName) === trip.trippedAt) return;
  reportCircuitTripped(codeName, trip).catch((err) => {
    log(`[circuit-breaker] Failed to report trip for '${codeName}' to API: ${(err as Error).message}`);
  });
}

// ENG-8679 AC2 + AC4 (CodeRabbit, #4351). Idempotent flush of a queued
// suppression report — the sibling of `maybeReportCurrentTrip`, and called from
// the same place in `processAgent` for the same reason: the id the report needs
// is not knowable until the agent has been observed in /host/agents.
//
// Returns without doing anything when there is nothing queued, so the common
// case (no suppression) costs one map lookup per agent per poll.
//
// `takeSuppressionReport` removes the entry BEFORE the POST — see that module
// for why (a hold-until-success queue re-POSTs a stale suppression every poll
// against a failing endpoint, which is the retry-storm this route forbids).
function maybeReportPendingSuppression(codeName: string): void {
  const agentId = agentState.codeNameToAgentId.get(codeName);
  const payloads = takeSuppressionReports(codeName, agentId);
  if (payloads.length === 0 || !agentId) return; // nothing queued, or still cold — stays queued
  // ENG-9138: one POST per REASON. An agent can hold a new-agent-grace
  // suppression and a rollover-loop report at the same time, and they are two
  // different things for an operator to act on — collapsing them into one call
  // would have to discard one of the two payloads.
  for (const payload of payloads) {
    void api
      .post('/host/circuit-breaker/suppressed', { agent_id: agentId, ...payload })
      .catch((err: unknown) => {
        log(
          `[restart-breaker] agent=${codeName} suppression report failed (non-fatal): ` +
            `${err instanceof Error ? err.message : String(err)}`,
        );
      });
  }
}

// ENG-7577: LEVEL-triggered reconcile of a held trip against the authoritative
// status. The pre-existing operator-resume clear (in the status-change block
// below) is EDGE-triggered on `agentState.knownStatuses` — an in-memory map that
// starts empty on every manager (re)start, while trips are persisted and
// rehydrated. So across a manager restart the edge never fires and the trip
// latches forever ('don', 28 Jul: 3h+ dead, console Resume never reached
// clear(), recovery required hand-editing manager-state.json on the host).
//
// Here we ask the level question instead — "does the control plane still have
// this agent paused?" — which is correct regardless of what this manager
// witnessed. See breaker-stale-trip.ts for why a still-`active` agent after the
// settle window always wants the trip dropped.
function maybeClearStaleTrip(agent: {
  agent_id: string;
  code_name: string;
  status: string;
  kill_switch?: unknown;
}): void {
  const codeName = agent.code_name;
  const trip = restartBreaker.getTrip(codeName);
  const decision = decideStaleTripClear({
    trip,
    agentStatus: agent.status,
    killSwitched: Boolean(agent.kill_switch),
    settleMs: readStaleTripSettleMs(),
    now: Date.now(),
  });
  // Holds are steady-state (every poll for the life of a pause) — never logged.
  if (decision.action !== 'clear') return;

  restartBreaker.clear(codeName);
  reportedTrips.delete(codeName);
  // ENG-6392: the trip cleared, so reset the per-integration recovery diff. It is
  // only meaningful within the trip cycle that accumulated it; carrying it over
  // would let the NEXT trip's auto-resume report dependencies that recovered
  // during a completely different incident (CodeRabbit on PR #3861).
  dependencyRecoveryLedger.clear(codeName);
  log(
    `[circuit-breaker] agent=${codeName} cleared STALE trip — authoritative status=active while a trip was held ` +
      `(age=${Math.round(decision.ageMs / 60_000)}min, trippedAt=${new Date(trip!.trippedAt).toISOString()}); ` +
      `the pause this trip caused is no longer in force (ENG-7577)`,
  );

  // Mirror the edge path's credit re-arm: a resume we did not issue is
  // deliberate external intent and re-arms the one auto-resume credit. Marker
  // freshness is the self-vs-external discriminator (same rule, same window as
  // the paused→active watcher), so our own auto-resume can't refund itself.
  const marker = getAutoResumeMarker(codeName);
  const isSelfResume = marker !== undefined && Date.now() - marker.autoResumedAt < AUTO_RESUME_SELF_WINDOW_MS;
  if (!isSelfResume) deleteAutoResumeMarker(codeName);

  // Persist NOW, not at end-of-cycle. A worker exit in that window would
  // rehydrate the very trip we just cleared — which is the failure mode this
  // whole function exists to end.
  state = {
    ...state,
    circuitBreakerTrips: restartBreaker.serialize(),
    circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
  };
  send({ type: 'state-update', state });
}

// ENG-6088: bounded auto-resume. One credit per trip cycle — markers are
// persisted into manager-state.json (circuitBreakerAutoResumes) so a
// manager restart can't hand a re-tripped agent a fresh credit. Cleared on
// operator resume (deliberate human intent re-arms the credit) or by the
// AGT_AUTO_RESUME_BACKOFF_RESET_MS TTL inside decideAutoResume.
//
// ENG-7891 slice 5b (ADR-0049 durable-state rule): the marker map is DURABLE,
// persisted per-agent, so it converges on the rename-stable runtime key alongside
// the breaker's trip state — a codename rename that stranded the credit but not
// the trip would hand a re-tripped agent a fresh unattended resume. Every access
// goes through the get/set/deleteAutoResumeMarker helpers, which key on
// agentRuntimeKey() (agent_id when id-keyed, code_name on legacy — a no-op on the
// pre-arming fleet). The end-of-cycle snapshot (and the inline persists after the
// spend/re-arm paths) serialise whatever keys the map holds, so keying
// consistently at every access keeps the map and circuitBreakerAutoResumes in one
// key-space with no reverse lookup. getAutoResumeMarker dual-reads a surviving
// legacy code_name entry and rekeys it on first touch after the flip; unlike the
// kanban-nudge sidecar it need NOT force a write there — the rekey is captured by
// the next poll's snapshot and is idempotent if the manager exits first (the file
// still holds the code_name key, re-migrated on the next read), so no credit is
// lost. The ephemeral siblings (reportedTrips, autoResumeInFlight,
// autoResumeStandDowns, autoResumeLoggedSkips) are NOT persisted and are rebuilt
// every generation, always accessed with the same-generation code_name and
// consistent with the breaker's code_name-facing API — so they deliberately stay
// code_name-keyed.
let autoResumeMarkers = new Map<string, AutoResumeRecord>();

/** ADR-0049 durable-state key for the auto-resume marker map (agent_id when id-keyed). */
function autoResumeMarkerKey(codeName: string): string {
  return agentRuntimeKey(codeName);
}

/**
 * Read an agent's auto-resume marker, migrating a surviving legacy code_name-keyed
 * entry onto the runtime key on first touch after the id-keyed layout is armed
 * (dual-read + rekey). No-op on legacy hosts (runtime key === code_name, so the
 * fallback branch is skipped). No forced persist — see the block comment above.
 */
function getAutoResumeMarker(codeName: string): AutoResumeRecord | undefined {
  const key = autoResumeMarkerKey(codeName);
  const hit = autoResumeMarkers.get(key);
  if (hit || key === codeName) return hit;
  const legacy = autoResumeMarkers.get(codeName);
  if (!legacy) return undefined;
  autoResumeMarkers.delete(codeName);
  autoResumeMarkers.set(key, legacy);
  return legacy;
}

/** Write an agent's auto-resume marker under the runtime key, dropping any stale legacy code_name entry. */
function setAutoResumeMarker(codeName: string, rec: AutoResumeRecord): void {
  const key = autoResumeMarkerKey(codeName);
  if (key !== codeName) autoResumeMarkers.delete(codeName);
  autoResumeMarkers.set(key, rec);
}

/** Delete an agent's auto-resume marker under either key; returns true if anything was removed. */
function deleteAutoResumeMarker(codeName: string): boolean {
  const key = autoResumeMarkerKey(codeName);
  let removed = autoResumeMarkers.delete(key);
  if (key !== codeName && autoResumeMarkers.delete(codeName)) removed = true;
  return removed;
}
// POSTs in flight (or acked but not yet observed as active): stops a 5s
// poll tick from duplicating the request mid-round-trip. NOT the
// self-vs-operator discriminator — that's marker freshness (see the
// paused→active watcher), which survives manager restarts where this
// in-memory set does not.
const autoResumeInFlight = new Set<string>();
// How long after issuing an auto-resume a paused→active transition is
// attributed to US rather than an operator. Sound because after OUR
// resume the agent is active — an operator resume can only follow a
// LATER re-pause (re-trip quiet-period timescale ≫ this window).
const AUTO_RESUME_SELF_WINDOW_MS = 120_000;
// Per-(codeName, trippedAt) one-shot logging for skip reasons, so a
// 5s poll loop doesn't write 'not-quiet-yet' to manager.log 360×/hour.
const autoResumeLoggedSkips = new Map<string, string>();
// Trips the server answered `not-eligible` for (operator/cost pause owns
// the agent). Keyed `${codeName}:${trippedAt}` so the stand-down dies with
// the trip — a NEW trip gets a fresh evaluation. Without this tombstone a
// not-eligible answer would re-POST every poll tick.
const autoResumeStandDowns = new Set<string>();
// ENG-6392: which integrations the manager's connectivity probe saw go down and
// later read ok again — diffed into `recovered_dependencies` on auto-resume so
// the server can fire the ENG-6384 "X is back" confirmation. Populated by the
// connectivity-probe round; consumed + cleared when a trip clears.
const dependencyRecoveryLedger = new DependencyRecoveryLedger();
// ENG-6392: unstable latches whose manager-DM report (POST /host/circuit-breaker/
// unstable-latch) has NOT yet landed a response. Keyed `${codeName}:${trippedAt}`.
// A latch-unstable stands the trip down (no "next trip" to carry a retry), and
// the agent stays paused — so a transient POST failure would otherwise lose the
// manager notification permanently. We keep the key here until a POST succeeds
// (the endpoint is idempotent per (agent, trip)) and re-attempt on each poll.
const unstableLatchUnreported = new Set<string>();
// Guards against stacking a second report POST while one is in flight (the poll
// fires every few seconds; a slow POST must not fan out into duplicates).
const unstableLatchReportInFlight = new Set<string>();

// ENG-6146: code_names the manager has halted because their scope is killed
// (the API overlaid status:'paused' + a kill_switch marker). Used to announce a
// resume notice when the switch clears (paused → active transition). Added in
// the paused branch, drained on resume.
const killPausedCodeNames = new Set<string>();

/**
 * ENG-9061: code_names whose runtime the manager is NOT running — anything the
 * control plane reports as other than `active` (draft, paused, revoked, and the
 * kill-switch overlay, which arrives as `paused` + a `kill_switch` marker).
 *
 * Maintained because `stopAgentRuntime` tears the tmux session down but does
 * NOT remove the agent from `agentState.persistentSessionAgents` — that set is
 * only drained on day-rollover, channel-restart and the two auth paths. So a
 * paused agent stays in the live set, the pane sampler keeps stat'ing its
 * lingering `pane.log` successfully, and it would be attested as "measured" and
 * rostered into the occupancy stand-down.
 *
 * That is the seam between what `hasFreshPaneObservation` can prove and what the
 * roster claims. The watermark attests that the FILE is readable; the roster
 * asserts that the METER is running. For an agent whose runtime was torn down
 * those diverge permanently — its ledger can never accrue again, so it would sit
 * in the roster forever, standing the fallback producer down on a measurement
 * that is not happening. Excluding it here keeps the two claims the same claim.
 *
 * Fail-safe in the direction that matters: a stale entry only ever REMOVES an
 * agent from the roster, which hands it back to the legacy cron — the
 * pre-existing behaviour, never a silent zero.
 *
 * ## The one-poll lag is accepted, and it is safe in both directions
 *
 * Within `pollCycle` the responsiveness probe READS this set before the
 * `processAgent` loop WRITES it, so on the cycle where an agent's status
 * changes the set still reflects the previous poll. Both directions were
 * checked, and neither can produce a wrong bill:
 *
 *   active -> paused/revoked/killed — the agent stays eligible for one cycle,
 *     so an empty drain can roster it and stand the cron down for ten minutes.
 *     The agent is halted, so zero IS the correct bill for that window; the lag
 *     reaches the right answer one cycle early rather than the wrong answer.
 *     (And an agent that was actually working when it was paused has buckets to
 *     report, so it takes the normal bucket path and is never rostered.)
 *
 *   paused -> active — the agent is excluded for one cycle, so it is not
 *     rostered and the legacy cron remains eligible to bill it. That is exactly
 *     the pre-existing behaviour, i.e. the fail-safe direction.
 *
 * Deliberately documented rather than tested: a regression test would have to
 * drive a full `pollCycle` — network I/O, tmux, docker, the whole agent loop —
 * to assert a property whose only observable effect is which of two correct
 * answers arrives one 10s poll sooner. Pinning the poll ORDER is what would
 * actually matter, and that is a property of this comment's placement next to
 * both call sites, not of a fixture. (CodeRabbit, PR #4639.)
 */
const notRunningCodeNames = new Set<string>();

// ENG-6316: agents reliably announce "I'm back online" after these
// manager-driven session restarts — on `--resume` they're responding to the
// "your manager will restart your session shortly" notice injected just before
// the restart. Left to themselves they prefix that announcement with a 🟢
// green-light emoji. Standardize it at the exact message the agent reacts to:
// open the announcement with a 👋 wave instead. Appended to every pre-restart
// notice below (all 'system'-typed, all immediately followed by a restart).
export const BACK_ONLINE_GREETING_GUIDANCE =
  ' When you reconnect, if you tell anyone you are back, start that message with a 👋 wave emoji and do not use a 🟢 green-light emoji.';

function maybeAutoResume(agent: { agent_id: string; code_name: string }): void {
  const codeName = agent.code_name;
  // ENG-6383/ENG-6395: when the `resume-reconciler` flag is on it SUPERSEDES the
  // blind ENG-6088 quiet-timer below. Resolved via the host flag store
  // (env override `AGT_RESUME_RECONCILER_ENABLED` → heartbeat → default off), so
  // the canary bake flips per-host centrally. Fire-and-forget (maybeResumeReconcile
  // owns its own in-flight guard + fail-safe holds); the blind path stays the
  // flag-off fallback so a bad rollout reverts by flipping the flag.
  if (hostFlagStore().getBoolean('resume-reconciler')) {
    void maybeResumeReconcile(agent);
    return;
  }
  // In-flight guard. Without it, a second poll tick during the POST
  // round-trip would duplicate the request (harmless server-side — the
  // duplicate lands `not-eligible` — but it would tombstone the trip and
  // log a misleading stand-down).
  if (autoResumeInFlight.has(codeName)) return;
  const trip = restartBreaker.getTrip(codeName);
  if (trip && autoResumeStandDowns.has(`${codeName}:${trip.trippedAt}`)) return;
  const decision = decideAutoResume({
    trip,
    // Rebase note: `main` replaced the raw `autoResumeMarkers.get(codeName)` read
    // with the `getAutoResumeMarker(codeName)` accessor while this branch was
    // open. Both sides of the conflict are independent - that change is about
    // WHERE the marker comes from, this branch's is about how the CONFIG is
    // resolved - so both are kept.
    marker: getAutoResumeMarker(codeName),
    // ENG-8395: gate resolved PER AGENT. getBooleanForAgent applies
    // env override (AGT_AUTO_RESUME_ENABLED) → this agent's heartbeat override →
    // host-wide flag → compiled default false. That per-agent layer is the point
    // of the ticket: the env var's grain is the whole host, so arming one agent
    // on a 14-agent host was impossible before. readAutoResumeConfig re-applies
    // the env layer on top (same precedence, idempotent) so the env override
    // still wins even for a caller that passes no flag value.
    config: readAutoResumeConfig({
      flagEnabled: hostFlagStore().getBooleanForAgent(AUTO_RESUME_FLAG_KEY, agent.agent_id),
    }),
    now: Date.now(),
  });

  if (decision.action === 'skip') {
    // 'disabled' and 'no-trip' are steady-state — never log. The three
    // interesting reasons log once per (trip, reason) transition.
    if (
      (decision.reason === 'already-auto-resumed' ||
        decision.reason === 'not-quiet-yet' ||
        decision.reason === 'class-not-eligible') &&
      trip
    ) {
      const key = `${codeName}:${trip.trippedAt}`;
      if (autoResumeLoggedSkips.get(key) !== decision.reason) {
        autoResumeLoggedSkips.set(key, decision.reason);
        const detail = decision.reason === 'not-quiet-yet'
          ? ` (~${Math.ceil((decision.remainingMs ?? 0) / 60_000)}min remaining)`
          : decision.reason === 'class-not-eligible'
            // ENG-7577: the blind quiet-timer only probes provisioning-reload
            // churn. A crash-class trip keeps requiring manual clearance.
            ? ` class=${tripClass(trip)} — the blind quiet-timer only auto-recovers provisioning-reload trips; this one stays paused for an operator (ENG-7577)`
            : ' — credit spent for this cycle; operator resume re-arms it';
        log(`[auto-resume] agent=${codeName} decision=skip reason=${decision.reason}${detail} (ENG-6088)`);
      }
    }
    return;
  }

  // Issue the resume. Fire-and-forget like reportCircuitTripped — a failed
  // POST leaves no marker, so the next poll retries. The server's RPC only
  // flips status='paused' AND paused_reason='circuit_breaker' rows, so a
  // race with an operator action degrades to a `resumed:false` no-op.
  const trippedAt = trip!.trippedAt;
  log(
    `[auto-resume] agent=${codeName} decision=resume quiet=${Math.round(decision.quietMs / 60_000)}min ` +
    `trippedAt=${new Date(trippedAt).toISOString()} (ENG-6088)`,
  );
  autoResumeInFlight.add(codeName);
  api.post<{ ok: boolean; resumed?: boolean; reason?: string }>('/host/circuit-breaker/auto-resume', {
    agent_id: agent.agent_id,
    quiet_ms: decision.quietMs,
    tripped_at: new Date(trippedAt).toISOString(),
  }).then((res) => {
    autoResumeInFlight.delete(codeName);
    if (res.resumed) {
      // Spend the credit and clear the local trip so the spawn gate opens
      // on the next poll. reportedTrips is cleared so a NEW trip after
      // this resume posts to the API again.
      setAutoResumeMarker(codeName, { trippedAt, autoResumedAt: Date.now() });
      restartBreaker.clear(codeName);
      reportedTrips.delete(codeName);
      // ENG-6392: the trip cleared — reset the recovery diff so it doesn't
      // linger into the next cycle (this blind path sends no
      // recovered_dependencies, but the ledger must still not leak across trips).
      dependencyRecoveryLedger.clear(codeName);
      // Persist the spent credit NOW rather than waiting for the next
      // poll's end-of-cycle snapshot — a worker exit in that window would
      // drop the marker and hand a re-tripped agent a fresh credit after
      // restart, breaking the bounded-resume guarantee (CodeRabbit on
      // this PR).
      state = {
        ...state,
        circuitBreakerTrips: restartBreaker.serialize(),
        circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
      };
      send({ type: 'state-update', state });
      log(`[auto-resume] agent=${codeName} resumed — re-trip within backoff window will stay paused (ENG-6088)`);
    } else {
      // not-eligible: an operator/cost pause owns this agent, or someone
      // already resumed it. Stand down for THIS trip — the tombstone stops
      // the per-poll retry; the status watcher reconciles the rest. Keep
      // the local trip; if the agent is genuinely active again the
      // paused→active transition clears it (and the tombstone key dies
      // with the trip).
      autoResumeStandDowns.add(`${codeName}:${trippedAt}`);
      log(`[auto-resume] agent=${codeName} not applied (reason=${res.reason ?? 'unknown'}) — standing down for this trip (ENG-6088)`);
    }
  }).catch((err) => {
    autoResumeInFlight.delete(codeName);
    log(`[auto-resume] agent=${codeName} POST failed (will retry next poll): ${(err as Error).message}`);
  });
}

// ENG-6383: log a reconciler HOLD at most once per (trip, reason) transition —
// a hold is steady-state (every 5s poll until the dependency recovers), so an
// unguarded log would write 720×/hour. Reuses the ENG-6088 skip-log map (the
// blind and reconciler paths are mutually exclusive by flag).
//
// `reason` is the STABLE dedupe key — it must NOT carry transient detail (a
// varying error message, a climbing cycles-short count), or the same HOLD state
// would re-log every poll and defeat the guard (CodeRabbit on this PR). Put any
// changing diagnostic in `detail`: it rides the log body but never the key.
function logResumeReconcileHoldOnce(
  codeName: string,
  trippedAt: number,
  reason: string,
  detail?: string,
): void {
  const key = `${codeName}:${trippedAt}`;
  if (autoResumeLoggedSkips.get(key) !== reason) {
    autoResumeLoggedSkips.set(key, reason);
    log(`[resume-reconciler] agent=${codeName} decision=hold reason=${reason}${detail ? ` (${detail})` : ''} (ENG-6383)`);
  }
}

// ENG-6383 (ENG-6375 Slice C): the health-gated auto-resume path. Replaces the
// blind ENG-6088 quiet-timer (maybeAutoResume) with decideResumeReconcile —
// a tripped, paused agent is cleared ONLY when its dependency has genuinely
// recovered (status active + MCP present + connectivity ok >= minHealthyCycles).
//
// FAIL-SAFE by construction: a failed/garbled health fetch, or any unexpected
// error, resolves to a HOLD (retry next poll) — NEVER a resume. The reconciler
// exists because a blind resume re-creates the crash-loop, so ambiguity holds.
//
// Binding invariants (ENG-6333 council; mirror decideResumeReconcile's): the
// restart counter is NEVER reset (we only read the trip + marker; only
// restartBreaker.clear() on a confirmed resume drops the trip, leaving the
// spent-credit marker as the one-per-window cap); a resumed→re-tripped agent
// LATCHES to mandatory-manual rather than oscillating.
// ENG-6392: best-effort, retry-until-landed report of an unstable latch to the
// host (which DMs the manager). The endpoint is idempotent on (agent, trip), so
// re-POSTing until one call returns is safe; we clear the unreported marker on
// ANY response (success or a 4xx/5xx the server chose to return) and only retry
// when the POST itself rejected (network/transport). The in-flight guard stops
// the poll loop from stacking duplicate POSTs. The latch's correctness (agent
// stays paused) never depends on this landing.
function reportUnstableLatch(
  agent: { agent_id: string; code_name: string },
  trippedAt: number,
  standDownKey: string,
): void {
  if (unstableLatchReportInFlight.has(standDownKey)) return;
  unstableLatchReportInFlight.add(standDownKey);
  void api
    .post('/host/circuit-breaker/unstable-latch', {
      agent_id: agent.agent_id,
      tripped_at: new Date(trippedAt).toISOString(),
    })
    .then(() => {
      // The host received and answered (sent / dark / already-latched) — the
      // notification is no longer our responsibility to retry.
      unstableLatchUnreported.delete(standDownKey);
    })
    .catch((err) => {
      // Transport-level failure only — leave the key so the next poll retries.
      log(
        `[resume-reconciler] agent=${agent.code_name} unstable-latch report failed (will retry next poll): ${(err as Error).message} (ENG-6392)`,
      );
    })
    .finally(() => {
      unstableLatchReportInFlight.delete(standDownKey);
    });
}

async function maybeResumeReconcile(agent: { agent_id: string; code_name: string }): Promise<void> {
  const codeName = agent.code_name;
  // In-flight guard FIRST — the async health GET now precedes the decision, so
  // a later poll tick must not re-enter mid-fetch and duplicate the work.
  if (autoResumeInFlight.has(codeName)) return;
  const trip = restartBreaker.getTrip(codeName);
  if (!trip) return; // no local trip → nothing to reconcile (and no GET).
  const standDownKey = `${codeName}:${trip.trippedAt}`;
  // Already latched-unstable or answered not-eligible for THIS trip → stop
  // re-evaluating every poll (a new trip gets a fresh trippedAt → fresh key).
  if (autoResumeStandDowns.has(standDownKey)) {
    // ENG-6392: the trip is decided, but if we latched unstable and the manager
    // report hasn't landed yet, keep retrying it here — the stood-down agent
    // stays paused, so this early-return is the only place a retry can happen.
    if (unstableLatchUnreported.has(standDownKey)) {
      reportUnstableLatch(agent, trip.trippedAt, standDownKey);
    }
    return;
  }

  autoResumeInFlight.add(codeName);
  try {
    // Numeric knobs (minHealthyCycles, backoffResetMs) stay env-tunable; the
    // enable gate is the registry flag (env override → heartbeat → default), so
    // decideResumeReconcile's own `disabled` check agrees with the call-site gate.
    const config = { ...readResumeReconcilerConfig(), enabled: hostFlagStore().getBoolean('resume-reconciler') };
    const trippedAt = trip.trippedAt;

    // Server-owned health (ENG-6382). Fail SAFE: a thrown/timed-out GET or a
    // malformed body is treated as "not healthy yet" (hold), never as healthy.
    // We deliberately do NOT fall back to the blind decideAutoResume here — a
    // second resume door would defeat the one-per-window cap.
    let serverHealth: ReturnType<typeof parseResumeHealthResponse> = null;
    try {
      const raw = await api.get(
        `/host/circuit-breaker/resume-health?agent_id=${encodeURIComponent(agent.agent_id)}`,
      );
      serverHealth = parseResumeHealthResponse(raw);
    } catch (err) {
      logResumeReconcileHoldOnce(codeName, trippedAt, 'health-fetch-failed', (err as Error).message.slice(0, 80));
      return;
    }
    if (!serverHealth) {
      logResumeReconcileHoldOnce(codeName, trippedAt, 'health-unparseable');
      return;
    }

    // Manager-owned mcpPresent — the one signal the server CANNOT see
    // (.mcp.json desired state never leaves the host). Whole-agent: every
    // declared MCP server is present (none given up by the presence-reaper).
    const declaredKeys = projectMcpKeys(codeName, psGetProjectDir(codeName));
    const mcpPresent = deriveMcpPresent(declaredKeys, givenUpMcpServerKeys(codeName));

    const decision = decideResumeReconcile({
      trip,
      marker: getAutoResumeMarker(codeName),
      health: { ...serverHealth, mcpPresent },
      config,
      now: Date.now(),
    });

    if (decision.action === 'hold') {
      // Stable dedupe key = which precondition is failing (so a status→mcp→
      // connectivity progression still logs each transition once). The climbing
      // cyclesShort count rides `detail`, NOT the key — else it'd re-log per poll.
      const reason = decision.precondition
        ? `not-healthy-yet:${decision.precondition}`
        : decision.reason;
      const detail = decision.cyclesShort ? `${decision.cyclesShort}-short` : undefined;
      logResumeReconcileHoldOnce(codeName, trippedAt, reason, detail);
      return;
    }

    if (decision.action === 'latch-unstable') {
      // The agent auto-resumed once, then re-tripped within the backoff window
      // — UNSTABLE, not down. We refuse to chase the oscillation: stop
      // auto-resuming and latch to mandatory-manual. We do NOT clear the trip
      // and NOT reset the counter — the agent stays paused, and the re-trip's
      // own /host/circuit-breaker/trip report keeps the operator-visible
      // circuit-breaker alert OPEN. ENG-6392: we ALSO report the latch to the
      // host so it DMs the manager a distinct worded 'unstable' message ("X keeps
      // falling over after I bring it back — take a look"), separate from the
      // dependency 'down' alert.
      autoResumeStandDowns.add(standDownKey);
      const key = `${codeName}:${trippedAt}`;
      if (autoResumeLoggedSkips.get(key) !== 'latch-unstable') {
        autoResumeLoggedSkips.set(key, 'latch-unstable');
        log(
          `[resume-reconciler] agent=${codeName} decision=latch-unstable — auto-resumed once then re-tripped ` +
          `within the backoff window; latching to mandatory-manual (NOT resuming; restart counter untouched; ` +
          `agent stays paused with its circuit-breaker alert open) (ENG-6383)`,
        );
      }
      // Mark the manager report as owed and attempt it. Tracked OUTSIDE the
      // log-once guard so a transient POST failure is retried on later polls
      // (via the stand-down branch above) until it lands — a stood-down agent
      // has no "next trip" to carry a lost notification.
      unstableLatchUnreported.add(standDownKey);
      reportUnstableLatch(agent, trippedAt, standDownKey);
      return;
    }

    // decision.action === 'resume' — the genuine-health gate passed.
    log(
      `[resume-reconciler] agent=${codeName} decision=resume healthyCycles=${decision.healthyCycles} ` +
      `trippedAt=${new Date(trippedAt).toISOString()} (ENG-6383)`,
    );
    // ENG-6392: the whole-agent gate can't name WHICH dependency recovered, so
    // we diff the manager's own per-integration probe ledger: every dep we saw
    // go `down` and later read `ok`. The resume-health gate already proved all
    // deps read active, so these are the genuine recoveries; the server confirms
    // only the ones with an OPEN incident and skips the rest, so a stale or
    // already-resolved entry is harmless. Omitted from the body when empty.
    const recoveredDependencies = dependencyRecoveryLedger.collectRecovered(codeName);
    const res = await api.post<{ ok: boolean; resumed?: boolean; reason?: string }>(
      '/host/circuit-breaker/auto-resume',
      {
        agent_id: agent.agent_id,
        tripped_at: new Date(trippedAt).toISOString(),
        ...(recoveredDependencies.length > 0
          ? { recovered_dependencies: recoveredDependencies }
          : {}),
      },
    );
    if (res.resumed) {
      // Spend the credit + drop the local trip so the spawn gate reopens. The
      // marker (the one-per-window cap) is persisted NOW — a worker exit before
      // the next end-of-cycle snapshot would otherwise hand a re-tripped agent
      // a fresh credit after restart (mirrors the ENG-6088 path).
      setAutoResumeMarker(codeName, { trippedAt, autoResumedAt: Date.now() });
      restartBreaker.clear(codeName);
      reportedTrips.delete(codeName);
      // ENG-6392: the diff has been reported (above) — reset it so the next
      // trip cycle starts from a clean slate and recovered/deleted deps don't
      // linger across cycles.
      dependencyRecoveryLedger.clear(codeName);
      state = {
        ...state,
        circuitBreakerTrips: restartBreaker.serialize(),
        circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
      };
      send({ type: 'state-update', state });
      log(`[resume-reconciler] agent=${codeName} resumed — a re-trip within the backoff window will latch unstable (ENG-6383)`);
    } else {
      // not-eligible: an operator/cost pause owns the agent, or someone already
      // resumed it. Stand down for THIS trip; the status watcher reconciles.
      autoResumeStandDowns.add(standDownKey);
      log(`[resume-reconciler] agent=${codeName} not applied (reason=${res.reason ?? 'unknown'}) — standing down for this trip (ENG-6383)`);
    }
  } catch (err) {
    // Any unexpected error is a fail-safe hold — the next poll retries.
    log(`[resume-reconciler] agent=${codeName} reconcile failed (will retry next poll): ${(err as Error).message}`);
  } finally {
    autoResumeInFlight.delete(codeName);
  }
}


/**
 * ENG-7401 Phase 2: the restart classes that stop the session specifically to
 * (re)bind a CHANGED MCP server set - integration add/remove, managed-toolkit
 * churn, a channel-set change (the channel listener is an MCP), or a
 * bind-remediation re-respawn. These are exactly the restarts that WANT to come
 * back immediately, and by the time their stop fires processAgent has already
 * re-rendered .mcp.json, so respawning now (rather than waiting for the next poll
 * cycle) picks up the correct new config. This is the SAME set that arms the
 * ENG-6174 respawn verification below - both key off "this stop must rebind an
 * MCP". Exported for unit testing the gate in isolation.
 */
export function restartReasonBindsNewMcp(breakerReason: RestartReason): boolean {
  return (
    breakerReason === 'hot-reload-mcp' ||
    breakerReason === 'managed-mcp-churn' ||
    breakerReason === 'bind-remediation' ||
    breakerReason === 'channel-set-change' ||
    // ENG-7576: the dashboard integration-add stop (the poll's restart-consumption
    // loop) is the archetypal "stop to rebind a changed MCP set" - the docstring
    // above always described it, but the path passed no breakerReason so it never
    // hit this predicate. Including it arms the ENG-6174 respawn verification for
    // the ONE real respawn of an integration add.
    breakerReason === 'integration-change' ||
    // ENG-8388: the operator-attributed variant is the SAME reload with a
    // different breaker tally - it binds a changed MCP set identically. Omitting
    // it here would mean attributing a reload to a human silently disabled the
    // respawn verification for it, i.e. the fix would break the thing it was
    // meant to leave alone.
    breakerReason === 'operator-integration-change' ||
    // ENG-7541: a 'credential-rotation' respawn IS the env-only re-mint path
    // (ENG-7510), just tagged with a non-tripping breaker class. Once its stop
    // fires it must behave IDENTICALLY to 'hot-reload-mcp' — come back
    // immediately so the agent picks up the fresh credential, and arm the
    // ENG-6174 respawn verification (the unchanged MCP set re-binds normally, so
    // verification passes).
    // ENG-8343 scope note: "immediately" here is POST-STOP respawn timing only.
    // WHEN the stop itself fires is now gated (defer-until-idle, window-exempt) —
    // exactly the combination 'channel-set-change' above already has: gated at
    // the stop, immediate on the way back. Do not read this predicate as a claim
    // that the stop is ungated.
    breakerReason === 'credential-rotation'
  );
}

/**
 * ENG-7576: map a dashboard restart's `host_agents.restart_reason` to the
 * breaker reason the poll's restart-consumption stop should record. Only
 * 'integration-change' (the value the integration add/remove path stamps -
 * mirrors host-actions.ts RESTART_REASON_INTEGRATION_CHANGE) maps to a counted
 * provisioning-class reason. 'manual' / null (a pure operator restart) and any
 * unrecognised future reason return undefined = uncounted, preserving the
 * pre-ENG-7576 behaviour for everything that isn't an integration reload - an
 * operator hammering Restart while debugging must not accumulate against the
 * breaker. Exported for unit testing.
 */
export function dashboardRestartBreakerReason(
  restartReason: string | null,
  // ENG-8388: host_agents.restart_actor. 'user' means the API could prove a
  // console user asked for this reload, which routes it to the looser
  // operator-config breaker budget instead of the shared provisioning one.
  // Anything else - including undefined, which is what an API older than the
  // column returns - keeps today's classification exactly.
  restartActor?: string | null,
): RestartReason | undefined {
  if (restartReason !== 'integration-change') return undefined;
  return restartActor === 'user' ? 'operator-integration-change' : 'integration-change';
}

/**
 * ENG-6399: does this restart class need reporting to `audit_log` from the host?
 *
 * A `false` here means "not a restart, or already audited elsewhere", never "not
 * worth recording":
 *  - `undefined`            — the deprovision paths (pause / revoke / remove)
 *                             pass no breaker reason. Those are teardowns, not
 *                             restarts.
 *  - `agent-requested`      — restart-handler already reports it with a much
 *  - `channel-restart-flag`   richer free-text reason (the agent's own words),
 *                             so reporting here too would double-log.
 *  - `integration-change`   — ENG-7576: audited server-side at the API route
 *                             that stamps `host_agents.restart_requested_at`.
 *
 * Everything else — including the whole hot-reload lane (`hot-reload-mcp`,
 * `credential-rotation`, `channel-set-change`, `bind-remediation`,
 * `managed-mcp-churn`) — must be reported.
 *
 * Exported for unit testing: this predicate is the single place both teardown
 * lanes agree on what is already audited, so it must not drift.
 */
export function shouldReportRestartToAudit(breakerReason: RestartReason | undefined): boolean {
  return (
    !!breakerReason &&
    breakerReason !== 'agent-requested' &&
    breakerReason !== 'channel-restart-flag' &&
    breakerReason !== 'integration-change' &&
    // ENG-8388: the operator-attributed variant is the SAME dashboard reload,
    // audited server-side at the same API route. Without this line, attributing
    // a reload to a human would start double-logging it - a fix whose only
    // visible effect is duplicated audit rows.
    breakerReason !== 'operator-integration-change'
  );
}

/**
 * ENG-6399: report a system-initiated restart to `audit_log` via POST
 * /host/restart-event. Best-effort and fire-and-forget — a restart's
 * correctness must never depend on the audit write landing.
 *
 * Extracted so BOTH teardown lanes share one implementation. There are two, and
 * until now only one of them audited:
 *
 *  - `stopPersistentSessionAndForgetMcpBaseline` — auth-tuple-change,
 *    day-rollover, self-update, stale-mcp. Audited since ENG-6399.
 *  - `scheduleSessionRestart` — hot-reload-mcp, credential-rotation,
 *    channel-set-change, bind-remediation, managed-mcp-churn. It calls
 *    `stopPersistentSession` directly and so NEVER reported, meaning every
 *    restart caused by a config / credential / integration change was invisible
 *    to `debug_query_audit_log`.
 *
 * Confirmed on prod before the fix: bob and don were both torn down and
 * respawned (`[hot-reload] Scheduled respawn ... [reason=credential-rotation]`)
 * at 2026-08-02T00:30:20Z and 00:34:27Z, and `debug_query_audit_log` returned
 * zero events for either agent over the following three hours. That blind spot
 * is why ENG-8264 could not be answered from the audit trail: a quiet trail was
 * read as "no restarts are happening" when the trail simply could not see this
 * class.
 *
 * The skip-list is shared deliberately, so the two lanes cannot drift on which
 * reasons are already audited server-side.
 *
 * MUST NOT carry secret material — this lands in an immutable, org-readable
 * row. Note the audit scrubber redacts by KEY NAME (`/token/i`, `/api[_-]?key/i`,
 * `/credential/i`), so an env-var name like `GITHUB_ACCESS_TOKEN` has to travel
 * as a string VALUE under a neutral key, never as the key itself.
 */
function reportRestartEvent(
  codeName: string,
  breakerReason: RestartReason | undefined,
  auditDetail?: { reason?: string; detail?: Record<string, unknown> },
): void {
  if (!shouldReportRestartToAudit(breakerReason)) return;
  const restartAgentId = agentState.codeNameToAgentId.get(codeName);
  if (!restartAgentId) return;
  void api
    .post('/host/restart-event', {
      agent_id: restartAgentId,
      source: breakerReason,
      // ENG-8293: fall back to the source string (the pre-ENG-8293 shape)
      // only when the caller supplied nothing better.
      reason: auditDetail?.reason ?? breakerReason,
      ...(auditDetail?.detail ? { detail: auditDetail.detail } : {}),
    })
    .catch((err) => log(`[restart-event] report failed for '${codeName}': ${(err as Error).message}`));
}

function scheduleSessionRestart(
  codeName: string,
  delayMs: number,
  reason: string,
  // ENG-5441: typed restart reason for the circuit breaker. Defaults to
  // 'hot-reload-mcp' (the .mcp.json drift / integration-change class). The
  // channel-set-change caller now passes its reason explicitly (ENG-6491) so the
  // window gate can exempt it; the parameter stays explicit so a future caller
  // can't silently bypass the breaker by omitting it.
  breakerReason: RestartReason = 'hot-reload-mcp',
  // ENG-6491: optional side effect run EXACTLY ONCE, at the moment the restart
  // actually fires (immediately before stopPersistentSession) — not at schedule
  // time. Used to stamp the channel-add DM-notice marker with the real stop time
  // so a long idle-defer can't leave it stale. Preserved across defer re-arms and
  // coalescing so the deferred restart still runs it.
  beforeStop?: () => void,
  // Structured diagnosis for the `agent.restart` audit row this restart reports
  // once it actually fires. The free-text `reason` above is already the
  // human-readable summary (e.g. "env-only integration change
  // (GITHUB_ACCESS_TOKEN, GITHUB_TOKEN)"); this carries the machine-readable
  // form that `debug_query_audit_log` projects. Threaded through the defer
  // re-arm and stored on the pending entry, so a restart held for the
  // maintenance window does not lose it. Names only, never values — see
  // reportRestartEvent.
  auditDetail?: Record<string, unknown>,
): void {
  const existing = pendingSessionRestarts.get(codeName);
  if (existing) {
    // ENG-6491: don't let a generic restart downgrade an already-scheduled
    // window-exempt one. A channel ADD schedules 'channel-set-change' (exempt),
    // then the same poll's .mcp.json / channel-secret drift watchers schedule a
    // generic 'hot-reload-mcp' that coalesces over it. Keep the exempt restart as
    // scheduled — and KEEP ITS TIMER (don't clear + reschedule), so a drift call
    // with delayMs=0 can't make the add fire immediately and skip the notice delay.
    const keepExistingWindowExempt =
      isWindowExemptRestartReason(existing.breakerReason) &&
      !isWindowExemptRestartReason(breakerReason);
    if (keepExistingWindowExempt) {
      log(
        `[hot-reload] Coalesced restart for '${codeName}': keeping the already-scheduled window-exempt '${existing.reason}' (${existing.breakerReason}); ignoring lower-priority '${reason}' (${breakerReason}) (ENG-6491)`,
      );
      return;
    }
    clearTimeout(existing.timer);
    log(`[hot-reload] Coalesced restart for '${codeName}': replacing pending timer with ${reason}`);
  }
  const timer = setTimeout(() => {
    pendingSessionRestarts.delete(codeName);
    // ENG-5963: hold the restart while outside the maintenance window or the
    // agent is mid-turn (defer-until-idle). Re-arm a re-check rather than
    // restarting now; the marker delete below is skipped on defer so the
    // baseline stays intact. (The delete above already cleared any pending
    // timer, so this re-arm logs no "coalesced" line.)
    const gate = restartGateFor(codeName, breakerReason);
    if (gate !== 'bypass' && gate !== 'proceed') {
      // ENG-6123: a held restart re-checks every 60s; logging each re-check
      // floods manager.log (valai sat deferred for ~50 min = ~50 identical
      // lines). Log on the first defer of a cycle, then at most every
      // DEFER_LOG_THROTTLE_MS, so the held state is still visible without the
      // per-minute spam. The throttle clears once the restart proceeds/cancels.
      const lastLogged = deferLogThrottle.get(codeName);
      if (lastLogged === undefined || Date.now() - lastLogged >= DEFER_LOG_THROTTLE_MS) {
        log(
          `[maintenance-window] Deferring '${reason}' restart for '${codeName}' (${gate}) — re-checking every ${RESTART_DEFER_RECHECK_MS / 1000}s until the window opens`,
        );
        deferLogThrottle.set(codeName, Date.now());
      }
      scheduleSessionRestart(
        codeName,
        RESTART_DEFER_RECHECK_MS,
        reason,
        breakerReason,
        beforeStop,
        // Carry the audit detail across the re-arm: a restart held for the
        // maintenance window must still be able to say what changed when it
        // finally fires.
        auditDetail,
      );
      return;
    }
    deferLogThrottle.delete(codeName);
    // ENG-6203 (CodeRabbit): re-evaluate the breaker at FIRE time for a
    // bind-remediation restart. shouldRemediateUnverifiedRestart checked the
    // breaker when this restart was SCHEDULED, but a bind-remediation fires after
    // a jitter delay during which another restart for this agent can trip the
    // breaker. Stopping now would leave the session down — a tripped breaker
    // blocks the respawn at ensurePersistentSession — which is the exact "stop a
    // session we can't bring back" hazard the schedule-time guard exists to
    // prevent. Abort instead; the session keeps running its current (degraded)
    // tools and the failure stays visible. Scoped to bind-remediation: other
    // restart classes intend to count toward / trip the breaker, and pre-empting
    // their stop would change long-standing behaviour.
    if (breakerReason === 'bind-remediation' && restartBreaker.isTripped(codeName)) {
      log(
        `[bind-remediation] '${codeName}' breaker tripped before the re-respawn fired — ` +
        `aborting to avoid stopping a session the breaker won't let respawn (ENG-6203)`,
      );
      return;
    }
    // ENG-6491: run the fire-time side effect (channel-add DM-notice marker write)
    // immediately before the stop, so the marker timestamp reflects the real
    // restart moment. Best-effort — never let it block the restart.
    if (beforeStop) {
      try {
        beforeStop();
      } catch (err) {
        log(`[hot-reload] beforeStop hook threw for '${codeName}' (non-fatal): ${(err as Error).message}`);
      }
    }
    stopPersistentSession(codeName, log);
    // ENG-8818: close the session handle on THIS lane too. There are two
    // teardown lanes and this is the one that calls stopPersistentSession
    // directly rather than going through
    // stopPersistentSessionAndForgetMcpBaseline — the same asymmetry that made
    // this lane's restarts unreported until ENG-8293. Closing on only the
    // audited lane would leave every scheduled restart (hot-reload,
    // auth-tuple rotation, bind-remediation, channel-set change) still
    // orphaning a row, which is most of the fleet's restart volume.
    // `breakerReason` is a defaulted parameter ('hot-reload-mcp'), so it is
    // always set on this lane — no `??` fallback, which would be a branch that
    // can never be taken.
    closeSessionRunForCode(codeName, 'completed', `session stopped (${breakerReason})`);
    // ENG-7401: stamp the stop for the restart-timing breakdown (no-op unless a
    // measured restart is active for this agent). This isolates the schedule->stop
    // "inject/gate delay" from the stop->bound respawn+MCP-bind cost.
    markRestartStopped(codeName, log);
    // ENG-7771: the stop is now a fact, so the NEXT session reads the
    // freshly-written .mcp.json env — advance the persisted delivery
    // baselines to what the in-memory baselines converged to. Doing this
    // post-stop (never at schedule time) is load-bearing: a manager restart
    // during the idle-defer window must reload the OLD hash so the lost
    // restart gets rescheduled. Generic across restart reasons — any stop
    // delivers the current env, including a channel-set restart that
    // coalesced a same-tick sender_policy change (CodeRabbit PR #1686 path).
    // ENG-7779: the slack/msteams behaviour baselines advance here too, for
    // the same reason — the respawn's MCP children boot with the fresh env.
    {
      const baselineAgentId = agentState.codeNameToAgentId.get(codeName);
      if (baselineAgentId) {
        persistDeliveryBaselineEntry(
          'senderPolicy',
          baselineAgentId,
          agentState.knownSenderPolicyHashes.get(baselineAgentId),
        );
        persistDeliveryBaselineEntry(
          'slackBehaviour',
          baselineAgentId,
          agentState.knownSlackBehaviourHashes.get(baselineAgentId),
        );
        persistDeliveryBaselineEntry(
          'msteamsBehaviour',
          baselineAgentId,
          agentState.knownMsTeamsBehaviourHashes.get(baselineAgentId),
        );
        // ENG-9802: same reason — the respawned MCP children boot with the
        // freshly written telegram chat policy, so delivery is now a fact.
        persistDeliveryBaselineEntry(
          'telegramPolicy',
          baselineAgentId,
          agentState.knownTelegramPolicyHashes.get(baselineAgentId),
        );
        // ENG-9861: and the standing rules. Subtly different from the policy
        // concerns above and worth stating: those are read by an MCP CHILD from
        // its env, this is read by CLAUDE CODE ITSELF from CLAUDE.md at session
        // start. Same delivery moment either way — the respawn is what makes the
        // rendered rules live — which is why it belongs in this same block
        // rather than needing its own hook.
        persistDeliveryBaselineEntry(
          'agentRules',
          baselineAgentId,
          agentState.knownAgentRulesHashes.get(baselineAgentId),
        );
      }
    }
    // ENG-4897: drop the hash baseline when the session actually stops.
    // Without this, the next poll compares the fresh session against the
    // dead one's hash and schedules another spurious restart. Covers the
    // existing hot-reload-restart callers (channel-set change, new MCP
    // servers) that already pre-date the drift check.
    runningMcpHashes.delete(codeName);
    sessionLaunchManagedStructure.delete(codeName); // ENG-7576: no session, no launch anchor
    // ENG-5441: record into the breaker AFTER the actual stop fires so
    // coalesced restarts (two triggers in the same tick collapsed into
    // one timer) only count as one restart event — matching what the
    // operator sees in manager.log.
    recordRestartForBreaker(codeName, breakerReason);
    log(`[hot-reload] Session stopped for '${codeName}' — will respawn with ${reason}`);
    // Report to audit_log, now that the stop is a fact. This lane never did:
    // it calls stopPersistentSession directly rather than the audited teardown,
    // so every hot-reload / credential-rotation / channel-set-change /
    // bind-remediation restart was invisible to debug_query_audit_log. Placed
    // here, after the stop and alongside the breaker record, so a
    // maintenance-deferred restart (which returned early above) is never
    // reported as one — matching the other lane's "only after an ACTUAL
    // teardown" rule. `reason` is the free-text summary the operator sees in
    // manager.log, which is exactly what the audit row should carry.
    reportRestartEvent(codeName, breakerReason, { reason, detail: auditDetail });
    // ENG-6174: arm respawn verification for hot-reload-MCP restarts (the class
    // that exists to bind newly-connected MCP tools). The stop has fired; from
    // here the manager must confirm the respawn actually lands healthy rather
    // than assume it. Preserve any existing attempt count so a re-armed window
    // (verifyPendingRestarts) keeps escalating toward the ERROR rather than
    // resetting to attempt 0 each time. Other restart classes (crash, wedge,
    // auth-rotation) have their own watchers and don't need this binding check.
    // ENG-6203: a 'bind-remediation' re-respawn is itself verified, so a session
    // that still won't bind loops back to `unverified final` — and is capped by
    // the breaker (which this restart counts toward) rather than looping forever.
    // ENG-6491: 'channel-set-change' also connects a new MCP (the channel
    // listener) that must bind, so it needs the same respawn verification as the
    // generic hot-reload-mcp class it used to be tagged as.
    const bindsNewMcp = restartReasonBindsNewMcp(breakerReason);
    if (bindsNewMcp) {
      const prior = pendingRestartVerifications.get(codeName);
      pendingRestartVerifications.set(codeName, {
        firedAt: Date.now(),
        attempts: prior?.attempts ?? 0,
      });
    }
    // ENG-7401 Phase 2: respawn this ONE agent now instead of waiting up to a full
    // poll cycle (~50-110s) for the next health check to notice the session is
    // down. The stop above already fired and .mcp.json was re-rendered by
    // processAgent before it, so the immediate respawn cold-starts with the new
    // config. Flag-gated (ships dark, agt-aws-1 canary first); fire-and-forget and
    // fully backstopped - on any failure the slow poll respawns on its next cycle,
    // exactly as before this change.
    if (bindsNewMcp && hostFlagStore().getBoolean('fast-mcp-restart-respawn')) {
      void respawnAgentAfterMcpStop(codeName, reason);
    }
  }, delayMs);
  // Don't keep the manager alive solely on a pending restart timer.
  timer.unref?.();
  pendingSessionRestarts.set(codeName, { timer, reason, breakerReason, beforeStop, auditDetail });
}

// CodeRabbit on PR #773 (round 2): cancel any pending hot-reload restart
// timer for an agent so a non-hot-reload teardown path (pause / revoke /
// unassign / auth-rotation stop / day-rollover / shutdown / restart-flags)
// doesn't get followed seconds later by a stale timer firing and killing
// the freshly respawned replacement. Idempotent: safe to call when no
// timer is pending. Call this BEFORE every direct stopPersistentSession
// call in this file (the single in-timer call is already self-cleaning
// via the pendingSessionRestarts.delete above).
function cancelPendingSessionRestart(codeName: string): void {
  // ENG-6174: a non-hot-reload teardown (pause / revoke / unassign / shutdown)
  // means the session is intentionally NOT coming back — drop any armed respawn
  // verification so verifyPendingRestarts doesn't later flag a "restart failed"
  // ERROR for an agent we deliberately stopped.
  pendingRestartVerifications.delete(codeName);
  // ENG-7401: this teardown means the restart is intentionally not coming back,
  // so drop any measured timing track rather than leave it dangling for a later
  // unrelated restart to attach to.
  clearRestartTiming(codeName);
  const existing = pendingSessionRestarts.get(codeName);
  if (!existing) return;
  clearTimeout(existing.timer);
  pendingSessionRestarts.delete(codeName);
  deferLogThrottle.delete(codeName); // ENG-6123: reset the defer-log throttle for the next cycle
  log(`[hot-reload] Cancelled pending restart timer for '${codeName}' (another teardown path is handling it)`);
}

// ENG-6203: opt-in self-heal. When a hot-reload restart reaches `unverified
// final` (never respawned healthy / never re-bound its MCP tools), force ONE
// gated re-respawn instead of only logging ERROR. Default OFF — flip per-host
// for canary before fleet-wide.
// ENG-8581: the last persistent-session outcome per agent, so bind-remediation
// can distinguish "this session started and failed to bind its tools" from
// "this session was never allowed to start". Written once per agent per tick,
// beside the [persistent-session-decision] log line.
//
// KEYED ON SHAPE, NOT ON A LIST OF DECISION NAMES. `spawnAttempted === false`
// together with `sessionHealthyAfter === false` means: we did not start a
// session, and there is no healthy one already running. A forced re-respawn
// cannot bind tools in that state, so remediating is incapable of succeeding.
// Enumerating decision strings (`skipped-not-authenticated`,
// `skipped-openrouter-no-key`, …) would be a parallel allowlist that silently
// stops covering every skip reason added after it — the ENG-8231 drift class.
// The shape test picks up new skip reasons for free.
//
// `healthy` is NOT captured: it reports `spawnAttempted: false` but
// `sessionHealthyAfter: true`, so a healthy session whose tools genuinely failed
// to bind still remediates exactly as before. That is the case this must not
// break.
const lastSpawnOutcomeByAgent = new Map<string, { spawnAttempted: boolean; sessionHealthyAfter: boolean }>();

// ENG-7188: the decision STRING, for display only, kept beside the shape map
// rather than folded into it. Deliberately separate so no logic can start
// branching on it — the shape booleans stay the only thing anything decides on,
// which is what keeps a new skip reason covered for free (ENG-8581/ENG-8231).
const lastSpawnDecisionByAgent = new Map<string, string>();

/**
 * ENG-8581 — was this agent's most recent spawn REFUSED before it began, leaving
 * no running session? True only when the manager both declined to spawn and saw
 * no healthy session. Unknown agent (no tick recorded yet) ⇒ false, so the
 * default is to remediate exactly as today: the dangerous direction here is
 * suppressing legitimate remediation, not one extra restart.
 */
/**
 * ENG-7188 — the spawn outcome, shaped for the per-agent heartbeat.
 *
 * `lastSpawnOutcomeByAgent` already holds the fact the console was missing: the
 * manager knows whether it started a session and whether a healthy one was
 * running afterwards. It just never left the host, so an agent skipped at spawn
 * (no OpenRouter key, host cannot authenticate, a per-agent gate) rendered
 * exactly like a healthy one — every signal the console sees is written BY THE
 * MANAGER on the agent's behalf.
 *
 * Returns undefined for an agent with no tick recorded yet, which the console
 * must read as NO SIGNAL rather than "not spawned".
 */
/**
 * ENG-7188 — the ONLY writer of the two spawn-outcome maps.
 *
 * `processAgent` is not the only path that spawns. Both fast respawn lanes
 * (`restart-lane`, `fast-mcp-respawn`) call `ensurePersistentSession` directly
 * and then flush a diagnostics-only heartbeat within seconds — so before this
 * helper existed they published whatever the last slow poll happened to leave in
 * the map, or nothing at all. A successful lane respawn could therefore report
 * an agent as refused, which is the same class of divergence this card is about:
 * one path records the fact, another does not, and nothing fails.
 *
 * An empty `decision` leaves the previous one in place rather than writing a
 * blank — the booleans are what anything decides on, and a stale display string
 * is strictly better than an empty one.
 */
function recordSpawnOutcome(
  codeName: string,
  result: { spawnAttempted: boolean; sessionHealthyAfter: boolean; decision?: string | null },
): void {
  lastSpawnOutcomeByAgent.set(codeName, {
    spawnAttempted: result.spawnAttempted,
    sessionHealthyAfter: result.sessionHealthyAfter,
  });
  if (result.decision) lastSpawnDecisionByAgent.set(codeName, result.decision);
}

function spawnOutcomeForDiagnostics(
  codeName: string,
): { spawnAttempted: boolean; sessionHealthyAfter: boolean; lastDecision: string | null } | undefined {
  const last = lastSpawnOutcomeByAgent.get(codeName);
  if (!last) return undefined;
  return {
    spawnAttempted: last.spawnAttempted,
    sessionHealthyAfter: last.sessionHealthyAfter,
    lastDecision: lastSpawnDecisionByAgent.get(codeName) ?? null,
  };
}

function spawnWasRefusedWithNoSession(codeName: string): boolean {
  const last = lastSpawnOutcomeByAgent.get(codeName);
  if (!last) return false;
  return !last.spawnAttempted && !last.sessionHealthyAfter;
}

function bindRemediationEnabled(): boolean {
  // ENG-7586 (ADR-0022): migrated from a raw AGT_BIND_REMEDIATION_ENABLED env
  // gate to the `bind-remediation` registry flag so it can be flipped
  // centrally/staged from the admin Feature Flags page (like connectivity-probe,
  // ENG-7220). HostFlagStore.getBoolean applies the registry's envVar override
  // automatically, so precedence stays env override > flag value > compiled
  // default (false) — an operator who already pins AGT_BIND_REMEDIATION_ENABLED
  // keeps that value through the migration.
  return hostFlagStore().getBoolean('bind-remediation');
}

// ENG-6203: spread the remediation re-respawn over a small random window so a
// fleet-wide trigger (e.g. a kill-switch flip that stalls many restarts at once)
// can't synchronise every agent's respawn into one thundering-herd tick.
const BIND_REMEDIATION_JITTER_MS = 30_000;

// ENG-7575: first-connect backoff for bind-remediation. An integration created
// within this window whose connectivity is still `transient_error` is treated as
// "still settling first-connect" — bind-remediation defers rather than forcing a
// re-respawn to bind tools that can't bind yet. Older rows are NOT deferred (a
// long-lived integration that genuinely breaks still remediates normally).
//
// ENG-8363: this is now imported, not defined here, because the connectivity
// probe needs the SAME window — it is the boundary at which a missing
// `.mcp.json` key stops reading `transient_error` and starts reading `down`. If
// the probe escalated earlier than the backoff stopped deferring, a row would
// sit in a contradictory state: the probe calling it broken while the remediator
// still called it settling. One constant, two consumers, one env override.
const BIND_REMEDIATION_FIRST_CONNECT_WINDOW_MS = FIRST_CONNECT_WINDOW_MS;
// Hard cap on how many times a terminal remediation may be deferred for one
// restart, so a persistently-flapping first-connect eventually falls through to
// the normal remediation / ERROR path instead of holding forever. Total max hold
// ≈ maxDeferrals × the verify grace window.
const BIND_REMEDIATION_MAX_CONNECTIVITY_DEFERRALS = readBreakerEnvNumber(
  'AGT_BIND_REMEDIATION_MAX_CONNECTIVITY_DEFERRALS',
  3,
);

// ENG-7575: per-agent snapshot of "does this agent have a freshly-added
// integration still flapping on first-connect?", recomputed each poll from the
// integration fetch (which now carries last_connectivity_status + created_at) and
// read by verifyPendingRestarts (which runs after all agents are processed). A
// plain module Map mirrors the other per-agent poll caches in this file. The
// classification itself lives in mcp-restart-verify.ts (computeFirstConnectUnsettled).
const firstConnectUnsettledByAgent = new Map<string, boolean>();

// ENG-6174: each poll, confirm that hot-reload-MCP restarts actually respawned a
// HEALTHY session. Pre-fix the manager stopped the session and assumed success;
// a stalled respawn (the canonical case: one that raced the kill-switch test)
// left the agent on its pre-restart tool set with nothing surfaced — the console
// said "Connected" while the live session never bound the tools. This turns that
// silence into a bounded WARN→ERROR escalation.
//
// The verification decision itself stays OBSERVATIONAL (decidePostRestartVerification
// never forces a respawn — fighting whatever legitimately holds a session down
// would re-create the exact thrashing harm). ENG-6203 layers a SEPARATE, opt-in
// remediation gate on top: only at the TERMINAL failure, and only when the
// breaker is closed, it initiates ONE re-respawn through the same gated
// scheduleSessionRestart path (maintenance-window + breaker honored) — initiating
// a normal gated restart from a settled state, never bypassing a gate.
function verifyPendingRestarts(now: number): void {
  if (pendingRestartVerifications.size === 0) return;
  for (const [codeName, pending] of pendingRestartVerifications) {
    // Probe health FIRST: isSessionHealthy() hydrates a not-yet-tracked tmux
    // session into the session Map with startedAt=now — which is exactly the
    // case when a respawn is first observed on this poll. Reading startedAt
    // before the probe would see null and delay verification by a full poll
    // (CodeRabbit on ENG-6174).
    // ENG-10335: a respawn that landed on the login picker passes
    // isSessionHealthy() (claude is alive) with a post-restart startedAt and a real
    // spawn id, so it would be marked verified and logged "tools bound" when no
    // tool can run. The helper still probes isSessionHealthy() first, preserving
    // the hydration above. Remediation stays off for such a session: its corrected
    // non-spawn verdicts make spawnWasRefusedWithNoSession() true (ENG-8581).
    const healthy = sessionHealthyAndUnblocked(codeName, isSessionHealthy);
    const session = getSessionState(codeName);
    const startedAt = session?.startedAt ?? null;
    // currentSessionId is the spawn identity — null for a zombie rehydrated by
    // the health probe after a failed kill, non-null for a genuine respawn. This
    // is what stops a lingering pre-restart session from being mistaken for a
    // successful respawn (CodeRabbit on ENG-6174).
    const respawnId = session?.currentSessionId ?? null;
    const outcome = decidePostRestartVerification(pending, startedAt, healthy, respawnId, now);
    switch (outcome.kind) {
      case 'verified':
        pendingRestartVerifications.delete(codeName);
        log(`[restart-verify] '${codeName}' respawned healthy after MCP change — tools bound (ENG-6174)`);
        // ENG-7401: success terminal for the restart-timing breakdown.
        finishRestartTiming(codeName, 'tools-bound', undefined, log, now);
        // ENG-7429: the drift/hot-reload restart bound the new MCP tools. Arm a
        // budget of force-due prompt probes so a freshly-added integration's `bound`
        // verdict is written promptly - the signal the Add Integration modal's
        // confirming step waits on - instead of waiting out the ~1h rolling
        // due-interval. Flag-gated so it's inert unless the probe itself is armed.
        // (The dashboard-restart path an add actually takes arms this at its own
        // respawn-healthy point in the poll loop.)
        if (hostFlagStore().getBoolean('session-tool-probe')) {
          promptBindProbeAttempts.set(codeName, PROMPT_BIND_PROBE_ATTEMPTS);
        }
        break;
      case 'waiting':
        // Still inside the grace window — keep watching silently.
        break;
      case 'unverified':
        if (outcome.final) {
          // ENG-7575: before the terminal ERROR + forced re-respawn, back off if a
          // freshly-added integration on this agent is still flapping on
          // first-connect. Forcing a re-respawn to bind tools that can't bind
          // until the connection settles just produces the provisioning-reload
          // churn ENG-7560 exists to bound — so defer (re-arm the window) instead,
          // capped so a persistently-broken integration still surfaces.
          const deferrals = pending.connectivityDeferrals ?? 0;
          if (
            shouldDeferBindRemediationForFirstConnect(outcome, {
              enabled: bindRemediationEnabled(),
              breakerTripped: restartBreaker.isTripped(codeName),
              authSkipped: spawnWasRefusedWithNoSession(codeName),
              firstConnectUnsettled: firstConnectUnsettledByAgent.get(codeName) ?? false,
              deferralsSoFar: deferrals,
              maxDeferrals: BIND_REMEDIATION_MAX_CONNECTIVITY_DEFERRALS,
            })
          ) {
            // Re-arm the verify window WITHOUT bumping `attempts` (the attempt
            // count stays put; the deferral counter is what bounds the hold). Next
            // lapse re-evaluates — once the integration settles (or the cap is
            // hit) this falls through to the normal remediation / ERROR path.
            pendingRestartVerifications.set(codeName, {
              firedAt: now,
              attempts: pending.attempts,
              connectivityDeferrals: deferrals + 1,
            });
            log(
              `[bind-remediation] '${codeName}' deferring forced re-respawn ` +
              `(${deferrals + 1}/${BIND_REMEDIATION_MAX_CONNECTIVITY_DEFERRALS}) — a freshly-added ` +
              `integration is still transient_error; waiting for first-connect to settle before ` +
              `re-binding its tools (ENG-7575)`,
            );
            break;
          }
          // Bounded: emit ONE ERROR then stop watching so a genuinely-stuck
          // agent produces a single greppable line, not a per-poll flood.
          pendingRestartVerifications.delete(codeName);
          log(
            `[restart-verify] ERROR '${codeName}' did NOT respawn healthy after ${outcome.attempt} attempts ` +
            `following an MCP change — the live session may be stuck on its pre-restart tools. ` +
            `Check session health / manager.log; a manual session restart may be required (ENG-6174)`,
          );
          // ENG-7401: failure terminal for the restart-timing breakdown. Bind
          // remediation (below) may schedule one more respawn under a fresh
          // begin()-less track; this closes the original measured attempt.
          finishRestartTiming(codeName, 'failed', `${outcome.attempt} verify attempts`, log, now);
          // ENG-6203: close the loop — force ONE gated re-respawn so the
          // ensure-pass re-reads .mcp.json and binds the tools. Gated on the
          // breaker NOT being tripped (scheduleSessionRestart stops before the
          // breaker records, and a tripped breaker blocks the respawn — so
          // remediating into a tripped breaker would stop a session it can't
          // bring back). The remediation restart counts toward the breaker and
          // is itself verified, so a session that still won't bind degrades to
          // this same visible ERROR after a bounded number of attempts, never an
          // infinite loop. Jittered so a fleet-wide stall doesn't synchronise
          // respawns across this host's agents.
          // ENG-8581: say so when the auth-skip gate is what stopped us, or the
          // suppression is indistinguishable from bind-remediation being off.
          // A silent suppression is the mirror-image defect of the loop it fixes.
          if (bindRemediationEnabled() && spawnWasRefusedWithNoSession(codeName)) {
            log(
              `[bind-remediation] '${codeName}' NOT remediating — the last spawn was refused and no session is ` +
              `running, or the session is blocked on a prompt it cannot answer (e.g. the host cannot authenticate ` +
              `to Claude, ENG-10335). A re-respawn cannot bind tools when nothing ` +
              `can start, so this would loop without the breaker ever seeing it (ENG-8581). Fix the host, not the session.`,
            );
          }
          if (
            shouldRemediateUnverifiedRestart(outcome, {
              enabled: bindRemediationEnabled(),
              breakerTripped: restartBreaker.isTripped(codeName),
              authSkipped: spawnWasRefusedWithNoSession(codeName),
            })
          ) {
            const jitterMs = Math.floor(Math.random() * BIND_REMEDIATION_JITTER_MS);
            log(
              `[bind-remediation] '${codeName}' restart never bound MCP tools — forcing one gated ` +
              `re-respawn in ${Math.round(jitterMs / 1000)}s (breaker-capped, ENG-6203)`,
            );
            scheduleSessionRestart(
              codeName,
              jitterMs,
              'bind-remediation: prior restart left MCP tools unbound',
              'bind-remediation',
            );
          }
        } else {
          // Re-arm a fresh window, carrying the bumped attempt count forward.
          pendingRestartVerifications.set(codeName, { firedAt: now, attempts: outcome.attempt });
          log(
            `[restart-verify] WARN '${codeName}' not yet respawned healthy after MCP change ` +
            `(attempt ${outcome.attempt}) — still watching (ENG-6174)`,
          );
        }
        break;
    }
  }
}

// ENG-5963 (-4): how often to re-evaluate a restart that the maintenance-window
// / defer-until-idle gate is holding, until the gate opens.
const RESTART_DEFER_RECHECK_MS = 60_000;

// ENG-6123: throttle for the per-re-check "Deferring … restart" log line so a
// long-held restart doesn't emit one line every RESTART_DEFER_RECHECK_MS. Keyed
// by codeName → last-logged epoch ms; cleared when the restart proceeds or is
// cancelled so the next defer cycle logs fresh.
const DEFER_LOG_THROTTLE_MS = 600_000;
const deferLogThrottle = new Map<string, number>();

// ENG-5963 (-4): last time the manager handled an inbound message for an agent
// (direct-chat — the manager-visible inbound path; Slack/Telegram are delivered
// by MCP children and surface via pane.log freshness instead). Feeds the
// defer-until-idle gate so a restart never lands right as a message arrives,
// before the agent has produced any pane output yet.
const lastInboundMs = new Map<string, number>();

// ENG-8461: agents that were marked stale-for-today at day rollover while IDLE.
// Instead of eagerly tearing down a healthy idle session at local midnight — a
// full context load (system prompt + CLAUDE.md + every skill + every MCP schema)
// that prints only "Ready." and books the boot as utilization, i.e. 100% waste
// for an agent that never does work (e.g. `ledger`) — we keep the warm session
// running and DEFER the fresh-session mint to the first manager-observable real
// trigger (direct-chat inbound / scheduled firing), which pays the boot alongside
// actual work via performLazyDayRolloverReset(). Cleared when the session next
// respawns for any reason (isStaleForToday flips false → self-heal in the
// rollover block) or when a lazy reset fires. In-memory is sufficient and
// self-healing: on a manager restart the tmux session survives, isStaleForToday
// is still true, and the idle branch re-marks it. The ENG-7548 busy-force path is
// untouched — a bloated busy-yesterday agent is still reset at the grace boundary.
const pendingDayRolloverReset = new Set<string>();

// ENG-8881: how long the inbound gate has actually been holding this agent's
// forced rollover. Bounds the hold (DAY_ROLLOVER_INBOUND_DEFER_MAX_MIN) so a
// continuously chatty agent cannot defer its rollover indefinitely.
//
// ACCUMULATED, not "since the first hold" (CodeRabbit, #4637). The first cut
// stored a single start timestamp and cleared it after the rollover decision.
// `ensurePersistentSession` has TEN early returns before that decision — a
// tripped breaker, auth resolution, a missing API key, an unsupported runtime —
// and any of them leaves the timestamp behind. Wall-clock keeps running while
// nothing is being held, so on resumption the hold reads as already over budget
// and the very next inbound is force-restarted instead of held: the guard stops
// guarding while still looking present.
//
// Clearing on each of those ten paths was the suggested fix. This is preferred
// because it cannot be broken by an eleventh return being added later — time is
// only ever credited for intervals where the gate genuinely chose to hold.
interface InboundHoldState {
  /** Milliseconds this episode has genuinely spent holding. */
  accumulatedMs: number;
  /** When the gate last chose to hold, epoch ms. */
  lastHeldMs: number;
}
const dayRolloverInboundHold = new Map<string, InboundHoldState>();

// A gap longer than this means the gate was not the thing holding the agent, so
// the next hold starts a fresh episode rather than inheriting the elapsed time.
// Comfortably above a poll interval, far below the hold ceiling.
const INBOUND_HOLD_EPISODE_GAP_MS = 120_000;

/**
 * ENG-8881: seconds since this agent last saw channel inbound activity (a message
 * arriving OR being answered), across every channel. Null when it has never had
 * any. See newestPendingInboundActivityMtimeMs for why the directory timestamp is
 * the only signal that survives the drain.
 */
async function channelInboundActivityAgeSecondsFor(codeName: string): Promise<number | null> {
  // Dynamically imported to match how this file already loads the probe module
  // (the wedge block below) — a static import would pull it in on every manager
  // start, including for hosts that never reach either path.
  const { newestPendingInboundActivityMtimeMs } = await import('./responsiveness-probe.js');
  const newest = newestPendingInboundActivityMtimeMs(dirname(paneLogPath(codeName)));
  if (newest === null) return null;
  return Math.max(0, Math.floor((Date.now() - newest) / 1000));
}

/**
 * ENG-8881: minutes the inbound gate has genuinely held this agent's forced
 * rollover, INCLUDING the interval since the last hold if that hold is still
 * part of the same episode. Exported shape kept pure so the decision above stays
 * testable — see `creditInboundHold` for the commit half.
 */
export function inboundHoldMinutes(
  prev: InboundHoldState | undefined,
  nowMs: number,
  episodeGapMs = INBOUND_HOLD_EPISODE_GAP_MS,
): number {
  if (!prev) return 0;
  const gap = Math.max(0, nowMs - prev.lastHeldMs);
  // A long gap is not hold time: the gate was not what was holding the agent.
  if (gap > episodeGapMs) return 0;
  return (prev.accumulatedMs + gap) / 60_000;
}

/** ENG-8881: the state to store after the gate chooses to hold again. */
export function creditInboundHold(
  prev: InboundHoldState | undefined,
  nowMs: number,
  episodeGapMs = INBOUND_HOLD_EPISODE_GAP_MS,
): InboundHoldState {
  const gap = prev ? Math.max(0, nowMs - prev.lastHeldMs) : Infinity;
  const accumulatedMs = !prev || gap > episodeGapMs ? 0 : prev.accumulatedMs + gap;
  return { accumulatedMs, lastHeldMs: nowMs };
}

function inboundHeldMinutesFor(codeName: string): number {
  return inboundHoldMinutes(dayRolloverInboundHold.get(codeName), Date.now());
}

// ENG-6153: per-agent streak of consecutive poll cycles that looked like a
// wedge (inbound waiting + pane not advancing). Reset to 0 the moment a cycle
// no longer qualifies; once it reaches the configured minCycles the manager
// force-fresh respawns the session. Keyed by code_name.
const consecutiveWedgeCycles = new Map<string, number>();

// ENG-6294: agents whose suppressed-wedge episode has already been logged.
// A parent idle-awaiting a `run_in_background` sub-agent matches the wedge
// signature exactly (quiet pane + stale main transcript + queued inbound), so
// the detector exempts it — and logs ONE line per episode here rather than one
// per poll cycle. Cleared when the exemption stops applying (task finished /
// inbound drained / agent left the persistent set).
const wedgeExemptionLogged = new Set<string>();

// ENG-6171: per-(agent,card) consecutive wedge-respawn accounting. When a card
// is in_progress across `threshold` consecutive wedge-respawns it's flagged a
// suspected poison task and the hybrid inject stops re-nudging it (it falls to
// the 30-min stale-auto-fail instead), breaking the respawn loop — while the
// agent's other actionable cards keep being nudged. Outer key = code_name,
// inner key = card id. See lib/wedge-poison-card.ts.
const wedgeRestartsByCard = new Map<string, Map<string, CardWedgeState>>();

/** Stamp an inbound message for `codeName` (called from the direct-chat path). */
function noteInbound(codeName: string): void {
  lastInboundMs.set(codeName, Date.now());
}

/** Age (seconds) since the last manager-seen inbound, or null if none seen. */
function inboundAgeSecondsFor(codeName: string): number | null {
  const ts = lastInboundMs.get(codeName);
  if (ts === undefined) return null;
  return Math.max(0, Math.floor((Date.now() - ts) / 1000));
}

/**
 * ENG-8461 (pure, unit-tested): the day-rollover decision for the supervisor
 * tick. Extracted from ensurePersistentSession so the branch table is testable
 * without a live tmux session / network. Side effects (mark flag, teardown,
 * logging) stay at the call site and dispatch on this verdict.
 *
 *   - 'none'        not stale for today, or session already down, or no current
 *                   entry → nothing to do.
 *   - 'defer-idle'  stale + healthy + idle → KEEP the warm session and defer the
 *                   fresh-session mint to the first real trigger (the ENG-8461
 *                   change; the old behaviour tore down + rebooted here).
 *   - 'force-reset' stale + healthy + busy past the grace boundary → tear down
 *                   anyway to shed bloated context (ENG-7548, unchanged).
 *   - 'busy-wait'   stale + healthy + busy within grace → defer one tick.
 *   - 'defer-device-state'  would force-reset, but the host's `~/.claude.json`
 *                   is not usable, so the respawn would land on the login picker
 *                   (ENG-10351). Keep the working session; re-evaluated every tick.
 */
export type DayRolloverDecision =
  | 'none'
  | 'defer-idle'
  | 'force-reset'
  | 'busy-wait'
  | 'inbound-wait'
  | 'defer-device-state';
export function decideDayRolloverAction(i: {
  staleForToday: boolean;
  sessionHealthy: boolean;
  hasCurrent: boolean;
  idle: boolean;
  overdueMin: number;
  graceMin: number;
  /**
   * ENG-8881: seconds since the agent last had channel inbound ACTIVITY
   * (arrival or drain), or null if unknown / never. Omitted → the gate is off
   * and behaviour is exactly as before.
   */
  recentInboundAgeSec?: number | null;
  /** How fresh that activity must be to hold the forced restart. */
  inboundGraceSec?: number;
  /** Minutes this agent has ALREADY been held by this gate. */
  inboundDeferredMin?: number;
  /** Hard ceiling on that hold, so the gate cannot defer forever. */
  inboundDeferMaxMin?: number;
  /**
   * ENG-10351: can a respawn start on this host right now? Omitted → treated as
   * ready and behaviour is exactly as before. Callers compute it only when the
   * verdict would otherwise be 'force-reset', so a healthy host pays no file read.
   */
  deviceStateReady?: boolean;
}): DayRolloverDecision {
  if (!i.staleForToday || !i.sessionHealthy || !i.hasCurrent) return 'none';
  if (i.idle) return 'defer-idle';
  if (i.overdueMin < i.graceMin) return 'busy-wait';

  // ENG-8881: the forced restart is correct in general and catastrophic in one
  // specific instant — the seconds between an agent accepting work from a human
  // and writing the kanban row that makes the promise durable. A restart there
  // loses the work with no card, no alert and no user-visible error, because
  // every recovery path we have is card-based.
  //
  // A restart that has already waited hours can wait one more minute.
  //
  // BOUNDED, because the branch immediately above exists precisely to stop an
  // always-busy agent deferring its rollover forever (ENG-7548, Paige pinned at
  // 99% context). An agent in continuous conversation would keep this signal
  // fresh indefinitely, so without the ceiling this gate would quietly reopen
  // the hole it sits next to.
  const age = i.recentInboundAgeSec;
  const graceSec = i.inboundGraceSec ?? 0;
  const heldMin = i.inboundDeferredMin ?? 0;
  const maxHoldMin = i.inboundDeferMaxMin ?? 0;
  if (age !== null && age !== undefined && age < graceSec && heldMin < maxHoldMin) {
    return 'inbound-wait';
  }
  // ENG-10351: UNBOUNDED, unlike the inbound hold above, and that is the point.
  // ENG-7548 forces a reset because a session pinned near its context limit is
  // worse than one interrupted turn. A session that cannot start at all is worse
  // than both: tearing it down converts a bloated-but-working agent into a dead
  // one. The hold ends the tick the file is usable again, not after a timer.
  if (i.deviceStateReady === false) return 'defer-device-state';
  return 'force-reset';
}

/**
 * ENG-8461 (pure, unit-tested): gating verdict for performLazyDayRolloverReset.
 *
 *   - 'skip'        not flagged, or no current entry, or mid-turn → do nothing
 *                   (keep any flag; a later idle trigger retries).
 *   - 'clear-stale' flagged but the deferred reset is already moot → the session
 *                   is down (respawning) OR it is no longer stale for today (a
 *                   respawn for any other reason already minted today's UUID);
 *                   drop the flag.
 *   - 'reset'       flagged + still-stale + healthy + idle → tear the warm session
 *                   down now.
 *
 * The `staleForToday` gate closes a window (CodeRabbit, PR #4126): the flag is set
 * at an idle midnight, but an unrelated respawn (auth rotation / wedge /
 * self-update) can mint today's UUID before the poll-tick self-heal clears the
 * flag. The async direct-chat path calls this helper between poll ticks, so
 * without a staleness check it would tear down a brand-new today-dated session —
 * the exact boot waste this ticket removes. Checking staleness here (not only at
 * the poll-tick self-heal) makes the helper safe from any caller.
 */
export type LazyResetOutcome = 'skip' | 'clear-stale' | 'reset' | 'defer-device-state';
export function classifyLazyDayRolloverReset(i: {
  flagged: boolean;
  staleForToday: boolean;
  sessionHealthy: boolean;
  hasCurrent: boolean;
  idle: boolean;
  /**
   * ENG-10351: can a respawn start on this host? Omitted → ready. When false, a
   * would-be 'reset' becomes 'defer-device-state': keep the warm session and keep
   * the flag, so the next real trigger asks again.
   */
  deviceStateReady?: boolean;
}): LazyResetOutcome {
  if (!i.flagged) return 'skip';
  if (!i.sessionHealthy) return 'clear-stale';
  if (!i.staleForToday) return 'clear-stale';
  if (!i.hasCurrent) return 'skip';
  if (!i.idle) return 'skip';
  return i.deviceStateReady === false ? 'defer-device-state' : 'reset';
}

/**
 * ENG-10351: one host-wide gate per manager. Every agent on the host respawns
 * from the same `~/.claude.json`, so holds are recorded here and reported as a
 * single heartbeat verdict rather than per agent.
 */
const deviceStateRotationGate = new DeviceStateRotationGate();

/**
 * ENG-10724: the hold key the SELF-UPDATE path passes to `deviceStateRotationGate`.
 *
 * `mayRotate`'s first argument is only a dedup key for "log this hold once per
 * episode", so a sentinel is safe. It deliberately shares the gate INSTANCE with
 * the day-rollover callers rather than taking its own: the condition is one
 * host-wide file, so a single `usable` reading must clear BOTH kinds of hold at
 * once, and the hold must reach `heartbeatField()` - a host silently deferring
 * its upgrades forever is the failure this replaces, not one to introduce.
 *
 * Shaped so it cannot be mistaken for a code_name in `deferred_agents`.
 */
const SELF_UPDATE_DEVICE_STATE_HOLD_KEY = '(self-update)';

/**
 * ENG-10724: never APPLY a CLI upgrade while the host's `~/.claude.json` is
 * unusable AND this host has live Claude Code sessions to lose.
 *
 * WHY THE UPGRADE IS THE DANGEROUS PART. The upgrade ends in a manager restart,
 * which rebuilds every isolation container on the host, and each one
 * bind-mounts that single host file (`persistent-session.ts`: "Shared across
 * agents today (all run as root, HOME=/root)"). When the bind SOURCE is missing
 * Docker creates a DIRECTORY at that path; Claude Code then finds no config,
 * runs first-run onboarding, the manager auto-answers the theme picker, and
 * every agent on the host parks on the login picker permanently. Measured
 * 2026-09-19: seven agents across three customer orgs lost exactly that way as
 * their staggered maintenance windows came around.
 *
 * ENG-10351 built the check and the gate, host-wide, over this same file - for
 * the DAY ROLLOVER teardown. It was simply never consulted here, so of the two
 * triggers that respawn a session we gated one and left the other open.
 *
 * THE PREDICATE, AND WHY IT IS NOT THE GATE'S JOB. The gate answers one
 * question: is the file usable. Only the caller knows whether an unusable file
 * MATTERS. For a rollover it always does - you are tearing down a session that
 * is working right now. For the self-update it matters only on a host that is
 * supposed to have the file at all, and `checkAndUpdateCli` runs on EVERY host
 * unconditionally where `deviceStateAllowsRollover` is only ever reached from
 * the teardown of a live session. Obi Wan on #6250 priced the difference:
 * `~/.claude.json` is written by the pairing flow, so it is legitimately absent
 * on a bootstrapped host that has not been paired and on a host running only
 * non-Claude-Code agents. Without a predicate those hosts latch a host-wide
 * hold within five minutes of manager start, page the global ops ladder on
 * every heartbeat (the alert is critical and team-less by design), and refuse
 * every CLI upgrade including an urgent dist-tag hotfix - for a file they were
 * never supposed to have. That is worse than the bug this change fixes.
 *
 * `hostShouldHaveClaudeDeviceState` is the caller-side half, and its own
 * docblock carries the reasoning and the three versions it took to get right -
 * two of them wrong in opposite directions, both caught by Obi Wan on #6250.
 * The short version: the question is durable, so the answer must not come from
 * process-local memory, because a MANAGER RESTART is exactly the event this code
 * exists for. It reads the host's `claude_auth_mode` and, in subscription mode
 * only, the presence of the credentials file the pair flow writes.
 *
 * What this costs, stated rather than buried: a host whose Claude Code sessions
 * are ALREADY all on the login picker still holds, where an earlier version let
 * the upgrade through on the argument that there was nothing left to lose.
 * Failing closed is what the reviewer asked for and it is right anyway - the
 * remedy is to pair the host, the alert says exactly that, and it clears on the
 * next heartbeat.
 *
 * Returns `'failed'` when the caller must abandon the upgrade, or `null` when it
 * may proceed. `'failed'` and not `'noop'`: `shouldConsumeForcedUpdate`
 * (`update-window-gate.ts`) treats every non-`'failed'` outcome as a genuine
 * completion, so a `'noop'` lets `runPendingForcedUpdate` persist
 * `lastUpdateRequestProcessedAt` and ack the operator's "Update CLI now" one-shot
 * away while the console reports success for an upgrade that never ran - the
 * ENG-6771 failure mode. Nothing was installed; the request must survive to retry.
 *
 * WHERE IT IS ASKED: immediately before each command that actually replaces the
 * binary, and nowhere else. An earlier draft also asked at the top of
 * `checkAndUpdateCli`; that call is gone, because at that point we do not yet
 * know whether an upgrade exists, whether this host can install one, or which
 * installer would run - so it read the multi-MB config on the poll loop of every
 * healthy host, latched holds on hosts that can never install anything, and made
 * the operator-facing alert claim a deferred upgrade that in the common case did
 * not exist. Here, both callers have already established that an upgrade is
 * available and about to be applied, so everything the alert says is true.
 *
 * LOGGING, stated honestly because an earlier version of this comment was not. A
 * hold is announced once per episode, and once more the first time a FORCED call
 * is held - the routine path will normally have claimed `firstHold` already, and
 * an operator who clicked "Update CLI now" would otherwise get no reason at all.
 * `force` skips the throttle (ENG-6692) and `'failed'` deliberately keeps the
 * one-shot alive, so `runPendingForcedUpdate` re-kicks this path every poll for
 * as long as the file stays broken; without the second latch that is a log line
 * every ten seconds. Both latches clear when the file comes back.
 */
let selfUpdateForcedHoldAnnounced = false;

/**
 * ENG-10724: state for ONE "Update CLI now" request, so a hold that persists
 * until a human pairs the host does not turn the forced path into a per-poll
 * log flood and a per-poll `brew update` + `brew outdated`.
 *
 * Before this change every non-consumed outcome was TRANSIENT - a registry
 * timeout, a failed install - so `runPendingForcedUpdate` re-kicking every poll
 * was bounded and needed no latch at all. A device-state hold is the first
 * `'failed'` that is not: `'failed'` deliberately keeps the one-shot alive
 * (ENG-6771), so without this the request is re-kicked every 10s indefinitely.
 *
 * Keyed on `requestedUpdateAt`, NOT just the outcome. ENG-8449 already treats
 * that as a bug class one latch over (`forcedUpdateRelaxAnnounced` re-arms "so a
 * LATER request that starves again says so instead of staying silent on the
 * latch"), and Obi Wan on #6250 caught the same shape here: gate both log lines
 * on an outcome-only latch and a SECOND operator click while the first request
 * is still failing produces no log line at all - the only place the new
 * `requestedUpdateAt` is printed is the line the latch suppresses.
 */
let forcedUpdateRequestLatch: {
  requestedAt: string;
  lastOutcome: SelfUpdateOutcome | null;
  lastAttemptMs: number;
} | null = null;

/** Convenience for the two log guards: the outcome this request last reported. */
function lastForcedOutcomeFor(requestedAt: string | null): SelfUpdateOutcome | null {
  return requestedAt !== null && forcedUpdateRequestLatch?.requestedAt === requestedAt
    ? forcedUpdateRequestLatch.lastOutcome
    : null;
}

/**
 * ENG-10724: latched like the other two lines on this path. `hostShouldHaveClaudeDeviceState`
 * runs FIRST on every forced re-kick, so an unlatched line here floods exactly
 * the loop the other latches exist to quiet - three lines in that loop, and a
 * test claiming "BOTH forced log lines are latched" was asserting a completeness
 * that did not hold (Obi Wan on #6250). Cleared as soon as the verdict changes.
 */
let credentialsUnreadableAnnounced = false;

/**
 * ENG-10724: thin adapter over `deviceStateHoldApplies`. The decision lives in
 * `self-update-device-state-hold.ts` so it can be tested by its OUTCOME rather
 * than its shape - Obi Wan on #6250 pointed out that inverting the subscription
 * branch's two returns left every assertion here green while the predicate did
 * the exact opposite of its job. All this does is fetch the three inputs.
 */
async function hostShouldHaveClaudeDeviceState(): Promise<boolean> {
  const verdict = deviceStateHoldApplies({
    authMode: getCachedClaudeAuthMode(),
    credentialPaths: await findClaudeCredentialsPaths(),
  });
  const unreadable = verdict.applies && verdict.because === 'credentials-unreadable';
  if (!unreadable) credentialsUnreadableAnnounced = false;
  if (unreadable && !credentialsUnreadableAnnounced) {
    credentialsUnreadableAnnounced = true;
    // The one verdict worth a line: it is indistinguishable from "never paired"
    // in the outcome, and it means a filesystem problem an operator can fix.
    // No path or errno is echoed - the paths are well known and the errno is not
    // worth the risk of leaking one that is not.
    log(
      '[self-update] could not determine whether this host is paired: a Claude credentials path exists but '
      + 'could not be stat\'d. Holding the CLI upgrade rather than guessing. Check the permissions on '
      + '~/.claude. [ENG-10724]',
    );
  }
  return verdict.applies;
}

async function selfUpdateDeviceStateHold(force: boolean): Promise<'failed' | null> {
  if (!(await hostShouldHaveClaudeDeviceState())) return null;
  const deviceState = deviceStateRotationGate.mayRotate(SELF_UPDATE_DEVICE_STATE_HOLD_KEY, Date.now());
  if (deviceState.ready) {
    selfUpdateForcedHoldAnnounced = false;
    return null;
  }
  const announceForced = force && !selfUpdateForcedHoldAnnounced;
  if (deviceState.firstHold || announceForced) {
    if (announceForced) selfUpdateForcedHoldAnnounced = true;
    log(
      `[self-update] HELD${force ? ' (forced "Update CLI now")' : ''} - the host's ~/.claude.json is not `
      + `usable (${deviceState.reason}) and this host is paired for Claude Code. A CLI upgrade ends in a `
      + 'manager restart, which would rebuild every isolation container on this host against the missing '
      + 'file and park every agent on the login picker. Keeping the current binary; the upgrade applies '
      + 'on the first check after the file is usable again. Pair the host from the Hosts page. '
      + '(further holds this episode are not logged) [ENG-10724]',
    );
  }
  return 'failed';
}

/**
 * ENG-10351: ask the gate on behalf of a rollover teardown, logging once per
 * hold episode per agent. Returns true when the teardown may proceed.
 */
function deviceStateAllowsRollover(codeName: string, path: 'force-reset' | 'lazy-reset'): boolean {
  const gate = deviceStateRotationGate.mayRotate(codeName, Date.now());
  if (gate.ready) return true;
  if (gate.firstHold) {
    log(
      `[persistent-session] Day rollover for '${codeName}' HELD (${path}) — the host's ~/.claude.json is ` +
        `not usable (${gate.reason}), so a fresh session would land on the login picker. Keeping the ` +
        `working session; the rotation runs the first tick the file is usable again. Pair the host ` +
        `from the Hosts page. (further holds this episode are not logged) [ENG-10351]`,
    );
  }
  return false;
}

/**
 * ENG-8461 (shared): resolve an agent's effective day-boundary timezone from its
 * raw tier inputs, applying the ENG-6695 precedence (agent override → team →
 * org-effective maintenance-window tz) with the ENG-8016 override-TTL lapse, and
 * returning `undefined` (host-local, ENG-4642) when no tier is configured.
 *
 * Extracted so the poll block (fresh /host/refresh inputs) and the async
 * direct-chat lazy-reset path (the cached inputs) compute the day boundary against
 * the SAME clock. Without a single resolver, a tz-mismatched agent's direct-chat
 * isStaleForToday could disagree with the poll's and re-open the stale-session
 * teardown window the PR #4126 staleness guard closes.
 */
function resolveEffectiveAgentTimezone(inputs: {
  agentTimezone: string | null;
  teamTimezone: string | null;
  agentTimezoneExpiresAt: string | null;
}): string | undefined {
  const orgTierTz = cachedMaintenanceWindow?.timezone ?? null;
  const ownTzEffective = isAgentTimezoneOverrideExpired(inputs.agentTimezoneExpiresAt)
    ? null
    : inputs.agentTimezone;
  if (
    isUnsetTimezone(ownTzEffective) &&
    isUnsetTimezone(inputs.teamTimezone) &&
    isUnsetTimezone(orgTierTz)
  ) {
    return undefined;
  }
  return resolveAgentTimezone(
    ownTzEffective,
    inputs.teamTimezone,
    orgTierTz,
    inputs.agentTimezoneExpiresAt,
  );
}

/**
 * ENG-8461: the agent's effective day-boundary timezone read from the cached
 * restart tz inputs (populated every poll from /host/refresh), for callers OUTSIDE
 * the poll such as the async direct-chat handler. `undefined` = host-local, and
 * also returned when the agent hasn't been seen by a poll yet.
 */
function cachedAgentTimezoneFor(codeName: string): string | undefined {
  const inputs = agentRestartTimezoneInputs.get(codeName);
  if (!inputs) return undefined;
  return resolveEffectiveAgentTimezone({
    agentTimezone: inputs.agentTimezone,
    teamTimezone: inputs.teamTimezone,
    agentTimezoneExpiresAt: inputs.agentTimezoneExpiresAt,
  });
}

/**
 * ENG-8461: perform the day-rollover fresh-session reset that was DEFERRED at
 * local midnight because the agent was idle (see the idle branch in
 * ensurePersistentSession). Called from the first manager-observable real trigger
 * — a direct-chat inbound or a due scheduled task — so the boot is paid alongside
 * actual work instead of at an idle midnight for nothing.
 *
 * Tears down the warm (yesterday-dated) session so the next ensure-session pass
 * respawns it, minting today's UUID via getOrCreateDailySession. Idempotent and
 * self-gating via classifyLazyDayRolloverReset():
 *   - no-op (returns false) unless the agent is flagged pendingDayRolloverReset;
 *   - clears the flag and returns false if the session is already gone (a respawn
 *     for any other reason already minted today's UUID — nothing left to reset);
 *   - returns false WITHOUT tearing down if the agent is mid-turn (not idle), so a
 *     real trigger never interrupts an in-flight turn; the flag stays set and the
 *     next idle trigger (or the ENG-7548 busy-force path) handles it.
 *
 * Returns true iff it actually tore the session down. Callers use that to skip
 * their normal delivery/firing and let the fresh session pick the work up via the
 * existing durable-replay (direct chat) / stays-ready (scheduler) paths.
 *
 * 'day-rollover' is not in GATEABLE_RESTART_REASONS, so the teardown bypasses the
 * maintenance-window gate and counts one restart against the breaker — byte-for-
 * byte the old idle-rollover teardown, only paid later.
 */
function performLazyDayRolloverReset(
  codeName: string,
  agentTimezone?: string | null,
): boolean {
  // Cheap-first: do the reads only when still flagged so an unflagged agent pays
  // nothing on the hot direct-chat path. staleForToday guards the between-tick
  // window where an unrelated respawn already minted today's UUID (PR #4126).
  const flagged = pendingDayRolloverReset.has(codeName);
  const sessionHealthy = flagged && isSessionHealthy(codeName);
  const staleForToday = flagged
    ? isStaleForToday(codeName, new Date(), agentTimezone ?? undefined)
    : false;
  const current =
    flagged && sessionHealthy && staleForToday ? peekCurrentSession(codeName) : null;
  const lazyInputs = {
    flagged,
    staleForToday,
    sessionHealthy,
    hasCurrent: current !== null,
    idle: current ? isAgentIdle(psGetProjectDir(codeName), current.sessionId) : false,
  };
  // ENG-10351: the device-state read happens only when a teardown is actually
  // about to happen, so the hot direct-chat path pays nothing for it.
  let outcome = classifyLazyDayRolloverReset(lazyInputs);
  if (outcome === 'reset' && !deviceStateAllowsRollover(codeName, 'lazy-reset')) {
    outcome = classifyLazyDayRolloverReset({ ...lazyInputs, deviceStateReady: false });
  }
  if (outcome === 'clear-stale') {
    pendingDayRolloverReset.delete(codeName);
    return false;
  }
  // 'defer-device-state' keeps pendingDayRolloverReset set, so the next real
  // trigger asks again.
  if (outcome !== 'reset' || !current) return false;
  log(
    `[persistent-session] Day rollover for '${codeName}' (yesterday=${current.date}) — first real ` +
      `trigger after an idle rollover; minting a fresh session now (deferred boot, paid alongside ` +
      `real work) [ENG-8461]`,
  );
  stopPersistentSessionAndForgetMcpBaseline(codeName, 'day-rollover');
  agentState.persistentSessionAgents.delete(codeName);
  // The teardown released the pipe-pane `cat` fd; rotate the overnight pane.log
  // now, mirroring the poll-path rotation at the midnight teardown (ENG-8340).
  rotatePaneLogForDayRollover(codeName, log, agentTimezone);
  pendingDayRolloverReset.delete(codeName);
  return true;
}

/**
 * Age (seconds) of the agent's pane.log — our primary idle signal. Claude
 * writes a line on every output (including the in-place progress heartbeats),
 * so a fresh pane.log means the agent is mid-turn / recently active.
 *
 * Returns null ONLY when the pane.log genuinely doesn't exist yet (ENOENT —
 * a never-spawned agent, nothing to interrupt). Any OTHER stat failure
 * (transient I/O, permissions) returns 0 — "treat as just-active" — so a read
 * blip fails safe toward NOT interrupting rather than toward a restart.
 */
function paneLogAgeSecondsFor(codeName: string): number | null {
  try {
    const mtimeMs = statSync(paneLogPath(codeName)).mtimeMs;
    return Math.max(0, Math.floor((Date.now() - mtimeMs) / 1000));
  } catch (err) {
    if ((err as NodeJS.ErrnoException)?.code === 'ENOENT') return null;
    return 0;
  }
}

/**
 * ENG-6264: age (seconds) of the running session's transcript JSONL — the
 * primary "is the model actually producing tokens" signal (ENG-6238). Returns
 * null when there's no live session id or the file can't be stat'd, in which
 * case the gate falls back to pane.log age. Pure mtime read; mirrors
 * paneLogAgeSecondsFor + the wedge detector's transcript signal.
 */
function transcriptAgeSecondsFor(codeName: string): number | null {
  const sessionId = getSessionState(codeName)?.currentSessionId ?? null;
  return transcriptActivityAgeSeconds(ccGetProjectDir(codeName), sessionId, new Date());
}

/**
 * ENG-5963: resolve the restart gate for a (codeName, reason). Returns
 * 'bypass' for reasons that aren't gateable (deprovision / dashboard / auth
 * recovery / day-rollover / channel-restart-flag) so the caller restarts
 * immediately as before; otherwise the window + defer-until-idle decision
 * (transcript/pane AND inbound both quiet) against the cached window.
 *
 * ENG-6264: `reason` is the GATE reason, which may differ from the breaker-count
 * reason — the single-MCP reaper restart is gateable ('mcp-presence-reaper')
 * even though it does not count against the breaker (see
 * stopPersistentSessionAndForgetMcpBaseline).
 */
/**
 * ENG-8224: consecutive gate decisions that did NOT let a restart through, keyed
 * by `agent::gate-reason`. Feeds `decideRestartGate`'s window-escalation ceiling.
 *
 * Reset ONLY on 'proceed'/'bypass' — i.e. when the restart actually happens.
 * Deliberately NOT reset on 'defer-idle': once escalation has dropped the window
 * axis the decision becomes 'defer-idle' or 'proceed', so resetting on
 * 'defer-idle' would re-arm the window axis on the next poll and the pair would
 * oscillate at the threshold, never escalating for real. Counting every defer
 * keeps escalation sticky until the repair lands.
 *
 * Escalating on a long run of genuine 'defer-idle' is harmless: escalation drops
 * the window axis only, and a busy agent keeps deferring on the idle axis.
 */
const restartGateDeferStreaks = new Map<string, number>();

function restartGateFor(
  codeName: string,
  reason: RestartReason | undefined,
): 'bypass' | 'proceed' | 'defer-window' | 'defer-idle' {
  if (!isGateableRestartReason(reason)) return 'bypass';
  // ENG-6695: interpret the host-level maintenance window in THIS agent's
  // resolved timezone (agent override → team → the cached window's host/org
  // -effective tz), so a disruptive restart defers to the OPERATOR's local
  // off-peak, not the host default. When the agent has no override and no team
  // timezone, resolveAgentTimezone returns the cached window tz unchanged —
  // byte-for-byte today's behaviour.
  const tzInputs = agentRestartTimezoneInputs.get(codeName);
  const perAgentWindow = cachedMaintenanceWindow
    ? {
        ...cachedMaintenanceWindow,
        timezone: resolveAgentTimezone(
          tzInputs?.agentTimezone ?? null,
          tzInputs?.teamTimezone ?? null,
          cachedMaintenanceWindow.timezone,
          tzInputs?.agentTimezoneExpiresAt ?? null,
        ),
      }
    : cachedMaintenanceWindow;
  // ENG-8224: feed the accumulated defer streak so the window axis can't hold a
  // repair forever. dwight sat behind `defer-window` for 7.7h / ~800 checks with
  // a dead anchor-browser MCP, looking healthy the whole time.
  const streakKey = `${codeName}::${reason}`;
  const priorStreak = restartGateDeferStreaks.get(streakKey) ?? 0;
  const decision = decideRestartGate({
    window: perAgentWindow,
    // ENG-6491: channel adds (channel-set-change) skip the off-peak window but
    // still defer-until-idle — they come online as soon as the agent is quiet.
    windowExempt: isWindowExemptRestartReason(reason),
    windowDeferStreak: priorStreak,
    paneLogAgeSeconds: paneLogAgeSecondsFor(codeName),
    transcriptAgeSeconds: transcriptAgeSecondsFor(codeName),
    inboundAgeSeconds: inboundAgeSecondsFor(codeName),
    now: new Date(),
  });
  // decideRestartGate never returns 'bypass' — that early-returns above for a
  // non-gateable reason, which never reaches (or needs) a streak.
  const nextStreak = nextWindowDeferStreak(priorStreak, decision);
  if (nextStreak === 0) restartGateDeferStreaks.delete(streakKey);
  else restartGateDeferStreaks.set(streakKey, nextStreak);
  // Announce the escalation exactly once, on the poll that crosses the bar, so
  // the transition is greppable. Acceptance criterion 1 of ENG-8224 is that a
  // deferral never repeats SILENTLY forever — the ~800 identical defer lines
  // that preceded this fix said nothing about escalating because nothing did.
  // Gated on an actual increment so a carried streak can't re-log every poll.
  if (nextStreak === RESTART_WINDOW_DEFER_ESCALATE_AFTER && nextStreak !== priorStreak) {
    log(
      `[maintenance-window] '${codeName}': '${reason}' restart deferred ${nextStreak}× by the ` +
      `window — dropping the maintenance-window wait (ENG-8224). Still defer-until-idle: ` +
      `it will fire on the next quiet gap, never mid-turn.`,
    );
  }
  return decision;
}

/**
 * ENG-6692: is ANY agent on this host mid-turn / recently active? The forced
 * "Update CLI now" self-update bypasses the maintenance window + throttle +
 * coalesce, but must still never interrupt a live conversation — the manager
 * restart it triggers tears down every agent session on the host. So it's
 * host-level: busy if a SINGLE agent is busy. Reuses decideRestartGate's idle
 * axes (transcript/pane progress AND inbound quiet) with `window: null` so only
 * the defer-until-idle gate applies (no window check).
 *
 * `env: {}` is passed deliberately: AGT_MAINTENANCE_WINDOW_DISABLE short-circuits
 * decideRestartGate to 'proceed', but ENG-6692 (AC: "defer-until-idle is still
 * respected") bypasses only the WINDOW, never the idle-safety gate — so a host in
 * incident-override mode still won't have a live customer turn interrupted by the
 * forced update. The empty env makes the idle check ignore that flag.
 */
function isHostBusyForForcedUpdate(opts?: { relaxed?: boolean }): boolean {
  const now = new Date();
  for (const agent of state.agents) {
    const decision = decideRestartGate({
      window: null,
      paneLogAgeSeconds: paneLogAgeSecondsFor(agent.codeName),
      transcriptAgeSeconds: transcriptAgeSecondsFor(agent.codeName),
      // ENG-8079: past the defer-streak threshold, drop the inbound-quiet axis
      // and gate on progress alone. Passing null means "no signal" for that axis
      // (see decideRestartGate), so only transcript/pane progress governs — an
      // agent mid-turn still defers, an agent that merely received a message in
      // the last 5 minutes no longer does. On a 14-agent host the inbound axis
      // is what made the gate un-satisfiable in practice.
      inboundAgeSeconds: opts?.relaxed ? null : inboundAgeSecondsFor(agent.codeName),
      now,
      env: {},
    });
    if (decision === 'defer-idle') return true;
  }
  return false;
}

/**
 * ENG-8449: render how long a forced update has been pending, for the log lines
 * that an operator greps when a request looks stuck. Null (no request, or an
 * unparseable timestamp) prints as `unknown` rather than a fake `0m`.
 */
function describePendingFor(pendingForMs: number | null): string {
  if (pendingForMs === null) return 'unknown';
  const totalSeconds = Math.floor(pendingForMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  return `${Math.floor(totalSeconds / 60)}m${String(totalSeconds % 60).padStart(2, '0')}s`;
}

/**
 * ENG-8079: run a pending "Update CLI now" one-shot, from the TOP of the poll
 * cycle. Returns true when it kicked the updater (so the caller skips the
 * routine check rather than colliding on `selfUpdateInFlight`).
 *
 * This is the ENG-6692 consume block, relocated. It previously ran inline in the
 * heartbeat response handler; everything about the dedup, the idle gate and the
 * ENG-6771 outcome-based consume is unchanged. What changed is WHEN: the forced
 * `fetch` to the npm registry now runs while the cycle awaits its own heartbeat
 * POST, instead of behind the per-agent `execFileSync` sweep that blocked the
 * event loop long enough for `AbortSignal.timeout(10_000)` to fire 22-70s late
 * on every single attempt.
 */
function runPendingForcedUpdate(): boolean {
  const requestedUpdateAt = cachedUpdateRequestedAt;
  // ENG-8449: the escalation tiers are decided in decidePendingForcedUpdate, not
  // here — ageing the request, deriving the relaxed/past-deadline flags and
  // ordering them against the dedup check are all things this call site used to
  // do by hand, and none of it was reachable by a test. `isHostBusy` is passed as
  // a callback because relaxed-ness has to be known before the busy check runs:
  // it decides which axes that check consults.
  const {
    decision: forcedUpdate,
    pendingForMs,
    relaxed,
  } = decidePendingForcedUpdate({
    requestedAt: requestedUpdateAt,
    lastProcessedAt: state.lastUpdateRequestProcessedAt ?? null,
    deferStreak: forcedUpdateDeferStreak,
    nowMs: Date.now(),
    isHostBusy: (isRelaxed) => isHostBusyForForcedUpdate({ relaxed: isRelaxed }),
  });

  if (forcedUpdate !== 'defer-idle') {
    forcedUpdateDeferStreak = 0;
    // ENG-8449: re-arm the announcement with the streak, so a LATER request
    // that starves again says so instead of staying silent on the latch.
    forcedUpdateRelaxAnnounced = false;
  }

  if (forcedUpdate === 'none' || forcedUpdate === 'already-handled') return false;

  // ENG-10724: a NEW request re-arms everything. Without this a second click
  // while the first is still failing is completely silent, because the only line
  // that prints the new `requestedUpdateAt` is the one the latch suppresses.
  if (forcedUpdateRequestLatch && forcedUpdateRequestLatch.requestedAt !== requestedUpdateAt) {
    forcedUpdateRequestLatch = null;
    selfUpdateForcedHoldAnnounced = false;
  }

  // ENG-10724: back off while the SAME request keeps reporting the SAME
  // non-consumed outcome. The request stays pending - `'failed'` keeping the
  // one-shot alive is ENG-6771's contract and is not what is being changed - but
  // it is retried once per UPDATE_CHECK_INTERVAL_MS instead of once per poll.
  // What that actually saves is the work BEHIND the retry: `brew update --quiet`
  // (60s, a full tap refresh) + `brew outdated` (30s), or two registry fetches
  // plus the urgent dist-tag fetch, every ten seconds, for as long as the host
  // stays unpaired.
  //
  // Bounded HERE, at the loop, and not by an early check inside
  // `checkAndUpdateCli`: an earlier attempt at that put the hold above the
  // dev-mode return, the install-method detection, the pin gates and
  // `brew outdated`, so a host that would have installed NOTHING latched the
  // shared gate and opened a critical alert claiming a deferred upgrade that did
  // not exist - the exact placement the hold's own docblock says was removed, and
  // Obi Wan caught it the round it shipped. The retry cadence is the thing that
  // was wrong; the work is only expensive because it repeats.
  if (
    forcedUpdate !== 'defer-idle'
    && forcedUpdateRequestLatch?.requestedAt === requestedUpdateAt
    && forcedUpdateRequestLatch.lastOutcome !== null
    && Date.now() - forcedUpdateRequestLatch.lastAttemptMs < UPDATE_CHECK_INTERVAL_MS
  ) {
    return false;
  }

  if (forcedUpdate === 'defer-idle') {
    forcedUpdateDeferStreak += 1;
    // Log the first defer of a streak and then every 10th, so a long starvation
    // is visible without the 313-line log flood a single request produced before.
    if (forcedUpdateDeferStreak === 1 || forcedUpdateDeferStreak % 10 === 0) {
      log(
        `[self-update] "Update CLI now" requested at ${requestedUpdateAt} — deferring: an agent is mid-turn / recently active ` +
          // ENG-8449: the streak alone was misleading in the agt-aws-1 incident —
          // it restarts at 0 with the manager, so "consecutive defers: 3" could
          // mean 3 polls or 78 minutes. The pending-for age is the number that
          // actually tracks the operator's wait, and the one both tiers use.
          `(consecutive defers: ${forcedUpdateDeferStreak}, pending ${describePendingFor(pendingForMs)}). Retrying next poll.`,
      );
    }
    // ENG-8449: announce the relaxation when it actually happens, not when the
    // streak hits 20. Since the wall clock can now reach this tier first — and
    // usually does on a restarting manager, where the streak keeps rewinding —
    // a streak-triggered line would credit the counter for a transition the
    // clock made, in a change whose whole premise is that the counter misleads.
    if (relaxed && !forcedUpdateRelaxAnnounced) {
      forcedUpdateRelaxAnnounced = true;
      log(
        `[self-update] WARN "Update CLI now" has been pending ${describePendingFor(pendingForMs)} ` +
          `(${forcedUpdateDeferStreak} consecutive defers) waiting for an idle host. ` +
          `Relaxing the idle gate to the progress axis only (an agent actively producing tokens still defers; ` +
          `"received a message in the last 5 minutes" no longer does). ` +
          `It runs regardless once ${Math.round(FORCED_UPDATE_HARD_DEADLINE_MS / 60_000)}m have elapsed.`,
      );
    }
    return false;
  }

  // forcedUpdate === 'consume' | 'consume-deadline'
  //
  // ENG-8624: defer only while the latch is legitimately HELD. An expired latch
  // means the previous updater never settled, and deferring to it is what made
  // "Update CLI now" useless on two-tractors-host — the operator's request from
  // 00:43:36Z was still being deferred to the same dead updater hours later, so
  // neither the automatic nor the manual path could move the host. Falling
  // through lets `checkAndUpdateCli` break the latch and run.
  if (selfUpdateLatchVerdict(selfUpdateInFlightSince, Date.now()) === 'held') {
    // A self-update is already running. A forced kick would no-op on its
    // in-flight guard and the request would be LOST (we'd have advanced
    // the dedup marker for an update that never ran). Defer WITHOUT
    // consuming: the column stays set, the marker doesn't move, and the
    // next poll retries once this update settles (or after the restart it
    // may trigger, where the host is already converging).
    log(
      `[self-update] "Update CLI now" requested at ${requestedUpdateAt} — deferring: a self-update is already in flight. Retrying next poll.`,
    );
    return false;
  }

  // ENG-6771: consume the one-shot based on the OUTCOME, not optimistically.
  // The old code advanced the dedup marker BEFORE kicking the updater, so a
  // pre-install failure (a transient npm registry timeout, a non-200, an
  // install/smoke error) burned the request silently — the next heartbeat
  // acked it, the API cleared hosts.update_requested_at, and nothing retried
  // while the webapp had already shown success. Now we only consume on
  // 'updated' (install committed; restart scheduled) or 'noop' (already on
  // the latest in-channel build); a 'failed' attempt leaves the request
  // pending so the next idle poll retries.
  //
  // Restart-survival: on 'updated', installAgtCliViaNpm only SETS
  // restartAfterUpgrade (the loop restarts later), so the .then() below
  // persists the marker durably before that restart. If a restart still
  // races ahead, the post-restart manager re-runs this forced check, finds
  // itself already-current ('noop'), and consumes then — no double install.
  // ENG-8449: say which of the two reached the update — a quiet host, or the
  // deadline overriding a host that never went quiet. Reading "host idle" in the
  // log when the gate was in fact bypassed would misdescribe the one case an
  // operator investigating an interrupted turn most needs to recognise.
  // ENG-10724: latched on the same run as the outcome line below. This used to
  // be unconditional, which was right while a non-consumed outcome was always
  // transient - the request cleared within a poll or two, so the line fired a
  // handful of times. A device-state hold persists until a human pairs the host,
  // and ENG-8079 measured what that looks like: 32 "running window-bypassing
  // self-update now" lines for one request. Latching only the outcome line would
  // have halved the flood and called it fixed.
  if (lastForcedOutcomeFor(requestedUpdateAt) === null) {
    log(
      forcedUpdate === 'consume-deadline'
        ? `[self-update] WARN "Update CLI now" requested at ${requestedUpdateAt} has been pending ${describePendingFor(pendingForMs)} ` +
            `and the host still reads busy — running the window-bypassing self-update anyway (ENG-8449 hard deadline). ` +
            `A live turn may be interrupted; the operator asked for this update explicitly.`
        : `[self-update] "Update CLI now" requested at ${requestedUpdateAt} — running window-bypassing self-update now (host idle${relaxed ? ', relaxed gate' : ''}).`,
    );
  }
  const prevProcessedAt = state.lastUpdateRequestProcessedAt ?? null;
  const attemptStartedMs = Date.now();
  void checkAndUpdateCli({ force: true })
    .then((outcome) => {
      if (!shouldConsumeForcedUpdate(outcome)) {
        const previous = lastForcedOutcomeFor(requestedUpdateAt);
        forcedUpdateRequestLatch = requestedUpdateAt === null ? null : {
          requestedAt: requestedUpdateAt,
          lastOutcome: outcome,
          lastAttemptMs: attemptStartedMs,
        };
        // ENG-10724: latched on the outcome. This used to log unconditionally,
        // which was fine while every non-consumed outcome was TRANSIENT (a
        // registry timeout, a failed install) - the request cleared within a
        // poll or two. The device-state hold is the first `'failed'` that
        // persists until a human pairs the host, and because `'failed'`
        // deliberately keeps the one-shot alive the forced path is re-kicked
        // every poll, so this line would repeat for hours in manager.log. Say it
        // once per run of the same outcome; a change of outcome says it again.
        if (previous !== outcome) {
          log(
            `[self-update] "Update CLI now" did not complete (outcome: ${outcome}, no install ran) — leaving the `
            + `request pending; the next idle poll retries. (repeats of this outcome are not logged)`,
          );
        }
        return;
      }
      forcedUpdateRequestLatch = null;
      selfUpdateForcedHoldAnnounced = false;
      // ENG-8079: 'noop' is a legitimate completion, but it is NOT the same
      // event as an install — distinguish them so an operator can tell "already
      // current" from "upgraded" instead of reading silence either way.
      log(
        outcome === 'updated'
          ? `[self-update] "Update CLI now" completed — upgrade installed; manager restart scheduled.`
          : `[self-update] "Update CLI now" completed — host was already on the latest in-channel build; nothing to install.`,
      );
      // 'updated' or 'noop' — consume the one-shot durably.
      state.lastUpdateRequestProcessedAt = requestedUpdateAt;
      try {
        atomicWriteFileSync(getStateFile(), JSON.stringify(state, null, 2));
      } catch (err) {
        state.lastUpdateRequestProcessedAt = prevProcessedAt;
        log(
          `[self-update] failed to persist update-request ack; retrying next poll: ${(err as Error).message}`,
        );
      }
    })
    .catch((err) => {
      // Threw before committing to an install → treat as failed → don't
      // consume → retry next poll.
      log(
        `[self-update] forced check failed: ${(err as Error).message} — leaving the request pending for retry.`,
      );
    });
  return true;
}
// ADR-0011 PR-A: agentState.writtenHashes + agentState.knownSecretsHashes moved into `agentState`.

// ENG-4897: per-agent hash of the project `.mcp.json` that the running
// claude session was launched with. The existing stale-MCP reaper
// (ENG-4832) only watches env-var-keyed integration changes; structural
// changes to .mcp.json (new server entries, removed entries, inline env
// changes for tokens that aren't ${VAR}-substituted) slip past it.
//
// Sterling/agt-demo (2026-05-09): operator added Telegram, the manager
// wrote the new .mcp.json, but the running claude session — launched
// with --strict-mcp-config — had only the pre-Telegram view. Its
// `--dangerously-load-development-channels server:telegram` flag then
// errored "no MCP server configured with that name" until manual
// `tmux kill-session` triggered a respawn.
//
// On each poll we hash the project .mcp.json and compare to the value
// recorded for this agent. If they differ, schedule a session restart
// (the existing hot-reload coalescer takes care of the rest). On first
// observation post-manager-start we adopt the current hash as the
// optimistic baseline — worst case is one missed drift event after a
// manager restart, the next change is caught.
const runningMcpHashes = new Map<string, string>();

// ENG-7748: per-agent Anchor Browser persistent-session lifecycle. Mints /
// re-mints profile-bound sessions and hands back the id to stamp into
// config.session_id. In-memory (a manager restart re-mints); anchor-browser
// routes through the live proxy so a re-mint takes effect with no respawn.
const anchorSessionLifecycle = new AnchorSessionLifecycle();

// ENG-5285: parallel map of the SET of declared mcpServers keys for the
// running session. Used by the drift handler to compute a precise diff
// (added/removed keys) so we only reset presence-reaper give-up state
// for keys whose membership actually changed — not for keys whose value
// rotated (e.g. composio refreshing a signed proxy URL every ~20 min,
// which was the root cause of Stirling flapping 450× over six days
// post-ENG-5279).
const runningMcpServerKeys = new Map<string, Set<string>>();

// ENG-6062: per-agent value hash of the channel-owned secrets in the running
// session's project `.env.integrations` (TELEGRAM_BOT_TOKEN, SLACK_*_TOKEN,
// MSTEAMS_CLIENT_SECRET). Post-Track-D these are `${VAR}` templates in
// `.mcp.json`, so a token rotation moves neither the `.mcp.json` content hash
// (runningMcpHashes) nor the structure hash — this is the only signal that a
// channel credential changed and the session must respawn to pick it up.
// Same optimistic-baseline semantics as runningMcpHashes.
const runningChannelSecretHashes = new Map<string, string>();

// ENG-7576 (PR 2): structure hash of the managed-MCP set the RUNNING session
// was actually LAUNCHED with, derived from the on-disk .mcp.json at spawn
// (seedSessionLaunchBaselines). This is the cross-poll de-dup anchor: the
// managed-toolkit convergence restarts only when the desired structure differs
// from what the live session launched with, so a change that was already
// applied before the respawn (the integration-add common case) can't fire a
// redundant restart off a baseline that desynced across polls. Cleared on
// stop (no session = no anchor), re-seeded at every spawn. Fail-open: an
// unreadable file seeds nothing and the convergence behaves as pre-ENG-7576.
const sessionLaunchManagedStructure = new Map<string, string>();

function projectMcpHash(_codeName: string, projectDir: string): string | null {
  try {
    // ENG-5071: hash the canonical (key-sorted) JSON, not the raw bytes.
    // Pre-fix this hashed `readFileSync(...)` directly, so two writers
    // producing logically-equivalent files with different key insertion
    // order would trip drift detection every poll — Scout flapped for
    // hours because writeMcpServer emitted `{ url, headers, command,
    // args }` in one order and sanitizeMcpJson rewrote the same logical
    // entry in another. Canonicalising means only real structural
    // changes (new server, removed server, changed values) move the
    // hash; pure shape/order churn no longer fires a restart. Belt-and-
    // braces alongside the sanitize-headers fix in the same PR — the
    // sanitizer was producing semantically-different content for
    // Granola, but byte-level hashing would still bite the next time
    // any writer pair disagreed on key order.
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    return createHash('sha256').update(canonicalJson(JSON.parse(raw))).digest('hex');
  } catch {
    return null;
  }
}

/**
 * ENG-5285: read the project `.mcp.json` and return the set of declared
 * `mcpServers` keys. Returns null when the file is missing or unparseable
 * (caller treats that as "no information" — falls back to the wholesale
 * give-up-state clear).
 */
function projectMcpKeys(_codeName: string, projectDir: string): Set<string> | null {
  try {
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    const parsed = JSON.parse(raw);
    const servers = (parsed as { mcpServers?: Record<string, unknown> }).mcpServers;
    if (!servers || typeof servers !== 'object') return new Set();
    return new Set(Object.keys(servers));
  } catch {
    return null;
  }
}

/**
 * ENG-7576 (PR 2): snapshot the launch-state baselines from the on-disk
 * `.mcp.json` at the moment a session is spawned. Called from the single
 * shared spawn path (ensurePersistentSession) so every respawn route — full
 * poll, ENG-7335 fast lane, ENG-7401 Phase-2 respawn — anchors identically.
 *
 * Why eager (vs the drift check's lazy first-observation adopt at
 * checkMcpConfigDriftAndScheduleRestart): the lazy adopt runs only on the
 * full poll's processAgent pass. A fast-lane or Phase-2 respawn doesn't run
 * it, so the baseline used to stay empty until the NEXT full poll — and if
 * the managed-toolkit convergence rewrote `.mcp.json` in that same later
 * poll (which runs BEFORE the drift check), the adopt landed on the
 * rewritten file, silently swallowing a genuine membership change the
 * running session never got (missed restart, stale tools). Seeding at spawn
 * makes every baseline mean the same thing: "what the live session launched
 * with".
 *
 * Best-effort: a missing/unparseable file seeds nothing (the lazy adopt
 * remains as fallback) and clears the launch anchor so a stale anchor from a
 * previous session can never suppress a restart for this one.
 */
function seedSessionLaunchBaselines(codeName: string, projectDir: string): void {
  // Mirror the drift check's baseline-adopt semantics (ENG-5285 follow-up,
  // CodeRabbit on PR #1178): an unreadable file means "unknown", so DELETE the
  // stale entry rather than leave a previous session's value poisoning the
  // next comparison; the lazy adopt re-establishes it once readable.
  const hash = projectMcpHash(codeName, projectDir);
  const keys = projectMcpKeys(codeName, projectDir);
  if (hash) runningMcpHashes.set(codeName, hash);
  else runningMcpHashes.delete(codeName);
  if (keys) runningMcpServerKeys.set(codeName, keys);
  else runningMcpServerKeys.delete(codeName);
  let launchStructure: string | null = null;
  try {
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    launchStructure = managedMcpStructureHashFromFile(
      JSON.parse(raw) as { mcpServers?: Record<string, { headers?: Record<string, string> }> },
      isManagedMcpServerKey,
    );
  } catch {
    /* no config yet — no anchor */
  }
  if (launchStructure !== null) {
    sessionLaunchManagedStructure.set(codeName, launchStructure);
  } else {
    sessionLaunchManagedStructure.delete(codeName);
  }
}

// ENG-6441: `readMcpHttpServerConfig`, `deriveMcpServerKey`, and the
// probeEnv/probeDeps assembly were moved to ./connectivity-probe-context.js so
// the on-demand `agt integration probe` command shares the exact same probe
// context as this periodic loop (no drift). They're imported above.

// ENG-5641: run the rolling host-side connectivity probes for one agent and
// report results to the control plane. Flag-gated by the caller
// (AGT_CONNECTIVITY_PROBE_ENABLED) — dormant until enabled + host-validated.
// ENG-6139: managed (Composio) toolkits ARE probed here now — the agent's wired
// MCP server carries the x-api-key + user_id, so the host verifies the
// connected-account binding (the signal tools/list misses). Non-fatal: throws
// are caught by the caller.
async function runAgentConnectivityProbes(
  agent: { agent_id: string; code_name: string },
  integrations: Array<{
    id: string;
    definition_id: string;
    scope: string;
    auth_type: string;
    credentials: Record<string, unknown>;
    source_type?: string | null;
    last_connectivity_check_at?: string | null;
    connectivity_test?: ConnectivityTestOverride | null;
    connection_key?: string | null;
    // ENG-5463: the toolkit's CLI binary (`toolkit_definitions.cli_binary`),
    // enriched by /host/agent-integrations. Declared rather than asserted at the
    // read site so a rename on either side is a compile error, not a silent
    // `undefined` that routes the row back to `builtin` (CodeRabbit, PR #4453).
    cli_binary?: string | null;
    // ENG-8055: inline `.mcp.json`-shaped server entry reconstructed by the API
    // for a quarantined MANAGED row (dropped from `.mcp.json`). Present only on
    // quarantined-probe rows; the executor probes it instead of a file lookup.
    inline_server_entry?: unknown;
    // ENG-8363: the row's age (forwarded by /host/agent-integrations for
    // ENG-7575) and whether it is quarantined. Both feed the "is a missing
    // `.mcp.json` key a fault?" decision — this very poll writes `.mcp.json`
    // AFTER probing, and a quarantined row is absent on purpose.
    created_at?: string | null;
    // ENG-9718: the reconnect settling deadline. Same decision as `created_at`
    // above, different history: a reconnect returns the row to the forwarded
    // set and this poll writes `.mcp.json` AFTER probing, so the key is absent
    // for the same reason and must not read as a fault.
    reconnect_quiesced_until?: string | null;
    quarantined?: boolean;
  }>,
  projectDir: string,
): Promise<void> {
  if (integrations.length === 0) return;

  // ENG-6441: the probeEnv overlay + the four-leg probeDeps are built by the
  // shared factory so the on-demand `agt integration probe` command runs the
  // IDENTICAL probe context as this loop.
  // ENG-9122 AC3: the agent id goes into the probe child's env so the ENG-8344
  // credential shim can mint a token. Without it the gh probe reported
  // "AGT_AGENT_ID is not set in this process environment" once an hour.
  const probeEnv = buildProbeEnv(projectDir, agent.agent_id);
  const probeDeps = buildConnectivityProbeDeps(projectDir, probeEnv);

  const intervalSec = Number(process.env.AGT_CONNECTIVITY_PROBE_INTERVAL_SECONDS) || 3600;
  const result = await runConnectivityProbes(
    integrations.map((i) => {
      const sourceType = (i.source_type ?? null) as ToolkitSourceType | null;
      return {
        id: i.id,
        definition_id: i.definition_id,
        scope: i.scope as 'agent' | 'team' | 'organization',
        auth_type: i.auth_type as IntegrationAuthType,
        source_type: sourceType,
        credentials: i.credentials,
        last_connectivity_check_at: i.last_connectivity_check_at ?? null,
        // ENG-5665 / ENG-6396: for any integration whose probe is an MCP
        // `tools/list` against the agent's wired server (managed/Composio,
        // mcp_server, or a remote-MCP OAuth provider like granola), derive the
        // .mcp.json server key. The executor looks this up in .mcp.json; an
        // unresolvable key degrades to transient_error, never a false 'down'.
        mcp_server_key: deriveMcpServerKey({
          definitionId: i.definition_id,
          sourceType,
          authType: i.auth_type as IntegrationAuthType,
          connectivityTest: (i.connectivity_test ?? null) as ConnectivityTestOverride | null,
          // ENG-7543: target this connection's own .mcp.json server.
          connectionKey: i.connection_key ?? null,
          // ENG-7077: a static-token hosted remote MCP (monday, peec,
          // anchor-browser) is only recognisable from its catalog remote_mcp
          // spec — its toolkit source_type is `native` like any built-in.
          remoteMcpUrl: (i as { remote_mcp?: { url?: string | null } | null }).remote_mcp?.url ?? null,
        }),
        // ENG-6242: forward the prescribed connectivity-test tool to the executor.
        connectivity_test: i.connectivity_test ?? null,
        // ENG-5463: forward the toolkit's CLI binary. The runner has read this
        // field since it was written and nothing ever set it, so every
        // CLI-backed toolkit seeded `native` (github, gcloud, xurl, greenlight,
        // expo) resolved to `builtin` and reported `unverified` forever.
        cli_binary: i.cli_binary ?? undefined,
        // ENG-8055: forward the reconstructed inline server entry (managed
        // quarantined rows) so the MCP probe legs hit the real server instead of
        // the missing `.mcp.json` key. Absent on normal (active) rows.
        inline_server_entry: i.inline_server_entry,
        // ENG-8363: age + quarantine status, so an absent `.mcp.json` key is
        // classified rather than guessed. Forwarding both is what makes the
        // escalation to `down` reachable at all — an omitted `created_at` reads
        // as "still settling" and never escalates (fail-open by design).
        created_at: i.created_at ?? null,
        // ENG-9718: the SECOND settling anchor, and it must be forwarded HERE.
        // This mapper is why declaring the field on the parameter type above is
        // not enough — the object handed to `runConnectivityProbes` is rebuilt
        // field by field, so anything not named here arrives as `undefined` and
        // the anchor silently never fires. Same failure shape as the `cli_binary`
        // note a few lines up: read by the runner, set by nobody, wrong forever.
        reconnect_quiesced_until: i.reconnect_quiesced_until ?? null,
        quarantined: i.quarantined ?? false,
        // ENG-7077: the catalog remote-MCP spec, so the executor's resolver can
        // tell an ADR-0033 static-token remote MCP from a built-in native module.
        remote_mcp: (i as { remote_mcp?: { url?: string | null } | null }).remote_mcp ?? null,
      };
    }),
    { probeDeps, intervalMs: intervalSec * 1000 },
  );

  if (result.reports.length > 0) {
    await api.post('/host/integration-connectivity', {
      agent_id: agent.agent_id,
      results: result.reports,
    });
    // ENG-10061: the route stamped `last_connectivity_check_at` before it
    // answered (`applyConnectivityReports` is awaited), and that stamp is the
    // ONLY thing that makes these rows not-due. Without this force the next
    // pass would serve a cached payload still carrying the pre-report timestamp
    // and re-probe every row we just reported — for the whole TTL, silently,
    // POSTing here again each time. See `forceIntegrationsFetch`.
    forceIntegrationsFetch(agent.agent_id);
    // ENG-6392: mirror this round's verdicts into the recovery ledger so a
    // later auto-resume can confirm which integrations came back. Reports carry
    // `integration_id`; the incident label is `definition_id`, so resolve it
    // from the probed set (a report whose id we can't map is dropped).
    const labelById = new Map(integrations.map((i) => [i.id, i.definition_id]));
    dependencyRecoveryLedger.record(
      agent.code_name,
      result.reports.flatMap((r) => {
        const label = labelById.get(r.integration_id);
        return label ? [{ kind: 'integration' as const, label, status: r.status }] : [];
      }),
    );
    log(
      `Connectivity probe for '${agent.code_name}': probed=${result.probed} reported=${result.reports.length} due=${result.due}`,
    );
  }
}

/**
 * ENG-7053 / ENG-7220 - rolling host-side session-tool-bind probe. For each
 * installed integration, decides whether its MCP tools are actually BOUND in the
 * running agent session (a backing stdio child is alive, or a remote MCP answers
 * `tools/list`) and reports a `bound|missing|unreachable|unknown` verdict to
 * `/host/session-tool-bind`. Reuses the presence-reaper's stdio-liveness check
 * and the connectivity-probe context's HTTP `tools/list` handshake, so the
 * verdict composes signals the manager already computes. Best-effort: a failure
 * here never fails the poll (the caller wraps it). Flag-gated by
 * `session-tool-probe`; dormant until armed.
 */
// ENG-7318: per (codeName, serverKey) cooldown for the session-tool rebind reap.
// The probe's ~1h due-cadence already spaces reaps (a probed integration's
// last_session_tool_bind_at is refreshed, so it isn't due again soon), but this
// is a defence-in-depth guard so a shortened cadence / forced-due re-probe can't
// turn a persistently-broken integration into a reap-every-cycle loop. In-memory
// (a manager restart re-spawns the session fresh anyway); independent of the
// ENG-4911 env-rotation grace so the two reap paths don't couple.
const sessionToolRebindCooldownAt = new Map<string, number>();
const SESSION_TOOL_REBIND_COOLDOWN_MS = 10 * 60 * 1000;

function sessionToolRebindKey(codeName: string, serverKey: string): string {
  return `${codeName}\x00${serverKey}`;
}

// ENG-7429: agents (by code_name) owed a PROMPT session-tool-bind probe after a
// restart that (re)bound their MCP tools, with a small budget of force-due
// re-checks. Each poll consumes one attempt and runs a force-due probe so a
// freshly-added integration reports `bound` right after its tools bind, instead of
// waiting out the ~1h rolling due-interval its own too-early first probe pushed it
// into (the ENG-7415 canary gap). The BUDGET (not a one-shot) is what makes it
// robust to timing: an early attempt that fires before the tools are callable
// stamps not-bound, but a later attempt re-checks (force-due ignores the stamp) and
// catches `bound` once they're up - the modal completes on the first `bound` it
// sees. Armed from BOTH restart paths (ENG-7401): restart-verify success for the
// drift/hot-reload path, and the dashboard-restart respawn-healthy point for the
// path an integration add actually takes. In-memory: a manager restart re-provisions
// every session fresh, so a dropped entry just falls back to the rolling cadence.
const promptBindProbeAttempts = new Map<string, number>();
// ~60s of force-due re-checks at the 10s poll cadence: comfortably spans a Composio
// add's respawn + tools/list bind, bounded so a genuinely-unbindable integration
// can't force-probe every poll forever.
const PROMPT_BIND_PROBE_ATTEMPTS = 6;

async function runAgentSessionToolBindProbes(
  agent: { agent_id: string; code_name: string },
  integrations: Array<{
    id: string;
    definition_id: string;
    scope: string;
    auth_type: string;
    source_type?: string | null;
    last_session_tool_bind_at?: string | null;
    connectivity_test?: ConnectivityTestOverride | null;
  }>,
  projectDir: string,
  // ENG-7429: a one-shot PROMPT probe queued right after restart-verify confirmed
  // the newly-added integration's MCP tools bound. Forces every row due so the
  // just-added integration is re-checked NOW instead of waiting out its ~1h
  // due-interval (see the intervalMs note below).
  opts?: { forceDue?: boolean },
  // ENG-7429: returns whether the probe actually RAN (a result was produced). The
  // caller uses this to keep the one-shot prompt-probe marker if a transient
  // failure (throw, or a null gather from an unparseable .mcp.json / ps hiccup
  // right after the restart) meant the forced re-check never happened - so the
  // guarantee isn't silently lost on exactly the flaky post-restart window
  // (CodeRabbit on #3063).
): Promise<boolean> {
  if (integrations.length === 0) return true;

  // ENG-7255: the host-side gather+probe lives in a shared lib so the `agt` CLI
  // one-shot ("Probe now") can reuse it. ENG-7263: the manager supplies the MCP
  // server keys the running session loaded at spawn (persisted in
  // direct-chat-session.json, keyed on agent_id) - the same source the standalone
  // command reads - to gate HTTP `bound`. The rolling ~1h cadence does the
  // due-selection.
  const intervalSec = Number(process.env.AGT_CONNECTIVITY_PROBE_INTERVAL_SECONDS) || 3600;
  // ENG-7429: a prompt bind-probe forces EVERY row due (intervalMs 0) so the
  // just-added integration is re-checked NOW - even though the rolling probe
  // stamped it moments ago while its tools were still binding (which pushed its
  // due-time an hour out) - instead of waiting out the ~1h due-interval. That
  // stale early stamp is exactly why the Add modal's confirming step never got its
  // `bound` (ENG-7415 canary). The rolling path keeps the normal ~1h cadence.
  const intervalMs = opts?.forceDue ? 0 : intervalSec * 1000;
  // ENG-8448: one read of the persisted session state serves both the loaded
  // server-key snapshot (ENG-7263) and the spawn time (`startedAtMs`) used for the
  // cold-start grace, so a forced post-restart probe doesn't count a broker that's
  // still launching as `missing`.
  const dcSession = readDirectChatSessionState(agent.agent_id);
  const sessionLoadedServerKeys = new Set(dcSession?.mcpServerKeys ?? []);
  const result = await gatherSessionToolBindProbe(agent, integrations, projectDir, {
    sessionLoadedServerKeys,
    sessionStartedMs: dcSession?.startedAtMs ?? null,
    intervalMs,
    // ENG-7429 (CodeRabbit on #3063): a prompt probe must cover the WHOLE set, not
    // the rolling batch cap (25), so the just-added integration can't be crowded out
    // on a busy agent. Rolling probes keep the default cap.
    ...(opts?.forceDue ? { maxPerRun: integrations.length } : {}),
  });

  if (result && (result.reports.length > 0 || result.skippedDetail.length > 0)) {
    // The POST stays gated on reports - there is nothing to persist for a batch
    // that produced none. The LOG must not be: a run where every due row was
    // skipped is exactly the run whose silence AC2 exists to break, and gating
    // both on the same condition meant the all-skipped case printed nothing at
    // all. That is the `due=4 reported=0` shape a reader has to explain, with
    // the explanation withheld. (CodeRabbit)
    if (result.reports.length > 0) {
      await api.post('/host/session-tool-bind', {
        agent_id: agent.agent_id,
        results: result.reports,
      });
      // ENG-10061: as on the connectivity report above — the route stamped
      // `last_session_tool_bind_at` before answering, and that stamp is what
      // makes these rows not-due. Inside the `reports.length > 0` guard on
      // purpose: a run that POSTed nothing changed nothing server-side, so
      // there is no new stamp to go and read.
      forceIntegrationsFetch(agent.agent_id);
    }
    // ENG-9114 AC2: name what was skipped and why. `probed=2 reported=2 due=4`
    // told a reader that two of four rows vanished and nothing about which or
    // why - and the gap WAS the signal. Attributing it is what stops the next
    // reader inventing a bind failure to explain the difference.
    const naCount = result.reports.filter((r) => r.status === 'not_applicable').length;
    const skipSummary = result.skippedDetail.length > 0
      ? ` skipped=[${result.skippedDetail.map((s) => `${s.definition_id}:${s.reason}`).join(' ')}]`
      : '';
    log(
      `Session-tool-bind probe${opts?.forceDue ? ' (prompt, ENG-7429)' : ''} for '${agent.code_name}': ` +
      `probed=${result.probed} reported=${result.reports.length} due=${result.due} ` +
      `not_applicable=${naCount}${skipSummary}`,
    );
  }

  // ENG-7318 (ENG-7053 Slice A): rebind-on-refresh. When the probe found an
  // integration's tools fell out of the LIVE session - a dead stdio/proxy child
  // (`missing`, the ENG-7301/Phil case) or an alive OAuth-proxy child whose
  // upstream stopped answering tools/list after a token rotated under a running
  // session (`unreachable`, the ENG-7318/Pepper case) - SIGTERM just that
  // integration's MCP child so Claude Code's transport respawns it with a fresh
  // connection. No full agent restart; conversation context is preserved. The
  // reaper records its own short rotation grace (recordStaleRotation) so the
  // presence-reaper doesn't tear down the session for the just-killed child.
  // Flag-gated (`session-tool-rebind`, ships dark); the on-demand CLI probe never
  // reaches this manager path, so a probe can never mutate runtime state.
  if (result && result.rebindCandidates.length > 0 && hostFlagStore().getBoolean('session-tool-rebind')) {
    let mcpJsonForRebind: Parameters<typeof reapStaleMcpChildren>[0]['mcpJson'] = null;
    try {
      mcpJsonForRebind = JSON.parse(readFileSync(join(projectDir, '.mcp.json'), 'utf-8'));
    } catch {
      mcpJsonForRebind = null; // fall back to the reaper's bare-key argv matcher
    }
    const isolated = isolationMode(agent.code_name) === 'docker';
    const now = Date.now();
    for (const candidate of result.rebindCandidates) {
      // Skip keys reaped within the cooldown so a still-broken integration is
      // rebound at most once per window, not every probe cycle.
      const keysToReap = candidate.serverKeys.filter((k) => {
        const last = sessionToolRebindCooldownAt.get(sessionToolRebindKey(agent.code_name, k));
        return last === undefined || now - last >= SESSION_TOOL_REBIND_COOLDOWN_MS;
      });
      if (keysToReap.length === 0) continue;
      reapStaleMcpChildren({
        log,
        codeName: agent.code_name,
        serverKeys: keysToReap,
        mcpJson: mcpJsonForRebind,
        isolated,
      });
      for (const k of keysToReap) {
        sessionToolRebindCooldownAt.set(sessionToolRebindKey(agent.code_name, k), now);
      }
      log(
        `Session-tool rebind for '${agent.code_name}': ${candidate.status} integration=${candidate.integration_id} reaped MCP child(ren) [${keysToReap.join(', ')}] for respawn`,
      );
    }
  }

  // ENG-7429: the probe RAN iff gather produced a result. A null result (e.g. an
  // unparseable .mcp.json or a `ps` hiccup during the post-restart churn) means the
  // forced re-check didn't happen - report that so the caller requeues the marker.
  return result != null;
}

/**
 * ENG-4897 follow-up (CodeRabbit on PR #829): drop the `runningMcpHashes`
 * entry whenever a session is stopped for ANY reason (auth rotation,
 * day rollover, pause, terminate, hot-reload restart). Without this,
 * the post-`ensurePersistentSession()` drift check on the NEXT poll
 * compares the freshly-respawned session against the dead session's
 * hash, immediately scheduling a spurious second restart.
 *
 * `cancelPendingSessionRestart` is called before every direct
 * `stopPersistentSession` (see its docstring), but the cancel doesn't
 * clear the hash. Call this wrapper instead — it bundles the cancel +
 * the hash drop, so every stop path stays consistent.
 */
function stopPersistentSessionAndForgetMcpBaseline(
  codeName: string,
  // ENG-5441: every call site that represents a *restart loop trigger*
  // (day-rollover, auth-tuple rotation, reaper-driven restart, hot-reload,
  // channel-set change) passes its reason so the breaker can count it.
  // De-provisioning paths (agent paused / revoked / removed from host /
  // operator-initiated MANUAL dashboard restart) pass undefined — those are
  // intentional and shouldn't accumulate against the breaker.
  // ENG-7576: the dashboard-initiated integration-change reload is the
  // exception - the restart-consumption loop passes 'integration-change' so
  // the add's real respawn counts on the provisioning tally (ENG-7560).
  breakerReason?: RestartReason,
  // ENG-6264: the reason that drives the defer-until-idle GATE, which may differ
  // from the breaker-count reason above. The single-MCP reaper restart must
  // still defer-until-idle (gateable) yet NOT count against the breaker
  // (breakerReason=undefined). Defaults to breakerReason so every other caller
  // is unchanged — only the reaper passes a distinct gate reason.
  gateReason: RestartReason | undefined = breakerReason,
  // ENG-8260: how to close the runs this teardown interrupts. Defaults to the
  // pre-ENG-8260 behaviour — `('cancelled', 'session stopped (<reason>)')` —
  // which is right for a deprovision or a routine restart. A teardown caused by
  // a PLATFORM or CREDENTIAL fault passes `{ outcome: 'failed', message }` so
  // the runs table records that an infra fault killed the work instead of
  // laundering it into an ordinary cancellation attributed to a "deprovision"
  // that never happened.
  runClose?: { outcome: 'failed' | 'timeout'; message: string },
  // ENG-8293: diagnostic content for the `agent.restart` audit row this teardown
  // reports below. Before this, the row's `reason` restated `source`, so an
  // `auth-tuple-change` restart was permanently unexplainable once manager.log
  // rotated (~75 min). `reason` here replaces that restatement with a
  // human-readable summary; `detail` carries the structured form the
  // `debug_query_audit_log` tool projects. Callers that have nothing to add omit
  // it and the row is shaped exactly as before.
  //
  // MUST NOT carry secret material — it lands in an immutable, org-readable
  // audit row. See auth-tuple-diff.ts for why the auth tuple's components are
  // safe to carry in full, and for the key-name shape that survives the audit
  // scrubber.
  auditDetail?: { reason?: string; detail?: Record<string, unknown> },
): void {
  // ENG-5963: gate non-urgent restart-causing maintenance to the maintenance
  // window + defer-until-idle. Bypass reasons (deprovision/dashboard/auth/
  // day-rollover) resolve to 'bypass' and tear down immediately as before. A
  // deferred restart returns early WITHOUT clearing the running-state baselines
  // (runningMcpHashes/Keys) or recording the breaker, so the underlying trigger
  // (drift / changed channel set / dead MCP) is simply re-detected on the next
  // poll and the gate re-evaluated — a natural retry, no extra timer needed.
  const gate = restartGateFor(codeName, gateReason);
  if (gate !== 'bypass' && gate !== 'proceed') {
    log(`[maintenance-window] Deferring '${gateReason}' restart for '${codeName}' (${gate})`);
    return;
  }
  cancelPendingSessionRestart(codeName);
  stopPersistentSession(codeName, log);
  // ENG-8013 (storm-awareness): stamp whether the generation we're about to let
  // the ensure-session pass respawn was torn down by an EXTERNAL cause (anything
  // but the reaper's own mcp-presence recovery restart). Reached only after an
  // actual teardown: a maintenance-deferred call returned early above, so a
  // deferred restart never flips this. The presence reaper reads it to avoid
  // counting a channel MCP that's missing as collateral of external respawn churn
  // toward its quarantine budget (the ENG-8003 storm-quarantine bug).
  lastRestartWasExternal.set(codeName, gateReason !== 'mcp-presence-reaper');
  runningMcpHashes.delete(codeName);
  runningMcpServerKeys.delete(codeName);
  runningChannelSecretHashes.delete(codeName); // ENG-6062: re-baseline on next start
  sessionLaunchManagedStructure.delete(codeName); // ENG-7576: no session, no launch anchor
  // ENG-5565: a session teardown (pause / revoke / remove / restart trigger)
  // interrupts any in-flight injected kanban run — close it so it doesn't hang
  // open. 'cancelled' since the work was interrupted, not completed.
  // ENG-8260: unless the caller identified a platform/credential fault, in which
  // case record 'failed' with that cause — the runs table must not report an
  // infra kill as a routine cancellation from a "deprovision".
  const runOutcome = runClose?.outcome ?? 'cancelled';
  const runMessage = runClose
    ? `session stopped (${runClose.message})`
    : `session stopped (${breakerReason ?? 'deprovision'})`;
  closeInjectedRunIfOpen(codeName, runOutcome, runMessage);
  // ENG-5599: likewise close any in-flight scheduled-task runs routed in-session
  // so an interrupted firing doesn't hang open and mis-attribute later turns.
  closeScheduledRunsForCode(codeName, runOutcome, runMessage);
  // ENG-8818: and the session handle itself (ENG-5051's `${AGT_RUN_ID}` row).
  // A clean stop is 'completed', NOT the 'cancelled' the two calls above use:
  // those close work that was interrupted mid-flight, whereas the session
  // handle has no work to interrupt — it ran for exactly as long as the
  // session did, and reached its natural end here.
  closeSessionRunForCode(codeName, runClose?.outcome ?? 'completed', runMessage);
  if (breakerReason) {
    recordRestartForBreaker(codeName, breakerReason);
  }

  // ENG-6399: report system-initiated restarts to audit_log (POST
  // /host/restart-event), best-effort and fire-and-forget. Reached only after
  // an ACTUAL teardown — a maintenance-gated/deferred call returned early
  // above, so a deferred restart is never logged as one. Emitted only for
  // `breakerReason` restarts: deprovision paths (pause/revoke/remove) pass
  // breakerReason=undefined and are not restarts, and the operator dashboard
  // restart is audited server-side at its API route. The two flag-driven
  // reasons are SKIPPED here because restart-handler already reports them with
  // a richer free-text reason (the agent's own words for `agent-requested`),
  // so emitting here too would double-log.
  // ENG-7576: 'integration-change' is also skipped - the dashboard restart
  // request that carries it is already audited server-side at its API route
  // (the host_agents.restart_requested_at stamp path), so reporting here would
  // double-log the same operator action.
  // The skip-list and POST shape now live in reportRestartEvent, shared with the
  // scheduleSessionRestart lane so the two cannot drift on which reasons are
  // already audited server-side.
  reportRestartEvent(codeName, breakerReason, auditDetail);
}

/**
 * ENG-8092: the real dependencies behind `stopAgentRuntime`. Injected rather
 * than imported inside the helper so the teardown contract is unit-testable
 * without dragging in the manager-worker module graph — same reason
 * restart-stop-strategy.ts exists as a standalone predicate.
 */
const agentRuntimeTeardownDeps: AgentRuntimeTeardownDeps = {
  stopOpencodeServe: (codeName, teardownLog) => stopOpencodeSession(codeName, teardownLog),
  stopManagerHeldChannelIngest: (codeName, teardownLog) => {
    stopOpencodeSlackIngest(codeName, teardownLog);
    stopOpencodeTelegramIngest(codeName, teardownLog);
  },
  // No breaker reason: a deprovision is intentional and must not count against
  // the restart circuit breaker (see stopPersistentSessionAndForgetMcpBaseline).
  stopClaudePersistentSession: (codeName) => stopPersistentSessionAndForgetMcpBaseline(codeName),
  killTmuxSession: (sessionName) => {
    try {
      // execFileSync (no shell) rather than the `execSync('tmux ... 2>/dev/null')`
      // string these sites used to build by hand — `stdio: 'ignore'` already
      // suppresses tmux's "can't find session" stderr, and there is no shell to
      // interpolate the name into.
      syncExecFile('tmux', ['kill-session', '-t', sessionName], { stdio: 'ignore' });
      return true;
    } catch {
      return false; // no such session, or no tmux — nothing was killed
    }
  },
};

/**
 * ENG-8092: tear down an agent's runtime on a DEPROVISION path (paused, draft,
 * revoked, removed-from-host). Stops both the opencode serve and the Claude
 * pane; see agent-runtime-teardown.ts for why deprovision deliberately does not
 * branch on framework the way the ENG-8000 restart lanes do.
 *
 * Every deprovision site must route through here. The restart lanes must NOT —
 * they branch on `restartTargetsOpencodeServe` because the teardown they perform
 * has to match the respawn that follows it.
 */
function stopAgentRuntime(codeName: string, reason: string): void {
  stopAgentRuntimeWithDeps(codeName, reason, log, agentRuntimeTeardownDeps);
}

function checkMcpConfigDriftAndScheduleRestart(codeName: string, projectDir: string): void {
  const currentHash = projectMcpHash(codeName, projectDir);
  const action = decideMcpDriftAction(currentHash, runningMcpHashes.get(codeName));
  switch (action.kind) {
    case 'no-config':
    case 'no-drift':
      return;
    case 'baseline': {
      runningMcpHashes.set(codeName, action.hash);
      // ENG-5285: also capture the declared mcpServers key set so the
      // next drift event can diff added/removed keys without re-reading
      // the previous file (which is already overwritten by then).
      //
      // ENG-5285 follow-up (CodeRabbit on PR #1178): preserve the
      // "unknown" signal from projectMcpKeys — a transient read/parse
      // race should NOT poison the baseline with an empty set, which
      // would make the next drift see every current key as "newly
      // added" and reset the cap on every MCP. Treat null as "no
      // baseline" — the drift handler falls back to the wholesale
      // clear, which is the safe choice when we don't know what the
      // previous declared set was.
      const baselineKeys = projectMcpKeys(codeName, projectDir);
      if (baselineKeys) {
        runningMcpServerKeys.set(codeName, baselineKeys);
      } else {
        runningMcpServerKeys.delete(codeName);
      }
      return;
    }
    case 'drift': {
      log(
        `[hot-reload] .mcp.json content changed for '${codeName}' ` +
        `(${action.previous.slice(0, 12)} → ${action.current.slice(0, 12)}) (ENG-4897)`,
      );
      // ENG-5285: clear presence-reaper give-up state ONLY for keys
      // whose membership in the declared set actually changed — i.e.
      // keys that were added or removed. Keys present in both before
      // and after (typical for value-only drift, e.g. composio rotating
      // a signed proxy URL every ~20 min) keep their cap accumulated.
      // Pre-fix this wholesale-cleared the agent's reaper state on
      // every URL rotation, which was why Stirling flapped 450× over
      // six days despite ENG-5279's restart cap — the cap was reset
      // before it could trip.
      // ENG-5285 follow-up (CodeRabbit on PR #1178): preserve the
      // "unknown" signal from projectMcpKeys. Coercing either side to
      // an empty set would either poison the comparison (every current
      // key looks "newly added") or hide the drift (both empty looks
      // like "no membership change"). When either side is unknown,
      // fall back to the wholesale clear — the safe behaviour we had
      // pre-ENG-5285, used here as the fallback rather than the
      // default.
      const previousKeys = runningMcpServerKeys.get(codeName);
      const currentKeys = projectMcpKeys(codeName, projectDir);
      // ENG-5537: only a *membership* change (server added/removed) warrants
      // restarting the running session. A value-only drift — the same set of
      // servers with a rotated url/header value, e.g. composio re-signing
      // `mcp_url` every poll — leaves the running session's established MCP
      // connections intact, and a future respawn reads the freshly-written
      // file anyway. Pre-fix this scheduled a restart on EVERY drift, so a
      // flapping signed url tripped the ENG-5441 breaker and auto-paused the
      // agent within minutes. When key membership is unknown we keep the
      // pre-ENG-5537 safe default (treat as a restart-worthy change).
      const decision = decideMcpRestartOnDrift(previousKeys, currentKeys);
      // ENG-5932: 0-restart-on-removal twin for the .mcp.json drift watcher.
      // When the only membership change is the REMOVAL of quarantined
      // channel(s) — the quarantine drop rewrote .mcp.json without the dead
      // channel — adopt the new baseline but DON'T bounce the live session
      // (bouncing to "notice" a dead channel left would SIGINT the healthy
      // siblings, the exact harm we're removing). Only when membership is known.
      let quarantineOnlyDrift = false;
      if (decision.restart && !decision.membershipUnknown && currentKeys) {
        const quarantined = channelQuarantineStore().getQuarantinedKeys(codeName);
        const added = decision.addedOrRemoved.filter((k) => currentKeys.has(k));
        const removed = decision.addedOrRemoved.filter((k) => !currentKeys.has(k));
        quarantineOnlyDrift =
          added.length === 0 && removed.length > 0 && removed.every((k) => quarantined.has(k));
      }

      // ENG-6123: if this agent's managed-MCP set is currently flapping, hold
      // the drift-driven restart the same way a value-only drift does — adopt
      // the new on-disk baseline but DON'T bounce the session. This is the
      // drift-watcher twin of the convergence-loop suppression: without it the
      // restart burn just moves from the '.mcp.json content change' scheduler to
      // here every poll while the desired set oscillates (valai, ENG-6123).
      const flapSuppressed =
        decision.restart &&
        !quarantineOnlyDrift &&
        mcpFlapDampener.isFlapping(codeName, FLAP_CHANNEL_MANAGED_MCP);

      if (decision.membershipUnknown) {
        clearPresenceReaperState(codeName);
        log(
          `[hot-reload] .mcp.json key membership unavailable for '${codeName}' — clearing full presence-reaper state and restarting (ENG-5285/ENG-5537)`,
        );
      } else if (quarantineOnlyDrift) {
        log(
          `[channel-quarantine] .mcp.json drift for '${codeName}' is quarantined-channel removal only [${decision.addedOrRemoved.join(', ')}] — adopting new baseline WITHOUT restart (0-restart-on-removal, ENG-5932)`,
        );
      } else if (flapSuppressed) {
        log(
          `[mcp-flap-dampener] .mcp.json membership drift for '${codeName}' [${decision.addedOrRemoved.join(', ')}] held — managed-MCP set is flapping, adopting baseline WITHOUT restart until it settles (ENG-6123, ENG-6512)`,
        );
      } else if (decision.restart) {
        clearPresenceReaperStateForKeys(codeName, new Set(decision.addedOrRemoved));
        log(
          `[hot-reload] .mcp.json membership changed for '${codeName}': ` +
          `${decision.addedOrRemoved.length} key(s) added/removed [${decision.addedOrRemoved.join(', ')}] — reaper state cleared for those keys only (ENG-5285)`,
        );
      } else {
        log(
          `[hot-reload] .mcp.json value-only drift for '${codeName}' — preserving presence-reaper state and NOT restarting (ENG-5285/ENG-5537)`,
        );
      }

      if (decision.restart && !quarantineOnlyDrift && !flapSuppressed) {
        // ENG-7239 (CodeRabbit PR #2870): tag the restart 'managed-mcp-churn'
        // only when EVERY changed .mcp.json key is a managed-MCP/brokered
        // toolkit server, so the breaker carve-out for a flapping managed-MCP set
        // can't also swallow a co-occurring channel-listener or credential
        // restart. A mixed, channel-only, or membership-unknown drift stays
        // 'hot-reload-mcp' and always counts toward the breaker.
        const driftBreakerReason: RestartReason =
          decision.addedOrRemoved.length > 0 && decision.addedOrRemoved.every(isManagedMcpServerKey)
            ? 'managed-mcp-churn'
            : 'hot-reload-mcp';
        scheduleSessionRestart(codeName, 0, '.mcp.json content change (ENG-4897)', driftBreakerReason);
        // Drop the entries — the next poll after respawn captures the
        // new hash + key set as baseline. Keeping the old values would
        // re-fire every poll until the respawn timer triggers.
        runningMcpHashes.delete(codeName);
        runningMcpServerKeys.delete(codeName);
      } else if (flapSuppressed) {
        // ENG-6123: deliberately do NOT adopt the new baseline while flapping.
        // Keeping the running session's launch-time baseline stale means that
        // once the set finally settles (dampener recovers → flapSuppressed
        // false), this same drift is still pending and fires exactly ONE
        // restart to land the session on the settled config — instead of the
        // session being silently stranded on its stale tool view. The per-poll
        // re-detection until then is a cheap log line, no opus involvement.
      } else {
        // ENG-5537: value-only drift — adopt the new content as the baseline
        // so we don't re-log/re-evaluate every poll, but leave the running
        // session untouched. currentKeys is non-null here (membershipUnknown
        // would have set decision.restart=true), so the key set stays in sync.
        runningMcpHashes.set(codeName, action.current);
        if (currentKeys) runningMcpServerKeys.set(codeName, currentKeys);
      }
      return;
    }
  }
}

/**
 * ENG-9350 (epic ENG-9347): single source of truth for whether this agent's
 * channel bot tokens are delivered via the spawn env (`tmux -e`, tokens off
 * disk) rather than the on-disk `.env.integrations` file. Read from the
 * `spawn-inject-channel-secrets` registry flag, per-agent so a soak can target
 * one agent on a shared host. The manager owns the spawn path, so it evaluates
 * the flag directly (no in-container MCP reads it); it materializes the VALUES.
 * Deterministic within a tick, so the writer/config/drift call sites can each
 * call it and stay in agreement.
 */
function shouldInjectChannelSecrets(agentId: string): boolean {
  return hostFlagStore().getBooleanForAgent('spawn-inject-channel-secrets', agentId);
}

/**
 * ENG-6062: value hash of the channel-owned secrets in the running session's
 * project `.env.integrations`. Returns null when the file is missing /
 * unreadable or carries none of the channel keys (agent has no channels).
 */
function projectChannelSecretHash(projectDir: string): string | null {
  try {
    const entries = parseEnvIntegrations(
      readFileSync(join(projectDir, '.env.integrations'), 'utf-8'),
    );
    return channelSecretValueHash(entries, CHANNEL_SECRET_ENV_KEYS);
  } catch {
    return null;
  }
}

/**
 * ENG-6062: schedule a respawn when a channel token in `.env.integrations`
 * changed since the running session launched. Unlike the `.mcp.json` drift
 * check, a change here is ALWAYS restart-worthy — these keys only move on a
 * deliberate rotation, and the live channel MCP holds the old (now revoked)
 * token in its spawn env until respawned. Reuses the optimistic-baseline
 * primitive and the shared `scheduleSessionRestart` coalescer, so it inherits
 * the maintenance-window deferral and ENG-5441 restart-breaker protection.
 */
function checkChannelSecretDriftAndScheduleRestart(
  codeName: string,
  projectDir: string,
  // ENG-9350: in spawn-env mode the channel tokens are injected via `tmux -e`,
  // NOT written to `.env.integrations` — so the file-based hash returns null and
  // this drift check would go permanently blind (a later rotation would never
  // respawn, leaving the revoked token live in the running MCP's spawn env).
  // When defined, hash THIS in-memory map (the same values the manager just
  // injected at spawn) instead of the file; the baseline/compare machinery is
  // otherwise unchanged. Undefined ⇒ file mode, exactly as before.
  spawnEnvSecrets?: Record<string, string>,
): void {
  const currentHash =
    spawnEnvSecrets !== undefined
      ? channelSecretValueHash(spawnEnvSecrets, CHANNEL_SECRET_ENV_KEYS)
      : projectChannelSecretHash(projectDir);
  const action = decideMcpDriftAction(currentHash, runningChannelSecretHashes.get(codeName));
  switch (action.kind) {
    case 'no-config':
    case 'no-drift':
      return;
    case 'baseline':
      runningChannelSecretHashes.set(codeName, action.hash);
      return;
    case 'drift':
      log(
        `[hot-reload] channel credential rotated for '${codeName}' ` +
        `(${action.previous} → ${action.current}) — respawning to load the new token (ENG-6062)`,
      );
      scheduleSessionRestart(codeName, 0, 'channel credential rotation (ENG-6062)');
      // Drop the baseline — the next poll after respawn re-captures the new
      // hash, so we don't re-fire every poll until the respawn timer runs.
      runningChannelSecretHashes.delete(codeName);
      return;
  }
}
// ADR-0011 PR-A: the per-agent hash caches that were declared here moved
// into `agentState` (./manager/agent-state.ts). Persistence semantics are
// unchanged: `loadChannelHashCache()` / `saveChannelHashCache()` below still
// own the on-disk mirror of `agentState.knownChannelConfigHashes` (ENG-4712).
// Original ENG- annotations preserved at the use sites:
//   - agentState.knownManagedMcpHashes       ENG-4481 (self-heal toolkits without cred change)
//   - agentState.knownManagedMcpStructure    ENG-5537 (structure hash gates session restart;
//                                 prevents URL-rotation flap on Composio)
// ENG-5292: reverse mapping from (agent_id, serverId) to toolkit_id.
// The serverId is the .mcp.json key — the lowercased-non-alnum-to-
// underscore form of toolkit_id that writeMcpServer uses. The reaper's
// onGiveUp callback consults this so it can tell the API which toolkit
// to mark unhealthy.
//
// CodeRabbit on PR #1184: scoped per-agent (composite key) — NOT keyed
// by serverId alone. Two different agents can declare the same
// toolkit (same serverId), and stale entries from a now-removed agent
// must not misattribute a give-up on another agent's MCP to the wrong
// toolkit_id. The composite key also lets us prune per-agent on agent
// removal without a full scan.
// ENG-7543: value carries connection_key too, so the reaper's give-up marks
// ONLY the dead connection unhealthy (not every connection of the toolkit).
const managedToolkitIdByAgentAndServerId = new Map<string, { toolkitId: string; connectionKey: string }>();

function managedToolkitMapKey(agentId: string, serverId: string): string {
  return `${agentId}\x00${serverId}`;
}
// Track last-seen cron run timestamps per job to avoid re-processing
const lastCronRunTs = new Map<string, number>();
// Track last work_trigger_at per agent to avoid duplicate triggers
const lastWorkTriggerAt = new Map<string, number>();
// Track which in_progress items we've already alerted as stale
const alertedStaleItems = new Set<string>();
// ENG-5408: separate set for the structured stuck-task watchdog — we want
// one log line per stuck row per occurrence so CloudWatch metric filters
// can count them, but we still don't want to spam every poll tick for
// the same row. Cleared in lockstep with alertedStaleItems below.
const stuckKanbanLogged = new Set<string>();
// Track agents with known API key errors (avoid spamming the API)
const apiKeyStatusCache = new Map<string, boolean>();
// ENG-5408: threshold env var, falls back to 30 min. The existing
// auto-fail path (around manager-worker.ts:5042) and the new structured
// stuck-task watchdog log line both honour this — operators can tighten
// or loosen the budget without a code change.
const STALE_TASK_THRESHOLD_MS = (() => {
  const env = process.env['AUGMENTED_STUCK_TASK_THRESHOLD_MINUTES'];
  const minutes = env ? Number.parseInt(env, 10) : NaN;
  if (Number.isFinite(minutes) && minutes > 0) return minutes * 60 * 1000;
  return 30 * 60 * 1000;
})();

// Track alerted job IDs to avoid duplicate alerts within a session
const alertedJobs = new Set<string>();
// Map cron job name (aug:template:uuid) → human-readable task info
const taskDisplayInfo = new Map<string, { taskName: string; schedule: string; agentDisplayName: string }>();
// ADR-0011 PR-A: agentState.agentDisplayNames + agentState.codeNameToAgentId moved into `agentState`
// (./manager/agent-state.ts). Use `agentState.agentDisplayNames`,
// `agentState.codeNameToAgentId`.
// ADR-0011 PR-4: agentChannelTokens (code_name → channel bot tokens) and its
// test-only seeder __setAgentChannelTokensForTest moved into
// ./manager/channels/state.ts (imported above).

// Per-cycle channel tracking — populated by processAgent, reconciled after all agents
const activeChannels = new Map<string, Set<string>>(); // channelId -> Set<codeName>

// Cumulative state for the parent
let state: ManagerState = {
  pid: process.pid,
  startedAt: new Date().toISOString(),
  lastPollAt: null,
  pollCount: 0,
  errorCount: 0,
  agents: [],
};

// Cache registered agents per framework per poll cycle
const registeredAgentsCache = new Map<string, Set<string>>();

// Per-agent framework ID cache (populated from API response and refresh data)
const agentFrameworkCache = new Map<string, string>();

// ENG-5901 PR 3: agents whose on-disk secret storage has been migrated this
// manager run (literal-hoist is idempotent; the gate just avoids re-reading
// .mcp.json every poll tick for every agent).
const migratedSecretStorage = new Set<string>();

// Track which framework binaries we've already checked/installed this session
const frameworkBinaryChecked = new Set<string>();
// In-flight framework-binary installs, so concurrent callers coalesce onto one
// attempt and a FAILED install isn't cached (frameworkBinaryChecked is only
// stamped on success) - a transient npm failure retries next cycle.
const frameworkBinaryInflight = new Map<string, Promise<void>>();

// Claude Code auth status — set during ensureFrameworkBinary, checked before launching sessions
let agentRuntimeAuthenticated = false;

// ENG-6068: outcome of the last channels managed-settings assertion
// (ensureClaudeManagedSettings). Anything but 'ok' means Claude Code may be
// silently dropping ALL channel inbound on this host — reported in the host
// heartbeat so the control plane can alert instead of relying on an operator
// reading manager.log.
let lastManagedSettingsStatus: ManagedSettingsStatus | null = null;

/** Resolve the framework adapter for a given agent, using the cache or defaulting to claude-code (ENG-6919). */
function resolveAgentFramework(codeName: string): FrameworkAdapter {
  const frameworkId = agentFrameworkCache.get(codeName) ?? DEFAULT_FRAMEWORK;
  return getFramework(frameworkId);
}

/**
 * Clear all per-agent caches when an agent is unassigned, revoked, or needs
 * a full reprovision. Keeps the cleanup in one place instead of scattering
 * .delete() calls for every new Map we add.
 *
 * ADR-0011 PR-A: the agentState-owned Maps are cleared via `clearAgentState`
 * in ./manager/agent-state.ts. Subsystem-owned caches still live in this
 * file and will lift with their owning module in the stacked follow-up PRs.
 */
function clearAgentCaches(agentId: string, codeName: string): void {
  const channelCacheMutated = clearAgentState(agentId, codeName);

  // ADR-0011 PR-4 / CR #2145: agentChannelTokens now lives in the shared
  // channels/state.ts singleton, so teardown must purge it here too — otherwise
  // a revoked/handed-off/unassigned agent's Slack/Telegram bot tokens stay
  // resident for the rest of the process. All clearAgentCaches callers are
  // teardown paths (host-handoff, decommission, realtime unassign), each having
  // already killed the agent's channel processes, so this never strands an
  // active delivery.
  agentChannelTokens.delete(codeName);

  // ENG-9585: the fetch-spacing state and the cached desired-state payload.
  // Both directions matter. Leaving them behind leaks a full /host/refresh
  // payload per agent for the life of the process — and worse, an agent that is
  // unassigned and later RE-ADDED would be served the payload it had before it
  // left, for up to a TTL, when re-assignment is precisely the moment its
  // desired state is most likely to have changed.
  lastRefreshPayload.delete(agentId);
  // ENG-9584: and the conditional-request board. Same two directions as the
  // refresh payload above — it leaks a board per agent for the life of the
  // process, and a re-added agent would be told its OLD board is still current
  // at exactly the moment its work is most likely to have moved on.
  boardTokenCache.forget(agentId);
  refreshFetchGate.forget(agentId);
  memoriesFetchGate.forget(agentId);
  // ENG-9585 (slice 2): same two directions as the gates above. Leaving these
  // behind holds a TTL entry per agent for the life of the process, and an
  // agent that is unassigned and later RE-ADDED would be told its secrets and
  // default schedules are still current for up to a TTL — at precisely the
  // moment a re-provision is most likely to need them.
  secretsFetchGate.forget(agentId);
  defaultSchedulesFetchGate.forget(agentId);
  // ENG-10061: both integration lanes, gate AND payload. The re-add direction is
  // the sharp one here: an agent that leaves and comes back is re-served the
  // integration set it had BEFORE it left — including credentials — at exactly
  // the moment a re-provision is most likely to need the current one.
  integrationsFetchGate.forget(agentId);
  quarantinedFetchGate.forget(agentId);
  lastIntegrationsPayload.delete(agentId);
  lastQuarantinedPayload.delete(agentId);

  // ENG-9265: and any realtime-rebind streak, for the same reason.
  forgetRealtimeRebindState(agentId);

  // ENG-9252: drop any channel-credential-sync streak. Teardown means nothing
  // will attempt this agent's sync again, so a surviving streak would keep
  // riding the heartbeat with a growing duration and hold a `channel_sync_stuck`
  // alert open about work that is no longer being tried.
  forgetChannelSyncState(agentId);

  // ENG-8116 (CodeRabbit, PR #3752): drop any undrained occupancy. The opencode
  // path clears the ledger via OpencodeActivityTracker.reset(), but Claude Code
  // occupancy is written straight into the shared ledger by the pane sampler
  // with no equivalent teardown. Buckets for a revoked/unassigned agent can
  // never drain (the drain iterates live sessions), so they would sit until
  // evicted — and if the code_name is later reused by a DIFFERENT agent, they
  // would be reported against it and billed to the wrong tenant.
  sharedBusyBuckets.reset(codeName);

  // ADR-0047: for an opencode agent the manager itself holds the Slack socket +
  // Telegram poller (there is no channel MCP child for killAgentChannelProcesses
  // to reap), so the "channel processes already killed" precondition above only
  // holds for Claude agents. Stop them here or a revoked / unassigned opencode
  // agent keeps answering Slack / Telegram from this process. No-op for Claude
  // agents and for any opencode agent without a live loop.
  stopOpencodeSlackIngest(codeName, log);
  stopOpencodeTelegramIngest(codeName, log);

  // Subsystem-owned caches (move with their subsystem in PR-B onwards):
  agentFrameworkCache.delete(codeName);
  // ENG-8093: the serve this fingerprint described is gone (ENG-8092 stops it on
  // every deprovision path), so drop it rather than let it accumulate for agents
  // that churn on and off this host.
  opencodeCredentialFingerprintBySession.delete(codeName);
  kanbanBoardCache.delete(codeName);
  // ENG-7798: drop the persisted nudge-backoff state so a re-enrolled agent
  // starts at the fast cadence instead of inheriting an escalated interval
  // from the previous generation's blocked board.
  clearKanbanNudgeState(codeName);
  // Notify dedupe caches: reset so a re-enrolled agent re-seeds (seed-on-first-
  // cycle) rather than inheriting the previous generation's done / waiting-human
  // notification state. (notifyBoardCache was previously missed here too.)
  notifyBoardCache.delete(codeName);
  notifiedWaitingHumanIds.delete(codeName);
  lastHarvestAt.delete(codeName);
  claudeSchedulerStates.delete(codeName);
  claudeTaskConcurrency.delete(codeName);
  // CodeRabbit on PR #1318: drop the per-agent dedupe so a re-enrolled
  // agent's first banner observation isn't suppressed by the previous
  // generation's cached count.
  lastMcpFailedBannerCount.delete(codeName);
  lastModelApiErrorSig.delete(codeName);

  // Memory sync caches
  memoryFileHashes.delete(agentId);
  lastDownloadHash.delete(agentId);
  lastLocalFileHash.delete(agentId);
  // ENG-9584: the conditional-response cache dies wherever `lastDownloadHash`
  // does. Keeping a token past a teardown would have the next enrolment of this
  // agent id present a hash for a corpus it no longer holds the body of.
  lastMemoriesBody.delete(agentId);

  // Compound-keyed (`${codeName}:suffix`) subsystem caches
  for (const key of taskDisplayInfo.keys()) {
    if (key.startsWith(`${codeName}:`)) taskDisplayInfo.delete(key);
  }

  // ENG-6123: drop flap-dampener history + defer-log throttle so a re-enrolled
  // agent starts with a clean flap window.
  mcpFlapDampener.forget(codeName);
  deferLogThrottle.delete(codeName);

  if (channelCacheMutated) saveChannelHashCache();
  // ENG-7771: drop the persisted delivery baselines with the in-memory ones
  // (clearAgentState deleted those above) so a re-enrolled agent gets
  // genuine first-poll semantics instead of a stale claim about a session
  // that no longer exists. ENG-7779: covers all concerns (sender policy +
  // both behaviour subsets).
  removeDeliveryBaselineEntries(agentId);
}

// Cached framework version (detected once at startup, refreshed periodically)
let cachedFrameworkVersion: string | null = null;
// ENG-5963: the host's resolved maintenance window, delivered on each heartbeat
// response (host-runtime.ts). Cached module-level (mirrors cachedFrameworkVersion)
// so the self-update + Claude Code update gates can consult it without an extra
// fetch. null until the first heartbeat returns — the gate fails open in that
// window so a host that hasn't heard from the API isn't frozen out of updates.
let cachedMaintenanceWindow: { start: string; end: string; timezone: string } | null = null;
// ENG-7853: the host's DB-driven agt-cli version pin (hosts.desired_agt_cli_version,
// set from the console), delivered on each heartbeat response. Cached module-level
// like cachedMaintenanceWindow so the self-update pin gate can consult it without an
// extra fetch. null = no DB pin. The explicit AGT_CLI_PIN_VERSION host env var
// (bootstrap / SSH) still wins over this (see resolveAgtCliPinRaw). Applied
// unconditionally each heartbeat so a cleared pin falls back within one poll rather
// than pinning stale; the fire-and-forget self-update at the top of the NEXT poll
// cycle consumes it (a version pin is not latency-critical).
let cachedDesiredAgtCliVersion: string | null = null;
// True once ANY heartbeat has resolved this host's DB pin state. Until then the
// cache above (null) is "not yet heard", NOT "explicitly unpinned"; the
// distinction matters because the fire-and-forget self-update runs before the
// first heartbeat. Without this gate a console-pinned host (no env pin) would
// briefly converge to the channel tip (and restart) on manager start before its
// pin arrives. Flipped true on any heartbeat (even an older API that omits the
// field, which just means "no DB pin capability") so the gate never stalls.
let desiredAgtCliVersionHydrated = false;
// ENG-6695: per-agent timezone inputs captured on each /host/refresh, so the
// per-agent restart gate can interpret the (host-level) maintenance window in
// the operator's zone. Resolution precedence agent → team → host/org-effective
// window tz; when both agent and team are unset this reproduces the cached
// window timezone exactly (today's behaviour).
const agentRestartTimezoneInputs = new Map<
  string,
  {
    agentTimezone: string | null;
    teamTimezone: string | null;
    // ENG-8016: TTL on the agent's own override. Kept as the raw instant
    // rather than a pre-computed boolean so the lapse is evaluated at USE
    // time - a manager can run for a long stretch between refreshes, and a
    // flag baked at refresh time would keep an agent pinned to a zone that
    // expired hours ago.
    agentTimezoneExpiresAt: string | null;
  }
>();
let lastVersionCheckAt = 0;
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000; // Re-check every 5 minutes

// ENG-5399: Tier 1 responsiveness probe — pane.log freshness reported
// per agent on a slow cadence. Throttled here because pollCycle runs
// every 10s but we only need this once every few minutes (the existing
// SyntheticReplyAgeSeconds cron runs every 15 min as the deeper check).
let lastResponsivenessProbeAt = 0;

// agt CLI version, injected by tsup at build time. Reported on heartbeat
// so the host page can surface "which agt is actually running here?".
// In dev (tsx), the constant isn't defined — fall back to 'dev'.
declare const __CLI_VERSION__: string;
const agtCliVersion = typeof __CLI_VERSION__ !== 'undefined' ? __CLI_VERSION__ : 'dev';

// ---------------------------------------------------------------------------
// Framework binary install / upgrade
// ---------------------------------------------------------------------------

/**
 * Ensure a Homebrew formula is installed. Used for framework dependencies
 * like tmux that are required but not part of the framework itself.
 * Returns true if the binary is available after the check.
 */
async function ensureBrewDependency(binary: string, formula: string): Promise<boolean> {
  if (frameworkBinaryChecked.has(`dep:${binary}`)) return true;

  const { execFileSync } = await import('node:child_process');

  try {
    execFileSync('which', [binary], { timeout: 5_000 });
    frameworkBinaryChecked.add(`dep:${binary}`);
    return true;
  } catch {
    // Binary not found — try to install
  }

  let brewPath: string;
  try {
    brewPath = execFileSync('which', ['brew'], { timeout: 5_000 }).toString().trim();
  } catch {
    log(`${binary} not found and Homebrew not available — install manually: brew install ${formula}`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return false;
  }

  log(`${binary} not found — installing via Homebrew...`);
  try {
    execFileSync(brewPath, ['install', formula], { timeout: 120_000, stdio: 'pipe' });
  } catch (err) {
    log(`Failed to install ${formula}: ${(err as Error).message}`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return false;
  }

  // Prepend brew's bin dir to PATH so the post-install verification (and any
  // downstream subprocesses) can find the binary. Brew installs to a custom
  // prefix that isn't on the manager's PATH when spawned by cloud-init.
  const brewBinDir = dirname(brewPath);
  if (!process.env.PATH?.split(':').includes(brewBinDir)) {
    process.env.PATH = `${brewBinDir}:${process.env.PATH ?? ''}`;
  }

  try {
    execFileSync('which', [binary], { timeout: 5_000 });
    log(`${binary} installed successfully`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return true;
  } catch {
    log(`${formula} install completed but ${binary} not found on PATH`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return false;
  }
}

/**
 * Ensure the CLI binary for a framework is available. Currently only handles
 * `claude-code` via Homebrew. Called once per session when we first see an
 * agent using that framework.
 */
/**
 * Resolve the brew binary. `which brew` fails when PATH doesn't include the
 * Homebrew bin dir (common when the manager is spawned by cloud-init with a
 * minimal env). Fall back to the canonical Linuxbrew install location.
 */
function resolveBrewPath(execFileSync: typeof import('node:child_process').execFileSync): string | null {
  try {
    const out = execFileSync('which', ['brew'], { timeout: 5_000 }).toString().trim();
    if (out) return out;
  } catch {
    // fall through to absolute path check
  }
  // Canonical install locations across the three platforms we run on.
  // Linuxbrew first (the AL2023 hosts the manager runs on most often),
  // then Apple-silicon macOS (`/opt/homebrew`), then Intel macOS
  // (`/usr/local`). Without the macOS entries the launchd-spawned
  // manager bails out of self-update on Macs whose sparse PATH doesn't
  // include the brew bin dir.
  const fallbacks = [
    '/home/linuxbrew/.linuxbrew/bin/brew',
    '/opt/homebrew/bin/brew',
    '/usr/local/bin/brew',
  ];
  for (const path of fallbacks) {
    if (existsSync(path)) return path;
  }
  return null;
}

/**
 * Detect whether the `claude` binary is installed on this host. Mirrors
 * resolveBrewPath's three-platform-fallback shape: Linuxbrew (EC2 default),
 * Apple-silicon macOS, Intel macOS — then `which claude` as a last resort
 * for unusual install locations.
 *
 * Pre-CodeRabbit fix (PR #1226 review): only the Linuxbrew path was
 * checked before falling back to `which`, which fails on macOS launchd
 * with a sparse PATH. The manager would then treat Claude as missing,
 * stamp the upgrade marker, and skip the upgrade — silently never
 * refreshing Claude Code on dev macs.
 */
function claudeBinaryInstalled(
  execFileSync: typeof import('node:child_process').execFileSync,
): boolean {
  const canonical = [
    '/home/linuxbrew/.linuxbrew/bin/claude',
    '/opt/homebrew/bin/claude',
    '/usr/local/bin/claude',
  ];
  if (canonical.some((path) => existsSync(path))) return true;
  try {
    execFileSync('which', ['claude'], { timeout: 5_000 });
    return true;
  } catch {
    return false;
  }
}

/**
 * ENG-4421: resolve a toolkit → install its CLI if missing.
 *
 * Plugins declare `required_toolkits: [...]`. The manager unions these across
 * an agent's installed plugins and calls this per toolkit. Idempotent: a hit
 * on `command -v <binary>` short-circuits; the in-memory guard makes repeat
 * polls cheap.
 *
 * Installer strategies are declared in the catalog (`IntegrationCliTool.installer`):
 *   - 'npm'    : `npm install -g <package>`
 *   - 'brew'   : `brew install <package>` (proxied via ec2-user when running as root)
 *   - 'script' : runs `cli_tool.script` verbatim. The catalog is the trust boundary —
 *                never take the script from plugin-side data.
 *   - 'manual' : skip — logged as a hint for the operator.
 *
 * All failures are non-fatal: the manager logs and moves on so a missing CLI
 * doesn't block the rest of the agent's provisioning.
 *
 * Caching semantics:
 *   - `toolkitCliEnsured` caches *permanent* outcomes: success, 'manual'
 *     installer (operator's responsibility), or catalog authoring errors
 *     (missing package/script). Those states won't change poll-to-poll.
 *   - `toolkitCliRetryAfter` caches *transient* failures (network, brew
 *     refused root, npm 500, post-install verify fail) with a 5-min
 *     cooldown. This lets a retry recover automatically instead of
 *     requiring a manager restart.
 */
const toolkitCliEnsured = new Set<string>();
const toolkitCliRetryAfter = new Map<string, number>();
const toolkitCliFailureCount = new Map<string, number>();
const TOOLKIT_INSTALL_RETRY_MS = 5 * 60_000;
// After this many consecutive failures, mark the toolkit permanently
// failed for the lifetime of the manager process. Stops the log spam
// when an upstream package is broken (e.g. linear-cli's musl fallback
// doesn't exist) or the host is missing some prereq we can't auto-
// install. Operator restarts the manager to retry.
const TOOLKIT_INSTALL_MAX_FAILURES = 3;

function recordToolkitFailure(toolkitSlug: string, reason: string): void {
  const count = (toolkitCliFailureCount.get(toolkitSlug) ?? 0) + 1;
  toolkitCliFailureCount.set(toolkitSlug, count);
  if (count >= TOOLKIT_INSTALL_MAX_FAILURES) {
    log(`[toolkit-install] ${toolkitSlug}: ${reason} (giving up after ${count} attempts — restart the manager to retry)`);
    toolkitCliEnsured.add(toolkitSlug);
    toolkitCliRetryAfter.delete(toolkitSlug);
  } else {
    log(`[toolkit-install] ${toolkitSlug}: ${reason} (attempt ${count}/${TOOLKIT_INSTALL_MAX_FAILURES}, retrying in ${TOOLKIT_INSTALL_RETRY_MS / 60_000}m)`);
    toolkitCliRetryAfter.set(toolkitSlug, Date.now() + TOOLKIT_INSTALL_RETRY_MS);
  }
}

async function ensureToolkitCli(toolkitSlug: string): Promise<void> {
  if (toolkitCliEnsured.has(toolkitSlug)) return;
  const retryAfter = toolkitCliRetryAfter.get(toolkitSlug) ?? 0;
  if (retryAfter > Date.now()) return;

  const integration = getIntegration(toolkitSlug);
  if (!integration?.cli_tool) {
    // Unknown toolkit or no CLI declared — permanent, won't change between polls.
    toolkitCliEnsured.add(toolkitSlug);
    return;
  }

  const { binary, installer, package: pkg, script } = integration.cli_tool;
  const resolvedInstaller = installer ?? 'manual';

  const { execFileSync, execSync } = await import('node:child_process');

  // Fast path: already on PATH.
  try {
    execFileSync('which', [binary], { timeout: 5_000, stdio: 'pipe' });
    toolkitCliEnsured.add(toolkitSlug);
    toolkitCliRetryAfter.delete(toolkitSlug);
    toolkitCliFailureCount.delete(toolkitSlug);
    return;
  } catch { /* not on PATH — attempt install */ }

  if (resolvedInstaller === 'manual') {
    log(`[toolkit-install] ${toolkitSlug}: binary '${binary}' missing, installer=manual — operator must install`);
    // Not going to change without operator action; mark permanent to avoid log spam.
    toolkitCliEnsured.add(toolkitSlug);
    return;
  }

  let brewBinDir: string | null = null;
  try {
    if (resolvedInstaller === 'npm') {
      if (!pkg) {
        log(`[toolkit-install] ${toolkitSlug}: installer=npm but no package declared`);
        // Catalog authoring mistake — permanent, retrying won't fix it.
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      log(`[toolkit-install] ${toolkitSlug}: installing via npm (${pkg})…`);
      execFileSync('npm', ['install', '-g', pkg], { timeout: 180_000, stdio: 'pipe' });
    } else if (resolvedInstaller === 'brew') {
      if (!pkg) {
        log(`[toolkit-install] ${toolkitSlug}: installer=brew but no package declared`);
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      const brewPath = resolveBrewPath(execFileSync);
      if (!brewPath) {
        log(`[toolkit-install] ${toolkitSlug}: installer=brew but Homebrew not available — install manually: brew install ${pkg}`);
        // Env-level problem — unlikely to self-heal; mark permanent.
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      brewBinDir = dirname(brewPath);
      const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
      log(`[toolkit-install] ${toolkitSlug}: installing via brew (${pkg})…`);
      if (isRoot) {
        // cwd: '/tmp' — brew refuses if the cwd isn't readable to
        // ec2-user; the manager's cwd is /root (mode 0700) by default.
        execFileSync('sudo', ['-u', 'ec2-user', '-H', brewPath, 'install', pkg], { timeout: 180_000, stdio: 'pipe', cwd: '/tmp' });
      } else {
        execFileSync(brewPath, ['install', pkg], { timeout: 180_000, stdio: 'pipe' });
      }
    } else if (resolvedInstaller === 'script') {
      if (!script) {
        log(`[toolkit-install] ${toolkitSlug}: installer=script but no script declared`);
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      log(`[toolkit-install] ${toolkitSlug}: running declared install script…`);
      // The script comes from the catalog (source-controlled) — never from
      // runtime plugin data. execSync is safe here because the catalog is
      // the trust boundary.
      execSync(script, { timeout: 180_000, stdio: 'pipe' });
    }
  } catch (err) {
    // execSync/execFileSync surface the child's real failure reason on `.stderr`,
    // not in `.message` (which for a `script` installer is just "Command failed:
    // <the entire install script>"). Prefer stderr so the log line is actionable
    // (e.g. the script's own "gh: no supported installer" or a dnf/apt error)
    // instead of a truncated echo of the command (ENG-7662).
    const e = err as Error & { stderr?: Buffer | string };
    const stderr = e.stderr ? e.stderr.toString().trim() : '';
    const detail = (stderr || e.message).slice(0, 300);
    recordToolkitFailure(toolkitSlug, `installer=${resolvedInstaller} failed: ${detail}`);
    return;
  }

  // Prepend brew's bin dir to PATH so the post-install `which` + any downstream
  // spawns can actually find the new binary. Brew installs to a custom prefix
  // (e.g. /home/linuxbrew/.linuxbrew/bin) that isn't on the manager's PATH when
  // spawned by cloud-init with a minimal env.
  if (brewBinDir && !process.env.PATH?.split(':').includes(brewBinDir)) {
    process.env.PATH = `${brewBinDir}:${process.env.PATH ?? ''}`;
  }

  // Verify the binary landed on PATH after install.
  try {
    execFileSync('which', [binary], { timeout: 5_000, stdio: 'pipe' });
    log(`[toolkit-install] ${toolkitSlug}: installed — ${binary} now on PATH`);
    toolkitCliEnsured.add(toolkitSlug);
    toolkitCliRetryAfter.delete(toolkitSlug);
    toolkitCliFailureCount.delete(toolkitSlug);
  } catch {
    recordToolkitFailure(toolkitSlug, `installer=${resolvedInstaller} completed but ${binary} still not on PATH`);
  }
}

// ── ENG-7972: agt-runtime isolation image pull ──────────────────────────────
// Docker-isolated hosts no longer hand-build the agt-runtime image; the
// control-plane deploy builds + pushes it to ECR Public (public.ecr.aws) and
// hands each host the exact digest URI via /host/refresh. This ensures that
// digest is pulled and retagged as the local `agt-runtime:latest` that the
// spawn path (buildDockerRunCommand → AGT_ISOLATION_IMAGE || 'agt-runtime:latest')
// resolves - so build-once/pull-many replaces build-on-every-host, and hosts in
// customer-owned AWS accounts pull the same image with no cross-account IAM.
//
// A public.ecr.aws image pulls anonymously, so the AWS CLI is OPTIONAL here (it
// only buys higher pull rate limits when present). A private *.dkr.ecr.* URI
// (reachable only via an operator AGT_ISOLATION_IMAGE override, which actually
// short-circuits this function) would require the AWS CLI to authenticate; that
// branch is kept for completeness.
//
// STRICTLY FAIL-OPEN: any failure (docker missing, login, pull, tag) leaves
// whatever local image already exists in place and only logs. A broken pull
// must never make agent spawning worse than the pre-ENG-7972 hand-baked image -
// the host keeps running its current image and retries next poll.
//
// Keyed on the (digest) URI: a new deploy changes the digest, so a new URI
// re-pulls; an unchanged URI hits the ensured-set fast path and is a no-op.
const isolationImageEnsured = new Set<string>();
const isolationImageRetryAfter = new Map<string, number>();
const isolationImageFailureCount = new Map<string, number>();
const isolationImageInFlight = new Set<string>();
const ISOLATION_IMAGE_RETRY_MS = 5 * 60_000;
const ISOLATION_IMAGE_MAX_FAILURES = 3;

function isolationImageErrDetail(err: unknown): string {
  const e = err as Error & { stderr?: Buffer | string };
  const stderr = e?.stderr ? e.stderr.toString().trim() : '';
  return (stderr || e?.message || String(err)).slice(0, 300);
}

function recordIsolationImageFailure(imageUri: string, reason: string): void {
  const count = (isolationImageFailureCount.get(imageUri) ?? 0) + 1;
  isolationImageFailureCount.set(imageUri, count);
  if (count >= ISOLATION_IMAGE_MAX_FAILURES) {
    log(`[isolation-image] ${reason} (giving up after ${count} attempts - existing local image stays in use; restart the manager to retry)`);
    isolationImageEnsured.add(imageUri);
    isolationImageRetryAfter.delete(imageUri);
  } else {
    log(`[isolation-image] ${reason} (attempt ${count}/${ISOLATION_IMAGE_MAX_FAILURES}, retrying in ${ISOLATION_IMAGE_RETRY_MS / 60_000}m)`);
    isolationImageRetryAfter.set(imageUri, Date.now() + ISOLATION_IMAGE_RETRY_MS);
  }
}

/**
 * Pull the control-plane-designated agt-runtime image and point the local
 * `agt-runtime:latest` tag at it. No-op unless the host is Docker-isolated and
 * the control plane handed us an image URI. See the block comment above.
 */
async function ensureIsolationImage(imageUri: string): Promise<void> {
  // Operator override wins: if AGT_ISOLATION_IMAGE pins a specific image, the
  // manager doesn't manage the tag (env is highest precedence, per convention).
  if (process.env.AGT_ISOLATION_IMAGE) return;
  if (!imageUri || isolationImageEnsured.has(imageUri)) return;
  if (isolationImageInFlight.has(imageUri)) return;
  const retryAfter = isolationImageRetryAfter.get(imageUri) ?? 0;
  if (retryAfter > Date.now()) return;

  const registry = imageUri.split('/')[0] ?? '';
  const isPublic = registry === 'public.ecr.aws';
  const privRegion = registry.match(/\.dkr\.ecr\.([a-z0-9-]+)\.amazonaws\.com$/)?.[1];
  if (!isPublic && !privRegion) {
    // Not an ECR URI we know how to handle. Won't change poll-to-poll, so mark
    // permanent to avoid log spam; an operator pinning some other image should
    // use AGT_ISOLATION_IMAGE instead.
    log(`[isolation-image] '${imageUri}' is not a recognised ECR image URI - skipping managed pull`);
    isolationImageEnsured.add(imageUri);
    return;
  }

  isolationImageInFlight.add(imageUri);
  try {
    const { execFileSync } = await import('node:child_process');
    // 0. Docker is required (it runs the sandbox and does the pull). Check
    // explicitly so the log names the missing tool instead of a cryptic ENOENT.
    // Uses the retry cooldown (not permanent) so it self-heals once Docker is
    // installed (Docker is itself an isolation-enablement prerequisite; see the
    // runbook docs/runbooks/agt-runtime-image-pipeline.md).
    try {
      execFileSync('which', ['docker'], { timeout: 5_000, stdio: 'pipe' });
    } catch {
      recordIsolationImageFailure(imageUri, `'docker' not found on host - required to pull the isolation image (install it, then the manager retries)`);
      return;
    }
    // The AWS CLI is optional for a public.ecr.aws pull (anonymous works; it
    // only raises rate limits), but required to authenticate a private pull.
    const hasAws = (() => {
      try { execFileSync('which', ['aws'], { timeout: 5_000, stdio: 'pipe' }); return true; }
      catch { return false; }
    })();

    // 1. Authenticate. Public: best-effort (fall through to anonymous on any
    // failure or when aws is absent). Private: mandatory.
    if (isPublic) {
      if (hasAws) {
        try {
          const password = execFileSync('aws', ['ecr-public', 'get-login-password', '--region', 'us-east-1'], {
            timeout: 30_000,
            stdio: ['ignore', 'pipe', 'pipe'],
          }).toString().trim();
          execFileSync('docker', ['login', '--username', 'AWS', '--password-stdin', 'public.ecr.aws'], {
            input: password,
            timeout: 30_000,
            stdio: ['pipe', 'pipe', 'pipe'],
          });
        } catch (err) {
          // Non-fatal: an anonymous pull still works (just rate-limited).
          log(`[isolation-image] ecr-public login failed, falling back to anonymous pull: ${isolationImageErrDetail(err)}`);
        }
      }
    } else if (privRegion) {
      if (!hasAws) {
        recordIsolationImageFailure(imageUri, `'aws' CLI not found - required to authenticate a private ECR pull of ${registry} (install it, then the manager retries)`);
        return;
      }
      try {
        const password = execFileSync('aws', ['ecr', 'get-login-password', '--region', privRegion], {
          timeout: 30_000,
          stdio: ['ignore', 'pipe', 'pipe'],
        }).toString().trim();
        execFileSync('docker', ['login', '--username', 'AWS', '--password-stdin', registry], {
          input: password,
          timeout: 30_000,
          stdio: ['pipe', 'pipe', 'pipe'],
        });
      } catch (err) {
        recordIsolationImageFailure(imageUri, `ecr login for ${registry} failed: ${isolationImageErrDetail(err)}`);
        return;
      }
    }
    // 2. Pull (slow, ~700MB). runAsync keeps the event loop alive during it.
    log(`[isolation-image] pulling ${imageUri}…`);
    const pull = await runAsync('docker', ['pull', imageUri], { timeout: 600_000 });
    if (pull.code !== 0) {
      recordIsolationImageFailure(imageUri, `docker pull failed (exit ${pull.code}): ${(pull.stderr || pull.stdout).slice(0, 300)}`);
      return;
    }
    // 3. Retag to the local name the spawn path resolves.
    const localTag = 'agt-runtime:latest';
    const tag = await runAsync('docker', ['tag', imageUri, localTag], { timeout: 30_000 });
    if (tag.code !== 0) {
      recordIsolationImageFailure(imageUri, `docker tag → ${localTag} failed (exit ${tag.code}): ${tag.stderr.slice(0, 200)}`);
      return;
    }
    log(`[isolation-image] ${localTag} now points at ${imageUri}`);
    // ENG-8401 (docker-hygiene, Layer 1): this retag just orphaned the PRIOR
    // agt-runtime image — the tag moved to the new digest, leaving the old
    // ~850MB layer untagged with nothing to reap it. Clean it up, gated on the
    // flag. Best-effort: it runs AFTER the pull/retag has already succeeded, so
    // a docker failure here can't undo that success.
    if (shouldReapOnRetag(hostFlagStore().getString('docker-hygiene'))) {
      await reapSupersededRuntimeImages(imageUri, localTag);
    }
    isolationImageEnsured.add(imageUri);
    isolationImageRetryAfter.delete(imageUri);
    isolationImageFailureCount.delete(imageUri);
  } finally {
    isolationImageInFlight.delete(imageUri);
  }
}

/**
 * ENG-8401 (docker-hygiene): which local `agt-runtime` image ids to reap after a
 * retag. After `ensureIsolationImage` moves `agt-runtime:latest` onto a freshly
 * pulled digest, every PRIOR image under the same runtime repo is superseded —
 * the tag has moved, so the old ~850MB layer lingers untagged with nothing to
 * remove it. The rule is simply "every image under the repo that isn't the one we
 * just tagged". Pure + exported so the selection is unit-tested without docker.
 *
 * `currentId` is the full `sha256:…` id of the just-tagged image; `repoImageIds`
 * are the ids docker lists for the runtime repo. The just-tagged image — and thus
 * `agt-runtime:latest`, which points at it — is never returned.
 */
export function supersededRuntimeImageIds(
  currentId: string,
  repoImageIds: readonly string[],
): string[] {
  const cur = currentId.trim();
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of repoImageIds) {
    const id = raw.trim();
    if (!id || id === cur || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** ENG-8401: docker-hygiene modes that reap superseded images after a retag. */
export function shouldReapOnRetag(mode: string): boolean {
  return mode === 'reap-superseded' || mode === 'full';
}

/**
 * ENG-8401 (docker-hygiene, Layer 1): remove the agt-runtime images a retag just
 * superseded.
 *
 * Lists images under the runtime REPO (the ECR repo the manager pulled from — a
 * different docker repo name than the local `agt-runtime:latest` tag, so that tag
 * can't be listed here) and `docker rmi`s every id that isn't the one we just
 * tagged. Entirely best-effort: an image still held by a running agent container
 * fails `rmi` and is skipped (a later sweep reaps it once the container cycles),
 * and any docker error is logged and swallowed. Never uses `-a`/`--volumes`/
 * `prune` — only targeted `rmi` of superseded runtime images.
 */
async function reapSupersededRuntimeImages(imageUri: string, localTag: string): Promise<void> {
  try {
    // repo = imageUri minus its @digest (or :tag) suffix, e.g.
    // public.ecr.aws/m5u0e9t1/agt-runtime@sha256:… → public.ecr.aws/m5u0e9t1/agt-runtime
    const repo = imageUri.replace(/@sha256:[a-f0-9]+$/i, '').replace(/:[^/:]+$/, '');
    const idRes = await runAsync('docker', ['image', 'inspect', '--format', '{{.Id}}', localTag], { timeout: 15_000 });
    if (idRes.code !== 0) {
      log(`[docker-gc] could not resolve ${localTag} id, skipping reap: ${idRes.stderr.trim().slice(0, 160)}`);
      return;
    }
    const currentId = idRes.stdout.trim();
    const lsRes = await runAsync('docker', ['images', '--no-trunc', '--format', '{{.ID}}', repo], { timeout: 15_000 });
    if (lsRes.code !== 0) {
      log(`[docker-gc] could not list ${repo} images, skipping reap: ${lsRes.stderr.trim().slice(0, 160)}`);
      return;
    }
    const targets = supersededRuntimeImageIds(currentId, lsRes.stdout.split('\n'));
    let reaped = 0;
    for (const id of targets) {
      const rm = await runAsync('docker', ['rmi', id], { timeout: 30_000 });
      if (rm.code === 0) reaped++;
      else log(`[docker-gc] skip ${id.slice(0, 19)}: ${(rm.stderr || rm.stdout).trim().slice(0, 120)}`);
    }
    if (reaped > 0) log(`[docker-gc] reaped ${reaped} superseded ${repo} image(s)`);
  } catch (err) {
    log(`[docker-gc] reap failed: ${isolationImageErrDetail(err)}`);
  }
}

/**
 * Run a command and capture stdout, without blocking the Node event loop.
 *
 * `execFileSync` blocks the entire process until the child returns, which on
 * a slow `brew upgrade --cask claude-code` can be 30–120s. Every other
 * manager poll task — HTTP, WS heartbeats, agent processing — stalls for
 * that whole window. Switching to `spawn` + a Promise wrapper keeps the
 * event loop alive while brew runs.
 */
function runAsync(
  cmd: string,
  args: string[],
  opts: { timeout: number; cwd?: string },
): Promise<{ code: number; stdout: string; stderr: string }> {
  return new Promise((resolve, reject) => {
    import('node:child_process').then(({ spawn }) => {
      const child = spawn(cmd, args, { stdio: ['ignore', 'pipe', 'pipe'], cwd: opts.cwd });
      let stdout = '';
      let stderr = '';
      // 'close' only fires after stdio streams drain, which can hang
      // indefinitely if the child ignores SIGTERM. Settle the promise the
      // moment the deadline hits so callers don't block past `opts.timeout`,
      // then escalate to SIGKILL as a backstop.
      let settled = false;
      const timer = setTimeout(() => {
        child.kill('SIGTERM');
        if (settled) return;
        settled = true;
        reject(new Error(`${cmd} ${args.join(' ')} timed out after ${opts.timeout}ms`));
        setTimeout(() => { try { child.kill('SIGKILL'); } catch { /* already gone */ } }, 5_000).unref();
      }, opts.timeout);

      child.stdout?.on('data', (b) => { stdout += b.toString(); });
      child.stderr?.on('data', (b) => { stderr += b.toString(); });
      child.on('error', (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      });
      child.on('close', (code) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ code: code ?? -1, stdout, stderr });
      });
    }).catch(reject);
  });
}

/**
 * Path to Claude Code's *managed*-settings file. macOS reads it from
 * /Library/Application Support/ClaudeCode/, every other platform (Linux EC2
 * hosts) from /etc/claude-code/. This is the enterprise-controlled policy
 * file — distinct from user settings.json, which the channels gate ignores.
 */
export function claudeManagedSettingsPath(): string {
  // ENG-10806: derived from the SAME constant `buildDockerRunCommand` mounts
  // into isolated containers, so the write target and the mount cannot drift.
  // They did not drift before - the mount simply did not exist - but a second
  // hard-coded copy is how that gap would come back after being closed.
  return join(CLAUDE_MANAGED_SETTINGS_DIR, 'managed-settings.json');
}

/**
 * ENG-5786: Claude Code 2.1.158+ gates the channels feature behind a managed
 * setting. Unless `channelsEnabled: true` is present in the managed-settings
 * file, CC blocks `--dangerously-load-development-channels` (how the manager
 * pushes telegram / direct-chat / slack inbound into the agent session) and
 * SILENTLY DROPS all inbound — no error, the agent just goes quiet on every
 * channel. Host provisioning historically never wrote this file, so any host
 * that auto-upgraded to 2.1.158+ on subscription/Team auth went dark.
 *
 * Ensure the file exists with channelsEnabled:true, *merging* (never clobbering)
 * any other keys. Runs once per manager process for claude-code hosts, so it
 * self-heals existing hosts (fleet backfill) and re-asserts after a CC upgrade
 * or host re-provision. Idempotent: only writes when the value is missing/wrong.
 * Best-effort — a write failure (e.g. non-root without perms on macOS
 * /Library) is logged, not fatal.
 *
 * ENG-6068: returns the assertion outcome so the heartbeat can report it —
 * 'invalid_json' and 'write_failed' both mean channels may be silently
 * blocked, and the control plane alerts on them instead of the failure
 * living only in manager.log.
 *
 * ENG-9223: the file now also carries `enableAllProjectMcpServers: true` —
 * see MANAGED_SETTINGS_KEYS below.
 */
export type ManagedSettingsStatus = 'ok' | 'invalid_json' | 'write_failed';

/**
 * ENG-9223: the managed-settings keys this host MUST assert, and why each one
 * is in this file rather than user settings.
 *
 * - `channelsEnabled` (ENG-5786): without it Claude Code blocks
 *   `--dangerously-load-development-channels` and silently drops all inbound.
 *
 * - `enableAllProjectMcpServers`: without it, a session that finds servers in
 *   the project's `.mcp.json` it has not been told to trust BLOCKS THE TUI on
 *   "N new MCP servers found in this project". On a headless host nobody
 *   answers it, `augmented` and `direct-chat` never bind, mcp-presence-reaper
 *   restarts the session to recover them, and the new session renders the
 *   identical prompt. The trust answer is only ever persisted to
 *   `localSettings` by the interactive path, so a restart structurally cannot
 *   make progress — it ends when the restart circuit breaker pauses the agent.
 *   Both customer orgs onboarded on 2026-08-20 died exactly this way.
 *
 *   There is no trust decision for a human to make here: the servers offered
 *   are the ones WE provisioned into `.mcp.json` (`augmented`, `direct-chat`,
 *   and the agent's own integrations). Answering it at provision time is the
 *   only shape that survives — ENG-9224 fixed the auto-answer to match the
 *   2.1.237 checkbox widget after ENG-5375 had already fixed it for the 2.1.146
 *   rewording, and matching whatever widget ships today is a treadmill. A
 *   dialog that never renders cannot be missed.
 *
 * Verified against the shipped bundle, not inferred from docs (the key is
 * absent from the public settings reference): in both 2.1.220 and 2.1.237 the
 * MCP approval check iterates every settings source and returns approved when
 * ANY of them carries `enableAllProjectMcpServers`, and the source list always
 * has `"policySettings"` — Claude Code's internal name for this managed file —
 * force-added to it, independent of `allowedSettingSources`. So a value here
 * is honoured and cannot be un-set by user or project settings.
 */
const MANAGED_SETTINGS_KEYS = ['channelsEnabled', 'enableAllProjectMcpServers'] as const;

/**
 * ENG-10620: env this host MUST assert in the managed settings' `env` block.
 *
 * `DISABLE_AUTOUPDATER` was previously set only on the manager process and
 * the agent wrapper (ENG-9577), so it reached only the `claude` processes the
 * manager itself starts. An operator's `sudo -i` shell on agt-aws-1 ran
 * `claude` without it; that process self-updated a native install to 2.1.274
 * and rewrote `~/.claude.json` to `installMethod: "native"`, taking the host
 * off its fleet pin.
 *
 * Why the managed `env` block and not anything else, verified against the
 * shipped 2.1.274 bundle: the updater is disabled only by
 * `DISABLE_UPDATES` / `DISABLE_AUTOUPDATER` in the environment, or by config
 * `autoUpdates: false` — and that config value is IGNORED when
 * `installMethod` is `native` and `autoUpdatesProtectedForNative` is true,
 * exactly the state agt-aws-1 was in. Claude Code's own migration persists a
 * user's "updates off" preference as settings `env.DISABLE_AUTOUPDATER`, so
 * settings env is the mechanism it honours; the managed file is the one
 * source every launch reads (see policySettings above), whoever starts it —
 * operator shell, tmux, the pair flow, or a launch path nobody has written yet.
 */
const MANAGED_SETTINGS_ENV: Readonly<Record<string, string>> = { DISABLE_AUTOUPDATER: '1' };

export function ensureClaudeManagedSettings(path: string = claudeManagedSettingsPath()): ManagedSettingsStatus {
  try {
    let settings: Record<string, unknown> = {};
    if (existsSync(path)) {
      const raw = readFileSync(path, 'utf-8').trim();
      if (raw) {
        let parsed: unknown;
        try {
          parsed = JSON.parse(raw);
        } catch (err) {
          // Don't blindly overwrite a managed-settings file we can't parse —
          // an operator may have hand-authored other policy there.
          log(`[managed-settings] ${path} is not valid JSON (${(err as Error).message}) — leaving as-is; Claude Code channels may be blocked`);
          return 'invalid_json';
        }
        // Valid JSON but a non-object (null, array, number, string) can't carry
        // these keys — and dereferencing it would throw. Treat it like an
        // empty object: we overwrite with a well-formed managed-settings file.
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          settings = parsed as Record<string, unknown>;
        }
      }
    }
    // ENG-9223: check EVERY required key, not just the first one. The previous
    // version returned early on `channelsEnabled === true`, which would have
    // made this fix unreachable on exactly the hosts that need it: every host
    // already provisioned by ENG-5786 has channelsEnabled:true, so a
    // first-key-only check would short-circuit before ever writing
    // enableAllProjectMcpServers and the MCP prompt would keep freezing new
    // agents on every host in the fleet. Any key added here from now on
    // backfills the same way.
    const missing = MANAGED_SETTINGS_KEYS.filter((key) => settings[key] !== true);
    // ENG-10620: merge into `env`, never replace it — an operator may carry
    // other policy env there. A non-object `env` is unusable by Claude Code
    // anyway, so it is replaced.
    const existingEnv = settings['env'];
    const env: Record<string, unknown> =
      existingEnv && typeof existingEnv === 'object' && !Array.isArray(existingEnv)
        ? (existingEnv as Record<string, unknown>)
        : {};
    const missingEnv = Object.entries(MANAGED_SETTINGS_ENV).filter(([k, v]) => env[k] !== v);
    if (missing.length === 0 && missingEnv.length === 0) return 'ok'; // already correct — no write
    for (const key of missing) settings[key] = true;
    if (missingEnv.length > 0) {
      for (const [k, v] of missingEnv) env[k] = v;
      settings['env'] = env;
    }
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, `${JSON.stringify(settings, null, 2)}\n`);
    const changed = [
      ...missing.map((k) => `${k}:true`),
      ...missingEnv.map(([k, v]) => `env.${k}=${v}`),
    ];
    log(`[managed-settings] set ${changed.join(', ')} in ${path} (ENG-5786 unblocks Claude Code channels; ENG-9223 pre-approves project MCP servers so a headless session never freezes on the trust prompt; ENG-10620 disables Claude Code's self-updater for every launch so the fleet pin holds)`);
    return 'ok';
  } catch (err) {
    log(`[managed-settings] could not ensure ${path}: ${(err as Error).message} — Claude Code channels may be blocked and a new agent may freeze on the MCP trust prompt; set ${MANAGED_SETTINGS_KEYS.map((k) => `${k}:true`).join(' and ')} manually`);
    return 'write_failed';
  }
}

/**
 * The opencode CLI version installed on opencode hosts. opencode ships as an npm
 * package (`opencode-ai`) whose `opencode serve` HTTP + inbound-bridge contract
 * the manager depends on, so it's pinned to the version that contract was
 * validated against - bumping it requires re-validating the runtime.
 */
const OPENCODE_NPM_VERSION = '1.18.3';

/**
 * Ensure the `opencode` binary is present. opencode runs as a headless
 * `opencode serve` process the manager spawns per agent; unlike Claude Code (a
 * brew cask installed below), NOTHING in host provisioning installs opencode, so
 * a wizard-provisioned opencode host would loop forever spawning a missing
 * binary (session never healthy -> agent never comes online -> deploy times out).
 * Install it once via npm global. Idempotent + best-effort: a failed install logs
 * and returns false rather than crashing the manager - the serve spawn keeps
 * failing visibly until an operator resolves it, and the caller leaves the
 * framework un-cached so the next cycle retries.
 *
 * Returns true only once `opencode --version` actually resolves (already-present
 * OR freshly installed AND re-probed post-PATH-fix); false on any failure.
 */
async function ensureOpencodeBinary(): Promise<boolean> {
  const { execFileSync } = await import('node:child_process');
  const bin = process.env['AGT_OPENCODE_BIN']?.trim() || 'opencode';
  // Cheap liveness probe - if `--version` works, it's installed + on PATH.
  const probe = (): boolean => {
    try {
      execFileSync(bin, ['--version'], { stdio: 'ignore', timeout: 10_000 });
      return true;
    } catch {
      return false;
    }
  };
  if (probe()) return true;
  log('[framework-install] opencode binary not found - installing opencode-ai via npm...');
  try {
    const r = await runAsync('npm', ['install', '-g', `opencode-ai@${OPENCODE_NPM_VERSION}`], { timeout: 180_000 });
    if (r.code !== 0) {
      log(
        `[framework-install] opencode install failed (exit ${r.code}): ${(r.stderr || r.stdout).trim().slice(0, 300)} ` +
          `- install manually: npm i -g opencode-ai@${OPENCODE_NPM_VERSION}`,
      );
      return false;
    }
  } catch (err) {
    log(`[framework-install] opencode install threw: ${(err as Error).message} - install manually: npm i -g opencode-ai@${OPENCODE_NPM_VERSION}`);
    return false;
  }
  // Prepend npm's global bin to PATH so the serve spawn resolves `opencode` on a
  // minimal-PATH host (mirrors the brew-bin PATH fix on the Claude path below).
  try {
    const prefix = execFileSync('npm', ['prefix', '-g'], { encoding: 'utf-8', timeout: 10_000 }).trim();
    if (prefix) {
      const npmBin = join(prefix, 'bin');
      const current = (process.env.PATH ?? '').split(pathDelimiter);
      if (!current.includes(npmBin)) {
        process.env.PATH = [npmBin, ...current.filter(Boolean)].join(pathDelimiter);
      }
    }
  } catch {
    // Best-effort PATH fix; `opencode` on the default PATH covers the common case.
  }
  // Re-verify post-install: only report success if the binary now resolves, so a
  // partial/broken install isn't cached as "checked" (the caller retries next cycle).
  if (!probe()) {
    log(`[framework-install] opencode installed but '${bin} --version' still fails to resolve - will retry next cycle`);
    return false;
  }
  log('[framework-install] opencode installed');
  return true;
}

async function ensureFrameworkBinary(frameworkId: string): Promise<void> {
  if (frameworkId !== 'claude-code' && frameworkId !== 'opencode') return;
  if (frameworkBinaryChecked.has(frameworkId)) return;

  // opencode: install the npm binary and stop - the Claude-specific brew/managed-
  // settings path below does not apply. Cache "checked" ONLY on a verified
  // install, and coalesce concurrent callers onto one in-flight attempt so a
  // failed install both (a) isn't cached (retries next cycle) and (b) doesn't
  // let a parallel caller proceed against a missing binary.
  if (frameworkId === 'opencode') {
    const inflight = frameworkBinaryInflight.get(frameworkId);
    if (inflight) return inflight;
    const attempt = (async () => {
      const ok = await ensureOpencodeBinary();
      if (ok) frameworkBinaryChecked.add(frameworkId);
    })().finally(() => frameworkBinaryInflight.delete(frameworkId));
    frameworkBinaryInflight.set(frameworkId, attempt);
    return attempt;
  }

  // claude-code: brew-cask path below is idempotent and self-healing, so mark
  // checked up front (its own internal probes gate the actual install work).
  frameworkBinaryChecked.add(frameworkId);

  // ENG-5786: assert the channels managed-setting before anything else. Runs
  // regardless of how (or whether) the binary gets installed below, so hosts
  // that already have Claude Code — including ones that silently auto-upgraded
  // to 2.1.158+ — get backfilled on the next manager start.
  lastManagedSettingsStatus = ensureClaudeManagedSettings();

  const { execFileSync } = await import('node:child_process');

  const brewPath = resolveBrewPath(execFileSync);
  if (!brewPath) {
    log('Homebrew not found (no `brew` on PATH, no /home/linuxbrew/.linuxbrew/bin/brew). Cannot auto-install Claude Code. Install manually: https://claude.ai/download');
    return;
  }
  log(`Using brew at ${brewPath}`);

  // Brew refuses to run as root. When the manager is spawned by the EC2
  // bootstrap (user-data runs as root), proxy the brew call through ec2-user.
  // Otherwise invoke brew directly.
  //
  // The cwd matters: brew refuses to run if the cwd isn't readable to the
  // user it'll run as. The manager's cwd is typically /root (mode 0700)
  // when launched by cloud-init, which ec2-user can't read — so the
  // sudo'd brew bails out with "current working directory must be
  // readable to ec2-user". /tmp is world-readable and has no other
  // semantic meaning to brew.
  const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
  const runBrew = (args: string[], opts: { timeout: number }) => {
    if (isRoot) {
      return runAsync('sudo', ['-u', 'ec2-user', '-H', brewPath, ...args], { ...opts, cwd: '/tmp' });
    }
    return runAsync(brewPath, args, opts);
  };

  const claudeExists = claudeBinaryInstalled(execFileSync);

  if (!claudeExists) {
    log(`Claude Code binary not found — installing via Homebrew${isRoot ? ' (as ec2-user via sudo)' : ''}...`);
    try {
      const r = await runBrew(['install', '--cask', 'claude-code'], { timeout: 120_000 });
      if (r.code !== 0) {
        log(`Claude Code install failed (exit ${r.code}): ${r.stderr.trim() || r.stdout.trim()}`);
        return;
      }
    } catch (err) {
      log(`Claude Code install failed: ${(err as Error).message}`);
      return;
    }

    // Prepend brew's bin dir to PATH so subsequent `which claude` /
    // resolveClaudeBinary calls find the new binary on minimal-PATH hosts
    // (e.g. EC2 cloud-init root env). Mirrors ensureBrewDependency().
    const brewBinDir = dirname(brewPath);
    if (!process.env.PATH?.split(':').includes(brewBinDir)) {
      process.env.PATH = `${brewBinDir}:${process.env.PATH ?? ''}`;
    }

    // Verify
    if (existsSync('/home/linuxbrew/.linuxbrew/bin/claude')) {
      log('Claude Code installed successfully');
    } else {
      log('Claude Code install completed but binary not found at expected path — check brew logs');
    }

    // ENG-5357: fresh install is by definition the latest version — stamp
    // the upgrade marker so the next poll's maybeUpgradeClaudeCode() won't
    // immediately fire a redundant `brew upgrade`. Same disk-persisted
    // throttle described next to maybeUpgradeClaudeCode().
    stampClaudeCodeUpgradeMarker();
  }
  // ENG-5357: upgrade for existing installs is no longer kicked off here.
  // The in-memory `frameworkBinaryChecked` guard above is process-scope,
  // so on alyve-host-1 a restart storm fired N concurrent `brew upgrade
  // --cask claude-code` jobs and pegged the host until it wedged. Upgrade
  // now lives in maybeUpgradeClaudeCode() which is called from pollCycle
  // and throttled via an on-disk 24h marker — restarts can no longer
  // re-trigger it.

  // Check if Claude Code is authenticated. The binary that's already on
  // disk works regardless of upgrade state.
  agentRuntimeAuthenticated = await checkClaudeAuth();
}

// ---------------------------------------------------------------------------
// Claude Code background upgrade (ENG-5357)
// ---------------------------------------------------------------------------
//
// Called from pollCycle on every tick. Returns immediately when the disk
// marker is fresh — only the once-per-day actual `brew upgrade` does any
// real work. This is the same disk-marker pattern as the agt CLI self-
// update path (checkAndUpdateCli); the window is just longer because
// Claude Code releases on a much slower cadence than the agt CLI.
//
// The marker is stamped BEFORE the fire-and-forget upgrade kicks off so
// concurrent poll cycles inside the 30-120s upgrade window cannot double-
// trigger. Without that pre-stamp, every poll tick during a slow upgrade
// would see "no marker yet" and queue another brew job behind the lock —
// the same restart-loop pathology this issue was created to fix.

let claudeCodeUpgradeInFlight = false;

// The marker-throttle primitives live in a tiny dependency-free module so
// their unit test (ENG-6418) doesn't have to evaluate this whole file's
// import graph. Imported for internal use (maybeUpgradeClaudeCode below)
// and re-exported to keep the public surface unchanged for any caller that
// imports them from manager-worker.js.
export {
  claudeCodeUpgradeMarkerPath,
  stampClaudeCodeUpgradeMarker,
  claudeCodeUpgradeThrottled,
};

export async function maybeUpgradeClaudeCode(): Promise<void> {
  if (claudeCodeUpgradeInFlight) return;
  if (claudeCodeUpgradeThrottled()) return;

  // ENG-5963: gate this legacy `brew upgrade --cask claude-code` path to the
  // maintenance window too — the upgraded binary applies on the next session
  // start, so it's restart-adjacent like the agt-cli self-update. Deferring
  // BEFORE the throttle stamp below means an out-of-window poll retries on the
  // next in-window cycle instead of burning the ~24h throttle on a no-op.
  if (decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'defer') {
    return;
  }

  // Stamp BEFORE kicking off the upgrade so concurrent polls inside the
  // upgrade window see "throttled" and bail. Without this, every poll
  // tick during the 30-120s brew run would queue another job. The
  // in-flight flag is a second belt for the rare case where two polls
  // race past the disk read in the same event-loop tick.
  claudeCodeUpgradeInFlight = true;
  stampClaudeCodeUpgradeMarker();

  const { execFileSync } = await import('node:child_process');
  const brewPath = resolveBrewPath(execFileSync);
  if (!brewPath) {
    claudeCodeUpgradeInFlight = false;
    return;
  }

  // Only upgrade if the binary already exists; install lives in
  // ensureFrameworkBinary's hot path (it's a hard prerequisite, not a
  // refresh, and shouldn't be deferred to the poll loop).
  if (!claudeBinaryInstalled(execFileSync)) {
    claudeCodeUpgradeInFlight = false;
    return;
  }

  const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
  const runBrew = (args: string[], opts: { timeout: number }) => {
    if (isRoot) {
      return runAsync('sudo', ['-u', 'ec2-user', '-H', brewPath, ...args], { ...opts, cwd: '/tmp' });
    }
    return runAsync(brewPath, args, opts);
  };

  // ENG-10039: DOES THIS HOST ACTUALLY HAVE THE CASK? Without this check the
  // upgrade below fails every single day, on every host provisioned since
  // Claude Code moved to npm, with `Error: Cask 'claude-code' is not installed.`
  //
  // WHY THE EXISTING GUARDS DO NOT CATCH IT. `claudeBinaryInstalled` returns
  // true for a `claude` on PATH from ANY source — and on these hosts that is
  // `/usr/bin/claude`, installed by `npm install -g @anthropic-ai/claude-code`
  // in host-bootstrap.ts. So the binary check passes, brew is present, and we
  // proceed to upgrade a cask that was never installed. Measured on agt-aws-1:
  // 9 attempts across 9 consecutive days, 9 failures, zero successes, and
  // `brew list --cask` returns nothing at all.
  //
  // THIS IS A CLEAN SKIP, NOT A FIX FOR THE UPGRADE. Claude Code on these hosts
  // is kept current by `claude-code-updater.ts` (ENG-5672), which is npm-based
  // and works — on agt-aws-1 it moved 2.1.247 → 2.1.261 over the same nine days
  // this path was failing. This brew call is the pre-ENG-5672 mechanism
  // (ENG-4221) that was never removed. It is left in place rather than deleted
  // because hosts provisioned in the cask era still have a cask for it to
  // upgrade, and this check is exactly what tells the two apart.
  //
  // The throttle marker is stamped ABOVE, so a cask-less host runs this check
  // once per day rather than on every poll.
  //
  // THE IN-FLIGHT FLAG IS CLEARED ON EVERY PATH OUT OF HERE, and that is why
  // this is a try/finally rather than a bare `await` (CodeRabbit on #5616).
  // `claudeCodeUpgradeInFlight` gates the top of this function, so leaking it
  // as `true` does not merely skip one cycle — it wedges the updater for the
  // life of the manager process, permanently. The original code never awaited
  // after setting the flag; it was cleared in the promise's `.finally()`. My
  // first cut of this probe introduced two new await points (the dynamic
  // import and the probe itself), either of which could reject and escape
  // before the flag was cleared. Turning a daily-noise bug into a permanent
  // wedge would have been a strictly worse trade.
  //
  // A REJECTED PROBE IS NOT "NO CASK". Failing closed to `absent` would skip
  // the upgrade on a cask host whenever brew was momentarily unavailable, and
  // silently — the same invisible-skip shape this ticket is about. So a
  // rejection skips only THIS cycle and says so; the next in-window poll after
  // the throttle expires tries again.
  let proceedToUpgrade = false;
  let brewResult: typeof import('./claude-code-brew-result.js') | undefined;
  try {
    brewResult = await import('./claude-code-brew-result.js');
    const caskProbe = await runBrew(['list', '--cask', 'claude-code'], { timeout: 60_000 });
    const presence = brewResult.caskPresenceFromListResult(caskProbe);
    proceedToUpgrade = presence === 'present';
    if (presence === 'absent') {
      // Deliberately does NOT claim how Claude Code got here (CodeRabbit on
      // #5616). `claudeBinaryInstalled` accepts any `claude` on PATH, so a
      // manually placed or image-baked binary reaches this branch too, and
      // naming npm would state as fact something we did not check. What we DID
      // establish is the only thing this message needs to say: Homebrew does
      // not manage this install, so the brew upgrade has nothing to act on.
      log(
        'Claude Code is not installed as a Homebrew cask on this host, so the legacy brew ' +
          'upgrade path has nothing to upgrade — skipping it (ENG-10039). This is not an error. ' +
          'Where Claude Code was installed via npm, claude-code-updater (ENG-5672) keeps it current.',
      );
    } else if (presence === 'unknown') {
      // A non-zero exit that is NOT brew's not-installed message. Could be a
      // network failure, a lock, a broken tap. Treating it as `absent` would
      // silently skip the upgrade on a genuine cask host for ~24h, because the
      // throttle marker is already stamped.
      log(
        `Claude Code cask probe inconclusive (exit ${caskProbe.code}), skipping this cycle ` +
          `(ENG-10039): ${(caskProbe.stderr || caskProbe.stdout).trim().slice(0, 200)}`,
      );
    }
  } catch (err) {
    log(
      `Claude Code cask probe failed, skipping this cycle (ENG-10039): ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  } finally {
    // Cleared unless we are definitely continuing to the upgrade below, which
    // owns the flag from here via its own `.finally()`.
    if (!proceedToUpgrade) claudeCodeUpgradeInFlight = false;
  }
  if (!proceedToUpgrade || !brewResult) return;
  const { classifyBrewUpgradeResult } = brewResult;

  log(`Checking for Claude Code updates in background${isRoot ? ' (as ec2-user via sudo)' : ''}...`);
  runBrew(['upgrade', '--cask', 'claude-code'], { timeout: 120_000 })
    .then((r) => {
      // ENG-10039: one classifier for both exit paths. The duplicated inline
      // string checks disagreed — the exit-0 branch omitted `not upgraded`,
      // which the non-zero branch treated as fine — so the same brew output
      // could be read two ways depending only on the exit code.
      switch (classifyBrewUpgradeResult(r)) {
        case 'already-current':
          log('Claude Code is already up to date');
          break;
        case 'upgraded':
          log('Claude Code upgraded successfully (will apply on next session start)');
          break;
        case 'failed':
          log(`Claude Code upgrade failed (exit ${r.code}): ${r.stderr.trim() || r.stdout.trim()}`);
          break;
      }
    })
    .catch((err) => log(`Claude Code upgrade failed: ${(err as Error).message}`))
    .finally(() => { claudeCodeUpgradeInFlight = false; });
}

// ---------------------------------------------------------------------------
// CLI self-update via Homebrew (throttled to once every 5 minutes)
// ---------------------------------------------------------------------------

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ENG-5862 — path of the coalesce-gate marker. Distinct from
// `.last-update-check` (which throttles the cheap version probe): this one
// records the last time we actually applied an upgrade and triggered a
// restart, so the brew + npm paths can both refuse to restart again within
// the coalesce window. See self-update-coalesce.ts for the rationale.
function selfUpdateAppliedMarkerPath(): string {
  return join(homedir(), '.augmented', '.last-self-update-applied');
}
// ENG-4485: only log "up to date" once per manager process so the periodic
// check doesn't spam the log every 5 minutes. Errors, "update available",
// and "upgraded to X" always log.
let selfUpdateUpToDateLogged = false;
// ENG-7833: throttle the pin log lines to once per process, same as the
// up-to-date line above, because the poll loop calls the updater every cycle.
let selfUpdatePinnedLogged = false;
let selfUpdatePinInvalidLogged = false;
// ENG-4488: set after a successful `brew upgrade` so the poll loop exits
// cleanly and a supervisor can re-spawn onto the new binary. Without this,
// the running process keeps executing from the old Cellar path whose lazy-
// imported hashed chunks brew has already removed — the first dynamic
// import after the swap throws `Cannot find module`.
let restartAfterUpgrade = false;
let pendingUpgradeVersion: string | null = null;
// In-flight lock for the self-update path. pollCycle() kicks
// checkAndUpdateCli() off fire-and-forget, so without this a slow
// `brew update` or `npm install -g` lets later polls re-enter and
// launch a second updater concurrently. brew/npm both lock-file
// internally and would error on the collision; worse, both could
// flip restartAfterUpgrade and double-schedule a manager respawn.
let selfUpdateInFlight = false;
/**
 * ENG-8624: WHEN the latch above was taken, so a latch that never clears can be
 * detected and broken instead of freezing self-update for the life of the
 * process.
 *
 * Measured three times on live hosts, each found by accident while looking for
 * something else:
 *
 *   zanda my-second-host   0.28.470, stuck 8 days,  ~70 releases behind
 *   alyve alyve-host-1     0.28.457, stuck 10 days, ~83 releases behind
 *   prod agt-aws-1         0.28.610, stuck 7h17m,   17 releases behind
 *
 * and a fourth is live on two-tractors-host right now: an operator pressed
 * "Update CLI now" at 00:43:36Z and at 10:07Z the host is still on 0.28.610
 * with zero `[self-update]` lines in the log.
 *
 * The latch is cleared only in `checkAndUpdateCli`'s `finally`, so an `await`
 * inside the `try` that never settles holds it forever. The early return sits
 * BEFORE any logging, so the routine path then goes completely silent — alyve
 * had zero `[self-update]` lines for ten days and looked perfectly healthy from
 * every other angle: heartbeating, agents running, no alarms.
 *
 * A host frozen on an old version is indistinguishable from a host that is
 * merely behind, which is how a version-gated rollout was read as "the fleet
 * hasn't reached the floor yet" when it had actually stopped moving.
 */
let selfUpdateInFlightSince: number | null = null;

/**
 * How long the self-update latch may be held before it is treated as wedged.
 *
 * Twenty minutes. The slowest legitimate hold observed is a `brew update` +
 * upgrade on a cold cache at a few minutes, so this is several times the real
 * worst case — the cost of breaking the latch early is two concurrent updaters
 * (brew and npm both lock internally, so they would error rather than corrupt),
 * while the cost of setting it too high is another ten-day freeze.
 */
export const SELF_UPDATE_LATCH_MAX_HOLD_MS = 20 * 60_000;

export type SelfUpdateLatchVerdict = 'free' | 'held' | 'expired';

/**
 * Whether the self-update latch may be taken, and if not, whether the current
 * holder has outlived the bound.
 *
 * Pure and exported so the wedge condition is unit-testable: `checkAndUpdateCli`
 * ends in package-manager execs and a possible `process.exit`, so the decision
 * has to be reachable without running it. 'expired' is the self-heal case — the
 * caller logs loudly and takes the latch anyway.
 */
export function selfUpdateLatchVerdict(
  heldSince: number | null,
  now: number,
  maxHoldMs: number = SELF_UPDATE_LATCH_MAX_HOLD_MS,
): SelfUpdateLatchVerdict {
  if (heldSince === null) return 'free';
  // A `heldSince` in the future (clock jump) gives a negative age, which can
  // never reach the bound and so reads 'held'. That is the safe direction — it
  // declines to break a latch it cannot reason about — and it self-corrects as
  // soon as `now` passes the stamp. An earlier cut clamped the age to zero; the
  // clamp changed no verdict and only made the code look like it defended
  // something, so it is gone.
  const heldMs = now - heldSince;
  return heldMs >= maxHoldMs ? 'expired' : 'held';
}
// ENG-9137: has the upgrade-restart sequence already been entered?
//
// `selfUpdateInFlight` above guards the UPDATER. This guards the RESTART, which
// is a separate re-entry and was unguarded. The sibling comment predicted this
// exact failure ("double-schedule a manager respawn") but only defended the half
// it was written for.
//
// `restartNow()` is reachable from three `scheduleNext()` call sites and from two
// branches inside `scheduleNext()` itself, and it does not exit synchronously: it
// fires `reportSelfUpdateRestarts()` (one POST per live agent) and only clears
// `running` via `stopPolling()` in the `.finally()`, up to the 3s report deadline
// later. For that whole window `running` is still true and `restartAfterUpgrade`
// is still true, so every further `scheduleNext()` re-enters and posts a COMPLETE
// second/third batch for every agent on the host.
//
// Measured consequence (ENG-9137): acquire-intelligence wrote exactly 15 rows for
// 5 agents inside 80ms - a clean 3x, i.e. three `restartNow()` entries - and
// agt-aws-1 wrote 40+ rows for 16 agents in 1.3s at 1-5x per agent, the spread
// being the race between re-entries and process exit. The manager log for that
// same wave is strictly sequential with each agent provisioned exactly once, so
// the RESTARTS were never duplicated; only the audit rows were.
//
// That inflated `restart_count`, the field debug_host_versions documents as "the
// tell for a thrashing host", by up to 8x - and it is read during incident
// triage, so it turned "16 agents is genuinely 3-5 minutes of work" into "this
// host is thrashing". Set synchronously before any await so re-entry cannot
// interleave.
let upgradeRestartInitiated = false;
// ENG-8079: the `update_requested_at` the last heartbeat delivered, cached so the
// forced "Update CLI now" self-update runs at the TOP of the next poll cycle
// rather than inline in the heartbeat handler.
//
// Why it moved: the forced kick used to be fire-and-forget from inside the
// heartbeat response handler, immediately upstream of the per-agent sweep — 24
// `execFileSync` sites over every agent on the host. That synchronous work blocks
// the event loop, so the updater's `fetch` to registry.npmjs.org never got
// scheduled and its `AbortSignal.timeout(10_000)` fired 22-70s late, every single
// time. On agt-aws-1 (14 agents) that made the button a guaranteed no-op: 32
// "running window-bypassing self-update now" log lines, 31 of them followed by
// `npm registry fetch failed: The operation was aborted due to timeout`, while
// the NON-forced check — kicked at the top of the cycle, where its fetch overlaps
// the awaited heartbeat POST and the loop is free — fetched the same registry
// successfully seconds earlier. Same host, same registry, opposite outcome: the
// difference is purely WHERE in the cycle the call is made. Running the forced
// path from the same site as its working sibling is the fix.
let cachedUpdateRequestedAt: string | null = null;
// ENG-8079: consecutive `defer-idle` decisions for a pending forced update. On a
// host with many agents the idle gate is OR-ed across all of them, so the 300s
// inbound-quiet axis alone can keep the host "busy" indefinitely — 313 straight
// defers observed on agt-aws-1. Past the threshold we relax to the progress axis
// only (see runPendingForcedUpdate), which still never interrupts a turn that is
// actually producing tokens.
let forcedUpdateDeferStreak = 0;
// ENG-8449: has the relaxation been announced for the CURRENT pending request?
// The transition can now be reached by either the streak or the wall clock, so
// the log is latched on the transition itself rather than on a counter value.
let forcedUpdateRelaxAnnounced = false;
// ENG-6692: `force` is the admin "Update CLI now" one-shot. It skips the
// `.last-update-check` throttle here and (threaded down) bypasses the
// maintenance-window + coalesce gates in the brew/npm paths — the operator
// explicitly asked for an immediate upgrade, like the `urgent` dist-tag. It does
// NOT bypass the defer-until-idle gate: the caller (the heartbeat consume block)
// only invokes this once the host is idle, so a live turn is never interrupted.
interface CheckAndUpdateCliOpts {
  /** Forced "Update CLI now" (ENG-6692): bypass throttle + window + coalesce. */
  force?: boolean;
}

async function checkAndUpdateCli(opts?: CheckAndUpdateCliOpts): Promise<SelfUpdateOutcome> {
  const force = opts?.force ?? false;
  // Already running: don't double-kick. 'failed' (not 'noop') so a forced caller
  // that somehow races past its own in-flight guard retries rather than burns
  // its one-shot for an update this call didn't run.
  //
  // ENG-8624: this early return used to be silent AND unbounded. Both halves
  // are fixed here — it now says so, and a latch held past the bound is broken
  // rather than obeyed, because the alternative is a host frozen on its current
  // version for the life of the manager process with completely clean logs.
  const verdict = selfUpdateLatchVerdict(selfUpdateInFlightSince, Date.now());
  if (verdict === 'held') {
    const heldSec = Math.round((Date.now() - (selfUpdateInFlightSince ?? Date.now())) / 1000);
    log(
      `[self-update] deferring: a self-update has been in flight for ${heldSec}s. `
        + `Retrying next poll; the latch breaks at ${Math.round(SELF_UPDATE_LATCH_MAX_HOLD_MS / 60_000)}m.`,
    );
    return 'failed';
  }
  if (verdict === 'expired') {
    const heldMin = Math.round((Date.now() - (selfUpdateInFlightSince ?? Date.now())) / 60_000);
    // Loud on purpose. This is the ten-day freeze becoming a self-healing event,
    // and it is the ONLY line that says a wedge happened at all.
    console.warn(
      `[manager-worker] [self-update] WEDGE: the in-flight latch has been held for ${heldMin}m, `
        + `past the ${Math.round(SELF_UPDATE_LATCH_MAX_HOLD_MS / 60_000)}m bound. Breaking it and proceeding — `
        + 'the previous updater never settled (ENG-8624).',
    );
  }
  selfUpdateInFlight = true;
  selfUpdateInFlightSince = Date.now();
  try {
    const cliPath = process.argv[1] ?? '';
    const isDevMode = cliPath.includes('/src/') || cliPath.includes('tsx');
    if (isDevMode) return 'noop';

    // Distinguish brew formula installs from npm-global installs. A bare
    // `/home/linuxbrew/` or `/opt/homebrew/` prefix isn't enough — Linuxbrew's
    // hosted Node lays npm-global packages under `.../lib/node_modules/...`,
    // so we match on the canonical formula path `/Cellar/<formula>/`.
    let resolvedPath = cliPath;
    try {
      const { realpathSync } = await import('node:fs');
      resolvedPath = realpathSync(cliPath);
    } catch { /* fall back to argv[1] verbatim */ }
    const isBrewFormula = /\/Cellar\/[^/]+\//.test(resolvedPath);
    const isNpmGlobal = !isBrewFormula && resolvedPath.includes('node_modules');
    if (!isBrewFormula && !isNpmGlobal) return 'noop'; // Unknown install method — can't self-update; don't leave the one-shot stuck

    // Throttle: check last update time from disk. ENG-6692: the forced
    // "Update CLI now" path skips the throttle so an operator click isn't
    // swallowed by a recent cheap-probe stamp.
    const { readFileSync: readF, writeFileSync: writeF } = await import('node:fs');
    const markerPath = join(homedir(), '.augmented', '.last-update-check');
    if (!force) {
      try {
        const lastCheck = parseInt(readF(markerPath, 'utf-8').trim(), 10);
        if (Date.now() - lastCheck < UPDATE_CHECK_INTERVAL_MS) return 'noop';
      } catch { /* no marker yet — first run */ }
    }

    // Stamp the marker BEFORE running the heavy updater so a long
    // brew/npm run doesn't let the same poll cycle re-enter past the
    // throttle as soon as the lock releases. Update again at the end
    // so the next throttle window measures from completion.
    try {
      writeF(markerPath, String(Date.now()));
    } catch { /* non-fatal */ }

    const outcome = isBrewFormula
      ? await checkAndUpdateCliViaBrew(force)
      : await checkAndUpdateCliViaNpm(force);

    // Update marker regardless of outcome — failures here shouldn't trigger
    // a tight retry loop on every poll cycle.
    try {
      writeF(markerPath, String(Date.now()));
    } catch { /* non-fatal */ }
    return outcome;
  } finally {
    selfUpdateInFlight = false;
    // ENG-8624: the timestamp must clear with the flag, or the next hold would
    // be measured from the previous one and the watchdog would fire immediately.
    selfUpdateInFlightSince = null;
  }
}

async function checkAndUpdateCliViaBrew(force = false): Promise<SelfUpdateOutcome> {
  const { execFileSync } = await import('node:child_process');
  // Use the shared resolveBrewPath helper rather than a bare `which brew`.
  // The manager runs with cloud-init's minimal env on EC2 (and a similarly
  // sparse PATH under launchd on macOS), so `which brew` returns nothing
  // even on hosts where the CLI clearly lives in a Cellar — without this,
  // self-update on those hosts silently no-ops.
  const brewPath = resolveBrewPath(execFileSync);
  if (!brewPath) return 'noop'; // No Homebrew on PATH or canonical install dirs.

  // ENG-7833: version pin on a Homebrew install. brew can't install an arbitrary
  // exact version (the tap only ships the latest formula), so we can't reconcile
  // a brew host to a pin the way the npm path does. Be honest rather than report
  // a misleading "pin wins" no-op:
  //  - satisfied (installed === pin) → quiet no-op.
  //  - mismatch (installed !== pin)  → we CANNOT satisfy the pin here; warn
  //    loudly (the host is NOT on the pinned version) and refuse to upgrade past
  //    it. Exact pinning on a brew host requires switching to the npm-global
  //    install (or holding the formula).
  //  - invalid / unpinned            → fall through to the normal brew upgrade.
  {
    const brewPinRaw = resolveAgtCliPinRaw();
    // ENG-7853: same first-heartbeat gate as the npm path; don't converge to
    // the channel before we know whether a console pin is in effect.
    if (brewPinRaw === undefined && !desiredAgtCliVersionHydrated) {
      return 'noop';
    }
    const brewPin = decidePin({ installed: agtCliVersion, pinRaw: brewPinRaw });
    if (brewPin.kind === 'satisfied') {
      if (!selfUpdatePinnedLogged) {
        log(`[self-update] agt CLI pinned to ${brewPin.version} (AGT_CLI_PIN_VERSION); already satisfied on this brew install.`);
        selfUpdatePinnedLogged = true;
      }
      return 'noop';
    }
    if (brewPin.kind === 'install') {
      if (!selfUpdatePinnedLogged) {
        log(
          `[self-update] agt CLI is pinned to ${brewPin.version} (AGT_CLI_PIN_VERSION) but this is a Homebrew install ` +
            `(currently ${agtCliVersion}); brew can't install an exact version, so the pin CANNOT be satisfied here. ` +
            `Not upgrading. Switch to the npm-global install (or hold the formula) to pin exactly.`,
        );
        selfUpdatePinnedLogged = true;
      }
      return 'noop';
    }
    // 'invalid' or 'unpinned': ignore and run the normal brew upgrade below.
  }

  // ENG-6771: a failed tap-metadata refresh makes a subsequent "no outdated"
  // result UNRELIABLE (brew outdated reads the stale local cache), so a forced
  // check must not consume its one-shot on that false no-op — track freshness
  // and return 'failed' (retry) rather than 'noop' (consume) when stale.
  let metadataFresh = true;
  // Refresh tap metadata first. `brew outdated` consults the LOCAL formula
  // cache, so without this it will happily report no updates even when the
  // tap has a newer version on GitHub. Hitting this was the reason self-
  // updates silently stopped working for hours after a version bump.
  try {
    execFileSync(brewPath, ['update', '--quiet'], { timeout: 60_000, stdio: 'pipe' });
  } catch (err) {
    metadataFresh = false;
    log(`[self-update] brew update failed (continuing with stale cache): ${(err as Error).message}`);
  }

  try {
    const outdated = execFileSync(brewPath, ['outdated', '--json=v2'], {
      timeout: 30_000,
      encoding: 'utf-8',
    });
    const data = JSON.parse(outdated) as { formulae?: Array<{ name: string; installed_versions: string[]; current_version: string }> };
    const agtOutdated = data.formulae?.find((f) => f.name === 'agt' || f.name === 'integrity-labs/tap/agt');

    if (agtOutdated) {
      const installed = agtOutdated.installed_versions?.[0] ?? 'unknown';
      const latest = agtOutdated.current_version ?? 'unknown';

      // ENG-5963: gate the restart-causing upgrade to the maintenance window
      // before the coalesce gate. (The urgent npm dist-tag escape hatch is
      // npm-only — brew hosts have no equivalent moving target — so brew relies
      // on the window + coalesce. AGT_MAINTENANCE_WINDOW_DISABLE bypasses.)
      // ENG-6692: the forced "Update CLI now" path bypasses the window.
      if (!force && decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'defer') {
        log(
          `[self-update] agt CLI ${installed} → ${latest} (brew) deferred — ` +
            formatWindowDeferLogLine(cachedMaintenanceWindow ?? {}, new Date()),
        );
        return 'noop';
      }

      // ENG-5862 — gate the restart-causing upgrade behind the coalesce window
      // shared with the npm path. Defer logs but never executes the upgrade so
      // we don't burn into the marker that another caller is racing us for.
      // ENG-6692: the forced "Update CLI now" path bypasses coalesce too.
      const coalesceWindowMs = resolveCoalesceWindowMs();
      const coalesce = decideSelfUpdateCoalesce({
        windowMs: coalesceWindowMs,
        lastAppliedMs: readLastSelfUpdateAppliedMs(selfUpdateAppliedMarkerPath()),
        now: Date.now(),
      });
      if (!force && !coalesce.proceed) {
        log(formatCoalesceDeferLogLine({
          installed, latest, channelLabel: 'brew',
          remainingMs: coalesce.remainingMs, windowMs: coalesceWindowMs,
        }));
        return 'noop';
      }

      log(`[self-update] agt CLI update available: ${installed} → ${latest}. Upgrading via brew${force ? ' (forced — Update CLI now)' : ''}...`);

      // ENG-10724: the LAST thing before the binary is replaced, and the only
      // place this path asks. By here `brew outdated` has established an upgrade
      // exists and `resolveBrewPath` that this host can install one, so a hold
      // raised from this point describes something real - which the operator
      // alert then repeats verbatim. The two commands above are 60s and 30s
      // timeouts of real network work; a check any earlier would be answering
      // about a host state that has since moved.
      const brewInstallHold = await selfUpdateDeviceStateHold(force);
      if (brewInstallHold) return brewInstallHold;

      try {
        execFileSync(brewPath, ['upgrade', 'integrity-labs/tap/agt'], {
          timeout: 120_000,
          stdio: 'pipe',
        });
        log(`[self-update] agt CLI upgraded to ${latest}. Scheduling manager restart so the new binary takes effect.`);
        stampLastSelfUpdateApplied(selfUpdateAppliedMarkerPath());
        restartAfterUpgrade = true;
        pendingUpgradeVersion = latest;
        return 'updated';
      } catch (err) {
        log(`[self-update] brew upgrade failed: ${(err as Error).message}`);
        return 'failed';
      }
    } else if (!selfUpdateUpToDateLogged) {
      log(`[self-update] agt CLI is up to date (brew, ${agtCliVersion})`);
      selfUpdateUpToDateLogged = true;
    }
    // Not outdated. On fresh metadata this is a genuine no-op (consume the
    // one-shot). On STALE metadata (the `brew update` above failed) the
    // "no outdated" verdict is unreliable, so report 'failed' — a forced check
    // then retries next heartbeat instead of burning the request on a result we
    // couldn't confirm. (ENG-6771; CodeRabbit PR #2447.)
    return metadataFresh ? 'noop' : 'failed';
  } catch (err) {
    log(`[self-update] brew outdated failed: ${(err as Error).message}`);
    return 'failed';
  }
}

/**
 * ENG-7853: resolve the effective agt-cli pin source for the self-update gate.
 * Precedence: the explicit host env var AGT_CLI_PIN_VERSION (set at bootstrap or
 * by an operator over SSH, the highest-precedence per-host override, matching
 * the "env stays highest per host" convention) wins when set; otherwise fall
 * back to the DB-driven pin delivered on the heartbeat (set from the console).
 * Either source flows through the same ENG-7833 decidePin machinery unchanged,
 * so a garbage value is still ignored with a warning rather than looping. Clearing
 * the DB pin (heartbeat delivers null) resumes normal channel tracking within one
 * poll; a host with an explicit env pin keeps it until that env var is removed.
 */
function resolveAgtCliPinRaw(): string | undefined {
  return resolveEffectivePinRaw(process.env.AGT_CLI_PIN_VERSION, cachedDesiredAgtCliVersion);
}

/**
 * npm-global install upgrade path — used on AWS EC2 hosts (the bootstrap
 * runs `npm install -g @integrity-labs/agt-cli`). Queries the npm registry
 * directly (the source of truth that the API also reads from), compares
 * against the build-time `__CLI_VERSION__`, and re-runs `npm install -g`
 * when behind. Requires `npm` on PATH; on AL2023 hosts the manager runs as
 * ec2-user with passwordless sudo so we shell out via `sudo -n` to write
 * into /usr/lib/node_modules (owned by root).
 */
async function checkAndUpdateCliViaNpm(force = false): Promise<SelfUpdateOutcome> {
  // Skip the comparison entirely in dev where __CLI_VERSION__ wasn't injected.
  if (agtCliVersion === 'dev') return 'noop';

  // Release channel — `latest` (prod default) or a gated channel like `test`
  // (set on the test environment's hosts via AGT_CLI_RELEASE_CHANNEL). The
  // channel is the npm dist-tag we track: prod hosts follow `latest`,
  // test hosts follow `test`, same registry. Unset ⇒ `latest`, so prod
  // behaviour is unchanged.
  const channel = process.env.AGT_CLI_RELEASE_CHANNEL || 'latest';

  // ENG-7833: version pin. A valid AGT_CLI_PIN_VERSION freezes the host on an
  // exact version: pin wins over the channel poll AND the urgent dist-tag
  // (unless AGT_CLI_PIN_ALLOW_URGENT opts back in). We install the exact pin,
  // including a downgrade, bypassing shouldUpdate/window/coalesce because a pin
  // change is a deliberate operator action that should apply promptly and then
  // settle to a no-op once the versions match. An invalid pin is ignored (with
  // a one-time warning) so a typo can't silently freeze a host on nothing.
  // ENG-7853: prefer the explicit env pin, else the DB pin from the heartbeat.
  const pinRaw = resolveAgtCliPinRaw();
  // Until the first heartbeat resolves the DB pin, we can't tell "no pin" from
  // "pinned but not yet heard". Defer channel convergence in that window so a
  // console-pinned host doesn't briefly jump to the channel tip (and restart)
  // before its pin arrives. An explicit env pin (pinRaw defined) needs no
  // heartbeat, so it is unaffected.
  if (pinRaw === undefined && !desiredAgtCliVersionHydrated) {
    return 'noop';
  }
  const pinDecision = decidePin({ installed: agtCliVersion, pinRaw });
  if (pinDecision.kind === 'invalid') {
    if (!selfUpdatePinInvalidLogged) {
      log(
        `[self-update] AGT_CLI_PIN_VERSION="${pinDecision.raw}" is not a valid version; ` +
          `ignoring the pin and tracking channel=${channel} as usual.`,
      );
      selfUpdatePinInvalidLogged = true;
    }
  } else if (pinDecision.kind !== 'unpinned') {
    // A valid pin is in effect. Optional opt-out: still take an urgent hotfix
    // over the pin when AGT_CLI_PIN_ALLOW_URGENT is set.
    if (pinAllowsUrgent(process.env.AGT_CLI_PIN_ALLOW_URGENT)) {
      const urgentTarget = await fetchUrgentDistTagVersion();
      // Already on the active urgent hotfix: stay put. Without this, the next
      // poll finds urgentTarget not newer, falls through to the pin-install
      // branch, and downgrades straight back to the pin, flapping between the
      // urgent hotfix and the pin every cycle. Only reconcile back to the pin
      // once the urgent tag is retired (fetchUrgentDistTagVersion → null).
      if (urgentTarget && urgentTarget === agtCliVersion) {
        return 'noop';
      }
      if (
        isUrgentUpgrade({
          urgentTarget,
          installed: agtCliVersion,
          isNewer: (installed, candidate) =>
            candidate !== installed && !isOlderSemverTuple(installed, candidate),
        })
      ) {
        log(
          `[self-update] URGENT agt CLI hotfix ${agtCliVersion} → ${urgentTarget} ` +
            `overrides pin=${pinDecision.version} (AGT_CLI_PIN_ALLOW_URGENT). Upgrading via npm...`,
        );
        return await installAgtCliViaNpm(urgentTarget!, 'urgent(pin-allow-urgent)', force);
      }
    }
    if (pinDecision.kind === 'satisfied') {
      if (!selfUpdatePinnedLogged) {
        log(`[self-update] agt CLI pinned to ${pinDecision.version} (AGT_CLI_PIN_VERSION); up to date.`);
        selfUpdatePinnedLogged = true;
      }
      return 'noop';
    }
    log(
      `[self-update] agt CLI pinned to ${pinDecision.version} (AGT_CLI_PIN_VERSION); ` +
        `installing exact version (${agtCliVersion} → ${pinDecision.version})...`,
    );
    return await installAgtCliViaNpm(pinDecision.version, `pin=${pinDecision.version}`, force);
  }

  // Query the npm registry's channel dist-tag. Direct fetch — same source
  // the /cli/version API endpoint reads from — so the manager doesn't need
  // any extra wiring beyond outbound HTTPS. AbortSignal.timeout keeps a
  // hung registry from blocking the poll loop.
  // ENG-8079: retry a timed-out/aborted registry read once before giving up.
  // The routine path can afford to lose a poll — it re-runs in ~30s — but a
  // forced "Update CLI now" that returns 'failed' leaves an operator staring at
  // a button that did nothing. agt-aws-1's logs show genuine transient aborts
  // ("The operation was aborted due to timeout") several times a day even when
  // the event loop is healthy, so one retry meaningfully changes the odds.
  // A non-200 or a malformed body is NOT retried: those are real answers from a
  // reachable registry, not transport flakiness.
  const REGISTRY_TIMEOUT_MS = 10_000;
  const registryAttempts = force ? 2 : 1;
  let latest: string | null = null;
  let lastFetchErr = '';
  for (let attempt = 1; attempt <= registryAttempts; attempt++) {
    try {
      const res = await fetch(
        `https://registry.npmjs.org/@integrity-labs/agt-cli/${encodeURIComponent(channel)}`,
        {
          signal: AbortSignal.timeout(REGISTRY_TIMEOUT_MS),
          headers: { Accept: 'application/json' },
        },
      );
      if (!res.ok) {
        log(`[self-update] npm registry returned ${res.status} (channel=${channel})`);
        return 'failed';
      }
      const body = (await res.json()) as { version?: string };
      if (!body.version) {
        log(`[self-update] npm registry response missing version field`);
        return 'failed';
      }
      latest = body.version;
      break;
    } catch (err) {
      lastFetchErr = (err as Error).message;
      if (attempt < registryAttempts) {
        log(
          `[self-update] npm registry fetch failed (attempt ${attempt}/${registryAttempts}): ${lastFetchErr} — retrying.`,
        );
      }
    }
  }
  if (latest === null) {
    log(`[self-update] npm registry fetch failed: ${lastFetchErr}`);
    return 'failed';
  }

  // ENG-5963: urgent escape hatch — a version published to the `urgent` npm
  // dist-tag installs immediately, bypassing BOTH the maintenance window and
  // the coalesce gate, and overriding the channel pin entirely (a fleet-wide
  // hotfix lands on `test`-pinned hosts too). Checked before the channel
  // shouldUpdate logic so an urgent fix lands even when the channel is current.
  const urgentTarget = await fetchUrgentDistTagVersion();
  // Prerelease-safe comparison: install the urgent target when it differs from
  // what's installed AND isn't a numeric-tuple downgrade. `isNewerSemver`
  // alone parses prerelease tags (e.g. 0.27.6-test.2) into NaN and would treat
  // a canary/test host as "not newer", silently skipping an urgent hotfix —
  // exactly the hosts the escape hatch is for. `isOlderSemverTuple` strips the
  // prerelease suffix before comparing, so a hotfix still lands there.
  if (
    isUrgentUpgrade({
      urgentTarget,
      installed: agtCliVersion,
      isNewer: (installed, candidate) =>
        candidate !== installed && !isOlderSemverTuple(installed, candidate),
    })
  ) {
    log(
      `[self-update] URGENT agt CLI hotfix ${agtCliVersion} → ${urgentTarget} ` +
        `(overrides channel=${channel} + maintenance window). Upgrading via npm...`,
    );
    return await installAgtCliViaNpm(urgentTarget!, 'urgent', force);
  }

  // `latest` channel: never move backwards — only upgrade to a strictly
  // newer semver (prod-safe; ignores prerelease tags entirely). A gated
  // channel is a moving pointer to a specific build, so track it exactly:
  // install whenever the tag points somewhere other than what we run,
  // including prereleases that isNewerSemver can't compare.
  //
  // ENG-5785: even on a gated channel, refuse to *downgrade*. The silent
  // revert this guards against: a host is pinned to `test` (canary),
  // someone manually upgrades to a newer stable via `npm i -g @latest`,
  // then the next poll cycle finds `test` still pointing at an older
  // prerelease and reinstalls it on top. Symptom: agents look upgraded
  // but bounce back to the old version. Today (2026-05-31) this blocked
  // the ENG-5779 / ENG-5781 fixes from landing on agt-aws-1 until the
  // canary pin was manually removed.
  let shouldUpdate: boolean;
  if (channel === 'latest') {
    shouldUpdate = shouldUpdateOnLatest(agtCliVersion, latest);
  } else if (latest === agtCliVersion) {
    shouldUpdate = false;
  } else if (isOlderSemverTuple(agtCliVersion, latest)) {
    // Channel target is strictly older than what we run. Refuse and warn.
    log(
      `[self-update] refusing to downgrade: installed ${agtCliVersion} is newer than ` +
        `channel=${channel} target ${latest}. Bump the channel tag forward to resume tracking.`,
    );
    shouldUpdate = false;
  } else {
    shouldUpdate = true;
  }
  if (!shouldUpdate) {
    if (!selfUpdateUpToDateLogged) {
      log(`[self-update] agt CLI is up to date (npm, channel=${channel}, ${agtCliVersion})`);
      selfUpdateUpToDateLogged = true;
    }
    // Already on the latest in-channel build: a legitimate no-op. A forced
    // request is consumed here (matches the dialog's "already on the latest CLI
    // → no-op"), so it doesn't retry forever.
    return 'noop';
  }

  // ENG-5963: gate the (non-urgent) restart-causing upgrade to the maintenance
  // window before the coalesce gate. Outside the window we defer to the next
  // in-window poll; AGT_MAINTENANCE_WINDOW_DISABLE bypasses for incidents.
  // ENG-6692: the forced "Update CLI now" path bypasses the window.
  if (!force && decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'defer') {
    log(
      `[self-update] agt CLI ${agtCliVersion} → ${latest} (channel=${channel}) deferred — ` +
        formatWindowDeferLogLine(cachedMaintenanceWindow ?? {}, new Date()),
    );
    // Window defer only happens for non-forced checks (force bypasses above);
    // the return value is ignored by non-forced callers.
    return 'noop';
  }

  // ENG-5862 — same coalesce gate as the brew path. Multiple patch bumps that
  // arrive within the window collapse into one restart at the next poll cycle
  // after the window expires (the registry will return the latest tag then).
  // ENG-6692: the forced "Update CLI now" path bypasses coalesce too.
  const coalesceWindowMs = resolveCoalesceWindowMs();
  const coalesce = decideSelfUpdateCoalesce({
    windowMs: coalesceWindowMs,
    lastAppliedMs: readLastSelfUpdateAppliedMs(selfUpdateAppliedMarkerPath()),
    now: Date.now(),
  });
  if (!force && !coalesce.proceed) {
    log(formatCoalesceDeferLogLine({
      installed: agtCliVersion, latest, channelLabel: `npm, channel=${channel}`,
      remainingMs: coalesce.remainingMs, windowMs: coalesceWindowMs,
    }));
    return 'noop';
  }

  log(`[self-update] agt CLI update available: ${agtCliVersion} → ${latest} (channel=${channel}). Upgrading via npm...`);
  return await installAgtCliViaNpm(latest, `channel=${channel}`, force);
}

/**
 * ENG-5963: fetch the version the `urgent` npm dist-tag points at, or null when
 * the tag isn't published (the 404 common case) or the registry is unreachable.
 * Best-effort — a failure here just means "no urgent hotfix", and never blocks
 * the normal channel path.
 */
async function fetchUrgentDistTagVersion(): Promise<string | null> {
  try {
    const res = await fetch('https://registry.npmjs.org/@integrity-labs/agt-cli/urgent', {
      signal: AbortSignal.timeout(10_000),
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: string };
    return body.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Install a specific agt-cli version globally via npm and schedule the manager
 * restart. Shared by the channel path and the urgent escape hatch; `reasonLabel`
 * only colours the log line. Stamps the coalesce marker so a same-window race
 * collapses to one restart.
 *
 * /usr/lib/node_modules is root-owned on EC2; the manager runs as ec2-user.
 * `sudo -n` non-interactively elevates (NOPASSWD on AL2023) and fails fast
 * rather than hang on a TTY prompt; when already root we skip sudo entirely.
 */
async function installAgtCliViaNpm(version: string, reasonLabel: string, force = false): Promise<SelfUpdateOutcome> {
  const { execFileSync } = await import('node:child_process');
  const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
  const cmd = isRoot ? 'npm' : 'sudo';
  const installArgs = [
    'install',
    '-g',
    `@integrity-labs/agt-cli@${version}`,
    '--registry=https://registry.npmjs.org',
  ];
  const args = isRoot ? installArgs : ['-n', 'npm', ...installArgs];

  // ENG-10724: the LAST thing before the binary is replaced, and the only place
  // this path asks. Everything that resolved `version` - the channel dist-tag
  // read, the urgent dist-tag fetch - is a registry round trip, and `npm i -g`
  // removes the old package before writing the new one. Installing onto a host
  // whose ~/.claude.json went away in the meantime would schedule the restart
  // that parks every agent on the login picker.
  //
  // `force` IS threaded, and the comment here used to claim it did not need to
  // be because "the caller above has already announced a forced hold". No caller
  // above announces anything: this is the npm path's ONLY hold site, and
  // `checkAndUpdateCliViaNpm` makes none. So on an npm-global host - the EC2
  // production install method - an operator clicking "Update CLI now" after the
  // routine poll had already claimed `firstHold` got no reason at all, only
  // `runPendingForcedUpdate`'s generic "did not complete". The brew path passed
  // `force` and worked; this one did not. Obi Wan on #6250.
  const npmInstallHold = await selfUpdateDeviceStateHold(force);
  if (npmInstallHold) return npmInstallHold;

  try {
    execFileSync(cmd, args, { timeout: 180_000, stdio: 'pipe' });
  } catch (err) {
    log(`[self-update] npm upgrade failed: ${(err as Error).message}`);
    return 'failed';
  }

  // ENG-6698: post-install smoke check. `npm i -g` removes the old package
  // before writing the new one, so an install interrupted mid-flight can leave
  // `agt` a dangling symlink even after the spawn returns. Restarting into a
  // dangling binary is what took the whole fleet dark (203/EXEC crashloop ->
  // systemd `failed`). Verify the freshly-installed binary actually runs and
  // reports the new version BEFORE scheduling the restart; if it doesn't, keep
  // the current (still-working) in-memory process running and let the next
  // cycle retry. The systemd ExecStartPre repair is the backstop for the case
  // where this manager is itself killed mid-install and never reaches here.
  let smokeOk = false;
  try {
    const out = execFileSync('agt', ['--version'], { timeout: 15_000, stdio: 'pipe' }).toString();
    smokeOk = isAgtVersionSmokeOk(out, version);
    if (!smokeOk) {
      log(
        `[self-update] smoke check failed: \`agt --version\` did not report ${version} after install (got: ${out.trim().slice(0, 80)}). NOT restarting.`,
      );
    }
  } catch (err) {
    log(
      `[self-update] smoke check failed: \`agt --version\` would not run after install (${(err as Error).message}). The binary may be dangling; NOT restarting. systemd ExecStartPre will repair on the next start if needed.`,
    );
  }
  if (!smokeOk) return 'failed';

  log(
    `[self-update] agt CLI upgraded to ${version} (${reasonLabel}). Scheduling manager restart so the new binary takes effect.`,
  );
  stampLastSelfUpdateApplied(selfUpdateAppliedMarkerPath());
  restartAfterUpgrade = true;
  pendingUpgradeVersion = version;
  return 'updated';
}

/**
 * ENG-6353: true when a version string carries a prerelease suffix
 * (e.g. `0.27.150-test.17`). `isNewerSemver` NaN-parses the prerelease patch
 * component and reports "not newer" for EVERY candidate, so a host left on a
 * `-test.x` build (an unpinned canary) would never auto-upgrade to a higher
 * stable `latest`. The self-update path detects prereleases and falls back to
 * tuple comparison (`isOlderSemverTuple`) instead. Build metadata after `+` is
 * not a prerelease, so it's stripped before the `-` check.
 */
export function isPrereleaseVersion(v: string): boolean {
  return v.replace(/^v/, '').split('+')[0]!.includes('-');
}

/**
 * ENG-6353: the `latest`-channel self-update decision.
 *
 * - Stable installed → upgrade only to a strictly-newer semver
 *   (`isNewerSemver`) — unchanged prod behaviour.
 * - Prerelease installed (e.g. `0.27.150-test.17`, left behind when a canary
 *   host is unpinned) → `isNewerSemver` NaN-parses the prerelease patch and
 *   reports "not newer" for EVERY stable `latest`, stranding the host on the
 *   prerelease forever (the trap that forced a manual force-install on
 *   agt-aws-1 during the ENG-6300 unpin). Compare by numeric tuple instead:
 *   upgrade unless `latest` is a strictly-OLDER tuple — preserving the
 *   never-downgrade guarantee while letting a higher stable land.
 */
export function shouldUpdateOnLatest(installed: string, latest: string): boolean {
  if (isPrereleaseVersion(installed)) {
    return latest !== installed && !isOlderSemverTuple(installed, latest);
  }
  return isNewerSemver(installed, latest);
}

/** Compare two semver strings; returns true when `remote` is strictly newer. */
function isNewerSemver(local: string, remote: string): boolean {
  const parse = (v: string) => v.replace(/^v/, '').split('.').map(Number);
  const l = parse(local);
  const r = parse(remote);
  for (let i = 0; i < 3; i++) {
    const lv = l[i] ?? 0;
    const rv = r[i] ?? 0;
    if (rv !== lv) return rv > lv;
  }
  return false;
}

/**
 * Compare the major.minor.patch tuple of two semver strings, ignoring
 * prerelease/build suffixes; returns true when `remote`'s tuple is
 * strictly older than `local`'s. Used by the channel-aware self-update
 * guard (ENG-5785) to refuse a downgrade from a manually-upgraded
 * stable back to an older channel-pinned prerelease.
 *
 * Note: a same-tuple/different-prerelease pair (e.g. local=`0.27.6`,
 * remote=`0.27.6-test.2`) returns false — the channel still moves the
 * host onto its target. The guard only blocks strictly-older tuples.
 */
export function isOlderSemverTuple(local: string, remote: string): boolean {
  const parse = (v: string) =>
    v
      .replace(/^v/, '')
      .split(/[-+]/)[0]!
      .split('.')
      .map((p) => Number(p));
  const l = parse(local);
  const r = parse(remote);
  for (let i = 0; i < 3; i++) {
    const lv = l[i] ?? 0;
    const rv = r[i] ?? 0;
    if (rv !== lv) return rv < lv;
  }
  return false;
}

/**
 * Check Claude Code auth and return true if logged in.
 *
 * Uses the same file/keychain probe as `detectClaudeAuth` (our heartbeat
 * reporter) instead of shelling out to `claude auth status`. On a root-owned
 * manager that's spawned by cloud-init, `claude` isn't on PATH and even if
 * it were, running it as root would look at `/root/.claude/` — but the
 * operator logs in as `ssm-user` or `ec2-user`, so the creds live under
 * their home. `detectClaudeAuth` probes every user home in `/home` when
 * running as root on Linux, so it finds subscription creds regardless of
 * which user completed `claude /login`.
 */
// ---------------------------------------------------------------------------
// ENG-8623 — the runtime auth probe
// ---------------------------------------------------------------------------
//
// `checkClaudeAuth()` below reads the credentials FILE. That is the whole
// problem: `computeSubscriptionStatus()` reports `valid` whenever a
// `refreshToken` key is present regardless of expiry, so a refresh token that
// has itself died reads healthy forever while no agent on the host can start.
// Measured 2026-08-09: `claude auth status --json` also answers
// `{"loggedIn": true}` for such a credential, so a cheaper check does not exist
// — only a real round-trip separates the two. Rationale, evidence and the
// asymmetric classification live in ./claude-runtime-auth-probe.ts.
//
// The probe REFINES the file answer, it does not replace it: an inconclusive
// result leaves the file-derived value exactly as it is today, so the worst case
// of this feature is the status quo.
const RUNTIME_AUTH_PROBE_INTERVAL_MS = 15 * 60 * 1000;
const RUNTIME_AUTH_PROBE_TIMEOUT_MS = 60_000;
/** Two probes ≈ 30 minutes of sustained refusal before the host is condemned. */
const RUNTIME_AUTH_PROBE_FAILURES_TO_CONDEMN = 2;

let lastRuntimeAuthProbeAt = 0;
let runtimeAuthProbeInFlight = false;
let runtimeAuthConsecutiveFailures = 0;

/**
 * The probe's own verdict, kept SEPARATE from `agentRuntimeAuthenticated`
 * (which stays the file-derived value it has always been). `null` means the
 * probe holds no conclusive opinion and the file answer stands.
 *
 * Two reasons they cannot be one variable:
 *
 *  1. `attemptPersistentSession` re-runs `checkClaudeAuth()` — the FILE check —
 *     whenever the boolean is false. Folding the probe into the same variable
 *     would let that per-agent re-check silently overwrite a probe verdict with
 *     the very inference the probe exists to correct, roughly once per spawn.
 *  2. The spawn GATE deliberately keeps using the file value (see
 *     `effectiveRuntimeAuthenticated`), so the two need to be readable apart.
 */
let runtimeAuthProbeVerdict: boolean | null = null;

/**
 * The best answer available locally: the probe's verdict when it has one, the
 * file-derived value otherwise.
 *
 * This is the FOLD BASELINE, not the wire value. What the heartbeat sends is
 * `heartbeatRuntimeAuthReport(runtimeAuthProbeVerdict)`, which sends nothing at
 * all without a verdict — the API stamps a freshness timestamp beside whatever
 * boolean it receives, and a file-derived value there would read downstream as a
 * runtime observation that never happened.
 *
 * The baseline still defers to the file value, and must: it is what
 * `foldRuntimeAuthVerdict` carries forward on an inconclusive probe, so a single
 * blip cannot flip a host that the file already believes is fine.
 *
 * The probe does not gate session spawning either, deliberately: a wrong `false`
 * would stop every agent on the host, whereas a wrong `false` in the report
 * costs one alert an operator can dismiss. The alert is the deliverable this
 * ticket asked for; blocking spawns on the same signal is a separate decision
 * with a much larger blast radius, and it should be made deliberately once the
 * probe has a track record in the fleet.
 */
function effectiveRuntimeAuthenticated(): boolean {
  return runtimeAuthProbeVerdict ?? agentRuntimeAuthenticated;
}

/**
 * Fire-and-forget from pollCycle. Throttled, single-flight, and never throws —
 * an auth probe that could wedge the poll loop would be a worse bug than the one
 * it exists to catch.
 */
export async function maybeProbeClaudeRuntimeAuth(): Promise<void> {
  if (runtimeAuthProbeInFlight) return;
  if (Date.now() - lastRuntimeAuthProbeAt < RUNTIME_AUTH_PROBE_INTERVAL_MS) return;

  const { execFileSync } = await import('node:child_process');
  if (!claudeBinaryInstalled(execFileSync)) return;

  runtimeAuthProbeInFlight = true;
  lastRuntimeAuthProbeAt = Date.now();
  try {
    const before = effectiveRuntimeAuthenticated();
    const result = await runClaudeRuntimeAuthProbe();
    const folded = foldRuntimeAuthVerdict({
      verdict: result.verdict,
      previous: before,
      consecutiveFailures: runtimeAuthConsecutiveFailures,
      requiredConsecutiveFailures: RUNTIME_AUTH_PROBE_FAILURES_TO_CONDEMN,
    });
    runtimeAuthConsecutiveFailures = folded.consecutiveFailures;

    // Record a verdict only when the probe actually reached one: a success, or
    // a refusal confirmed across enough probes to change the answer. An
    // inconclusive probe — or a first, unconfirmed refusal — leaves the
    // deferral to the file value intact rather than freezing today's value in.
    if (result.verdict === 'authenticated' || folded.reported !== before) {
      runtimeAuthProbeVerdict = folded.reported;
    }

    if (folded.reported !== before) {
      log(
        `[runtime-auth-probe] reported authentication ${before} -> ${folded.reported} ` +
          `(verdict=${result.verdict}, consecutiveFailures=${folded.consecutiveFailures}): ${result.detail}`,
      );
    } else if (result.verdict !== 'authenticated') {
      // Logged even when nothing changed: an operator reading manager.log after
      // an outage needs to see that the probe ran and what it saw, not only the
      // transitions. Silence is what got us here.
      log(
        `[runtime-auth-probe] verdict=${result.verdict} ` +
          `(consecutiveFailures=${runtimeAuthConsecutiveFailures}): ${result.detail}`,
      );
    }
  } catch (err) {
    log(`[runtime-auth-probe] probe error (treated as inconclusive): ${(err as Error).message}`);
  } finally {
    runtimeAuthProbeInFlight = false;
  }
}

async function runClaudeRuntimeAuthProbe(): Promise<RuntimeAuthProbeResult> {
  const childEnv: Record<string, string | undefined> = { ...process.env };
  await applyClaudeAuthToEnv(childEnv, 'runtime-auth-probe');
  const emptyMcp = ensureEvalEmptyMcpConfig();
  const args = [
    '-p', RUNTIME_AUTH_PROBE_PROMPT,
    '--model', process.env['AGT_CONV_EVAL_CLAUDE_MODEL']?.trim() || DEFAULT_CLAUDE_EVAL_MODEL, // feature-gate-allow: model tunable, not a gate
    '--output-format', 'text',
    // Same isolation as conversation-eval: no project config, no MCP, no tools.
    // The probe must exercise AUTH and nothing else.
    '--mcp-config', emptyMcp,
    '--strict-mcp-config',
    '--permission-mode', 'auto',
    '--allowedTools', '',
  ];

  try {
    const { stdout, stderr } = await execFilePromiseLong(resolveClaudeBinary(), args, {
      cwd: homedir(),
      timeout: RUNTIME_AUTH_PROBE_TIMEOUT_MS,
      stdin: 'ignore',
      env: childEnv,
      onSpawn: (pid) => registerClaudeSpawn({ pid, started_at: Date.now(), kind: 'conv-eval' }),
      onExit: (pid) => unregisterClaudeSpawn(pid),
    });
    return classifyRuntimeAuthProbe({ exitCode: 0, stdout, stderr });
  } catch (err) {
    if (err instanceof ChildProcessError) {
      return classifyRuntimeAuthProbe({
        exitCode: err.code,
        stdout: err.stdout,
        stderr: err.stderr,
      });
    }
    // execFilePromiseLong rejects with a plain Error on ITS timeout, and with a
    // spawn error if the binary vanished. Neither says anything about the
    // credential, so neither may condemn the host.
    return classifyRuntimeAuthProbe({
      exitCode: null,
      stdout: '',
      stderr: (err as Error).message,
      timedOut: true,
    });
  }
}

/**
 * ENG-9394: run `claude -p "/usage" --output-format json` and hand the raw
 * stdout back.
 *
 * Isolated exactly like the runtime-auth probe - no project config, no MCP, no
 * tools - because none of that is needed to render local usage metadata, and
 * loading it would give the poll side effects it has no business having. Measured
 * on a live host: identical output and identical ~2.95s latency with the flags
 * on, so the isolation is free.
 *
 * Never throws. Every failure is `failed: true`, which the poller maps to
 * `unknown` - never to at-limit. A usage reading that fails closed gates the
 * whole fleet off, which is worse than the false alerts this replaces.
 */
async function runHostUsageCommand(): Promise<UsageCommandResult> {
  // Setup lives INSIDE the try (CodeRabbit on #4988). `applyClaudeAuthToEnv` and
  // `ensureEvalEmptyMcpConfig` both do real work - a keychain read and a file
  // write - and either can throw. With them outside, the docblock's "never
  // throws" was false, and the function would have propagated a setup failure
  // instead of returning the failed result its own contract promises. The
  // caller happens to catch it too, so this was not a live defect; it was a
  // comment that lied, which is how the next reader gets it wrong.
  try {
    const childEnv: Record<string, string | undefined> = { ...process.env };
    await applyClaudeAuthToEnv(childEnv, 'host-usage-poll');
    const emptyMcp = ensureEvalEmptyMcpConfig();
    const args = [
      ...USAGE_COMMAND_ARGS,
      '--mcp-config', emptyMcp,
      '--strict-mcp-config',
      '--permission-mode', 'auto',
      '--allowedTools', '',
    ];
    const { stdout } = await execFilePromiseLong(resolveClaudeBinary(), args, {
      cwd: homedir(),
      timeout: HOST_USAGE_POLL_TIMEOUT_MS,
      stdin: 'ignore',
      env: childEnv,
      onSpawn: (pid) => registerClaudeSpawn({ pid, started_at: Date.now(), kind: 'conv-eval' }),
      onExit: (pid) => unregisterClaudeSpawn(pid),
    });
    return { stdout, failed: false };
  } catch (err) {
    // A non-zero exit still carries stdout, but a CLI that exited non-zero is
    // not a CLI whose usage render we trust, so it is reported as failed either
    // way. `failed` is what keeps the reading `unknown`.
    if (err instanceof ChildProcessError) return { stdout: err.stdout ?? '', failed: true };
    return { stdout: '', failed: true };
  }
}

/**
 * Fire-and-forget from pollCycle, same contract as maybeProbeClaudeRuntimeAuth:
 * throttled and single-flight inside the poller, and never allowed to throw into
 * the poll loop.
 */
async function maybePollHostUsageFromCycle(): Promise<void> {
  const { execFileSync } = await import('node:child_process');
  if (!claudeBinaryInstalled(execFileSync)) return;
  await maybePollHostUsage({
    api,
    runUsageCommand: runHostUsageCommand,
    authMode: getCachedClaudeAuthMode(),
    cliVersion: cachedFrameworkVersion,
    log,
  });
}

async function checkClaudeAuth(): Promise<boolean> {
  try {
    const report = await detectClaudeAuth();
    if (!report) {
      log('⚠️  Claude Code is not authenticated. Run `claude /login` (or set `ANTHROPIC_API_KEY`) on the host, then the manager will pick it up on the next cycle.');
      return false;
    }
    if (report.status === 'expired') {
      log(`⚠️  Claude Code auth expired (${report.mode}). Re-run \`claude /login\` to refresh.`);
      return false;
    }
    if (report.status === 'expiring_soon') {
      log(`[auth] Claude Code token expiring soon (expires_at=${report.expires_at}) — still valid, proceeding`);
    }
    return true;
  } catch (err) {
    log(`⚠️  Claude Code auth probe failed: ${(err as Error).message}`);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Conversation success evaluation — isolated Haiku spawn
//
// Runs `claude -p --model <haiku>` with the agent's auth applied but WITHOUT
// the agent's CLAUDE.md system prompt, MCP servers, or tools. This is an
// objective evaluator, not the agent persona — and keeping the transcript
// host-side (it only reaches Anthropic via the agent's own auth, never the agt
// API) is the whole point of scoring on the manager. Returns stdout text.
// ---------------------------------------------------------------------------

let evalEmptyMcpConfigPath: string | null = null;
function ensureEvalEmptyMcpConfig(): string {
  if (evalEmptyMcpConfigPath && existsSync(evalEmptyMcpConfigPath)) return evalEmptyMcpConfigPath;
  const dir = join(homedir(), '.augmented');
  try { mkdirSync(dir, { recursive: true }); } catch { /* best-effort */ }
  const p = join(dir, '.eval-empty-mcp.json');
  writeFileSync(p, JSON.stringify({ mcpServers: {} }));
  evalEmptyMcpConfigPath = p;
  return p;
}

async function runEvalClaude(prompt: string, model: string): Promise<string> {
  const childEnv: Record<string, string | undefined> = { ...process.env };
  await applyClaudeAuthToEnv(childEnv, 'conversation-eval');
  const emptyMcp = ensureEvalEmptyMcpConfig();
  const args = [
    '-p', prompt,
    '--model', model,
    '--output-format', 'text',
    // Isolation: only the empty MCP config, ignore project/user config + no tools.
    '--mcp-config', emptyMcp,
    '--strict-mcp-config',
    '--permission-mode', 'auto',
    '--allowedTools', '',
  ];
  const { stdout } = await execFilePromiseLong(resolveClaudeBinary(), args, {
    cwd: homedir(),
    timeout: 120_000,
    stdin: 'ignore',
    env: childEnv,
    onSpawn: (pid) => registerClaudeSpawn({ pid, started_at: Date.now(), kind: 'conv-eval' }),
    onExit: (pid) => unregisterClaudeSpawn(pid),
  });
  return stdout;
}

// Pluggable conversation-eval scoring backend:
//   AGT_CONV_EVAL_BACKEND=claude-p (default) - Haiku via `claude -p`, reusing the
//     agent's auth; transcript reaches Anthropic (the boundary the agent already
//     crosses), nothing else. A heavy subprocess, but free under the agent's Max
//     subscription.
//   AGT_CONV_EVAL_BACKEND=anthropic-api - Haiku via a direct Anthropic Messages
//     API fetch (no subprocess). Same Anthropic boundary, but metered per-token.
//     Auth: AGT_CONV_EVAL_ANTHROPIC_API_KEY (falls back to ANTHROPIC_API_KEY);
//     missing key fails CLOSED (eval disabled) rather than reverting to claude-p,
//     so the cost/auth boundary never shifts by accident.
//   AGT_CONV_EVAL_BACKEND=local - an OpenAI-compatible endpoint on the host
//     (Ollama / vLLM / llama.cpp / LM Studio) serving e.g. Gemma 4 12B; the
//     transcript never leaves the host at all. Tunables:
//       AGT_CONV_EVAL_LOCAL_URL     (default http://localhost:11434/v1/chat/completions)
//       AGT_CONV_EVAL_LOCAL_MODEL   (default gemma4:12b)
//       AGT_CONV_EVAL_LOCAL_API_KEY (optional bearer, for vLLM etc.)
//   AGT_CONV_EVAL_CLAUDE_MODEL overrides the Haiku model id for both Claude paths.
// The backend KIND is the 'conversation-eval-backend' registry flag (ADR-0022),
// so AGT_CONV_EVAL_BACKEND is now the operator override on that flag rather than
// a standalone gate, and the fleet default flips centrally (no restart). The
// other AGT_CONV_EVAL_* knobs above stay plain env. Selection logic lives in
// selectEvalBackend (conversation-evaluator.ts) so it's pure / unit-testable;
// this wrapper reads the flag + env and rebuilds only when they change.
// ENG-6200 / ADR-0022: host-side memory extraction is now the 'memory-extraction'
// registry feature flag, resolved env override → heartbeat → compiled default.
// Ships dark (default false). The legacy AGT_MEMORY_EXTRACTION_ENABLED env var
// stays the highest-precedence operator override during migration, so any host
// already setting it keeps working; a central DB flip now ALSO reaches a running
// manager within one heartbeat (~5m) without a restart. Reuses the
// conversation-eval backend (same on-host privacy boundary).
function memoryExtractionEnabled(): boolean {
  return hostFlagStore().getBoolean('memory-extraction');
}

// ENG-8575 / ADR-0022: host-side tool-call audit extraction, gated by the
// 'tool-call-audit' registry flag (env override → heartbeat → compiled
// default). Ships dark (default false).
//
// This is ROLLOUT control, not the commercial gate. POST /host/tool-calls
// independently re-checks the advanced_governance entitlement and refuses
// regardless of what this host believes, because the host is customer-owned
// infrastructure. The host-side skip in maybeScanToolCalls is an efficiency
// optimisation latched off the route's own answer — never the control.
//
// Unlike the flags materialized into the agent spawn env, this one is read by
// the MANAGER process, which reads flags-cache.json directly. There is no
// container boundary to cross, so a central flip reaches it within one
// heartbeat (~5m) with no agent respawn — see the rollout note in
// docs/runbooks/tool-call-audit-rollout.md.
function toolCallAuditEnabled(): boolean {
  return hostFlagStore().getBoolean('tool-call-audit');
}

// ENG-7235: on an OpenRouter-metered host the idle, no-deliverable scorers
// (conversation eval + memory extraction) are real per-token cost - and the
// default `claude-p` backend additionally can't authenticate there (no Max
// OAuth) so it would retry-loop "Not logged in" every poll. Suppress the metered
// backends; the free, host-private `local` backend is the explicit opt-in to
// keep scoring on a metered host. Decided per poll off the cached host auth mode
// (host-level, populated by the early exchange) + the resolved backend flag.
function conversationScorerSuppressed(): boolean {
  return meteredScorerSuppressed(
    getCachedClaudeAuthMode(),
    hostFlagStore().getString('conversation-eval-backend'),
  );
}

let lastMeteredScorerLogMs = 0;
function logMeteredScorerSuppressed(): void {
  const nowMs = Date.now();
  // Throttle: this runs per-agent per-poll; one line / 30 min is enough to make
  // the cost-skip observable without flooding manager.log.
  if (nowMs - lastMeteredScorerLogMs < 30 * 60_000) return;
  lastMeteredScorerLogMs = nowMs;
  const kind = hostFlagStore().getString('conversation-eval-backend');
  log(
    `[conversation-eval] host is OpenRouter-metered (backend='${kind}') - skipping conversation eval + memory extraction to avoid per-token cost for a no-deliverable scorer (ENG-7235). Set conversation-eval-backend=local to run the free host-local scorer instead.`,
  );
}

let conversationEvalBackend: ConversationEvalBackend | null = null;
let conversationEvalBackendSig: string | null = null;
function resolveConversationEvalBackend(): ConversationEvalBackend {
  // Backend kind is the 'conversation-eval-backend' enum flag (ENG-6581), read
  // through the host flag store so it carries full ADR-0022 precedence:
  // env override (AGT_CONV_EVAL_BACKEND) → central heartbeat flag/overrides →
  // compiled default ('claude-p'). Reading it per-call (the store reads its
  // cached map, cheap) means a central flip reaches a running manager within one
  // heartbeat with no restart - we just rebuild the backend when the resolved
  // inputs actually change (signature below), so steady state stays memoised.
  const kind = hostFlagStore().getString('conversation-eval-backend');
  const claudeModel = process.env['AGT_CONV_EVAL_CLAUDE_MODEL']?.trim() || DEFAULT_CLAUDE_EVAL_MODEL; // feature-gate-allow: model tunable, not a gate (matches the _MODE substring of _MODEL)
  const anthropicApiKey =
    process.env['AGT_CONV_EVAL_ANTHROPIC_API_KEY']?.trim() ||
    process.env['ANTHROPIC_API_KEY']?.trim() ||
    '';
  const local = {
    url: process.env['AGT_CONV_EVAL_LOCAL_URL']?.trim() || DEFAULT_LOCAL_EVAL_URL,
    model: process.env['AGT_CONV_EVAL_LOCAL_MODEL']?.trim() || DEFAULT_LOCAL_EVAL_MODEL, // feature-gate-allow: model tunable, not a gate (matches the _MODE substring of _MODEL)
    apiKey: process.env['AGT_CONV_EVAL_LOCAL_API_KEY']?.trim() || undefined,
  };
  const sig = `${kind}|${claudeModel}|${anthropicApiKey ? 'key' : 'nokey'}|${local.url}|${local.model}|${local.apiKey ? 'lkey' : 'nolkey'}`;
  if (conversationEvalBackend && conversationEvalBackendSig === sig) return conversationEvalBackend;
  // selectEvalBackend is non-throwing by design (fail-closed via a backend whose
  // run() rejects, contained by the try/catch in maybeEvaluateConversations /
  // maybeExtractMemories). Wrap it anyway so a future change that made backend
  // construction throw could only ever disable eval, never bubble out of the
  // synchronous resolve and abort the rest of processAgent's refresh/session work.
  try {
    conversationEvalBackend = selectEvalBackend({
      kind,
      claudeModel,
      anthropicApiKey,
      local,
      // The manager owns the spawn helpers; selectEvalBackend stays transport-free.
      claudePRunner: runEvalClaude,
      log,
    });
  } catch (err) {
    const message = (err as Error).message;
    log(`[conversation-eval] backend unavailable, eval disabled: ${message}`);
    conversationEvalBackend = {
      model: `${kind || 'default'}:unavailable`,
      run: async () => {
        throw new Error(message);
      },
    };
  }
  conversationEvalBackendSig = sig;
  return conversationEvalBackend;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// State file lives under the manager's configDir so a non-default dir
// doesn't split manager artifacts across locations.
function getStateFile(): string {
  return join(config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented'), 'manager-state.json');
}

// ENG-4712: see channel-hash-cache.ts for the rationale. We keep the
// load/save call sites inline here so every cache mutation pairs with a
// persist; the module holds the file format + IO and stays trivially
// testable.
function channelHashCacheDir(): string {
  return config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
}

function loadChannelHashCache(): void {
  loadChannelHashCacheFromDisk(agentState.knownChannelConfigHashes, channelHashCacheDir());
}

function saveChannelHashCache(): void {
  saveChannelHashCacheToDisk(agentState.knownChannelConfigHashes, channelHashCacheDir());
}

/**
 * ENG-8290: consecutive credential writes that reported no error but did not
 * actually land on disk, keyed `${agent_id}:${channelId}`.
 *
 * Deliberately in-memory only. This counts a CURRENT unresolved condition, not
 * a historical fact — a manager restart re-derives it within one poll cycle,
 * and persisting it would mean an operator who fixed the underlying problem
 * still saw escalations until something cleared the file.
 *
 * Cleared the moment a write lands, so a recovery is visible rather than
 * inferred from silence.
 */
const channelWriteFailureCounts = new Map<string, number>();

/**
 * How often to re-log a non-landing credential write.
 *
 * The bug this exists for logged an info line every ~40 seconds forever, which
 * is precisely how it stayed invisible: a path that reports "missing,
 * rewriting" ninety times an hour as normal operation makes a genuinely missing
 * credential indistinguishable from steady state. So: shout on the first
 * failure, then only periodically. Roughly hourly at a ~40s poll.
 */
const CHANNEL_WRITE_FAILURE_ESCALATE_EVERY = 90;

/**
 * ENG-9252 — a channel credential sync that cannot converge must not retry at
 * full rate forever, and must not stay invisible while it does.
 *
 * THE OBSERVED FAILURE. `sheraz` was un-paused on two-tractors-host at 22:46 on
 * 2026-08-20. From that moment the manager logged
 * `[channels] Credential sync did not converge for 'sheraz'` on every tick —
 * measured absent at 22:40, present at 23:34, still going at 01:58, 3h+ later —
 * each line accompanied by a `[provision/active-tasks]` call, so every retry did
 * real work. Zero alerts fired in that window, because non-convergence touches
 * neither heartbeat nor session health nor restart count: the agent projection
 * read `active` / `fresh` with `restart_count: 0` throughout. It was found by
 * reading the raw manager log while chasing something else.
 *
 * WHY THE LOGGING MADE IT WORSE RATHER THAN BETTER. The three sites that set
 * `channelConfigConverged = false` each log their own reason, but the summary
 * line printed afterwards carried no reason at all — and the most common of the
 * three (`ENG-8290`, a write that reported no error and did not land) escalates
 * only once per ninety attempts. So the line with the diagnosis appeared rarely
 * and the line without it appeared every tick. The signal-to-noise was exactly
 * inverted, which is how hours of a hot loop read as ordinary log volume.
 *
 * WHAT THIS ADDS. A per-AGENT streak: the reason travels with it, the summary
 * line is throttled and states how long and how many attempts, the retry backs
 * off geometrically to a ceiling instead of running every tick, and a streak
 * older than {@link CHANNEL_SYNC_STUCK_AFTER_MS} is reported in the heartbeat so
 * the control plane can open an alert (mirroring ENG-6068's
 * `channels_setting_status`, which is why this needs no new endpoint).
 *
 * TWO INVARIANTS THE BACKOFF MUST NOT BREAK:
 *   - the diff stays LIVE. `agentState.knownChannels` is still only advanced on
 *     convergence, so backing off delays a retry and never swallows one.
 *   - a REAL change jumps the queue. `channelsChanged` clears the streak, so an
 *     operator who binds or unbinds a channel is served on the next tick rather
 *     than waiting out a backoff earned by the previous state. A content-only
 *     change (same channel set, different config) does wait, bounded by the
 *     ceiling below — stated rather than hidden.
 *
 * In-memory only, for the same reason as `channelWriteFailureCounts`: it counts
 * a CURRENT unresolved condition. A manager restart re-derives it within one
 * poll, and persisting it would keep escalating at an operator who had already
 * fixed the cause.
 */
export interface ChannelSyncFailureState {
  /** Consecutive non-convergent ticks that actually ATTEMPTED the sync. */
  attempts: number;
  /** Epoch ms of the first failure in this streak. */
  since: number;
  /** Why the most recent attempt did not converge. */
  lastReason: string;
  /** `attempts` at the last summary line, so the log can be throttled. */
  loggedAtAttempt: number;
  /** Epoch ms before which the sync is skipped entirely. */
  nextAttemptAt: number;
}

const channelSyncFailures = new Map<string, ChannelSyncFailureState>();

/**
 * ENG-9652: the definition ids last observed in each agent's effective
 * integration set, consulted by the channel sync so it does not write
 * credentials for a channel whose integration is not installed. Populated in
 * processAgent step 6b; see channel-sync-precondition.ts for why terminating
 * here is safe when the backoff deliberately does not terminate.
 */
const effectiveIntegrationObservations: EffectiveIntegrationObservations =
  createEffectiveIntegrationObservations();

/**
 * The channel set as of the last sync ATTEMPT, per agent.
 *
 * Not the same thing as `agentState.knownChannels`, and conflating them made the
 * first cut of this change inert in exactly the failure it targets (CodeRabbit
 * on #4840). `knownChannels` only advances on CONVERGENCE, so while a change is
 * failing to converge it keeps the pre-change value and `channelsChanged` stays
 * true on EVERY subsequent tick — not just on the tick of the operator action.
 * Clearing the streak on `channelsChanged` therefore cleared it every tick:
 * `attempts` never passed 1, the backoff never engaged, the summary line still
 * printed per tick, and `since` reset so the stuck report never fired. Every
 * mechanism here was disabled by the one line meant to keep an operator action
 * responsive.
 *
 * This map advances on every attempt instead, so "the set changed since I last
 * tried" is answerable independently of whether that attempt worked — which is
 * the question the queue-jumping rule actually wanted to ask.
 */
const channelSyncAttemptedSets = new Map<string, Set<string>>();

/**
 * ENG-9517: the per-channel credential verdict, as last RE-OBSERVED on disk.
 *
 * The two maps above are failure-tracking: they answer "has this agent's sync
 * been broken, and for how long". Nothing answered the positive question — "is
 * THIS channel's credential actually present right now" — anywhere the control
 * plane could see it, so a channel that never reached the host was
 * indistinguishable from one working perfectly. Both render as a row with
 * status 'pending'.
 *
 * Deliberately NOT derived from the absence of a `channelSyncFailures` entry.
 * That set only populates after a write is attempted and fails, so its absence
 * covers three different states at once: converged, never attempted, and
 * deferred by the backoff. Reading it as success is the same "absence of a
 * complaint means it worked" inference this ticket exists to kill.
 *
 * Keyed agentId -> channelId. Rebuilt from a fresh `hasChannelCredentials`
 * check on every non-deferred tick rather than accumulated, so a credential
 * that disappears is reported missing on the next tick instead of leaving a
 * stale success behind.
 */
export interface ChannelCredentialVerdict {
  /** Whether the credential was observed ON DISK this tick. */
  verified: boolean;
  /** The manager's reported cause, when it did not converge. */
  reason?: string;
}

const channelCredentialVerdicts = new Map<string, Map<string, ChannelCredentialVerdict>>();

/** Same-membership test for the two channel sets above. */
export function sameChannelSet(a: Set<string> | undefined, b: Set<string>): boolean {
  if (a === undefined) return false;
  if (a.size !== b.size) return false;
  for (const v of b) if (!a.has(v)) return false;
  return true;
}

/**
 * Teardown for both maps. A stuck agent that is then paused, revoked,
 * unassigned or handed off never reaches the sync block again, so without this
 * its streak would live forever: the heartbeat would keep reporting it with a
 * growing `stuck_for_seconds` and the control plane would hold an alert open
 * about a sync nobody is attempting (CodeRabbit on #4840).
 */
export function forgetChannelSyncState(agentId: string): void {
  channelSyncFailures.delete(agentId);
  channelSyncAttemptedSets.delete(agentId);
  // ENG-9517: the verdict map needs the same teardown, and for a sharper
  // reason than the two above. A paused or unassigned agent never reaches the
  // verification pass again, so a retained `verified: true` would keep
  // asserting on every heartbeat that a credential is present on a host that
  // may no longer even hold the agent — a success claim nothing can refute,
  // which is the precise failure mode this field was added to remove.
  channelCredentialVerdicts.delete(agentId);
  // ENG-9652: same argument as the verdict map. A retained observation would
  // let a later tick terminate (or decline to terminate) a channel sync on a
  // belief about an integration set nobody re-checked while the agent was away.
  forgetEffectiveIntegrations(effectiveIntegrationObservations, agentId);
}

/**
 * Attempts served at full tick rate before the backoff engages. Small on
 * purpose: the overwhelming majority of non-convergences are a single transient
 * I/O failure that clears on the very next tick, and making those wait would
 * turn a self-healing blip into a visible outage.
 */
export const CHANNEL_SYNC_FREE_ATTEMPTS = 3;

/**
 * Backoff ceiling. Five minutes, not "give up": a credential sync has no safe
 * terminal state — abandoning it leaves an agent permanently unable to use a
 * channel with nothing retrying — so the give-up the ticket asks for is a
 * give-up on the RATE, not on the work. The alert below is what escalates to a
 * human; the loop keeps a slow heartbeat behind it.
 */
export const CHANNEL_SYNC_MAX_BACKOFF_MS = 5 * 60_000;

/**
 * How long a streak must survive before the heartbeat reports it as stuck.
 *
 * Ten minutes is comfortably longer than any transient (which clear inside one
 * or two ticks) and far shorter than the three hours this ran unseen.
 */
export const CHANNEL_SYNC_STUCK_AFTER_MS = 10 * 60_000;

/**
 * Delay before the next attempt after `attempts` consecutive failures.
 *
 * Geometric from the first non-free attempt, capped. Pure and exported so the
 * schedule is asserted directly rather than inferred from a timing test.
 */
export function channelSyncBackoffMs(attempts: number): number {
  if (attempts <= CHANNEL_SYNC_FREE_ATTEMPTS) return 0;
  const step = attempts - CHANNEL_SYNC_FREE_ATTEMPTS;
  // 10s, 20s, 40s, 80s, 160s, then the ceiling.
  const delay = 10_000 * 2 ** (step - 1);
  return Math.min(delay, CHANNEL_SYNC_MAX_BACKOFF_MS);
}

/** Record one non-convergent attempt and return the updated streak. */
export function recordChannelSyncFailure(
  failures: Map<string, ChannelSyncFailureState>,
  agentId: string,
  reason: string,
  now: number,
): ChannelSyncFailureState {
  const prior = failures.get(agentId);
  const attempts = (prior?.attempts ?? 0) + 1;
  const next: ChannelSyncFailureState = {
    attempts,
    since: prior?.since ?? now,
    lastReason: reason,
    loggedAtAttempt: prior?.loggedAtAttempt ?? 0,
    nextAttemptAt: now + channelSyncBackoffMs(attempts),
  };
  failures.set(agentId, next);
  return next;
}

/**
 * Whether the summary line should be written for this attempt.
 *
 * First failure always, then on the same escalating cadence as the backoff, so
 * the log density falls off with the retry density instead of staying flat.
 */
export function shouldLogChannelSyncFailure(state: ChannelSyncFailureState): boolean {
  if (state.attempts === 1) return true;
  if (state.attempts <= CHANNEL_SYNC_FREE_ATTEMPTS) return false;
  // Once backing off, every attempt is already rate-limited by the backoff
  // itself, so logging each one is at most one line per ceiling interval.
  return state.attempts > state.loggedAtAttempt;
}

/**
 * Agents whose channel sync has been stuck longer than the threshold, shaped for
 * the heartbeat body. Sorted by longest-stuck first so a truncating consumer
 * keeps the worst.
 */
export function stuckChannelSyncs(
  failures: Map<string, ChannelSyncFailureState>,
  now: number,
  thresholdMs: number = CHANNEL_SYNC_STUCK_AFTER_MS,
): Array<{ agent_id: string; attempts: number; stuck_for_seconds: number; reason: string }> {
  const out: Array<{ agent_id: string; attempts: number; stuck_for_seconds: number; reason: string }> = [];
  for (const [agentId, state] of failures) {
    const stuckForMs = now - state.since;
    if (stuckForMs < thresholdMs) continue;
    out.push({
      agent_id: agentId,
      attempts: state.attempts,
      stuck_for_seconds: Math.floor(stuckForMs / 1000),
      reason: state.lastReason,
    });
  }
  out.sort((a, b) => b.stuck_for_seconds - a.stuck_for_seconds);
  return out;
}

/**
 * ENG-9517: per-channel credential verdicts, shaped for the heartbeat body.
 *
 * The counterpart to `stuckChannelSyncs` above, in the direction that was
 * missing. That one reports agents whose sync has been broken for longer than
 * a threshold; this one reports the current on-disk verdict for every channel
 * the manager actually checked this tick, converged or not.
 *
 * Both directions are carried in ONE field on purpose. Sending only failures
 * would leave the consumer inferring success from an absence again, and an
 * absence here is genuinely ambiguous: a channel missing from this array was
 * not checked (agent not active, sync deferred by the backoff, adapter without
 * `hasChannelCredentials`) — which is not the same claim as "its credentials
 * are fine". The consumer has to be able to tell "verified present", "verified
 * absent" and "no verdict" apart, so all three must be distinguishable on the
 * wire.
 *
 * Sorted so a truncating consumer and a diffing test both see a stable order.
 */
export function verifiedChannelCredentials(
  verdicts: Map<string, Map<string, ChannelCredentialVerdict>>,
): Array<{ agent_id: string; channel_id: string; verified: boolean; reason?: string }> {
  const out: Array<{ agent_id: string; channel_id: string; verified: boolean; reason?: string }> = [];
  for (const [agentId, channels] of verdicts) {
    for (const [channelId, verdict] of channels) {
      out.push({
        agent_id: agentId,
        channel_id: channelId,
        verified: verdict.verified,
        // Omitted rather than sent as null when converged: a reason only means
        // anything alongside a failure, and a stale one travelling with
        // `verified: true` reads as a warning about a healthy channel.
        ...(verdict.verified || !verdict.reason ? {} : { reason: verdict.reason }),
      });
    }
  }
  out.sort((a, b) =>
    a.agent_id === b.agent_id
      ? a.channel_id.localeCompare(b.channel_id)
      : a.agent_id.localeCompare(b.agent_id),
  );
  return out;
}

// ENG-7771: on-disk mirror of the DELIVERY baselines — the hash the running
// (or next-spawned) session is believed to have, per agent and per concern
// (sender_policy; ENG-7779 added the slack/msteams behaviour subsets, whose
// in-memory maps shared the same wipe-on-manager-restart pathology). This
// deliberately LAGS the in-memory `agentState.known*Hashes` maps (which
// advance at converge time to debounce the per-tick decision) while a
// delivering restart is still pending: persisting at schedule time would let
// a manager restart during the idle-defer window load a sidecar that already
// claims delivery, silently dropping the restart forever — the exact
// ENG-7771 bug one layer down. Advance these maps ONLY via
// persistDeliveryBaselineEntry at the points where delivery is a fact: the
// restart's post-stop hook, or a tick that proves no live consumer needs
// delivering (see the tick block).
const persistedDeliveryBaseline = createDeliveryBaselineMaps();

function loadSenderPolicyBaseline(): void {
  loadSenderPolicyBaselineFromDisk(persistedDeliveryBaseline, channelHashCacheDir(), log);
  // Hydrate the decision baselines from the persisted ones so a manager
  // restart is no longer mistaken for a first poll — the whole point.
  for (const [agentId, hash] of persistedDeliveryBaseline.senderPolicy) {
    agentState.knownSenderPolicyHashes.set(agentId, hash);
  }
  for (const [agentId, hash] of persistedDeliveryBaseline.slackBehaviour) {
    agentState.knownSlackBehaviourHashes.set(agentId, hash);
  }
  for (const [agentId, hash] of persistedDeliveryBaseline.msteamsBehaviour) {
    agentState.knownMsTeamsBehaviourHashes.set(agentId, hash);
  }
  for (const [agentId, hash] of persistedDeliveryBaseline.telegramPolicy) {
    agentState.knownTelegramPolicyHashes.set(agentId, hash);
  }
  // ENG-9861. Hydrating this is the whole reason the sidecar exists (ENG-7771):
  // without it every manager restart looks like a first poll, and a rules change
  // that happened across a deploy is never delivered and never self-heals.
  for (const [agentId, hash] of persistedDeliveryBaseline.agentRules) {
    agentState.knownAgentRulesHashes.set(agentId, hash);
  }
}

// CodeRabbit PR #3432: a transiently-failed sidecar write must not be marked
// clean - the in-memory mirror has already advanced, so the identical-hash
// short-circuits below would skip every retry and the stale sidecar would
// survive until an unrelated baseline change (recreating a restart wave
// after the next manager restart). Track dirtiness and retry: every mutation
// path bypasses its short-circuit while dirty, and the poll loop flushes.
let deliveryBaselineDirty = false;

function saveDeliveryBaseline(): void {
  deliveryBaselineDirty = !saveSenderPolicyBaselineToDisk(
    persistedDeliveryBaseline,
    channelHashCacheDir(),
    log,
  );
}

/** Retry a transiently-failed sidecar write. Called once per poll cycle. */
function flushDeliveryBaselineIfDirty(): void {
  if (deliveryBaselineDirty) saveDeliveryBaseline();
}

// ENG-9346: the stuck-streak sidecar. `channelSyncFailures` (ENG-9252) and
// `realtimeRebindFailures` (ENG-9265) are in-memory Maps whose ABSENCE is a
// wire signal: the heartbeat always sends both arrays, and the API reads an
// agent's absence from them as convergence and closes its alert `recovered`.
// A manager restart therefore made the control plane assert a recovery nobody
// observed — measured on mustard (dig) and sterling (alyve) on 2026-08-22,
// both with a dead Telegram channel for 50+ hours, each alert closing itself
// once a day and reopening ten minutes later with the age restarted from zero.
//
// Reconciled from the live Maps once per poll rather than written at every
// mutation site. There are five of those across two subsystems, and a sixth
// added later would silently not persist; a reconcile cannot be forgotten by a
// future edit. `lastStuckStreakSignature` keeps that from writing a file on
// every poll when nothing changed.
let stuckStreakSidecarDirty = false;
let lastStuckStreakSignature = '';

/**
 * Snapshot both live Maps into the persisted shape.
 *
 * `lastReason` / `lastStatus` collapse to `detail` — the two states are
 * otherwise identical, and the store carries their intersection rather than
 * growing a branch per concern.
 */
function snapshotStuckStreaks(): StuckStreakMaps {
  const maps = createStuckStreakMaps();
  for (const [agentId, s] of channelSyncFailures) {
    maps.channelSync.set(agentId, {
      attempts: s.attempts,
      since: s.since,
      nextAttemptAt: s.nextAttemptAt,
      loggedAtAttempt: s.loggedAtAttempt,
      detail: s.lastReason,
    });
  }
  for (const [agentId, s] of realtimeRebindFailures) {
    maps.realtimeRebind.set(agentId, {
      attempts: s.attempts,
      since: s.since,
      nextAttemptAt: s.nextAttemptAt,
      loggedAtAttempt: s.loggedAtAttempt,
      detail: s.lastStatus,
    });
  }
  return maps;
}

/**
 * Persist the streak sidecar when it has changed. Called once per poll cycle.
 *
 * The signature deliberately excludes `nextAttemptAt` and `loggedAtAttempt`:
 * both move on almost every tick of an active backoff, and neither changes what
 * the control plane concludes. Including them would write the file every poll
 * for the entire life of a stuck streak — which is precisely the long-lived
 * state this is for.
 */
function flushStuckStreaksIfChanged(): void {
  const maps = snapshotStuckStreaks();
  const signature = JSON.stringify([
    [...maps.channelSync].map(([id, s]) => [id, s.since, s.attempts, s.detail]),
    [...maps.realtimeRebind].map(([id, s]) => [id, s.since, s.attempts, s.detail]),
  ]);
  if (signature === lastStuckStreakSignature && !stuckStreakSidecarDirty) return;
  const wrote = saveStuckStreaksToDisk(maps, channelHashCacheDir(), log);
  stuckStreakSidecarDirty = !wrote;
  if (wrote) lastStuckStreakSignature = signature;
}

/**
 * Rehydrate both streak Maps at manager start.
 *
 * Called BEFORE the first poll, so the first heartbeat after a restart reports
 * what was actually stuck instead of an empty array. That ordering is the whole
 * fix: one heartbeat with a phantom empty array is enough to close the alert.
 */
function loadStuckStreaks(): void {
  const maps = createStuckStreakMaps();
  loadStuckStreaksFromDisk(maps, channelHashCacheDir(), log);
  for (const [agentId, s] of maps.channelSync) {
    channelSyncFailures.set(agentId, {
      attempts: s.attempts,
      since: s.since,
      lastReason: s.detail,
      loggedAtAttempt: s.loggedAtAttempt,
      nextAttemptAt: s.nextAttemptAt,
    });
  }
  for (const [agentId, s] of maps.realtimeRebind) {
    realtimeRebindFailures.set(agentId, {
      attempts: s.attempts,
      since: s.since,
      lastStatus: s.detail,
      loggedAtAttempt: s.loggedAtAttempt,
      nextAttemptAt: s.nextAttemptAt,
    });
  }
  const restored = channelSyncFailures.size + realtimeRebindFailures.size;
  if (restored > 0) {
    log(
      `[stuck-streaks] restored ${channelSyncFailures.size} channel-sync and `
        + `${realtimeRebindFailures.size} rebind streak(s) across the manager restart — `
        + `their ages continue rather than restarting (ENG-9346)`,
    );
  }
}

/**
 * Drop persisted streaks for agents this host no longer runs.
 *
 * Only needed because the state now outlives the process: an agent unassigned
 * while the manager was DOWN never reaches `forgetChannelSyncState`, so its
 * entry would rehydrate and be reported on every heartbeat for an agent the API
 * will reject as unassigned.
 *
 * GUARDED ON A NON-EMPTY SET, and that guard is load-bearing rather than
 * defensive tidiness. Pruning against an empty `activeAgentIds` would delete
 * every streak on the host, the next heartbeat would send empty arrays, and the
 * control plane would close every alert as `recovered` — reconstructing this
 * exact bug from inside its own fix. The sibling rebind prune tolerates an
 * empty set because its worst case is a reset backoff; here the worst case is a
 * fabricated all-clear on a live outage.
 */
function pruneStuckStreaksToActiveAgents(activeAgentIds: string[]): void {
  if (activeAgentIds.length === 0) return;
  const dropped = pruneStuckStreaksToAgents(snapshotStuckStreaks(), new Set(activeAgentIds));
  if (dropped.length === 0) return;
  for (const agentId of dropped) {
    channelSyncFailures.delete(agentId);
    realtimeRebindFailures.delete(agentId);
  }
  log(
    `[stuck-streaks] dropped ${dropped.length} persisted streak(s) for agent(s) no longer on this host (ENG-9346)`,
  );
}

function persistDeliveryBaselineEntry(
  concern: BaselineConcern,
  agentId: string,
  hash: string | undefined,
): void {
  if (hash === undefined) return;
  if (persistedDeliveryBaseline[concern].get(agentId) === hash && !deliveryBaselineDirty) return;
  persistedDeliveryBaseline[concern].set(agentId, hash);
  saveDeliveryBaseline();
}

function removeDeliveryBaselineEntry(concern: BaselineConcern, agentId: string): void {
  if (!persistedDeliveryBaseline[concern].delete(agentId) && !deliveryBaselineDirty) return;
  saveDeliveryBaseline();
}

/** Drop every concern's persisted entry for an unenrolled agent. */
function removeDeliveryBaselineEntries(agentId: string): void {
  let mutated = false;
  for (const map of Object.values(persistedDeliveryBaseline)) {
    if (map.delete(agentId)) mutated = true;
  }
  if (mutated || deliveryBaselineDirty) saveDeliveryBaseline();
}

// ENG-5932: lazily-constructed singleton quarantine store, rooted at the
// manager's configDir so the marker survives manager restarts AND per-agent
// reprovisions (the persistence is what actually kills the flap — see
// channel-quarantine.ts). Lazy because `config` isn't populated at module load.
let _channelQuarantineStore: ChannelQuarantineStore | null = null;
function channelQuarantineStore(): ChannelQuarantineStore {
  if (!_channelQuarantineStore) {
    const dir = config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
    _channelQuarantineStore = new ChannelQuarantineStore(defaultQuarantinePath(dir));
  }
  return _channelQuarantineStore;
}

let _channelResumeHistoryStore: ChannelResumeHistoryStore | null = null;

/**
 * ENG-10510: resume history, in a SIDECAR file beside the quarantine markers.
 *
 * Not inside `channel-quarantine.json`, and not for tidiness. That file's
 * `isQuarantineFile` validates every entry and `load()` falls back to `{}` when
 * validation fails, so a new shape would make an older manager on a rolling
 * deploy read the file as empty, un-quarantine every channel it manages, and
 * re-arm the ENG-5932 flap fleet-wide.
 */
function channelResumeHistoryStore(): ChannelResumeHistoryStore {
  if (!_channelResumeHistoryStore) {
    const dir = config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
    _channelResumeHistoryStore = new ChannelResumeHistoryStore(defaultResumeHistoryPath(dir));
  }
  return _channelResumeHistoryStore;
}

/** ENG-10510. Declared safe value `off`: a flag read error must never re-add a channel. */
/**
 * ENG-10510 (obi-wan `7b85a224e858`): latch the auto-resume mode-downgrade line
 * to once per (auto-resume mode, quarantine mode) pair.
 *
 * It is a per-poll condition and a config mistake, so it would otherwise print
 * every ten seconds for as long as the two flags disagree - which is how a line
 * that matters becomes one an operator filters out. Keyed on the PAIR rather
 * than latched once, so flipping either flag to a new wrong combination says so
 * again instead of staying quiet because it already spoke.
 */
const autoResumeDowngradeLogged = new Set<string>();
function shouldLogAutoResumeModeDowngrade(
  autoResume: string,
  quarantine: string,
): boolean {
  const key = `${autoResume}->${quarantine}`;
  if (autoResumeDowngradeLogged.has(key)) return false;
  autoResumeDowngradeLogged.add(key);
  return true;
}

function channelQuarantineAutoResumeMode(): 'off' | 'shadow' | 'enforce' {
  return hostFlagStore().getString('channel-quarantine-auto-resume') as
    | 'off'
    | 'shadow'
    | 'enforce';
}

// ENG-8018: accessor handed to collectDiagnostics so each per-agent heartbeat
// row carries that agent's channel-quarantine set (serverKey + reason + when).
// Injected rather than read inside persistent-session because the authoritative
// store singleton lives here (persistent-session imports this module, so a
// back-import would be a cycle). Reads through the single cached store, so a
// just-quarantined / just-cleared channel is reflected on the next heartbeat.
function quarantineEntriesFor(
  codeName: string,
): Array<{ serverKey: string; reason: string; quarantinedAt: number }> {
  return channelQuarantineStore().getQuarantineEntries(codeName);
}

// ENG-8163 follow-up: accessor handed to collectDiagnostics so each per-agent
// heartbeat row carries the size of the DEPLOYED project/CLAUDE.md. Injected
// for the same reason as quarantineEntriesFor - the measurement needs
// `config.configDir`, which persistent-session has no global handle on.
//
// This is the drift-catcher for ENG-8163. The CI guard measures the GENERATED
// artifact, so it could not see that every active agent on agt-aws-1 was over
// threshold on the deployed file (scout 51,059) - and could not see it still
// climbing (pepper 48,772 -> 51,115 within an hour as skills were added).
//
// ENG-9459: "over" here means over a CONTEXT-COST threshold, not over a
// truncation boundary. Claude Code was measured not to truncate CLAUDE.md at
// ~40,000 chars on either delivery path; see the block comment in
// packages/core/src/provisioning/frameworks/claudecode/identity.ts. The appended content is DB-driven and per-agent, so
// the host is the only place this is knowable.
//
// Also emits a WARN on the over/under EDGES only (never every heartbeat - that
// is the ENG-6478 env-mask-warn mistake), so manager.log carries the crossing.
function claudeMdSizeFor(codeName: string): ClaudeMdSizeReport | null {
  if (!config?.configDir) return null;
  const report = measureClaudeMd(config.configDir, codeName);
  logClaudeMdCeilingTransition(codeName, report, log);
  return report;
}

/**
 * ENG-9477 — the forwarded-tool registration report the `augmented` MCP server
 * left in the agent's home dir.
 *
 * Injected like `claudeMdSizeFor` and for the same reason: the read needs
 * `config.configDir`, which persistent-session has no global handle on.
 *
 * A pure read. The MCP server does the measuring at its own startup; the
 * heartbeat only carries it. `null` on no config, no file, or a file that does
 * not parse as a report — all of which mean NO SIGNAL, never a passing zero.
 */
function forwardedToolsFor(codeName: string): ForwardedToolsReport | null {
  if (!config?.configDir) return null;
  return readForwardedToolsReport(join(config.configDir, codeName));
}

// ENG-6251: lazily-constructed singleton host flag store, rooted at the
// manager's configDir so the last-known-good `flags-cache.json` survives
// manager restarts. Lazy because `config` isn't populated at module load.
// Consumers read flags through this (env -> heartbeat -> compiled default); the
// heartbeat merge below keeps it fresh so DB flips take effect within one poll.
let _hostFlagStore: HostFlagStore | null = null;
function hostFlagStore(): HostFlagStore {
  if (!_hostFlagStore) {
    const dir = config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
    _hostFlagStore = new HostFlagStore({ cachePath: defaultFlagsCachePath(dir), log });
  }
  return _hostFlagStore;
}

/**
 * ENG-8174: should `## Integrations` be rendered into project/CLAUDE.md?
 *
 * `packages/core` has no flag evaluator, and BOTH CLAUDE.md writers live
 * there — `generateClaudeMd` (via generateArtifacts) and the
 * `writeIntegrations` side-effect path — so the manager has to resolve the flag
 * and thread the answer into both. They target the same sentinel range; letting
 * them disagree would have one re-add on every integration sync exactly what the
 * other had just stripped.
 *
 * CALL THIS ONCE PER TICK, at the top of `processAgent`, and pass the boolean
 * down. Do NOT re-read it at the individual write sites: they sit behind many
 * awaits and the heartbeat can refresh `hostFlagStore()` in between, so per-site
 * reads would let a mid-tick flip resolve differently for each writer —
 * reintroducing exactly the churn this gate exists to prevent. (CodeRabbit on
 * PR #3850 caught the first version doing this.)
 *
 * Host-wide, like `claude-md-skills-index`: HostFlagStore.getBoolean has no
 * per-agent form, so flipping this moves every agent on the host.
 *
 * Defaults OFF. The section is redundant with surfaces Claude Code presents
 * natively — an `integration-*` skill for integrations that ship a skill
 * bundle, and a dynamically-discovered `mcp__augmented__*` tool for those
 * that do not (image-gen / video-gen are the latter: they carry no
 * `integration_definition_skills` rows at all, and reach the agent purely as
 * broker tools fanned out from `metadata.tools` via /host/mcp/tools/list).
 */
function shouldRenderIntegrationsSection(): boolean {
  return hostFlagStore().getBoolean('claude-md-integrations-section');
}

/**
 * ENG-7363: classify a model-call failure from the manager's own backends
 * (conversation-eval / memory-extraction) and report it for fleet early
 * warning. Gated by the `model-api-error-reporting` flag (ships dark) and
 * fully best-effort (never throws), so a broken reporter can't disrupt the
 * scorer/extractor.
 */
function reportManagerModelCallError(
  source: ModelApiErrorSource,
  err: unknown,
  modelId: string | null,
): void {
  try {
    if (!hostFlagStore().getBoolean('model-api-error-reporting')) return;
    const classified = classifyModelApiError({ error: err });
    if (!classified) return;
    void reportModelApiError({
      source,
      errorClass: classified.errorClass,
      httpStatus: classified.httpStatus,
      modelId,
      detail: err instanceof Error ? err.message : String(err),
      log,
    });
  } catch {
    // never let telemetry break the caller
  }
}

/**
 * ENG-5932 rollout control, flag-managed since ENG-6252. Resolves through the
 * host flag store (`channel-quarantine-mode`, enum off|shadow|enforce):
 *   env var (AGT_CHANNEL_QUARANTINE_MODE, operator override)
 *     -> heartbeat value (set centrally via the admin surface, no restart)
 *     -> compiled default ('shadow' — observe-only, see the registry comment).
 * A central flip to 'enforce' now takes effect within one heartbeat cycle with
 * no `systemctl restart`; the env var still wins as the operator escape hatch.
 *   'off'     — disable the quarantine logic entirely (legacy reaper behaviour).
 *   'enforce' — quarantine dead optional channels (drop from .mcp.json).
 *   'shadow'  — compute + log "would quarantine X" without acting (the default).
 */
function channelQuarantineMode(): 'off' | 'shadow' | 'enforce' {
  return hostFlagStore().getString('channel-quarantine-mode') as 'off' | 'shadow' | 'enforce';
}

/**
 * ENG-8465 utilization gate. Resolves through the host flag store
 * (`utilization-occupancy-qualifier`, enum shadow|enforce): env var
 * (AGT_UTILIZATION_OCCUPANCY_QUALIFIER) -> heartbeat -> compiled default
 * ('shadow'). Read once per probe cycle, so a central flip lands within one
 * heartbeat with no restart.
 *   'shadow'  — report every measured bucket as before; only COUNT how many a
 *               transcript qualifier would drop (the forward soak). Default.
 *   'enforce' — drop measured buckets with no qualifying turn nearby.
 * Anything unexpected degrades safe to 'shadow' (never silently under-bill).
 */
function occupancyQualifierMode(): OccupancyQualifierMode {
  return hostFlagStore().getString('utilization-occupancy-qualifier') === 'enforce'
    ? 'enforce'
    : 'shadow';
}

// ENG-5932: dwell floor (ms) an optional channel must sit continuously missing
// — beyond the restart budget — before it's quarantine-eligible. Council-locked
// at 180s. Overridable for ops via AGT_CHANNEL_QUARANTINE_DWELL_MS.
function channelQuarantineDwellMs(): number {
  const raw = parseInt(process.env['AGT_CHANNEL_QUARANTINE_DWELL_MS'] ?? '', 10);
  return Number.isFinite(raw) && raw >= 0 ? raw : 180_000;
}

// ENG-5932: small set-difference helper (no side effects on either input).
function setWithout(source: Set<string>, remove: Set<string>): Set<string> {
  if (remove.size === 0) return new Set(source);
  const out = new Set<string>();
  for (const v of source) if (!remove.has(v)) out.add(v);
  return out;
}

function send(msg: ManagerEvent): void {
  // Write state updates to file for `agt manager status` to read
  if (msg.type === 'state-update') {
    try {
      // ENG-5865 — atomicWriteFileSync (write-tmp + fsync + rename) so concurrent
      // readers (synthetic probe, `agt status`) never see a torn JSON write.
      atomicWriteFileSync(getStateFile(), JSON.stringify(msg.state, null, 2));
    } catch { /* non-fatal */ }
  }
  // Log notable events
  if (msg.type === 'provisioned') {
    log(`Provisioned ${msg.codeName}`);
  } else if (msg.type === 'drift-detected') {
    log(`Drift detected: ${msg.codeName} (${msg.files?.join(', ')})`);
  } else if (msg.type === 'error') {
    log(`Error: ${msg.message}`);
  }
}

// ---------------------------------------------------------------------------
// Skills index — refresh the "## Available Skills" section in CLAUDE.md by
// scanning the agent's .claude/skills/ directory and parsing frontmatter.
// ---------------------------------------------------------------------------

const SKILLS_INDEX_START = '<!-- AGT:SKILLS_INDEX_START -->';
const SKILLS_INDEX_END = '<!-- AGT:SKILLS_INDEX_END -->';

function sanitizeSkillsIndexText(value: string): string {
  return value
    .replaceAll(SKILLS_INDEX_START, '')
    .replaceAll(SKILLS_INDEX_END, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\r?\n+/g, ' ')
    .trim();
}

function parseSkillFrontmatter(content: string): { name?: string; description?: string } {
  if (!content.startsWith('---')) return {};
  const end = content.indexOf('\n---', 3);
  if (end === -1) return {};
  const block = content.slice(3, end);
  const out: { name?: string; description?: string } = {};
  for (const line of block.split('\n')) {
    const m = line.match(/^(name|description)\s*:\s*(.*)$/);
    if (m && m[1] && m[2] !== undefined) out[m[1] as 'name' | 'description'] = m[2].trim().replace(/^["']|["']$/g, '');
  }
  return out;
}

/** Exported for tests (claude-md-skills-index flag behaviour). */
export async function refreshSkillsIndexInClaudeMd(configDir: string, codeName: string, log: (msg: string) => void): Promise<void> {
  const { readdirSync, readFileSync: rfs, existsSync: ex, writeFileSync } = await import('node:fs');
  const skillsDir = join(configDir, codeName, 'project', '.claude', 'skills');
  const claudeMdPath = join(configDir, codeName, 'project', 'CLAUDE.md');
  if (!ex(skillsDir) || !ex(claudeMdPath)) return;

  const entries: { id: string; name: string; description: string }[] = [];
  for (const dir of readdirSync(skillsDir).sort()) {
    const skillFile = join(skillsDir, dir, 'SKILL.md');
    if (!ex(skillFile)) continue;
    try {
      const { name, description } = parseSkillFrontmatter(rfs(skillFile, 'utf-8'));
      entries.push({
        id: dir,
        name: sanitizeSkillsIndexText(name ?? dir),
        description: sanitizeSkillsIndexText(description ?? '(no description)'),
      });
    } catch { /* skip unreadable */ }
  }

  const body = entries.length
    ? entries.map((e) => `- **${e.name}** — ${e.description}`).join('\n')
    : '_(no skills installed)_';

  // Claude Code discovers .claude/skills/*/SKILL.md and surfaces name +
  // description to the model itself, so on current models this bullet list is
  // duplicated context - and not cheap duplication: 12,045 chars across 29
  // skills on a prod agent which, on top of a ~37k generated body, put
  // project/CLAUDE.md at 51k against Claude Code's 40,000-char ceiling. Past
  // that ceiling the file is truncated and the agent silently loses the tail
  // of its OWN system prompt. Every active agent on agt-aws-1 was over it.
  //
  // Host-wide: HostFlagStore.getBoolean has no per-agent form (only the
  // notify-dispatch enum threads per agent), so flipping this moves every
  // agent that host runs.
  //
  // Suppression REWRITES the managed block rather than returning early. That
  // is load-bearing: nothing else rewrites project/CLAUDE.md, because
  // deployArtifactsToProject strips the index before comparing and re-grafts
  // whatever it found (claudecode/index.ts:764-770). A skipped refresh would
  // leave the existing 12k block on disk forever and the flag would look inert.
  const injectSkillList = hostFlagStore().getBoolean('claude-md-skills-index');

  // The "Updating Integrations" half is NOT duplicated by native discovery -
  // it is the only place the agent is told that integration-* SKILL.md files
  // are platform-managed and must be changed via `integration_improve`. It
  // stays inside the markers in both states so the block remains managed.
  const skillList = injectSkillList
    ? `## Available Skills

The following skills are installed in \`.claude/skills/\`. Claude Code auto-activates them when relevant — you don't need to read them manually, but you should know they exist.

${body}

`
    : '';

  const section = `${SKILLS_INDEX_START}
${skillList}## Updating Integrations (ENG-4341)

Integration skills under \`.claude/skills/integration-*/SKILL.md\` are **read-only** and managed by the platform. They are derived from the integration's database row plus any per-agent context overrides, and are re-rendered every time the manager polls or a context change is broadcast over Supabase Realtime.

**Never edit \`.claude/skills/integration-*/SKILL.md\` files directly.** If you do, your edit will be silently overwritten on the next manager refresh, AND it won't propagate to other agents using the same integration.

To change an integration's behavior (add a rule, update a default, tell the integration not to do something), call the **\`integration_improve\`** MCP tool with the user's request. The tool calls the platform API, which uses an LLM to translate the request into a structured update of the integration's typed context fields and/or freeform overrides text. You'll get a diff back to show the user; on confirmation, call the tool again with \`auto_apply: true\` to apply.

Examples of when to use \`integration_improve\`:
- *"Update the Coding integration so we always use trunk instead of main"*
- *"Add a rule to the Coding integration: never merge directly to main, always open a PR"*
- *"Tell the Knowledge Base integration not to return results below 70% relevance"*
- *"The Slack integration should post incidents to #oncall, not #general"*
${SKILLS_INDEX_END}`;

  const current = rfs(claudeMdPath, 'utf-8');
  let next: string;
  if (current.includes(SKILLS_INDEX_START) && current.includes(SKILLS_INDEX_END)) {
    next = current.replace(new RegExp(`${SKILLS_INDEX_START}[\\s\\S]*?${SKILLS_INDEX_END}`), section);
  } else {
    next = current.trimEnd() + '\n\n' + section + '\n';
  }

  if (next !== current) {
    writeFileSync(claudeMdPath, next, 'utf-8');
    log(
      injectSkillList
        ? `Refreshed skills index in CLAUDE.md for '${codeName}' (${entries.length} skills)`
        : `Refreshed CLAUDE.md managed block for '${codeName}' (skill list suppressed by claude-md-skills-index=false; ${entries.length} skills on disk)`,
    );
  }
}


/**
 * ENG-9585: minimum spacing between desired-state fetches, per agent.
 *
 * The poll loop keeps running on its 10s gap so the LOCAL work — the scheduler
 * due-check above all — stays responsive. These gates stop each pass from also
 * re-downloading state that changes on the order of hours.
 *
 * 60s is deliberately conservative: it is only ~1.4x today's measured ~44s
 * per-agent period, so the first deploy changes desired-state latency very
 * little while removing most of the network cost from the cycle. Raise it once
 * prod confirms the Realtime lanes are carrying the freshness (they push config,
 * doc-version, assignment and integration-context changes, each of which forces
 * a fetch through `forceRefreshFetch`).
 */
const DESIRED_STATE_FETCH_TTL_MS = 60_000;

/**
 * ENG-10093: how stale the host's Claude auth may be when `ensurePersistentSession`
 * acts on it.
 *
 * NOT a fetch gate, and deliberately not one. `/host/exchange` is host-grained —
 * the manager's exchange sends no `agent_id`, and `cachedExchange` in
 * `api-client.ts` is a single module-level object shared by every agent on the
 * host. A `createFetchGate` keyed by agent id would therefore permit
 * `agents × 60/hour` real exchanges over one host-grained resource: a gate that
 * is present, correct-looking, and buying a third of what it appears to. The
 * bound belongs on the cache, so it lives there (`exchangeApiKey`'s `maxAgeMs`).
 *
 * WHAT THE BOUND COSTS. Before this, the call site forced on every pass, so an
 * auth-mode flip or key rotation in the console was picked up within one cycle
 * (~13-21s measured). It is now picked up within `maxAgeMs` of that, so ≤60s +
 * one cycle. That is the same staleness budget every other desired-state lane on
 * this poll path already runs under (ENG-9585, ENG-10061), and the exposure it
 * opens is bounded to a SPAWN decision — a session already running holds its own
 * env either way, and outlives any of these windows.
 *
 * AND WHY NO PUSH CARRIES IT. Read this before assuming the Realtime lanes make
 * the bound free, because for the other gates they do and here they do not.
 * `startRealtimeTokenRotation` is `agent_integrations` token rotation, not host
 * auth; the operator-facing switch (`claude-pair-actions.ts`, the one site the
 * ENG-9482 freeze permits to write `hosts.claude_auth_mode`) stamps no
 * `restart_requested_at`, so it rings no doorbell. Nothing in the fleet pushes
 * this change today. `forceRefreshFetch` still forces the exchange — see there
 * for why that is worth its cost anyway — but the honest floor is the 60s, not
 * the push.
 *
 * The residual is a choice, not an oversight: 60/hour/host sits ~50x above the
 * ~1.2/hour the JWT TTL alone requires. Closing THAT gap needs change detection
 * (exchange only when `/host/refresh` reports the mode or key fingerprint moved)
 * rather than spacing, which is a separate change against a fail-closed path.
 */
const AUTH_EXCHANGE_MAX_AGE_MS = DESIRED_STATE_FETCH_TTL_MS;

/** `/host/refresh` — desired state. Realtime-backed; see the module header. */
const refreshFetchGate = createFetchGate(DESIRED_STATE_FETCH_TTL_MS);

/**
 * The last successful `/host/refresh` payload per agent, served while the gate
 * is closed.
 *
 * Typed `unknown` because the payload's type is a ~250-line inline literal
 * local to `processAgent`; naming it at module scope would be a large mechanical
 * diff for no behavioural gain. The single cast at the read site is annotated.
 */
const lastRefreshPayload = new Map<string, unknown>();

/** `/host/memories` — the DB memory corpus. */
const memoriesFetchGate = createFetchGate(DESIRED_STATE_FETCH_TTL_MS);

/**
 * `/host/secrets` — the agent's auth profiles.
 *
 * ENG-9585 (slice 2): the same TTL as the two gates above, but NOT the same
 * relative change, and the constant's own docstring should not be read as
 * covering it. That justification — "~1.4x today's measured ~44s per-agent
 * period" — was measured on `/host/refresh`. This endpoint was never spaced at
 * all, so it ran at the poll cadence: 257,617 requests/day across 58 agents on
 * 2026-09-04, a ~19.5s per-agent period. 60s is a **~3x** cadence cut here.
 *
 * What it costs: an auth-profile change (an LLM key rotated in the console)
 * reaches the agent in up to 60s where it previously took up to ~20s. Every
 * Realtime lane funnels through `triggerEarlyPoll`, which calls
 * `forceRefreshFetch()`, so any push reporting a change still beats the TTL.
 * The ceiling applies only to a change that nothing pushed.
 *
 * Safe to skip wholesale rather than serve a cached payload, because the block
 * is self-contained: `secretsData` is read nowhere outside its own `try`, and
 * the two `let`s it can update (`secretsHash`, `lastSecretsProvisionAt`) are
 * seeded from prior state and written straight back at the `agentStates.push`
 * below — so a skipped pass records "unchanged", which is what it observed.
 */
const secretsFetchGate = createFetchGate(DESIRED_STATE_FETCH_TTL_MS);

/**
 * `/host/resolve-default-schedules` — org/team default schedule templates.
 *
 * The slowest-moving desired state on the poll path: team-level templates,
 * edited by an operator, whose only effect here is to insert schedule rows the
 * agent is missing. A 60s ceiling on noticing a newly-added template is
 * immaterial against how often they change.
 *
 * Same self-contained shape as `secretsFetchGate`: `defaultsData` is confined
 * to its own `try`, and `tasks` is seeded from the refresh payload before it,
 * so a skipped pass leaves the agent on the schedule set it already had.
 */
const defaultSchedulesFetchGate = createFetchGate(DESIRED_STATE_FETCH_TTL_MS);

/**
 * `/host/agent-integrations` — the agent's installed integrations (ENG-10061).
 *
 * WHY THIS ONE IS CACHED AND NOT SKIPPED. The three gates above each carry a
 * "safe to skip wholesale" note, earned by the block being self-contained. This
 * block is the opposite: `integrations` is read by the OAuth near-expiry
 * refresh, the Anchor Browser session lifecycle, both rolling probes, the
 * orphaned-remote-MCP prune, the membership/flap trackers that record on EVERY
 * poll by design, and the `intHash`-gated `writeIntegrations`. Skipping the
 * fetch would skip all of that, so this lane serves the last payload instead —
 * the `/host/refresh` pattern, for the same reason.
 *
 * The cache holds the array POST-`withResolvedRemoteMcp`, and the objects in it
 * are the very ones the block mutates in place (a rotated `access_token`, an
 * Anchor `session_id`). That is deliberate: caching a pre-mutation copy would
 * make `computeIntegrationsHash` alternate between two views of an unchanged
 * row, rewriting `.env.integrations` and reaping MCP children on a poll where
 * nothing actually happened.
 */
const integrationsFetchGate = createFetchGate(DESIRED_STATE_FETCH_TTL_MS);

/**
 * The last successful `/host/agent-integrations` payload per agent.
 *
 * Typed `unknown[]` because the row shape is a ~60-line inline literal local to
 * `processAgent`; naming it at module scope would be a large mechanical diff for
 * no behavioural gain. The single cast at the read site is annotated — the same
 * trade `lastRefreshPayload` makes, for the same reason.
 */
const lastIntegrationsPayload = new Map<string, unknown[]>();

/**
 * `/host/agent-integrations/quarantined` — ENG-7916-quarantined rows, fetched so
 * ENG-8036 auto-resume can observe a recovery.
 *
 * A SEPARATE gate from the one above rather than a shared one, because this
 * fetch sits inside the `connectivity-probe` flag gate and the other does not.
 * Sharing would mean arming that flag mid-TTL finds the shared gate already
 * closed with no cached quarantined payload to serve — a state needing a
 * special case anyway, so the two lanes are simply kept independent.
 */
const quarantinedFetchGate = createFetchGate(DESIRED_STATE_FETCH_TTL_MS);

/**
 * The last successful `/host/agent-integrations/quarantined` payload per agent.
 *
 * The cached rows carry the host-side `inline_server_entry` reconstruction
 * (ENG-8056) that was built when they were fetched, so a cached pass reuses it
 * rather than rebuilding it — the reconstruction is a pure function of the
 * forwarded descriptors, so reusing it cannot drift from rebuilding it.
 */
const lastQuarantinedPayload = new Map<string, unknown[]>();

/**
 * Force this agent's next integrations fetch, BOTH lanes (ENG-10061).
 *
 * THE FAILURE THIS EXISTS TO PREVENT. Both rolling probes decide "due" purely
 * from a server-returned timestamp on the payload — `last_connectivity_check_at`
 * (`connectivity-probe-runner.ts`) and `last_session_tool_bind_at`
 * (`session-tool-bind-runner.ts`). Neither keeps host-side state, and neither
 * stamps the row after probing. So a cached payload keeps presenting a STALE
 * timestamp, the row stays due, and it is re-probed on every pass for the whole
 * TTL. Each re-probe POSTs a report, so gating this endpoint without this force
 * would have cut its requests while MULTIPLYING two others' — a saving read off
 * one access log and paid for out of two more.
 *
 * WHY A FORCE AND NOT A HOST-SIDE STAMP. Stamping the cached row would mean
 * replicating exactly WHEN the server stamps, and that rule has nuance: it
 * stamps for any report that reaches it including `unverified`, and
 * `reconnect_quiesced_until` participates in the due decision. Getting that
 * subtly wrong is the ENG-8448 cold-start class of bug, silently. Forcing a
 * refetch leaves the server's own stamp as the only stamp, and costs one extra
 * fetch per probe event against an ~hourly probe interval.
 *
 * BOTH lanes, from EITHER report. `applyConnectivityReports` can auto-resume a
 * row out of quarantine (ENG-8036) and `applySessionToolBindReports` can put one
 * into it (ENG-8084), so either report can move a row ACROSS the two sets —
 * which makes both stale, whichever one was reported.
 *
 * Called only after the report POST resolves. A failed report means the server
 * stamped nothing, so the row is still genuinely due and re-probing it on the
 * next pass is correct — the pre-ENG-10061 behaviour, unchanged.
 */
function forceIntegrationsFetch(agentId: string): void {
  integrationsFetchGate.forceNext(agentId);
  quarantinedFetchGate.forceNext(agentId);
}

/**
 * Per-cycle instrumentation, reset at the top of each pass.
 *
 * These exist to settle an open question with a measurement rather than an
 * estimate: the cycle was ASSUMED network-bound (~34s of the ~44s period), and
 * how much this change buys depends entirely on whether that is true. If it is,
 * skipping fetches collapses the cycle and the scheduler ticks far more often;
 * if the cycle turns out CPU-bound, the request saving is even larger but the
 * cadence does not improve. The log line below distinguishes those outcomes on
 * the first host that runs it, so nobody has to reason about it again.
 */
let pollCycleFetches = 0;
let pollCycleFetchSkips = 0;

/**
 * The same pair for the two integration lanes (ENG-10061), counted SEPARATELY.
 *
 * The pair above renders as `refresh_fetched=`/`refresh_skipped=`, and the log
 * line's own comment defines it as "refresh-payload decisions" — today only the
 * `/host/refresh` lane feeds it. Adding these lanes to it would have left a
 * per-endpoint metric quietly answering a different question under an unchanged
 * name: no error, no format change, and nothing for a reader of the line to
 * notice.
 *
 * Counters at all because this is the cheapest corroboration of the access-log
 * measurement this ticket owes. A host-side fetches-vs-skips ratio says whether
 * the gate is closing; an access-log total cannot tell a working gate from a
 * quieter fleet.
 *
 * The quarantined lane gets its own axis because it is flag-gated
 * (`connectivity-probe`). With the flag off it never executes, and folding its
 * zeroes into the integrations pair would read as a lane the TTL deferred
 * rather than one that never ran.
 */
let pollCycleIntegrationsFetches = 0;
let pollCycleIntegrationsSkips = 0;
let pollCycleQuarantinedFetches = 0;
let pollCycleQuarantinedSkips = 0;

/**
 * ENG-9584: how the `/host/memories` conditional request is actually landing.
 *
 * A hit rate is the one thing that cannot be read off the server side alone. In
 * the access log an unchanged response and a genuinely tiny corpus look
 * identical — both are ~90 bytes — so a token that never matches (a hash
 * flapping on a non-total sort, say) would present as a fleet of agents with
 * nothing to remember rather than as a saving that is not being taken.
 */
let pollCycleMemoriesUnchanged = 0;
let pollCycleMemoriesFull = 0;

/**
 * Force EVERY desired-state gate open for every agent on the next pass. Called
 * wherever a Realtime event says the world changed, so a push always beats the
 * TTL.
 *
 * ENG-9585 (slice 2): this is the reason a new gate needs no per-lane force
 * wiring of its own. `triggerEarlyPoll` is the single funnel for all the
 * Realtime lanes and calls this, so every gate listed here is opened by every
 * push. The corollary is the maintenance rule: **a gate added above and not
 * added here is a gate no Realtime push can open**, and its lane's changes
 * would then wait out the full TTL — silently, because the push still fires,
 * still logs, and still schedules the pass that serves stale state.
 */
function forceRefreshFetch(): void {
  refreshFetchGate.forceAll();
  memoriesFetchGate.forceAll();
  secretsFetchGate.forceAll();
  defaultSchedulesFetchGate.forceAll();
  // ENG-10061. This registration is what removes the hazard that kept the
  // integrations lanes ungated through slices 2 and 3: `startRealtimeTokenRotation`
  // reacts to a rotation via `triggerEarlyPoll`, which calls this. Without the
  // two lines below, that early poll would have re-served the cached payload
  // carrying the OLD credential — leaving in place the exact 401 the token
  // rotation lane exists to remove, for a full TTL, while logging that it had
  // reacted.
  integrationsFetchGate.forceAll();
  quarantinedFetchGate.forceAll();
  // ENG-10093. Not a fetch gate (see AUTH_EXCHANGE_MAX_AGE_MS for why the bound
  // lives on the exchange cache instead), and none of the three lanes that reach
  // here actually carries a host auth-mode change — so unlike the gates above,
  // this line is not what makes its bound safe.
  //
  // It is here anyway because the cost is bounded and the alternative is worse.
  // Forces COUNT rather than latch (`api-client.ts`), so N pushes arriving before
  // one exchange collapse into ONE extra network call, not N; the worst case is
  // therefore today's behaviour, reached only if pushes never stop. Against that,
  // omitting it would make this the single lane a reader has to know is exempt
  // from the maintenance rule stated above — and that rule is exactly the kind
  // that gets applied from memory.
  forceNextExchange();
}

// ---------------------------------------------------------------------------
// Poll cycle
// ---------------------------------------------------------------------------

// ENG-5041: in-process backoff for /host/exchange and poll-cycle failures.
// Pre-fix the manager would `process.exit(1)` on any "exchange failed" or
// "401" error and rely on the systemd/launchd watchdog to restart it,
// which during a sustained API outage (e.g. the S3 SDK prod-down on
// 2026-05-12 that returned 500 for ~30 min) caused a restart storm and
// blackholed the agent for the duration. Now we stay running, log each
// attempt, and double the delay up to a 60s cap. A successful cycle
// resets the counter so we go straight back to the normal interval.
// Math + classifier extracted to ./poll-backoff.ts so they're testable
// without dragging in this whole module's import graph.
let consecutivePollFailures = 0;

// ENG-10760: consecutive SUCCESSFUL /host/agents responses that carried zero
// agents. A host with nothing assigned was paying the full poll cost to re-learn
// an empty roster — 22,287 of one measured host's 25,359 requests/day. Only a
// success counts: a failed fetch says nothing about the roster, and the failure
// backoff above already owns that case.
let consecutiveEmptyAgentPolls = 0;

// Resolved once. The cap is an operator tunable, and re-reading process.env on
// every poll would let it change under a running manager without anything
// recording that it had.
const idlePollBackoffCapMs = resolveIdlePollBackoffCapMs();

/**
 * The inter-poll delay actually in force — the ONE place it is computed.
 *
 * ENG-10760 made this a function rather than leaving the `Math.max` inlined at
 * its three call sites, because two of those sites are not the scheduler: they
 * are the `[poll-backoff]` log line and, critically, the `poll_backoff_ms` the
 * liveness heartbeat reports to the control plane. `poll-loop-liveness.ts` calls
 * a loop stopped once `last_poll_at` is staler than
 * `poll_backoff_ms * STOPPED_AFTER_INTERVALS` (30), so a scheduler that backs
 * off while the reported value does not is not a cosmetic mismatch — at the
 * default 10s interval the allowance is exactly 300,000ms, so a 5-minute idle
 * gap would raise `poll_loop_stopped` CRITICAL on every correctly backed-off
 * host. Three copies of one expression is how that drift happens; one function
 * is how it cannot.
 */
function currentEffectivePollDelayMs(): number {
  const intervalMs = config?.intervalMs ?? 0;
  return Math.max(
    intervalMs,
    currentPollBackoffMs(consecutivePollFailures),
    currentIdlePollBackoffMs(consecutiveEmptyAgentPolls, intervalMs, idlePollBackoffCapMs),
  );
}

/**
 * ENG-7401 Phase 2b: order the per-agent processAgent pass so agents whose
 * session the restart-consumption loop just killed (a dashboard-issued restart,
 * e.g. an integration add/remove) are processed FIRST.
 *
 * The consumption loop kills the tmux session up front but the respawn only
 * happens when the processAgent loop reaches that agent - so on a multi-agent
 * host the restarted agent could sit down for up to a full cycle (~50-110s)
 * waiting its turn. Front-loading it makes the render+respawn follow the kill by
 * seconds. processAgent re-renders .mcp.json from a fresh /host/refresh AND
 * respawns in the same call, so this is stale-config-safe (unlike the Phase 2
 * post-stop path, we do NOT respawn before the new config is rendered).
 *
 * Reorders ONLY the iteration; the caller keeps the original `agents` array for
 * its other uses. All processAgent state is agent-keyed, so relative order is
 * functionally irrelevant (the one host-global "first team seeds the alert
 * webhook" cache is order-insensitive). Stable within each partition.
 */
export function reorderRestartedFirst<T extends { agent_id: string }>(
  agents: T[],
  restartedIds: ReadonlySet<string>,
): T[] {
  if (restartedIds.size === 0) return agents;
  const restarted: T[] = [];
  const rest: T[] = [];
  for (const a of agents) {
    (restartedIds.has(a.agent_id) ? restarted : rest).push(a);
  }
  return [...restarted, ...rest];
}

/**
 * ENG-7543 (Phase 4): resolve the `.mcp.json` server key for a managed toolkit
 * connection. A toolkit can now surface N connections (N distinct servers); the
 * API precomputes each connection's key as `mcp_server_key`. Prefer it; fall
 * back to the historical toolkit-level derivation when it's absent (an older API
 * build, which only ever returns default connections) so behaviour stays
 * byte-identical for the single-connection fleet.
 */
export function resolveManagedMcpServerId(toolkit: {
  toolkit_id: string;
  mcp_server_key?: string;
}): string {
  return toolkit.mcp_server_key ?? toolkit.toolkit_id.replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

/**
 * ENG-7401 Phase 2c: flush an out-of-band, diagnostics-only heartbeat for
 * just-restarted agents so their fresh `session.startedAt` reaches the API within
 * seconds. The restart/integration progress modals key "back online" on
 * `startedAt` advancing past `restart_requested_at`; without a prompt flush they
 * wait for the next regular heartbeat - up to a full poll cycle after the agent
 * is actually back. Fire-and-forget + best-effort: a failed flush self-heals on
 * the next poll's regular heartbeat. Shared by the per-agent early flush (right
 * after Phase 2b's front-loaded respawn) and the end-of-cycle backstop (ENG-7328).
 */
function flushRestartedAgentDiagnostics(hostId: string, codeNames: string[]): void {
  if (codeNames.length === 0) return;
  void (async () => {
    try {
      const { collectDiagnostics } = await import('./persistent-session.js');
      await api.post('/host/heartbeat', {
        host_id: hostId,
        agent_diagnostics: collectDiagnostics(codeNames, quarantineEntriesFor, claudeMdSizeFor, spawnOutcomeForDiagnostics, pidPressureFor, forwardedToolsFor, runtimeVersionFor),
      });
    } catch (err) {
      log(`[restart] post-respawn diagnostics flush failed: ${(err as Error).message}`);
    }
  })();
}

/**
 * ENG-9046: the cycle, wrapped so it cannot overlap itself.
 *
 * `triggerEarlyPoll()` is called from three Realtime callbacks and schedules
 * this on a `setTimeout(…, 0)` after clearing the pending timer. Its docstring
 * claimed that made it idempotent. It does not: `clearTimeout` cancels a
 * PENDING timer and cannot cancel a RUNNING cycle, so every Realtime event
 * arriving mid-cycle started another full pass over every agent, concurrently.
 *
 * Measured on agt-aws-1 (16 agents) at 2026-08-17T07:27:38-57Z:
 * `provision/active-tasks` 8x for one agent in 8.6s, `persistent-session-
 * decision` 7x for another with five inside 553ms, and three identical
 * `[bob] Board diff` lines inside a SINGLE MILLISECOND. Each one is a database
 * read, against an instance then sitting at 88% CPU.
 *
 * Coalescing rather than dropping (unlike `livenessInFlight` next door, where a
 * missed heartbeat self-corrects): a request arriving mid-cycle is honoured by
 * exactly one follow-up pass, so the low-latency reason `triggerEarlyPoll`
 * exists is preserved while the concurrency is not.
 */
const pollCycleGuard = createSingleFlight(() => pollCycleInner(), {
  onCoalesce: (total) => {
    // Visible in manager.log so the "after" measurement can show the
    // duplication being absorbed rather than merely absent.
    log(`[poll-cycle] coalesced an early-poll trigger into the in-flight cycle (total=${total})`);
  },
});

/**
 * ENG-9637: the ceiling, applied at the one place every poll passes through.
 *
 * Memoised on `intervalMs` rather than built once: the interval comes from
 * `config`, which is not populated when this module is evaluated, and a
 * limiter built against a fallback interval would keep enforcing that fallback
 * for the life of the process.
 */
let pollRateLimiter: PollRateLimiter | null = null;
let pollRateLimiterIntervalMs = 0;
function getPollRateLimiter(): PollRateLimiter {
  const intervalMs = config?.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  if (!pollRateLimiter || pollRateLimiterIntervalMs !== intervalMs) {
    pollRateLimiter = createPollRateLimiter({ intervalMs });
    pollRateLimiterIntervalMs = intervalMs;
  }
  return pollRateLimiter;
}

/**
 * Monotonic timestamp of the last throttle line we emitted, so a manager
 * carrying surplus poll chains reports the condition without writing one line
 * per rejected wake-up. A runaway at ~1 Hz would otherwise add ~86k lines a day
 * to `manager.log` — turning a cost bug into a disk bug and burying the very
 * signal the line exists to raise.
 */
let lastPollThrottleLogAt = 0;
const POLL_THROTTLE_LOG_INTERVAL_MS = 60_000;

// ENG-9637: returns the guard's outcome verbatim, so `pollCycleAndReschedule`
// can tell an owner of this pass from a caller that merely rode along on it.
// A coalesced caller that re-armed the poll timer forked a second, permanently
// untracked poll chain — see the docstring on `createSingleFlight`.
function pollCycle(): Promise<SingleFlightRunResult> {
  // ENG-9637: the rate ceiling. A throttled call performs no work and makes no
  // request, which is the thing being bounded.
  //
  // It reports `coalesced: false` — i.e. "you own this pass" — and that is
  // deliberate, not an oversight of the ownership contract. `coalesced: true`
  // means "another caller will re-arm on your behalf", which is exactly what a
  // throttled caller has no way to know: it never reached the single-flight
  // guard and there may be no other caller at all. Declining to re-arm here
  // would end the chain, and if it were the LAST chain the manager would be
  // alive, heartbeating, and never poll again — the ENG-9068 failure, arrived
  // at from a new direction. A surplus chain that keeps waking up and keeps
  // being turned away costs a timer; a stopped loop costs the agent.
  const decision = getPollRateLimiter().tryAcquire();
  if (!decision.allowed) {
    const at = performance.now();
    if (decision.consecutiveThrottled === 1 || at - lastPollThrottleLogAt >= POLL_THROTTLE_LOG_INTERVAL_MS) {
      lastPollThrottleLogAt = at;
      log(
        `[poll-rate] throttled a poll pass (consecutive=${decision.consecutiveThrottled}, ` +
          `total=${decision.totalThrottled}, ceiling=1 per ${pollRateLimiterIntervalMs}ms) — ` +
          'more callers are asking to poll than the configured cadence allows',
      );
    }
    return Promise.resolve({ coalesced: false });
  }
  return pollCycleGuard.run();
}

async function pollCycleInner(): Promise<void> {
  if (!config) return;

  // ENG-4488: short-circuit every entrypoint into pollCycle — not just the
  // normal scheduleNext timer. triggerEarlyPoll() (fired by Realtime
  // events, webapp-direct-chat, etc.) queues
  // pollCycle() with zero delay, which could otherwise sneak one more
  // cycle in on the old Cellar path after restartAfterUpgrade flipped.
  if (restartAfterUpgrade) return;

  // CodeRabbit PR #3432: retry a transiently-failed delivery-baseline sidecar
  // write so a stale sidecar can't survive until the next unrelated baseline
  // change (which would recreate a restart wave after a manager restart).
  flushDeliveryBaselineIfDirty();

  // ENG-9346: reconcile the stuck-streak sidecar from the live Maps. Runs here,
  // beside its sibling, because both exist for the same reason — state the
  // control plane draws a conclusion from must survive a manager restart.
  flushStuckStreaksIfChanged();

  // ENG-4568: process /restart flag files written by channel MCPs. Run
  // before the rest of the cycle so a kill takes effect immediately and
  // the ensure-session pass below respawns the killed agent in the same
  // poll. Errors are logged inside the handler — never let a flag take
  // down the whole cycle.
  try {
    await processRestartFlags({
      log,
      resolveFramework: (codeName) => agentFrameworkCache.get(codeName) ?? null,
      stopSession: (codeName, flag) => {
        // CodeRabbit on PR #773 (round 2): cancel any pending hot-reload
        // restart timer so it can't fire after this teardown and kill
        // the replacement session.
        // ENG-5441 / ENG-10066: restart-flags are written when a HUMAN types
        // `/restart` in a channel. That is the only thing that writes one: the
        // writers are slack-channel.ts (slash command + message interceptor,
        // both gated on SLACK_ALLOWED_USERS) and telegram-channel.ts, plus the
        // agent's own self-restart, which carries source 'agent' and is split
        // off to 'agent-requested' on the next line.
        //
        // This comment used to say the flags were written by channel MCPs "when
        // they hit an unrecoverable error they can't fix in-place", and that a
        // channel writing them repeatedly should count against the breaker. No
        // such machine writer exists in this repo, and the ENG-5963 it cited is
        // about maintenance windows. The belief had teeth: 'channel-restart-flag'
        // was left on the tight crash bar (max 2 / 10 min), so the THIRD
        // `/restart` inside ten minutes auto-paused the agent the operator was
        // trying to revive, and a crash-class trip never auto-recovers
        // (auto-resume.ts) — so it then sat paused until a human noticed.
        // razorclaw-sales hit this seven times on 2026-09-05 and stayed down
        // over 14h on the seventh. It is now classified 'operator-command',
        // which is recorded and reported but never trips.
        //
        // If you ever add a machine writer, give it its OWN reason — do not
        // reuse 'channel-restart-flag'. A channel that restarts itself in a loop
        // IS what the breaker should bound; a person recovering an agent is not.
        //
        // Still RECOVERY (a person restarts because something is wrong NOW), so
        // it keeps the non-gateable reason — it must NOT be held behind the
        // maintenance window like the config/drift/reaper restarts, or a broken
        // Slack/Telegram/direct-chat would stay down until 01:00.
        //
        // ENG-6229: the in-session agent's self-restart (source 'agent', via
        // the request_restart MCP tool after a human approved over its ask_user
        // HITL) records as 'agent-requested'. Same RECOVERY semantics — restarts
        // immediately, not maintenance-gated, because the agent only asks when
        // it's actively stuck mid-conversation — and still counts toward the
        // breaker so a structurally-confused agent that keeps asking trips.
        const breakerReason: RestartReason =
          flag.source === 'agent' ? 'agent-requested' : 'channel-restart-flag';
        stopPersistentSessionAndForgetMcpBaseline(codeName, breakerReason);
        agentState.persistentSessionAgents.delete(codeName);
        claudeAuthTupleBySession.delete(codeName);
        clearSessionPolicyBaseline(codeName);
      },
      getTelegramTokens: (codeName) => {
        const t = agentChannelTokens.get(codeName);
        if (!t?.telegram) return null;
        return { token: t.telegram, allowedChats: t.telegramAllowedChats };
      },
      sendTelegram: (botToken, method, body) => telegramApiCall(botToken, method, body),
      getSlackToken: (codeName) => agentChannelTokens.get(codeName)?.slack ?? null,
      sendSlack: async (botToken, body) => {
        // Match the timeout pattern other chat.postMessage call sites in
        // this file already use (lines ~5543, ~5762) — without this, a
        // hung Slack API would block pollCycle and stall every agent's
        // ensure-session pass behind the restart ack (CodeRabbit feedback).
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 5_000);
        try {
          const res = await fetch('https://slack.com/api/chat.postMessage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json; charset=utf-8',
              Authorization: `Bearer ${botToken}`,
            },
            body: JSON.stringify(body),
            signal: controller.signal,
          });
          const data = (await res.json()) as { ok: boolean; error?: string };
          return data;
        } catch (err) {
          const isAbort = (err as Error).name === 'AbortError';
          return { ok: false, error: isAbort ? 'timeout' : (err as Error).message };
        } finally {
          clearTimeout(timer);
        }
      },
      // ENG-6399: report the restart to audit_log via POST /host/restart-event.
      // Maps the flag's transport `source` onto the canonical audit source: the
      // in-session self-restart (request_restart, ENG-6229) is 'agent-self-request';
      // a channel /restart (slack/telegram) is 'channel-command'. Best-effort —
      // restart-handler calls this fire-and-forget so a reporting failure never
      // blocks the restart.
      reportRestartEvent: async (flag) => {
        const agentId = agentState.codeNameToAgentId.get(flag.codeName);
        if (!agentId) {
          log(`[restart-handler] no agent_id for '${flag.codeName}' — skipping restart-event report`);
          return;
        }
        // Map the flag's transport source onto the canonical audit source. The
        // in-session self-restart (request_restart, ENG-6229) is the only
        // 'agent' source; a channel /restart writes 'telegram'/'slack'; a flag
        // with a missing/garbled source falls back to 'unknown'
        // (restart-flags.ts) — keep that distinct rather than mislabelling it a
        // channel command (CodeRabbit on #2134).
        const source =
          flag.source === 'agent'
            ? 'agent-self-request'
            : flag.source === 'unknown'
              ? 'unknown'
              : 'channel-command';
        // Truly fire-and-forget: don't await (a reporting failure must never
        // surface as a processRestartFlags throw) and swallow rejections
        // locally. The async wrapper still resolves a Promise so the caller's
        // optional `.catch` stays valid.
        void api
          .post('/host/restart-event', {
            agent_id: agentId,
            source,
            ...(flag.reason ? { reason: flag.reason } : {}),
          })
          .catch((err) => log(`[restart-event] report failed for '${flag.codeName}': ${(err as Error).message}`));
      },
    });
  } catch (err) {
    log(`[restart-handler] processRestartFlags threw: ${(err as Error).message}`);
  }

  // ENG-8079: a pending "Update CLI now" takes precedence over the routine
  // check and runs from HERE — the top of the cycle, where a fire-and-forget
  // fetch overlaps the awaited heartbeat POST instead of the synchronous
  // per-agent sweep that used to starve it. Returns true when it kicked the
  // updater, so we don't also fire the routine check into the same in-flight
  // lock (which would return 'failed' and burn the retry).
  // Guarded like processRestartFlags() above: runPendingForcedUpdate does
  // synchronous work (the per-agent idle stat sweep), and pollCycle has no
  // process-level rejection handler, so an unexpected throw here would reject
  // the whole cycle. On a throw we fall back to `false` so the ROUTINE check
  // still runs — a broken forced path must not also freeze normal self-updates.
  let forcedUpdateKicked = false;
  try {
    forcedUpdateKicked = runPendingForcedUpdate();
  } catch (err) {
    log(`[self-update] runPendingForcedUpdate threw: ${(err as Error).message}`);
  }

  // ENG-4485: Kick off the CLI self-update check on every poll. The function
  // throttles the actual `brew outdated` call via an on-disk marker
  // (`.last-update-check`) + `UPDATE_CHECK_INTERVAL_MS`, so calling it each
  // poll is cheap — most invocations return immediately without shelling out.
  // Fire-and-forget: we don't want poll latency coupled to brew.
  if (!forcedUpdateKicked) {
    checkAndUpdateCli().catch((err) => log(`[self-update] Check failed: ${(err as Error).message}`));
  }

  // ENG-5357: same fire-and-forget shape for the Claude Code framework
  // binary. Throttled to once per 24h via an on-disk marker so a restart
  // storm (e.g. transient API 5xx during a deploy) cannot fire N
  // concurrent `brew upgrade --cask claude-code` jobs the way it could
  // pre-fix — that pathology pegged alyve-host-1 until reboot.
  maybeUpgradeClaudeCode().catch((err) => log(`[claude-code-upgrade] Check failed: ${(err as Error).message}`));

  // ENG-8623: same fire-and-forget shape. Throttled to one round-trip per 15
  // minutes so `agent_runtime_authenticated` reports what Anthropic actually
  // accepted rather than what a credentials file happens to contain. Never
  // awaited — a slow probe must not delay a poll cycle.
  maybeProbeClaudeRuntimeAuth().catch((err) => log(`[runtime-auth-probe] Check failed: ${(err as Error).message}`));

  // ENG-9394: host-level Claude usage. Same fire-and-forget shape, throttled to
  // one ~2.9s subprocess per 15 minutes. HOST level, not per agent - there is one
  // Claude login per host, and measuring a per-login quantity per agent is what
  // turned one fact into thirteen false criticals on 2026-08-24.
  maybePollHostUsageFromCycle().catch((err) => log(`[host-usage] Check failed: ${(err as Error).message}`));

  try {
    // Clear per-cycle caches
    registeredAgentsCache.clear();

    // 1. Discover assigned agents
    const hostId = await getHostId();
    if (!hostId) {
      send({ type: 'error', message: 'Could not resolve host ID from API key' });
      return;
    }

    // 1b. Detect framework version (cached, refreshed every 5 min)
    //
    // WHAT THIS MEASURES, precisely: the version on the MANAGER'S OWN PATH. The
    // adapter's getVersion() shells out inside this process's environment and
    // never enters an agent container, so the number reported as
    // hosts.framework_version describes the manager and nothing else. An agent
    // running a much older image is invisible to it and does not contradict it.
    //
    // Note the asymmetry on the next two lines, which reads as if it were
    // per-agent and is not: the first agent decides which ADAPTER is used, but
    // the adapter then measures the manager's environment either way. On a host
    // whose agents all share one framework that distinction never shows; it is
    // still the wrong mental model to carry into reading the field.
    const now = Date.now();
    if (now - lastVersionCheckAt > VERSION_CHECK_INTERVAL_MS) {
      try {
        // Use the first known agent's framework, or the default framework
        const firstAgent = state.agents[0];
        const versionAdapter = firstAgent ? resolveAgentFramework(firstAgent.codeName) : getFramework(DEFAULT_FRAMEWORK);
        if (versionAdapter.getVersion) {
          cachedFrameworkVersion = await versionAdapter.getVersion();
        }
      } catch {
        // Non-fatal — version detection is supplementary
      }
      lastVersionCheckAt = now;
    }

    // 1c. Heartbeat — update last_seen_at, framework_version, host security, diagnostics, and hostname
    try {
      const { detectHostSecurity } = await import('./host-security.js');
      const { collectDiagnostics } = await import('./persistent-session.js');

      // Collect diagnostics for all known persistent session agents
      const diagCodeNames = [...agentState.persistentSessionAgents];
      const agentDiagnostics = diagCodeNames.length > 0 ? collectDiagnostics(diagCodeNames, quarantineEntriesFor, claudeMdSizeFor, spawnOutcomeForDiagnostics, pidPressureFor, forwardedToolsFor, runtimeVersionFor) : undefined;

      // Detect Tailscale hostname for remote SSH attach
      let tailscaleHostname: string | undefined;
      try {
        const { execSync: es } = await import('node:child_process');
        const tsJson = es('tailscale status --self --json 2>/dev/null', {
          encoding: 'utf-8', timeout: 3000,
        }).trim();
        const ts = JSON.parse(tsJson);
        tailscaleHostname = ts.Self?.DNSName?.replace(/\.$/, '') || undefined;
      } catch {
        // Tailscale not installed or not connected — fall back to hostname
        try {
          const { execSync: es } = await import('node:child_process');
          tailscaleHostname = es('hostname', { encoding: 'utf-8', timeout: 1000 }).trim();
        } catch { /* non-fatal */ }
      }

      // Get OS username for SSH command
      let osUsername: string | undefined;
      try {
        const { userInfo } = await import('node:os');
        osUsername = userInfo().username;
      } catch { /* non-fatal */ }

      // Claude Code auth freshness — console uses this to show a re-pair prompt
      // before the OAuth token expires silently. Returns null if no auth is
      // detectable (e.g. a fresh host with no Claude Code login yet).
      let claudeAuth: Awaited<ReturnType<typeof detectClaudeAuth>> = null;
      try {
        claudeAuth = await detectClaudeAuth();
      } catch (err) {
        // Per coding guidelines: default to hash-only/redacted logging in prod.
        // Auth detection errors can surface credential file paths — hash
        // instead of logging raw.
        const errText = err instanceof Error ? err.message : String(err);
        const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
        log(`Claude auth detection failed (error_id=${errId})`);
      }

      // ENG-6068: re-assert the channels managed-setting each heartbeat on
      // claude-code hosts. Idempotent (no write when already correct), so
      // this self-heals drift (operator edits, CC reinstalls) without a
      // manager restart and keeps the reported status fresh.
      // CodeRabbit: gate on *current* agents, not the ever-growing
      // frameworkBinaryChecked set, and clear the cached status when no
      // claude-code agent remains — otherwise a host whose last claude-code
      // agent was removed keeps reporting (and alerting on) a file that no
      // longer gates anything.
      const hostHasClaudeCode = state.agents.some(
        (a) => agentFrameworkCache.get(a.codeName) === 'claude-code',
      );
      if (hostHasClaudeCode) {
        lastManagedSettingsStatus = ensureClaudeManagedSettings();
      } else {
        lastManagedSettingsStatus = null;
      }

      const hbResp = (await api.post('/host/heartbeat', {
        host_id: hostId,
        framework_version: cachedFrameworkVersion ?? undefined,
        agt_version: agtCliVersion,
        host_security: detectHostSecurity() ?? undefined,
        // ENG-8623: the probe's verdict, or the field is OMITTED. The column is
        // named for a runtime observation and the API now stamps a freshness
        // timestamp beside it, so a file-derived value here would manufacture an
        // observation nobody made. Spread as ONE unit: the boolean and its
        // provenance marker must never diverge, so they are produced together
        // rather than as two fields a future edit could separate. See
        // `heartbeatRuntimeAuthFields`.
        ...heartbeatRuntimeAuthFields(runtimeAuthProbeVerdict),
        agent_diagnostics: agentDiagnostics,
        hostname: tailscaleHostname,
        os_username: osUsername,
        claude_auth: claudeAuth ?? undefined,
        // ENG-5614: inbound-loop liveness. The actual phoenix socket state
        // behind the direct-chat Realtime subscription. When this is false
        // but the manager keeps heartbeating, inbound is being served by the
        // polling fallback (healRealtimeSocketDivergence forces it). Surfaced
        // so a sustained socket-down window is observable rather than hidden
        // behind a fresh heartbeat.
        realtime_socket_connected: isRealtimeSocketConnected(),
        // ENG-6068: outcome of the channels managed-settings assertion.
        // 'invalid_json' / 'write_failed' mean CC may be silently dropping
        // ALL channel inbound — the API opens a host-scoped alert on it.
        // Omitted (undefined) on hosts that haven't run the assertion
        // (non-claude-code frameworks).
        channels_setting_status: lastManagedSettingsStatus ?? undefined,
        // ENG-9252: agents whose channel credential sync has failed to converge
        // for longer than CHANNEL_SYNC_STUCK_AFTER_MS. The API opens/closes a
        // per-agent alert on the edge, exactly as it does for
        // `channels_setting_status` above.
        //
        // This field is the whole point of the ticket. The retry loop it
        // reports on ran for over three hours on a customer host and raised
        // NOTHING: non-convergence moves no heartbeat, no session-health signal
        // and no restart counter, so every projection an operator would consult
        // read healthy while the manager span. A bounded retry that is still
        // silent would have fixed the CPU and left the invisibility.
        //
        // ALWAYS SENT, including as an empty array — that is what lets the API
        // close a resolved alert on the edge. Omitting it when empty would make
        // "recovered" and "this CLI is too old to report" the same wire value.
        channel_sync_stuck: stuckChannelSyncs(channelSyncFailures, Date.now()),
        // ENG-9517: the POSITIVE direction of the field above — the current
        // per-channel on-disk credential verdict. Without it the control plane
        // could only ever learn that a channel was broken, and only after ten
        // minutes; it had no way to learn that one was working, so "installed"
        // and "never reached the host" were the same row.
        //
        // SAME absent-vs-empty contract as its neighbours, and it matters more
        // here because this field drives a UI completion state: an older CLI
        // omits it and the consumer must change nothing, while `[]` is an
        // affirmative "this manager checked and has no channels to report".
        // Collapsing the two would let a stale CLI read as an all-clear.
        //
        // Note this is NOT the complement of `channel_sync_stuck`. A channel
        // can be absent from both — not yet attempted, deferred by the backoff,
        // or on an adapter that cannot probe — and that third state has to stay
        // distinguishable from success.
        channel_credential_status: verifiedChannelCredentials(channelCredentialVerdicts),
        // ENG-9265: agents whose realtime token-rotation subscription has been
        // failing to bind for longer than the stuck threshold. Same contract as
        // the field above — ALWAYS sent, including as an empty array, because
        // that is what lets the API close a resolved alert on the edge. An
        // absent field means an older CLI that cannot report; an empty one is
        // the affirmative all-clear.
        //
        // This is the ask that matters most on the ticket. A manager churning
        // subscriptions every tick for half an hour produced NO signal of its
        // own; the only alert was the host going silent, by which point four
        // customer agents had been dark for three minutes. Bounding the retry
        // without this would fix the CPU and leave the blindness.
        realtime_rebind_stuck: stuckRealtimeRebinds(realtimeRebindFailures, Date.now()),
        // ENG-10351: ONE host-level verdict for rotations held because the
        // host's ~/.claude.json is not usable. ALWAYS sent: `null` is the
        // affirmative all-clear that lets the API close the alert on the edge,
        // and an absent field means an older CLI. Re-checks the file while a
        // hold exists, so pairing the host closes the alert on the next beat.
        claude_device_state_rotation_blocked: deviceStateRotationGate.heartbeatField(),
        // ENG-6251: capability advertisement — the flags schema version this
        // CLI compiled with, so the admin UI can show flip reach across a
        // mixed-CLI fleet (an older CLI sends an older fingerprint).
        flags_schema_version: FLAGS_SCHEMA_VERSION,
        // ENG-6412: the host's allowlisted, non-secret env gates. The API
        // combines these with the org's server-resolved flags + versions into a
        // host_config_snapshots row (on change) so bugs correlate with config.
        // Allowlist-only by construction — no raw env / secrets ever sent.
        env_gates: collectEnvGates(process.env),
        // ENG-6692: ack the last consumed "Update CLI now" timestamp so the API
        // clears hosts.update_requested_at. Echoes our persisted dedup marker;
        // null until we've ever consumed one.
        update_request_processed_at: state.lastUpdateRequestProcessedAt ?? null,
      })) as
        | {
            desired_claude_code_version?: string;
            // ENG-7853: per-host agt-cli version pin (null/absent = no pin).
            desired_agt_cli_version?: string | null;
            maintenance_window?: { start: string; end: string; timezone: string };
            feature_flags?: Record<string, FlagValue>;
            flags_schema_version?: string;
            // ENG-7682 follow-up: per-agent notify-dispatch values (keyed by
            // agent_id) for agents whose resolved value differs from the
            // host-wide map. Absent when no agent has a per-agent override.
            notify_dispatch_by_agent?: Record<string, FlagValue>;
            // ENG-7891 / ADR-0049 slice 3: per-agent BOOLEAN overrides keyed
            // `agent_id -> flag_key -> bool` (read by getBooleanForAgent), so a
            // one-way-door gate like id-keyed-layout-migration is armed one agent
            // at a time. Absent until the API sends it (the flag ships dark).
            feature_flags_by_agent?: Record<string, Record<string, FlagValue>>;
            // ENG-6412: authoritative content hash of the host's current config,
            // computed API-side. Cached for the X-Config-Hash request header.
            config_hash?: string;
            // ENG-6692: one-shot "Update CLI now" signal (null = none pending).
            update_requested_at?: string | null;
          }
        | undefined;

      // ENG-5963: cache the resolved maintenance window for the update gates.
      // Keep the last-known value if a heartbeat omits it (older API) so we
      // don't flap to fail-open mid-session.
      if (hbResp?.maintenance_window) {
        cachedMaintenanceWindow = hbResp.maintenance_window;
      }

      // ENG-7853: cache the DB-driven agt-cli version pin. Mark the pin state
      // resolved on any heartbeat so the self-update gate below can't stall.
      // Only overwrite the cached pin when the field is actually present: an
      // omitted field (rolling deploy to an API without it) keeps the last-known
      // pin rather than silently clearing it (mirrors the maintenance-window
      // last-known handling); an explicit null clears it. The env var
      // AGT_CLI_PIN_VERSION still wins when set (resolveAgtCliPinRaw). Consumed
      // by the fire-and-forget self-update at the top of the next poll cycle.
      if (hbResp) {
        desiredAgtCliVersionHydrated = true;
        if ('desired_agt_cli_version' in hbResp) {
          cachedDesiredAgtCliVersion = hbResp.desired_agt_cli_version ?? null;
        }
      }

      // ENG-6692: consume a one-shot "Update CLI now" request. The admin button
      // stamps hosts.update_requested_at; the heartbeat re-delivers it every
      // cycle until we ack-clear it. We run ONE window-bypassing self-update per
      // distinct timestamp — dedup on state.lastUpdateRequestProcessedAt, which
      // is persisted so the fire survives the manager restart the update itself
      // triggers (exactly the AgentState.lastRestartProcessedAt pattern, but
      // host-level). We STILL defer until the host is idle: bypassing the window
      // must not mean interrupting a live turn.
      // ENG-8079: only CACHE the request here. The actual forced self-update runs
      // from runPendingForcedUpdate() at the top of the next poll cycle — the same
      // site as the non-forced check — because kicking it inline here starved its
      // registry fetch on a blocked event loop and failed 100% of the time. See
      // the cachedUpdateRequestedAt declaration for the full diagnosis.
      cachedUpdateRequestedAt = hbResp?.update_requested_at ?? null;

      // ENG-6251: merge the pre-evaluated flag map from this heartbeat into the
      // host flag store — logs per-flag transitions + env-masking warns and
      // refreshes the on-disk last-known-good cache. A DB flip lands here within
      // one poll cycle, no restart. An older API that omits the field is a
      // no-op, so we keep the last-known map rather than flapping to defaults.
      if (hbResp?.feature_flags) {
        hostFlagStore().applyHeartbeat(hbResp.feature_flags, hbResp.flags_schema_version);
      }
      // ENG-7682 follow-up: merge the per-agent notify-dispatch map so each
      // agent's spawn env can carry ITS resolved value rather than one host-wide
      // value. Always applied (even when absent) so a cleared override falls
      // back to the host-wide value within one poll rather than pinning stale.
      hostFlagStore().applyNotifyDispatchByAgent(hbResp?.notify_dispatch_by_agent);
      // ENG-7891 / ADR-0049 slice 3: merge the per-agent boolean override map so
      // the dark id-keyed migration can be armed per agent. Always applied (even
      // when absent → empty) so a cleared override reverts to host-wide within
      // one poll rather than pinning a stale opt-in for a one-way-door migration.
      hostFlagStore().applyBooleanFlagsByAgent(hbResp?.feature_flags_by_agent);

      // ENG-6412: cache the API-computed config_hash so subsequent host-runtime
      // requests carry X-Config-Hash (audit_log config correlation). Clear it
      // when a heartbeat omits it (older API, or a transient eval miss) rather
      // than preserving a possibly-stale hash — a NULL config_hash on an audit
      // row is an honest "unknown", whereas a stale one would mis-correlate the
      // row to the wrong snapshot. Self-heals on the next heartbeat that returns
      // one. setConfigHash treats null/empty as "clear".
      setConfigHash(hbResp?.config_hash ?? null);

      // ENG-5672: idle-gated Claude Code auto-update. Host-level — runs once
      // per poll alongside the heartbeat, using the server-pinned desired
      // version from the heartbeat response. The updater is internally
      // throttled (~6h), idle-gated (defers while a `claude --name agt-*`
      // agent is live), and log-and-continue, so it never disrupts the poll.
      //
      // ENG-5963: also gate the binary upgrade to the maintenance window — it's
      // restart-adjacent (the next session picks up the new binary). Outside
      // the window we skip the updater entirely; it runs on the next in-window
      // poll. Urgent CLI hotfixes are handled in the agt-cli self-update path,
      // not here (Claude Code has no `urgent` dist-tag of ours).
      try {
        // ENG-10620: no target from the server means "change nothing", not
        // "go to latest". The API always resolves a value (host pin → fleet
        // default → 'latest'), so an absent field is a malformed or partial
        // response — upgrading on it moved hosts off their pin.
        const desiredClaudeCodeVersion = hbResp?.desired_claude_code_version?.trim();
        if (
          desiredClaudeCodeVersion &&
          decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'proceed'
        ) {
          const { maybeUpdateClaudeCode } = await import('./claude-code-updater.js');
          await maybeUpdateClaudeCode({
            desiredVersion: desiredClaudeCodeVersion,
            currentVersion: cachedFrameworkVersion,
            log,
          });
        }
      } catch (err) {
        log(`Claude Code updater error: ${(err as Error).message}`);
      }
    } catch (err) {
      // ENG-6412: a failed heartbeat means config-hash freshness is unknown —
      // clear the cache so later requests don't carry a stale X-Config-Hash and
      // mis-correlate audit rows. Self-heals on the next successful heartbeat.
      setConfigHash(null);
      log(`Heartbeat failed: ${(err as Error).message}`);
    }

    // 1d. ENG-5399 Tier 1 responsiveness probe — pane.log freshness per
    // agent. Reports `PaneActivityAgeSeconds` on a slow cadence (default
    // 5 min via AUGMENTED_RESPONSIVENESS_INTERVAL_MS). Complements the
    // existing 15-min agent-synthetic-probe cron with a faster, zero-
    // token canary that catches silent agents before the 35-min
    // SyntheticReplyAgeSeconds alarm window.
    try {
      const {
        collectResponsivenessProbes,
        collectPanelessActivityProbes,
        getResponsivenessIntervalMs,
        occupancyQualificationClassifications,
      } = await import('./responsiveness-probe.js');
      const probeIntervalMs = getResponsivenessIntervalMs();
      if (now - lastResponsivenessProbeAt > probeIntervalMs) {
        const probeCodeNames = [...agentState.persistentSessionAgents];
        if (probeCodeNames.length > 0) {
          // ENG-6017: attach the watchdog's give-up counter (events since
          // the last probe) so "input typed but unsubmittable" reaches
          // CloudWatch as InputStuckGiveUps within one probe cycle. The
          // collect call itself also adds pending_inbound_oldest_age_seconds
          // when marker files exist.
          // ENG-6037: the take is destructive, so remember what each probe
          // drained and re-credit it if the POST fails — a transient API
          // blip must not permanently lose the counts.
          const drainedGiveUps = new Map<string, number>();
          // ENG-9680: the same drain-and-re-credit contract for the watchdog's
          // UNCLAIMED-DIALOG counter. ENG-9006 built that counter and exported
          // its drain, and until now nothing called it: the count incremented on
          // the host, was never read, and never reached the control plane. The
          // freeze it detects — a focused modal no handler recognises — is the
          // one shape `input_stuck_give_ups` structurally cannot see, because a
          // modal with focus leaves no unsubmitted text for the watchdog to
          // retry on. That is the shape that cost ada 5h20m.
          const drainedUnclaimedDialogs = new Map<string, number>();
          // ENG-8090: opencode agents have no `pane.log`, so the collector above
          // has always skipped them entirely — no pane age, and (because the
          // stat and the counter reads shared one try block) no deflection or
          // lane-classification counters either. The paneless collector covers
          // exactly the agents the first one drops, and feeds the busy/idle
          // pipeline from the turn lifecycle.
          //
          // Note which of the two signals goes where: occupancy
          // (`busy_activity_age_seconds`) is for utilisation and billing ONLY,
          // and turn completion (`turn_completion_age_seconds`) is the only one
          // the API is allowed to treat as proof of life. A serve wedged
          // mid-turn is occupied but not alive, and conflating the two would
          // hide the wedge class this whole area exists to surface.
          const paneless = collectPanelessActivityProbes(probeCodeNames, {
            busyActivityAgeSeconds: (codeName) => getOpencodeActivityAgeSeconds(codeName),
            turnCompletionAgeSeconds: (codeName) => {
              const lastRepliedAt = getOpencodeTurnHealth(codeName)?.lastRepliedAt ?? null;
              return lastRepliedAt == null
                ? null
                : Math.max(0, Math.floor((Date.now() - lastRepliedAt) / 1000));
            },
          });
          // ENG-8116: measured occupancy DURATIONS, replacing cadence-counted
          // buckets. Drained here and reported per agent; the API stamps the
          // real seconds into agent_activity_samples.bucket_seconds instead of
          // a constant 60. Drain is destructive, so every path below must
          // re-credit on failure — see the catch.
          const drainedBuckets = new Map<string, BusyBucket[]>();
          // Tracked separately from `drainedBuckets`, which only records
          // NON-EMPTY drains: an agent whose drain came back empty is still
          // drained, and re-draining it below would be a second destructive
          // read dressed up as a first. (CodeRabbit, PR #3752.)
          const drainedCodeNames = new Set<string>();
          // ENG-8465: read once per cycle. shadow keeps every bucket (measure
          // only); enforce drops buckets with no qualifying transcript turn.
          const qualifierMode = occupancyQualifierMode();
          // ENG-9061: agents whose occupancy this host MEASURED and found to be
          // zero — as distinct from agents it cannot measure at all. Reported as
          // one POST-level roster rather than a per-agent field, because there
          // is exactly one drain per POST and because a per-entry flag would
          // have to pass the API's entry-validity check, whose every disjunct
          // means "this entry carries a measurement worth recording". A sixth
          // disjunct saying "there is no measurement" inverts that predicate.
          //
          // Membership requires a FRESH PANE OBSERVATION, not merely that the
          // drain ran: drainClosed returns [] for an agent with no ledger entry
          // at all, so a roster built from "we called it" would be byte-
          // identical whether the sampler had run all week or died at boot —
          // and this roster's whole effect is to switch the fallback billing
          // producer off. Opencode agents have no pane.log, so nothing here can
          // attest their meter; they stay out and keep the legacy path.
          //
          // Scoped to `enforce` deliberately. Under `shadow` the legacy billing
          // behaviour IS the specification ("reproduces pre-flag billing
          // exactly", FLAG_REGISTRY), so changing it there — on a schedule set
          // by the host self-update scheduler rather than by us — would break
          // that contract. This also makes the existing org-scoped, sensitive
          // `utilization-occupancy-qualifier` flag the kill switch.
          const measuredCodeNames = new Set<string>();
          const takeBuckets = (
            codeName: string,
          ): { buckets: BusyBucket[]; classifications: Record<string, number> | null } => {
            if (drainedCodeNames.has(codeName)) return { buckets: [], classifications: null };
            drainedCodeNames.add(codeName);
            const nowMs = Date.now();
            const raw = sharedBusyBuckets.drainClosed(codeName, nowMs);
            if (raw.length === 0) {
              if (
                rostersMeasuredZero(
                  qualifierMode,
                  hasFreshPaneObservation(codeName, nowMs),
                  !notRunningCodeNames.has(codeName),
                )
              ) {
                measuredCodeNames.add(codeName);
              }
              return { buckets: [], classifications: null };
            }
            // ENG-8465: gate measured buckets on transcript evidence of real
            // work. `kept` is every bucket in shadow, only the qualified ones in
            // enforce. Re-credit only what we REPORT — dropped buckets are
            // deliberately discarded and must not resurrect on a failed POST.
            //
            // drainClosed is destructive and re-credit only happens for entries
            // already in drainedBuckets (the POST .catch). A throw here — from
            // extractQualifyingTurns on untrusted transcript text, or
            // isBucketQualified — would unwind past that bookkeeping and lose the
            // just-drained billable buckets with no re-credit. So the qualifier
            // can only ever DOWNGRADE to "report everything ungated". (CodeRabbit)
            let q: QualifyBucketsResult;
            try {
              q = qualifyBuckets(raw, collectQualifyingTurns(codeName, nowMs), qualifierMode, nowMs);
            } catch (err) {
              log(
                `[occupancy-gate] qualification failed for '${codeName}' — reporting ` +
                  `${raw.length} bucket(s) ungated: ${(err as Error).message}`,
              );
              q = {
                kept: raw,
                qualified: 0,
                unqualifiedDropped: 0,
                unreadable: raw.length,
                coverageShortfall: 0,
                qualifiedByBracket: 0,
                bracketDeferred: 0,
                deferred: [],
              };
            }
            // ENG-9178: put DEFERRED buckets back before anything else touches
            // the ledger. They are not billed and not dropped — their tool call
            // has not returned, so the next drain re-judges them against a
            // bracket that may by then have closed.
            //
            // `credit` is the same merge the POST-failure path uses, and the two
            // cannot collide: `deferred` and `kept` are disjoint by construction,
            // and only `kept` is recorded in `drainedBuckets` for re-credit. A
            // bucket therefore has exactly one owner on every path.
            //
            // Ordering matters. `drainClosed` above already removed these from
            // the ledger; crediting them back here, inside the same synchronous
            // block, means no drain can observe the gap.
            if (q.deferred.length > 0) sharedBusyBuckets.credit(codeName, q.deferred);
            if (q.kept.length > 0) drainedBuckets.set(codeName, q.kept);
            return {
              buckets: q.kept,
              classifications: occupancyQualificationClassifications(q),
            };
          };

          const probes: Array<
            ResponsivenessProbePayloadEntry & {
              input_stuck_give_ups: number;
              unclaimed_dialogs?: number;
            }
          > = [...collectResponsivenessProbes(probeCodeNames), ...paneless].map((p) => {
            const giveUps = takeWatchdogGiveUpCount(p.code_name);
            if (giveUps > 0) drainedGiveUps.set(p.code_name, giveUps);
            // ENG-9680: absent, never zero, when there is nothing to report —
            // the same absence-vs-zero contract every optional probe field on
            // this payload follows (ENG-6017). An older API ignores the field;
            // a newer one must not read a shipped 0 as a measurement.
            const unclaimedDialogs = takeWatchdogUnclaimedDialogCount(p.code_name);
            if (unclaimedDialogs > 0) drainedUnclaimedDialogs.set(p.code_name, unclaimedDialogs);
            const { buckets: busyBuckets, classifications } = takeBuckets(p.code_name);
            return {
              ...p,
              input_stuck_give_ups: giveUps,
              ...(unclaimedDialogs > 0 ? { unclaimed_dialogs: unclaimedDialogs } : {}),
              ...(busyBuckets.length > 0 ? { busy_buckets: busyBuckets } : {}),
              ...(classifications
                ? { occupancy_qualification_classifications: classifications }
                : {}),
            };
          });

          // An agent can hold occupancy while producing no other probe signal —
          // an opencode serve mid-turn whose age fields are both null, or a
          // Claude Code agent whose pane.log vanished between the sample and
          // the stat. Reporting only what the collectors happened to emit would
          // silently drop that work, which is the failure mode this issue is
          // about. Carry those agents on their own entries.
          for (const codeName of probeCodeNames) {
            const { buckets, classifications } = takeBuckets(codeName);
            if (buckets.length > 0 || classifications) {
              probes.push({
                code_name: codeName,
                input_stuck_give_ups: 0,
                ...(buckets.length > 0 ? { busy_buckets: buckets } : {}),
                ...(classifications
                  ? { occupancy_qualification_classifications: classifications }
                  : {}),
              });
            }
          }

          // ENG-9061: the roster is a reason to POST in its own right. An
          // all-idle host can produce probe entries and an empty roster, but the
          // converse must not silently drop the stand-down signal.
          const occupancyMeasuredCodeNames = [...measuredCodeNames];
          if (probes.length > 0 || occupancyMeasuredCodeNames.length > 0) {
            // Fire-and-forget — never block pollCycle on CloudWatch
            // latency, and a transient API error here mustn't pause
            // the rest of the cycle.
            void api
              .post('/host/responsiveness-probe', {
                host_id: hostId,
                probes,
                ...(occupancyMeasuredCodeNames.length > 0
                  ? { occupancy_measured_code_names: occupancyMeasuredCodeNames }
                  : {}),
              })
              .catch((err: unknown) => {
                for (const [codeName, count] of drainedGiveUps) {
                  creditWatchdogGiveUpCount(codeName, count);
                }
                // ENG-9680: re-credit for the ENG-6037 reason, and it bites
                // harder here. The watchdog alerts at most ONCE per distinct
                // modal (a hash guard), so a dropped count is not "we will see
                // it again on the next cycle" — it is the only report that
                // freeze will ever produce, and losing it re-hides exactly the
                // condition this wiring exists to surface.
                for (const [codeName, count] of drainedUnclaimedDialogs) {
                  creditWatchdogUnclaimedDialogCount(codeName, count);
                }
                // ENG-8116: occupancy MUST be re-credited too. Unlike the
                // give-up counters, these buckets are billable and cannot be
                // reconstructed from anything else — a transient 5xx that
                // dropped them would permanently under-charge, invisibly.
                for (const [codeName, buckets] of drainedBuckets) {
                  sharedBusyBuckets.credit(codeName, buckets);
                }
                log(
                  `[responsiveness-probe] post failed (give-up counts + ${drainedUnclaimedDialogs.size} unclaimed-dialog count(s) + ${drainedBuckets.size} busy-bucket set(s) re-credited): ${(err as Error).message}`,
                );
              });
          }
        }
        lastResponsivenessProbeAt = now;
      }
    } catch (err) {
      log(`[responsiveness-probe] collection failed: ${(err as Error).message}`);
    }

    // 1d-2. ENG-6153 — wedged-but-alive detection. A session whose claude
    // process is alive but isn't draining inbound (frozen turn / hung Stop
    // hook) reads HEALTHY to isSessionHealthy, and a manual restart just
    // `--resume`s the same stuck transcript and re-wedges (the sherlock
    // incident, 2026-06-08). Detect it from the cheap pane+inbound signals the
    // responsiveness probe already scans and force a FRESH respawn (rotated
    // UUID, no --resume; old transcript preserved). Shadow-first — does nothing
    // until AGT_WEDGE_RESTART_MODE is shadow/enforce. See lib/wedge-detection.ts.
    try {
      const wedgeConfig = resolveWedgeConfig();
      if (wedgeConfig.mode !== 'off') {
        const {
          collectResponsivenessProbes,
          livePendingInboundOldestAgeSeconds,
          parkPendingInbound,
        } = await import('./responsiveness-probe.js');
        // ENG-6238: transcript growth = "the model is actually producing
        // tokens", the signal that distinguishes a legit long turn from a
        // frozen/hung one and spares it from the hard-cap respawn.
        // (ENG-6264: transcriptActivityAgeSeconds is now a static import — the
        // restart gate shares the same signal.)
        const { getProjectDir: wedgeProjectDir } = await import('./scheduler-engine.js');
        const wedgeNow = new Date();
        const liveAgents = agentState.persistentSessionAgents;
        // CR #1901: prune streaks for agents that have left the persistent set
        // so a departed-then-returned agent never inherits a stale wedge streak.
        for (const tracked of consecutiveWedgeCycles.keys()) {
          if (!liveAgents.has(tracked)) consecutiveWedgeCycles.delete(tracked);
        }
        for (const tracked of wedgeExemptionLogged) {
          if (!liveAgents.has(tracked)) wedgeExemptionLogged.delete(tracked);
        }
        // ENG-6171: same hygiene for the poison-card accounting — a departed
        // agent must not carry its suspected-poison suppression into a later
        // re-add.
        for (const tracked of wedgeRestartsByCard.keys()) {
          if (!liveAgents.has(tracked)) wedgeRestartsByCard.delete(tracked);
        }
        const poisonConfig = resolvePoisonCardConfig();
        for (const probe of collectResponsivenessProbes([...liveAgents])) {
          const codeName = probe.code_name;
          // A wedge is by definition ALIVE-but-stuck. A crashed/absent session
          // (isSessionHealthy=false) is owned by the normal unhealthy-recovery
          // path (which resumes today's transcript) — the wedge path must not
          // cold-start it via rotateSessionForWedge. (CR #1901)
          if (!isSessionHealthy(codeName)) {
            consecutiveWedgeCycles.delete(codeName);
            wedgeExemptionLogged.delete(codeName);
            continue;
          }
          // ENG-6160: count only LIVE inbound for the wedge signal — markers
          // older than the current session start (leftovers from a prior
          // session) or flagged undeliverable must not register as "this
          // session isn't draining". Without this, an orphan marker survives a
          // fresh respawn and the detector re-fires forever on a healthy idle
          // agent (the sherlock enforce loop, 2026-06-08). The alarm-facing
          // probe.pending_inbound_oldest_age_seconds is deliberately untouched.
          const wedgeSession = getSessionState(codeName);
          const sessionStartMs = wedgeSession?.startedAt ?? null;
          // ENG-6238: age of the live session's transcript JSONL. null when the
          // session id / file can't be resolved → isSessionProducing falls back
          // to pane age (pre-6238 behaviour) rather than treating it as frozen.
          const transcriptAge = transcriptActivityAgeSeconds(
            wedgeProjectDir(codeName),
            wedgeSession?.currentSessionId ?? null,
            wedgeNow,
          );
          // ENG-6294: a parent that dispatched a `run_in_background` sub-agent
          // and ended its turn is healthy, not wedged — the worker's own
          // transcript (<session>/subagents/*.jsonl) keeps growing while the
          // main one goes quiet. Counted as "producing" by the detector.
          const subagentAge = subagentActivityAgeSeconds(
            wedgeProjectDir(codeName),
            wedgeSession?.currentSessionId ?? null,
            wedgeNow,
          );
          const signals = {
            paneActivityAgeSeconds: probe.pane_activity_age_seconds,
            transcriptActivityAgeSeconds: transcriptAge,
            subagentActivityAgeSeconds: subagentAge,
            pendingInboundOldestAgeSeconds: livePendingInboundOldestAgeSeconds(
              codeName,
              sessionStartMs,
              wedgeNow,
            ),
          };
          if (!isWedgeCandidateCycle(signals, wedgeConfig)) {
            consecutiveWedgeCycles.delete(codeName);
            // ENG-6294: if the ONLY thing sparing this session is the in-flight
            // background task, say so — once per episode — so a suppressed
            // wedge is visible in manager.log instead of silently resetting.
            const exemption = wedgeExemptionReason(signals, wedgeConfig);
            if (exemption !== null) {
              if (!wedgeExemptionLogged.has(codeName)) {
                wedgeExemptionLogged.add(codeName);
                log(
                  `[wedge] exempt agent=${codeName} reason=${exemption} ` +
                    `subagentAge=${subagentAge}s ` +
                    `inboundAge=${signals.pendingInboundOldestAgeSeconds}s ` +
                    `transcriptAge=${transcriptAge === null ? 'n/a' : `${transcriptAge}s`}`,
                );
              }
            } else {
              wedgeExemptionLogged.delete(codeName); // episode over — re-arm the log
            }
            continue;
          }
          wedgeExemptionLogged.delete(codeName);
          const streak = (consecutiveWedgeCycles.get(codeName) ?? 0) + 1;
          consecutiveWedgeCycles.set(codeName, streak);
          if (decideWedgeRestart({ ...signals, consecutiveWedgeCycles: streak }, wedgeConfig) !== 'wedged') {
            continue; // streak hasn't reached minCycles yet
          }
          const transcriptAgeLabel = transcriptAge === null ? 'n/a' : `${transcriptAge}s`;
          const detail =
            `agent=${codeName} paneAge=${signals.paneActivityAgeSeconds}s ` +
            `transcriptAge=${transcriptAgeLabel} ` +
            `inboundAge=${signals.pendingInboundOldestAgeSeconds}s cycles=${streak}`;
          if (wedgeConfig.mode === 'shadow') {
            // One line per wedge episode (logged the cycle the streak first
            // crosses minCycles) — not every poll, to avoid spamming.
            if (streak === wedgeConfig.minCycles) {
              log(`[wedge] SHADOW would force-fresh respawn ${detail}`);
            }
            continue;
          }
          // enforce: rotate today's UUID so the next spawn cold-starts fresh,
          // then tear down the wedged session; the ensure-session pass below
          // respawns onto the fresh (transcript-less) UUID.
          try {
            const newId = rotateSessionForWedge(codeName);
            try {
              const { execSync: es } = await import('node:child_process');
              es(`tmux kill-session -t agt-${codeName} 2>/dev/null`, { stdio: 'ignore' });
            } catch {
              /* no session to kill — the respawn will start a fresh one */
            }
            stopPersistentSessionAndForgetMcpBaseline(codeName);
            consecutiveWedgeCycles.delete(codeName);
            // ENG-6289 (supersedes the ENG-6160 blanket dead-letter): PARK the
            // wedged session's undrained pending-inbound markers — leave them in
            // the live dirs so the fresh session's orient hook surfaces them and
            // ENG-5969 replay (when enabled) re-pushes them. The ENG-6160
            // pre-session exclusion keeps untouched parked markers out of the
            // fresh session's wedge signal, so the loop still can't re-form.
            // Only `undeliverable: true` markers (already ⏳-noticed, never
            // drainable) are dead-lettered to `-stale`.
            const { parked, deadLettered } = parkPendingInbound(codeName, wedgeNow);
            const parkNote = parked > 0 ? `, ${parked} inbound parked for fresh session` : '';
            const deadNote = deadLettered > 0 ? `, ${deadLettered} undeliverable inbound dead-lettered` : '';
            log(
              `[wedge] forced fresh respawn ${detail} → new session ${newId} ` +
                `(transcript preserved${parkNote}${deadNote})`,
            );
            // ENG-7360: if this wedge followed a transient LLM-API error
            // (529/429/503/500 that exhausted its retries), the raw error never
            // became a scraped reply — so ENG-6861's friendly rewrite never ran
            // and the user is left in silence. Reuse the ENG-6058 give-up signal,
            // tagged reason=transient_overload, so the channel sweeps post the
            // friendly "please resend" notice instead. Flag-gated (ships dark);
            // best-effort — a detection/write failure must never affect the respawn.
            if (hostFlagStore().getBoolean('wedge-transient-notice')) {
              try {
                const paneTail = readFileSync(paneLogPath(codeName), 'utf8').slice(-65536);
                const transient = detectTransientApiErrorInLog(paneTail);
                if (transient) {
                  const wedgeHome = join(homedir(), '.augmented', codeName);
                  if (existsSync(wedgeHome)) {
                    atomicWriteFileSync(
                      join(wedgeHome, 'watchdog-give-up.json'),
                      JSON.stringify({
                        gave_up_at: wedgeNow.toISOString(),
                        reason: 'transient_overload',
                      }),
                    );
                    log(
                      `[wedge] transient API error (${transient.code}/${transient.kind}) preceded the ` +
                        `wedge for '${codeName}' — wrote transient-overload give-up signal so pending ` +
                        `conversations get a friendly resend notice (ENG-7360)`,
                    );
                  }
                }
              } catch (err) {
                log(
                  `[wedge] transient-notice check failed for '${codeName}' (ENG-7360): ` +
                    `${(err as Error).message} — respawn unaffected`,
                );
              }
            }
            // ENG-6265: record the respawn (+ dead-lettered count) with the API
            // so the agent-heartbeat-monitor cron rolls it into the
            // WedgeRespawnsHourly / DeadLetteredInboundHourly metrics + alarms.
            // Without this the wedge path is invisible to CloudWatch — an agent
            // can thrash and drop messages while every other signal stays green.
            // Fire-and-forget: the respawn's correctness must not depend on it.
            const wedgeAgentId = agentState.codeNameToAgentId.get(codeName);
            if (wedgeAgentId) {
              api
                .post('/host/wedge-respawn', {
                  agent_id: wedgeAgentId,
                  code_name: codeName,
                  // dead_lettered_count keeps its "messages actually dropped"
                  // alarm semantics (DeadLetteredInboundHourly fires on the
                  // first one); parked messages are recoverable, reported
                  // separately. Older APIs ignore the unknown field.
                  dead_lettered_count: deadLettered,
                  parked_count: parked,
                  pane_age_seconds: signals.paneActivityAgeSeconds,
                  inbound_age_seconds: signals.pendingInboundOldestAgeSeconds,
                  transcript_age_seconds: transcriptAge,
                })
                .catch((err) => {
                  log(
                    `[wedge] failed to record respawn event for '${codeName}' (ENG-6265): ${(err as Error).message} — respawn proceeded; CloudWatch metric will under-count this event`,
                  );
                });
            }
            // ENG-6171: attribute this respawn to whatever the agent had
            // in_progress when it wedged (last-known board snapshot — the
            // cache the hybrid inject populated; absent ⇒ unattributable, a
            // no-op). A card in_progress across `threshold` consecutive
            // wedge-respawns is a suspected poison task; the hybrid inject
            // then stops re-nudging it (see maybeInjectKanbanCheck).
            const inProgressCardIds = (kanbanBoardCache.get(codeName) ?? [])
              .filter((item) => item.status === 'in_progress' && isHybridActionable(item))
              .map((item) => item.id);
            const cardStates = wedgeRestartsByCard.get(codeName) ?? new Map<string, CardWedgeState>();
            const { next, newlyPoisoned } = recordWedgeForCards(
              cardStates,
              inProgressCardIds,
              wedgeNow.getTime(),
              poisonConfig,
            );
            wedgeRestartsByCard.set(codeName, next);
            for (const cardId of newlyPoisoned) {
              log(
                `[wedge] poison-card suspected agent=${codeName} card=${cardId} ` +
                  `consecutiveWedgeRespawns=${poisonConfig.threshold} — suppressing re-nudge ` +
                  `(falls to stale-auto-fail). See ENG-6171.`,
              );
            }
          } catch (err) {
            log(`[wedge] force-fresh respawn failed for ${codeName}: ${(err as Error).message}`);
          }
        }
      }
    } catch (err) {
      log(`[wedge] detection failed: ${(err as Error).message}`);
    }

    // 1e. ENG-5459 — scrape "N MCP servers failed · /mcp" from each
    // active agent's pane.log and POST to the API so the webapp can
    // surface a "needs attention" signal alongside the existing
    // needsReconnect indicator. Catches the broken-but-process-alive
    // case mcp-presence-reaper can't see (Composio MCP whose URL
    // 401s on every tool call — process up, API dead).
    //
    // Fire-and-forget per agent. Only POSTs when the count differs
    // from the last observation (cheap dedupe — most ticks the count
    // is stable). Skipped entirely for agents with no pane.log.
    try {
      const { scrapeMcpFailedBannerCount } = await import('./pane-mcp-banner-scraper.js');
      // ENG-8877 — same lazy-import style as the scraper above.
      const { probeSessionAuth } = await import('./session-auth-dead.js');
      const observations: Array<{ agent_id: string; count: number }> = [];
      // CodeRabbit on PR #1318: commit dedupe-cache only after POST
      // succeeds, otherwise a transient API failure permanently
      // suppresses future POSTs of the same value. Build a parallel
      // (codeName, count) list while we walk agents so the success
      // handler can apply the cache without a reverse lookup.
      const pendingCacheCommits: Array<{ codeName: string; count: number }> = [];
      // ENG-7363: resolve the model-API-error reporting flag once per tick.
      const modelApiErrorReportingOn = hostFlagStore().getBoolean('model-api-error-reporting');
      for (const codeName of agentState.persistentSessionAgents) {
        const agentId = agentState.codeNameToAgentId.get(codeName);
        if (!agentId) continue;
        // ENG-8877: is this session's PLATFORM auth alive? The banner below
        // and the presence reaper both answer "is the MCP process up", which
        // a session holding a dead token passes. Nothing on this host could
        // previously see the difference, so `decision=healthy` was logged for
        // a session whose every platform call 401s.
        //
        // ABOVE THE PANE READ, DELIBERATELY. This probe reads JSONL
        // transcripts and never touches pane.log — the whole point is that a
        // tool error's text reaches the transcript in full and does NOT reach
        // the pane, which collapses it to `Error: API Error`. My first draft
        // put this after `if (!tail) continue`, so an agent with no pane.log
        // was silently exempt from the one check that does not need one.
        // CodeRabbit caught it; a fix whose own comment explains that the
        // pane is the wrong surface should not then be gated on the pane.
        //
        // OBSERVE ONLY. No restart, no alert, no status write. Two of the
        // three states must not trigger a restart at all (a tier outage
        // self-heals; an impersonation token is baked into .mcp.json so a
        // respawn re-reads it), and shipping the remedy before the signal has
        // been seen in the wild is how ENG-8877 got filed wrong in the first
        // place.
        try {
          const authVerdict = probeSessionAuth({ projectDir: psGetProjectDir(codeName) });
          if (authVerdict.state !== 'ok' && lastSessionAuthDetail.get(codeName) !== authVerdict.detail) {
            log(
              `[session-auth] agent=${codeName} ${authVerdict.detail}` +
                `${authVerdict.worst?.toolName ? ` tool=${authVerdict.worst.toolName}` : ''}` +
                ` — ${authVerdict.worst?.remedy ?? ''}`,
            );
          }
          // Track both directions so a recovered-then-recurring state re-reports.
          lastSessionAuthDetail.set(codeName, authVerdict.detail);
        } catch (err) {
          log(`[session-auth] probe failed for ${codeName}: ${(err as Error).message}`);
        }

        // 40 lines is enough to catch the banner — it renders on
        // every frame, so the last few KB always contain it.
        const tail = readPaneLogTail(codeName, 40);
        if (!tail) continue;
        // ENG-7363 surface (a): scrape the agent's pane for a model-API error
        // banner (529/429/5xx/timeout) and report the transition. Deduped per
        // agent by signature so a static banner emits one event per episode
        // not one per poll. Runs before the MCP dedupe `continue` below so an
        // unchanged MCP count doesn't skip it.
        if (modelApiErrorReportingOn) {
          const observedErr = scrapeAgentRuntimeModelApiError(tail);
          const sig = observedErr ? `${observedErr.errorClass}:${observedErr.httpStatus ?? ''}` : '';
          if (observedErr && sig !== (lastModelApiErrorSig.get(codeName) ?? '')) {
            void reportModelApiError({
              source: 'agent_runtime',
              errorClass: observedErr.errorClass,
              httpStatus: observedErr.httpStatus,
              agentCodeName: codeName,
              // ENG-10156: the policy this session was LAUNCHED under, read from
              // the baseline rather than resolved now — see
              // `modelPolicyIdBySession`. Absent for an unpoliced agent, and for
              // one whose policy did not shape the session.
              policyId: modelPolicyIdBySession.get(codeName) ?? null,
              detail: `agent pane: ${observedErr.errorClass}`,
              log,
            });
          }
          // Track both directions so a recovered-then-recurring error re-reports.
          lastModelApiErrorSig.set(codeName, sig);
        }
        const observed = scrapeMcpFailedBannerCount(tail);
        // Null = no banner detected this tick. Treat as 0 — Claude
        // Code omits the banner when all MCPs are healthy. Persisting
        // 0 also lets the alarm transition back to OK on recovery.
        const count = observed ?? 0;
        const last = lastMcpFailedBannerCount.get(codeName);
        if (last === count) continue; // dedupe — no change since last poll
        observations.push({ agent_id: agentId, count });
        pendingCacheCommits.push({ codeName, count });
      }
      if (observations.length > 0) {
        // CodeRabbit on PR #1318 round 4: only commit the cache for
        // observations the API actually accepted. The endpoint
        // returns `{ results: [{ agent_id, ok, error? }] }` — a row
        // that failed (e.g. host not assigned anymore) should be
        // retried on the next tick, not silently suppressed.
        void api
          .post<{ results?: Array<{ agent_id: string; ok: boolean }> }>(
            '/host/mcp-banner-observation',
            { observations },
          )
          .then((resp) => {
            const accepted = new Set(
              (resp?.results ?? [])
                .filter((r) => r.ok)
                .map((r) => r.agent_id),
            );
            for (const { codeName, count } of pendingCacheCommits) {
              const agentId = agentState.codeNameToAgentId.get(codeName);
              if (agentId && accepted.has(agentId)) {
                lastMcpFailedBannerCount.set(codeName, count);
              }
            }
          })
          .catch((err: unknown) => {
            log(`[mcp-banner] post failed: ${(err as Error).message}`);
          });
      }
    } catch (err) {
      log(`[mcp-banner] collection failed: ${(err as Error).message}`);
    }

    const data = await api.post<{
      agents: Array<{
        agent_id: string;
        code_name: string;
        display_name: string;
        status: string;
        environment: string;
        framework?: string;
        // ENG-9634: the control plane holds an OPEN usage_limit_reached alert
        // for this agent. Present only when true. Fed to the banner monitor so
        // it can re-seed the absence counter a manager restart wiped — without
        // it, an agent whose cap lifted while the manager was down can never
        // post the clear and its CRITICAL waits out the 8-day timer.
        usage_limit_alert_open?: boolean;
        // ENG-4947: dashboard-issued restart signal. The manager kills+
        // respawns the agent's tmux session when this advances past
        // AgentState.lastRestartProcessedAt.
        restart_requested_at?: string | null;
        // ENG-7576: the doorbell discriminator (host_agents.restart_reason,
        // written by host-actions.ts and cleared with the ack). Lets the
        // restart-consumption loop below classify an integration-change reload
        // (counted on the breaker's provisioning tally, measured + verified)
        // vs a plain 'manual' operator restart (uncounted, as before).
        restart_reason?: string | null;
    /** ENG-8388: 'user' when an authenticated console user drove the reload. */
    restart_actor?: string | null;
        // ENG-8690: when this agent was created. Feeds the restart breaker's
        // new-agent grace: for the first 24 hours, provisioning churn is the
        // EXPECTED shape of onboarding rather than evidence of a fault, so it
        // cannot pause the agent. Crash restarts are unaffected. Optional
        // because an older API returns no such field, and the breaker treats an
        // unknown age as "governed", never "exempt".
        created_at?: string | null;
        // ENG-6146: kill switch. When set, the API has overlaid status:'paused'
        // for this agent because its scope (global/org/team) is killed. The
        // manager halts it via the normal paused path and uses this marker to
        // log the reason + announce resume when the switch clears.
        kill_switch?: { scope: string | null; reason: string | null; source: string | null } | null;
        // ENG-7909: the resolved SOFT enforcement level (warn/mute) for this
        // agent's org scope, or null/absent when none. `halt` is NOT carried
        // here — it rides the kill_switch marker above. The manager materializes
        // this to the per-agent account-enforcement marker the channel adapters
        // read (auto-reply on mute; nag after replies on warn).
        enforcement?: 'warn' | 'mute' | null;
        // ENG-8541: the operator-configured notice for this agent's enforcement
        // level, set platform-wide on the admin Kill Switches page. Absent when
        // no override is configured (the common case), in which case the channel
        // adapters use their built-in default copy for the level.
        enforcement_text?: string | null;
        // ENG-8045 (ENG-8015 AC2): operator re-enabled these auto-quarantined
        // channels. The manager clears each host-local quarantine marker BEFORE
        // processAgent re-renders .mcp.json (so the channel comes back), then
        // acks via /host/channel-clear-ack. The paired 'channel-reenable'
        // restart_reason forces the clean respawn that reloads the channel MCP.
        channel_clears?: Array<{ channel_id: string; requested_at: string }>;
      }>;
    }>('/host/agents', { host_id: hostId });

    const agents = data.agents ?? [];

    // ENG-10760: an empty roster is the backoff signal. Reset on any agent, so
    // the first assignment restores the normal cadence on the very next pass and
    // provisioning is never waiting out a long sleep. Logged only on the two
    // transitions, so an idle host does not write one line per pass.
    if (agents.length > 0) {
      if (consecutiveEmptyAgentPolls > 0) {
        log(
          `[idle-backoff] ${agents.length} agent(s) assigned after ` +
            `${consecutiveEmptyAgentPolls} empty poll(s), resuming normal interval`,
        );
      }
      consecutiveEmptyAgentPolls = 0;
    } else {
      consecutiveEmptyAgentPolls += 1;
      if (consecutiveEmptyAgentPolls === 1) {
        log(
          '[idle-backoff] no agents assigned to this host — backing off the poll ' +
            `loop toward a ${idlePollBackoffCapMs}ms cap`,
        );
      }
    }

    // ENG-8690: refresh the breaker's view of how old each agent is, BEFORE any
    // code below can record a restart event. The restart-consumption loop that
    // follows records on the breaker, so populating this afterwards would leave
    // the first poll of a newly-provisioned agent - precisely the poll most
    // likely to carry onboarding churn - deciding without an age and therefore
    // without its grace.
    {
      const seen = new Set<string>();
      for (const agent of agents) {
        seen.add(agent.code_name);
        noteAgentCreatedAt(agent.code_name, agent.created_at);
      }
      // An agent that has left this host's roster should not keep a cached age.
      // Same reasoning as the delete above: the map outlives the poll, and a
      // codename later reassigned to a different agent must not inherit the old
      // one's creation time and, with it, a grace it never earned.
      for (const codeName of [...agentCreatedAtMs.keys()]) {
        if (!seen.has(codeName)) agentCreatedAtMs.delete(codeName);
      }
    }

    // ENG-4947: act on dashboard-issued restart signals before the normal
    // refresh pass. Killing the tmux session here means processAgent below
    // sees the agent as needing a fresh respawn rather than a no-op refresh.
    // We track the acks in a per-cycle Map and apply them to the
    // AgentState rows produced by processAgent below, so the timestamp
    // survives even when the agent's first appearance on this host
    // already carries a pending signal (no `prev` state to mutate).
    const restartAcks = new Map<string, string>();
    // ENG-7011: restart requests whose server-side `restart_requested_at` we must
    // clear this cycle. Covers both the requests we action below AND ones already
    // serviced on a prior poll whose clear never landed — so the standing stuck-
    // restart backlog auto-heals without ever re-restarting a healthy agent.
    const restartClears: Array<{ agentId: string; requestedAt: string }> = [];
    for (const agent of agents) {
      const requested = agent.restart_requested_at ?? null;
      if (!requested) continue;
      // ENG-7335: the fast-restart lane (restart doorbell) is actively servicing
      // this agent right now - let it own the kill + ack this pass so the two
      // paths can't double-kill/double-respawn. If the lane fails it won't ack,
      // so a later poll (lane no longer in-flight) services it normally.
      if (restartInFlight.has(agent.agent_id)) continue;
      const prev = state.agents.find((a) => a.agentId === agent.agent_id);
      const lastProcessed = prev?.lastRestartProcessedAt ?? null;
      // Same timestamp (or older) → already serviced; do NOT restart again. We
      // still queue the clear below: the request is still set server-side, which
      // means our earlier ack never landed (or predates this manager process).
      const alreadyServiced =
        lastProcessed != null && Date.parse(lastProcessed) >= Date.parse(requested);
      if (!alreadyServiced) {
        const restartReason = agent.restart_reason ?? null;
        log(
          `[restart] Dashboard requested restart for '${agent.code_name}' at ${requested}` +
          ` (reason=${restartReason ?? 'manual'})`,
        );
        // ENG-8000: an opencode agent's serve runs under its own tmux namespace
        // (`agt-oc-<code>`), not the Claude `agt-<code>` pane, and its teardown is
        // stopOpencodeSession. The Claude kill + stopPersistentSessionAndForgetMcpBaseline
        // below miss it entirely, so the serve stays healthy, the subsequent
        // processAgent -> ensureOpencodeRuntime early-returns 'healthy', and the
        // restart is a silent no-op even though it gets acked. Route opencode
        // through stopOpencodeSession (which also marks the in-memory session
        // 'stopped', so startOpencodeSession's already-'running' guard cannot
        // short-circuit the respawn - a raw tmux kill leaves that guard set and
        // wedges the serve, the nora recovery note on ENG-8000). processAgent later
        // this cycle then sees an unhealthy opencode session and respawns it against
        // the fresh opencode.json. The breaker/telemetry + MCP-rebind follow-ups
        // below are all Claude-shaped and do not apply to opencode's serve.
        // ENG-8000 (CodeRabbit): `framework` is optional on the /host/agents
        // response, so fall back to the agentFrameworkCache (populated by a prior
        // processAgent) before DEFAULT_FRAMEWORK - identical to the doorbell lane.
        // Without the cache fallback, an omitted framework on a known opencode
        // agent would silently take the Claude-pane teardown and re-open this bug.
        const framework =
          agent.framework ?? agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
        if (restartTargetsOpencodeServe(framework)) {
          stopOpencodeSession(agent.code_name, log);
        } else {
          try {
            const { execSync: es } = await import('node:child_process');
            es(`tmux kill-session -t agt-${agent.code_name} 2>/dev/null`, { stdio: 'ignore' });
          } catch {
            /* no session to kill — the respawn below will start a fresh one */
          }
          // ENG-7576: an integration add/remove reload counts on the breaker's
          // looser provisioning tally (ENG-7560) like its hot-reload siblings -
          // before this it passed NO reason, so the first (and after coalescing,
          // only) restart of an integration add was invisible to the breaker and
          // to restart telemetry. A plain operator restart (reason null/'manual')
          // keeps passing undefined = uncounted, so restart-hammering while
          // debugging can never trip the breaker.
          const breakerReason = dashboardRestartBreakerReason(restartReason, agent.restart_actor);
          stopPersistentSessionAndForgetMcpBaseline(agent.code_name, breakerReason);
          if (breakerReason && restartReasonBindsNewMcp(breakerReason)) {
            // ENG-7576: this stop exists to rebind a changed MCP server set, so
            // give it the same follow-ups the coalescer's timer gives hot-reload
            // stops. Ordering matters: the wrapper above ran
            // cancelPendingSessionRestart, which cleared any armed verification
            // AND (via clearRestartTiming) the doorbell's noteRestartRequested
            // stamp - so re-stamp/arm strictly after it.
            //  - restart-timing (ENG-7401): the managed-toolkit begin() is gated
            //    on isSessionHealthy, which is false right after this stop, so
            //    without a begin() here the ONE real respawn of an integration
            //    add went unmeasured (only the redundant burst restarts ever
            //    produced a begin->stop->finish triple).
            //  - restart-verify (ENG-6174): arm respawn verification so
            //    verifyPendingRestarts confirms the respawn actually bound its
            //    tools (emitting the tools-bound/failed terminal) instead of
            //    assuming.
            noteRestartRequested(agent.code_name, requested);
            beginRestartTiming(
              agent.code_name,
              { reason: 'integration-change (dashboard restart)', delivered: null, injectDelayMs: null },
              log,
            );
            markRestartStopped(agent.code_name, log);
            const prior = pendingRestartVerifications.get(agent.code_name);
            pendingRestartVerifications.set(agent.code_name, {
              firedAt: Date.now(),
              attempts: prior?.attempts ?? 0,
            });
          }
        }
        restartAcks.set(agent.agent_id, requested);
      }
      // ENG-7011: report the request as serviced so the API clears
      // host_agents.restart_requested_at. Without this the dashboard shows the
      // restart "stuck" forever and the restart-monitor cron pages a false
      // `agent_restart_stuck` ~15 min later.
      restartClears.push({ agentId: agent.agent_id, requestedAt: requested });
    }

    // ENG-7011: fire the clears best-effort (fire-and-forget, like /restart-event).
    // Race-safe server-side: the clear only nulls the column when its stored value
    // still equals `requestedAt`, so a newer click is preserved. A failed clear just
    // retries next poll — the `alreadyServiced` guard keeps the agent from being
    // re-restarted in the meantime.
    for (const clear of restartClears) {
      void api
        .post('/host/restart-ack', {
          host_id: hostId,
          agent_id: clear.agentId,
          restart_requested_at: clear.requestedAt,
        })
        .catch((err) =>
          log(`[restart] clear failed for agent_id=${clear.agentId}: ${(err as Error).message}`),
        );
    }

    // ENG-8045 (ENG-8015 AC2): service operator channel re-enables. For each
    // pending clear delivered on this poll, drop the host-local quarantine marker
    // BEFORE processAgent (below) re-reads getQuarantinedKeys and re-renders
    // .mcp.json — so the re-enabled channel is back in the effective set. The
    // paired 'channel-reenable' restart_reason (serviced by the restart loop
    // above, same poll) forces the clean respawn that actually reloads the
    // channel MCP; clearing the marker alone does NOT reload it (ENG-8003). Ack
    // best-effort per (agent, channel) so a serviced row isn't re-delivered every
    // poll — a missed ack just re-runs the idempotent clear (a no-op once the
    // marker is gone) next poll.
    for (const agent of agents) {
      const clears = agent.channel_clears ?? [];
      if (clears.length === 0) continue;
      for (const clear of clears) {
        const removed = channelQuarantineStore().clearQuarantine(
          agent.code_name,
          clear.channel_id,
        );
        // ENG-10510: an operator re-enable FORGETS the auto-resume strikes. A
        // human pressing the button is a statement that the underlying fault was
        // addressed, so the next automatic trial starts from a clean 2h rather
        // than inheriting an 8h wait, or a spent budget, from before the fix.
        // Without this an operator could fix a channel, watch it break weeks
        // later for an unrelated reason, and find auto-resume already exhausted.
        // BOTH NAMESPACES (Obi-Wan on #6313). Shadow strikes are stored under
        // `shadow:<key>` (resumeHistoryKey), so forgetting the bare key alone
        // left a shadow ladder intact across the very event it exists to
        // reset. An operator who previewed in shadow, fixed the fault and
        // pressed Re-enable would have seen the next preview resume at attempt
        // 4 with a 16h wait - wrong in exactly the situation `forget` was
        // added for, and wrong about the enforce path it is supposed to model.
        // It also stops `shadow:*` entries being orphaned in the sidecar once
        // the flag moves to enforce, since the success sweep only walks the
        // ACTIVE mode's namespace.
        for (const mode of ['enforce', 'shadow'] as const) {
          channelResumeHistoryStore().forget(
            agent.code_name,
            resumeHistoryKey(mode, clear.channel_id),
          );
        }
        log(
          `[channel-reenable] ${removed ? 'cleared' : 'no marker for'} ` +
            `'${clear.channel_id}' on '${agent.code_name}' (operator re-enable)`,
        );
        void api
          .post('/host/channel-clear-ack', {
            agent_id: agent.agent_id,
            channel_id: clear.channel_id,
            requested_at: clear.requested_at,
          })
          .catch((err) =>
            log(
              `[channel-reenable] ack failed for agent_id=${agent.agent_id} ` +
                `channel=${clear.channel_id}: ${(err as Error).message}`,
            ),
          );
      }
    }

    // ENG-10510: time-based auto-resume of channel quarantines.
    //
    // Deliberately HERE, sharing the operator clears' position in the poll: both
    // must drop the marker BEFORE processAgent re-reads `getQuarantinedKeys()`
    // and recomputes `currentChannelIds`. That recomputation is the whole
    // mechanism — a cleared channel reappears in the effective set, which flips
    // `channelsChanged`, which re-renders `.mcp.json` and respawns the session.
    // So no bespoke restart is requested and no restart REASON is invented: the
    // ordinary hot-reload path does it, and stays counted against the breaker
    // like any other. Reusing the operator's 'channel-reenable' reason would
    // have made these restarts UNCOUNTED (`dashboardRestartBreakerReason`
    // returns undefined for it), removing the only backstop from a loop that
    // runs without a human.
    //
    // This is a TRIAL, not a recovery: nothing here knows whether the channel
    // is healthy, because a quarantined channel is unobservable by construction.
    // If it is still dead the reaper re-quarantines it, the strike count grows,
    // and the next wait doubles.
    const autoResumeMode = channelQuarantineAutoResumeMode();
    /**
     * AUTO-RESUME REQUIRES A WORKING RE-QUARANTINE, AND THAT IS A DIFFERENT
     * FLAG (obi-wan `7b85a224e858`, MAJOR).
     *
     * The whole safety argument of this feature is "put the channel back and let
     * the reaper re-quarantine it if it is still dead" - the module header says
     * it, and the flag description repeats it verbatim. That backstop exists
     * only when `channel-quarantine-mode` is `enforce`: at `shadow` the reaper
     * logs `SHADOW would-quarantine` and writes NO marker
     * (mcp-presence-reaper.ts, the `quarantineMode === 'enforce'` gate).
     *
     * These are two independently flippable sensitive enums and this block read
     * only its own. So with auto-resume at `enforce` and quarantine-mode at
     * `shadow` - de-escalating quarantine during an incident, or flipping the
     * two in the wrong order during rollout - `clearQuarantine` removes every
     * marker, nothing can ever re-create one, `shouldForgetResumeHistory` sees
     * `isQuarantined === false`, and after the grace it logs `trial SUCCEEDED`
     * and wipes the strikes. Permanently un-quarantining every dead channel and
     * re-arming the ENG-5932 session flap, with no error anywhere and a log line
     * asserting the opposite.
     *
     * A trial whose failure cannot be observed is not a trial. So enforce-mode
     * auto-resume is DOWNGRADED to shadow rather than refused outright: the
     * bookkeeping still runs and still previews, nothing is cleared, and the
     * operator gets one line naming the other flag. Refusing silently would
     * trade a dangerous no-op for an invisible one.
     */
    const quarantineMode = channelQuarantineMode();
    const autoResumeEffective = resolveAutoResumeEffectiveMode(autoResumeMode, quarantineMode);
    if (autoResumeMode === 'enforce' && autoResumeEffective === 'shadow') {
      if (shouldLogAutoResumeModeDowngrade(autoResumeMode, quarantineMode)) {
        log(
          `[channel-auto-resume] channel-quarantine-auto-resume is 'enforce' but ` +
            `channel-quarantine-mode is '${quarantineMode}', so the reaper writes no quarantine ` +
            `marker and a failed trial could never be observed - every trial would score a ` +
            `success and permanently un-quarantine a dead channel. Running in SHADOW instead ` +
            `(bookkeeping only, nothing cleared). Set channel-quarantine-mode to 'enforce' first.`,
        );
      }
    }
    if (autoResumeEffective !== 'off') {
      const nowMs = Date.now();
      for (const agent of agents) {
        // CodeRabbit on #6313: a draft / paused / revoked agent does not
        // provision the cleared channel this cycle, so a trial spent on one
        // never tests anything — it just burns a strike from a five-attempt
        // budget. The give-up branch is silent, so an agent parked in draft
        // for a few days would come back permanently un-resumable with nothing
        // in the log to say why.
        if (agent.status !== 'active') continue;

        /**
         * ONE AGENT'S DISK FAULT MUST NOT COST EVERY AGENT ITS POLL
         * (obi-wan `453be3795d0f`).
         *
         * `recordAttempt`, `forget`, `noteGaveUpLogged` and `clearQuarantine`
         * all call `atomicWriteFileSync`, and this whole block sits ABOVE the
         * `for (const agent of processOrder)` loop. A throw - ENOSPC, EACCES,
         * EROFS on configDir, all plausible on a host that also writes pane
         * logs and transcripts - reached only the outer handler, which logged a
         * generic "Poll error:" and bumped the backoff. `processAgent` then ran
         * for NO agent that cycle: no respawns, no channel convergence. And
         * because auto-resume writes on a schedule, the condition repeated every
         * poll for as long as the disk fault lasted.
         *
         * The store's READ path already fails open by design; this is the
         * equivalent for the write path, and it is PER-AGENT rather than around
         * the whole block so one bad agent does not cost the others their trial
         * either.
         *
         * Logged every time rather than latched: a write failure here is rare,
         * actionable, and precisely the thing that used to be invisible.
         */
        try {

          const quarantineEntries = channelQuarantineStore().getQuarantineEntries(agent.code_name);
          const quarantinedKeys = new Set(quarantineEntries.map((e) => e.serverKey));

          // ENG-10510 (Obi-Wan on #6313): FORGET THE STRIKES OF A TRIAL THAT WORKED.
          //
          // `recordAttempt` only increments and `forget` had exactly one caller —
          // the operator re-enable above — so a successful trial left its strikes
          // on disk forever. The five-attempt cap was therefore a LIFETIME budget
          // per (agent, channel), not a per-episode one: a channel that broke,
          // auto-recovered, and broke again weeks later for an unrelated reason
          // would start at an 8h wait instead of 2h, and after five lifetime
          // trials would never auto-resume again. Invisibly, because the give-up
          // branch deliberately logs nothing.
          //
          // The `forget` docblock already states this reasoning for the operator
          // case. A successful automatic trial is the same evidence that the
          // fault cleared, and it was not acting on it.
          //
          // Swept HERE rather than in the trial loop below because a recovered
          // channel has no quarantine entry, so that loop can never reach it.
          for (const [storedKey, history] of channelResumeHistoryStore().entriesFor(agent.code_name)) {
            const isShadowKey = storedKey.startsWith('shadow:');
            if (isShadowKey !== (autoResumeEffective === 'shadow')) continue;
            const serverKey = isShadowKey ? storedKey.slice('shadow:'.length) : storedKey;
            if (
              !shouldForgetResumeHistory({
                isQuarantined: quarantinedKeys.has(serverKey),
                history,
                now: nowMs,
                // THE CONFIGURED DWELL, NOT THE FLOOR (obi-wan `7414dbf3d758`).
                // A host that raised AGT_CHANNEL_QUARANTINE_DWELL_MS pushes
                // re-quarantine PAST the floor-derived grace, so a failed trial
                // read as a success and wiped the strikes - restarting the
                // five-attempt budget every episode, forever.
                configuredDwellMs: channelQuarantineDwellMs(),
              })
            ) {
              continue;
            }
            channelResumeHistoryStore().forget(agent.code_name, storedKey);
            log(
              `[channel-auto-resume] trial SUCCEEDED for '${serverKey}' on ` +
                `'${agent.code_name}' — un-quarantined for ` +
                `${Math.round((nowMs - history.lastAttemptAt) / 60_000)}m, clearing ` +
                `${history.attempts} strike(s) so the next episode starts from a full budget` +
                (autoResumeEffective === 'shadow' ? ' (shadow bookkeeping)' : ''),
            );
          }

          for (const entry of quarantineEntries) {
            const historyKeyFor = (key: string): string => resumeHistoryKey(autoResumeEffective, key);
            const historyKey = historyKeyFor(entry.serverKey);
            const history = channelResumeHistoryStore().get(agent.code_name, historyKey);
            const decision = decideChannelAutoResume({
              quarantinedAt: entry.quarantinedAt,
              history,
              now: nowMs,
            });
            if (decision.action === 'wait') continue;
            if (decision.action === 'give-up') {
              // SAY IT ONCE (Obi-Wan on #6313). The previous version said nothing
              // ever, on the argument that "the marker and its console badge
              // already report it". They report that the channel is QUARANTINED -
              // equally true while a trial is pending in 16h and after auto-resume
              // has permanently abandoned it. Nothing recorded the transition, so
              // an operator could not tell "still retrying" from "given up and
              // left for you", which is the state the flag's own description
              // promises to hand them.
              //
              // The original concern was real - one line per poll on a dead
              // channel is noise - so this is a once-per-episode latch, the same
              // shape the reaper uses for `gaveUpLogged` / `quarantineLogged`. The
              // latch lives in the history entry, so it resets exactly when the
              // strikes do: an operator Re-enable, or a trial that succeeded.
              if (!channelResumeHistoryStore().noteGaveUpLogged(agent.code_name, historyKeyFor(entry.serverKey))) {
                // QUALIFIED IN SHADOW, like its two siblings (obi-wan
                // `fa6e14d65bab`). This branch runs BEFORE the shadow check
                // below, so in shadow it asserted "5 trials ... all ended in
                // re-quarantine" when no trial ever ran - the marker is never
                // cleared, so re-quarantine is guaranteed by construction rather
                // than observed - and "it will NOT be retried automatically
                // again" when the strikes live in the `shadow:` namespace and
                // flipping to enforce starts at attempt 1. The line's central
                // claim was false in the mode where it is most likely to be read,
                // while the two sibling lines in this same block are both
                // qualified, so an operator reading manager.log during a bake
                // would reasonably take it for a real abandonment.
                log(
                  autoResumeEffective === 'shadow'
                    ? `[channel-auto-resume] WOULD HAVE GIVEN UP on '${entry.serverKey}' for ` +
                        `'${agent.code_name}' — ${decision.attempts} shadow trials recorded, which is ` +
                        `the cap. No trial ran and nothing is abandoned: the strikes are in the shadow ` +
                        `namespace, so enforcing would start this key at attempt 1 (shadow bookkeeping)`
                    : `[channel-auto-resume] GIVING UP on '${entry.serverKey}' for ` +
                        `'${agent.code_name}' — ${decision.attempts} trials over ` +
                        `~2 days all ended in re-quarantine, so it will NOT be ` +
                        `retried automatically again. It stays quarantined until a ` +
                        `human presses Re-enable, which also clears the strike count.`,
                );
              }
              continue;
            }
            // Obi-Wan on #6313: waitedMs is `now - max(quarantinedAt, lastAttemptAt)`
            // — deliberately, so a failed trial restarts the clock. Both log lines
            // used to render it as "quarantined Xm ago", which on any attempt after
            // the first reports the BACKOFF INTERVAL and calls it the age of the
            // quarantine. An operator triaging a channel broken for three days saw
            // "quarantined 240m ago". The two numbers are now named separately,
            // because the age is the one a human uses to judge how long it has
            // been broken and the wait is the one that explains why it fired now.
            const quarantineAgeMin = Math.round((nowMs - entry.quarantinedAt) / 60_000);
            const waitedMin = Math.round(decision.waitedMs / 60_000);
            // RECORD BEFORE CLEARING (CodeRabbit on #6313). `clearQuarantine`
            // persists the marker deletion; if the sidecar write then throws, the
            // error reaches the poll's broad catch with the marker already gone
            // and no durable attempt count — so the next poll re-adds the channel
            // with the episode's history reset, granting trials beyond the cap.
            // Recording first inverts the failure: a sidecar write that fails
            // leaves the marker in place and costs one wasted strike, which can
            // only lengthen the next wait, never shorten it.
            //
            // Recorded REGARDLESS of whether a marker is found, for the same
            // conservative reason: a missing marker means something else cleared
            // it in the same poll, and counting the trial anyway is the safe
            // direction.
            const recorded = channelResumeHistoryStore().recordAttempt(
              agent.code_name,
              historyKey,
              nowMs,
            );
            if (autoResumeEffective === 'shadow') {
              // Shadow RECORDS (into its own namespace, see resumeHistoryKey) so
              // the preview actually escalates 2/4/8/16/24h and stops at the cap,
              // instead of logging an unbounded stream of first attempts that
              // models nothing an operator would be flipping on.
              log(
                `[channel-auto-resume] WOULD resume '${entry.serverKey}' on ` +
                  `'${agent.code_name}' (attempt ${recorded.attempts}/${AUTO_RESUME_MAX_ATTEMPTS}, ` +
                  `quarantined ${quarantineAgeMin}m ago, waited ${waitedMin}m since the last trial) ` +
                  `— shadow mode, not acting`,
              );
              continue;
            }
            const cleared = channelQuarantineStore().clearQuarantine(
              agent.code_name,
              entry.serverKey,
            );
            log(
              `[channel-auto-resume] ${cleared ? 'resumed' : 'no marker for'} ` +
                `'${entry.serverKey}' on '${agent.code_name}' for a TRIAL run ` +
                `(attempt ${recorded.attempts}/${AUTO_RESUME_MAX_ATTEMPTS}, quarantined ` +
                `${quarantineAgeMin}m ago, waited ${waitedMin}m since the last trial). ` +
                `The reaper will re-quarantine it if it is still dead.`,
            );
          }
        } catch (err) {
          // WHAT THIS CANNOT PROMISE (obi-wan `1b670af549c4`). The previous
          // wording said "markers and strike counts are unchanged", which is
          // false: the guarded block walks every history entry and every
          // quarantine entry for this agent, and each of forget /
          // recordAttempt / noteGaveUpLogged / clearQuarantine is its own
          // atomic write. A throw on the third quarantine entry leaves the
          // first two recorded and their markers already cleared, and a throw
          // inside clearQuarantine leaves that key's strike recorded - which
          // the RECORD BEFORE CLEARING comment intends, and the old line
          // denied. Telling an operator no state moved during a disk incident,
          // when some of it did, is worse than telling them nothing.
          //
          // It also blamed "a sidecar write" for every failure, when
          // clearQuarantine writes channel-quarantine.json. Both files are
          // named so the next step is obvious.
          log(
            `[channel-auto-resume] a write FAILED partway through '${agent.code_name}' — ` +
              `${err instanceof Error ? err.message : String(err)}. Skipping the REST of ` +
              `auto-resume for this agent this cycle; every other agent still gets its poll. ` +
              `State is PARTIAL, not untouched: entries processed before the throw have already ` +
              `been written (channel-resume-history.json, channel-quarantine.json). Every strike ` +
              `is recorded before its marker is cleared, so the failure direction is a wasted ` +
              `strike rather than a trial nobody paid for - but do not read this as "nothing ` +
              `happened".`,
          );
          continue;
        }
      }
    }

    // Ensure framework binaries are installed/upgraded for any new frameworks
    const frameworksThisCycle = new Set(agents.map((a) => a.framework).filter(Boolean));
    for (const fw of frameworksThisCycle) {
      await ensureFrameworkBinary(fw!);
    }

    // Track which channels have at least one active agent per profile
    activeChannels.clear();

    // Update state agents list
    const agentStates: AgentState[] = [];

    // ENG-7401 Phase 2b: front-load agents the restart-consumption loop just
    // stopped (restartAcks) so their respawn follows the kill by seconds instead
    // of up to a full cycle. Flag-gated (fast-mcp-restart-respawn) so it lights up
    // with the Phase 2 fast respawn; when off (or nothing was restarted) the order
    // is unchanged. `agents` itself is left intact for the post-loop restartAcks
    // filters (ENG-7328 diagnostics flush).
    const fastRespawn = hostFlagStore().getBoolean('fast-mcp-restart-respawn');
    const processOrder = fastRespawn
      ? reorderRestartedFirst(agents, new Set(restartAcks.keys()))
      : agents;

    // ENG-9057: one managed-toolkit fetch for the whole host per cycle instead
    // of one per agent. Built from `agents` rather than `processOrder` so the
    // request set is independent of the fast-respawn reordering — the two hold
    // the same ids, and keying on the unordered source makes that obvious.
    // Lazy: nothing is requested unless an agent actually reaches the
    // `.mcp.json` convergence step.
    const managedToolkits = createManagedToolkitReader(agents.map((a) => a.agent_id));
    // ENG-9140: reset alongside the reader so the ledger's scope is exactly the
    // cycle the reader serves — the unit in which a cross-agent prune is
    // meaningful.
    managedPruneLedger = createManagedPruneLedger();

    // ENG-9585: measure the pass. See the counters' declaration — this exists to
    // settle whether the cycle is network-bound or CPU-bound with a number
    // instead of an estimate, since that is what decides how much the fetch
    // spacing actually buys.
    const cycleStartedAt = Date.now();
    pollCycleFetches = 0;
    pollCycleFetchSkips = 0;
    pollCycleIntegrationsFetches = 0;
    pollCycleIntegrationsSkips = 0;
    pollCycleQuarantinedFetches = 0;
    pollCycleQuarantinedSkips = 0;
    pollCycleMemoriesUnchanged = 0;
    pollCycleMemoriesFull = 0;
    // ENG-9697: the board counters live on the cache itself, because that is
    // where the hit/miss branch is taken — see `kanban-board-token.ts`. Reset
    // here so the numbers on the log line below are per-cycle, exactly like the
    // memories pair beside them.
    boardTokenCache.resetStats();

    for (const agent of processOrder) {
      // ENG-7335: the fast-restart lane is mid-respawn for this agent - do NOT let
      // processAgent observe the transiently-killed session and respawn it a
      // second time, nor overwrite the lane's lastRestartProcessedAt when
      // agentStates replaces state.agents at end of cycle. Carry the existing
      // state row forward; the next poll (lane done) processes it normally. If
      // there's no prior row (agent brand-new this cycle) we fall through - the
      // lane's per-agent lock + startPersistentSession's "already running" no-op
      // still guard against a double spawn.
      if (restartInFlight.has(agent.agent_id)) {
        const existing = state.agents.find((a) => a.agentId === agent.agent_id);
        if (existing) {
          agentStates.push(existing);
          continue;
        }
      }
      try {
        await processAgent(agent, agentStates, managedToolkits);
      } catch (err) {
        log(`Error processing agent '${agent.code_name}': ${(err as Error).message}`);
        // Preserve existing state for this agent on error
        const existing = state.agents.find((a) => a.agentId === agent.agent_id);
        if (existing) {
          agentStates.push(existing);
        } else {
          agentStates.push({
            agentId: agent.agent_id,
            codeName: agent.code_name,
            status: agent.status,
            charterVersion: '',
            toolsVersion: '',
            secretsHash: null,
            lastRefreshAt: null,
            lastProvisionAt: null,
            lastDriftCheckAt: null,
            lastSecretsProvisionAt: null,
            acpSessions: [],
          });
        }
      }
      // ENG-7401 Phase 2c: this agent was just restarted (front-loaded by Phase
      // 2b) and processAgent has now respawned it - flush its fresh startedAt
      // out-of-band NOW so the restart/integration modal confirms "back online"
      // within seconds of the fast respawn, instead of waiting for the
      // end-of-cycle ENG-7328 flush that only fires after every other agent is
      // processed (~a full cycle later). Guarded on the session actually being
      // healthy so a failed respawn doesn't flush a stale/absent session.
      if (fastRespawn && restartAcks.has(agent.agent_id) && isSessionHealthy(agent.code_name)) {
        flushRestartedAgentDiagnostics(hostId, [agent.code_name]);
      }
      // ENG-7429: a dashboard restart (the path an integration ADD takes) doesn't
      // arm the ENG-6174 restart-verify, so the drift-path prompt-probe hook never
      // fires for it. Arm the force-due prompt-probe budget here instead, once this
      // just-restarted agent (restartAcks) is confirmed healthy - the analogous
      // "respawned, MCP re-read" signal. Flag-gated; the ~1h rolling probe is the
      // fallback when unarmed. Independent of fastRespawn (that only front-loads
      // ordering) so it still arms on a slow respawn.
      // ENG-10601: `sessionHealthyAndUnblocked`, NOT `isSessionHealthy`. A session
      // parked on Claude Code's login picker passes the narrow health check - the
      // tmux session exists and a claude process is alive inside it - while having
      // spawned no MCP child at all (session-blocked-prompt.ts:5). Arming the
      // force-due budget there fires PROMPT_BIND_PROBE_ATTEMPTS all-rows-due
      // probes, which reaches the 3-failure quarantine threshold in two poll ticks
      // instead of three hours. The witness gate makes those probes report
      // `unknown` rather than `missing`, so this is belt-and-braces - but not
      // arming a burst of probes at a session that cannot answer them is right on
      // its own merits, and the sibling arming site at the restart-verify hook was
      // already moved to this predicate by ENG-10335. The two now agree.
      if (
        restartAcks.has(agent.agent_id) &&
        sessionHealthyAndUnblocked(agent.code_name, isSessionHealthy) &&
        hostFlagStore().getBoolean('session-tool-probe')
      ) {
        promptBindProbeAttempts.set(agent.code_name, PROMPT_BIND_PROBE_ATTEMPTS);
      }
    }

    // Snapshot once: three template reads of a live object could otherwise
    // straddle a concurrent resolve and report a set that never existed.
    const boardStats = boardTokenCache.stats();

    // One line per pass. `fetched`/`skipped` are refresh-payload decisions;
    // agents is the denominator so a per-agent cost can be derived without
    // needing to know the host's roster.
    log(
      `[poll-cycle] agents=${processOrder.length} refresh_fetched=${pollCycleFetches} ` +
        `refresh_skipped=${pollCycleFetchSkips} ` +
        // ENG-9584: memories_unchanged / memories_full is the conditional
        // request's hit rate. `full + unchanged` is the number of passes that
        // reached the network at all, so it is strictly <= agents and does not
        // count the ones the ENG-9585 TTL deferred.
        `memories_unchanged=${pollCycleMemoriesUnchanged} memories_full=${pollCycleMemoriesFull} ` +
        // ENG-9697: the `/host/my-kanban` conditional request's hit rate. Same
        // reason the memories pair exists — server-side an unchanged board and
        // an empty board are indistinguishable (~90 bytes either way), so this
        // is the only place the feature can be shown to be working rather than
        // merely deployed. `board_refetch` is the teardown race and reads 0.
        `board_unchanged=${boardStats.unchanged} board_full=${boardStats.full} ` +
        `board_refetch=${boardStats.refetch} ` +
        // ENG-10061: the two integration lanes, on their own axes. Deliberately
        // NOT folded into the refresh pair above, which the comment there
        // defines as refresh-payload decisions. `quarantined_*` reads 0/0 while
        // the `connectivity-probe` flag is off — that is the lane never running,
        // which is a different fact from the TTL deferring it.
        `integrations_fetched=${pollCycleIntegrationsFetches} ` +
        `integrations_skipped=${pollCycleIntegrationsSkips} ` +
        `quarantined_fetched=${pollCycleQuarantinedFetches} ` +
        `quarantined_skipped=${pollCycleQuarantinedSkips} ` +
        `duration_ms=${Date.now() - cycleStartedAt}`,
    );

    // ENG-5445: forward Claude Code's host-wide activity counts from
    // `~/.claude/stats-cache.json` to /host/activity-observations. Once
    // per cycle (not per-agent) — the cache is host-scoped, every
    // session on the machine contributes to the same numbers. The
    // monitor handles its own rate-limiting and the API endpoint
    // upserts on (host_id, date), so calling here every cycle is
    // safe. Fire-and-forget — observability writes must not slow the
    // poll cycle.
    void maybeReportActivityCache({ api, log });

    // ENG-4947: stamp lastRestartProcessedAt on the AgentState rows produced
    // above so the next poll doesn't replay the same restart. Done after
    // processAgent so the timestamp applies even when this was the agent's
    // first appearance on the host (no prior state to mutate up-front).
    // Persist to disk IMMEDIATELY (don't wait for end of cycle) so a crash
    // between here and the cycle's end-of-cycle state-update can't replay
    // the restart on the next manager boot.
    //
    // ENG-5008: carry forward `lastRestartProcessedAt` from `state.agents`
    // (the prior cycle's state) for every agent in this cycle's
    // agentStates, regardless of whether `restartAcks` has a fresh
    // timestamp this poll. `processAgent` rebuilds AgentState from scratch
    // on every successful path (line 4416+), so without this copy-forward
    // the field gets dropped any cycle where a previously-acked agent
    // doesn't have a brand-new restart signal — and the very next cycle
    // sees `prev?.lastRestartProcessedAt = null`, the timestamp comparison
    // fails, and the manager re-fires the kill. That's the loop that
    // pinned vigil/vera/stirling for hours on 2026-05-12.
    // CodeRabbit (PR #953): key the immediate write off whether any
    // ack state actually changed this cycle, not just fresh signals.
    // A carry-forward repair after a prior write-failure should also
    // persist immediately to close the same crash-replay window.
    // ENG-9140 AC5: emitted once the whole agent loop has run, because the fact
    // being reported is a property of the CYCLE, not of any one agent. Placed
    // before the state writes so it lands in the log even if a later step in
    // the cycle throws.
    const crossAgentPrune = managedPruneLedger.summarize();
    if (crossAgentPrune) log(crossAgentPrune);

    const restartAckStateChanged = applyRestartAcks({
      agentStates,
      priorAgents: state.agents,
      restartAcks,
    });
    if (restartAckStateChanged) {
      try {
        const ackedState: ManagerState = { ...state, agents: agentStates };
        // ENG-5865 — atomic write so the synthetic probe never reads a torn
        // restart-ack state mid-write (this site fires off the hot path).
        atomicWriteFileSync(getStateFile(), JSON.stringify(ackedState, null, 2));
      } catch (err) {
        // Non-fatal — the end-of-cycle state-update will retry the write.
        // Worst case: a crash between here and end-of-cycle replays the
        // restart on next boot, which is the pre-fix behaviour anyway.
        log(`[restart] failed to persist ack immediately: ${(err as Error).message}`);
      }
    }

    // ENG-7328: when we serviced a restart this cycle, processAgent above already
    // killed the old session and respawned a fresh one, stamping a new
    // session.startedAt. But the /host/heartbeat that carries agent_diagnostics
    // fired EARLIER in this same cycle (before the kill + respawn), so the fresh
    // startedAt would not reach the API until the NEXT poll - a one-poll-interval
    // lag. That lag is what makes the dashboard restart-progress UI sit on
    // "respawning session" and eventually time out instead of confirming "back
    // online" (the poll keys on diagnostics.startedAt advancing past
    // restart_requested_at). Flush an out-of-band, diagnostics-only heartbeat for
    // just the restarted agents so their new startedAt lands within seconds.
    // Fire-and-forget and best-effort: a failed flush simply self-heals on the
    // next poll's regular heartbeat.
    // End-of-cycle backstop: re-flush for any restarted agent (covers the
    // flag-off path, where the per-agent Phase 2c early flush above didn't run,
    // and self-heals a failed early flush). Redundant with the early flush when
    // both fire, which is harmless (a diagnostics-only heartbeat is idempotent).
    if (restartAcks.size > 0) {
      const restartedCodeNames = agents
        .filter((a) => restartAcks.has(a.agent_id))
        .map((a) => a.code_name);
      flushRestartedAgentDiagnostics(hostId, restartedCodeNames);
    }

    // Reconcile channel enabled state per profile.
    // Each agent has its own profile config, so enable/disable within that profile.
    try {
      for (const [channelId, codeNames] of activeChannels) {
        for (const codeName of codeNames) {
          const adapter = resolveAgentFramework(codeName);
          if (adapter.setChannelEnabled) {
            adapter.setChannelEnabled(channelId, true, codeName);
          }
        }
      }
    } catch { /* non-fatal */ }

    // Detect unassigned/deleted agents (were in state but not in current list)
    const currentIds = new Set(agents.map((a) => a.agent_id));
    for (const prev of state.agents) {
      if (!currentIds.has(prev.agentId)) {
        log(`Agent '${prev.codeName}' removed from host (deleted or unassigned)`);
        const adapter = resolveAgentFramework(prev.codeName);
        // Stop the runtime first — that takes the agent process and most of its
        // MCP children with it. The explicit tmux kill inside stopAgentRuntime
        // covers sessions started by a PREVIOUS manager run that this manager's
        // in-memory session Maps don't track.
        //
        // ENG-8092: this is the site that most needs the unconditional both-stop.
        // `prev` comes from `state.agents`, which carries no framework, and the
        // agent is by definition already absent from /host/refresh — so after a
        // manager restart there is no framework to resolve at all, and nothing
        // will ever poll this agent again to retry a missed teardown.
        stopAgentRuntime(prev.codeName, 'removed-from-host');
        // ENG: tear down slack/telegram/direct-chat MCPs explicitly. Channel
        // children sometimes reparent to init when their Claude Code parent
        // exits, and the periodic channel-sweep won't touch them after this
        // point because their codeName is no longer in the manager's active
        // set. Without this, a Telegram poller for the de-provisioned agent
        // can keep running and conflict with whichever new host now owns it.
        killAgentChannelProcesses(prev.codeName, { log });
        // ENG-9491: ship this agent's transcripts to S3 BEFORE teardown. On a
        // host-removal migration the source host is torn down before the daily
        // archiver's next run, so the session open at the moment of migration
        // would otherwise be lost (the sweep only ships files older than it, and
        // this one is newer). Runtime + channel processes are already stopped
        // above, so the transcript JSONL is quiescent — the only writer that
        // survives a dead session, the host-resident WhatsApp inbound route,
        // writes to a different tree, never the transcript. Best-effort: teardown
        // MUST proceed even if this fails, but a failure is logged loudly inside
        // the flush (never silently treated as success). The try/catch guards
        // only against an unexpected throw; the flush itself never throws.
        try {
          const flush = await flushAgentTranscriptsOnDrain(
            prev.codeName,
            drainTranscriptFlushDeps(log),
          );
          if (flush.status === 'flushed') {
            log(
              `[drain-flush] shipped ${flush.uploaded} transcript file(s) for '${prev.codeName}' before teardown, ` +
                `manifest at '${flush.manifestKey}' (ENG-9491/ENG-9566)`,
            );
          } else if (flush.status === 'no-transcripts') {
            log(
              `[drain-flush] no transcripts on disk for '${prev.codeName}' — nothing to flush (ENG-9491)`,
            );
          }
          // no-address / no-manifest / partial / failed are already logged
          // loudly by the flush. ENG-9566: 'no-manifest' in particular is NOT a
          // success — the bytes are in S3 but nothing records the session pin or
          // the source layout, so no destination can restore them.
        } catch (err) {
          log(
            `[drain-flush] FAILED for '${prev.codeName}': ${(err as Error).message} — proceeding to teardown; ` +
              `this session was NOT shipped (ENG-9491)`,
          );
        }
        const agentDir = join(adapter.getAgentDir(prev.codeName), 'provision');
        await cleanupAgentFiles(prev.codeName, agentDir);
        clearAgentCaches(prev.agentId, prev.codeName);
      }
    }

    // ENG-4581: poll for pending Claude Code OAuth pairing sessions across
    // all agents on this host and dispatch each one. Best-effort —
    // failures are logged and the next refresh will retry.
    try {
      await processClaudePairSessions(agents.map((a) => ({
        agentId: a.agent_id,
        codeName: a.code_name,
      })));
    } catch (err) {
      log(`[claude-pair] poll failed: ${(err as Error).message}`);
    }

    // Channel MCP zombie sweep (ENG-4453) — every CHANNEL_SWEEP_INTERVAL_MS,
    // kill surplus per-agent channel MCP processes (orphans reparented to
    // init after a parent Claude Code died). ENG-4434 added signal handlers
    // that *should* prevent this, but on hosts we've still seen channel
    // zombies accumulate; this is the belt-and-braces net.
    if (Date.now() - lastChannelSweepAt >= CHANNEL_SWEEP_INTERVAL_MS) {
      lastChannelSweepAt = Date.now();
      // ENG-6670: EXCLUDE Docker-isolated agents from the host sweep. It anchors
      // channel procs by PPID-chain to the agent's HOST tmux pane pids; an
      // isolated agent's channels run inside its container (chain to container
      // PID-1 claude), so the host sweep mis-classifies them as not-in-live-chain
      // and SIGTERMs LIVE channels -> presence-reaper restarts -> flap (measured
      // on agt-aws-1). There are no HOST orphans to sweep for an isolated agent:
      // its channels exist only in the `--rm` container (in-container liveness is
      // already owned by the Docker-aware presence-reaper), so skipping is correct.
      const agentCodeNames = new Set(
        agentStates.map((a) => a.codeName).filter((c) => isolationMode(c) !== 'docker'),
      );
      sweepChannelProcesses({
        agentCodeNames,
        dryRun: CHANNEL_SWEEP_DRY_RUN,
        log,
      }).catch((err) => {
        log(`[channel-sweep] sweep error: ${(err as Error).message}`);
      });
    }

    // ENG-4705: channel input watchdog. Detect channel-server-injected
    // messages that landed in the Claude Code TUI input buffer but never
    // got their auto-submit Enter (the bug: text sits as `❯ <message>` and
    // never becomes a user turn). Fire Enter for any input that's been
    // unchanged for >5s on a session with no human attached. Cheap — one
    // tmux capture-pane per claude-code agent per poll.
    {
      const claudeCodeAgents = agentStates
        .filter((a) => a.status === 'active')
        .filter((a) => (agentFrameworkCache.get(a.codeName) ?? DEFAULT_FRAMEWORK) === 'claude-code')
        .map((a) => a.codeName);
      // Always call checkChannelInputs — when the list is empty it still
      // performs the per-cycle state cleanup pass for agents that have
      // been paused/revoked since last check.
      checkChannelInputs(claudeCodeAgents, {
        capturePane: (codeName) => {
          try {
            return syncExecFile('tmux', ['capture-pane', '-t', `agt-${codeName}`, '-p'], {
              stdio: ['ignore', 'pipe', 'ignore'],
              timeout: 2_000,
            }).toString();
          } catch {
            return null;
          }
        },
        isClientAttached: (codeName) => {
          try {
            const out = syncExecFile('tmux', ['list-clients', '-t', `agt-${codeName}`], {
              stdio: ['ignore', 'pipe', 'ignore'],
              timeout: 2_000,
            }).toString();
            return out.trim().length > 0;
          } catch {
            // Fail closed: if we can't tell whether a human is attached,
            // assume they are and skip the watchdog action. Better to
            // miss a stuck buffer than to fire Enter while someone is
            // typing.
            return true;
          }
        },
        sendEnter: (codeName) => {
          try {
            syncExecFile('tmux', ['send-keys', '-t', `agt-${codeName}`, 'Enter'], {
              stdio: 'ignore',
              timeout: 2_000,
            });
          } catch {
            // Best-effort watchdog action — never fail the poll cycle on
            // a tmux hiccup. Next cycle will retry.
          }
        },
        // ENG-6017: dialog-dismissal keys — one send-keys call per key so a
        // multi-key sequence can't be wrapped into a single bracketed paste
        // (see defaultArmSender in persistent-session.ts). The inter-key
        // delay is a blocking sleep; dialog sequences are at most two keys
        // with a 300ms gap, well within poll-cycle tolerances.
        sendKeys: (codeName, keys, interKeyDelayMs) => {
          try {
            for (let i = 0; i < keys.length; i++) {
              if (i > 0 && interKeyDelayMs > 0) {
                const view = new Int32Array(new SharedArrayBuffer(4));
                Atomics.wait(view, 0, 0, interKeyDelayMs);
              }
              syncExecFile('tmux', ['send-keys', '-t', `agt-${codeName}`, keys[i]!], {
                stdio: 'ignore',
                timeout: 2_000,
              });
            }
          } catch {
            // Best-effort — the dialog will still be there next cycle.
          }
        },
        log,
        now: () => Date.now(),
        // ENG-6058: persist the give-up for the agent's channel servers —
        // their periodic sweeps turn it into a throttled user-facing
        // "please resend" notice for every conversation with an undrained
        // pending-inbound marker. Best-effort: a write failure is logged by
        // the watchdog, never fails the poll cycle.
        signalGiveUp: (codeName) => {
          const dir = join(homedir(), '.augmented', codeName);
          if (!existsSync(dir)) return;
          // CR on PR #1824: atomic (tmp + fsync + rename) — a torn JSON
          // parses as "no signal" downstream and would silently suppress
          // the user-facing notice.
          atomicWriteFileSync(
            join(dir, 'watchdog-give-up.json'),
            JSON.stringify({ gave_up_at: new Date().toISOString() }),
          );
        },
        // ENG-9006: the agent is parked on Claude Code's usage-limit modal.
        //
        // Reuses the ENG-6058 signal file rather than adding a second channel —
        // the same precedent as the transient-overload wedge path above. What
        // differs is the `reason`, which makes the channel servers post "I've
        // reached my usage limit, your message is queued" instead of the
        // generic "please resend": the message is NOT lost, and telling a
        // customer to resend into a blocked pane is exactly what the ENG-9006
        // capture shows them already doing unprompted, four times.
        //
        // `resets_hint` is the modal's own reset text, passed through verbatim.
        // It is the only place Claude Code gives us a DATED reset; the banner
        // parser elsewhere can only reconstruct an hour (ENG-8901).
        signalUsageLimit: (codeName, resetsHint) => {
          // ADR-0049 / ENG-7891: resolve through the framework seam, NOT by
          // re-joining '.augmented' with a codename. The per-agent host dir is
          // agent_id-keyed and the codename path is only a compatibility
          // symlink, so an inline join is one more site the migration has to
          // sweep and one more way to land on the wrong side of it. The
          // adjacent signalGiveUp still has the old literal — it is
          // grandfathered in the ADR-0049 appendix inventory, not a precedent.
          //
          // Safe to hard-code 'claude-code' here: this whole watchdog block is
          // built from `claudeCodeAgents`, filtered on that framework above.
          const dir = getFramework('claude-code').getAgentDir(codeName);
          if (!existsSync(dir)) return;
          atomicWriteFileSync(
            join(dir, 'watchdog-give-up.json'),
            JSON.stringify({
              gave_up_at: new Date().toISOString(),
              reason: 'usage_limit',
              ...(resetsHint ? { resets_hint: resetsHint } : {}),
            }),
          );
        },
      }, {
        // ENG-6055: heal-keystroke escalation. 'disturb' (default) retries
        // with x→BSpace→Enter after the first bare Enter fails — a bare
        // retry repeats the exact keystroke the stuck TUI state eats
        // (kylie 2026-06-05). AGT_INPUT_HEAL_MODE=bare reverts to the
        // pre-ENG-6055 behaviour if a future CC/tmux pairing misbehaves.
        healMode: process.env.AGT_INPUT_HEAL_MODE === 'bare' ? 'bare' : 'disturb',
      });
    }

    // ENG-5614: reconcile the realtime socket vs the channel-subscription
    // flag BEFORE deciding whether to poll. A half-open socket leaves the
    // flag stale-true, which would otherwise skip the poll fallback and
    // silently drop inbound direct-chat. On divergence this forces the
    // flag false (and a reconnect next tick) so the poll fallback runs now.
    healRealtimeSocketDivergence();

    // Direct chat: Realtime delivers claude-code direct-chat when connected,
    // with polling as the fallback when it is down. opencode agents ALWAYS poll,
    // even while realtime reports "connected": the manager is their sole delivery
    // path (opencode has no in-session channel MCP), and the host-JWT realtime
    // subscription cannot pass the direct_chat_messages RLS SELECT, so
    // postgres_changes stays SUBSCRIBED but silently delivers nothing (claude
    // direct-chat is unaffected because it is delivered by its in-session MCP).
    // The poll claims rows server-side, so it never double-delivers a message
    // already handled via realtime.
    if (!isRealtimeConnected()) {
      pollDirectChatMessages(agentStates).catch((err) => {
        log(`Direct chat poll error: ${(err as Error).message}`);
      });
    } else {
      const opencodeAgents = agentStates.filter(
        (a) => (agentFrameworkCache.get(a.codeName) ?? DEFAULT_FRAMEWORK) === 'opencode',
      );
      if (opencodeAgents.length > 0) {
        pollDirectChatMessages(opencodeAgents).catch((err) => {
          log(`Direct chat poll error (opencode): ${(err as Error).message}`);
        });
      }
    }

    // Start Realtime subscription if not yet started and we have Supabase config
    ensureRealtimeStarted(agentStates);
    ensureRealtimeDriftStarted(agentStates);
    ensureRealtimeAssignStarted(agentStates);
    ensureRealtimeConfigStarted(agentStates);
    ensureRealtimeKanbanStarted(agentStates);
    ensureRealtimeIntegrationContextStarted(agentStates);

    // Spawn due recurring kanban templates
    try {
      const spawnData = await api.post<{ spawned: number }>('/host/kanban/recurring/spawn');
      if (spawnData.spawned > 0) {
        log(`Spawned ${spawnData.spawned} recurring kanban item(s)`);
      }
    } catch { /* non-fatal */ }

    state = {
      ...state,
      lastPollAt: new Date().toISOString(),
      pollCount: state.pollCount + 1,
      agents: agentStates,
      // ENG-5441: serialise trip state on every poll so manager restarts
      // never silently clear a tripped breaker. Cheap — only tripped
      // entries are stored, and the typical state is zero entries.
      circuitBreakerTrips: restartBreaker.serialize(),
      // ENG-6088: spent auto-resume credits survive manager restarts too.
      circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
    };

    // ENG-5041: cycle completed — clear the backoff counter so the next
    // tick fires at the normal interval. Logging only on the transition
    // (n>0 → 0) so a healthy host doesn't spam recovery messages.
    if (consecutivePollFailures > 0) {
      log(`[poll-backoff] recovered after ${consecutivePollFailures} failure(s), resuming normal interval`);
      consecutivePollFailures = 0;
    }
    // ENG-6174: after the cycle's session work, verify any hot-reload-MCP
    // restarts actually respawned healthy — surfacing a stalled restart instead
    // of the pre-fix silent fire-and-forget.
    verifyPendingRestarts(Date.now());
    send({ type: 'state-update', state });
  } catch (err) {
    state.errorCount++;
    const message = (err as Error).message;
    log(`Poll error: ${message}`);
    send({ type: 'error', message });

    // ENG-5041: stay running and back off in-process instead of exiting
    // for the watchdog. Auth and 5xx failures are usually transient (key
    // rotation, API restart, prod outage) and the watchdog restart loop
    // was both slow and noisy — see the 2026-05-12 prod-down where the
    // S3 SDK regression made every /host/exchange return 500 for ~30
    // min and agt-aws-1 thrashed the whole time.
    consecutivePollFailures += 1;
    // CR (CodeRabbit on PR #982): log the *actual* scheduled delay (which
    // scheduleNext computes as max(intervalMs, backoffMs)) rather than the
    // raw backoff value, so the log line agrees with what really happens.
    // Use `_ms` suffix instead of seconds-with-s so downstream parsers
    // (Loki, awk, etc.) don't have to strip units.
    const scheduledDelayMs = currentEffectivePollDelayMs();
    const errorClass = classifyPollError(message);
    log(`[poll-backoff] attempt=${consecutivePollFailures} class=${errorClass} next_retry_ms=${scheduledDelayMs}`);
  }
}

async function getOrCacheRegisteredAgents(adapter: FrameworkAdapter, profile?: string): Promise<Set<string>> {
  const cacheKey = profile ? `${adapter.id}:${profile}` : adapter.id;
  let cached = registeredAgentsCache.get(cacheKey);
  if (!cached) {
    cached = await adapter.getRegisteredAgents(profile);
    registeredAgentsCache.set(cacheKey, cached);
  }
  return cached;
}

/**
 * ENG-9057: one managed-toolkit row as `/host/managed-toolkits` returns it.
 *
 * Named (rather than left inline on the request generic) so the batch reader
 * and the per-agent fallback are provably returning the same row shape.
 * `mcp_url` / `mcp_headers` were previously reached through an unchecked
 * `tk as Record<string, unknown>` cast at the call site; they are modelled here
 * instead. Both remain optional — a provider that mints no direct MCP URL
 * leaves them absent and the call site falls back to `proxy_url`, exactly as
 * the cast did.
 */
interface ManagedToolkitEntry {
  agent_id: string;
  toolkit_id: string;
  toolkit_name: string;
  proxy_url: string;
  // ENG-7543 (Phase 4): a toolkit can surface N connections, each its own MCP
  // server. The API precomputes the per-connection server key
  // (`mcp_server_key`); older API builds omit it, so the call site falls back
  // to the historical toolkit-level derivation.
  connection_key?: string;
  mcp_server_key?: string;
  mcp_url?: string;
  mcp_headers?: Record<string, string>;
}

/**
 * ENG-9140: thrown when `/host/managed-toolkits` answers 200 with a body that is
 * not this endpoint's response.
 *
 * A distinct type rather than a bare Error so the batch reader's catch can log
 * it as what it is — an untrustworthy answer — instead of filing it with genuine
 * transport failures.
 */
export class UntrustworthyToolkitResponseError extends Error {
  constructor(detail: string) {
    super(`/host/managed-toolkits returned an untrustworthy body: ${detail}`);
    this.name = 'UntrustworthyToolkitResponseError';
  }
}

/**
 * ENG-9140 — "we could not get an answer" must never be spelled `[]`.
 *
 * This used to end `return data.toolkits ?? []`, and that single `??` is the
 * amplifier that cost all 16 agents on prod-integritylabs-agt-aws-1 about 90
 * minutes of total integration loss on 2026-08-18. The full chain:
 *
 *   1. `handleResponse` (api-client.ts) does
 *      `await res.json().catch(() => ({}))` and only THEN checks `res.ok`. A 200
 *      whose body is not JSON — a proxy HTML error page, a gateway interstitial,
 *      a truncated response — therefore resolves to `{}` as a SUCCESSFUL answer.
 *   2. `{}.toolkits` is undefined, and `?? []` asserts "there are none".
 *   3. `createManagedToolkitReader` seeds every requested agent from that empty
 *      list, so each reads as covered-and-empty rather than absent.
 *   4. The convergence prune removes every managed MCP server not in the
 *      expected set — which is now nothing — from every agent on the host.
 *
 * Every step is a successful write. No error, no non-zero exit, no alarm, and
 * `.mcp.json` stays valid JSON and is merely smaller. Detection was a human
 * noticing their agents had lost their tools.
 *
 * The distinction that was missing is not covered-vs-absent (the reader already
 * models that, correctly, and honouring a real `[]` is what makes revocation
 * take effect — property 3, which ENG-7994 depends on). It is
 * TRUSTWORTHY-EMPTY vs UNTRUSTWORTHY-EMPTY. A field that is absent, or present
 * as something other than an array, is not an empty list; it is the absence of
 * an answer, and it must reach the caller as a failure so the batch reader falls
 * back to per-agent fetches — the path ENG-9057 already built and tested for
 * exactly this "do not let one bad response cost the whole host" reason.
 *
 * Note what is deliberately NOT done here: a well-formed `{ toolkits: [] }` is
 * still honoured as a genuine empty and still prunes. Refusing to act on a real
 * revocation would be the opposite failure, and a silently-retained MCP entry
 * for a revoked toolkit wedged a prod agent's turns once already (nora,
 * 2026-07-22, ENG-7994).
 */
export interface ManagedToolkitFetchResult {
  toolkits: ManagedToolkitEntry[];
  /**
   * The agents the RESPONSE says it answered for.
   *
   * `null` means the API build predates ENG-9140 and asserts nothing, which is
   * a different fact from "it asserted an empty list". Callers must treat the
   * two differently — see `coverageOf`.
   */
  coveredAgentIds: string[] | null;
}

export async function fetchManagedToolkits(
  agentIds: readonly string[],
): Promise<ManagedToolkitFetchResult> {
  const data = await api.post<{ toolkits?: unknown; covered_agent_ids?: unknown }>(
    '/host/managed-toolkits',
    { agent_ids: [...agentIds] },
  );

  const toolkits = data?.toolkits;
  if (toolkits === undefined || toolkits === null) {
    // Absent is UNKNOWN, not empty. This is the `{}` an unparseable 200
    // produces, and it is the case that caused the incident.
    throw new UntrustworthyToolkitResponseError('`toolkits` field is missing');
  }
  if (!Array.isArray(toolkits)) {
    throw new UntrustworthyToolkitResponseError(
      `\`toolkits\` is ${typeof toolkits}, expected an array`,
    );
  }

  // `covered_agent_ids` is OPTIONAL, and its absence is tolerated rather than
  // thrown on, because a host can poll an API build older than this change.
  // But a field that is PRESENT and malformed is a shape violation like any
  // other — reading it loosely is how the original `?? []` happened.
  const covered = data?.covered_agent_ids;
  let coveredAgentIds: string[] | null = null;
  if (covered !== undefined && covered !== null) {
    if (!Array.isArray(covered) || covered.some((id) => typeof id !== 'string')) {
      throw new UntrustworthyToolkitResponseError(
        '`covered_agent_ids` is present but is not an array of strings',
      );
    }
    coveredAgentIds = covered as string[];
  }

  return { toolkits: toolkits as ManagedToolkitEntry[], coveredAgentIds };
}

/**
 * ENG-9140 — resolve which agents a response actually answered for.
 *
 * Three inputs, and the order matters:
 *
 *   1. `coveredAgentIds` when the server asserted it. Authoritative: the
 *      handler's authorization gate is all-or-nothing, so a 200 means every id
 *      in that list was resolved and evaluated.
 *   2. Any agent that appears in `toolkits`. A row FOR an agent proves the
 *      response answered for it, whatever the coverage field says or omits.
 *      This is the compatibility path for an older API build.
 *   3. Everything else in `requested`: NOT covered. Unknown, not empty.
 *
 * Step 3 is the whole fix. It used to be step 1 — coverage was seeded from the
 * request, so "we did not hear about this agent" and "this agent has nothing"
 * arrived at the prune wearing the same shape.
 *
 * The old-API path (no assertion) is deliberately the SAFE side of the trade:
 * an agent that is genuinely empty is treated as unknown and re-asked
 * per-agent, costing one extra request per empty agent per cycle until the API
 * ships. That is a partial, temporary retreat from ENG-9057's batching for
 * empty agents only — still far below the pre-ENG-9057 one-request-per-agent —
 * and the per-agent fetch is the well-tested path that answers the question
 * unambiguously for a single id.
 */
export function coverageOf(
  result: ManagedToolkitFetchResult,
  requested: readonly string[],
): { covered: Set<string>; assertedByServer: boolean } {
  const covered = new Set<string>(result.coveredAgentIds ?? []);
  for (const tk of result.toolkits) covered.add(tk.agent_id);
  // Intersect with what we asked for: a server naming an agent we did not
  // request tells us nothing about this cycle, and letting it seed an entry
  // would be a way back into trusting the response's word over our own scope.
  const scoped = new Set([...covered].filter((id) => requested.includes(id)));
  return { covered: scoped, assertedByServer: result.coveredAgentIds !== null };
}

/**
 * ENG-9057: per-cycle managed-toolkit reader.
 *
 * `/host/managed-toolkits` has always been a BATCH endpoint — it takes
 * `agent_ids[]` and the call site has always filtered the response back down
 * per agent, which is the tell that it was written for one. It was being driven
 * one agent at a time, and each of those requests independently re-ran the
 * whole authorization fan-out (`agents`, `hosts`, `host_agents`, `teams`) plus
 * a token verification. On prod that was ~43 reads/sec of each of those tables
 * for a fleet of ~70 agents — the largest single source of control-plane query
 * volume, none of it new information.
 *
 * Three properties are load-bearing:
 *
 *   1. LAZY. Nothing is fetched until an agent actually asks. A host whose
 *      framework adapter has no `writeMcpServer` never reaches the call site,
 *      so it issues zero requests — exactly as before, rather than gaining one
 *      per cycle for an answer nobody reads.
 *
 *   2. FALLS BACK TO THE OLD SHAPE, NOT TO NOTHING. The endpoint fails the
 *      whole request closed if ANY requested agent is unauthorized, so one bad
 *      id would otherwise cost every agent on the host its `.mcp.json`
 *      convergence. On any batch failure this returns to per-agent fetches for
 *      the rest of the cycle, which is precisely the previous behaviour: the
 *      unauthorized agent fails alone and its neighbours are untouched.
 *
 *   3. AN EMPTY LIST IS AN ANSWER. `forAgent` distinguishes "the batch covered
 *      this agent and it has no managed toolkits" (`[]`, honoured — and the
 *      thing that correctly prunes a revoked toolkit's MCP entry) from "the
 *      batch did not cover this agent" (absent, so go and ask). Collapsing the
 *      two would either strip a healthy agent's servers or silently skip a
 *      prune.
 *
 *      ENG-9140 changed WHERE coverage comes from, not what it means. It was
 *      inferred from the REQUEST — every id we asked about was seeded as an
 *      entry — so an agent the response omitted read as covered-and-empty and
 *      was pruned. It is now taken from the RESPONSE (`covered_agent_ids`, plus
 *      any agent that actually appears in `toolkits`); see `coverageOf`.
 */
export interface ManagedToolkitReader {
  forAgent(agentId: string): Promise<ManagedToolkitEntry[]>;
}

/**
 * ENG-9140 AC5 — the fleet-level signal that was missing.
 *
 * Each individual removal has always been logged
 * (`[managed-toolkit] Removed stale MCP server ...`), so a destructive prune is
 * greppable per agent. What no line said is the thing that actually mattered on
 * agt-aws-1: that ONE CYCLE pruned across MANY agents at once.
 *
 * That distinction is the whole diagnosis. Individual prunes are routine — a
 * revoked toolkit produces exactly one, every day, correctly. A single cycle
 * pruning several agents simultaneously is not routine: agents are configured
 * independently, so their toolkits do not get revoked in lockstep. It is far
 * more likely to mean the host was told "there is nothing" by something that
 * did not know. Ninety minutes of the incident was spent establishing by hand
 * what this one line says.
 *
 * Deliberately a REPORT, not a veto. It does not block the prune: refusing to
 * act would strand a genuine mass revocation (an operator disconnecting a
 * shared integration is a real thing), and the shape check in
 * `fetchManagedToolkits` is where untrustworthy answers are stopped. This makes
 * the event findable, which is what nobody had.
 */
export interface ManagedPruneLedger {
  record(agentCodeName: string, serverKey: string): void;
  /** A warning line when more than one agent was pruned this cycle; null otherwise. */
  summarize(): string | null;
}

export function createManagedPruneLedger(): ManagedPruneLedger {
  const byAgent = new Map<string, string[]>();
  return {
    record(agentCodeName: string, serverKey: string) {
      const list = byAgent.get(agentCodeName);
      if (list) list.push(serverKey);
      else byAgent.set(agentCodeName, [serverKey]);
    },
    summarize() {
      // One agent losing servers is ordinary revocation. Two or more in the
      // same cycle is the shape that needs a human to look.
      if (byAgent.size < 2) return null;
      const total = [...byAgent.values()].reduce((n, l) => n + l.length, 0);
      const detail = [...byAgent.entries()]
        .map(([codeName, keys]) => `${codeName}(${keys.join(',')})`)
        .join(' ');
      return (
        `[managed-toolkit][WARN] cross-agent prune: ${total} MCP server(s) removed across ` +
        `${byAgent.size} agents in ONE cycle — agents are configured independently, so a ` +
        `simultaneous revocation is unlikely and this may be an untrustworthy toolkit ` +
        `response rather than a real one (ENG-9140): ${detail}`
      );
    },
  };
}

/**
 * The current cycle's prune ledger. Module-level and reset per cycle, matching
 * the existing per-cycle caches in this file, so the prune site does not need a
 * new parameter threaded through `processAgent`'s signature.
 */
let managedPruneLedger: ManagedPruneLedger = createManagedPruneLedger();

export function createManagedToolkitReader(
  agentIds: readonly string[],
  // Injected so the three properties above can be asserted directly — in
  // particular the REQUEST COUNT, which is the whole point of the change and
  // is not observable from the returned toolkits. Defaulted to the real
  // fetcher, so production wiring exercises the default rather than a
  // test-only path.
  fetchToolkits: (ids: readonly string[]) => Promise<ManagedToolkitFetchResult> = fetchManagedToolkits,
): ManagedToolkitReader {
  const requested = [...new Set(agentIds)];
  // The PROMISE is memoized, not its value, so concurrent callers share one
  // in-flight request rather than racing to issue their own.
  let batch: Promise<Map<string, ManagedToolkitEntry[]> | null> | null = null;

  const loadBatch = (): Promise<Map<string, ManagedToolkitEntry[]> | null> => {
    batch ??= (async () => {
      if (requested.length === 0) return new Map();
      try {
        const result = await fetchToolkits(requested);
        // ENG-9140: seed from what the RESPONSE covered, never from what we
        // asked for. Property 3 below is preserved exactly — a covered agent
        // with no rows is still an entry (`[]`) and still prunes — but the
        // coverage now comes from the answer instead of the question, so an
        // agent the response omitted stays ABSENT and is re-asked rather than
        // being told it has nothing.
        const { covered, assertedByServer } = coverageOf(result, requested);
        const byAgent = new Map<string, ManagedToolkitEntry[]>(
          [...covered].map((id) => [id, [] as ManagedToolkitEntry[]]),
        );
        for (const tk of result.toolkits) byAgent.get(tk.agent_id)?.push(tk);

        const uncovered = requested.length - covered.size;
        if (uncovered > 0) {
          log(
            `[managed-toolkit] batch answered for ${covered.size}/${requested.length} agent(s)` +
            `${assertedByServer ? '' : ' (API build asserts no coverage)'} — the other ` +
            `${uncovered} are UNKNOWN, not empty, and will be re-asked per agent rather ` +
            `than pruned (ENG-9140)`,
          );
        }
        return byAgent;
      } catch (err) {
        log(
          `[managed-toolkit] batch fetch for ${requested.length} agent(s) failed: ` +
          `${(err as Error).message} — falling back to per-agent fetches this cycle (ENG-9057)`,
        );
        return null;
      }
    })();
    return batch;
  };

  return {
    async forAgent(agentId: string): Promise<ManagedToolkitEntry[]> {
      const byAgent = await loadBatch();
      const fromBatch = byAgent?.get(agentId);
      if (fromBatch) return fromBatch;

      // Per-agent fallback. A single-id request narrows the question to the
      // point where the answer is unambiguous, which is why ENG-9057 chose it
      // as the degraded path.
      const single = await fetchToolkits([agentId]);
      const { covered, assertedByServer } = coverageOf(single, [agentId]);
      if (assertedByServer && !covered.has(agentId)) {
        // ENG-9140: the server asserted coverage and left out the ONE agent it
        // was asked about. That is a contradiction, not an answer. Throwing
        // sends this to the convergence block's catch, which skips the agent
        // for this cycle — no write, no prune — instead of handing back an
        // `[]` that reads as revocation.
        throw new UntrustworthyToolkitResponseError(
          `per-agent fetch for '${agentId}' returned a response that does not cover it`,
        );
      }
      // No assertion (an API build predating ENG-9140): a well-formed response
      // to a SINGLE-id request is honoured as that agent's answer, empty
      // included. This is deliberate and is the pre-existing semantics — the
      // ambiguity this issue is about comes from bucketing a BATCH, and the
      // untrustworthy-body case (the `{}` that caused the incident) has already
      // thrown in `fetchManagedToolkits` before reaching here. Refusing an
      // honest empty would strand every genuine revocation until the API ships,
      // which is the ENG-7994 failure in the other direction.
      return single.toolkits.filter((tk) => tk.agent_id === agentId);
    },
  };
}

async function processAgent(
  agent: { agent_id: string; code_name: string; display_name: string; status: string; environment: string; framework?: string; usage_limit_alert_open?: boolean; kill_switch?: { scope: string | null; reason: string | null; source: string | null } | null; enforcement?: 'warn' | 'mute' | null; enforcement_text?: string | null },
  agentStates: AgentState[],
  // ENG-9057: the cycle's shared managed-toolkit reader. Required, not
  // defaulted: a default would be untested dead code today (there is exactly
  // one caller) and would let a future caller silently reintroduce the
  // per-agent fetch this issue removed. Making it explicit forces that choice
  // into the open.
  managedToolkits: ManagedToolkitReader,
): Promise<void> {
  if (!config) return;

  // ENG-8174: resolve the `## Integrations` gate ONCE for this tick.
  //
  // Three consumers below write or strip the same sentinel range —
  // generateArtifacts (the generator) and both writeIntegrations call sites (the
  // side-effect writer) — and they are separated by many awaits. hostFlagStore()
  // is refreshed by the heartbeat, so re-reading the flag per call site would let
  // a mid-tick flip land differently on each: one writer adds the section back
  // while the other strips it, producing exactly the churn this gate exists to
  // stop. Reading once here makes the tick atomic with respect to the flag; a
  // flip simply takes effect on the next poll.
  const renderIntegrationsSection = shouldRenderIntegrationsSection();

  // ENG-7909: materialize the SOFT account-enforcement level (warn/mute) to the
  // per-agent marker the channel adapters read. Runs every poll for every status
  // so a level flip takes effect within one poll and clears the instant it drops
  // back to none (or is superseded by a halt, which arrives as enforcement:null +
  // the kill_switch/paused overlay). Best-effort — a marker write/clear failure
  // never breaks the poll cycle.
  // ENG-8541: the operator-configured copy for the level rides along, so a wording
  // change lands within one poll like a level change does. Absent → no `text` in
  // the marker → the adapters use their built-in default for the level.
  syncAccountEnforcementMarker(
    agent.code_name,
    agent.enforcement ?? null,
    log,
    agent.enforcement_text ?? null,
  );

  // ENG-5080: the per-agent banner used to fire here unconditionally —
  // one line per agent per poll-cycle (10s default), so an idle host with
  // three agents wrote ~26k pure-decoration lines per day to CloudWatch.
  // Every nested log() in this function carries `'${agent.code_name}'`,
  // so removing the banner doesn't cost any forensic context. Keep the
  // map writes; they're cheap and other call sites depend on them.
  //
  // The status-branches below (draft / paused / revoked) similarly used
  // to log unconditionally; they now edge-trigger on `agentState.knownStatuses`,
  // matching the existing "status changed" pattern further down.
  const previousKnownStatus = agentState.knownStatuses.get(agent.agent_id);
  // ENG-9061: one place, evaluated on every poll, so the occupancy roster can
  // never claim to have measured an agent whose runtime the manager tore down.
  // Level-triggered rather than edge-triggered on purpose — the paused/draft
  // branch below returns early, and a kill-switch overlay can arrive without a
  // status EDGE the manager sees, so a transition-based set would miss it.
  if (agent.status === 'active') notRunningCodeNames.delete(agent.code_name);
  else notRunningCodeNames.add(agent.code_name);
  agentState.agentDisplayNames.set(agent.code_name, agent.display_name);
  agentState.codeNameToAgentId.set(agent.code_name, agent.agent_id);
  // ENG-7577: reconcile a held trip against the AUTHORITATIVE status BEFORE the
  // report below. Ordering is load-bearing: a trip hydrated from
  // `manager-state.json` against an agent the operator has already resumed would
  // otherwise be re-POSTed to /host/circuit-breaker/trip and re-pause them.
  maybeClearStaleTrip(agent);
  // ENG-5441 + CodeRabbit on PR #1302: if a trip was hydrated from
  // `manager-state.json` (manager restart with a pre-existing trip),
  // `agentState.codeNameToAgentId` wasn't populated when the breaker's record path
  // first tried to report. Retry now that we know the agent_id.
  // Idempotent — `reportedTrips` short-circuits already-reported trips.
  maybeReportCurrentTrip(agent.code_name);
  // ENG-8679: same race, same remedy — a suppression recorded before this
  // agent had ever been observed has no agent_id, so it was queued rather than
  // dropped. The id is known as of the line above; flush it.
  maybeReportPendingSuppression(agent.code_name);

  // Cache the framework from the agent list response (refreshAgentConfig may override with more specific data)
  if (agent.framework) {
    agentFrameworkCache.set(agent.code_name, agent.framework);
  }

  // ENG-5389: scrape the Claude Code "X% of your weekly limit" banner from
  // pane.log and forward observations to the API. Claude Code only — the
  // banner doesn't exist in other framework runtimes. The monitor handles
  // its own rate-limiting and dedupes against an in-memory last value, so
  // calling it every cycle for every active CC agent is safe. Fire-and-
  // forget — observability writes must not slow the poll cycle.
  if (agent.framework === 'claude-code' && agent.status === 'active') {
    void maybeReportUsageBanner({
      api,
      codeName: agent.code_name,
      agentId: agent.agent_id,
      log,
      // ENG-9634: the monitor's last-seen-banner map is module memory and does
      // not survive a restart, and its clear POST is gated on having seen a
      // banner in THIS process. For a cap that already lifted there is no
      // banner left to see, so the gate could never re-open. This is the
      // memory it lost, carried on the roster we just polled.
      openUsageAlert: agent.usage_limit_alert_open === true,
    });

    // ENG-5516: read per-agent token usage from the session transcripts and
    // upsert it via /host/token-usage so usage is comparable across agents.
    // Same fire-and-forget contract as the banner monitor: self-rate-limited,
    // self-deduped, errors swallowed — must not slow the poll cycle.
    void maybeReportTokenUsage({
      api,
      codeName: agent.code_name,
      agentId: agent.agent_id,
      log,
    });

    // ENG-6314: reconcile accurate workflow-run token totals from the same
    // transcripts (summed over each run's window). Same fire-and-forget
    // contract: self-rate-limited (5-min/agent), errors swallowed.
    void maybeReconcileWorkflowRunTokens({
      api,
      codeName: agent.code_name,
      agentId: agent.agent_id,
      log,
    });

    // ENG-7235: metering-aware cost gate for the two idle, no-deliverable model
    // scorers below. On an OpenRouter-metered host both would be per-token spend
    // (and claude-p would retry-loop "Not logged in"), so they're suppressed
    // unless the operator picks the free host-local backend. Resolved once and
    // applied to both eval + extraction.
    const scorerSuppressed = conversationScorerSuppressed();
    if (scorerSuppressed) {
      logMeteredScorerSuppressed();
    }

    // Score completed end-user conversations for "did the agent help" with a
    // pluggable host-side backend (Haiku via `claude -p` by default, or a fully
    // local model such as Gemma 4 — see resolveConversationEvalBackend). The raw
    // transcript stays on the host; only the verdict crosses the wire. Same
    // fire-and-forget contract: self-throttled (5-min/agent), bounded
    // spawns/cycle, errors swallowed.
    if (!scorerSuppressed) {
      const evalBackend = resolveConversationEvalBackend();
      void maybeEvaluateConversations({
        api,
        backend: evalBackend,
        codeName: agent.code_name,
        agentId: agent.agent_id,
        log,
        onModelCallError: (err) =>
          reportManagerModelCallError('conversation_eval', err, evalBackend.model ?? null),
      });
    }

    // ENG-6200: extract durable memories from completed conversations
    // host-side (the replacement for the dead `sessions`-fed dreaming cron).
    // Same privacy boundary + pluggable backend as conversation eval — the raw
    // transcript stays on the host, only candidate memories cross the wire.
    // Gated by the 'memory-extraction' registry flag (ADR-0022), ships dark.
    // Arm it centrally from the admin Feature Flags page (reaches a running
    // manager within one heartbeat) or per-host via AGT_MEMORY_EXTRACTION_ENABLED=true.
    if (memoryExtractionEnabled() && !scorerSuppressed) {
      const memBackend = resolveConversationEvalBackend();
      void maybeExtractMemories({
        api,
        backend: memBackend,
        codeName: agent.code_name,
        agentId: agent.agent_id,
        log,
        onModelCallError: (err) =>
          reportManagerModelCallError('memory_extraction', err, memBackend.model ?? null),
      });
    }

    // ENG-8575: extract tool-call audit rows from the same transcripts. Same
    // privacy boundary as the two scorers above — the transcript stays on the
    // host and only redacted identity-without-content metadata crosses the
    // wire — but NOT gated by scorerSuppressed: this runs no model at all, so
    // it costs nothing on an OpenRouter-metered host.
    //
    // Gated by the 'tool-call-audit' registry flag (ADR-0022), ships dark. The
    // gate is deliberately OUT here rather than inside maybeScanToolCalls, so
    // that flag-off does literally no work: no salt IO, no session peek, no
    // transcript read (AC3).
    if (toolCallAuditEnabled()) {
      void maybeScanToolCalls({
        api,
        codeName: agent.code_name,
        agentId: agent.agent_id,
        log,
      });
    }
  }

  const now = new Date().toISOString();
  const adapter = resolveAgentFramework(agent.code_name);
  // ENG-4418: agentDir resolves through the adapter so manager + framework
  // share a single provision tree. Initial value uses the cached framework
  // from /host/agents; it is RE-resolved once /host/refresh returns the
  // authoritative framework below (see `agentDir = join(frameworkAdapter...)`
  // after agentFrameworkCache.set). Without the re-resolution, a stale
  // cache would trigger claudecodeAdapter.getAgentDir's migration on the
  // wrong framework's tree.
  let agentDir = join(adapter.getAgentDir(agent.code_name), 'provision');

  // 1a. Status-aware branching
  if (agent.status === 'draft' || agent.status === 'paused') {
    // ENG-9252: no credential sync runs for a draft or paused agent, and this
    // branch returns before every path that could clear the streak — the
    // changed-set clear, the convergence clear, and clearAgentCaches (revoke /
    // teardown only) are all below. A streak left behind therefore has no
    // remover: stuckChannelSyncs walks the whole map without consulting status,
    // so the heartbeat keeps reporting the agent with a growing
    // stuck_for_seconds and the API holds channel_sync_stuck open for a sync
    // that is no longer attempted. Purge on the way out.
    forgetChannelSyncState(agent.agent_id);
    if (previousKnownStatus !== agent.status) {
      log(`Agent '${agent.code_name}' is ${agent.status}, skipping provisioning`);
    }
    // ENG-6146: kill switch. The API overlays status:'paused' + a kill_switch
    // marker when this agent's scope is killed. Halt is handled by the normal
    // paused path below; here we just record WHY (host-side, edge-triggered so
    // the runbook can see it without 10s spam) and remember it so the
    // paused→active resume below can announce the clear.
    if (agent.kill_switch) {
      killPausedCodeNames.add(agent.code_name);
      if (previousKnownStatus !== agent.status) {
        log(
          `[kill-switch] '${agent.code_name}' halted by ${agent.kill_switch.scope ?? 'unknown'} kill switch` +
            (agent.kill_switch.reason ? ` (reason: ${agent.kill_switch.reason})` : '') +
            ` source=${agent.kill_switch.source ?? 'unknown'}`,
        );
      }
    } else if (killPausedCodeNames.delete(agent.code_name)) {
      // CR #1897: the kill switch cleared but the agent is still paused for
      // another reason (operator pause, cost guardrail, circuit breaker). Drop
      // the kill-paused marker so a later operator paused→active resume doesn't
      // emit a misleading "kill switch cleared" notice.
      log(`[kill-switch] '${agent.code_name}' kill switch cleared while still ${agent.status} (no resume — paused for another reason)`);
    }
    // Kill the runtime if it exists (prevents paused agents responding on channels).
    // Must kill by session name, not just via the in-memory Maps — those don't
    // track sessions started by a previous manager run.
    //
    // ENG-8092: this branch re-runs on EVERY ~10s poll for the lifetime of a
    // paused agent, so stopAgentRuntime is idempotent and logs only on the tick
    // that actually kills something.
    stopAgentRuntime(agent.code_name, agent.status); // 'paused' | 'draft'
    // ENG-6088: a circuit-breaker-paused agent gets ONE auto-resume after
    // the quiet period. No-op unless AGT_AUTO_RESUME_ENABLED and this
    // manager holds a trip for the agent (operator/cost pauses have no
    // local trip → 'no-trip' skip; the server-side RPC guard is the
    // backstop for any disagreement).
    // Skip auto-resume for kill-switch-paused agents — the API overlay
    // re-pauses them every poll regardless of DB status, so an auto-resume flip
    // would be pointless churn. Only operator/cost/breaker pauses auto-resume.
    if (agent.status === 'paused' && !agent.kill_switch) {
      maybeAutoResume(agent);
    }
    agentStates.push({
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: now,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    // ENG-5080: needed for the edge-triggered log above. The branch
    // returns before the canonical agentState.knownStatuses.set near the end of
    // processAgent, so we'd otherwise log "is draft, skipping" every
    // 10s for the lifetime of the draft.
    agentState.knownStatuses.set(agent.agent_id, agent.status);
    return;
  }

  if (agent.status === 'revoked') {
    // ENG-5090: short-circuit on the second-and-later tick. All cleanup
    // below is idempotent — tmux session already dead, files already
    // removed, caches already cleared — so
    // re-running it every 10s for the lifetime of a revoked agent is
    // pure waste (child-process spawn for tmux, fs walks, Map churn).
    // ENG-5080 silenced the log line; this short-circuits the work.
    //
    // Re-enrolment safety: if the agent ever flips off `revoked` (e.g.
    // an operator un-revokes — rare but possible), the canonical
    // `status changed: prev → curr` branch a few lines below picks up
    // the transition because we still keep `agentState.knownStatuses` accurate.
    if (shouldSkipRevokedCleanup(previousKnownStatus)) {
      // CodeRabbit on PR #1052: cleanupAgentFiles swallows internal errors
      // and logs them, so a failed first-pass cleanup can still set
      // agentState.knownStatuses['revoked'] and silently skip retries. If the
      // provision dir is still on disk, fall through to retry instead of
      // short-circuiting.
      const residuals = {
        gatewayRunning: false,
        portAllocated: false,
        provisionDirExists: existsSync(agentDir),
      };
      if (!hasRevokedResiduals(residuals)) {
        agentStates.push({
          agentId: agent.agent_id,
          codeName: agent.code_name,
          status: agent.status,
          charterVersion: '',
          toolsVersion: '',
          secretsHash: null,
          lastRefreshAt: now,
          lastProvisionAt: null,
          lastDriftCheckAt: null,
          lastSecretsProvisionAt: null,
          acpSessions: [],
        });
        return;
      }
      // Residuals detected — note it once (this branch only triggers
      // when the first-pass cleanup partially failed, which is rare)
      // and fall through to the full cleanup block below for retry.
      log(
        `[revoked] residuals for '${agent.code_name}': ` +
          `dir=${residuals.provisionDirExists} ` +
          `— retrying cleanup`,
      );
    }

    log(`Agent '${agent.code_name}' is revoked, cleaning up`);
    stopAgentRuntime(agent.code_name, 'revoked');
    killAgentChannelProcesses(agent.code_name, { log });
    await cleanupAgentFiles(agent.code_name, agentDir);
    clearAgentCaches(agent.agent_id, agent.code_name);
    agentState.knownStatuses.set(agent.agent_id, agent.status);
    agentStates.push({
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: now,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    return;
  }

  // 1b. Detect status change → force reprovision and channel re-evaluation
  const previousStatus = agentState.knownStatuses.get(agent.agent_id);
  if (previousStatus && previousStatus !== agent.status) {
    log(`Agent '${agent.code_name}' status changed: ${previousStatus} → ${agent.status}`);
    agentState.knownVersions.delete(agent.agent_id); // Force reprovision
    // ENG-9585: and force the next pass to FETCH, so the reprovision is built
    // from current desired state rather than the cached payload. The status
    // here comes from the host-agent roster, not from `refreshData`, so this
    // branch can fire on a pass that is serving a cached charter.
    //
    // The path that makes it matter: the drift lane subscribes to
    // `agent_doc_versions` for ACTIVE agent ids, so a charter edited while an
    // agent is paused or revoked pushes nothing. Reactivating it would then
    // reprovision from whatever was cached before it went quiet.
    refreshFetchGate.forceNext(agent.agent_id);
    // ENG-5441: paused → active means the operator cleared the trip
    // (either through the webapp resume or `agt agent resume`). Drop the
    // breaker state so the agent can spawn on this tick. We don't clear
    // on every status-change because draft/revoked transitions don't
    // imply operator intent to recover from a trip.
    if (previousStatus === 'paused' && agent.status === 'active' && restartBreaker.isTripped(agent.code_name)) {
      restartBreaker.clear(agent.code_name);
      reportedTrips.delete(agent.code_name);
      // ENG-6392 / ENG-7577: same reset as every other trip-clearing path — the
      // recovery diff is scoped to the trip cycle that accumulated it. This edge
      // path was the one clear site that leaked it into the next cycle.
      dependencyRecoveryLedger.clear(agent.code_name);
      log(`[circuit-breaker] Cleared trip for '${agent.code_name}' on operator resume (paused → active)`);
    }
    // ENG-6088: distinguish OUR auto-resume from an operator's. An
    // operator resume is deliberate human intent — it re-arms the
    // auto-resume credit by clearing the marker. Our own resume must NOT
    // (the marker is exactly what bounds re-trips to one unattended
    // resume). Discriminator: marker freshness. A transition observed
    // within AUTO_RESUME_SELF_WINDOW_MS of the marker's autoResumedAt is
    // ours (post-resume the agent is active, so an operator resume can
    // only follow a later re-pause — a quiet-period timescale away).
    // Marker-based rather than an in-memory flag so it survives a manager
    // restart mid-transition (CodeRabbit on this PR).
    if (previousStatus === 'paused' && agent.status === 'active') {
      const marker = getAutoResumeMarker(agent.code_name);
      const isSelfResume = marker !== undefined && Date.now() - marker.autoResumedAt < AUTO_RESUME_SELF_WINDOW_MS;
      if (!isSelfResume && deleteAutoResumeMarker(agent.code_name)) {
        // Persist the re-arm NOW, mirroring the spend path — a worker exit
        // before the end-of-cycle write would rehydrate the stale marker
        // and make the next trip look already-auto-resumed until the
        // backoff TTL expires (CodeRabbit on this PR).
        state = {
          ...state,
          circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
        };
        send({ type: 'state-update', state });
        log(`[auto-resume] Cleared auto-resume marker for '${agent.code_name}' on operator resume — credit re-armed (ENG-6088)`);
      }
    }
    // ENG-6146: kill-switch resume. If this agent was halted by a kill switch
    // that has now cleared (the manager's last-seen status was the API's
    // overlaid 'paused'; the underlying DB status was 'active' throughout), it
    // re-spawns on this tick like any paused→active. Announce the resume on its
    // channels, mirroring the restart-notice hygiene. Best-effort, edge-once
    // (drained from the set so it fires only on the clearing transition).
    if (
      previousStatus === 'paused' &&
      agent.status === 'active' &&
      killPausedCodeNames.delete(agent.code_name)
    ) {
      log(`[kill-switch] '${agent.code_name}' kill switch cleared — resuming session`);
      void injectMessage(
        agent.code_name,
        'system',
        'The kill switch that halted you has been cleared. Your session is resuming.',
        { task_name: 'kill-switch-resume' },
        log,
      ).catch(() => false);
    }
    // Invalidate channel config hashes so credentials/bindings/enabled state get rewritten
    let channelCacheMutated = false;
    for (const key of agentState.knownChannelConfigHashes.keys()) {
      if (key.startsWith(`${agent.agent_id}:`)) {
        agentState.knownChannelConfigHashes.delete(key);
        channelCacheMutated = true;
      }
    }
    if (channelCacheMutated) saveChannelHashCache();
  }
  agentState.knownStatuses.set(agent.agent_id, agent.status);

  // 2. Refresh — get latest charter/tools from API
  let refreshData: {
    agent: Record<string, unknown>;
    charter: { raw_content: string; version: string } | null;
    tools: { raw_content: string; version: string } | null;
    channel_configs: Record<string, { config: unknown; status: string }> | null;
    team_channel_policy: {
      team_id: string;
      allowed_channels: string[];
      denied_channels: string[];
      require_elevated_for_pii: boolean;
    } | null;
    team: { name: string; description: string | null; settings?: Record<string, unknown> } | null;
    scheduled_tasks: Array<{
      id: string;
      template_id: string;
      name: string;
      schedule_kind: string;
      schedule_expr: string | null;
      schedule_every: string | null;
      schedule_at: string | null;
      timezone: string;
      prompt: string;
      session_target: string;
      delivery_mode: string;
      // ENG-6124: /host/refresh returns this (ENG-6107 select); the type must
      // declare it so the producer map's cast isn't lying.
      delivery_policy?: string | null;
      delivery_channel: string;
      delivery_to: string | null;
      enabled: boolean;
    }> | null;
    plugin_skills?: Array<{
      plugin_slug: string;
      skill_id: string;
      skill_name: string;
      content: string;
    }>;
    plugin_install_hooks?: Array<{
      plugin_slug: string;
      script: string;
    }>;
    /** ENG-4421: toolkits required by installed plugins — manager resolves CLI installs from these. */
    plugin_toolkits?: Array<{
      plugin_slug: string;
      toolkits: string[];
    }>;
    /** ENG-4341: pre-resolved plugin contexts (defaults applied, agent-scope row merged in). */
    plugin_contexts?: Array<{
      plugin_id: string;
      plugin_slug: string;
      values: Record<string, unknown>;
      overrides: string;
    }>;
    /**
     * Org + team knowledge items for skill generation.
     *
     * ENG-9585: present only when the API answered WITHOUT the manifest — i.e.
     * an API build older than the manifest, which ignores the request flag. The
     * resolution below normalises both shapes into this field, so everything
     * downstream of the refresh sees exactly what it always has.
     */
    knowledge?: Array<{ title: string; slug: string; content: string | null; scope: 'org' | 'team' }>;
    /**
     * ENG-9585: the same list with content hashes instead of bodies. Returned
     * in place of `knowledge` when this manager asks for it. Bodies come from
     * the host-wide cache, topped up via POST /host/knowledge/contents.
     */
    knowledge_manifest?: KnowledgeManifestEntry[];
    /**
     * ENG-9585: the two ROOT-skill lanes. Present when the API answered WITHOUT
     * the manifest — an API build older than it, which ignores the request flag.
     * The resolution below normalises the manifest shape into these fields, so
     * `planGlobalSkillSync` further down sees exactly what it always has.
     *
     * Their ABSENCE is load-bearing: ENG-7492 reads "neither field present" as
     * "the desired set is unknown" and skips pruning. That is the signal used
     * when a body cannot be resolved, so neither may ever be set to `[]` as a
     * stand-in for "could not resolve" — `[]` means "nothing is assigned" and
     * deletes every skill folder the manager planted.
     */
    global_skills?: Array<{ code_name: string; display_name?: string; content: string }>;
    shared_skills?: Array<{ code_name: string; display_name?: string; content: string; scope?: string }>;
    /** ENG-9585: the same two lanes with content hashes instead of bodies. */
    global_skills_manifest?: SkillManifestEntry[];
    shared_skills_manifest?: SkillManifestEntry[];
    /**
     * ENG-9585: integration child skills. `integration_skills` is present when
     * the API answered WITHOUT the manifest; the resolution below normalises
     * the manifest shape into it, so the bundle builder further down sees
     * exactly what it always has.
     *
     * Its absence WHILE `integration_skills_manifest` is present is
     * load-bearing, and means "this poll could not resolve the lane". The
     * install loop and the orphan sweep both stand down on that, because `[]`
     * here means "every integration was uninstalled" and deletes every
     * `integration-<slug>/` folder on the box.
     */
    integration_skills?: Array<{
      plugin_slug: string;
      integration_slug?: string;
      skill_id: string;
      skill_name: string;
      content: string;
    }>;
    integration_skills_manifest?: IntegrationSkillManifestEntry[];
    /** Team members for CLAUDE.md directory. */
    team_members?: Array<{ display_name: string; email?: string; role: string; title?: string; contact_channel?: string }>;
    /** People/contacts for CLAUDE.md directory. */
    people?: Array<{ display_name: string; email?: string; title?: string; department?: string; relationship?: string; contact_channel?: string }>;
    /**
     * Effective guardrails for this agent, pre-resolved server-side
     * (org → team → agent + overrides) and joined with their
     * definitions. Threaded straight into `provisionInput.guardrails`
     * for the framework adapter to render. Omitted by older API
     * builds; downstream code treats `undefined` and `[]` the same.
     */
    guardrails?: ProvisionInput['guardrails'];
    // ENG-9862: standing rules, rendered into CLAUDE.md by the adapter.
    agent_rules?: ProvisionInput['agentRules'];
    /**
     * ADR-0012 / ENG-6352: active dynamic workflows for this agent, resolved
     * server-side (team default ∪ agent override, agent grain wins) and gated
     * on the `workflows-down-sync` flag. Threaded straight into
     * `provisionInput.workflows` so the claude-code adapter writes each as
     * `.claude/workflows/<name>.js`. Omitted by older API builds / empty when
     * the flag is off; downstream treats `undefined` and `[]` the same.
     */
    workflows?: ProvisionInput['workflows'];
  };

  // ENG-9585: the poll keeps its 10s cadence for the LOCAL work; the network
  // fetch is spaced per agent. A cached payload is byte-identical to what a
  // refetch would return whenever nothing changed upstream — the overwhelming
  // majority of passes — so every hash gate downstream behaves exactly as it
  // does today. What changes is only WHEN an upstream edit is observed, and the
  // Realtime lanes force the gate open the moment one lands.
  //
  // DIAGNOSTIC NOTE: `lastRefreshAt` in `manager-state.json` is still stamped on
  // every pass, cached or not — it has always meant "the refresh STEP completed
  // for this agent", and it feeds the watchdog's display rather than any
  // decision. It is no longer a record of when the host last spoke to the API.
  // The `[poll-cycle] refresh_fetched=/refresh_skipped=` line is the signal for
  // that, and it is per cycle rather than per agent.
  const cachedRefresh = lastRefreshPayload.get(agent.agent_id);
  // Taken BEFORE the request. A Realtime push landing mid-flight must not be
  // consumed by a response that predates it — see `FetchToken`.
  const refreshToken = refreshFetchGate.beginFetch(agent.agent_id);
  if (cachedRefresh !== undefined && !refreshFetchGate.shouldFetch(agent.agent_id)) {
    // Cast: the payload's type is a ~250-line inline literal local to this
    // function, and the cache is module-scoped. The object stored is the one
    // produced by the branch below, POST-normalisation, so the shape is the
    // same by construction.
    refreshData = cachedRefresh as typeof refreshData;
    pollCycleFetchSkips += 1;
  } else {
  try {
    refreshData = await api.post<typeof refreshData>('/host/refresh', {
      agent_id: agent.agent_id,
      // ENG-9585: ask for hashes instead of bodies on all three lanes. The
      // rationale for each flag, and for their staying three flags rather than
      // one, now lives with the constant — so that a new `/host/refresh` caller
      // inherits it instead of having to rediscover it. The manifest is
      // normalised back into `refreshData.knowledge` immediately below.
      ...REFRESH_MANIFEST_FLAGS,
    });

    // Normalise the manifest back into `refreshData.knowledge` right here, at
    // the wire boundary. Everything downstream — the provision input, the
    // `!!k.content` filter, the artifact hash comparison — is untouched, which
    // is what keeps this a transport change rather than a behaviour change.
    if (refreshData.knowledge_manifest) {
      const resolved = await resolveKnowledgeFromManifest({
        manifest: refreshData.knowledge_manifest,
        cacheDir: join(config.configDir, '_knowledge'),
        delivery:
          ((refreshData.agent as Record<string, unknown> | undefined)?.knowledge_delivery as
            | 'search' | 'files' | 'both' | undefined) ?? 'both',
        fetchContents: async (hashes) => {
          const res = await api.post<{ contents?: Array<{ hash: string; content: string }> }>(
            '/host/knowledge/contents',
            { agent_id: agent.agent_id, hashes },
          );
          return res.contents ?? [];
        },
        log,
      });
      refreshData.knowledge = resolved as typeof refreshData.knowledge;
    }

    // Same normalisation for skills, with one critical difference: it is
    // all-or-nothing. `resolveSkillsFromManifest` returns null if ANY body in
    // either lane is unresolved, and on null both fields are left ABSENT rather
    // than empty. That is deliberate and it is the whole safety property here —
    // `planGlobalSkillSync` PRUNES against the desired set, so an empty array
    // would read as "nothing is assigned" and delete the agent's skill folders
    // off disk over a transient fetch failure. Absent means "unknown", which
    // ENG-7492 already handles by skipping the prune entirely.
    if (refreshData.global_skills_manifest || refreshData.shared_skills_manifest) {
      const resolvedSkills = await resolveSkillsFromManifest({
        globals: refreshData.global_skills_manifest ?? [],
        shared: refreshData.shared_skills_manifest ?? [],
        cacheDir: join(config.configDir, '_skills'),
        fetchContents: async (hashes) => {
          const res = await api.post<{ contents?: Array<{ hash: string; content: string }> }>(
            '/host/skills/contents',
            { agent_id: agent.agent_id, hashes },
          );
          return res.contents ?? [];
        },
        log,
      });
      if (resolvedSkills) {
        refreshData.global_skills = resolvedSkills.globals as typeof refreshData.global_skills;
        refreshData.shared_skills = resolvedSkills.shared as typeof refreshData.shared_skills;
      }
    }

    // The integration lane, same rule and a SEPARATE resolver call because it
    // is a separate prune domain: its folders are swept by different code from
    // `planGlobalSkillSync`'s, so one unreachable integration body must not
    // stall root-skill sync (or the reverse). Shares the `_skills` cache
    // directory — the store is keyed on the hash alone, so a body reachable
    // through both lanes is one file.
    //
    // On null the field is left ABSENT, never `[]`. The install loop reads that
    // absence as "leave disk alone this poll"; `[]` would mean "every
    // integration was uninstalled".
    if (refreshData.integration_skills_manifest) {
      const resolvedIntegrationSkills = await resolveIntegrationSkillsFromManifest({
        manifest: refreshData.integration_skills_manifest,
        cacheDir: join(config.configDir, '_skills'),
        fetchContents: async (hashes) => {
          const res = await api.post<{ contents?: Array<{ hash: string; content: string }> }>(
            '/host/skills/contents',
            { agent_id: agent.agent_id, hashes },
          );
          return res.contents ?? [];
        },
        log,
      });
      if (resolvedIntegrationSkills) {
        refreshData.integration_skills =
          resolvedIntegrationSkills as typeof refreshData.integration_skills;
      }
    }
  } catch (err) {
    log(`Refresh failed for '${agent.code_name}': ${(err as Error).message}`);
    const existing = state.agents.find((a) => a.agentId === agent.agent_id);
    agentStates.push(existing ?? {
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: null,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    return;
  }
  // Only a SUCCESSFUL fetch resets the spacing. The catch above returns, so
  // reaching here means the payload AND all three manifest resolutions landed —
  // a transport failure is retried on the very next pass rather than being made
  // to wait out the TTL. Caching a failure would turn a blip into a guaranteed
  // stall of desired state, which is exactly when being current matters most.
  lastRefreshPayload.set(agent.agent_id, refreshData);
  refreshFetchGate.markFetched(agent.agent_id, refreshToken);
  pollCycleFetches += 1;
  }

  // Cache alert webhook from team settings (first agent wins)
  if (!getAlertSlackWebhook() && refreshData.team?.settings) {
    const webhook = refreshData.team.settings['alert_slack_webhook'];
    if (typeof webhook === 'string' && webhook.startsWith('https://')) {
      setAlertSlackWebhook(webhook);
    }
  }

  if (!refreshData.charter || !refreshData.tools) {
    log(`No charter/tools for '${agent.code_name}', skipping`);
    agentStates.push({
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: now,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    return;
  }

  // Resolve framework adapter from agent data and update cache
  const frameworkId = (refreshData.agent.framework as string) ?? DEFAULT_FRAMEWORK;
  agentFrameworkCache.set(agent.code_name, frameworkId);
  const frameworkAdapter = getFramework(frameworkId);
  // ENG-4418: re-resolve agentDir now that /host/refresh has given us the
  // authoritative framework. If the cache was stale (agent switched
  // framework since last poll), the initial agentDir would have pointed at
  // the wrong tree — recalculate before any writes or adapter calls use it.
  agentDir = join(frameworkAdapter.getAgentDir(agent.code_name), 'provision');

  // ENG-4422 §5: cache the agent delivery metadata so the DM resolver has
  // what it needs without an extra API call at fire time.
  // The host-runtime payload carries reports_to_* + owner_team_name (see §8).
  cacheAgentDeliveryMetadata(agent.code_name, refreshData);

  // ENG-6695: record this agent's timezone inputs (its own override + the
  // flattened team timezone) so restartGateFor can compute off-peak in the
  // operator's zone. The org tier is supplied at gate time from the cached
  // maintenance window's resolved timezone (host → org → region → UTC).
  agentRestartTimezoneInputs.set(agent.code_name, {
    agentTimezone:
      typeof refreshData.agent.timezone === 'string' ? (refreshData.agent.timezone as string) : null,
    teamTimezone:
      typeof (refreshData.team as { timezone?: unknown } | null)?.timezone === 'string'
        ? ((refreshData.team as { timezone?: string }).timezone ?? null)
        : null,
    // ENG-8016: the override's TTL, served alongside it on /host/refresh.
    agentTimezoneExpiresAt:
      typeof (refreshData.agent as { timezone_expires_at?: unknown }).timezone_expires_at
        === 'string'
        ? ((refreshData.agent as { timezone_expires_at?: string }).timezone_expires_at ?? null)
        : null,
  });

  // ENG-5901 PR 3: one-shot per manager run — hoist pre-Track-D literal
  // secrets out of the on-disk .mcp.json into .env.integrations and rewrite
  // templated. Without this, files carrying old literal entries can never
  // pass the armed lint (every incremental writer preserves the other
  // servers' entries verbatim → permanent write rejection; observed live
  // on agt-aws-1 2026-06-04). Idempotent and cheap for clean files.
  if (frameworkAdapter.migrateSecretStorage && !migratedSecretStorage.has(agent.code_name)) {
    try {
      frameworkAdapter.migrateSecretStorage(agent.code_name);
      migratedSecretStorage.add(agent.code_name);
    } catch (err) {
      // Leave un-marked so the next tick retries.
      log(`[mcp-migrate] failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // Ensure per-agent profile config exists
  if (frameworkAdapter.seedProfileConfig) {
    frameworkAdapter.seedProfileConfig(agent.code_name);
  }

  const charterVersion = refreshData.charter.version;
  const toolsVersion = refreshData.tools.version;
  const known = agentState.knownVersions.get(agent.agent_id);

  let lastProvisionAt = state.agents.find((a) => a.agentId === agent.agent_id)?.lastProvisionAt ?? null;

  // CodeRabbit on PR #773 (round 4): diff against the *launchable* channel
  // set, not raw channel_configs membership. The Claude launch flags
  // (`--dangerously-load-development-channels`, `--allowedTools`) only
  // include channels whose entry has `status` in {active, pending} AND a
  // non-null config — the same gate writeChannelCredentials runs at line
  // 2571. A status-only flip like `slack: active → revoked` leaves the
  // channel id in `Object.keys(channel_configs)` and would have been
  // missed by a raw-key diff, leaving the running session with stale
  // launch flags. Inversely, a row that never becomes launchable would
  // have triggered an unnecessary restart. Compute the same launchable
  // filter here so add/remove + restart decisions agree with what the
  // session actually loads.
  //
  // Detect channel changes (launchable filter via the shared helper so
  // unit tests cover the same gate this code uses at runtime).
  // ENG-5932: drop locally-quarantined channels from the effective set at the
  // source, so they're excluded uniformly from the launch flags, the
  // writeChannelCredentials loop, the channel-set-change diff, and the cache.
  // In shadow/off mode this set is always empty (no markers are written), so
  // every line below is a no-op until enforcement is flipped on.
  const quarantinedChannels = channelQuarantineStore().getQuarantinedKeys(agent.code_name);
  // ENG-9802: the restart-relevant telegram policy for this tick. Captured in
  // the channel loop below (where `peerDisabledMode` is computed) and consumed
  // by the restart decision further down — declared here because those two are
  // in different blocks. Undefined means no telegram entry was seen this tick.
  let telegramPolicyEnvForRestart: Record<string, string> | undefined;
  const currentChannelIds = setWithout(
    launchableChannelIds(refreshData.channel_configs),
    quarantinedChannels,
  );
  const previousChannelIds = agentState.knownChannels.get(agent.agent_id);
  const channelsChanged = !previousChannelIds ||
    currentChannelIds.size !== previousChannelIds.size ||
    [...currentChannelIds].some((ch) => !previousChannelIds.has(ch)) ||
    [...previousChannelIds].some((ch) => !currentChannelIds.has(ch));

  // CodeRabbit on PR #773 (round 2): track whether the on-disk channel
  // credential state actually converges to `currentChannelIds` before we
  // (a) advance the diff cache (`agentState.knownChannels.set`) and (b) trigger an
  // ENG-4807 hot-reload restart. If a remove or write fails, leave the
  // diff live so the next refresh tick sees the change again and retries
  // the credential sync — and don't restart the session on broken
  // credential state.
  let channelConfigConverged = true;
  // ENG-9252: the reason travels with the failure. Before this, the three sites
  // that clear `channelConfigConverged` each logged their own cause and the
  // summary line printed afterwards had none — so the line that repeated every
  // tick was the one that said nothing.
  let channelConfigFailureReason = '';

  // ENG-9252: a NEW channel-set change clears any backoff earned by the previous
  // state. Binding or unbinding a channel is an operator action and must be
  // served on the next tick, not made to wait out a retry schedule.
  //
  // Compared against the set we last ATTEMPTED, never against `channelsChanged`.
  // `channelsChanged` is derived from `knownChannels`, which only advances on
  // convergence — so during a non-convergent change it stays true on every tick,
  // and clearing the streak on it cleared the streak every tick. That left
  // `attempts` pinned at 1, the backoff never engaging, the summary line
  // printing per tick and `since` resetting so nothing was ever reported stuck:
  // the whole mechanism disabled by the line meant to keep operators responsive
  // (CodeRabbit on #4840). "Changed since I last tried" is the question that was
  // actually meant.
  const attemptedChannelIds = channelSyncAttemptedSets.get(agent.agent_id);
  const channelSetChangedSinceAttempt = !sameChannelSet(attemptedChannelIds, currentChannelIds);
  if (
    attemptedChannelIds !== undefined
    && channelSetChangedSinceAttempt
    && channelSyncFailures.delete(agent.agent_id)
  ) {
    log(
      `[channels] channel set changed for '${agent.code_name}' since the last attempt — clearing the credential-sync backoff and retrying immediately (ENG-9252)`,
    );
  }

  // Remove credentials for channels that were unbound
  if (previousChannelIds && channelsChanged && frameworkAdapter.removeChannelCredentials) {
    for (const ch of previousChannelIds) {
      if (!currentChannelIds.has(ch)) {
        try {
          frameworkAdapter.removeChannelCredentials(agent.code_name, ch);
          log(`Removed ${ch} credentials for '${agent.code_name}'`);
        } catch (err) {
          channelConfigConverged = false;
          channelConfigFailureReason = `removing ${ch} credentials threw: ${(err as Error).message}`;
          log(`Failed to remove ${ch} credentials for '${agent.code_name}': ${(err as Error).message}`);
        }
      }
    }
  }

  // 3. Content-based re-provisioning: always generate artifacts, compare against
  //    what's on disk, and only write files that actually changed. This catches
  //    template code changes, DB data changes (role, description), and version bumps.
  try {
    const artifacts = generateArtifacts(agent, refreshData, frameworkAdapter, renderIntegrationsSection);
    // Some artifacts (`.mcp.json`) need their on-disk merge computed *before*
    // the write loop, so track an optional override content alongside the
    // artifact. When set, it replaces artifact.content at write time.
    const changedFiles: { relativePath: string; content: string }[] = [];

    mkdirSync(agentDir, { recursive: true });
    for (const artifact of artifacts) {
      const filePath = join(agentDir, artifact.relativePath);

      // For CLAUDE.md: strip the skills index section before comparing,
      // since it's managed separately by refreshSkillsIndexInClaudeMd.
      // Without this, the provisioner re-writes CLAUDE.md every cycle
      // (it generates without the index, but on-disk has it).
      //
      // Both sides must be trimmed the same way — the stripped project copy
      // loses its trailing newline via trimEnd(), and the generated artifact
      // content preserves its trailing newline. Without trimming both,
      // hashes never match and the provisioner churns every poll.
      let existingHash: string | null;
      let newHash: string;
      let writeContent = artifact.content;
      if (artifact.relativePath === 'CLAUDE.md') {
        // Strip dynamically-patched sections from BOTH sides before hashing.
        // Two sections get added to project/CLAUDE.md by side-effect code paths
        // that the generator doesn't know about:
        //   1. `## Available Skills` + `## Updating Plugins` (index) — added by
        //      refreshSkillsIndexInClaudeMd, bracketed by AGT:SKILLS_INDEX markers.
        //   2. `## Integrations` — added by frameworkAdapter.writeIntegrations,
        //      bracketed by AGT:INTEGRATIONS markers (ENG-5794).
        //
        // ENG-5794: the Integrations strip used to be the broad
        //   `## Integrations[\s\S]*?(?=## Rules)`
        // which swallowed every section between `## Integrations` and
        // `## Rules` — capability prompt, knowledge, kanban work policy,
        // skill authoring, dashboards. Template changes to any of those
        // were invisible to the hash and never triggered a CLAUDE.md
        // rewrite, so improvements like ENG-5792's "What can you do for
        // me?" section could not reach existing agents without a manual
        // re-provision. The pattern is now pinned to the
        // AGT:INTEGRATIONS_START/_END sentinel pair so only the
        // Integrations block itself is excluded from the hash. A legacy
        // fallback regex remains so on-disk CLAUDE.md files written
        // before sentinels existed still hash cleanly until the next
        // render brings them up to date.
        const stripDynamicSections = (s: string): string => {
          let out = s.replace(new RegExp(`${SKILLS_INDEX_START}[\\s\\S]*?${SKILLS_INDEX_END}`), '');
          if (out.includes(INTEGRATIONS_SECTION_START)) {
            out = out.replace(
              new RegExp(`${escapeRegExp(INTEGRATIONS_SECTION_START)}[\\s\\S]*?${escapeRegExp(INTEGRATIONS_SECTION_END)}\\n*`),
              '',
            );
          } else {
            // Legacy on-disk shape — pre-ENG-5794. First render after the
            // manager upgrade replaces this with the sentinel-bracketed
            // form, after which this branch is dead.
            out = out.replace(/## Integrations[\s\S]*?(?=## Rules)/, '');
          }
          return out.trimEnd();
        };
        newHash = sha256(stripDynamicSections(artifact.content));
        try {
          const projectClaudeMd = join(config.configDir, agent.code_name, 'project', 'CLAUDE.md');
          const existing = readFileSync(projectClaudeMd, 'utf-8');
          existingHash = sha256(stripDynamicSections(existing));
        } catch {
          existingHash = null;
        }
      } else if (artifact.relativePath === '.mcp.json') {
        // The `.mcp.json` generator only owns a subset of mcpServers (augmented,
        // qmd, ...); channel servers (slack, direct-chat, …) and plugin-added
        // servers are merged in by other code paths. A naive overwrite would
        // wipe them every poll, which then makes step 5b re-add direct-chat,
        // step 5 re-add slack, etc. — the loop that produces:
        //   Updating 'agent': .mcp.json
        //   Channel credentials written for 'agent/direct-chat'
        // every cycle. Compare and write only the generator-owned keys,
        // preserving everything else.
        const parseMcp = (raw: string): Record<string, unknown> => {
          try {
            const parsed = JSON.parse(raw) as { mcpServers?: Record<string, unknown> };
            return parsed.mcpServers ?? {};
          } catch {
            return {};
          }
        };
        const generatorServers = parseMcp(artifact.content);
        const generatorKeys = Object.keys(generatorServers);
        let existingRaw = '';
        try { existingRaw = readFileSync(filePath, 'utf-8'); } catch { /* new file */ }
        const existingServers = parseMcp(existingRaw);
        const merged = { mcpServers: { ...existingServers, ...generatorServers } };
        writeContent = JSON.stringify(merged, null, 2);
        // Hash only the generator-owned slice on both sides. If the generator
        // produces the same shape as what's already there, skip the write.
        const sliceGeneratorKeys = (src: Record<string, unknown>): Record<string, unknown> => {
          const out: Record<string, unknown> = {};
          for (const k of generatorKeys) if (k in src) out[k] = src[k];
          return out;
        };
        newHash = sha256(JSON.stringify(generatorServers));
        existingHash = existingRaw
          ? sha256(JSON.stringify(sliceGeneratorKeys(existingServers)))
          : null;
      } else if (artifact.relativePath === 'opencode.json') {
        // ENG-7959: opencode's `mcp` block is co-owned, so this is the opencode
        // analogue of the `.mcp.json` merge above. The generator
        // (buildOpencodeConfig) owns the top-level config + the `augmented` MCP
        // entry; integration and channel MCP servers are added incrementally by
        // writeMcpServer / writeChannelCredentials directly into this same file
        // (the serve reads provision/opencode.json, and the runtime's
        // materializer lifts its `mcp` block into the global config the serve
        // loads at boot). A naive overwrite would wipe those extra servers every
        // poll (the loop then re-adds them, churning the serve). The pure helper
        // merges the two `mcp` maps and returns the generator-owned slice of each
        // side so we skip the write when only incrementally-added servers differ.
        let existingRaw: string | null = null;
        try { existingRaw = readFileSync(filePath, 'utf-8'); } catch { /* new file */ }
        const mergeResult = mergeOpencodeConfigArtifact(artifact.content, existingRaw);
        writeContent = mergeResult.content;
        newHash = sha256(mergeResult.generatorSlice);
        existingHash = mergeResult.existingSlice ? sha256(mergeResult.existingSlice) : null;
      } else {
        newHash = sha256(artifact.content);
        existingHash = hashFile(filePath);
      }

      if (newHash !== existingHash) {
        changedFiles.push({ relativePath: artifact.relativePath, content: writeContent });
      }
    }

    if (changedFiles.length > 0) {
      const isFirst = !existsSync(join(agentDir, 'CHARTER.md'));
      const verb = isFirst ? 'Provisioning' : 'Updating';
      const fileNames = changedFiles.map((f) => f.relativePath).join(', ');
      log(`${verb} '${agent.code_name}': ${fileNames}`);

      for (const file of changedFiles) {
        const filePath = join(agentDir, file.relativePath);
        mkdirSync(dirname(filePath), { recursive: true });
        if (file.relativePath === '.mcp.json') {
          // ENG-5901 (CodeRabbit #1731): the secret-bearing artifact must
          // never exist world-readable, even briefly — write it via the
          // atomic temp-file + rename path with 0600 set on the temp file
          // before the swap (same writer safeWriteMcpJson uses, minus the
          // validation: the merged content was produced by the generator
          // merge above and parsed on the way in).
          safeWriteJsonAtomic(filePath, file.content, { mode: 0o600 });
        } else {
          writeFileSync(filePath, file.content);
        }
      }

      // Prune stale legacy knowledge-* skill dirs from provision dir
      // (replaced by single core-knowledge skill)
      try {
        const provSkillsDir = join(agentDir, '.claude', 'skills');
        if (existsSync(provSkillsDir)) {
          for (const folder of readdirSync(provSkillsDir)) {
            if (folder.startsWith('knowledge-')) {
              try { rmSync(join(provSkillsDir, folder), { recursive: true }); } catch { /* ignore */ }
            }
          }
        }
      } catch { /* non-fatal */ }

      lastProvisionAt = new Date().toISOString();

      agentState.knownVersions.set(agent.agent_id, { charterVersion, toolsVersion });

      // Update written hashes for drift detection
      const trackedFiles = frameworkAdapter.driftTrackedFiles();
      const hashes = new Map<string, string>();
      for (const file of trackedFiles) {
        const h = hashFile(join(agentDir, file));
        if (h) hashes.set(file, h);
      }
      agentState.writtenHashes.set(agent.agent_id, hashes);

      // Register in framework runtime if not already present
      const resolvedModelsForRegistration = resolveModelChain(refreshData);
      const primaryModel = resolvedModelsForRegistration.primary ?? (refreshData.agent.primary_model as string | null | undefined);
      const registeredAgents = await getOrCacheRegisteredAgents(frameworkAdapter, agent.code_name);
      if (!registeredAgents.has(agent.code_name)) {
        const registered = await frameworkAdapter.registerAgent(agent.code_name, agentDir, primaryModel, agent.agent_id);
        if (registered) {
          registeredAgents.add(agent.code_name);
          log(`Registered '${agent.code_name}' in ${frameworkAdapter.label}`);
        }
      }

      send({ type: 'provisioned', agentId: agent.agent_id, codeName: agent.code_name });
    }

    // ADR-0012 / ENG-6352: prune stale workflow files from the provision dir.
    // A workflow content change rides the per-artifact hash above, but a
    // *removal* (revoked / renamed / flag turned off) produces no changedFiles
    // entry — the artifact simply stops being generated — so the old
    // `.claude/workflows/<name>.js` would linger in the provision dir and the
    // deploy step (which mirrors provision → project) would keep copying it.
    // Reconcile the provision dir against the server-resolved set every cycle.
    // Only when `workflows` is a defined array: an older API that omits the
    // field entirely carries no information, so leave the dir untouched.
    if (Array.isArray(refreshData.workflows)) {
      try {
        const provWorkflowsDir = join(agentDir, '.claude', 'workflows');
        if (existsSync(provWorkflowsDir)) {
          const expected = new Set(refreshData.workflows.map((w) => `${w.name}.js`));
          for (const file of readdirSync(provWorkflowsDir)) {
            if (!file.endsWith('.js')) continue;
            if (expected.has(file)) continue;
            try { rmSync(join(provWorkflowsDir, file)); } catch { /* non-fatal */ }
          }
        }
      } catch { /* non-fatal */ }
    }

    // Always deploy artifacts to project dir — the provision dir is the source
    // of truth, and channel MCP servers are merged from a separate path.
    // This must run outside changedFiles so the merge always produces a
    // complete .mcp.json (base servers + channel servers).
    if (frameworkAdapter.deployArtifactsToProject) {
      frameworkAdapter.deployArtifactsToProject(agent.code_name, agentDir);
    }
  } catch (err) {
    log(`Provision failed for '${agent.code_name}': ${(err as Error).message}`);
  }

  // 3b. Ensure model is set correctly in per-agent profile (on first run and on change)
  const resolvedForModel = resolveModelChain(refreshData);
  const primaryModel = resolvedForModel.primary ?? (refreshData.agent.primary_model as string | null | undefined);
  if (primaryModel && frameworkAdapter.updateAgentModel) {
    const previousModel = agentState.knownModels.get(agent.agent_id);
    if (previousModel !== primaryModel) {
      try {
        const updated = await frameworkAdapter.updateAgentModel(agent.code_name, primaryModel);
        if (updated) {
          if (previousModel) {
            log(`Model updated for '${agent.code_name}': ${previousModel} → ${primaryModel}`);
          } else {
            log(`Model set for '${agent.code_name}': ${primaryModel}`);
          }
        }
      } catch (err) {
        log(`Failed to update model for '${agent.code_name}': ${(err as Error).message}`);
      }

      // ENG-5635: a model change only takes effect on the next spawn — the
      // launcher derives `--model <alias>` from primary_model at launch
      // (ENG-5631); the settings.json write above is inert for claude-code at
      // runtime. So for a live claude-code session, proactively respawn now
      // (via the coalescing scheduler, which preserves the presence-reaper
      // baseline since channels/MCP are unchanged) instead of leaving the
      // agent on its old model until the next natural restart. The gate skips
      // first-provision (no previousModel — the imminent first spawn already
      // carries the model) and the no-session case (next spawn picks it up).
      if (
        shouldRespawnForModelChange({
          previousModel,
          primaryModel,
          framework: frameworkId,
          sessionHealthy: isSessionHealthy(agent.code_name),
        })
      ) {
        const restartNotice =
          `Your model was changed to ${primaryModel}. Claude Code applies the model at launch, ` +
          `so your manager will restart your session shortly to switch.`;
        const delivered = await injectMessage(
          agent.code_name, 'system', restartNotice + BACK_ONLINE_GREETING_GUIDANCE, { task_name: 'model-update' }, log,
        ).catch(() => false);
        const delay = delivered ? 8_000 : 3_000;
        if (!delivered) {
          log(`[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`);
        }
        log(`[hot-reload] Model changed for '${agent.code_name}': ${previousModel} → ${primaryModel} — restarting session`);
        scheduleSessionRestart(agent.code_name, delay, 'model change', 'model-change');
      }
    }
    agentState.knownModels.set(agent.agent_id, primaryModel);
  } else if (primaryModel && frameworkId === 'opencode') {
    // ENG-7928: opencode has no per-agent-profile model file to update (no
    // `updateAgentModel`); its model lives in the generated opencode.json, which
    // the provision step above regenerates every cycle. A live `opencode serve`
    // keeps the stale model until it respawns, so on a model change restart the
    // serve - the poll loop's ensureOpencodeRuntime respawns it against the fresh
    // opencode.json. Mirrors the claude-code hot-reload above; the gate skips
    // first provision and the no-session case. This is what makes the xai-direct
    // model self-heal (ENG-7928) actually take effect on a running agent.
    //
    // TRANSACTIONAL (CodeRabbit): only restart AND record the model once the new
    // model is confirmed on disk - if this poll's provision step failed/was
    // stale, opencode.json still shows the old model, so a restart would respawn
    // on stale config and stamping knownModels would mask the pending change
    // forever. Deferring both leaves the change to retry on the next poll.
    const previousModel = agentState.knownModels.get(agent.agent_id);
    const provisionedModel = toOpencodeModel(primaryModel);
    const onDiskModel = readProvisionedOpencodeModel(getFramework('opencode').getAgentDir(agent.code_name));
    const { restart, recordModel } = decideOpencodeModelRestart({
      previousModel,
      primaryModel,
      provisionedModel,
      onDiskModel,
      sessionHealthy: isOpencodeSessionHealthy(agent.code_name),
    });
    if (restart) {
      log(
        `[hot-reload] opencode model changed for '${agent.code_name}': ${previousModel} -> ${primaryModel} - restarting serve`,
      );
      stopOpencodeSession(agent.code_name, log);
    } else if (previousModel && previousModel !== primaryModel && onDiskModel !== provisionedModel) {
      log(
        `[hot-reload] opencode model change for '${agent.code_name}' deferred: opencode.json shows '${onDiskModel}' not '${provisionedModel}' (provision not yet applied) - retrying next poll`,
      );
    }
    if (recordModel) agentState.knownModels.set(agent.agent_id, primaryModel);
  }

  // 4. Drift check — hash local files and compare
  let lastDriftCheckAt = now;
  const written = agentState.writtenHashes.get(agent.agent_id);

  if (written && existsSync(agentDir)) {
    const driftedFiles: string[] = [];

    for (const [file, expectedHash] of written) {
      const localHash = hashFile(join(agentDir, file));
      if (localHash && localHash !== expectedHash) {
        driftedFiles.push(file);
      }
    }

    if (driftedFiles.length > 0) {
      log(`Drift detected for '${agent.code_name}': ${driftedFiles.join(', ')}`);
      send({ type: 'drift-detected', agentId: agent.agent_id, codeName: agent.code_name, files: driftedFiles });

      // Report drift to API
      try {
        const localHashes: Record<string, string | null> = {};
        for (const file of driftedFiles) {
          localHashes[file] = hashFile(join(agentDir, file));
        }
        await api.post('/host/drift', {
          agent_id: agent.agent_id,
          drifted_files: driftedFiles,
          local_hashes: localHashes,
        });
      } catch (err) {
        log(`Failed to report drift for '${agent.code_name}': ${(err as Error).message}`);
      }
    }
  }

  // Cache bot tokens from channel_configs for notification delivery
  if (refreshData.channel_configs) {
    const tokens: { slack?: string; telegram?: string; telegramAllowedChats?: string[] } = {};
    const slackCfg = refreshData.channel_configs['slack'];
    if (slackCfg?.config) {
      const bt = (slackCfg.config as Record<string, unknown>).bot_token;
      if (typeof bt === 'string' && bt) tokens.slack = bt;
    }
    const tgCfg = refreshData.channel_configs['telegram'];
    if (tgCfg?.config) {
      const tgConfig = tgCfg.config as Record<string, unknown>;
      const bt = tgConfig.bot_token;
      if (typeof bt === 'string' && bt) tokens.telegram = bt;
      const allowedChats = tgConfig.allowed_chat_ids;
      if (Array.isArray(allowedChats)) tokens.telegramAllowedChats = allowedChats as string[];
    }
    if (tokens.slack || tokens.telegram) {
      agentChannelTokens.set(agent.code_name, tokens);
    } else {
      agentChannelTokens.delete(agent.code_name);
    }
  }

  // 5. Write channel credentials to per-agent profile config (only if changed)
  //
  // ENG-9252: skipped entirely while this agent is inside a credential-sync
  // backoff. The work below is not free — on the observed failure each tick
  // re-rendered the config, re-hashed it, re-wrote it and made a
  // `[provision/active-tasks]` call, dozens of times a second for three hours,
  // on a host with a CPU-spike history. Skipping leaves `channelConfigConverged`
  // true for this tick, which is correct: nothing was attempted, so nothing
  // failed, and the diff is already held live by the streak rather than by this
  // flag. `channelsChanged` above has already cleared the streak if the operator
  // changed anything, so a skip can only ever delay a retry of the SAME state.
  const channelSyncBackoff = channelSyncFailures.get(agent.agent_id);
  const channelSyncDeferred =
    channelSyncBackoff !== undefined && Date.now() < channelSyncBackoff.nextAttemptAt;
  if (!channelSyncDeferred) {
    // Advanced on every ATTEMPT, converged or not — that is what makes
    // "changed since I last tried" answerable above.
    channelSyncAttemptedSets.set(agent.agent_id, new Set(currentChannelIds));
  }
  if (refreshData.channel_configs && frameworkAdapter.writeChannelCredentials && !channelSyncDeferred) {
    if (agent.status === 'active') {
      for (const [channelId, entry] of Object.entries(refreshData.channel_configs)) {
        // ENG-5932: a quarantined channel is dropped from the agent's set —
        // stop re-writing its credentials/.mcp.json entry, and actively delete
        // any entry a prior poll (or a manager restart, where the
        // removeChannelCredentials diff path below is skipped because
        // previousChannelIds is empty) left behind. removeChannelCredentials
        // deletes the channel from `.mcp.json mcpServers` + re-syncs the
        // project copy — the membership change the reaper keys off — so the
        // dead channel stops being seen declared-but-missing. Idempotent: a
        // no-op once the entry is already gone. Empty set in shadow/off mode.
        if (quarantinedChannels.has(channelId)) {
          try {
            frameworkAdapter.removeChannelCredentials?.(agent.code_name, channelId);
          } catch (err) {
            log(`[channel-quarantine] failed to drop quarantined channel '${agent.code_name}/${channelId}' from .mcp.json: ${(err as Error).message}`);
          }
          continue;
        }
        if ((entry.status === 'active' || entry.status === 'pending') && entry.config) {
          // Track that this channel is active for this agent's profile
          if (!activeChannels.has(channelId)) {
            activeChannels.set(channelId, new Set());
          }
          activeChannels.get(channelId)!.add(agent.code_name);

          // Hash the config to detect changes — skip credential write if
          // identical to last poll. ENG-4439: also verify the on-disk state
          // actually holds the entry before skipping. The cache is purely
          // "what I intended to write"; if an external process modified the
          // agent's runtime config between polls (manual edit, migration,
          // another tool), the cache would otherwise refuse to re-write
          // forever and the channel stays broken until the manager restarts.
          // Adapters without `hasChannelCredentials` are treated as "trust
          // the cache" so older framework adapters keep their existing
          // behavior.
          //
          // ENG-4468: hash the CANONICAL (recursively key-sorted) JSON,
          // not plain `JSON.stringify`. Postgres JSONB doesn't preserve
          // key order, so the same semantic config can serialise to
          // different strings across polls — producing different hashes
          // and forcing constant rewrites. The churn cascaded into
          // .mcp.json drift and MCP subprocess instability on augmenteds-mbp
          // (Phil / Pepper). Canonicalising fixes it regardless of which
          // field moved.
          // ENG-4912 / ENG-4940: fold the channel-agnostic peer kill
          // switch into the hash input so a team-admin flip invalidates
          // the cache and forces a credential rewrite (which propagates
          // the PEER_DISABLED env to the spawned MCP child). Without
          // this the per-channel hash on `entry.config` alone would skip
          // the rewrite — entry.config has nothing to do with team
          // settings — and the kill switch wouldn't take effect until
          // the channel's own config drifted.
          //
          // Reads `peer_disabled` (enum) first, falls back to the legacy
          // `telegram_peer_disabled` boolean (true → 'all') so any
          // pre-migration row still gates correctly while the deploy is
          // mid-flight.
          const teamSettings = (refreshData.team as { settings?: Record<string, unknown> } | undefined)?.settings;
          const peerDisabledMode: 'off' | 'cross_team_only' | 'all' = (() => {
            const next = teamSettings?.['peer_disabled'];
            if (next === 'off' || next === 'cross_team_only' || next === 'all') return next;
            return teamSettings?.['telegram_peer_disabled'] === true ? 'all' : 'off';
          })();
          // ENG-9802: capture BEFORE the hash/write branches below, so it is
          // recorded on every tick — including the steady-state ticks that
          // `continue` on an unchanged hash. Capturing inside the write branch
          // would only ever see ticks that already decided to write, and the
          // restart decision needs the current policy on every tick to notice
          // that a previously-scheduled delivery is still outstanding.
          if (channelId === 'telegram') {
            telegramPolicyEnvForRestart = telegramPolicyEnvFromInputs(
              entry.config as Record<string, unknown>,
              { peerDisabledMode },
            );
          }
          // ENG-5363: team timezone propagates into the spawned channel
          // MCP child as the `TZ` env var so progress-message timestamps
          // ("last update: HH:MM:SS TZ") render in the agent's local
          // time instead of the host system zone (UTC on Lambda/EC2).
          // Folded into the hash so a team timezone change invalidates
          // the cache and triggers a credential rewrite.
          const agentTimezone = (() => {
            const tz = teamSettings?.['timezone'];
            return typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : undefined;
          })();
          const teamSettingsForHash =
            channelId === 'telegram' || channelId === 'slack' || channelId === 'direct-chat'
              ? { peer_disabled: peerDisabledMode, timezone: agentTimezone ?? null }
              : null;
          // ENG-4923: also fold the resolved peer roster into the hash
          // input. entry.config carries peer_agent_mode + peer_group_ids
          // natively (TelegramChannelConfig fields), but the CHARTER-
          // derived peer list does not — so a CHARTER edit that changes
          // declared peers would otherwise leave the hash unchanged and
          // never propagate the new TELEGRAM_PEERS env to the child.
          const crossTeamData = refreshData as {
            organization?: { cross_team_peer_intra_org?: 'unrestricted' | 'consent_required' } | null;
            cross_team_grants?: {
              inbound?: ReadonlyArray<{
                grant_id: string;
                granted_agent_bot_id: number | null;
                /** ENG-4970 / ENG-4973: Slack `U…` user_id of granted_agent_id. */
                granted_agent_slack_user_id?: string | null;
                revoked_at: string | null;
                expires_at: string | null;
              }>;
            };
            team_peer_bot_ids?: ReadonlyArray<number>;
            /** ENG-4970 / ENG-4973: Slack `U…` user_ids of same-team agents. */
            team_peer_slack_user_ids?: ReadonlyArray<string>;
          };
          // CodeRabbit on PR #868: only build a gateContext when the
          // /host/refresh response actually carries the ENG-4935 fields.
          // Older API versions (and any partial payloads) leave the
          // discriminator field absent — passing a synthesised context
          // with `[]` defaults would silently mask cross-team peers as
          // `intra_org_unrestricted` and never reach the documented
          // backwards-compat path in extractCharterTelegramPeers.
          //
          // CodeRabbit on PR #934: pick the discriminator per-channel.
          // The previous all-Telegram discriminator would short-circuit
          // the Slack backwards-compat fallback on managers running
          // against a /host/refresh that has the Telegram fields but
          // not yet the Slack ones (mid-rollout). Without this, same-team
          // Slack peers would get downgraded to intra_org_unrestricted /
          // null because gateContext is "present but with empty Slack
          // arrays" — not the same as "absent entirely".
          const refreshHasCrossTeamFields =
            channelId === 'telegram'
              ? Array.isArray(crossTeamData.team_peer_bot_ids)
              : channelId === 'slack'
                ? Array.isArray(crossTeamData.team_peer_slack_user_ids)
                : false;
          const gateContext: CharterPeerGateContext | undefined =
            refreshHasCrossTeamFields
              ? {
                  teamPeerBotIds: crossTeamData.team_peer_bot_ids ?? [],
                  teamPeerSlackUserIds: crossTeamData.team_peer_slack_user_ids ?? [],
                  crossTeamGrants: crossTeamData.cross_team_grants?.inbound ?? [],
                  crossTeamPeerIntraOrg:
                    crossTeamData.organization?.cross_team_peer_intra_org ?? 'unrestricted',
                }
              : undefined;
          // Peer roster folded into the hash so a CHARTER edit / grant
          // flip invalidates the cache. Telegram + Slack each carry
          // their own peer arrays in CHARTER; whichever channel we're
          // rendering picks the matching one.
          const peersForHash =
            channelId === 'telegram'
              ? extractCharterTelegramPeers(refreshData.charter?.raw_content ?? '', gateContext)
              : channelId === 'slack'
                ? extractCharterSlackPeers(refreshData.charter?.raw_content ?? '', gateContext)
                : null;
          // ENG-7349: full same-team Slack roster (independent of the CHARTER
          // peer declarations above) → SLACK_TEAM_PEER_USER_IDS on the slack
          // MCP child, gating the peer-enablement operator hint. Folded into
          // the config hash below so a roster change (teammate added/removed,
          // bot_user_id backfilled) re-renders the entry on the next poll.
          const slackTeamPeerUserIds =
            channelId === 'slack' && Array.isArray(crossTeamData.team_peer_slack_user_ids)
              ? crossTeamData.team_peer_slack_user_ids
              : undefined;
          // Bump `writeVersion` whenever the env block emitted by
          // `writeChannelCredentials` changes shape (new env var added,
          // existing var renamed, etc.). Existing deployments cache the
          // hash on disk, so without this bump a fresh CLI binary will
          // skip the credentials rewrite — the agent never picks up
          // the new env vars until config / team / peers actually
          // change for some unrelated reason.
          //
          // History:
          //   v1 — initial channel hash (ENG-4439)
          //   v2 — PR #912 forwards AGT_HOST / AGT_API_KEY / AGT_AGENT_ID
          //        to the Telegram MCP child. Required for ENG-4986
          //        observed-chat, ENG-4937 audit emission, and ENG-4909
          //        durable peer rate limiting to function.
          //   v3 — ENG-4970 / ENG-4974: Slack MCP child gets the same
          //        AGT auth trio + SLACK_PEERS / SLACK_PEERS_GATE /
          //        SLACK_PEER_AGENT_MODE / SLACK_PEER_GROUP_IDS env so
          //        cross-team peer collab finally works on Slack at
          //        runtime (previously the classifier had an empty peer
          //        list).
          //   v5 — ENG-5363: fold `agentTimezone` (from
          //        team.settings.timezone) into the hash + emit `TZ` env
          //        var on telegram / slack MCP children so the
          //        in-place progress heartbeat's "last update: HH:MM:SS"
          //        footer renders in the agent's local timezone instead
          //        of UTC.
          //   v4 — ENG-5155: fold `session_mode` into the hash. The
          //        rendered slack/direct-chat blocks branch on
          //        `isPersistent` (`claudecode/index.ts:2149`), so a
          //        oneshot→persistent flip changes the output of
          //        `writeChannelCredentials` — but pre-v4 the hash
          //        didn't notice and the rewrite was skipped forever.
          //        Sherlock (2026-05-17) hit this: created as oneshot
          //        before #1108, then flipped to persistent, but his
          //        `.mcp.json` kept the broken `npx -y @augmented/
          //        claude-code-channel-slack` fallback until we cleared
          //        the cache by hand. The version bump itself forces
          //        one rewrite per agent on first poll after upgrade,
          //        which auto-repairs any other agents in the same
          //        stuck state.
          const sessionModeForHash =
            (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
          // ENG-5841: effective sender_policy lives at refreshData.sender_policy
          // (top-level field surfaced by /host/refresh). Fold into the hash so a
          // flip on either org or per-agent column triggers a credentials rewrite
          // — see v4 ENG-5155 precedent (Sherlock's stuck cache) for what happens
          // when the hash misses a shape change.
          const senderPolicyForHash =
            (refreshData as Record<string, unknown>).sender_policy ?? null;
          //   v6 — ENG-5841: SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY env
          //        vars injected into slack-channel / teams-channel MCP env
          //        when sender_policy is set on org or agent. Bumps so deployed
          //        agents get a one-shot rewrite that picks up the new vars
          //        even though their on-disk entry would otherwise look "fresh".
          //   v7 — ENG-5842: manager_only mode adds
          //        SLACK_SENDER_POLICY_PRINCIPAL_ID / MSTEAMS_SENDER_POLICY_PRINCIPAL_ID
          //        when the resolved principal has a Slack user_id / Teams
          //        AAD id. The hash already covers refreshData.sender_policy
          //        (which now carries the principal sub-object), but the bump
          //        forces a rewrite for any agent that was already on v6
          //        when manager_only first hit their org.
          //   v8 — ENG-5843: internal_only adds SLACK_INTERNAL_ONLY +
          //        SLACK_HOME_TEAM_ID (and MSTEAMS_ equivalents) when the
          //        org sets internal_only:true. The hash covers
          //        refreshData.sender_policy.internal_only, but the bump
          //        forces a rewrite for agents on v7 the moment the org
          //        flips internal_only on.
          //   v9 — ENG-5871: team_only mode adds
          //        SLACK_SENDER_POLICY_TEAM_PRINCIPAL_IDS /
          //        MSTEAMS_SENDER_POLICY_TEAM_PRINCIPAL_IDS comma-separated
          //        lists when the resolved team has members with the
          //        relevant contact prefs. The hash already covers
          //        refreshData.sender_policy.team_principals, but the bump
          //        forces a rewrite for agents already on v8 the moment
          //        team_only first gets selected.
          //
          // ENG-6600: the writeVersion above is a MANUAL knob. Every entry
          // in the v4–v9 history is a code-only change to what the adapter
          // RENDERS into a channel entry (a new env var on slack/teams) that
          // the input-only hash couldn't see, so a human had to remember to
          // bump it. ENG-6563/6582 added AGT_TURN_INITIATOR_FILE to the
          // slack/telegram env and FORGOT the bump, so the per-turn initiator
          // marker write stayed dormant fleet-wide (the read path shipped,
          // the write path never reached existing agents). Folding the
          // build-time agt CLI version into the hash makes that class of bug
          // structurally impossible: any release that changes channel
          // rendering (every adapter change auto-publishes a new CLI, ENG-5795)
          // gets a new hash, so the entry is re-rendered ONCE per self-update
          // and the code-only change propagates without a writeVersion bump.
          //
          // This does NOT cause restart storms (the AC#3 concern):
          //   - The hash only moves on a CLI upgrade, never per poll, so the
          //     `knownChannelConfigHashes` skip below still suppresses the
          //     steady-state rewrite.
          //   - A re-render that produces identical bytes is a no-op for the
          //     `.mcp.json` drift watcher (it hashes content, not mtime).
          //   - A re-render that adds/changes an env value is a VALUE-ONLY
          //     drift: the drift watcher adopts the new baseline WITHOUT
          //     bouncing the live session (decideMcpRestartOnDrift,
          //     ENG-5285/ENG-5537). Only a server-membership change restarts.
          // In dev (tsx) agtCliVersion is the stable literal 'dev', so this
          // adds no churn there.
          //
          // writeVersion is kept as an explicit-intent override (e.g. to force
          // a one-shot rewrite without cutting a release); the CLI-version fold
          // now subsumes the need to bump it for ordinary code-only changes.
          // The hash itself lives in the pure, unit-tested
          // computeChannelConfigHash (channel-config-hash.ts, ENG-6600).
          const configHash = computeChannelConfigHash({
            cliVersion: agtCliVersion,
            config: entry.config,
            // ENG-7349: the slack team roster rides the `team` input (it is
            // team-level data, not a CHARTER peer declaration).
            team:
              slackTeamPeerUserIds !== undefined
                ? { ...teamSettingsForHash, slack_team_peer_user_ids: slackTeamPeerUserIds }
                : teamSettingsForHash,
            peers: peersForHash,
            sessionMode: sessionModeForHash,
            senderPolicy: senderPolicyForHash,
            // ENG-9350: fold the delivery mode in so a `spawn-inject-channel-
            // secrets` flip invalidates the cache and re-runs the writer (evict
            // on enable / restore on disable). Same per-agent value the writer
            // call below passes as channelSecretDelivery.
            channelSecretDelivery: shouldInjectChannelSecrets(agent.agent_id) ? 'spawn-env' : 'file',
          });
          const cacheKey = `${agent.agent_id}:${channelId}`;
          let onDiskPresent = true;
          try {
            onDiskPresent =
              frameworkAdapter.hasChannelCredentials?.(agent.code_name, channelId) ?? true;
          } catch (err) {
            // A faulty adapter hook must not abort the provisioning loop.
            // Treat the failure as "on-disk entry missing" so we force a
            // rewrite rather than silently trusting a stale cache.
            log(`hasChannelCredentials failed for '${agent.code_name}/${channelId}': ${(err as Error).message} — forcing credential rewrite`);
            onDiskPresent = false;
          }
          if (agentState.knownChannelConfigHashes.get(cacheKey) === configHash && onDiskPresent) {
            continue;
          }
          // ENG-4468: capture prevHash BEFORE the on-disk-missing branch
          // can delete it, so the `reason` label below is accurate. Reading
          // after the delete would mislabel on-disk-missing as first-write.
          const prevHash = agentState.knownChannelConfigHashes.get(cacheKey);
          const reason = !prevHash
            ? 'first-write'
            : !onDiskPresent
              ? 'on-disk-missing'
              : 'content-changed';
          if (!onDiskPresent && prevHash) {
            log(`Cached hash for '${agent.code_name}/${channelId}' but on-disk entry missing — re-writing credentials`);
            agentState.knownChannelConfigHashes.delete(cacheKey);
          }
          try {
            const sessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
            // ENG-4573: pass agent_id through so the slack-channel MCP can
            // wire AGT_AGENT_ID for slack.ask_user (block_kit_ask_user_enabled).
            // ENG-4912 / ENG-4940: thread the channel-agnostic peer kill
            // switch enum through. peerDisabled is the canonical signal
            // (PEER_DISABLED env on the child); telegramPeerDisabled is
            // a derived boolean kept for backwards compat — the MCP
            // child accepts both during the rollout.
            const peerDisabled =
              channelId === 'telegram' || channelId === 'slack'
                ? peerDisabledMode
                : undefined;
            const telegramPeerDisabled =
              channelId === 'telegram' ? peerDisabledMode === 'all' : undefined;
            // ENG-4923: assemble the per-agent peer roster from CHARTER
            // multi_agent.telegram_peers. Each entry is the CHARTER's
            // declared { code_name, bot_id }; agent_id is left empty
            // here because the manager doesn't yet have a cheap way to
            // resolve it from the team roster (would need either an
            // extension to /host/refresh or a separate /host/team-agents
            // call). The classifier doesn't gate on agent_id — only on
            // bot_id — so leaving it blank is safe today; ENG-4904's
            // runtime can fetch the agent_id when it needs to surface
            // the peer's identity in the prompt preamble.
            const telegramPeers =
              channelId === 'telegram'
                ? extractCharterTelegramPeers(refreshData.charter?.raw_content ?? '', gateContext)
                : undefined;
            // ENG-4970 / ENG-4974: Slack mirror. Parallel resolution
            // from CHARTER multi_agent.slack_peers — same gate-path
            // model, keyed on bot_user_id.
            const slackPeers =
              channelId === 'slack'
                ? extractCharterSlackPeers(refreshData.charter?.raw_content ?? '', gateContext)
                : undefined;
            // ENG-5841: forward sender_policy only on channels that actually
            // enforce it (slack-channel + teams-channel MCPs). Other channels
            // ignore the option, but threading it everywhere would mean
            // updating every adapter call site if the shape ever changes.
            // ENG-9662: the enforced set now comes from @augmented/core, which
            // is the same consolidation ENG-7757 did for SENDER_POLICY_MODES
            // after copies of THOSE literals drifted. This was one of four
            // copies; the API had none at all, which is why it was resolving
            // and shipping Telegram chat ids to a consumer that never existed.
            const senderPolicyForCall =
              senderPolicyIsEnforcedOn(channelId)
                ? ((refreshData as Record<string, unknown>).sender_policy as
                    | {
                        // ENG-5843: 'all' is now a valid mode value when
                        // paired with internal_only:true. Adapter handles
                        // both shapes.
                        mode: 'all' | 'agents_only' | 'team_agents_only' | 'manager_only';
                        team_id?: string;
                        // ENG-5842: principal IDs for manager_only mode.
                        principal?: {
                          slack_user_id?: string;
                          telegram_chat_id?: string;
                          teams_aad_object_id?: string;
                        };
                        // ENG-5843: org-boundary gate flag.
                        internal_only?: boolean;
                        source: 'agent' | 'org';
                      }
                    | null
                    | undefined) ?? undefined
                : undefined;
            // ENG-6155: forward the agent's avatar URL to the slack-channel
            // MCP only — it's the only consumer (sets it as the bot's Slack
            // profile photo on first connect). null/empty → option omitted,
            // so the MCP simply doesn't attempt a photo set.
            // ENG-6245: resolveAvatarEnvUrl drops a data-URI / oversized value
            // that would E2BIG the slack MCP spawn (posix_spawn arg limit) and
            // silently brick the channel. The adapter applies the same guard
            // structurally; here we also emit a one-line WARN so the skip is
            // visible in manager.log instead of only surfacing as an E2BIG
            // deep in mcp-logs-slack.
            let agentAvatarUrl: string | undefined;
            if (channelId === 'slack') {
              const rawAvatarUrl =
                ((refreshData.agent as Record<string, unknown>).avatar_url as
                  | string
                  | null
                  | undefined) ?? undefined;
              const avatarResolution = resolveAvatarEnvUrl(rawAvatarUrl);
              agentAvatarUrl = avatarResolution.url ?? undefined;
              if (
                avatarResolution.skipReason &&
                avatarResolution.skipReason !== 'empty'
              ) {
                log(
                  `WARN: skipped Slack bot avatar for '${agent.code_name}' ` +
                    `(reason=${avatarResolution.skipReason}` +
                    `${avatarResolution.bytes !== undefined ? `, bytes=${avatarResolution.bytes}` : ''}) ` +
                    `— not a hosted URL / exceeds ${MAX_AVATAR_ENV_URL_BYTES}B cap; ` +
                    `Slack runs without the custom photo. Upload the avatar to ` +
                    `storage and store the hosted URL to restore it (ENG-6245).`,
                );
              }
            }
            // ENG-9739: the ENG-9652 precondition that used to sit here is GONE.
            //
            // It refused to write channel credentials when the channel's
            // "backing integration" was not installed, keyed on a map of five
            // channel->integration pairs. None of those integrations exist, and
            // that is not an oversight to be corrected by adding them:
            // "We don't have integrations for channels... They are separate
            // concepts" (Brad, 2026-09-01). Channels and integrations are
            // distinct by design, so no channel has a backing integration and
            // this check could never be satisfied by anyone.
            //
            // What it did instead: reached its positive-knowledge branch for
            // every agent whose integration set had been observed, terminated
            // the sync, and told operators to install something with no console
            // entry. Six agents live when it was found, one for 8.6 days. It
            // only bit agents whose sync was actively trying, so established
            // agents looked fine and NEW ones failed silently — a customer
            // messaged a brand-new bot three times and got nothing while the
            // console read "Token valid".
            //
            // ENG-9652's underlying complaint was real: sheraz spent 16.5h and
            // 198 attempts on a write that could not land. Removing this
            // restores that retry, and that is the honest state — the cause of
            // sheraz's non-convergence is undiagnosed, and a terminal condition
            // guessed at is what produced this outage. It needs its own ticket,
            // not another premise.
            // ENG-9840: capture the outcome. Pre-ENG-9840 adapters return
            // nothing, and `undefined` is treated as "cannot say why", never as
            // success — the landed check below remains the verdict either way.
            const writeOutcome = frameworkAdapter.writeChannelCredentials(
              agent.code_name,
              channelId,
              entry.config as Record<string, unknown>,
              {
                sessionMode,
                agentId: agent.agent_id,
                telegramPeerDisabled,
                peerDisabled,
                telegramPeers,
                slackPeers,
                // ENG-7349: same-team roster for the peer-enablement hint.
                slackTeamPeerUserIds,
                agentTimezone,
                senderPolicy: senderPolicyForCall,
                agentAvatarUrl,
                // ENG-9350 (epic ENG-9347): in spawn-env mode the writer null-
                // deletes the channel token from `.env.integrations` (evicting any
                // stale copy) and leaves the `.mcp.json` `${VAR}` template — the
                // manager injects the value at spawn via `tmux -e` instead. The
                // `.mcp.json` server entry is still written, so the landed check
                // below (which reads `.mcp.json`, not the file) is unaffected.
                channelSecretDelivery: shouldInjectChannelSecrets(agent.agent_id)
                  ? 'spawn-env'
                  : 'file',
              },
            );
            // ENG-8290: VERIFY the write landed before believing it.
            //
            // `writeChannelCredentials` has silent `return` paths that write
            // nothing — a missing `bot_token`, and a `writeMcpJsonGuarded`
            // rejection when the whole merged file fails the secret lint or the
            // rendered-config validator. Neither throws. So "did not throw" was
            // being read as "wrote", and the manager logged success and cached
            // the hash for a write that never touched disk.
            //
            // ENG-9840: it no longer returns `void` — it returns a
            // ChannelWriteOutcome naming which of those paths it took, so the
            // message below states the cause instead of listing two guesses.
            // The landed check stays exactly as it is: the writer's own claim
            // is never the verdict, only its explanation.
            //
            // The next poll's presence check then correctly found the entry
            // missing, deleted the cache, rewrote the identical bytes, and
            // re-cached the identical hash. Observed on dream-host: penny's
            // telegram rewritten every ~40s indefinitely, `hash=e6a373bf,
            // prev=e6a373bf` every time, while the channel sat at `pending`
            // because nothing ever observed the credential as present.
            //
            // The honest success criterion is not "no exception" — it is "the
            // credential is now on disk", and that is directly checkable with
            // the same hook the loop already uses. Slack escaped this only
            // because its entry was already present, so the writer was never
            // called for it.
            const landed =
              frameworkAdapter.hasChannelCredentials?.(agent.code_name, channelId) ?? true;
            if (!landed) {
              // Do NOT cache the hash. Caching it here is what made the loop
              // silent AND non-convergent: the log claimed success while the
              // next tick found nothing, forever, with no error anywhere.
              channelConfigConverged = false;
              channelConfigFailureReason = describeChannelWriteFailure(
                writeOutcome,
                channelId,
                reason,
              );
              const attempts = (channelWriteFailureCounts.get(cacheKey) ?? 0) + 1;
              channelWriteFailureCounts.set(cacheKey, attempts);
              // Escalate rather than repeat an info line 90 times an hour. A
              // credential path that logs "missing, rewriting" as steady state
              // means a genuinely missing credential looks exactly like normal
              // operation — which is how this ran unnoticed.
              if (attempts === 1 || attempts % CHANNEL_WRITE_FAILURE_ESCALATE_EVERY === 0) {
                log(
                  `ERROR: channel credentials for '${agent.code_name}/${channelId}' did NOT land on disk (attempt ${attempts}). ` +
                    `${channelConfigFailureReason} ` +
                    `The channel will stay unusable until this is resolved; not re-logging every cycle.`,
                );
              }
            } else {
              if (channelWriteFailureCounts.delete(cacheKey)) {
                log(`Channel credentials for '${agent.code_name}/${channelId}' landed on disk after ${reason} — recovered.`);
              }
              agentState.knownChannelConfigHashes.set(cacheKey, configHash);
              saveChannelHashCache();
              log(`Channel credentials written for '${agent.code_name}/${channelId}' (reason=${reason}, hash=${configHash.slice(0, 8)}${prevHash ? `, prev=${prevHash.slice(0, 8)}` : ''})`);
            }
          } catch (err) {
            channelConfigConverged = false;
            channelConfigFailureReason = `writing ${channelId} credentials threw: ${(err as Error).message}`;
            log(`Failed to write channel credentials for '${agent.code_name}/${channelId}': ${(err as Error).message}`);
          }
        }
      }

    } else if (agent.status === 'paused') {
      // Paused agents: disable channels in their profile
      if (frameworkAdapter.setChannelEnabled) {
        for (const channelId of Object.keys(refreshData.channel_configs)) {
          frameworkAdapter.setChannelEnabled(channelId, false, agent.code_name);
        }
      }
    }
  }

  // CodeRabbit on PR #773 (round 2): only advance the per-agent channel
  // diff cache when the on-disk credential state actually converged. If
  // a remove/write failed, leave `previousChannelIds` in place so the
  // next refresh tick re-sees the diff and retries the credential sync.
  // Otherwise we'd swallow the change forever the moment one transient
  // I/O failure occurred — and worse, fire a hot-reload restart on top
  // of broken credential state.
  if (channelConfigConverged) {
    // ENG-9252: a streak that ends must SAY so. Recovery inferred from the
    // absence of a line is exactly how three hours of this went unnoticed —
    // and with the summary line now throttled, silence is even less
    // distinguishable from health than it was before.
    const ended = channelSyncFailures.get(agent.agent_id);
    if (ended && !channelSyncDeferred) {
      channelSyncFailures.delete(agent.agent_id);
      const stuckForSeconds = Math.floor((Date.now() - ended.since) / 1000);
      log(
        `[channels] Credential sync for '${agent.code_name}' converged after ${ended.attempts} failed `
          + `attempt(s) over ${stuckForSeconds}s — recovered (ENG-9252)`,
      );
    }
    if (!channelSyncDeferred) {
      agentState.knownChannels.set(agent.agent_id, currentChannelIds);
    }
  } else {
    // ENG-9252: throttled, and it now carries the reason. The line this
    // replaced printed on every tick and said only that something had not
    // converged, while the site that knew WHY escalated once per ninety
    // attempts.
    const state = recordChannelSyncFailure(
      channelSyncFailures,
      agent.agent_id,
      channelConfigFailureReason || 'reason not recorded',
      Date.now(),
    );
    if (shouldLogChannelSyncFailure(state)) {
      state.loggedAtAttempt = state.attempts;
      const stuckForSeconds = Math.floor((Date.now() - state.since) / 1000);
      const waitMs = channelSyncBackoffMs(state.attempts);
      log(
        `[channels] Credential sync did not converge for '${agent.code_name}' — `
          + `attempt ${state.attempts}, stuck ${stuckForSeconds}s, next retry in ${Math.round(waitMs / 1000)}s. `
          + `Cause: ${state.lastReason}. Diff left live (ENG-9252).`,
      );
    }
  }

  // ENG-4807: when the channel set changes for a live persistent claude-code
  // session, restart the session so its launch flags reflect the new
  // channels. The flags baked at spawn time
  // (`--dangerously-load-development-channels` and `--allowedTools`) don't
  // pick up new channels without a respawn — the MCP loads but its
  // `notifications/claude/channel` get dropped, and tool calls to the new
  // channel's MCP (e.g. `telegram.reply`) get auto-denied. Pre-fix this
  // required an operator to manually run `tmux kill-session`. See ENG-4807
  // for the full diagnosis from Vigil's Telegram setup. The MCP-toolkit
  // hot-reload at line ~2926 already follows the same shape; this mirrors
  // it for channels.
  //
  // The decision logic lives in a pure helper so it's unit-testable
  // without standing up the rest of processAgent.
  //
  // CodeRabbit on PR #773 (round 2): suppress restart when credential
  // sync didn't converge — restarting on broken state would just reload
  // the session with stale flags that match the pre-failure state, then
  // the next tick's successful retry sees no diff (because previousChannelIds
  // is still the pre-change set) and never restarts again. Wait for
  // sync to land first.
  // ENG-9252 (CodeRabbit on #4840): a DEFERRED tick is not a converged one.
  // Nothing was written, so the on-disk credential state is exactly as
  // unconverged as it was — but `channelConfigConverged` stays true because
  // nothing was attempted and so nothing failed. Every consumer below asks "did
  // the credential state converge this tick?" and must be told no, or a deferred
  // tick silently unblocks the work a failed tick correctly blocks: a restart
  // against stale flags, a sender-policy baseline advanced past a write that
  // never happened, a secret-drift respawn against a partially written
  // `.env.integrations`. The backoff must slow the retry down without ever
  // looking like success to anything downstream.
  const channelConfigConvergedThisTick = channelConfigConverged && !channelSyncDeferred;

  // ENG-9517: re-observe each launchable channel's credential on disk and record
  // the verdict for the heartbeat, so the control plane can tell an installed
  // channel from one that was written to the database and never reached the box.
  //
  // A SEPARATE PASS, not a value threaded out of the sync loop above, and that
  // is deliberate. The loop only calls `hasChannelCredentials` on the path where
  // a write was actually needed — a channel whose config hash is unchanged skips
  // the write entirely, so on every steady-state tick it would yield no verdict
  // at all. Reporting only what the writer happened to touch would mean a
  // healthy channel goes silent after its first tick, and silence is exactly
  // what this field exists to stop being ambiguous.
  //
  // Gated on the same conditions as the sync block. On a DEFERRED tick nothing
  // was checked, so we clear rather than re-report: holding the previous verdict
  // would keep asserting a stale success through a backoff window that exists
  // precisely because something is wrong. No verdict is the honest wire value
  // for "did not look", and the consumer treats it as such.
  if (agent.status === 'active' && !channelSyncDeferred && frameworkAdapter.hasChannelCredentials) {
    const verdicts = new Map<string, ChannelCredentialVerdict>();
    for (const channelId of currentChannelIds) {
      // A quarantined channel is deliberately absent from disk (ENG-5932
      // actively deletes its entry), so reporting it as an unverified failure
      // would manufacture an alarm out of the system working as designed.
      if (quarantinedChannels.has(channelId)) continue;
      let onDisk = false;
      try {
        onDisk = frameworkAdapter.hasChannelCredentials(agent.code_name, channelId);
      } catch (err) {
        // A throwing probe is not evidence of a present credential. Fail to
        // `false` and carry the cause: the alternative — treating an unreadable
        // .mcp.json as "fine" — is the same optimistic default that let a
        // silently skipped write look like a successful one.
        onDisk = false;
        log(
          `[channels] credential verification threw for '${agent.code_name}/${channelId}': `
            + `${(err as Error).message} — reporting as unverified (ENG-9517)`,
        );
      }
      verdicts.set(channelId, {
        verified: onDisk,
        ...(onDisk ? {} : { reason: channelConfigFailureReason || 'credential not present on disk' }),
      });
    }
    if (verdicts.size > 0) channelCredentialVerdicts.set(agent.agent_id, verdicts);
    else channelCredentialVerdicts.delete(agent.agent_id);
  } else {
    channelCredentialVerdicts.delete(agent.agent_id);
  }
  const restartDecision = channelConfigConvergedThisTick
    ? decideChannelRestart({
        previousChannelIds,
        currentChannelIds,
        sessionMode: (refreshData.agent as Record<string, unknown>).session_mode as string | undefined,
        framework: agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK,
        sessionHealthy: isSessionHealthy(agent.code_name),
      })
    : { restart: false, added: [], removed: [] };
  // ENG-5932: 0-restart-on-removal. When the ONLY channel-set delta is the
  // removal of quarantined channel(s) — the transition poll where the
  // quarantine first drops a dead channel from `currentChannelIds` — do NOT
  // bounce the session. The dead channel never had a live MCP child, so
  // restarting to "drop" it achieves nothing and needlessly SIGINTs the
  // healthy sibling channels (the exact Kylie harm we're fixing). A real
  // add alongside the removal still restarts (the new channel needs it).
  const quarantineOnlyRemoval =
    restartDecision.restart &&
    restartDecision.added.length === 0 &&
    restartDecision.removed.length > 0 &&
    restartDecision.removed.every((c) => quarantinedChannels.has(c));
  // The post-suppression truth: did we ACTUALLY schedule a channel-set restart?
  // Downstream (decideSenderPolicyRestart) keys off this to avoid double-
  // restarting — so it must see the suppressed value, not raw
  // restartDecision.restart. Otherwise a same-tick sender_policy change would
  // be suppressed "because the channel-set restart will cover it" while that
  // restart was itself suppressed — leaving the live Slack/Teams MCP on a stale
  // policy with knownSenderPolicyHashes already advanced (CodeRabbit PR #1686).
  const channelSetRestartScheduled = restartDecision.restart && !quarantineOnlyRemoval;
  if (quarantineOnlyRemoval) {
    log(
      `[channel-quarantine] suppressing channel-set-change restart for '${agent.code_name}' — only delta is quarantined-channel removal [${restartDecision.removed.join(', ')}] (0-restart-on-removal, ENG-5932)`,
    );
  }
  if (channelSetRestartScheduled) {
    const reasonParts: string[] = [];
    if (restartDecision.added.length > 0) reasonParts.push(`added=${restartDecision.added.join(',')}`);
    if (restartDecision.removed.length > 0) reasonParts.push(`removed=${restartDecision.removed.join(',')}`);
    const reason = reasonParts.join(' ');
    log(`[hot-reload] Channel set changed for '${agent.code_name}' (${reason}) — restarting session`);

    const restartNotice = restartDecision.added.length > 0
      ? `New channels have been wired up (${restartDecision.added.join(', ')}). Note: channels require a session restart to attach their MCP servers as channel listeners. Your manager will restart your session shortly.`
      : `Channels were removed (${restartDecision.removed.join(', ')}). Your manager will restart your session shortly so the launch flags drop those channels.`;
    const delivered = await injectMessage(agent.code_name, 'system', restartNotice + BACK_ONLINE_GREETING_GUIDANCE, { task_name: 'channel-update' }, log).catch(() => false);
    const delay = delivered ? 8_000 : 3_000;
    if (!delivered) {
      log(`[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`);
    }
    // ENG-6491: tag an ADD restart 'channel-set-change' (not the default
    // 'hot-reload-mcp') so the maintenance-window gate can exempt it from the
    // off-peak WINDOW while still deferring-until-idle — a newly-added channel
    // must come online as soon as the agent is quiet, not hours later. The window
    // exemption is for ADDS only (a pure removal has no "comes online" urgency, so
    // it stays 'hot-reload-mcp' / fully window-gated). Integration churn likewise
    // stays 'hot-reload-mcp'.
    const isChannelAddRestart = restartDecision.added.length > 0;
    const addedChannels = [...restartDecision.added];
    // ENG-6491 (CodeRabbit): write the DM-notice marker at the moment the restart
    // ACTUALLY fires (beforeStop), not now — scheduleSessionRestart can idle-defer
    // and re-arm for minutes, and a marker stamped at schedule time could age past
    // the boot-freshness window and be skipped for the restart that just happened.
    // On a channel ADD only; drops a per-channel marker so the Slack/Telegram MCPs
    // notice DM users active in the last few minutes (shape must stay byte-
    // compatible with packages/mcp/src/dm-restart-notice.ts). Best-effort.
    const writeDmNoticeMarkers = isChannelAddRestart
      ? (): void => {
          try {
            const agentAugmentedDir = join(homedir(), '.augmented', agent.code_name);
            mkdirSync(agentAugmentedDir, { recursive: true });
            const markerJson = JSON.stringify({
              version: 1,
              at: new Date().toISOString(),
              added: addedChannels,
            });
            for (const file of ['slack-channel-add-restart.json', 'telegram-channel-add-restart.json']) {
              atomicWriteFileSync(join(agentAugmentedDir, file), markerJson);
            }
          } catch (err) {
            log(`[hot-reload] channel-add DM-notice marker write failed for '${agent.code_name}' (non-fatal): ${(err as Error).message}`);
          }
        }
      : undefined;
    scheduleSessionRestart(
      agent.code_name,
      delay,
      'new channel set',
      isChannelAddRestart ? 'channel-set-change' : 'hot-reload-mcp',
      writeDmNoticeMarkers,
    );
  }

  // ENG-5858: orthogonal restart trigger for sender_policy changes.
  // Why this exists despite the channel-cred hash already invalidating
  // on the same change: writeChannelCredentials rewrites .mcp.json on
  // disk, but the running slack-channel / teams-channel MCP child has
  // already read SLACK_SENDER_POLICY (and friends) from process.env at
  // boot. The two existing restart paths refuse to help — channel-set
  // restart only fires on membership changes, and the .mcp.json drift
  // watcher (ENG-5537) explicitly suppresses value-only restarts to
  // stop Composio URL rotation flapping the breaker. Keying this trigger
  // on the sender_policy hash alone means composio URL churn can't
  // accidentally fire it.
  // ENG-9252: `channelConfigConvergedThisTick`, not `channelConfigConverged` —
  // a deferred tick skipped the writeChannelCredentials call that materialises
  // SLACK_SENDER_POLICY and the behaviour env, so advancing the baseline here
  // would record a write that never happened and a restart would reload stale
  // env against it.
  if (channelConfigConvergedThisTick) {
    // CR on PR #1622: gate on whether this agent actually has a channel
    // that *consumes* the SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY env
    // vars. Only slack-channel and teams-channel read them at boot — a
    // telegram-only or direct-chat-only agent has no MCP child that
    // depends on the policy env, so a restart on an org-level policy
    // change would be wasted work (and a confusing user-visible blip).
    // Mirrors the same channel-id filter on the writeChannelCredentials
    // call site (~Lines 4433-4454) where the env vars are forwarded.
    // ENG-9662: second copy of the same set, now one shared answer. These two
    // sites HAVE to agree — a restart decision that disagrees with the env
    // write either restarts for env that did not change, or writes env that
    // never gets loaded.
    const hasSenderPolicyChannel = [...currentChannelIds].some((id) =>
      senderPolicyIsEnforcedOn(id),
    );
    const senderPolicyForRestartHash =
      (refreshData as Record<string, unknown>).sender_policy ?? null;
    // ENG-7435: hash via the shared helper, which is value-blind on the
    // team_principals id SETS. An unordered principal list (the ~10-min respawn
    // root cause) can no longer oscillate this hash into a false restart.
    const senderPolicyHash = computeSenderPolicyRestartHash(senderPolicyForRestartHash);
    const senderPolicyRestrictive = isSenderPolicyRestrictive(senderPolicyForRestartHash);
    const prevSenderPolicyHash = agentState.knownSenderPolicyHashes.get(agent.agent_id);
    const spSessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
    const spFramework = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
    const spSessionHealthy = isSessionHealthy(agent.code_name);
    const senderPolicyDecision = hasSenderPolicyChannel
      ? decideSenderPolicyRestart({
          previousHash: prevSenderPolicyHash,
          currentPolicyRestrictive: senderPolicyRestrictive,
          currentHash: senderPolicyHash,
          sessionMode: spSessionMode,
          framework: spFramework,
          sessionHealthy: spSessionHealthy,
          channelSetRestartAlreadyScheduled: channelSetRestartScheduled,
        })
      : { restart: false, firstPoll: prevSenderPolicyHash === undefined, changed: false };
    if (senderPolicyDecision.restart) {
      // ENG-7771: a first-poll restart is the fail-safe for an UNKNOWN
      // delivery baseline (transition deploy, lost/corrupt sidecar) with a
      // restrictive policy — nothing "changed"; the manager just cannot
      // prove the live session enforces the restriction, so it restarts to
      // guarantee it (fail closed). Worded honestly: telling the agent its
      // policy "has been updated" would be false.
      const isVerifyRestart = senderPolicyDecision.firstPoll;
      log(
        isVerifyRestart
          ? `[hot-reload] sender_policy delivery baseline unknown for '${agent.code_name}' with a restrictive policy ` +
              `(→ ${senderPolicyHash.slice(0, 8)}) — restarting session to guarantee enforcement (fail closed, ENG-7771)`
          : `[hot-reload] sender_policy changed for '${agent.code_name}' ` +
              `(${prevSenderPolicyHash?.slice(0, 8) ?? 'first'} → ${senderPolicyHash.slice(0, 8)}) — restarting session`,
      );
      const notice = isVerifyRestart
        ? 'Your manager was updated and needs to restart your session to make sure your configured sender policy is enforced by the channel MCP servers.'
        : 'Your sender_policy has been updated. Restarting session shortly so the channel MCP servers pick up the new gate.';
      const delivered = await injectMessage(
        agent.code_name,
        'system',
        notice + BACK_ONLINE_GREETING_GUIDANCE,
        { task_name: 'sender-policy-update' },
        log,
      ).catch(() => false);
      const delay = delivered ? 8_000 : 3_000;
      if (!delivered) {
        log(
          `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
        );
      }
      scheduleSessionRestart(
        agent.code_name,
        delay,
        isVerifyRestart ? 'sender policy verify (unknown baseline)' : 'sender_policy change',
        'sender-policy-change',
      );
    }
    // Always advance the IN-MEMORY baseline once it converged, even on first
    // poll or when we suppressed the restart (other framework, oneshot, etc.).
    // The hash itself only depends on the policy, so storing it costs
    // nothing and prevents a restart from queueing on the *next* tick if
    // the agent's sessionHealthy state flips true later.
    agentState.knownSenderPolicyHashes.set(agent.agent_id, senderPolicyHash);
    // ENG-7771: advance the PERSISTED delivery baseline only when delivery is
    // not outstanding. When a restart (ours, or the same-tick channel-set
    // restart that coalesced it) still has to deliver the env, the sidecar
    // must keep the OLD hash so a manager restart that loses the deferred
    // timer reloads it and reschedules — persisting the new hash at schedule
    // time would mark the change delivered and silently drop it (the exact
    // ENG-7771 bug, one layer down). Delivery is NOT outstanding when:
    //   - no channel consumes the policy env, or the session is oneshot /
    //     another framework / not currently alive: the next spawn reads the
    //     freshly written .mcp.json (spawns happen AFTER this block in the
    //     same processAgent pass), or
    //   - first poll with a non-restrictive policy: nothing a stale env
    //     could expose; baselining without a restart is the accepted trade
    //     that keeps the transition deploy from respawning the whole fleet.
    // Restart-bearing paths persist in the restart timer's post-stop hook.
    // With a LIVE consumer the sidecar is never advanced here — on steady
    // no-change ticks it either already matches or is deliberately lagging a
    // still-deferred restart (the in-memory map advanced at schedule time, so
    // every tick of a multi-hour defer window reads as "no change").
    const hasLiveSenderPolicyConsumer =
      hasSenderPolicyChannel &&
      spSessionHealthy &&
      spSessionMode === 'persistent' &&
      spFramework === 'claude-code';
    if (!hasLiveSenderPolicyConsumer) {
      persistDeliveryBaselineEntry('senderPolicy', agent.agent_id, senderPolicyHash);
    } else if (senderPolicyDecision.firstPoll && !senderPolicyRestrictive) {
      if (persistedDeliveryBaseline.senderPolicy.get(agent.agent_id) !== senderPolicyHash) {
        log(
          `[sender-policy] baseline established without restart for '${agent.code_name}' (policy is non-restrictive); ` +
            `a policy loosened while the baseline was unknown requires an agent restart to take effect (ENG-7771)`,
        );
      }
      persistDeliveryBaselineEntry('senderPolicy', agent.agent_id, senderPolicyHash);
    }

    // ENG-6024: orthogonal restart trigger for Teams behaviour-field changes
    // (thread_auto_follow / channel_response_mode / adaptive_cards_*) saved
    // via the webapp's Teams Advanced panel. Same last-hop gap as the
    // sender-policy trigger above: the teams-channel MCP child read those
    // env vars once at boot, the channel-set restart only fires on
    // membership change, and the .mcp.json drift watcher suppresses
    // value-only restarts (ENG-5537). Hashing only the behaviour subset
    // means token/credential churn can't fire it — the subset changes only
    // on an explicit operator save.
    // ENG-6035: tracks whether the msteams trigger scheduled a restart this
    // tick so the slack trigger below doesn't schedule a duplicate.
    let msteamsBehaviourRestartScheduled = false;
    // ENG-9802: the telegram trigger below is fourth in this chain and must not
    // schedule a duplicate on a tick where an earlier sibling already did.
    let slackBehaviourRestartScheduled = false;
    // ENG-9861: the standing-rules trigger is FIFTH in this chain, and the
    // telegram decision is scoped inside its own `if (hasTelegramChannel)`
    // block. Hoisting the outcome as a flag is what lets the rules trigger
    // coalesce against it the same way the others do.
    let telegramPolicyRestartScheduled = false;
    if (currentChannelIds.has('msteams')) {
      const msteamsEntry = refreshData.channel_configs?.['msteams'];
      const behaviourSubset = extractMsTeamsBehaviourSubset(
        msteamsEntry?.config as Record<string, unknown> | null | undefined,
      );
      const behaviourHash = createHash('sha256')
        .update(canonicalJson(behaviourSubset))
        .digest('hex');
      const prevBehaviourHash = agentState.knownMsTeamsBehaviourHashes.get(agent.agent_id);
      // ENG-7779: the baseline is now hydrated from the on-disk sidecar, so a
      // genuine first poll means the manager truly does not know what the
      // running teams-channel MCP enforces. The classifier is honest about
      // the stakes: no Teams behaviour field is access-control, so an
      // unknown baseline re-baselines without a restart (always
      // non-restrictive today).
      const msteamsBehaviourRestrictive = isMsTeamsBehaviourRestrictive(behaviourSubset);
      const behaviourDecision = decideMsTeamsBehaviourRestart({
        previousHash: prevBehaviourHash,
        currentPolicyRestrictive: msteamsBehaviourRestrictive,
        currentHash: behaviourHash,
        sessionMode: spSessionMode,
        framework: spFramework,
        sessionHealthy: spSessionHealthy,
        // A channel-set or sender-policy restart scheduled this tick will
        // respawn the MCP children with the freshly-written env for free.
        channelSetRestartAlreadyScheduled:
          channelSetRestartScheduled || senderPolicyDecision.restart,
      });
      if (behaviourDecision.restart) {
        log(
          `[hot-reload] msteams behaviour settings changed for '${agent.code_name}' ` +
            `(${prevBehaviourHash?.slice(0, 8) ?? 'first'} → ${behaviourHash.slice(0, 8)}) — restarting session`,
        );
        const behaviourNotice =
          'Your Microsoft Teams channel behaviour settings were updated. Restarting session shortly so the teams-channel MCP picks up the new configuration.';
        const behaviourDelivered = await injectMessage(
          agent.code_name,
          'system',
          behaviourNotice + BACK_ONLINE_GREETING_GUIDANCE,
          { task_name: 'channel-update' },
          log,
        ).catch(() => false);
        const behaviourDelay = behaviourDelivered ? 8_000 : 3_000;
        if (!behaviourDelivered) {
          log(
            `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
          );
        }
        scheduleSessionRestart(
          agent.code_name,
          behaviourDelay,
          'msteams behaviour change',
          'channel-behaviour-change',
        );
        msteamsBehaviourRestartScheduled = true;
      }
      // Always advance the IN-MEMORY baseline once converged — same rationale
      // as the sender-policy baseline above.
      agentState.knownMsTeamsBehaviourHashes.set(agent.agent_id, behaviourHash);
      // ENG-7779: advance the PERSISTED delivery baseline only when delivery
      // is not outstanding — same invariant as the sender-policy block above
      // (restart-bearing paths persist in the restart timer's post-stop hook;
      // persisting at schedule time would silently drop a restart lost to a
      // manager restart during the defer window).
      const hasLiveMsTeamsBehaviourConsumer =
        spSessionHealthy && spSessionMode === 'persistent' && spFramework === 'claude-code';
      if (!hasLiveMsTeamsBehaviourConsumer) {
        persistDeliveryBaselineEntry('msteamsBehaviour', agent.agent_id, behaviourHash);
      } else if (behaviourDecision.firstPoll && !msteamsBehaviourRestrictive) {
        persistDeliveryBaselineEntry('msteamsBehaviour', agent.agent_id, behaviourHash);
      }
    } else {
      // Channel removed (or quarantined): drop the baseline so a later
      // re-add starts from first-poll (the channel-set restart covers the
      // respawn) instead of comparing against a stale pre-removal hash.
      agentState.knownMsTeamsBehaviourHashes.delete(agent.agent_id);
      removeDeliveryBaselineEntry('msteamsBehaviour', agent.agent_id);
    }

    // ENG-6035: same trigger for Slack behaviour-field changes
    // (allowed_users / thread_auto_follow / channel_response_mode) saved via
    // the webapp's Slack panel. Closes the Slack half of the ENG-5537
    // value-only restart gap the same way ENG-6024 closed the Teams half:
    // slack-channel reads SLACK_ALLOWED_USERS (and friends) from process.env
    // once at boot, so the adapter's .mcp.json rewrite needs this dedicated
    // restart to reach the running child.
    if (currentChannelIds.has('slack')) {
      const slackEntry = refreshData.channel_configs?.['slack'];
      const slackBehaviourSubset = extractSlackBehaviourSubset(
        slackEntry?.config as Record<string, unknown> | null | undefined,
      );
      const slackBehaviourHash = createHash('sha256')
        .update(canonicalJson(slackBehaviourSubset))
        .digest('hex');
      const prevSlackBehaviourHash = agentState.knownSlackBehaviourHashes.get(agent.agent_id);
      // ENG-7779: the baseline is now hydrated from the on-disk sidecar, so a
      // genuine first poll means the manager truly does not know what the
      // running slack-channel MCP enforces. `allowed_users` is an
      // access-control gate, so an unknown baseline with a non-empty
      // allowlist fail-closes with a verify restart — same ENG-7771
      // semantics as a restrictive sender_policy.
      const slackBehaviourRestrictive = isSlackBehaviourRestrictive(slackBehaviourSubset);
      const slackBehaviourDecision = decideSlackBehaviourRestart({
        previousHash: prevSlackBehaviourHash,
        currentPolicyRestrictive: slackBehaviourRestrictive,
        currentHash: slackBehaviourHash,
        sessionMode: spSessionMode,
        framework: spFramework,
        sessionHealthy: spSessionHealthy,
        // A channel-set, sender-policy, or msteams-behaviour restart
        // scheduled this tick respawns the MCP children with the
        // freshly-written env for free.
        channelSetRestartAlreadyScheduled:
          channelSetRestartScheduled ||
          senderPolicyDecision.restart ||
          msteamsBehaviourRestartScheduled,
      });
      if (slackBehaviourDecision.restart) {
        // ENG-7771/ENG-7779: a first-poll restart is the fail-safe for an
        // UNKNOWN delivery baseline with a restrictive allowlist — nothing
        // "changed"; the manager cannot prove the live session enforces
        // allowed_users, so it restarts to guarantee it (fail closed).
        const isSlackVerifyRestart = slackBehaviourDecision.firstPoll;
        log(
          isSlackVerifyRestart
            ? `[hot-reload] slack behaviour delivery baseline unknown for '${agent.code_name}' with a restrictive allowed_users list ` +
                `(→ ${slackBehaviourHash.slice(0, 8)}) — restarting session to guarantee enforcement (fail closed, ENG-7779)`
            : `[hot-reload] slack behaviour settings changed for '${agent.code_name}' ` +
                `(${prevSlackBehaviourHash?.slice(0, 8) ?? 'first'} → ${slackBehaviourHash.slice(0, 8)}) — restarting session`,
        );
        const slackBehaviourNotice = isSlackVerifyRestart
          ? 'Your manager was updated and needs to restart your session to make sure your configured Slack access controls are enforced by the channel MCP server.'
          : 'Your Slack channel behaviour settings were updated. Restarting session shortly so the slack-channel MCP picks up the new configuration.';
        const slackBehaviourDelivered = await injectMessage(
          agent.code_name,
          'system',
          slackBehaviourNotice + BACK_ONLINE_GREETING_GUIDANCE,
          { task_name: 'channel-update' },
          log,
        ).catch(() => false);
        const slackBehaviourDelay = slackBehaviourDelivered ? 8_000 : 3_000;
        if (!slackBehaviourDelivered) {
          log(
            `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
          );
        }
        scheduleSessionRestart(
          agent.code_name,
          slackBehaviourDelay,
          isSlackVerifyRestart
            ? 'slack behaviour verify (unknown baseline)'
            : 'slack behaviour change',
          'channel-behaviour-change',
        );
      }
      // Always advance the IN-MEMORY baseline once converged — same rationale
      // as the sender-policy baseline above.
      if (slackBehaviourDecision.restart) slackBehaviourRestartScheduled = true;
      agentState.knownSlackBehaviourHashes.set(agent.agent_id, slackBehaviourHash);
      // ENG-7779: advance the PERSISTED delivery baseline only when delivery
      // is not outstanding — same invariant as the sender-policy block above
      // (restart-bearing paths persist in the restart timer's post-stop hook;
      // persisting at schedule time would silently drop a restart lost to a
      // manager restart during the defer window).
      const hasLiveSlackBehaviourConsumer =
        spSessionHealthy && spSessionMode === 'persistent' && spFramework === 'claude-code';
      if (!hasLiveSlackBehaviourConsumer) {
        persistDeliveryBaselineEntry('slackBehaviour', agent.agent_id, slackBehaviourHash);
      } else if (slackBehaviourDecision.firstPoll && !slackBehaviourRestrictive) {
        persistDeliveryBaselineEntry('slackBehaviour', agent.agent_id, slackBehaviourHash);
      }
    } else {
      // Channel removed (or quarantined): drop the baseline so a later
      // re-add starts from first-poll (the channel-set restart covers the
      // respawn) instead of comparing against a stale pre-removal hash.
      agentState.knownSlackBehaviourHashes.delete(agent.agent_id);
      removeDeliveryBaselineEntry('slackBehaviour', agent.agent_id);
    }

    // ENG-9802: orthogonal restart trigger for the telegram CHAT POLICY —
    // the allowlist, private-chats-only, and the peer kill switch.
    //
    // The same last-hop gap the three triggers above close, for the control an
    // operator is most likely to set from the console and most likely to
    // believe: telegram-channel reads TELEGRAM_ALLOWED_CHATS and friends from
    // process.env once at boot (measured — ALL 13 of its env keys are), the
    // channel-set restart only fires on membership change, and the .mcp.json
    // drift watcher suppresses value-only restarts to keep Composio URL
    // rotation out of the ENG-5441 breaker.
    //
    // Measured 2026-09-01: sophie's allowlist converged to disk at 11:33:11
    // and the live MCP was still running from 11:30:16 without it. A hand-rolled
    // restart at 11:39:44 made it live — which is also the proof that a respawn
    // is SUFFICIENT here, not merely necessary. All 12 agents recovered that day
    // needed the same manual step.
    //
    // Hashing only the classified policy subset (channel-policy-env.ts) means
    // credential churn and cosmetic edits cannot fire it; the subset moves only
    // on an explicit operator change.
    if (currentChannelIds.has('telegram')) {
      // `telegramPolicyEnvForRestart` is captured every tick in the channel
      // loop above, including ticks that skip the write on an unchanged hash.
      // Undefined here means the loop never saw a telegram entry this tick
      // (quarantined, or dropped between refreshes) — treat that as "no policy"
      // rather than as unknown, so it compares equal to itself and does not
      // respawn on every poll.
      const telegramPolicyEnv = telegramPolicyEnvForRestart ?? {};
      const telegramPolicyHash = createHash('sha256')
        .update(telegramPolicyEnvBasis(telegramPolicyEnv))
        .digest('hex');
      const prevTelegramPolicyHash = agentState.knownTelegramPolicyHashes.get(agent.agent_id);
      // An unknown baseline with a policy SET is unsafe in the same way a
      // restrictive sender_policy is: the running child may be enforcing
      // nothing. Unknown with no policy set establishes the baseline quietly,
      // so the deploy that ships this does not respawn the fleet (ENG-7771).
      const telegramPolicyRestrictive = isTelegramPolicyRestrictive(telegramPolicyEnv);
      const telegramPolicyDecision = decideSenderPolicyRestart({
        previousHash: prevTelegramPolicyHash,
        currentPolicyRestrictive: telegramPolicyRestrictive,
        currentHash: telegramPolicyHash,
        sessionMode: spSessionMode,
        framework: spFramework,
        sessionHealthy: spSessionHealthy,
        // Any earlier restart scheduled this tick already respawns the MCP
        // children with the freshly-written env.
        channelSetRestartAlreadyScheduled:
          channelSetRestartScheduled ||
          senderPolicyDecision.restart ||
          msteamsBehaviourRestartScheduled ||
          slackBehaviourRestartScheduled,
      });
      if (telegramPolicyDecision.restart) {
        telegramPolicyRestartScheduled = true;
        // A first-poll restart is the ENG-7771 fail-safe for an UNKNOWN
        // baseline, not a delivered change — telling the agent its policy "has
        // been updated" would be false, so the two are worded differently.
        const isTelegramVerifyRestart = telegramPolicyDecision.firstPoll;
        log(
          isTelegramVerifyRestart
            ? `[hot-reload] telegram chat-policy delivery baseline unknown for '${agent.code_name}' with a policy set ` +
                `(→ ${telegramPolicyHash.slice(0, 8)}) — restarting session to guarantee enforcement (fail closed, ENG-9802)`
            : `[hot-reload] telegram chat policy changed for '${agent.code_name}' ` +
                `(${prevTelegramPolicyHash?.slice(0, 8) ?? 'first'} → ${telegramPolicyHash.slice(0, 8)}) — restarting session`,
        );
        const telegramNotice = isTelegramVerifyRestart
          ? 'Your manager was updated and needs to restart your session to make sure your configured Telegram chat policy is enforced by the channel MCP server.'
          : 'Your Telegram chat policy has been updated. Restarting session shortly so the channel MCP server picks up the new allowlist.';
        const telegramDelivered = await injectMessage(
          agent.code_name,
          'system',
          telegramNotice + BACK_ONLINE_GREETING_GUIDANCE,
          { task_name: 'telegram-policy-update' },
          log,
        ).catch(() => false);
        const telegramDelay = telegramDelivered ? 8_000 : 3_000;
        if (!telegramDelivered) {
          log(
            `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
          );
        }
        scheduleSessionRestart(
          agent.code_name,
          telegramDelay,
          isTelegramVerifyRestart
            ? 'telegram chat-policy verify (unknown baseline)'
            : 'telegram chat-policy change',
          'telegram-policy-change',
        );
      }
      agentState.knownTelegramPolicyHashes.set(agent.agent_id, telegramPolicyHash);
      // Advance the PERSISTED baseline only where delivery is not outstanding.
      // A restart-bearing path persists in the restart timer's post-stop hook;
      // persisting at schedule time would mark the change delivered and drop it
      // if the manager restarted during the defer window (ENG-7771).
      const hasLiveTelegramPolicyConsumer =
        spSessionHealthy && spSessionMode === 'persistent' && spFramework === 'claude-code';
      if (!hasLiveTelegramPolicyConsumer) {
        persistDeliveryBaselineEntry('telegramPolicy', agent.agent_id, telegramPolicyHash);
      } else if (telegramPolicyDecision.firstPoll && !telegramPolicyRestrictive) {
        persistDeliveryBaselineEntry('telegramPolicy', agent.agent_id, telegramPolicyHash);
      }
    } else {
      // Channel removed or quarantined: drop the baseline so a later re-add
      // starts from first-poll (the channel-set restart covers that respawn)
      // rather than comparing against a stale pre-removal hash.
      agentState.knownTelegramPolicyHashes.delete(agent.agent_id);
      removeDeliveryBaselineEntry('telegramPolicy', agent.agent_id);
    }

    // ── ENG-9861: deliver a standing-rules change to the LIVE session ─────────
    //
    // ENG-9862 fixed rules never reaching CLAUDE.md at all. They render now — but
    // CLAUDE.md is read ONCE at session start, so a rule added mid-session sits on
    // disk while the running agent keeps its old instructions. `rule_add` returns
    // success, the file is correct, the person is told the rule is in force, and
    // it applies on no turn until something unrelated restarts the session.
    //
    // Same shape as the telegram block above, and the same machinery: this is a
    // hash and a call site, not new infrastructure.
    //
    // ONE DIFFERENCE WORTH NAMING. The policy concerns above are read by an MCP
    // CHILD from its env; this is read by Claude Code ITSELF from CLAUDE.md. The
    // delivery moment is identical either way — the respawn is what makes the
    // rendered rules live — which is why it reuses the same decision, the same
    // sidecar and the same post-stop advance rather than inventing a parallel one.
    {
      // Absent on an older API (pre-ENG-9862 refresh payload) means "no rules",
      // not "unknown": treat it as an empty list so it compares equal to itself
      // and cannot respawn a healthy session on every poll.
      const agentRules = (refreshData.agent_rules ?? []) as ReadonlyArray<{ id: string; text: string }>;
      const agentRulesHash = createHash('sha256')
        .update(agentRulesRestartBasis(agentRules))
        .digest('hex');
      const prevAgentRulesHash = agentState.knownAgentRulesHashes.get(agent.agent_id);
      // An unknown baseline with rules PRESENT is unsafe the same way a
      // restrictive sender_policy is: the running session may be operating
      // without instructions its owner was told are in force. Unknown with no
      // rules establishes the baseline quietly, so the deploy that ships this
      // does not respawn the fleet (ENG-7771).
      const rulesPresent = agentHasRules(agentRules);
      const agentRulesDecision = decideSenderPolicyRestart({
        previousHash: prevAgentRulesHash,
        currentPolicyRestrictive: rulesPresent,
        currentHash: agentRulesHash,
        sessionMode: spSessionMode,
        framework: spFramework,
        sessionHealthy: spSessionHealthy,
        // Any earlier restart this tick already respawns the session, which
        // re-reads CLAUDE.md for free. A second would only coalesce and overwrite
        // a friendlier notice.
        channelSetRestartAlreadyScheduled:
          channelSetRestartScheduled ||
          senderPolicyDecision.restart ||
          msteamsBehaviourRestartScheduled ||
          slackBehaviourRestartScheduled ||
          telegramPolicyRestartScheduled,
      });
      if (agentRulesDecision.restart) {
        // A first-poll restart is the ENG-7771 fail-safe for an UNKNOWN baseline,
        // not a delivered change — telling the agent its rules "have been updated"
        // would be false, so the two are worded differently.
        const isRulesVerifyRestart = agentRulesDecision.firstPoll;
        log(
          isRulesVerifyRestart
            ? `[hot-reload] standing-rules delivery baseline unknown for '${agent.code_name}' with rules set ` +
                `(→ ${agentRulesHash.slice(0, 8)}) — restarting session so the rendered rules are actually in force (fail closed, ENG-9861)`
            : `[hot-reload] standing rules changed for '${agent.code_name}' ` +
                `(${prevAgentRulesHash?.slice(0, 8) ?? 'first'} → ${agentRulesHash.slice(0, 8)}) — restarting session`,
        );
        const rulesNotice = isRulesVerifyRestart
          ? 'Your manager was updated and needs to restart your session to make sure the standing rules you have been given are actually in force.'
          : 'Your standing rules have changed. Restarting session shortly so they take effect - until then you are still running on the previous set.';
        const rulesDelivered = await injectMessage(
          agent.code_name,
          'system',
          rulesNotice + BACK_ONLINE_GREETING_GUIDANCE,
          { task_name: 'standing-rules-update' },
          log,
        ).catch(() => false);
        const rulesDelay = rulesDelivered ? 8_000 : 3_000;
        if (!rulesDelivered) {
          log(
            `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
          );
        }
        scheduleSessionRestart(
          agent.code_name,
          rulesDelay,
          isRulesVerifyRestart ? 'standing-rules verify (unknown baseline)' : 'standing-rules change',
          'agent-rules-change',
        );
      }
      agentState.knownAgentRulesHashes.set(agent.agent_id, agentRulesHash);
      // Advance the PERSISTED baseline only where delivery is not outstanding. A
      // restart-bearing path persists in the restart timer's post-stop hook;
      // persisting at schedule time would mark the change delivered and drop it if
      // the manager restarted during the defer window (ENG-7771).
      const hasLiveRulesConsumer =
        spSessionHealthy && spSessionMode === 'persistent' && spFramework === 'claude-code';
      if (!hasLiveRulesConsumer) {
        persistDeliveryBaselineEntry('agentRules', agent.agent_id, agentRulesHash);
      } else if (agentRulesDecision.firstPoll && !rulesPresent) {
        persistDeliveryBaselineEntry('agentRules', agent.agent_id, agentRulesHash);
      }
    }
  }

  // 5b. Always provision direct-chat channel for persistent sessions.
  // This runs as a standalone MCP channel server and pushes webapp direct
  // chat messages into the live session.
  //
  // Must live in project-scope .mcp.json (not a separate file). Claude Code's
  // channel name matcher (yG5 in 2.1.114) only resolves server:<name> against
  // MCP servers in enterprise/user/project/local scopes — --mcp-config loads
  // as session scope and is invisible to the lookup, which is why the separate
  // .mcp-channels.json approach always failed with "no MCP server configured".
  //
  // Write to the provision-dir .mcp.json (source of truth) then copy to the
  // project dir, so subsequent artifact syncs don't wipe the entry.
  const agentSessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
  if (agentSessionMode === 'persistent' && (agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK) === 'claude-code') {
    try {
      // ENG-4418: provision dir now lives at ~/.augmented/<codeName>/provision/
      // (formerly split between <codeName>/provision/ and <codeName>/claudecode/provision/).
      // Reuse the refresh-resolved agentDir so this always matches the
      // authoritative framework (not the cached one from /host/agents).
      const agentProvisionDir = agentDir;
      const projectDir = join(homedir(), '.augmented', agent.code_name, 'project');
      mkdirSync(agentProvisionDir, { recursive: true });
      mkdirSync(projectDir, { recursive: true });
      const provisionMcpPath = join(agentProvisionDir, '.mcp.json');
      const projectMcpPath = join(projectDir, '.mcp.json');

      let mcpConfig: { mcpServers: Record<string, unknown> } = { mcpServers: {} };
      try {
        mcpConfig = JSON.parse(readFileSync(provisionMcpPath, 'utf-8'));
        if (!mcpConfig.mcpServers) mcpConfig.mcpServers = {};
      } catch { /* new file */ }

      const localDirectChatChannel = join(homedir(), '.augmented', '_mcp', 'direct-chat-channel.js');
      // ENG-5363: team timezone propagates as `TZ` so the direct-chat
      // progress-heartbeat's "last update" footer renders in the agent's
      // local time. Mirror of the Slack/Telegram fix in
      // claudecode/index.ts. The current block only writes on first
      // install (`!mcpConfig.mcpServers['direct-chat']`), so existing
      // agents will pick up TZ the next time their `.mcp.json` is reset
      // — acceptable for a hotfix; a follow-up could re-evaluate the
      // env block on each refresh.
      const directChatTeamSettings = (refreshData.team as { settings?: Record<string, unknown> } | undefined)?.settings;
      const directChatTz = (() => {
        const tz = directChatTeamSettings?.['timezone'];
        return typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : undefined;
      })();
      if (existsSync(localDirectChatChannel)) {
        // ENG-5922: route through the framework adapter's writeMcpServer
        // so writeMcpJsonGuarded validation + syncMcpToProject + the
        // subagent render functions (renderChannelMessageHandlerForAgent
        // + renderAugmentedWorkerForAgent) all fire. The pre-fix path
        // was a direct writeFileSync to both provision and project
        // .mcp.json files, which DID get the server entry in place but
        // left the subagent .md tools allowlist stale.
        //
        // ENG-5924: gate REMOVED — call writeMcpServer every poll, NOT
        // only on first install. The "already present, skip" gate
        // (`!mcpConfig.mcpServers['direct-chat']`) prevented the
        // render chokepoint from firing for agents provisioned before
        // ENG-5922 landed: their .mcp.json had direct-chat from the
        // pre-fix direct write, so the gate was always false and the
        // render never re-fired, leaving .md allowlists stuck at
        // whatever stale snapshot the last write produced. Empirically
        // surfaced by `agt audit mcp-render` on agt-aws-1 2026-06-02:
        // 22 stale .md files across 8 agents. writeMcpServer +
        // syncMcpToProject + render are all idempotent on identical
        // content — calling them every poll heals existing drift and
        // costs only a per-poll .mcp.json mtime bump. DO NOT re-add
        // the gate without a different chokepoint for the render call.
        const directChatEnv: Record<string, string> = {
          AGT_HOST: requireHost(),
          // ENG-5901 Track D: templated — the manager exports the real
          // AGT_API_KEY to every spawn env (getApiKey()); Claude Code
          // substitutes at MCP-launch. No literal in .mcp.json.
          AGT_API_KEY: '${AGT_API_KEY}',
          AGT_AGENT_ID: agent.agent_id,
          // ENG-7262: the direct-chat MCP downloads inbound attachments into the
          // agent's code-name dir (~/.augmented/<code_name>/direct-chat-inbound/)
          // so the agent's isolation-hook-sandboxed file tools can read them.
          // Pass the code name explicitly (matches slack/telegram); the MCP also
          // falls back to dirname(AGT_TURN_INITIATOR_FILE) if this is ever unset.
          AGT_AGENT_CODE_NAME: agent.code_name,
          // ENG-6587 (D16): per-turn initiator marker path. The direct-chat MCP
          // (writer) and the broker MCPs (readers) share no other state-dir env,
          // so we inject the SAME absolute path buildMcpJson gives the brokers:
          // ~/.augmented/<codeName>/.current-turn-initiator.json. Note getAgentDir
          // returns the agent root (NOT the /provision subdir `agentDir` points at),
          // so it byte-matches the broker readers' path.
          AGT_TURN_INITIATOR_FILE: join(
            frameworkAdapter.getAgentDir(agent.code_name),
            '.current-turn-initiator.json',
          ),
        };
        if (directChatTz) directChatEnv['TZ'] = directChatTz;
        if (!frameworkAdapter.writeMcpServer) {
          // This branch is gated on `framework === 'claude-code'`
          // upstream — every Claude Code adapter MUST implement
          // writeMcpServer. If we reach here without it, that's a
          // framework-adapter contract violation, not a soft skip.
          // Fail loud so direct-chat doesn't silently stay
          // unprovisioned (CodeRabbit PR #1675 catch).
          throw new Error(
            `[direct-chat] Framework '${frameworkAdapter.id}' must implement writeMcpServer for direct-chat provisioning (agent=${agent.code_name}, cachedFramework=${agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK})`,
          );
        }
        frameworkAdapter.writeMcpServer(agent.code_name, 'direct-chat', {
          command: 'node',
          args: [localDirectChatChannel],
          env: directChatEnv,
        });
        // Only log on FIRST install — subsequent polls re-fire the
        // chokepoint as a no-op for the .mcp.json content and noise
        // would drown manager.log otherwise.
        if (!mcpConfig.mcpServers['direct-chat']) {
          log(`Channel credentials written for '${agent.code_name}/direct-chat'`);
        }
      }

      // Remove stale .mcp-channels.json from the pre-fix layout.
      const staleChannelsPath = join(projectDir, '.mcp-channels.json');
      if (existsSync(staleChannelsPath)) {
        try { rmSync(staleChannelsPath, { force: true }); } catch { /* non-fatal */ }
      }
    } catch (err) {
      log(`Failed to provision direct-chat channel for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // 6. Fetch and write auth profiles (secrets)
  let lastSecretsProvisionAt = state.agents.find((a) => a.agentId === agent.agent_id)?.lastSecretsProvisionAt ?? null;
  let secretsHash = agentState.knownSecretsHashes.get(agent.agent_id) ?? null;

  // ENG-9585 (slice 2): `beginFetch` BEFORE the gate check, so the token records
  // the force count as it stood when this pass decided. A force that lands while
  // the request is in flight must survive the response that predates it.
  const secretsToken = secretsFetchGate.beginFetch(agent.agent_id);
  if (secretsFetchGate.shouldFetch(agent.agent_id)) {
    try {
      const secretsData = await api.post<{
        profiles: Array<{
          provider: string;
          profile_name: string;
          auth_type: string;
          api_key?: string;
          metadata: Record<string, unknown>;
        }>;
        secrets_hash: string | null;
      }>('/host/secrets', { agent_id: agent.agent_id });

      const newHash = secretsData.secrets_hash;
      const hashChanged = newHash !== secretsHash;

      if (hashChanged && secretsData.profiles.length > 0) {
        frameworkAdapter.writeAuthProfiles(agent.code_name, secretsData.profiles);
        lastSecretsProvisionAt = new Date().toISOString();
        log(`Secrets updated for '${agent.code_name}'`);
      }

      if (newHash) {
        agentState.knownSecretsHashes.set(agent.agent_id, newHash);
        secretsHash = newHash;
      } else {
        agentState.knownSecretsHashes.delete(agent.agent_id);
        secretsHash = null;
      }

      // LAST statement in the `try`, and that placement is the whole contract:
      // anything above throwing skips it, so a FAILED fetch leaves the gate open
      // and the next pass retries immediately. `/host/refresh` needs an explicit
      // `return` in its catch to get this property because its mark sits outside
      // the try; here the catch falls through by design (secrets are non-fatal),
      // so the mark must be inside it instead.
      secretsFetchGate.markFetched(agent.agent_id, secretsToken);
    } catch (err) {
      // Non-fatal — secrets are supplementary to provisioning
      log(`Secrets fetch failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // 6b. Integration provisioning — fetch integrations, resolve satisfied capabilities,
  //     install skills + CLI tools
  try {
    // ENG-10061: the fetch, kept in a local so its ~60-line inline return type
    // stays inferred rather than having to be named at module scope for the
    // cached branch below to declare a variable of it.
    const fetchIntegrations = async () => {
      const integrationsData = await api.post<{
        integrations: Array<{
          id: string;
          definition_id: string;
          credentials: Record<string, unknown>;
          config: Record<string, unknown>;
          capabilities: Array<{ id: string; name: string; description: string; access: string }>;
          auth_type: string;
          display_name: string;
          scope: string;
          // ENG-7543: which connection of the toolkit this row is (default fleet: 'default').
          connection_key?: string | null;
          // ENG-5641: connectivity-probe inputs (enriched by /host/agent-integrations).
          source_type?: string | null;
          last_connectivity_check_at?: string | null;
          // ENG-7575: connectivity STATUS + row age, so bind-remediation can back
          // off while a freshly-added integration is still flapping on first-connect
          // (see computeFirstConnectUnsettled). Absent/null unless the connectivity
          // probe is armed — treated as "not a defer" (fail-open).
          last_connectivity_status?: string | null;
          consecutive_connectivity_failures?: number | null;
          created_at?: string | null;
          // ENG-9718: the reconnect settling deadline, so the backoff defers for a
          // just-RECONNECTED row as well as a just-created one. Moves in lockstep
          // with the probe's anchor (mcp-config-lookup) — reverting one alone
          // reintroduces the ENG-7560 churn loop silently.
          reconnect_quiesced_until?: string | null;
          // ENG-7220 (Slice 2): last session-tool-bind probe time, so the runner's
          // rolling due-selection paces the probe instead of re-probing every poll.
          last_session_tool_bind_at?: string | null;
          // ENG-6242: the toolkit's connectivity_test override (managed/Composio).
          connectivity_test?: ConnectivityTestOverride | null;
          // ENG-5463: the toolkit's CLI binary (`toolkit_definitions.cli_binary`),
          // enriched by /host/agent-integrations. This set is `as`-cast wholesale
          // onto the connectivity-probe input, so an undeclared field would widen
          // away silently rather than fail to compile.
          cli_binary?: string | null;
          // ENG-6993 / ADR-0033 (Slice 2): the catalog remote-MCP descriptor
          // (integration_definitions.remote_mcp), forwarded by the API in
          // snake_case. ENG-7253: the manager MUST map this onto the camelCase
          // `remoteMcp` the adapter reads, or a pure-catalog remote-MCP
          // integration (e.g. monday.com - no INTEGRATION_REGISTRY fallback)
          // silently never renders its server into .mcp.json.
          remote_mcp?: RemoteMcpSpec | null;
          // ENG-10614: per-tool HITL floors, declared at the seam so the
          // contract is visible here rather than only inside the mapper.
          //
          // NOTE, and it is a real limitation rather than an oversight:
          // computeIntegrationsHash covers definition_id + credentials + config
          // ONLY, so a tier change on its own does NOT register as "integration
          // changed" and will not re-provision an agent by itself. Escalating a
          // tool in the catalog therefore reaches a running agent on its next
          // provision for some OTHER reason, not immediately. Adding this to the
          // hash would fix it and would also re-hash the whole fleet once, which
          // is the respawn-storm shape ENG-8003 exists to avoid - so it is a
          // deliberate separate decision, not a line to slip into this change.
          tool_tiers?: Record<string, string> | null;
        }>;
      }>('/host/agent-integrations', { agent_id: agent.agent_id });

      // ENG-7253: map the API's snake_case `remote_mcp` onto the camelCase
      // `remoteMcp` field every downstream consumer reads (buildRemoteMcpEntry in
      // writeIntegrations / buildMcpJson). Without this the forwarded catalog
      // descriptor is dropped at the manager seam and pure-catalog remote-MCP
      // integrations never bind. Hash-neutral: computeIntegrationsHash covers only
      // definition_id + credentials + config, so this addition can't perturb
      // change-detection.
      return (integrationsData.integrations ?? []).map(withResolvedRemoteMcp);
    };

    // ENG-10061. `beginFetch` BEFORE the gate check, so the token records the
    // force count as it stood when this pass decided — a probe report later in
    // THIS pass calls `forceIntegrationsFetch`, and that force must survive the
    // `markFetched` below rather than being consumed by a payload that predates
    // it. Same mid-flight race `FetchToken` was built for.
    const integrationsToken = integrationsFetchGate.beginFetch(agent.agent_id);
    const cachedIntegrations = lastIntegrationsPayload.get(agent.agent_id);
    let integrations: Awaited<ReturnType<typeof fetchIntegrations>>;
    if (cachedIntegrations !== undefined && !integrationsFetchGate.shouldFetch(agent.agent_id)) {
      // Cast: the stored array is the one produced by `fetchIntegrations` above,
      // post-`withResolvedRemoteMcp`, so the shape is the same by construction.
      // Module-scoped storage cannot express a type local to this function.
      integrations = cachedIntegrations as typeof integrations;
      pollCycleIntegrationsSkips += 1;
    } else {
      integrations = await fetchIntegrations();
      lastIntegrationsPayload.set(agent.agent_id, integrations);
      // Marked HERE rather than at the end of the `try`, which is where the
      // `/host/secrets` lane marks. The difference is deliberate: there the
      // block IS the fetch-and-apply, so a downstream throw should re-fetch.
      // Here the block is ~1000 lines of local provisioning work, and a
      // PERSISTENT local failure in it (a full disk failing `writeIntegrations`,
      // say) would then re-download the payload on every pass forever — this
      // ticket's cost, reappearing exactly on a broken agent. The gate spaces
      // the FETCH; the fetch is what succeeded. A downstream throw still
      // retries on the next pass, against the cached payload.
      integrationsFetchGate.markFetched(agent.agent_id, integrationsToken);
      pollCycleIntegrationsFetches += 1;
    }

    // ENG-9652: remember which integrations this agent actually has, so the
    // channel credential sync can stop writing credentials for a channel whose
    // backing integration is not installed. The sync runs EARLIER in this same
    // function than this fetch, so it reads the previous tick's observation —
    // deliberately, because the alternative is reordering provisioning. A first
    // tick with no observation reads as "unknown" and retries as before.
    recordEffectiveIntegrationIds(
      effectiveIntegrationObservations,
      agent.agent_id,
      integrations.map((i) => i.definition_id),
    );

    // ENG-7575: refresh this agent's first-connect snapshot for bind-remediation.
    // verifyPendingRestarts runs after all agents are processed and only has the
    // codeName, so it reads this Map rather than re-fetching integrations.
    firstConnectUnsettledByAgent.set(
      agent.code_name,
      computeFirstConnectUnsettled(integrations, Date.now(), BIND_REMEDIATION_FIRST_CONNECT_WINDOW_MS),
    );

    // 6b-i. Refresh OAuth tokens nearing expiry (< 10 min remaining).
    //
    // ENG-6962: this host-side loop is the belt-and-braces fallback to the
    // central `oauth-token-refresh` cron (the primary path). It must call a
    // route the host JWT can actually authenticate. The old target,
    // `/integrations/oauth/:id/refresh`, is `authMiddleware + requireTeam`
    // (a webapp/team route). The host exchanges a host JWT and sends no
    // `X-Team-Slug`, so `requireTeam` 400'd every cycle ("Missing or invalid
    // X-Team-Slug header"), spamming manager.log every poll and never once
    // refreshing a token. We instead reuse the host-scoped
    // `/host/agent-integrations/:id/credential` route (hostAgentAuthMiddleware
    // + verifyHostAgentAccess, org from the host JWT claim), which already
    // refreshes via the shared `refreshIntegrationToken` helper when the token
    // is near expiry and returns the freshest access_token + expires_at. That
    // route's internal refresh threshold is ~5 min, so a token between 5 and
    // 10 min out comes back still-valid (no rotation) and the file handoff
    // below simply rewrites the current token (harmless); the actual rotation
    // lands on a later poll once it drops under the route's threshold.
    for (const integration of integrations) {
      if (integration.auth_type !== 'oauth2') continue;
      const expiresAt = integration.credentials?.token_expires_at as string | undefined;
      const refreshToken = integration.credentials?.refresh_token as string | undefined;
      if (!expiresAt || !refreshToken) continue;

      const msRemaining = new Date(expiresAt).getTime() - Date.now();
      if (msRemaining > 10 * 60 * 1000) continue; // Still valid for > 10 min

      try {
        const integrationId = integration.id;
        if (!integrationId) continue;
        const refreshResult = await api.post<{ access_token?: string | null; expires_at?: string | null }>(
          `/host/agent-integrations/${integrationId}/credential`,
          { agent_id: agent.agent_id },
        );
        if (refreshResult.access_token) {
          // The credential route always returns the current token, rotated only
          // when it was near expiry. Detect whether this poll actually rotated
          // it so we don't replace the old 400 spam with "refreshed" spam every
          // poll while the token sits between the manager's 10-min window and
          // the route's ~5-min threshold (CodeRabbit, ENG-6962).
          const rotated =
            refreshResult.access_token !== integration.credentials.access_token ||
            (refreshResult.expires_at != null &&
              refreshResult.expires_at !== integration.credentials.token_expires_at);

          // Update in-memory credentials so writeIntegrations() uses the
          // freshest token. The credential route always returns the current
          // (refreshed-if-needed) token, so a successful response means the
          // file handoff below should carry it.
          if (refreshResult.expires_at) {
            integration.credentials.token_expires_at = refreshResult.expires_at;
          }
          integration.credentials.access_token = refreshResult.access_token;
          if (rotated) {
            log(`OAuth token refreshed for '${agent.code_name}/${integration.definition_id}'`);
          }

          // Write live token file as fallback for agents not yet using the
          // token_refresh MCP tool. Agents with the plugin can call token_refresh
          // on demand instead of relying on this file-based handoff.
          if (frameworkAdapter.writeTokenFile) {
            frameworkAdapter.writeTokenFile(agent.code_name, integrations as Parameters<typeof frameworkAdapter.writeTokenFile>[1]);
          }
        }
      } catch (err) {
        log(`OAuth token refresh failed for '${agent.code_name}/${integration.definition_id}': ${(err as Error).message}`);
      }
    }

    // 6b-i.4 ENG-7748: Anchor Browser persistent-session mint. When an
    // anchor-browser integration carries a `profile_name`, mint (or reuse, or
    // re-mint before expiry) a profile-bound Anchor session and stamp its id into
    // `config.session_id` - writeIntegrations materializes it as
    // ANCHOR_BROWSER_SESSION_ID, and the live proxy reads that header per request
    // so a re-mint needs no respawn. Fail-soft: no profile / no key / mint error
    // ⇒ session id cleared (empty header → stateless ephemeral), never a hard
    // failure. Mirrors the OAuth in-memory-mutation pattern above; must run
    // BEFORE the intHash below so a changed id triggers the .env.integrations
    // rewrite.
    const anchorIntegration = integrations.find((i) => i.definition_id === 'anchor-browser');
    if (anchorIntegration) {
      const profileName = anchorIntegration.config?.['profile_name'] as string | undefined;
      const apiKey = anchorIntegration.credentials?.['api_key'] as string | undefined;
      const sessionId = await anchorSessionLifecycle.ensure({
        agentId: agent.agent_id,
        profileName,
        apiKey,
        now: Date.now(),
        log,
      });
      const config = { ...(anchorIntegration.config ?? {}) } as Record<string, unknown>;
      if (sessionId) config['session_id'] = sessionId;
      else delete config['session_id'];
      anchorIntegration.config = config;
    } else {
      // Integration removed (or never present): drop any cached session so it
      // isn't reused or leaked. Best-effort end runs only if we still hold a key
      // (we don't here), so this just clears the in-memory record; the live
      // session hits Anchor's own idle/max-duration expiry.
      anchorSessionLifecycle.forget(agent.agent_id, undefined, log);
    }

    // 6b-i.5 ENG-5641: rolling host-side connectivity probes — FLAG-GATED, default
    // off. Probes each integration ~hourly (rolling due-selection) using the
    // agent's real creds/MCP and reports to /host/integration-connectivity, where
    // the central evaluator escalates (also flag-off). Migrated to the
    // 'connectivity-probe' registry flag (ENG-7220, ADR-0022); the
    // AGT_CONNECTIVITY_PROBE_ENABLED env var stays the highest-precedence operator
    // override (env override > flag value > compiled default). Dormant until armed.
    if (hostFlagStore().getBoolean('connectivity-probe')) {
      try {
        const probeProjectDir = join(homedir(), '.augmented', agent.code_name, 'project');

        // ENG-8036: also connectivity-probe ENG-7916-quarantined integrations so
        // a sustained recovery can auto-resume them. Quarantined rows are excluded
        // from /host/agent-integrations (and therefore from .mcp.json + the
        // session-tool-bind probe), so they're fetched separately here and
        // concatenated into the connectivity-probe list ONLY - never rendered into
        // .mcp.json. The route returns [] unless 'mcp-auto-resume-mode' is armed,
        // so this is inert by default. Fail-open: a fetch error just probes the
        // active set (the quarantine simply stays until a later poll or a human).
        //
        // MCP reconstruction (ENG-8049 seam + ENG-8055 + ENG-8056): a quarantined
        // row is gone from .mcp.json, so an MCP probe leg has no server to
        // resolve. The ENG-8049 seam lets a leg probe an INLINE server entry
        // instead. Two producers fill it: ENG-8055 has the API re-mint the entry
        // for MANAGED (Composio) agent-scoped rows (the dominant kind) and return
        // it as `inline_server_entry`; ENG-8056 rebuilds the native/remote-MCP +
        // bundled-stdio entries HERE, host-side, from the forwarded descriptors
        // (config/remote_mcp/stdio_mcp) via synthetic-input buildMcpJson - stdio
        // entries need host-absolute paths the API can't produce. Both feed the
        // same `inline_server_entry` field so those rows recover-probe and
        // auto-resume. Still un-reconstructed (no inline entry → transient_error,
        // harmless): managed org/team scope. Non-MCP kinds (http_provider /
        // cli_command / builtin) emit no entry and never needed one.
        let probeSet: Parameters<typeof runAgentConnectivityProbes>[1] =
          integrations as Parameters<typeof runAgentConnectivityProbes>[1];
        // ENG-10061: same shape as the integrations lane above — the fetch lives
        // in a local so its inline row type stays inferred for the cached branch.
        const fetchQuarantined = async () => {
          const quarantined = await api.post<{
            integrations?: Array<{
              id: string;
              definition_id: string;
              scope: string;
              auth_type: string;
              credentials: Record<string, unknown>;
              source_type?: string | null;
              last_connectivity_check_at?: string | null;
              connectivity_test?: ConnectivityTestOverride | null;
              connection_key?: string | null;
              // ENG-8056: catalog descriptors forwarded so the manager can rebuild
              // the native/remote-MCP/stdio `.mcp.json` entry via buildMcpJson.
              config?: Record<string, unknown> | null;
              remote_mcp?: RemoteMcpSpec | null;
              stdio_mcp?: boolean | null;
              // ENG-8055/8056: the reconstructed `.mcp.json` server entry for a
              // quarantined row (removed from `.mcp.json`) so its probe can reach
              // the real server. Set API-side for MANAGED rows (ENG-8055); set
              // host-side just below for native/remote/stdio rows (ENG-8056).
              inline_server_entry?: unknown;
            }>;
          }>('/host/agent-integrations/quarantined', { agent_id: agent.agent_id });
          return quarantined.integrations ?? [];
        };

        try {
          // ENG-10061. Fail-open is unchanged and is the reason this stays
          // inside the existing `try`: a throw from the fetch — or from the
          // gate/cache path — still lands in the catch below, which probes the
          // active set alone and leaves the quarantine for a later poll.
          const quarantinedToken = quarantinedFetchGate.beginFetch(agent.agent_id);
          const cachedQuarantined = lastQuarantinedPayload.get(agent.agent_id);
          let qRows: Awaited<ReturnType<typeof fetchQuarantined>>;
          if (cachedQuarantined !== undefined && !quarantinedFetchGate.shouldFetch(agent.agent_id)) {
            // Cast: see the equivalent note on the integrations lane above. The
            // cached rows carry the `inline_server_entry` the reconstruction
            // below stamped onto them in place on the fetching pass.
            qRows = cachedQuarantined as typeof qRows;
            pollCycleQuarantinedSkips += 1;
          } else {
            qRows = await fetchQuarantined();
            // Stored by REFERENCE, before the reconstruction loop runs, which is
            // what lets that loop's in-place `inline_server_entry` writes reach
            // the cache. Copying here would cache the un-reconstructed rows and
            // silently degrade every cached pass to `transient_error`.
            lastQuarantinedPayload.set(agent.agent_id, qRows);
            quarantinedFetchGate.markFetched(agent.agent_id, quarantinedToken);
            pollCycleQuarantinedFetches += 1;
          }
          if (qRows.length > 0) {
            // ENG-8056: reconstruct the inline entry for the native/remote/stdio
            // rows the API did NOT mint (managed rows already carry one from
            // ENG-8055). buildMcpJson runs on THIS host so stdio entries get the
            // correct host-absolute command paths - byte-parity with what the
            // quarantine removed. Best-effort per row: a failure just leaves the
            // row without an entry (it degrades to transient_error, as before).
            for (const q of qRows) {
              if (q.inline_server_entry) continue; // managed: already reconstructed API-side
              try {
                const entry = reconstructQuarantinedMcpServerEntry(agent, {
                  id: q.id,
                  definition_id: q.definition_id,
                  display_name: q.definition_id,
                  scope: q.scope as IntegrationScope,
                  auth_type: q.auth_type as IntegrationAuthType,
                  credentials: q.credentials as IntegrationCredentials,
                  config: (q.config ?? {}) as Record<string, unknown>,
                  capabilities: [],
                  ...(q.remote_mcp ? { remoteMcp: q.remote_mcp } : {}),
                  ...(q.stdio_mcp === true ? { stdioMcp: true } : {}),
                });
                if (entry) q.inline_server_entry = entry;
              } catch (err) {
                log(
                  `Quarantined-entry reconstruction failed for '${agent.code_name}' toolkit=${q.definition_id}: ${(err as Error).message}`,
                );
              }
            }
            // ENG-8363: mark these as deliberately absent from `.mcp.json`.
            // Quarantine REMOVED them; they are probed here only so ENG-8036
            // auto-resume can observe recovery. Without the mark, the row whose
            // inline entry could not be reconstructed (managed org/team scope)
            // falls through to a file lookup, finds no key, and — now that a
            // missing key can escalate — would be reported hard `down` for
            // being in exactly the state quarantine put it in.
            probeSet = [...probeSet, ...markQuarantinedForProbe(qRows)];
            log(
              `Auto-resume: connectivity-probing ${qRows.length} quarantined integration(s) for '${agent.code_name}'`,
            );
          }
        } catch (err) {
          log(`Quarantined-probe fetch failed for '${agent.code_name}': ${(err as Error).message}`);
        }

        await runAgentConnectivityProbes(agent, probeSet, probeProjectDir);
      } catch (err) {
        log(`Connectivity probe failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // 6b-i.6 ENG-7053 / ENG-7220: rolling host-side session-tool-bind probe -
    // FLAG-GATED ('session-tool-probe', default off). Checks whether each
    // integration's MCP tools are actually bound in the running agent session
    // (stdio child alive / remote MCP answers tools/list) and reports a
    // bound|missing|unreachable|unknown verdict to /host/session-tool-bind. This
    // is the signal the Add Integration modal polls to confirm a newly-added
    // integration came online after the agent's next restart. AGT_SESSION_TOOL_PROBE_ENABLED
    // is the operator override. Best-effort: never fails the poll.
    if (hostFlagStore().getBoolean('session-tool-probe')) {
      // ENG-7429: if this agent has prompt-probe attempts left (armed by a restart
      // that re-bound its MCP tools), force every row due so the just-added
      // integration is re-checked NOW rather than on the ~1h cadence.
      const attemptsLeft = promptBindProbeAttempts.get(agent.code_name) ?? 0;
      const forceDue = attemptsLeft > 0;
      let probeRan = false;
      try {
        const probeProjectDir = join(homedir(), '.augmented', agent.code_name, 'project');
        probeRan = await runAgentSessionToolBindProbes(agent, integrations, probeProjectDir, { forceDue });
      } catch (err) {
        log(`Session-tool-bind probe failed for '${agent.code_name}': ${(err as Error).message}`);
      }
      // Burn an attempt only when the probe actually RAN (threw / null-gather on the
      // flaky post-restart window doesn't count - CodeRabbit on #3063), and drop the
      // agent once the budget is spent. The budget spans the bind window, so an early
      // attempt that fired before the tools were callable is followed by later ones
      // that re-check (force-due ignores the stale stamp) - the modal completes on
      // the first `bound`.
      if (forceDue && probeRan) {
        const left = attemptsLeft - 1;
        if (left > 0) promptBindProbeAttempts.set(agent.code_name, left);
        else promptBindProbeAttempts.delete(agent.code_name);
      }
    }

    // ENG-7359: prune orphaned remote/HTTP MCP servers. writeIntegrations
    // writes the CURRENT integrations but its reap only removes keys in a
    // static universe (INTEGRATION_REGISTRY nativeMcp + OAUTH_PROVIDERS +
    // hardcoded), so a removed DB-catalog remote MCP (integration_definitions.
    // remote_mcp, e.g. monday, keyed by definition_id) was never dropped: it
    // lingered in .mcp.json forever (the presence-reaper only reaps stdio
    // children; a remote MCP has no child). Reconcile the remote set against the
    // live integrations. Runs once per (successful) integrations fetch, OUTSIDE
    // the length>0 / intHash guards, so it also fires when the LAST integration
    // is removed (integrations.length === 0 => empty expected set => every
    // remaining remote non-managed key is an orphan) and self-heals a stale
    // entry on a quiet agent whose intHash never changes again. Safe against a
    // transient empty list: a failed fetch throws past this whole block, so
    // reaching here means `integrations` is authoritative. Native MCPs are stdio
    // and brokered toolkits are excluded, so neither needs to appear in the set.
    //
    // ENG-8359: the expected set is built from `remoteMcpServerKey`, NOT from
    // bare `definition_id`s. Every remote entry used to be keyed by
    // definition_id, but a NAMED connection is now written as
    // `<sanitized>-<connection_key>`; against a definition_id-only set that key
    // is in no expected entry, so this reaper would delete the second
    // connection's server on the very next poll — the manager silently undoing
    // what provisioning just wrote. This is the same rule the writer uses, from
    // the same module, for exactly that reason. removeMcpServer rewrites
    // the framework's own config (claude-code: provision/.mcp.json + a sync to
    // project/; opencode: provision/opencode.json); the downstream
    // membership-diff then restarts the session to drop it live.
    //
    // ENG-7994: the declared-server map comes from `readMcpServers`, not from
    // parsing `mcpServers` out of `getMcpPath()`. That inline parse was
    // claude-code's `.mcp.json` shape; opencode declares servers under `mcp` in
    // opencode.json, so this prune silently iterated nothing for opencode
    // agents and orphaned remotes lingered forever.
    if (frameworkAdapter.removeMcpServer && frameworkAdapter.readMcpServers) {
      try {
        const declaredServers = frameworkAdapter.readMcpServers(agent.code_name);
        const expectedRemoteKeys = expectedRemoteMcpKeys(
          integrations as Array<{ definition_id: string; connection_key?: string | null }>,
        );
        for (const key of orphanedRemoteMcpKeys(declaredServers, expectedRemoteKeys)) {
          frameworkAdapter.removeMcpServer(agent.code_name, key);
          log(`[hot-reload] Removed orphaned remote MCP server '${key}' for '${agent.code_name}' (integration removed)`);
        }
      } catch (err) {
        log(`[hot-reload] Failed to prune orphaned remote MCP servers for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // ENG-7812: attribute this poll's integration MEMBERSHIP change to the
    // specific integration(s) added/removed, so the provisioning-reload restart
    // that binds them lands in ITS OWN restart-breaker bucket. Bringing up N
    // integrations during onboarding therefore spreads N restarts across N
    // buckets of ~1 (no trip), while a single integration re-binding on an
    // UNCHANGED set produces no delta here (its restarts stay unattributed and
    // still accumulate into the shared bucket that trips + pages). Runs OUTSIDE
    // the length>0 guard below, on the authoritative `integrations` (a failed
    // fetch already threw past this block), so removing the LAST integration
    // records the now-empty membership and a later re-add is correctly seen as a
    // delta. `at` bounds the attribution to the breaker's provisioning window so a
    // stale add can't absorb a much later, unrelated reprovision.
    const currentIntIds = integrations.map((i) => i.definition_id);
    const prevIntIds = agentState.knownIntegrationMembership.get(agent.agent_id) ?? [];
    const prevIntSet = new Set(prevIntIds);
    const curIntSet = new Set(currentIntIds);
    const changedIntIds = Array.from(
      new Set([
        ...currentIntIds.filter((id) => !prevIntSet.has(id)), // added
        ...prevIntIds.filter((id) => !curIntSet.has(id)), // removed
      ]),
    ).sort();
    if (changedIntIds.length > 0) {
      agentState.provisioningAttribution.set(agent.code_name, {
        key: changedIntIds.join('+'),
        at: Date.now(),
      });
    }
    agentState.knownIntegrationMembership.set(agent.agent_id, currentIntIds);

    // 6b-ii. Integration credentials - env vars + CLAUDE.md snippets. Gated on
    // an integration hash that covers `definition_id + credentials + config`,
    // so config-only DB changes (e.g. switching Xero's `xero_tenant_id`
    // without rotating the access token) propagate to .env.integrations and
    // trigger the env-var-aware MCP child reap below. ENG-4911.
    // ENG-7946: compute capability surface hash and membership/flap state
    // outside the length+intHash gates so the integration-update notice fires
    // for capability-only changes (display_name/capabilities changed without a
    // credentials/config rotation) and for the last-integration-removed case.
    const capSurfaceHash = computeCapabilitySurfaceHash(integrations);
    const prevCapSurfaceHash = agentState.knownCapabilitySurfaceHashes.get(agent.agent_id);
    const capSurfaceNames = integrations.map((i) => i.display_name || i.definition_id).join(', ');

    // ENG-6123: record the integration MEMBERSHIP (definition_ids, ignoring
    // credential/config churn) on EVERY poll so an oscillating set is detected
    // and a stabilised one recovers on the next quiet poll. Keyed on
    // membership, not intHash, so a Composio mcp_url re-sign / credential
    // rotation (monotonic, never revisits) never trips it - only a genuine
    // add-remove-add flap does.
    const intMembership = membershipSignature(integrations.map((i) => i.definition_id));
    const intFlap = mcpFlapDampener.record(agent.code_name, FLAP_CHANNEL_INTEGRATIONS, intMembership);
    if (intFlap.onset) {
      log(
        `[mcp-flap-dampener] integration set for '${agent.code_name}' is FLAPPING ` +
        `(${intFlap.tier} tier: ${intFlap.transitions} transitions / ${intFlap.distinctStates} states in window) - ` +
        `suppressing integration-update notices until it settles (ENG-6123, ENG-6512)`,
      );
      recordConfigChurnEvent(agent.agent_id, agent.code_name, FLAP_CHANNEL_INTEGRATIONS, intMembership);
    }

    // reapNote accumulates MCP-reconnect and session-restart caveats from
    // the env-rewrite block below. Empty when no credential rotation ran
    // (capability-only change or last-integration-removed cases).
    let reapNote = '';
    // rotationHandled mirrors the inner flag: false only when the env-write
    // try/catch threw, meaning we must not advance either hash baseline.
    let rotationHandled = true;

    // ENG-8035: When ALL integrations are removed, the `integrations.length > 0`
    // gate below prevents writeIntegrations from running, so integration-derived
    // MCP server entries (e.g. anchor-browser stdio proxy) are never pruned and
    // keep running. Call writeIntegrations([]) once for the zero-integration case,
    // guarded by a sentinel hash so we don't re-run it on every subsequent poll.
    const EMPTY_INTEGRATIONS_HASH = '\x00empty\x00';
    if (integrations.length === 0 && frameworkAdapter.writeIntegrations) {
      const prevIntHash = agentState.knownIntegrationHashes.get(agent.agent_id);
      if (prevIntHash !== EMPTY_INTEGRATIONS_HASH) {
        try {
          frameworkAdapter.writeIntegrations(agent.code_name, [], agent.agent_id, {
            renderClaudeMdSection: renderIntegrationsSection,
          });
          agentState.knownIntegrationHashes.set(agent.agent_id, EMPTY_INTEGRATIONS_HASH);
          log(`[ENG-8035] Pruned integration-derived MCP entries for '${agent.code_name}' (integrations.length=0)`);
        } catch (err) {
          // Leave the sentinel unset so the next poll retries the prune.
          // Set rotationHandled=false so the capability-surface baseline does
          // not advance on a failed prune pass.
          rotationHandled = false;
          log(`[ENG-8035] Empty integration prune failed for '${agent.code_name}': ${(err as Error).message}`);
        }
      }
    }

    if (integrations.length > 0) {
      const intHash = computeIntegrationsHash(integrations);
      const prevIntHash = agentState.knownIntegrationHashes.get(agent.agent_id);

      if (intHash !== prevIntHash) {
        // ENG-4832 (CodeRabbit on PR #797 round 3): capture the
        // pre-write .env.integrations BEFORE writeIntegrations runs,
        // so we can diff against the post-write content and pass the
        // PRECISELY-rotated subset to findMcpServersUsingVars.
        // Without the diff, changedVars would be "every var currently
        // present in the file" and we'd over-reap MCP children whose
        // env didn't actually change. read returns undefined when the
        // file doesn't exist yet (first-ever integrations write for
        // this agent) — diffEnvIntegrations treats that as "every
        // post-write var is new", which is the correct first-write
        // behaviour: there's no prior state to preserve, so any new
        // vars warrant a reap.
        const projectDir = join(homedir(), '.augmented', agent.code_name, 'project');
        const envIntPath = join(projectDir, '.env.integrations');
        let preWriteEnv: string | undefined;
        try {
          preWriteEnv = readFileSync(envIntPath, 'utf-8');
        } catch {
          // No prior file — treat as empty so the diff shows all
          // post-write vars as added. fine.
          preWriteEnv = undefined;
        }

        if (frameworkAdapter.writeIntegrations) {
          // ENG-4920: thread agent.agent_id through so the claudecode
          // adapter can wire AGT_AGENT_ID + AGT_INTEGRATION_ID into the
          // Xero MCP server's spawn env.
          // ENG-8174: same flag decision generateArtifacts renders with, so
          // the two writers can't fight over the sentinel range.
          frameworkAdapter.writeIntegrations(agent.code_name, integrations as Parameters<typeof frameworkAdapter.writeIntegrations>[1], agent.agent_id, {
            renderClaudeMdSection: renderIntegrationsSection,
          });
        }
        log(`Integrations provisioned for '${agent.code_name}' (${integrations.length} integration(s))`);

        const fw = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;

        // ENG-7976: opencode initializes MCP servers only from the config the
        // serve loaded at boot, so newly-written integration servers don't reach a
        // RUNNING serve until it respawns. writeIntegrations above just landed them
        // in provision/opencode.json; restart the serve (the poll loop's
        // ensureOpencodeRuntime respawns it, re-materializing the fresh mcp block)
        // so they load now instead of waiting for a natural respawn. Gate on a
        // STRUCTURAL change (an integration definition_id added/removed, via the
        // membership delta computed above) - NOT the credential-sensitive intHash -
        // so an OAuth token rotation doesn't thrash the session and drop its
        // in-memory conversation map (that continuity gap is ENG-7975). The
        // isOpencodeSessionHealthy gate skips first-provision (the imminent first
        // spawn already carries the new servers) and the no-session case.
        if (fw === 'opencode' && changedIntIds.length > 0 && isOpencodeSessionHealthy(agent.code_name)) {
          log(
            `[hot-reload] opencode integration set changed for '${agent.code_name}' ` +
            `(${changedIntIds.join(', ')}) - restarting serve to load new MCP servers`,
          );
          stopOpencodeSession(agent.code_name, log);
        }

        // Rotated env vars only reach MCP children that get RE-spawned.
        // The pre-existing children hold their spawn-time env, so the
        // access_token they're calling Xero/Gmail with is whatever was
        // in the env block when claude first launched them. Once that
        // token's 30-min TTL ticks past, every upstream call 401s and
        // the agent reports "refresh failed" (Stirling, 3-day blackout).
        // Identify which MCP server keys reference any of the
        // just-rotated env vars and SIGTERM their children so Claude
        // Code's MCP transport detects the dead child and respawns it
        // on next request — fresh env, fresh token, conversation
        // context preserved.
        //
        // CodeRabbit on PR #797 round 4: defer the
        // agentState.knownIntegrationHashes.set() update until the reap pipeline
        // completes. If the read/parse/reap path throws, we leave the
        // hash unchanged so the NEXT manager tick retries the rotation
        // end-to-end. Without this defer, a single transient failure
        // (file briefly missing, ps temporarily unavailable, JSON
        // parse error mid-write) would mark the rotation handled and
        // leave the stale MCP child to keep returning 401s
        // indefinitely — exactly the silent failure mode this whole
        // PR exists to eliminate.
        // (rotationHandled is declared in the outer scope so the cap-surface
        // notice block can also gate on it after this if-block exits.)

        if (fw === 'claude-code' && isSessionHealthy(agent.code_name)) {
          try {
            const projectMcpPath = join(projectDir, '.mcp.json');
            const postWriteEnv = readFileSync(envIntPath, 'utf-8');
            const mcpContent = readFileSync(projectMcpPath, 'utf-8');
            // Diff pre-write vs post-write to get ONLY the rotated
            // var names. Without this we'd reap every MCP whose env
            // touches any var in the file — interrupting unrelated
            // tool traffic on every refresh tick.
            const changedVars = diffEnvIntegrations(preWriteEnv, postWriteEnv);
            const mcpJsonForReap = JSON.parse(mcpContent) as Parameters<typeof findMcpServersUsingVars>[0];
            const affectedServerKeys = findMcpServersUsingVars(mcpJsonForReap, changedVars);

            if (affectedServerKeys.length > 0) {
              reapStaleMcpChildren({
                log,
                codeName: agent.code_name,
                serverKeys: affectedServerKeys,
                mcpJson: mcpJsonForReap,
                // ENG-6670: enumerate + signal in the container PID namespace
                // for Docker-isolated agents (host ps can't see in-container
                // children; a host kill on a container pid would be wrong).
                isolated: isolationMode(agent.code_name) === 'docker',
              });
            }

            // ENG-7510: env-only / CLI integrations have no MCP child to reap.
            // GitHub is the canonical case: its GITHUB_ACCESS_TOKEN / GITHUB_TOKEN
            // live in the agent's OWN process env (backing the `gh` CLI + curl),
            // not an MCP server's env block, so `findMcpServersUsingVars` matches
            // nothing and the reap above is a no-op. Because the wrapper sources
            // .env.integrations exactly once at spawn (no live re-read), the running
            // agent cannot see an added/removed var of this kind without a session
            // respawn. Channel secrets are excluded here because they have their own
            // launch-time respawn watcher (checkChannelSecretDriftAndScheduleRestart,
            // ENG-6062) — respawning them here too would double-restart. So a changed
            // var that is (a) not a channel secret and (b) referenced by NO MCP server
            // needs a full respawn to take effect; without this, adding/removing such
            // an integration silently does nothing until a manual restart.
            // ENG-7748/ENG-7965: exempt vars a stdio proxy reads LIVE per request
            // from the respawn set. Two axes:
            //   • AGT_REMOTE_MCP_EXTRA_HEADERS (ENG-7748) - extra headers such as
            //     Anchor's re-minted ANCHOR_BROWSER_SESSION_ID; proxy serves the new
            //     value on the next request, no respawn needed.
            //   • AGT_REMOTE_MCP_TOKEN_VAR (ENG-7965) - the primary OAuth bearer (e.g.
            //     BRAND_NINJA_ACCESS_TOKEN, XERO_ACCESS_TOKEN). remote-oauth-proxy.ts
            //     calls readFileSync on the secrets file for every outbound request, so
            //     a rotated token is picked up automatically without restarting the
            //     session. These vars look "env-only" to findMcpServersUsingVars (the
            //     var name is a literal string value of TOKEN_VAR, not a ${VAR}
            //     placeholder), so without this exemption the manager would schedule a
            //     full session restart on every OAuth rotation - confirmed root-cause of
            //     Stirling × 2 and Jack × 1 restarts on 2026-07-22.
            const liveProxyVars = new Set([
              ...liveProxyExtraHeaderVars(mcpJsonForReap),
              ...liveProxyTokenVars(mcpJsonForReap),
            ]);
            // ENG-6918: control-plane bookkeeping vars never justify a respawn.
            //
            // The adapter no longer publishes them, but every agent provisioned
            // before that change still has them in its `.env.integrations`. The
            // first poll after the fix REMOVES them, and a removal is an
            // add/remove diff like any other — so without this filter the fix for
            // a restart flap would itself restart every agent in the fleet once,
            // on deploy. Nothing ever consumed these vars, so their disappearance
            // needs no restart to be noticed.
            //
            // It is also the belt to the adapter's braces: should any writer
            // reintroduce one, it can no longer reach the provisioning tally that
            // auto-pauses an onboarding agent (ENG-8679).
            const respawnVars = envOnlyRespawnVars(changedVars, mcpJsonForReap, CHANNEL_SECRET_ENV_KEYS)
              .filter((v) => !liveProxyVars.has(v) && !isControlPlaneOnlyEnvVar(v));
            // ENG-7541: distinguish a value-only credential RE-MINT (the same
            // env-only credential handed back with a new value — GitHub's App
            // installation token is re-minted server-side on every provision
            // poll) from a real integration ADD/REMOVE. A respawn whose changed
            // env-only vars are ALL value-only rotations is benign churn that
            // must never pause the agent, so it is tagged 'credential-rotation'
            // (its own non-tripping breaker class). If ANY changed var is an
            // add/remove, the integration set membership actually changed — keep
            // 'hot-reload-mcp' (wants an MCP rebind + counts on the provisioning
            // tally). preWriteEnv === '' (first provision) makes every var an
            // add, so a genuine first-time add is never misread as a rotation.
            const envDiff = classifyEnvIntegrationsDiff(preWriteEnv, postWriteEnv);
            const rotatedOnlyVars = new Set(envDiff.rotated);
            const envRespawnReason: RestartReason =
              respawnVars.length > 0 && respawnVars.every((v) => rotatedOnlyVars.has(v))
                ? 'credential-rotation'
                : 'hot-reload-mcp';
            // Gate on the flap dampener exactly like the notice below: an
            // add↔remove↔add flap adopts the new env baseline without
            // respawn-looping, and the next quiet, settled poll schedules the
            // restart. scheduleSessionRestart additionally coalesces and honours
            // the circuit breaker.
            //
            // ENG-8343: the two reasons this call site can emit are gated
            // DIFFERENTLY, and this comment used to claim both were deferred until
            // idle. They are not:
            //   • 'credential-rotation' (value-only re-mint) IS gateable as of
            //     ENG-8343 — window-exempt, but never fires mid-turn.
            //   • 'hot-reload-mcp' (membership add/remove) deliberately BYPASSES
            //     the gate (ENG-6568): a freshly-added integration that silently
            //     does nothing until the agent goes quiet is the worse failure.
            // So this restart is safe on a busy agent for a rotation, and is still
            // intentionally prompt-and-interrupting for an add/remove.
            const needsRespawn = respawnVars.length > 0 && !intFlap.flapping;

            // The two notes are INDEPENDENT, not exclusive: one poll can rotate an
            // MCP-referenced var (reconnect note) AND add/remove an env-only var
            // (restart note), so concatenate both rather than choosing one branch
            // (CodeRabbit on #3145 - a mixed set must not silently drop the restart
            // warning). "Signalled to reconnect" is accurate because the reaper
            // above forces the affected MCP children to respawn with fresh env; the
            // MCP client's retry policy covers the brief in-flight window.
            const reconnectNote = affectedServerKeys.length > 0
              ? ` The MCP servers that depend on rotating credentials (${affectedServerKeys.join(', ')}) have been signalled to reconnect.`
              : '';
            const restartNote = needsRespawn
              ? ' Your session will restart shortly to apply the change (ENG-7510).'
              : '';
            // Store in outer variable so the cap-surface notice block (after
            // the integrations.length gate) can append reap context.
            reapNote = `${reconnectNote}${restartNote}`;

            // ENG-7946: log a suppression line when a credential/config rotation
            // bumped intHash but left the capability surface unchanged. The
            // agent-facing notice is emitted (or suppressed for flap) in the
            // cap-surface block below, which runs regardless of intHash changes.
            if (!intFlap.flapping && capSurfaceHash === prevCapSurfaceHash) {
              log(`[hot-reload] Suppressed integration-update notice for '${agent.code_name}' - capability surface unchanged (credential/config rotation only, ENG-7946): ${capSurfaceNames}`);
            }

            // ENG-7510: schedule the respawn AFTER emitting the notice above so the
            // "your session will restart" warning is delivered before the zero-delay
            // restart is queued (CodeRabbit on #3145); ordering the emit first
            // removes any doubt. breakerReason is 'credential-rotation' for a pure
            // value-only re-mint (non-tripping, ENG-7541) or 'hot-reload-mcp' for a
            // membership add/remove — both come back immediately
            // (restartReasonBindsNewMcp).
            //
            // ENG-8343: the delayMs=0 below is the SCHEDULE delay, not the fire
            // time. For 'credential-rotation' the gate inside scheduleSessionRestart
            // now re-arms it until the agent is idle, so the notice above is read
            // before the stop. For 'hot-reload-mcp' the stop really is immediate by
            // design (ENG-6568) — that path is prompt on purpose.
            if (needsRespawn) {
              scheduleSessionRestart(
                agent.code_name,
                0,
                `env-only integration change (${respawnVars.join(', ')})`,
                envRespawnReason,
                undefined,
                // Structured "what changed" for the audit row. Var NAMES only —
                // never values — and carried as string values under neutral
                // keys, because the audit scrubber redacts by key name and
                // would blank a key like `github_access_token` (ENG-8293).
                { env_only_change: { vars: respawnVars, kind: envRespawnReason } },
              );
              log(
                `[hot-reload] Scheduled respawn for '${agent.code_name}' to apply env-only integration change ` +
                `(no MCP child to reap): ${respawnVars.join(', ')} [reason=${envRespawnReason}] (ENG-7510/ENG-7541)`,
              );
            }
          } catch (err) {
            // Don't mark handled — the next tick will reattempt the
            // full read/parse/reap pipeline. Idempotent
            // writeIntegrations means the second call is a no-op
            // write; the only practical cost is one extra
            // "Integrations provisioned" log line per retry, which is
            // the right operator signal that something's wrong.
            rotationHandled = false;
            log(`[hot-reload] Failed to compute / reap affected MCP servers for '${agent.code_name}': ${(err as Error).message} — will retry next tick`);
          }
        }

        if (rotationHandled) {
          agentState.knownIntegrationHashes.set(agent.agent_id, intHash);
        }
      }
    }

    // ENG-7946: fire the agent-facing notice whenever the capability surface
    // changes - runs outside both the integrations.length > 0 gate and the
    // intHash !== prevIntHash gate so it catches:
    //   (a) capability-only changes (display_name / capabilities reconfigured
    //       without a credential/config rotation - intHash stays the same);
    //   (b) the last-integration-removed case (integrations.length === 0).
    // reapNote was populated inside the intHash block above when an MCP child
    // reap ran; it is empty for cases (a) and (b) where no env-write occurred.
    // Guard on rotationHandled: if the env-write try/catch threw, defer to the
    // next tick rather than advancing the cap-surface baseline on a failed write.
    if (capSurfaceHash !== prevCapSurfaceHash && rotationHandled) {
      if (intFlap.flapping) {
        log(`[mcp-flap-dampener] suppressed integration-update notice for '${agent.code_name}' (set flapping, ENG-6123): ${capSurfaceNames}`);
      } else {
        injectMessage(agent.code_name, 'system', `Your integrations have been refreshed. You have access to: ${capSurfaceNames}.${reapNote}`, {
          task_name: 'integration-update',
        }, log).catch(() => {});
        log(`[hot-reload] Notified '${agent.code_name}' about integration update: ${capSurfaceNames}`);
      }
      // Persist the baseline even when the notice was suppressed (flapping):
      // the suppression is transient and the next settled poll should not
      // re-fire for the same capability change.
      agentState.knownCapabilitySurfaceHashes.set(agent.agent_id, capSurfaceHash);
    }

    // 6b-iii. Managed-toolkit MCP server convergence. Runs every poll (not
    // gated on integration credential changes) so the `.mcp.json` desired
    // state self-heals when an earlier write was skipped or lost — plugins
    // added to an agent now always propagate, not only on the exact poll
    // where credentials happened to change. ENG-4481.
    //
    // Idempotency: we hash the desired-state set (server_id → url + header
    // keys) and only re-write .mcp.json (and trigger the restart-session
    // notification) when that hash changes. `writeMcpServer` itself is
    // idempotent via canonical-json compare, so a redundant call with the
    // same config is a cheap no-op.
    const fwForMcp = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
    if (frameworkAdapter.writeMcpServer) {
      try {
        // ENG-9057: served from the cycle's single batch call (see
        // createManagedToolkitReader). This used to POST `agent_ids:
        // [agent.agent_id]` here — one request per agent per poll to a batch
        // endpoint — and then filter the response back down to this agent,
        // which the reader now does when it buckets the batch.
        const agentToolkits = await managedToolkits.forAgent(agent.agent_id);
        const expectedServerIds = new Set<string>();
        const desiredEntries: Array<{ serverId: string; url: string; headers?: Record<string, string>; name: string }> = [];
        for (const tk of agentToolkits) {
          // Prefer the API-precomputed per-connection server key; fall back to
          // the toolkit-level derivation for older API builds (default-only,
          // byte-identical). See resolveManagedMcpServerId.
          const serverId = resolveManagedMcpServerId(tk);
          expectedServerIds.add(serverId);
          // ENG-5292: record reverse mapping so the reaper's give-up
          // callback (later in this file) can resolve a serverId back
          // to the toolkit_id when POSTing to /host/integration-health.
          managedToolkitIdByAgentAndServerId.set(
            managedToolkitMapKey(agent.agent_id, serverId),
            { toolkitId: tk.toolkit_id, connectionKey: tk.connection_key ?? 'default' },
          );
          const mcpUrl = tk.mcp_url;
          const mcpHeaders = tk.mcp_headers;
          const proxyUrl = tk.proxy_url.startsWith('/') ? `${requireHost()}${tk.proxy_url}` : tk.proxy_url;
          const url = mcpUrl ?? proxyUrl;
          desiredEntries.push({ serverId, url, headers: mcpHeaders, name: tk.toolkit_name });
        }

        // Desired-state hash — stable ordering (sort by serverId), url + full
        // headers (keys AND values). Header values must participate so that a
        // provider rotating a credential-bearing header triggers a rewrite;
        // hashing only keys would silently leave `.mcp.json` on stale auth
        // until an unrelated toolkit change bumped the hash.
        const hashBasis = desiredEntries
          .slice()
          .sort((a, b) => a.serverId.localeCompare(b.serverId))
          .map((e) => {
            const headersHash = createHash('sha256')
              .update(canonicalJson(e.headers ?? {}))
              .digest('hex')
              .slice(0, 16);
            return `${e.serverId}|${e.url}|${headersHash}`;
          })
          .join('\n');
        const mcpHash = createHash('sha256').update(hashBasis).digest('hex').slice(0, 16);
        const prevMcpHash = agentState.knownManagedMcpHashes.get(agent.agent_id);

        // ENG-5537: structure-only hash — serverId membership + header KEY
        // names, deliberately excluding the url and header VALUES. The write
        // below still keys off `mcpHash` (so .mcp.json converges to the live
        // signed url), but the session RESTART is gated on this hash so a
        // value-only rotation (composio re-signing `mcp_url` every poll)
        // refreshes the file without churning the running session. Only a
        // real structural change — toolkit added/removed, or a header key
        // appearing/disappearing — restarts the session.
        const structureHash = managedMcpStructureHash(desiredEntries);
        const prevStructureHash = agentState.knownManagedMcpStructure.get(agent.agent_id);

        // ENG-6123: record the managed-MCP MEMBERSHIP (serverIds) on EVERY poll
        // so an oscillating toolkit set is caught and a stabilised one recovers
        // on the next quiet poll. Keyed on membership (not mcpHash) so Composio
        // mcp_url re-signs — which move mcpHash but not the server set — never
        // trip it; only a real add↔remove↔add flap does.
        const mcpMembership = membershipSignature(expectedServerIds);
        const mcpFlap = mcpFlapDampener.record(agent.code_name, FLAP_CHANNEL_MANAGED_MCP, mcpMembership);
        if (mcpFlap.onset) {
          log(
            `[mcp-flap-dampener] managed-MCP set for '${agent.code_name}' is FLAPPING ` +
            `(${mcpFlap.tier} tier: ${mcpFlap.transitions} transitions / ${mcpFlap.distinctStates} states in window) — ` +
            `suppressing mcp-update notice + restart until it settles (ENG-6123, ENG-6512)`,
          );
          recordConfigChurnEvent(agent.agent_id, agent.code_name, FLAP_CHANNEL_MANAGED_MCP, mcpMembership);
        }

        // ─── ENG-9708: prune only after N CONSECUTIVE absences ──────────────
        //
        // THIS BLOCK IS OUTSIDE `if (mcpHash !== prevMcpHash)` ON PURPOSE, and
        // moving it back inside would make it inert. The hash is stored at the
        // end of that block, so:
        //
        //   cycle 1 — toolkit missing, hash changed, defer (streak 1), hash stored
        //   cycle 2 — still missing, hash now EQUALS the stored hash, block skipped
        //   ...     — the streak never matures and NOTHING is ever pruned,
        //             including genuine revocations
        //
        // So both the observation and the removal have to run every cycle.
        // eng-9708-managed-prune-confirm-cycles.test.ts has a test that fails
        // against the inside-the-guard implementation.
        //
        // WHY DURATION AND NOT SHAPE (2026-08-31). A Composio per-key IP
        // allowlist made the API's Composio calls 401, so /host/managed-toolkits
        // returned WELL-FORMED responses containing no Composio toolkits. 24
        // `composio_*` servers were pruned across 6+ agents in under two minutes;
        // the allowlist came off twenty minutes later and the prune did not
        // reverse. ENG-9140's cross-agent ledger fired and named the cause
        // exactly, but it is deliberately a report rather than a veto, and no
        // shape check can tell "the API cannot reach Composio" from a real
        // revocation. Breadth and shape were both already covered. Duration was
        // the missing dimension.
        //
        // ENG-7994: the declared set comes from the adapter's `readMcpServers`,
        // NOT from parsing `mcpServers` out of `getMcpPath()`. That inline parse
        // only understood claude-code's `.mcp.json`; opencode declares its
        // servers under `mcp` in opencode.json, so the old loop iterated nothing
        // for opencode agents and a revoked toolkit's entry was never pruned
        // (prod agent nora, 2026-07-22).
        //
        // WHILE A PRUNE IS DEFERRED, THE WHOLE CONVERGENCE IS HELD — see the
        // `deferredPruneKeys` handling at the write gate below. Deferring only
        // the file edit would leave `.mcp.json` and the RUNNING SESSION saying
        // different things: the structure hash is derived from the desired set,
        // so an absent toolkit moves it on cycle 1 and would restart the session
        // (announcing a removal that has not happened) while the deferred entry
        // is still in the file, and the removal that eventually lands on cycle N
        // would then be invisible to the session because the hash had already
        // been banked. Holding the baseline keeps the two in step.
        const deferredPruneKeys: string[] = [];
        if (frameworkAdapter.removeMcpServer && frameworkAdapter.readMcpServers) {
          try {
            const declaredManaged = Object.keys(
              frameworkAdapter.readMcpServers(agent.code_name),
            ).filter(isManagedMcpServerKey);
            const decisions = agentState.managedPruneStreaks.observe(
              agent.agent_id,
              declaredManaged,
              expectedServerIds,
            );
            for (const d of decisions) {
              if (!d.mature) {
                deferredPruneKeys.push(d.serverKey);
                // Logged so a waiting prune is greppable rather than silent —
                // an operator watching an incident needs to see the guard
                // holding, not an absence of lines.
                log(
                  `[managed-toolkit] ${agent.code_name}: '${d.serverKey}' absent for ` +
                  `${d.streak}/${pruneConfirmCycles()} cycles — deferring prune (ENG-9708)`,
                );
                continue;
              }
              frameworkAdapter.removeMcpServer(agent.code_name, d.serverKey);
              log(
                `[managed-toolkit] Removed stale MCP server '${d.serverKey}' for ` +
                `'${agent.code_name}' after ${d.streak} consecutive absences (ENG-9708)`,
              );
              // ENG-9140 AC5: per-removal logging above is per agent; this is
              // what makes a whole-host prune visible as one event.
              managedPruneLedger.record(agent.code_name, d.serverKey);
            }
          } catch {
            // MCP config doesn't exist yet — nothing to observe or clean up.
          }
        }

        if (mcpHash !== prevMcpHash) {
          for (const e of desiredEntries) {
            frameworkAdapter.writeMcpServer(agent.code_name, e.serverId, { url: e.url, headers: e.headers });
            // Hash the URL — `e.url` can be a provider-issued direct MCP URL
            // that carries signed query tokens. manager.log is persisted to
            // disk, so don't let raw creds land there.
            const urlHash = createHash('sha256').update(e.url).digest('hex').slice(0, 12);
            log(`[managed-toolkit] ${agent.code_name}: wrote '${e.name}' (serverId=${e.serverId}, url_hash=${urlHash})`);
          }

          // ENG-9708: do NOT bank the baseline while a prune is awaiting
          // confirmation. The desired set has already lost the absent toolkit,
          // so banking here would record "this is the converged state" for a
          // state the file is deliberately not in yet — and the removal that
          // lands on cycle N would then compare equal to the stored hash and
          // never restart the session that still has the dead server loaded.
          // Holding the baseline costs an idempotent re-write per poll for at
          // most N cycles and buys the two properties that matter: a toolkit
          // that comes back mid-window is a COMPLETE no-op, and a prune that
          // does mature still restarts the session exactly once.
          if (deferredPruneKeys.length === 0) {
            agentState.knownManagedMcpHashes.set(agent.agent_id, mcpHash);
            agentState.knownManagedMcpStructure.set(agent.agent_id, structureHash);
          } else {
            log(
              `[managed-toolkit] ${agent.code_name}: holding the managed-MCP baseline and ` +
              `session restart while ${deferredPruneKeys.length} prune(s) await confirmation ` +
              `(${deferredPruneKeys.join(', ')}) — ENG-9708`,
            );
          }

          // Only notify about MCP server changes after the first poll — on
          // initial convergence the session is either not yet started or will
          // be started with the fresh .mcp.json, so a restart notification
          // would be a noisy false alarm. Full-removal case is still a valid
          // change (we need to restart so the session drops the tools it had
          // loaded), so don't gate on `agentToolkits.length > 0`.
          //
          // ENG-5537: additionally gate on a *structural* change
          // (`structureHash`), not just any `mcpHash` change. A value-only
          // rotation (composio re-signing `mcp_url`) writes the fresh file
          // above but leaves `structureHash` identical — the running session's
          // established MCP connections are unaffected, so we skip the restart
          // and the inject-notice. `prevStructureHash === undefined` is the
          // first-convergence case (no running session to disturb).
          //
          // ENG-6123: `flapping` holds the restart + inject-notice while the
          // managed-MCP membership is oscillating. The .mcp.json above was
          // still written (the file converges to the live set); we only
          // withhold the notice + the session reload — the two per-flip burns.
          // When the set settles, the next structural change restarts once.
          //
          // ENG-7576 (PR 2): the decision tree lives in decideManagedMcpRestart
          // (pure, unit-tested) and gains the launch-state anchor: when the
          // running session was SPAWNED with this exact structure (the
          // integration-add common case, where the doorbell stop + respawn
          // already applied the change before a baseline re-detected it), the
          // restart is suppressed as already-live instead of churning the
          // session again. The check runs BEFORE the inject-notice, so a
          // suppressed restart never strands a "restarting shortly" message.
          // Cheap-comparison short-circuit preserved from the old inline
          // condition: isSessionHealthy (a tmux probe) is only evaluated when a
          // structural change was actually detected, not on every quiet poll.
          const mcpRestartDecision =
            fwForMcp === 'claude-code' &&
            prevStructureHash !== undefined &&
            structureHash !== prevStructureHash
              ? decideManagedMcpRestart({
                  prevStructureHash,
                  structureHash,
                  launchStructureHash: sessionLaunchManagedStructure.get(agent.code_name),
                  sessionHealthy: isSessionHealthy(agent.code_name),
                  flapping: mcpFlap.flapping,
                  // ENG-9708: an unconfirmed prune holds the restart too. The
                  // structural change it would announce ("servers were removed")
                  // has not been applied to the file yet, so restarting now
                  // would churn the session and tell the agent something untrue.
                  deferringPrune: deferredPruneKeys.length > 0,
                })
              : null;
          if (mcpRestartDecision?.kind === 'suppress-already-live') {
            log(
              `[hot-reload] Restart suppressed for '${agent.code_name}': the managed MCP ` +
              `structure change is already live in the running session (launched with it) - ` +
              `no respawn needed (ENG-7576)`,
            );
          }
          if (mcpRestartDecision?.kind === 'restart') {
            const mcpNames = agentToolkits.map((tk) => tk.toolkit_name).join(', ') || 'none (all removed)';
            log(`[hot-reload] MCP servers changed for '${agent.code_name}': ${mcpNames} — restarting session`);

            const restartNotice = agentToolkits.length > 0
              ? `New MCP tool servers have been configured: ${mcpNames}. Note: MCP servers require a session restart to connect. Your manager will restart your session shortly.`
              : 'Managed MCP tool servers were removed. Your manager will restart your session shortly so the session drops those tools.';
            const delivered = await injectMessage(agent.code_name, 'system',
              restartNotice + BACK_ONLINE_GREETING_GUIDANCE,
              { task_name: 'mcp-update' }, log).catch(() => false);

            const delay = delivered ? 8_000 : 3_000;
            if (!delivered) {
              log(`[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`);
            }
            // ENG-7401: start measuring the integration-add restart here — the
            // render is done and we're about to schedule the stop. Pairs with the
            // markRestartStopped / finishRestartTiming marks so manager.log carries
            // a per-stage breakdown of the ~2 min wait. Pure telemetry.
            beginRestartTiming(
              agent.code_name,
              { reason: 'new MCP servers', delivered, injectDelayMs: delay },
              log,
            );
            // ENG-4807 / CodeRabbit on PR #773: route through the
            // coalescing scheduler so a same-tick channel-set change
            // doesn't double-restart the agent.
            scheduleSessionRestart(agent.code_name, delay, 'new MCP servers');
          }
        }
      } catch (err) {
        log(`[managed-toolkit] Failed to provision for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // ENG-4337: QMD CLI install + collection setup are now handled by the QMD
    // plugin (slug 'qmd') via its on_install hook. See refreshData.plugin_install_hooks
    // below — the manager runs the hook script `qmd collection add` after deploying
    // plugin skills. The QMD plugin is auto-installed for any agent that has the
    // QMD integration active (see migration 20260408000002_qmd_native_plugin.sql).
  } catch (err) {
    // Non-fatal — integration provisioning is supplementary
    log(`Integration provisioning failed for '${agent.code_name}': ${(err as Error).message}`);
  }

  // 7b. Auto-provision org/team default schedules
  let tasks = refreshData.scheduled_tasks ?? [];
  const existingTemplateIds = new Set(tasks.map((t) => t.template_id));

  // ENG-5662: the kanban-work retirement (hybrid is the SOLE path) applies only
  // to PERSISTENT claude-code agents — that's where the in-session `/loop` and
  // the `claude -p kanban-work` cron lived, and where the manager-gated hybrid
  // edge-trigger (step 7e, gated on persistent claude-code) replaces them.
  // Scoping to persistent is load-bearing: a claude-code *oneshot* agent has no
  // hybrid path, so it must keep firing kanban-work via syncAndCheckClaudeScheduler.
  const kanbanWorkRetired =
    (agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK) === 'claude-code' &&
    agentSessionMode === 'persistent';

  // ENG-9585 (slice 2): `beginFetch` BEFORE the gate check — see the secrets
  // fetch above for why the token has to predate the request.
  const defaultSchedulesToken = defaultSchedulesFetchGate.beginFetch(agent.agent_id);
  if (defaultSchedulesFetchGate.shouldFetch(agent.agent_id)) {
    try {
      const defaultsData = await api.post<{
        schedules: Array<{
          template_id: string;
          name: string;
          schedule_expr: string;
          prompt: string;
          session_target?: string;
          enabled?: boolean;
        }>;
        team_id: string;
        timezone: string;
      }>('/host/resolve-default-schedules', { agent_id: agent.agent_id });

      // ENG-5662: for claude-code agents, never (re)provision a kanban-work
      // default schedule — the hybrid edge-trigger drives it, not a scheduled
      // task / claude -p cron.
      const allSchedules = (defaultsData.schedules ?? []).filter(
        (s) => !(kanbanWorkRetired && s.template_id === 'kanban-work'),
      );
      const missing = allSchedules.filter(
        (s) => !existingTemplateIds.has(s.template_id),
      );

      // Always call ensure-default-schedules to insert missing tasks and
      // fix delivery_mode on existing tasks that don't match the template default.
      if (allSchedules.length > 0) {
        await api.post('/host/ensure-default-schedules', {
          agent_id: agent.agent_id,
          team_id: defaultsData.team_id,
          timezone: defaultsData.timezone,
          schedules: allSchedules,
        });
        if (missing.length > 0) {
          log(`Auto-provisioned ${missing.length} default schedule(s) for '${agent.code_name}': ${missing.map((s) => s.template_id).join(', ')}`);
        }

        // Re-fetch tasks since schedules may have changed
        // ENG-9585: this re-fetch reads `scheduled_tasks` and nothing else, so
        // it asks for the manifest too — a full second copy of every knowledge
        // body would be pure waste. (Re-measured 2026-09-04: this branch still
        // does not fire on the fleet — `/host/ensure-default-schedules` is
        // absent from the access log for the whole day, while the same query
        // resolves `/host/schedules/delete` at 1 request, so the zero is a real
        // zero and not a filter that quietly stopped matching. That is a
        // property of today's team defaults, not a guarantee — re-measure
        // before relying on it again.)
        //
        // The 1:1-with-`/host/refresh` reading this comment used to carry was
        // true on 2026-08-30 and is not any more — PR #5211 gave `/host/refresh`
        // a fetch gate and left this endpoint on the poll cadence, so by
        // 2026-09-04 they had split 257,611 to ~100,000 (2.6:1). Gating this
        // endpoint, just below, is what puts them back on a common cadence.
        // Do not restore the 1:1 claim as a way to reason about either.
        const freshData = await api.post<{ scheduled_tasks?: typeof tasks }>('/host/refresh', {
          agent_id: agent.agent_id,
          // Only `scheduled_tasks` is read here, so ask for the manifest shape on
          // every lane — nothing in this branch consumes a skill body, and the
          // skill lanes are the largest part of the payload being discarded.
          ...REFRESH_MANIFEST_FLAGS,
        });
        tasks = freshData.scheduled_tasks ?? tasks;
      }

      // LAST statement in the `try`. A throw above — including one from the
      // `/host/ensure-default-schedules` or `/host/refresh` calls inside the
      // branch — leaves the gate open so the next pass retries at once.
      defaultSchedulesFetchGate.markFetched(agent.agent_id, defaultSchedulesToken);
    } catch (err) {
      log(`Default schedule provisioning failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // ENG-5662: for claude-code agents, kanban work is now driven solely by the
  // manager-gated hybrid edge-trigger injection (step 7e). Drop any residual
  // `kanban-work` rows (legacy data / stale team defaults) so neither the
  // persistent-session scheduler nor syncAndCheckClaudeScheduler ever fires it
  // as a claude -p oneshot.
  if (kanbanWorkRetired) {
    tasks = tasks.filter((t) => t.template_id !== 'kanban-work');
  }

  // 7c. Skill provisioning for active agents.
  //
  // ENG-10214: the kanban skill used to be installed here from
  // `getBuiltInSkillContent('kanban')`, which read the monorepo's top-level
  // `skills/` directory. That producer could not resolve on a host running the
  // published CLI — its two candidates were `<cwd>/skills/...` (the manager's
  // cwd is `~/.augmented`, and `~/.augmented/skills` does not exist) and a climb
  // to a directory outside `apps/cli`'s package root, which npm cannot ship.
  // Measured on prod 2026-09-08: present on 0 of 120 agents, silently, since the
  // call site landed in ENG-4134. It has been deleted rather than repaired, so
  // no future built-in skill can inherit the same dead path.
  //
  // Kanban now ships through the ADR-0023 global-skill tier, the lane that
  // already delivers `workflow-mode` (120/120 agents) and `dashboards`
  // (118/120): a `scope='global'` row seeded from the same `skills/kanban/
  // SKILL.md` by `packages/supabase/scripts/seed-global-skills.ts`, unioned into
  // every agent's `/host/refresh` payload by `loadGlobalSkills`, and written to
  // disk by the global-skill sync below — through this same
  // `installSkillFiles`, which was never the broken part.
  if (agent.status === 'active') {
    // Integration skills — deploy scope-filtered skills and clean up orphans
    if (frameworkAdapter.installSkillFiles) {
      const currentIntegrationSkillIds = new Set<string>();
      const installedIntegrationSkills: string[] = [];
      const { createHash } = await import('node:crypto');

      // ENG-4341: Index resolved integration contexts by slug for fast
      // lookup when rendering each skill. This map is small (one entry
      // per installed integration) and built once per refresh.
      // ENG-4629: prefer the new `integration_*` wire keys; fall back to
      // the legacy `plugin_*` shape so the CLI keeps working against
      // older API deploys mid-rollout. Each row also carries both
      // `integration_slug` and `plugin_slug` aliases.
      const refreshAny = refreshData as unknown as {
        integration_contexts?: Array<{ integration_slug?: string; plugin_slug?: string; values?: Record<string, unknown>; overrides?: string }>;
        plugin_contexts?: Array<{ plugin_slug?: string; integration_slug?: string; values?: Record<string, unknown>; overrides?: string }>;
        integration_skills?: unknown[];
        plugin_skills?: unknown[];
        // ENG-9585: present when this manager asked for the manifest. Only its
        // PRESENCE is read here — the bodies were resolved and normalised into
        // `integration_skills` back at the wire boundary. It is what
        // distinguishes "the lane resolved to nothing" from "the lane could not
        // be resolved", which are the same `undefined` without it.
        integration_skills_manifest?: unknown[];
        integration_install_hooks?: Array<{ integration_slug?: string; plugin_slug?: string; script: string }>;
        plugin_install_hooks?: Array<{ plugin_slug?: string; integration_slug?: string; script: string }>;
        integration_toolkits?: Array<{ integration_slug?: string; plugin_slug?: string; toolkits: string[] }>;
        plugin_toolkits?: Array<{ plugin_slug?: string; integration_slug?: string; toolkits: string[] }>;
        // ADR-0023 / ENG-6349: published global skills delivered to every agent.
        global_skills?: Array<{ code_name: string; display_name?: string; content: string }>;
        // ENG-6815: published standalone agent/team/org skills, delivered via the
        // visibility chain (most-specific wins per code_name, resolved server-side).
        // Same shape + same delivery mechanism as global_skills.
        shared_skills?: Array<{ code_name: string; display_name?: string; content: string; scope?: string }>;
        // ENG-7972: the agt-runtime isolation image the control plane wants this
        // fleet on (a digest-pinned ECR URI). Set only when the deploy pushed an
        // image; the manager pulls + retags it for Docker-isolated hosts.
        desired_runtime_image?: string;
      };
      const contexts = refreshAny.integration_contexts ?? refreshAny.plugin_contexts ?? [];
      const contextBySlug = new Map<string, { values: Record<string, unknown>; overrides: string }>();
      for (const ctx of contexts) {
        const slug = ctx.integration_slug ?? ctx.plugin_slug;
        if (!slug) continue;
        contextBySlug.set(slug, { values: ctx.values ?? {}, overrides: (ctx.overrides ?? '').trim() });
      }

      // ENG-4502: group scopes by plugin and write a single plugin folder per
      // plugin. Claude Code's skill discovery does not recurse, so each
      // plugin's scopes go side-by-side under `plugin-<slug>/scopes/*.md`
      // while `plugin-<slug>/SKILL.md` is the umbrella index Claude actually
      // sees. Previous per-scope top-level folders (`plugin-<slug>-<id>/`)
      // are garbage-collected below alongside the orphan sweep.
      // ENG-4629: prefer integration_skills, fall back to plugin_skills.
      //
      // ENG-9585: `null` means the manifest was requested and could NOT be
      // resolved this poll — a fetch failure, a hash mismatch, a body the
      // server would not return. It is not the same fact as `[]`, and the whole
      // lane depends on the difference: `[]` says "every integration was
      // uninstalled" and the sweep below acts on it by deleting the folders,
      // whereas `null` says "I do not know what should be installed", which is
      // only ever an instruction to touch nothing. Both the install loop (via
      // an empty group map) and the sweep stand down on it, TOGETHER — standing
      // down on only the loop would leave `currentIntegrationSkillIds` empty
      // and hand the sweep the very empty set this guard exists to reject.
      const integrationSkillsInput = resolveIntegrationSkillLane<IntegrationSkillInput>({
        integration_skills: refreshAny.integration_skills as IntegrationSkillInput[] | undefined,
        plugin_skills: refreshAny.plugin_skills as IntegrationSkillInput[] | undefined,
        integration_skills_manifest: refreshAny.integration_skills_manifest,
      });
      if (integrationSkillsInput === null) {
        log(
          `Integration skill manifest unresolved for '${agent.code_name}' — leaving integration skill folders untouched this poll`,
        );
      }
      const integrationGroups = groupSkillsByIntegration(integrationSkillsInput ?? []);
      for (const [integrationSlug, scopes] of integrationGroups) {
        try {
          // ENG-4554: write under `integration-<slug>/` to align with the
          // post-cutover vocabulary. The orphan-sweep below recognises both
          // prefixes for one release so existing `plugin-<slug>/` folders
          // get cleaned up the first time the new manager runs.
          const integrationSkillId = `integration-${integrationSlug}`.replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
          currentIntegrationSkillIds.add(integrationSkillId);

          // ENG-4341: substitute {{context.<field>}} placeholders and
          // append the freeform overrides as "## Team Overrides".
          const ctx = contextBySlug.get(integrationSlug);
          const renderedScopes: IntegrationSkillInput[] = scopes.map((s) => ({
            plugin_slug: s.plugin_slug,
            skill_id: s.skill_id,
            skill_name: s.skill_name,
            content: renderIntegrationSkillContent(
              s.content,
              ctx?.values ?? {},
              ctx?.overrides ?? '',
              (warning) => log(`[plugin-context] ${s.plugin_slug}/${s.skill_id}: ${warning}`),
            ),
          }));

          const bundle = buildIntegrationBundle(renderedScopes);

          // Hash the whole bundle so any scope addition / removal / content
          // tweak triggers a re-install. Unchanged bundles skip disk I/O.
          const contentHash = createHash('sha256').update(bundleFingerprint(bundle.files)).digest('hex').slice(0, 12);
          // ENG-8441: key shape + skip/remember/forget live in one module so the
          // installer and the orphan sweep below cannot drift apart again.
          if (
            !shouldWriteIntegrationSkill(
              agentState.knownSkillHashes,
              agent.agent_id,
              integrationSkillId,
              contentHash,
            )
          ) {
            continue;
          }

          frameworkAdapter.installSkillFiles(agent.code_name, integrationSkillId, bundle.files);
          rememberIntegrationSkill(
            agentState.knownSkillHashes,
            agent.agent_id,
            integrationSkillId,
            contentHash,
          );
          for (const s of scopes) installedIntegrationSkills.push(s.skill_name);
          log(`Installed integration skill bundle '${integrationSkillId}' for '${agent.code_name}' (${scopes.length} scope(s))`);
        } catch (err) {
          log(`Integration skill install failed for '${agent.code_name}' / '${integrationSlug}': ${(err as Error).message}`);
        }
      }

      // Clean up orphaned plugin skills (removed plugins) and sweep the
      // pre-ENG-4502 flat layout (`plugin-<slug>-<skill_id>/`). After the
      // switch to one folder per plugin, those per-scope folders become
      // stale duplicates — leaving them confuses Claude's skill list.
      try {
        const { readdirSync, rmSync } = await import('node:fs');
        const { homedir } = await import('node:os');

        // All on-disk directories that installSkillFiles() may have
        // written into. installSkillFiles for Claude Code writes to the
        // agent dir's `skills/` AND the project dir's `.claude/skills/`.
        // The provision/.claude/skills path isn't an install target but is
        // swept defensively in case older manager versions stashed copies
        // there.
        const frameworkId = frameworkAdapter.id;
        const candidateSkillDirs: string[] = [
          // Claude Code — framework runtime tree
          join(homedir(), '.augmented', agent.code_name, 'skills'),
          // Claude Code — project tree
          join(homedir(), '.augmented', agent.code_name, 'project', '.claude', 'skills'),
          // Defensive: legacy provision-side path, not currently an
          // install target but cheap to sweep.
          join(agentDir, '.claude', 'skills'),
        ];

        const existingDirs = candidateSkillDirs.filter((d) => existsSync(d));

        // Union of platform-managed entries across every existing skill
        // dir — gives a complete view of what might need cleaning even if
        // the agent has since switched framework or only some paths got
        // written during the transition.
        //
        // ENG-4554: accept BOTH the legacy `plugin-` and the current
        // `integration-` prefix. Old `plugin-<slug>/` folders aren't in
        // currentIntegrationSkillIds (which now holds `integration-<slug>`),
        // so they fall through to the orphan branch below and get removed
        // — that's the migration. The two-prefix logic stays for one
        // release; once everyone's on a manager that writes
        // `integration-`, the `plugin-` half can drop.
        const discoveredEntries = new Set<string>();
        for (const dir of existingDirs) {
          try {
            for (const entry of readdirSync(dir)) {
              if (entry.startsWith('plugin-') || entry.startsWith('integration-')) {
                discoveredEntries.add(entry);
              }
            }
          } catch { /* non-fatal per dir */ }
        }

        // ENG-8441: the install above is guarded by `knownSkillHashes`, which
        // records "what I last wrote to disk". Deleting the folder without
        // clearing that entry leaves the cache asserting a folder that is no
        // longer there — so when the integration comes back, the install loop
        // recomputes the SAME bundle hash, matches the stale entry, `continue`s,
        // and never rewrites it.
        //
        // That state is STABLE, not transient: the entry is back in
        // `currentIntegrationSkillIds` so this sweep won't delete it again, and
        // the hash guard means it is never re-created either. The two mechanisms
        // deadlock and the skill stays missing for the rest of the manager
        // process (the cache is in-memory — only a restart clears it).
        //
        // Observed in prod (scout / agt-aws-1, 2026-08-04): `integration-vercel`
        // was swept while its integration was mid-transition between quarantined
        // and re-enabled, came back one poll later, and never returned to disk.
        // Silent, too — the console renders the control plane's view, so the
        // agent's card still listed a skill the host no longer had.
        //
        // The global-skill prune below already maintains this invariant
        // (`knownSkillHashes.delete('global-skill:…')` per `plan.removes`); this
        // is the same thing for the integration path.
        //
        // `removeIntegrationSkillFolder` clears the record BEFORE the first
        // delete — see its doc comment. This whole block is one try/catch, so
        // forgetting last would let a throw on the SECOND directory unwind past
        // the forget, leaving one install target missing and the cache still
        // claiming a folder is there (CodeRabbit, PR #4058).
        const removeSkillFolder = (entry: string, reason: string): void => {
          removeIntegrationSkillFolder({
            cache: agentState.knownSkillHashes,
            agentId: agent.agent_id,
            entry,
            dirs: existingDirs,
            removeDir: (p) => {
              if (existsSync(p)) {
                rmSync(p, { recursive: true, force: true });
              }
            },
          });
          log(`Removed ${reason} '${entry}' for '${agent.code_name}' (framework=${frameworkId})`);
        };

        // An entry that isn't in currentIntegrationSkillIds is one of:
        //   - a removed integration (uninstalled)
        //   - a stale pre-ENG-4502 per-scope folder (`plugin-<slug>-<skill_id>`)
        //   - an ENG-4554 legacy `plugin-<slug>/` superseded by the new
        //     `integration-<slug>/` written this poll
        // All three are right to delete.
        //
        // ENG-9585: unless the desired set is UNKNOWN, in which case nothing
        // here is orphaned — it is merely unlisted. The discovery above still
        // runs unconditionally (it is a readdir, and keeping both paths reading
        // the same keeps the sweep's input honest); only the deletion stands
        // down. `planIntegrationSkillSweep` owns that rule so it is testable.
        for (const entry of planIntegrationSkillSweep(
          discoveredEntries,
          currentIntegrationSkillIds,
          integrationSkillsInput !== null,
        )) {
          removeSkillFolder(entry, 'orphaned skill folder');
        }
      } catch (err) {
        log(`Integration skill cleanup failed for '${agent.code_name}': ${(err as Error).message}`);
      }

      // ADR-0023 / ENG-6349: global skills — delivered to EVERY agent
      // install-free (no agent_skill_installs row). ENG-6815 folds in
      // `shared_skills` (published standalone agent/team/org skills resolved via
      // the visibility chain): same shape, same delivery, no code_name collision
      // with globals (precedence resolved server-side), so the union runs
      // through one plan. /host/refresh includes both; we write each as
      // `.claude/skills/<code_name>/SKILL.md` (bare code_name so the agent's
      // `/<code_name>` resolves), with the same content-hash skip as
      // integration skills. Runs BEFORE the skills-index refresh below so the
      // CLAUDE.md index picks them up. Best-effort — a failure here never
      // blocks the rest of the refresh.
      try {
        // ENG-7492: the desired (currently-assigned) set is only authoritative
        // when the refresh payload actually carried the skill fields. On a
        // modern API /host/refresh fails closed (a DB error → 5xx → the host
        // retries), so an empty array here means "nothing assigned" and is safe
        // to prune to. Both fields absent means an old API that never sends them
        // — the desired set is unknown, so we must NOT prune (guard below).
        const globalSkillsPayload = refreshAny.global_skills;
        const sharedSkillsPayload = refreshAny.shared_skills;
        const desiredResolved =
          globalSkillsPayload !== undefined || sharedSkillsPayload !== undefined;

        // Union the in-memory delivered set with the on-disk manifest so a
        // manager restart (which empties the in-memory set) still knows which
        // bare-named folders it planted and can reconcile them against the
        // desired set. This is the core of the ENG-7492 fix: without the
        // persisted manifest, an unassign that happened across a restart left
        // the folder orphaned forever.
        const manifestPath = managedSkillManifestPath(
          join(homedir(), '.augmented', agent.code_name),
        );
        const prevIds = new Set<string>([
          ...(agentState.knownGlobalSkillIds.get(agent.agent_id) ?? new Set<string>()),
          ...readManagedSkillManifest(manifestPath),
        ]);

        const plan = planGlobalSkillSync(
          [...(globalSkillsPayload ?? []), ...(sharedSkillsPayload ?? [])],
          prevIds,
          (content) => createHash('sha256').update(content).digest('hex').slice(0, 12),
          (skillId) => agentState.knownSkillHashes.get(`global-skill:${agent.agent_id}:${skillId}`),
          { desiredResolved },
        );

        for (const { skillId, content, hash } of plan.installs) {
          frameworkAdapter.installSkillFiles(agent.code_name, skillId, [{ relativePath: 'SKILL.md', content }]);
          agentState.knownSkillHashes.set(`global-skill:${agent.agent_id}:${skillId}`, hash);
          log(`Installed global skill '${skillId}' for '${agent.code_name}'`);
        }

        // Orphan sweep: remove the folder for any global/shared skill we
        // previously planted (tracked in memory OR the on-disk manifest) that is
        // no longer in the desired set. Only ids we recorded as global/shared
        // are touched — never the agent's own, integration, or user-created
        // skills. Each candidate is defensively confirmed to be a provisioned
        // skill (its folder contains a SKILL.md) before deletion, so a
        // code_name collision with a non-skill directory can never cause data
        // loss.
        if (plan.removes.length) {
          const globalSkillDirs = [
            join(homedir(), '.augmented', agent.code_name, 'skills'),
            join(homedir(), '.augmented', agent.code_name, 'project', '.claude', 'skills'),
            join(agentDir, '.claude', 'skills'),
          ];
          for (const id of plan.removes) {
            let prunedAny = false;
            for (const dir of globalSkillDirs) {
              const p = join(dir, id);
              // Scope guard: only delete a directory that looks like a
              // provisioned skill (has SKILL.md). Never rm an unexpected entry.
              if (existsSync(p) && existsSync(join(p, 'SKILL.md'))) {
                rmSync(p, { recursive: true, force: true });
                prunedAny = true;
              }
            }
            agentState.knownSkillHashes.delete(`global-skill:${agent.agent_id}:${id}`);
            if (prunedAny) {
              log(`Pruned unassigned skill '${id}' for '${agent.code_name}' (ENG-7492)`);
            }
          }
        }

        agentState.knownGlobalSkillIds.set(agent.agent_id, plan.currentIds);
        // ENG-7492: persist the delivered set so the sweep survives a manager
        // restart. Only rewrite when the desired set was authoritative —
        // otherwise keep the prior manifest intact so a later good refresh can
        // still reconcile against it.
        if (desiredResolved) {
          writeManagedSkillManifest(manifestPath, plan.currentIds);
        }
      } catch (err) {
        log(`Global skill install failed for '${agent.code_name}': ${(err as Error).message}`);
      }

      // Refresh the "Available Skills" index in CLAUDE.md by scanning installed skills
      try {
        const agentFwForIndex = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
        if (agentFwForIndex === 'claude-code') {
          await refreshSkillsIndexInClaudeMd(config.configDir, agent.code_name, log);
        }
      } catch (err) {
        log(`Skills index refresh failed for '${agent.code_name}': ${(err as Error).message}`);
      }

      // Run integration on_install hooks (idempotent — runs every refresh).
      // Hooks are deduplicated by content hash so we only re-run on script changes.
      // ENG-4629: prefer integration_install_hooks; fall back to plugin_install_hooks.
      const installHooks = refreshAny.integration_install_hooks ?? refreshAny.plugin_install_hooks ?? [];
      if (frameworkAdapter.executePluginHook && installHooks.length) {
        for (const hook of installHooks) {
          const slug = hook.integration_slug ?? hook.plugin_slug;
          if (!slug) continue;
          try {
            const scriptHash = createHash('sha256').update(hook.script).digest('hex').slice(0, 12);
            const hookKey = `${agent.agent_id}:${frameworkAdapter.id}:plugin-hook:${slug}:on_install`;
            if (agentState.knownSkillHashes.get(hookKey) === scriptHash) continue;

            const result = await frameworkAdapter.executePluginHook({
              codeName: agent.code_name,
              pluginSlug: slug,
              hookName: 'on_install',
              script: hook.script,
            });

            if (result.exitCode === 0) {
              agentState.knownSkillHashes.set(hookKey, scriptHash);
              log(`Integration hook on_install '${slug}' succeeded for '${agent.code_name}' (${result.durationMs}ms)`);
            } else if (result.timedOut) {
              log(`Integration hook on_install '${slug}' TIMED OUT for '${agent.code_name}' after ${result.durationMs}ms`);
            } else {
              // Don't log raw stderr — hooks inherit manager env which may include secrets.
              // Log a fingerprint instead so failures are identifiable without leaking content.
              const stderrHash = createHash('sha256').update(result.stderr).digest('hex').slice(0, 12);
              // ENG-4510: on exit 127, bash prints `bash: <cmd>: command not found`
              // before any user code runs. extractCommandNotFound parses + validates
              // that line, but we still log only a hash — the project guideline is
              // hash-only logging by default, and operators can correlate the hash
              // with `pnpm install <pkg>` failures or recompute it locally with a
              // suspected binary name when triaging.
              const missingCmd = result.exitCode === 127 ? extractCommandNotFound(result.stderr) : null;
              const missingCmdHash = missingCmd ? createHash('sha256').update(missingCmd).digest('hex').slice(0, 8) : null;
              log(
                `Integration hook on_install '${slug}' exited ${result.exitCode} for '${agent.code_name}' ` +
                (missingCmdHash ? `[missing_command_hash=${missingCmdHash}] ` : '') +
                `[stderr_hash=${stderrHash} stderr_len=${result.stderr.length}]`,
              );
            }
          } catch (err) {
            log(`Integration hook on_install failed for '${agent.code_name}' / '${slug}': ${(err as Error).message}`);
          }
        }
      }

      // ENG-4421: install CLIs for toolkits declared by the agent's plugins.
      // Union across plugins, dedup per process via toolkitCliEnsured. Runs
      // for claude-code hosts.
      const agentFwForToolkits = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
      // ENG-4629: prefer integration_toolkits; fall back to plugin_toolkits.
      const toolkitsList = refreshAny.integration_toolkits ?? refreshAny.plugin_toolkits ?? [];
      if (agentFwForToolkits === 'claude-code' && toolkitsList.length) {
        const toolkitUnion = new Set<string>();
        for (const { toolkits } of toolkitsList) {
          for (const t of toolkits) toolkitUnion.add(t);
        }
        for (const toolkitSlug of toolkitUnion) {
          await ensureToolkitCli(toolkitSlug);
        }
      }

      // ENG-7972: pull the control-plane-designated agt-runtime image for
      // Docker-isolated hosts. Host-global, so per-URI dedup makes this a no-op
      // for every agent after the first triggers the pull; only a version bump
      // (new digest URI) re-pulls. Gated on isolation actually being on for this
      // agent so a non-Docker host never pulls. Fire-and-forget: the pull can
      // run for up to 600s, and awaiting it here would delay an otherwise-viable
      // agent's spawn on a slow/unavailable registry (not fail-open). Scheduling
      // it independently lets provisioning continue; a later respawn consumes the
      // refreshed tag. ensureIsolationImage swallows its own failures, so the
      // .catch is only a backstop for an unexpected throw.
      const desiredRuntimeImage = refreshAny.desired_runtime_image;
      if (desiredRuntimeImage && isolationMode(agent.code_name) !== 'none') {
        void ensureIsolationImage(desiredRuntimeImage).catch((err) => {
          log(`[isolation-image] unexpected managed-pull failure: ${isolationImageErrDetail(err)}`);
        });
      }

      // Hot-reload: notify running Claude Code session about new integration skills
      const agentFw = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
      if (agentFw === 'claude-code' && installedIntegrationSkills.length > 0 && isSessionHealthy(agent.code_name)) {
        const names = installedIntegrationSkills.join(', ');
        injectMessage(agent.code_name, 'system',
          `New integration skills installed: ${names}. These are available immediately — Claude Code loads skills on demand from .claude/skills/.`,
          { task_name: 'plugin-skill-update' }, log).catch(() => {});
        log(`[hot-reload] Notified '${agent.code_name}' about new integration skills: ${names}`);
      }
    }
  }

  // ENG-9585: ONE board fetch per tick, shared by 7d, 7e and 12. Section 12
  // fetched unconditionally every tick, moments after 7e had fetched the same
  // board for the same agent — two full `select('*')` payloads per agent per
  // 10s poll. Only a SUCCESSFUL fetch is shared; a rejection is re-attempted by
  // the next caller, because the three sites have different (and incompatible)
  // failure policies. Full reasoning in `kanban-board-fetch.ts`.
  const fetchTickBoard = createTickBoardFetcher(async () => {
    // ENG-9584: the conditional request. `known_token` is the token the server
    // returned for the board we already hold; when the board still hashes the
    // same the reply is `{ unchanged: true }` and no rows cross the wire.
    //
    // Sent only when we actually hold a board. An omitted token is the
    // pre-ENG-9584 request verbatim, which is what a first tick, a re-added
    // agent, and a manager talking to an API predating the feature all need.
    const knownToken = boardTokenCache.tokenFor(agent.agent_id);
    const post = (token: string | undefined) =>
      api.post<BoardResponse<BoardItem>>('/host/my-kanban', {
        agent_id: agent.agent_id,
        // ENG-9585: this is the ONLY caller that polls (every ~10s, per agent),
        // and it reads exactly `BoardItem`. `description` and `notes` are 57.8%
        // of the payload and are read here by nothing — but the `kanban_list` MCP
        // tool and `agt kanban` DO read them, so the route cannot simply drop
        // them. Asking for the lean shape is this caller saying what it needs;
        // every other caller keeps the full payload by default.
        detail: 'board',
        ...(token ? { known_token: token } : {}),
      });

    let rows = boardTokenCache.resolve(agent.agent_id, await post(knownToken));
    if (rows === null) {
      // The server said "unchanged" against a board we no longer hold — only
      // reachable if teardown cleared the cache between building the request and
      // reading the reply. Re-ask WITHOUT a token rather than serving an empty
      // board: section 12 writes whatever it is handed into `notifyBoardCache`
      // as this tick's truth, so an empty one would make the NEXT tick report
      // every done card as newly done, notifying real users about work they had
      // already been told about.
      rows = boardTokenCache.resolve(agent.agent_id, await post(undefined)) ?? [];
    }
    return rows.map(sanitizeBoardItem);
  });

  // 7d. Fetch kanban board for prompt injection into board-aware templates
  let boardItems: BoardItem[] = [];
  // ENG-7958: did THIS tick's fetch actually reach the API? On a transport
  // failure we fall back to the cached snapshot, which is STALE. The hybrid-
  // inject block below reuses this boardItems (when hasBoardTemplates) WITHOUT
  // re-fetching, so it must know the reused board is stale - otherwise a drained
  // board keeps firing kanban_list off long-done cached cards ("Maven has
  // nothing on her Kanban yet we're still injecting"). The ENG-7557 stale guard
  // only covered the hybrid block's OWN fetch failure, not this reused one.
  let boardItemsStale = false;
  const hasBoardTemplates = tasks.some((t) => BOARD_INJECT_TEMPLATES.has(t.template_id));
  if (hasBoardTemplates) {
    try {
      boardItems = await fetchTickBoard();
      kanbanBoardCache.set(agent.code_name, boardItems);
    } catch {
      // Non-fatal — board may not exist yet
      boardItems = kanbanBoardCache.get(agent.code_name) ?? [];
      boardItemsStale = true;
    }
  }

  // Resolve framework once for sections 8-11
  const agentFw = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;

  // 8. Sync scheduled tasks
  const sessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string ?? 'oneshot';

  // 7e. ENG-5435 hybrid kanban-work + ENG-5599/5600/5601 scheduled-task
  // reconcile & delivery. Both need a fresh board snapshot for a claude-code
  // persistent agent (oneshot agents have no live REPL), so they share one
  // fetch — but they're gated by SEPARATE flags and must not be coupled:
  //   - reconcile + delivery (ENG-5601) runs whenever scheduled-via-kanban is
  //     on. It's the PRIMARY delivery trigger; gating it behind the hybrid flag
  //     would silently drop every scheduled-task delivery when an operator
  //     enables AGT_SCHEDULED_VIA_KANBAN alone. (Harmless no-op when nothing is
  //     tracked in scheduledRunsByCode.)
  //   - the hybrid kanban-work inject (ENG-5435) runs only when the hybrid flag
  //     is on. Uses the tighter HYBRID_ACTIONABLE_STATUSES predicate
  //     (todo / in_progress — NOT backlog) per
  //     docs/research/eng-5435-hybrid-kanban-inject.md.
  const wantsHybridInject = isKanbanHybridEnabled();
  // ENG-6849: scheduled-task reconcile/delivery via the kanban route is now
  // unconditional - the AGT_SCHEDULED_VIA_KANBAN opt-out is retired with the
  // claude -p oneshot. Reconcile runs for every persistent claude-code agent;
  // the separate (always-on) hybrid-inject flag gates only the kanban-work nudge
  // below (the inner `if (wantsHybridInject)`), not scheduled-task delivery.
  if (agentFw === 'claude-code' && sessionMode === 'persistent') {
    // CR (CodeRabbit on PR #1293): the kanbanBoardCache is process-wide
    // and persists across poll ticks. The previous `!hybridBoard` short
    // circuit meant that once we populated the cache, we'd read stale
    // data forever for agents without a board-aware scheduled template
    // (step 7d only fetches when `hasBoardTemplates`). Reuse step 7d's
    // freshly-fetched board when it ran this tick; otherwise fetch our
    // own and update the cache. Falls back to whatever cached snapshot
    // exists on transport failure rather than treating the agent as
    // having an empty board.
    let hybridBoard = hasBoardTemplates ? boardItems : undefined;
    // ENG-7557: when the fetch below fails and we fall back to the cached
    // snapshot, the board state is STALE - good enough for reconcile (it
    // re-checks per card), but not for the nudge decision: enqueueing a nudge
    // off a stale snapshot is exactly the "nudge with nothing actionable"
    // failure this flag exists to prevent, so the inject/cancel block skips
    // its work for the tick.
    // ENG-7958: seed staleness from step 7d. When hasBoardTemplates is true we
    // reuse 7d's boardItems above without re-fetching, so if that fetch fell
    // back to the cached snapshot the reused board is stale too. Without this
    // seed the `if (!hybridBoard)` block below never runs for board-template
    // agents (boardItems is a non-null array), hybridBoardStale stayed false,
    // and a drained board kept nudging off long-done cached cards.
    let hybridBoardStale = boardItemsStale;
    if (!hybridBoard) {
      try {
        hybridBoard = await fetchTickBoard();
        kanbanBoardCache.set(agent.code_name, hybridBoard);
      } catch {
        hybridBoard = kanbanBoardCache.get(agent.code_name) ?? [];
        hybridBoardStale = true;
      }
    }
    // ENG-5600/5601: reconcile in-flight scheduled-task runs against this fresh
    // board snapshot — deliver the agent's result from the done card and close
    // the per-card run. Done here because we already have a current board and
    // the agent self-completing its card means the realtime onCompletion path
    // is suppressed by isSelfCompletion (so this poll is the primary trigger).
    // Fire-and-forget: it awaits delivery per card internally but the poll loop
    // doesn't block on it.
    void reconcileScheduledRuns(agent.code_name, agent.agent_id, hybridBoard);
    if (wantsHybridInject) {
      // ENG-6171: reset poison-card accounting against this fresh board — drop
      // any tracked card that has left in_progress or cooled down (AC: counter
      // resets), then suppress cards still flagged as suspected poison from the
      // nudge so a deterministically-wedging card stops driving the respawn loop
      // and falls to the 30-min stale-auto-fail instead.
      const poisonCfg = resolvePoisonCardConfig();
      const existingStates = wedgeRestartsByCard.get(agent.code_name);
      if (existingStates && existingStates.size > 0) {
        const liveInProgress = hybridBoard
          .filter((item) => item.status === 'in_progress' && isHybridActionable(item))
          .map((item) => item.id);
        const pruned = pruneCardStates(existingStates, liveInProgress, Date.now(), poisonCfg);
        if (pruned.size > 0) wedgeRestartsByCard.set(agent.code_name, pruned);
        else wedgeRestartsByCard.delete(agent.code_name);
      }
      const cardStates = wedgeRestartsByCard.get(agent.code_name);
      const actionable = hybridBoard.filter(isHybridActionable);
      const { allowed, suppressed } = cardStates
        ? partitionActionableByPoison(actionable, cardStates, poisonCfg)
        : { allowed: actionable, suppressed: [] as BoardItem[] };
      // ENG-7958: one decision point for the enqueue gate. Pure + unit-tested
      // (resolveHybridNudgeAction) so the stale-wins-over-actionable ordering
      // can't silently regress inside this ~9000-line function.
      const nudgeAction = resolveHybridNudgeAction(
        hybridBoardStale,
        allowed.length,
        suppressed.length,
      );
      if (nudgeAction === 'skip-stale') {
        // ENG-7557: the board fetch failed and hybridBoard is a cached
        // snapshot. Don't nudge (a stale snapshot's "actionable" items may be
        // long done) and don't treat an apparent drain as real (the cached
        // empty board may be missing fresh cards). Next tick re-fetches.
        if (allowed.length > 0) {
          log(
            `[manager-worker] kanban inject skipped for '${agent.code_name}' - board fetch failed this tick (stale cache)`,
          );
        }
      } else if (nudgeAction === 'nudge') {
        // ENG-8197: don't tick an agent that is at its Claude usage cap.
        //
        // A capped agent's turn fails in ~1s with the limit banner, so the tick
        // cannot be acted on — it just burns a turn, every cycle, for the whole
        // window. Unlike the scheduled-task path there is nothing to lose here
        // (the cards stay on the board and the next tick re-evaluates), so this
        // is purely stopping the manager shouting at an agent that cannot hear.
        //
        // Fails OPEN: an unclassifiable transcript, or a cap with no parseable
        // reset instant, resolves to null and the tick fires as before. Logged
        // once per capped tick rather than silently, so "why did this agent stop
        // being nudged" is answerable from manager.log.
        //
        // ENG-8208: reads the agent's transcript rather than the deleted
        // pane-scraped marker. Same gate, trustworthy input.
        const tickLimitedUntil = readUsageCapUntil({
          codeName: agent.code_name,
          projectDir: psGetProjectDir(agent.code_name),
        });
        if (tickLimitedUntil) {
          // Edge-triggered: this branch runs every poll (~10s) for the whole cap
          // window, so an unconditional log would repeat thousands of times per
          // agent. Same reason ENG-5080 silenced the per-agent banner above.
          if (shouldLogUsageCapDeferral('kanban-tick', agent.code_name, tickLimitedUntil)) {
            log(
              `[manager-worker] kanban inject skipped for '${agent.code_name}' - ` +
                `agent is at its Claude usage limit until ${tickLimitedUntil.toISOString()} ` +
                `(${allowed.length} actionable card(s) left on the board); ` +
                `further skips this window are not logged`,
            );
          }
        } else {
          // Fire-and-forget per design-doc Q2 — the 10s waitForPromptReady
          // cap would otherwise serialise across N agents and starve the
          // poll cycle. Caller doesn't await. Count excludes suppressed poison
          // cards so the nudge advertises only the cards we want picked up.
          // ENG-7704: pass the actionable signature so the nudge backs off the
          // fast cadence while this exact set is unchanged (nothing new to do).
          void maybeInjectKanbanCheck(
            agent.code_name,
            agent.agent_id,
            allowed.length,
            kanbanActionableSignature(allowed),
          );
        }
      } else if (nudgeAction === 'close-suppressed') {
        // Every actionable card is a suspected poison task (ENG-6171) — close
        // any open injected run so it doesn't absorb later non-kanban turns;
        // the suppression itself was logged once at attribution time, so no
        // per-poll spam here.
        closeInjectedRunIfOpen(
          agent.code_name,
          'completed',
          'kanban inject suppressed — suspected poison card(s) (ENG-6171)',
        );
      } else {
        // Board drained (fresh snapshot, nothing actionable or suppressed).
        // ENG-7704/ENG-7798: always forget the last-nudged signature — a card
        // that re-appears later must nudge promptly at the fast cadence, not
        // the escalated one. Unconditional (CodeRabbit PR #3441): after a
        // manager restart the hydrated persisted state can outlive a board
        // this process never nudged (openInjectedRunByCode is empty), and a
        // delivered nudge without a run id has state but no open run — gating
        // the clear on the open run left the escalated cadence stuck in both
        // cases. clearKanbanNudgeState only writes when an entry existed, so
        // steady empty boards don't rewrite the sidecar every tick.
        clearKanbanNudgeState(agent.code_name);
        if (openInjectedRunByCode.has(agent.code_name)) {
          // Board drained with a nudge cycle still open. ENG-7557: the last
          // enqueued notice may still be PENDING (never consumed - the agent
          // finished the board off its own turns), in which case delivering it
          // now would only burn a turn on an empty board. Cancel it server-side
          // first, then close the open injected run (ENG-5565) with an outcome
          // that records whether the nudge ever reached the agent. The map entry
          // is drained synchronously (same contract as closeInjectedRunIfOpen)
          // so a concurrent tick can't double-close.
          const openRunId = openInjectedRunByCode.get(agent.code_name)!;
          openInjectedRunByCode.delete(agent.code_name);
          void cancelKanbanNoticeOnDrain(agent.code_name, agent.agent_id, openRunId);
        }
      }
    }
  }

  if (
    (agentFw === 'claude-code' && sessionMode === 'persistent') ||
    agentFw === 'opencode'
  ) {
    // Claude Code persistent mode: manage long-running session with channels.
    // ADR-0047: opencode agents route through this same entry point regardless
    // of sessionMode. ensurePersistentSession early-returns to
    // ensureOpencodeRuntime for them (the spawn/supervise seam that boots
    // `opencode serve`). Without this branch the seam is never reached on a
    // live host - the agent is provisioned but the server is never spawned.
    //
    // ENG-5116: wrap the per-agent call in try/catch so an exception
    // affecting one agent's evaluation can't silently take that agent
    // down. Pre-fix, a throw inside ensurePersistentSession bypassed
    // every log statement and the operator only learned about it when
    // a user reported the agent unresponsive in Slack (Vigil, 2026-05-16).
    // Snapshot health *before* the call so an exception thrown after the
    // session is already healthy doesn't get misreported as down — that
    // would start a bogus stuck streak for an agent still serving traffic
    // (CodeRabbit PR #1070).
    const wasSessionHealthy = isSessionHealthy(agent.code_name);
    let psResult: PersistentSessionResult;
    try {
      psResult = await ensurePersistentSession(agent, tasks, boardItems, refreshData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      log(`[persistent-session] EXCEPTION evaluating '${agent.code_name}': ${msg}`);
      // Construct a synthetic result so the decision-summary + stuck
      // tracker still see this tick. Carry forward the pre-call health
      // observation — the throw doesn't kill the tmux session, only our
      // evaluation of it.
      psResult = {
        decision: 'skipped-auth-resolve-failed',
        spawnAttempted: false,
        sessionHealthyAfter: wasSessionHealthy,
        detail: `exception: ${msg}`,
      };
    }

    // One structured decision-summary line per agent per tick. This is
    // the line that would have shown Vigil silently stuck — pre-fix only
    // the happy path logged. `detail` is JSON-stringified so embedded
    // quotes/newlines from exception messages can't break single-line
    // parsing downstream (CodeRabbit PR #1070).
    const normalizedDetail = psResult.detail?.replace(/\s+/g, ' ').trim();
    const detailSuffix = normalizedDetail
      ? ` detail=${JSON.stringify(normalizedDetail)}`
      : '';
    log(
      `[persistent-session-decision] agent=${agent.code_name} decision=${psResult.decision} ` +
        `spawn_attempted=${psResult.spawnAttempted} session_healthy_after=${psResult.sessionHealthyAfter}${detailSuffix}`,
    );

    // ENG-8581: record the outcome shape for bind-remediation's auth-skip gate.
    // Same inputs the stuck tracker already consumes, kept in a separate map so
    // the tracker's own semantics are untouched.
    // ENG-7188: through the shared recorder, so this path and both fast respawn
    // lanes publish the same fact. The decision string rides along for DISPLAY
    // only — nothing acquires a list of decision names to branch on.
    recordSpawnOutcome(agent.code_name, psResult);

    const stuck = persistentSessionStuckTracker.record({
      codeName: agent.code_name,
      sessionHealthy: psResult.sessionHealthyAfter,
      spawnAttempted: psResult.spawnAttempted,
    });

    if (stuck.shouldWarn) {
      log(
        `[persistent-session-stuck] WARN: agent=${agent.code_name} unhealthy with no spawn attempt for ${stuck.consecutiveTicks} consecutive ticks ` +
          `(last_decision=${psResult.decision}) — manager-worker may need restart, or upstream guard is mis-firing (see ENG-5116)`,
      );
    } else if (stuck.recovered) {
      log(`[persistent-session-stuck] agent=${agent.code_name} recovered from stuck state`);
    }

    // ENG-9614: the inverse shape - we DID respawn, every tick, and it is not
    // sticking. The WARN above cannot fire here by construction (attempting a
    // spawn is one of its reset conditions), which is why the scratch/tmp
    // collision looped unrecoverably for 3m48s without this file emitting a
    // single line. The failure signature is included because that is the whole
    // point: an operator reading this needs the cause, not just the fact.
    if (stuck.shouldWarnSpawnLoop) {
      const failure = getLastFailureContext(agent.code_name);
      log(
        `[persistent-session-spawn-loop] WARN: agent=${agent.code_name} respawned ${stuck.spawnLoopTicks} consecutive ticks ` +
          `and is still unhealthy (last_decision=${psResult.decision} signature=${failure.signature} restart_count=${failure.restartCount}) — ` +
          `the spawn is failing in a way a retry cannot fix; see the pane log tail for the cause (ENG-9614)`,
      );
    }

    // ADR-0047 (CodeRabbit): everything below to the end of this branch is
    // Claude-Code MCP supervision — structural .mcp.json drift, channel-secret
    // drift, and the MCP-presence reaper — all keyed on psGetProjectDir() +
    // in-process Claude MCP children. opencode has neither (ensureOpencodeRuntime
    // owns its own `opencode serve` supervision), and its different on-disk
    // layout would be misread here as MCP drift / missing servers and bounce a
    // healthy session. Run it ONLY for claude-code; ensurePersistentSession +
    // the decision-log/stuck-tracker above already cover opencode.
    if (agentFw === 'claude-code') {
    // ENG-4897: detect structural .mcp.json drift since the session was
    // launched and schedule a respawn. Runs only on persistent claude-code
    // agents — the only path that uses --strict-mcp-config and therefore
    // pins the MCP server set at startup. Other framework/mode combos
    // either re-read .mcp.json on every spawn or don't use it.
    try {
      checkMcpConfigDriftAndScheduleRestart(agent.code_name, psGetProjectDir(agent.code_name));
    } catch (err) {
      log(`[hot-reload] .mcp.json drift check failed for '${agent.code_name}': ${(err as Error).message}`);
    }
    // ENG-6062: detect a channel-token rotation since launch. The token now
    // lives only in `.env.integrations` (templated in `.mcp.json`), so the
    // `.mcp.json` drift check above is structurally blind to it — without this
    // a rotation silently no-ops (old, revoked token stays live) until a manual
    // session bounce. Same persistent-claude-code scope as the check above.
    //
    // CodeRabbit #1831: gate on channelConfigConverged (same guard the
    // channel-set restart path uses) — if a credential write/remove failed
    // this tick, `.env.integrations` is only partially written, so don't
    // respawn against it. The drift is sticky: a skipped tick re-detects it
    // once convergence is reached (baseline isn't advanced while skipped).
    // ENG-9252: and a deferred tick counts as not-converged here too — the
    // drift stays sticky and is re-detected once a retry lands.
    if (channelConfigConvergedThisTick) {
      try {
        // ENG-9350: in spawn-env mode hash the in-memory channel secrets (the
        // tokens are no longer on disk); in file mode pass undefined so the
        // check reads `.env.integrations` exactly as before.
        checkChannelSecretDriftAndScheduleRestart(
          agent.code_name,
          psGetProjectDir(agent.code_name),
          shouldInjectChannelSecrets(agent.agent_id)
            ? collectChannelSpawnSecrets(refreshData.channel_configs)
            : undefined,
        );
      } catch (err) {
        log(`[hot-reload] channel-secret drift check failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // ENG-5053: presence check — flag declared MCP servers that have
    // no live children under this agent's claude (e.g. xero-mcp-server
    // crashed silently) and kill the session so the next ensure pass
    // respawns it. Skipped within the cold-start grace window so the
    // initial 20-30s npm-install of @composio/mcp (still npx-delivered;
    // xero is now CLI-bundled and starts instantly, ENG-7579) doesn't
    // trip a false-positive restart.
    try {
      const sess = getSessionState(agent.code_name);
      let mcpJsonParsed: import('./stale-mcp-reaper.js').McpConfig | null = null;
      try {
        const mcpPath = join(psGetProjectDir(agent.code_name), '.mcp.json');
        mcpJsonParsed = JSON.parse(readFileSync(mcpPath, 'utf-8'));
      } catch {
        // Missing/unreadable .mcp.json — nothing to check. The drift
        // check above already logs unparseable configs; we silently
        // skip presence so we don't double-log on a transient read.
      }
      // ENG-10134: actively check the Mac's reverse tunnel and hand the reaper a
      // corrected view of this one entry. Two distinct corrections, both argued
      // in computer-use-tunnel.ts:
      //   • tunnel reachable → narrow the entry's args to the proxy invocation,
      //     so the shared argv-signature derivation identifies THIS server
      //     rather than the Mac's SSH username (ENG-10258);
      //   • tunnel unreachable → omit the key, so a Mac that is merely off costs
      //     no restarts and accrues no give-up budget it could never shed.
      // Returned unchanged when the agent has no computer-use install, so this
      // is a strict no-op for every other agent on the host.
      const computerUseIsolated = isolationMode(agent.code_name) === 'docker';
      const computerUseVerdict = computerUseTunnels.observe(
        agent.code_name,
        mcpJsonParsed?.mcpServers?.[COMPUTER_USE_MCP_KEY]?.args,
        computerUseIsolated,
        {
          log,
          onRecovered: (codeName) => {
            clearPresenceReaperStateForKeys(codeName, new Set([COMPUTER_USE_MCP_KEY]));
            log(
              `[computer-use-tunnel] '${codeName}': tunnel reachable again — cleared presence-reaper state for '${COMPUTER_USE_MCP_KEY}' so it gets a fresh restart budget (ENG-10134)`,
            );
          },
        },
      );
      reapMissingMcpSessions({
        log,
        codeName: agent.code_name,
        mcpJson: computerUseReaperView(mcpJsonParsed, computerUseVerdict),
        sessionStartedAt: sess?.startedAt ?? null,
        // ENG-6660: under Docker the MCP children live in the agent's container
        // PID namespace - probe inside it (docker exec ps) instead of the host,
        // or every declared MCP looks "missing" -> restart loop -> flapping.
        isolated: computerUseIsolated,
        // ENG-6486: a restart is already scheduled/deferred for this agent (e.g.
        // a freshly-added channel awaiting its convergence restart, deferred by
        // the maintenance window). The pending restart will spawn the declared-
        // but-not-yet-live MCP, so don't let the reaper re-fire + inflate the
        // McpReaperRestartsHourly metric into a false flapping alarm.
        restartPending: pendingSessionRestarts.has(agent.code_name),
        // ENG-8013 (storm-awareness): tell the reaper whether THIS session
        // generation was born from an external respawn (integration-change /
        // channel-set-change / day-rollover / wedge / operator restart, or a
        // storm) vs the reaper's own recovery restart. When external, a missing
        // channel MCP is churn collateral and must not accrue quarantine budget,
        // the durable fix for the ENG-8003 storm-quarantine incident. Absent map
        // entry (first boot) → false → count normally.
        currentGenerationExternallyStarted:
          lastRestartWasExternal.get(agent.code_name) ?? false,
        // ENG-5441 / ENG-5547: a mcp-presence-reaper restart counts against
        // the agent-wide breaker ONLY when several distinct MCP servers are
        // missing at once — a thrashing collection that rotates restarts of
        // the whole session, which the per-MCP cap can't see. A restart
        // driven by a SINGLE persistently-failing server is left to that
        // server's own ENG-5279 give-up cap (→ marked unhealthy via onGiveUp
        // → dropped from the next .mcp.json), so one bad integration is
        // isolated and the agent keeps running degraded instead of being
        // paused wholesale. reaperRestartBreakerReason() encodes that
        // single-vs-multi decision; undefined means "restart, don't count".
        //
        // ENG-6264: the breaker-count reason (above, undefined for a single dead
        // MCP) is decoupled from the GATE reason. Pre-6264 an undefined breaker
        // reason also made the restart non-gateable → 'bypass' → the session was
        // torn down mid-turn (the common single-MCP case interrupted busy
        // agents). Pass 'mcp-presence-reaper' as the gate reason so the restart
        // defers-until-idle, while breakerReason still governs whether it counts
        // against the breaker.
        //
        // ENG-7949: EXCEPT when at least one dead MCP (ctx.activeKeys, already
        // confirmed to have no live children) is a recognised CHANNEL server —
        // then the gate reason promotes to 'channel-mcp-down', which is
        // window-exempt (still defer-until-idle, never interrupts a live turn,
        // but skips the up-to-~14h off-peak window wait). A dead channel MCP
        // means the agent is unreachable on that channel right now; sitting
        // silently deferred until the window opens is a worse outcome than a
        // gated restart. A routine tool-MCP-only reaper restart (xero,
        // composio_*, unrecognised keys) keeps the original 'mcp-presence-
        // reaper' gate reason and still waits for the window as before.
        // ENG-9932: let the reaper see the pane before it spends a restart.
        //
        // Read lazily — the reaper calls this ONLY once it has already decided
        // to restart, so a healthy agent never pays for a capture. Same
        // invocation shape as the channel-input-watchdog's capturePane above,
        // including the 2s bound and the catch: an unreadable pane returns null
        // and the reaper falls straight through to the restart it would have
        // done anyway. Failing open matters more here than anywhere else in
        // this call — a capture that failed CLOSED would silently suspend MCP
        // recovery for every agent on the host.
        readPane: (codeName) => {
          try {
            return syncExecFile('tmux', ['capture-pane', '-t', `agt-${codeName}`, '-p'], {
              stdio: ['ignore', 'pipe', 'ignore'],
              timeout: 2_000,
            }).toString();
          } catch {
            return null;
          }
        },
        // ENG-9932: the held restart has to REACH someone. Routed through the
        // same `/host/reaper-event` path onGiveUp/onQuarantine already use, so
        // this adds no new alerting channel — it opens a critical
        // `reaper_action` alert, deduped per (agent, reaper), which the
        // reaper-alert-sweeper closes once the agent stops reporting blocked.
        //
        // This is the signal whose absence cost two hours on 2026-09-03: the
        // reaper is the only component that knows a restart is futile, so it is
        // the only one that can say so.
        onPaneBlocked: (codeName, ctx) => {
          // ENG-10335: the alert below reaches a human; this reaches the
          // manager's own consumers (health verdict, scheduled firing, doorbell),
          // which otherwise go on treating the agent as live.
          noteSessionBlockedOnPrompt(codeName, ctx.kind, 'mcp-presence-reaper');
          void reportReaperEvent(
            agent.agent_id,
            'mcp-presence-reaper',
            'blocked',
            `session blocked on an interactive prompt (${ctx.kind}); declared MCP(s) [${ctx.activeKeys.join(', ')}] were never spawned, not killed — a restart re-presents the same prompt. Needs a human: tmux attach -t agt-${codeName}` +
              // Fingerprint, never pane text. This string is persisted on the
              // alert row, so it leaves the host; the pane can hold the
              // customer message the agent was mid-turn on. Same policy the
              // channel-input-watchdog follows for this very block.
              (ctx.paneHash ? ` (modal fp=${ctx.paneHash} len=${ctx.paneLength})` : ''),
          );
        },
        stopSession: (codeName, ctx) =>
          stopPersistentSessionAndForgetMcpBaseline(
            codeName,
            reaperRestartBreakerReason(ctx.activeKeys),
            ctx.activeKeys.some(isKnownChannelKey) ? 'channel-mcp-down' : 'mcp-presence-reaper',
          ),
        // ENG-5292: when the reaper gives up on a managed MCP (cap from
        // ENG-5279 + state-preservation from ENG-5285 both said "this
        // MCP keeps failing after 3 restart cycles"), mark the matching
        // integration unhealthy server-side. The next /host/managed-
        // toolkits poll drops it from the response → next writeMcpServer
        // omits it → broken MCP is cleanly gone instead of declared-but-
        // dead.
        //
        // Async fire-and-forget — the reaper's correctness must not
        // depend on the API call succeeding. Errors are caught inside
        // the .then/.catch chain and logged.
        onGiveUp: (codeName, serverKey) => {
          const mapping = managedToolkitIdByAgentAndServerId.get(
            managedToolkitMapKey(agent.agent_id, serverKey),
          );
          if (!mapping) {
            log(
              `[mcp-presence-reaper] give-up on '${codeName}:${serverKey}' — not a managed toolkit (no toolkit_id mapping), skipping integration-health API call`,
            );
            return;
          }
          const { toolkitId, connectionKey } = mapping;
          const agentId = agent.agent_id;
          api.post('/host/integration-health', {
            agent_id: agentId,
            toolkit_id: toolkitId,
            // ENG-7543: scope the mark-unhealthy to the specific connection whose
            // MCP server gave up, so a dead 2nd mailbox doesn't disable the first.
            connection_key: connectionKey,
            action: 'mark_unhealthy',
            reason: `mcp-presence-reaper cap (${3} consecutive failed restart cycles) tripped on '${serverKey}'`,
          }).then((res) => {
            log(
              `[mcp-presence-reaper] marked integration unhealthy for '${codeName}' toolkit '${toolkitId}' (ENG-5292): ${JSON.stringify(res).slice(0, 200)}`,
            );
          }).catch((err) => {
            log(
              `[mcp-presence-reaper] failed to mark integration unhealthy for '${codeName}' toolkit '${toolkitId}' (ENG-5292): ${(err as Error).message} — local give-up state still recorded; will retry on next agent provisioning cycle if .mcp.json changes`,
            );
          });
        },
        // ENG-8225: a stale-mcp-reaper rotation's grace window expired with the
        // child still absent — nothing made the tool call ENG-5344 was banking on
        // (dwight's `anchor-browser`, dead from 16:00:05, never called again all
        // evening). Arm ENG-7429's prompt bind-probe budget so the next tool call
        // is MANUFACTURED, which is the only lever that exists: a stdio MCP child
        // belongs to Claude Code on the session's stdio pipes, so a
        // manager-spawned one would be invisible to findMcpChildrenForAgent's
        // ppid-to-`claude` walk AND unreachable by any tool call.
        //
        // Returns whether the probe was actually armed. Gated on the same
        // `session-tool-probe` flag as every other arming site (2014, 6780) — when
        // it's off, arming is inert, so we report false and the reaper falls back
        // to its pre-ENG-8225 restart rather than suppressing a restart with no
        // repair behind it.
        onRotationRespawnStalled: (codeName, serverKey, { attempt, maxAttempts }) => {
          if (!hostFlagStore().getBoolean('session-tool-probe')) {
            log(
              `[mcp-presence-reaper] '${codeName}:${serverKey}' rotation respawn stalled but the 'session-tool-probe' flag is off — cannot arm the ENG-7429 prompt probe; falling back to the session restart (ENG-8225)`,
            );
            return false;
          }
          promptBindProbeAttempts.set(codeName, PROMPT_BIND_PROBE_ATTEMPTS);
          log(
            `[mcp-presence-reaper] '${codeName}:${serverKey}' armed force-due prompt bind-probe budget (${PROMPT_BIND_PROBE_ATTEMPTS}) to force a tool call, attempt ${attempt}/${maxAttempts} (ENG-8225)`,
          );
          return true;
        },
        // ENG-8225 (AC3): the prompt-probe budget is spent and the rotated child
        // never came back. Escalate down the two paths this area already uses —
        // mark the integration unhealthy (`/host/integration-health`, the ENG-5292
        // onGiveUp path) and open a `reaper_action` alert (`/host/reaper-event`,
        // the ENG-6184 onRestart path). No new alerting channel.
        onRotationRespawnGaveUp: (codeName, serverKey, { attempts }) => {
          const reason = `rotation respawn never happened: stale-mcp-reaper SIGTERM'd '${serverKey}' and the child never returned after ${attempts} prompt bind-probe(s) (ENG-8225)`;
          const mapping = managedToolkitIdByAgentAndServerId.get(
            managedToolkitMapKey(agent.agent_id, serverKey),
          );
          if (mapping) {
            const { toolkitId, connectionKey } = mapping;
            api.post('/host/integration-health', {
              agent_id: agent.agent_id,
              toolkit_id: toolkitId,
              connection_key: connectionKey,
              action: 'mark_unhealthy',
              reason,
            }).then(() => {
              log(
                `[mcp-presence-reaper] marked integration unhealthy for '${codeName}' toolkit '${toolkitId}' after a never-respawned rotation (ENG-8225)`,
              );
            }).catch((err) => {
              log(
                `[mcp-presence-reaper] failed to mark integration unhealthy for '${codeName}' toolkit '${toolkitId}' after a never-respawned rotation (ENG-8225): ${(err as Error).message} — local escalation state still recorded`,
              );
            });
          } else {
            log(
              `[mcp-presence-reaper] never-respawned rotation on '${codeName}:${serverKey}' — not a managed toolkit (no toolkit_id mapping), skipping integration-health API call (ENG-8225)`,
            );
          }
          // Warning-severity `reaper_action` alert. 'kill' is the API's
          // conservative default action for anything that isn't a restart or a
          // quarantine, which is exactly what this is: a child was killed and
          // never came back.
          void reportReaperEvent(agent.agent_id, 'mcp-presence-reaper', 'kill', reason);
        },
        // ENG-5286: record the restart event with the API so the
        // agent-heartbeat-monitor cron can aggregate per-agent counts
        // into the McpReaperRestartsHourly CloudWatch metric. Fire-and-
        // forget — the reaper's correctness must not depend on this.
        onRestart: (codeName, { activeKeys, givenUpKeys }) => {
          api.post('/host/mcp-reaper-restart', {
            agent_id: agent.agent_id,
            code_name: codeName,
            server_keys: activeKeys,
            given_up_keys: givenUpKeys,
          }).catch((err) => {
            log(
              `[mcp-presence-reaper] failed to record restart event for '${codeName}' (ENG-5286): ${(err as Error).message} — local restart still proceeded; CloudWatch metric will under-count this event`,
            );
          });
          // ENG-6184: also open a paged `reaper_action` alert so a single
          // restart is visible without waiting for the flapping threshold.
          // Fire-and-forget: the reaper must not block on the report.
          void reportReaperEvent(
            agent.agent_id,
            'mcp-presence-reaper',
            'restart',
            givenUpKeys.length > 0
              ? `restarted session; MCP server(s) still down after restart budget: ${givenUpKeys.join(', ')}`
              : `restarted session to recover MCP server(s): ${activeKeys.join(', ')}`,
          );
        },
        // ENG-5932: classify each declared key for the quarantine path. Reads a
        // server-declared criticality field off the .mcp.json entry when
        // present (Slice 2 will stamp it); otherwise fails closed to ESSENTIAL
        // for everything except the known optional channel set. Tool servers
        // (composio_*/xero) classify ESSENTIAL here and stay on the existing
        // managed-toolkit onGiveUp path above.
        classifyKey: (serverKey) =>
          classifyChannelCriticality(
            serverKey,
            (mcpJsonParsed?.mcpServers as Record<string, unknown> | undefined)?.[serverKey],
          ),
        quarantineDwellMs: channelQuarantineDwellMs(),
        quarantineMode: channelQuarantineMode(),
        // ENG-5932: enforce-mode quarantine. Persist the local marker; the next
        // provisioning poll then omits the channel from .mcp.json + the launch
        // flags, and the channel-set-change / .mcp.json-drift restart paths are
        // taught to NOT bounce the session for a quarantine-driven removal.
        // Local-authoritative: no API call — with the control plane down, the
        // flap still stops.
        onQuarantine: (codeName, serverKey) => {
          const newly = channelQuarantineStore().quarantine(
            codeName,
            serverKey,
            `mcp-presence-reaper: optional channel dead past restart budget + dwell`,
            Date.now(),
          );
          if (newly) {
            log(
              `[channel-quarantine] persisted quarantine marker for '${codeName}:${serverKey}' — will be dropped from .mcp.json on next provisioning poll (ENG-5932)`,
            );
            // ENG-6184: a newly-quarantined channel is a critical reaper action
            // (the channel is pulled out of service) — surface it once.
            // Fire-and-forget: the reaper must not block on the report.
            void reportReaperEvent(
              agent.agent_id,
              'mcp-presence-reaper',
              'quarantine',
              `optional channel '${serverKey}' dead past restart budget + dwell — quarantined`,
            );
            // ENG-8018: tell the AGENT its channel was quarantined, once, at the
            // moment it happens. The Sherlock failure (ENG-8003) was the agent
            // confabulating an "empty allowlist" cause because it had zero
            // visibility into the host-side quarantine. This system notice gives
            // it the real cause so it reports "my <channel> is quarantined, ask
            // an operator to re-enable it" instead of inventing one. The channel
            // is still in the store's set, so the heartbeat diagnostics (AC2)
            // carry it too; this is the immediate, in-session signal. Fire-and-
            // forget: in-session delivery must not block or fail the reaper.
            void injectMessage(
              codeName,
              'system',
              `Your "${serverKey}" channel has been quarantined by the host runtime: it repeatedly failed to start (no live MCP process) past the recovery budget, so it has been pulled from your active channel set. You cannot send or receive on ${serverKey} until an operator re-enables it. This is an infrastructure fault, not a policy or allowlist decision. If anyone asks why you are quiet on ${serverKey}, tell them the channel is quarantined and ask them to have an operator re-enable it. Do not guess at other causes.`,
              { task_name: 'channel-quarantine' },
              log,
            ).catch(() => {
              /* best-effort: a failed inject never breaks the reaper */
            });
          }
        },
      });
    } catch (err) {
      log(`[mcp-presence-reaper] presence check failed for '${agent.code_name}': ${(err as Error).message}`);
    }
    } // end claude-code MCP supervision (ADR-0047: skipped for opencode)

    // ENG-7878: opencode scheduled-task firing. opencode has no in-session
    // kanban MCP to work a materialised card (the claude route via
    // fireScheduledTaskViaKanban), so the manager drives schedules directly -
    // it injects each due task's prompt over HTTP into `opencode serve` and
    // delivers the reply to the task's configured target. Runs inside this
    // block (not the else-if below) because opencode routes here for the
    // spawn/supervise seam; a scheduled task needs the live serve that seam
    // boots. Never throws; a per-task failure is contained inside the helper.
    if (agentFw === 'opencode' && tasks.length > 0) {
      await syncAndCheckOpencodeScheduler(agent, tasks, refreshData);
    }
  } else if (agentFw === 'claude-code' && tasks.length > 0) {
    // Non-persistent claude-code scheduler path (the legacy `claude -p` oneshot
    // was retired in ENG-6849; this now materialises an in-session kanban card +
    // nudge, same as the persistent path).
    // ENG-8114: resolve the durable-nudge kill switch host-side and thread it in,
    // exactly as the persistent path does above — this module owns hostFlagStore()
    // and syncAndCheckClaudeScheduler can't read it without an import cycle. ON
    // rides the durable direct-chat rail (ENG-7971/ENG-8068); OFF (default) stays
    // on tmux send-keys. Org/team overrides are already merged into the host's
    // heartbeat flag map, so this host-wide read honours them.
    await syncAndCheckClaudeScheduler(
      agent,
      tasks,
      boardItems,
      refreshData,
      hostFlagStore().getBoolean('scheduled-task-nudge-durable'),
    );
  }

  // 10. Immediate work trigger — if webapp signalled a new task, fire kanban-work now
  {
    const triggerAt = (refreshData.agent as Record<string, unknown>).work_trigger_at as string | null;
    if (triggerAt) {
      const triggerTs = new Date(triggerAt).getTime();
      const lastTrigger = lastWorkTriggerAt.get(agent.code_name) ?? 0;
      if (triggerTs > lastTrigger) {
        lastWorkTriggerAt.set(agent.code_name, triggerTs);

        if (agentFw === 'claude-code') {
          if (sessionMode === 'persistent') {
            // ENG-8743: the durable direct-chat rail, not tmux send-keys.
            //
            // This was the last kanban nudge still typing into a pane. It called
            // `injectMessage` fire-and-forget — unawaited, return discarded — so
            // when the trailing Enter did not submit (send-keys is `-l <text>`
            // followed by a separate `Enter`, and tmux cannot confirm a submit)
            // the nudge stranded visibly in the composer and nothing retried.
            // Observed on prod agent `sophie`.
            //
            // ENG-7967 had already removed send-keys from `maybeInjectKanbanCheck`
            // for a second reason that applies here verbatim: send-keys has NO
            // consume-time revalidation, so it types a nudge off the manager's own
            // possibly-stale board belief — the ENG-7958 bug class. Riding
            // `injectKanbanCheckViaDirectChat` inherits the server-side gate:
            // `agentHasActionableKanban` re-checks the REAL board at consume time
            // and expires the notice if the board drained meanwhile.
            //
            // WHAT THIS DELIBERATELY GIVES UP: the ENG-5405 optimisation of naming
            // the top card inline so the agent could `kanban_move` without a
            // `kanban_list` first. That id was only safe to embed because send-keys
            // delivered ~immediately off a just-read board. On a durable queue the
            // notice can wait until the agent is free, and the consume-time gate is
            // board-level ("any actionable card"), NOT card-level — so an embedded
            // id is never revalidated and can name a card that has since moved.
            // Keeping it would trade a lost-message bug for a wrong-card bug; the
            // generic check command costs one extra MCP call and is always correct.
            const actionableCount = boardItems.filter((b) =>
              (KANBAN_NUDGE_ACTIONABLE_STATUSES as readonly string[]).includes(b.status),
            ).length;
            const enqueued = await injectKanbanCheckViaDirectChat(
              agent.code_name,
              agent.agent_id,
              actionableCount,
            );
            if (enqueued) {
              log(
                `[persistent-session] Work trigger enqueued on the durable rail for '${agent.code_name}' (${actionableCount} actionable card(s))`,
              );
            } else {
              // No send-keys fallback, per the ENG-7967 contract — a fallback with
              // no revalidation gate is exactly what was removed. The cards are
              // already durable on the board, so the recurring hybrid nudge
              // (maybeInjectKanbanCheck) re-attempts on its next tick: this costs
              // latency, not the work.
              log(
                `[persistent-session] Work trigger enqueue FAILED for '${agent.code_name}' — hybrid nudge will retry`,
              );
            }
            // The `isSessionHealthy` guard went with the pane: it existed because
            // send-keys needs a live TTY to type into. A durable notice does not —
            // it waits in the queue and the in-session MCP pulls it once the
            // session is up, so a trigger arriving mid-boot is now delivered late
            // instead of dropped.
          }
          // ENG-5662: non-persistent claude-code agents no longer have a
          // kanban-work claude -p fallback — kanban work is driven by the
          // hybrid edge-trigger on persistent sessions only.
        }
      }
    }
  }

  // 12. Detect newly-done kanban items and send notifications (runs every cycle)
  {
    const agentId = agentState.codeNameToAgentId.get(agent.code_name);
    if (agentId) {
      try {
        // ENG-9585: the tick's shared snapshot — usually already fetched by 7d
        // or 7e above, which is the duplicate this removes. `agentId` and
        // `agent.agent_id` (what the fetcher sends) cannot differ: the map is
        // written from `agent.agent_id` at the top of this same call.
        //
        // The snapshot may pre-date sections 8-11, so a card that completed
        // mid-tick is reported newly-done on the next tick instead. That is
        // bounded latency, not a lost notification — `notifyBoardCache` is
        // seeded from this same snapshot, so the transition is still in the
        // next diff. On a FAILED fetch nothing was retained, so this re-fetches
        // and throws to the catch below exactly as it did before.
        const freshBoard = await fetchTickBoard();
        const freshDoneIds = new Set(freshBoard.filter((b) => b.status === 'done' || b.status === 'failed').map((b) => b.id));

        const previousDoneIds = notifyBoardCache.get(agent.code_name);
        notifyBoardCache.set(agent.code_name, freshDoneIds);

        // Skip first cycle — no previous state to compare against
        if (!previousDoneIds) {
          log(`[${agent.code_name}] Board diff: initial cache (${freshDoneIds.size} done items)`);
        } else {
          const newlyDone = freshBoard.filter(
            (b) => (b.status === 'done' || b.status === 'failed') && !previousDoneIds.has(b.id),
          );

          log(`[${agent.code_name}] Board diff: ${previousDoneIds.size} prev done → ${freshDoneIds.size} now, ${newlyDone.length} newly done`);

        if (newlyDone.length > 0) {
          const displayName = agentState.agentDisplayNames.get(agent.code_name) ?? agent.code_name;
          for (const item of newlyDone) {
            // ENG-8749: item_id, not the title. Card titles are free text a
            // customer types, so they can carry anything; manager.log is durable
            // (rotated on the host, collected by the session archiver), and the
            // CLAUDE.md convention is hash-only/redacted. The id is preferred
            // over a hash throughout this block because a fingerprint is
            // unlookupable — it costs an operator the ability to find the card
            // while protecting no more than a uuid does.
            log(`Newly done: item_id=${item.id} notify_channel=${item.notify_channel ?? 'none'} notify_to=${item.notify_to ?? 'none'} suppress_delivery=${item.suppress_delivery === true}`);
            // CS-1651: honour the agent's suppression declaration HERE too.
            //
            // This is the second of two independent delivery paths for a done
            // card. The scheduled-card path already honoured the flag, which is
            // precisely why this survived: anyone testing suppression on the
            // path it was written for saw it work. Meanwhile this notifier
            // delivered the full result - refetched at up to 3,500 chars, not
            // the board's truncated copy - into the originating DM, while
            // `kanban_done` told the agent delivery was suppressed.
            //
            // Explicit `=== true`: the column is nullable, and only a genuine
            // true suppresses. An undefined from an older API build that does
            // not yet send the column must DELIVER, because silence is the
            // failure this whole ticket is about and defaulting to suppression
            // would trade a loud bug for a silent one.
            if (item.suppress_delivery === true) {
              log(`Newly done: item_id=${item.id} delivery suppressed by the card's suppress_delivery flag — result recorded on the card only`);
            } else if (item.notify_channel && item.notify_to) {
              const message = await buildDoneCardNotification(agentId, agent.code_name, displayName, item);
              sendTaskNotification(agent.code_name, item.notify_channel, item.notify_to, message).catch((err) => {
                log(`sendTaskNotification failed for '${agent.code_name}': ${(err as Error).message}`);
              });
            }
          }
        }
        }

        // ENG-7643 (ADR-0044 P5a): DM the requester (as the agent's own bot) when
        // a card newly parks in waiting on a human. Same seed-on-first-cycle
        // one-shot as the done-notify above — a card already parked at manager
        // boot is seeded (never re-DM'd); only a fresh in_progress->waiting(human)
        // transition with a captured requester fires.
        {
          // Only track cards that ALSO carry a requester: a waiting-human card
          // whose requester isn't stamped yet must not be marked seen (else it
          // would never notify once the requester appears). The newlyWaiting
          // filter below re-checks the requester too.
          const freshWaitingHumanIds = new Set(
            freshBoard
              .filter((b) => b.status === 'waiting' && b.waiting_kind === 'human' && b.metadata?.requester)
              .map((b) => b.id),
          );
          const prevWaitingHumanIds = notifiedWaitingHumanIds.get(agent.code_name);
          notifiedWaitingHumanIds.set(agent.code_name, freshWaitingHumanIds);
          if (prevWaitingHumanIds) {
            const newlyWaiting = freshBoard.filter(
              (b) =>
                b.status === 'waiting' &&
                b.waiting_kind === 'human' &&
                !prevWaitingHumanIds.has(b.id) &&
                b.metadata?.requester &&
                // ENG-7643 (P5b-2): the API posts inline Slack buttons for a Slack
                // requester WITH choices; suppress the text DM for that exact case
                // so the requester isn't pinged twice. Non-Slack or no-choices
                // still gets the manager text DM.
                !(
                  b.metadata.requester.channel === 'slack' &&
                  (b.waiting_context?.choices?.length ?? 0) > 0
                ),
            );
            if (newlyWaiting.length > 0) {
              const waitingDisplayName =
                agentState.agentDisplayNames.get(agent.code_name) ?? agent.code_name;
              const consoleUrl = getConsoleUrl();
              for (const item of newlyWaiting) {
                const requester = item.metadata!.requester!;
                const target = resolveRequesterDmTarget(requester);
                if (!target) {
                  log(
                    `[${agent.code_name}] waiting-human notify skipped for '${item.id}': no DM target for channel=${requester.channel}`,
                  );
                  continue;
                }
                const message = buildWaitingHumanNotification({
                  displayName: waitingDisplayName,
                  agentId,
                  itemId: item.id,
                  title: item.title,
                  waitingOn: item.waiting_on,
                  details: item.waiting_context?.details,
                  choices: item.waiting_context?.choices,
                  medium: target.channel,
                  consoleUrl,
                });
                sendTaskNotification(agent.code_name, target.channel, target.to, message).catch((err) => {
                  log(`waiting-human notify failed for '${agent.code_name}': ${(err as Error).message}`);
                });
              }
            }
          }
        }

        // Check for stale in_progress items
        const staleItems = freshBoard.filter((b) => {
          if (b.status !== 'in_progress' || !b.updated_at) return false;
          const age = Date.now() - new Date(b.updated_at).getTime();
          return age > STALE_TASK_THRESHOLD_MS && !alertedStaleItems.has(`${agent.code_name}:${b.id}`);
        });

        if (staleItems.length > 0) {
          const displayName = agentState.agentDisplayNames.get(agent.code_name) ?? agent.code_name;
          for (const item of staleItems) {
            const age = Math.round((Date.now() - new Date(item.updated_at!).getTime()) / 60_000);
            // ENG-5408: structured stuck-task watchdog line. Lands BEFORE
            // the existing auto-fail so CloudWatch metric filters can
            // count occurrences even when the auto-fail succeeds. Same
            // shape as `[persistent-session-stuck]` (ENG-5116) so the
            // alerting/monitoring pipeline can pattern-match without a
            // new parser. One line per row per occurrence — `stuckKanbanLogged`
            // dedupes within the session.
            const stuckKey = `${agent.code_name}:${item.id}`;
            if (!stuckKanbanLogged.has(stuckKey)) {
              log(
                `[persistent-session-stuck-kanban] agent=${agent.code_name} ` +
                  // ENG-8749: title dropped. JSON.stringify quotes and escapes,
                  // it does not redact — this line was writing the raw title
                  // with quotation marks around it. item_id is already here.
                  `item_id=${item.id} ` +
                  `status=in_progress age_minutes=${age} ` +
                  `threshold_minutes=${Math.round(STALE_TASK_THRESHOLD_MS / 60_000)}`,
              );
              stuckKanbanLogged.add(stuckKey);
            }
            log(`Stale task: item_id=${item.id} in_progress for ${age}m — reaping for '${agent.code_name}'`);
            alertedStaleItems.add(`${agent.code_name}:${item.id}`);

            // ENG-7767: report the staleness; let the SERVER decide the verdict.
            //
            // This used to POST `status: 'failed'` to /host/kanban. That was
            // wrong twice over: `failed` means "attempted and genuinely couldn't
            // complete" — an outcome, not a stopwatch — and worse, `failed` is
            // resurrection-blocked, so a card interrupted by a routine deploy
            // became permanently unreachable. No operator, agent, or intake cron
            // could ever move it back. Three cards died that way for ten days.
            //
            // The verdict now lives server-side (/host/kanban/reap-stale →
            // decideStaleReap), shared with the cron sweep, so the two triggers
            // can't disagree and the retry budget is counted with a CAS. We keep
            // detection + telemetry + the user-facing notice; we stop deciding.
            let reapAction: 'auto_return' | 'dead_letter' | null = null;
            try {
              const reapResult = await api.post<{
                ok?: boolean;
                updated?: number;
                action?: 'auto_return' | 'dead_letter';
                attempts_used?: number;
                max_attempts?: number;
                reason?: string;
                error?: string;
              }>('/host/kanban/reap-stale', {
                agent_id: agentId,
                item_id: item.id,
                reason: `Timed out — in progress for ${age} minutes with no update`,
              });
              reapAction = reapResult.action ?? null;
              log(
                `Reap result for item_id=${item.id}: updated=${reapResult.updated} action=${reapResult.action ?? reapResult.reason ?? 'none'}`,
              );
              // Only a DEAD LETTER is terminal, so only it should suppress a
              // later done-notification. An auto-returned card is going back on
              // the board and will legitimately complete later — adding it here
              // would swallow that completion notice.
              if (reapAction === 'dead_letter' && ((reapResult.updated ?? 0) > 0 || reapResult.ok === true)) {
                freshDoneIds.add(item.id);
              }
            } catch (err) {
              // ENG-8749: this line carried BOTH violations — the raw title and
              // a raw API error message. The error is hashed to the same 12-char
              // sha256 shape as startRun(), enqueueKanbanNotice() and the
              // ENG-8743 realtime .catch, all of which cite the CLAUDE.md log
              // policy; the prefix is enough to correlate with server-side logs.
              const errId = createHash('sha256')
                .update(err instanceof Error ? err.message : String(err))
                .digest('hex')
                .slice(0, 12);
              log(`Reap API error for item_id=${item.id} error_id=${errId}`);
            }

            // Notify — only when the card has actually GIVEN UP. An auto-return
            // is a retry: nothing has gone wrong yet, the card is going straight
            // back on the board, and telling a user "your task timed out" for a
            // blip we're about to transparently recover from is both false and
            // the kind of noise that teaches people to ignore these messages.
            // A lost race (the agent woke up) is likewise a non-event.
            if (reapAction === 'dead_letter') {
              const message = `⚠️ Task Stalled — ${displayName}\n${item.title}\nStalled repeatedly (last: ${age} minutes with no update) and has been retried as far as it goes — it now needs a look.`;
              if (item.notify_channel && item.notify_to) {
                sendTaskNotification(agent.code_name, item.notify_channel, item.notify_to, message).catch(() => {});
              }
              sendSlackWebhookMessage(`⚠️ *Task Stalled* — *${displayName}*\n*${item.title}*\nRetried to the limit after repeated stalls — needs a look.`).catch(() => {});
            }
          }
        }

        // Clear stale alerts for items no longer in_progress (scoped to this agent)
        const prefix = `${agent.code_name}:`;
        for (const key of alertedStaleItems) {
          if (key.startsWith(prefix)) {
            const itemId = key.slice(prefix.length);
            if (!freshBoard.some((b) => b.id === itemId && b.status === 'in_progress')) {
              alertedStaleItems.delete(key);
            }
          }
        }
        // ENG-5408: clear the structured-log dedupe set in lockstep, so an
        // item that gets cancelled / fixed / re-spawned can re-trigger the
        // watchdog if it goes stuck again later in this session.
        for (const key of stuckKanbanLogged) {
          if (key.startsWith(prefix)) {
            const itemId = key.slice(prefix.length);
            if (!freshBoard.some((b) => b.id === itemId && b.status === 'in_progress')) {
              stuckKanbanLogged.delete(key);
            }
          }
        }
      } catch (err) {
        log(`Board diff failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }
  }

  // 13. Memory sync — upload local memory files to DB, download DB memories to local
  if (frameworkId === 'claude-code' && agent.status === 'active') {
    try {
      await syncMemories(agent, config.configDir, log);
    } catch (err) {
      log(`Memory sync failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // ENG-4473: Re-capture agentState.writtenHashes AFTER all tracked-file writers have
  // finished this poll — channel credentials (step 5), the direct-chat
  // provisioner (step 5b), managed-provider proxies (step 7), etc. The
  // provisioner write loop at step 3 records `agentState.writtenHashes` before those
  // later writers run, so the drift check at the TOP of the next poll
  // sees the final file state vs the step-3 hash and mis-reports every
  // poll as drifted (even when the state is perfectly stable). Re-hashing
  // here converges agentState.writtenHashes to what's actually on disk after this
  // agent's full processing, so:
  //   - genuine external edits between polls are still detected (drift
  //     check runs BEFORE our writers in the next poll)
  //   - our own multi-writer sequence stops reading as drift
  const trackedFiles = frameworkAdapter.driftTrackedFiles();
  if (trackedFiles.length > 0 && existsSync(agentDir)) {
    // Rebuild from scratch so files no longer tracked (or missing on disk)
    // don't leave stale hashes that would mis-report drift next poll.
    const hashes = new Map<string, string>();
    for (const file of trackedFiles) {
      const h = hashFile(join(agentDir, file));
      if (h) hashes.set(file, h);
    }
    agentState.writtenHashes.set(agent.agent_id, hashes);
  } else {
    agentState.writtenHashes.delete(agent.agent_id);
  }

  // ENG-6533 (folds ENG-6527): drive self-onboarding. If this claude-code
  // persistent agent's onboarding is mid-flow (on a per-area step), inject a
  // one-shot "run onboarding_get and proceed" directive into its live session.
  // Idempotent via a per-agent marker that self-heals a stale area; pending/
  // ready clear it so a later /onboard (resets → the first area) re-drives. Wrapped
  // best-effort: an onboarding-drive failure must never break the refresh loop.
  try {
    const obFramework = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
    const obSessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
    if (obFramework === 'claude-code' && obSessionMode === 'persistent') {
      const obState = coerceOnboardingState(
        (refreshData.agent as Record<string, unknown>).onboarding_state,
      );
      const obStep = obState.step;
      const markerPath = join(homedir(), '.augmented', agent.code_name, 'onboarding-drive.json');
      const marker = readOnboardingDriveMarker(markerPath);
      // ENG-8703: the manager-contact stamp is what re-denominates the nudge
      // budget from elapsed minutes to asks the manager could actually answer.
      // Passed through UNPARSED: the decision compares it against the value it
      // last recorded, never against host time, so API/host clock skew cannot
      // decide whether a real ask counted. Absent reads as "never contacted",
      // which withholds ask credit and keeps the sequence nudging (bounded by
      // the inject ceiling) rather than standing down on an ask that may never
      // have gone out.
      const obContactRaw = (refreshData.agent as Record<string, unknown>).manager_last_contacted_at;
      const obContact = typeof obContactRaw === 'string' && obContactRaw ? obContactRaw : null;
      // ENG-6601: pass the live reset generation so a same-area RESET (/onboard)
      // re-injects even though the step didn't change.
      const decision = decideOnboardingDrive(obStep, marker, Date.now(), obState.generation ?? 0, {
        managerContactedAt: obContact,
      });
      if (decision.clearMarker) {
        clearOnboardingDriveMarker(markerPath);
      } else if (
        decision.inject &&
        isOnboardingArea(obStep) &&
        isSessionHealthy(agent.code_name)
      ) {
        const injectResult = await injectMessageWithStatus(
          agent.code_name,
          'system',
          // ENG-6583: anchor the ask to the channel onboarding was triggered
          // from, and phrase a staleness re-fire as a gentle reminder. ENG-6626:
          // the final bounded nudge flips to the stand-down message, which names
          // the agent's /onboard-<code> resume command. ENG-6636: a /onboard
          // restart (generation change) forces the "post the question afresh
          // this turn, don't stand down" variant.
          buildOnboardingDirective(obStep, obState.completed, {
            channel: obState.channel,
            reminder: decision.reminder,
            standDown: decision.standDown,
            freshRestart: decision.freshRestart,
            code: agent.code_name,
            // ENG-8703: never accuse a manager of not replying to a message
            // that was never sent. Read from the ask counter rather than the
            // raw column, which is per-agent and survives a /onboard restart —
            // an ask in a previous generation must not license an accusation in
            // this one. `nudgeCount > 0` means an ask was credited within THIS
            // area's sequence, by construction.
            everAsked: (decision.nextMarker?.nudgeCount ?? 0) > 0,
          }),
          { task_name: 'onboarding' },
          log,
        ).catch(() => ({ delivered: false, fallbackUsed: false }));
        // Persist the marker once the nudge REACHED the session (ENG-6576). With
        // acpx removed (ENG-6345) tmux send-keys is structurally delivered:false
        // even when it lands, so gating on `delivered` alone never wrote the
        // marker and re-injected every poll forever. `onboardingNudgeReachedSession`
        // treats a landed keystroke (fallbackUsed) as reached; a genuine miss
        // (neither flag) leaves the marker unwritten so the 5-min staleness
        // self-heal retries next poll instead of looping every cycle.
        if (onboardingNudgeReachedSession(injectResult) && decision.nextMarker) {
          writeOnboardingDriveMarker(markerPath, decision.nextMarker);
          // ENG-6626: surface the bounded-nudge progression (which nudge, and
          // whether this was the final stand-down) for fleet diagnosis.
          const tag = decision.standDown
            ? `stand-down (nudge ${decision.nextMarker.nudgeCount})`
            : `nudge ${decision.nextMarker.nudgeCount}`;
          log(`[onboarding] ${tag} '${agent.code_name}' into '${obStep}'`);
        }
      }
    }
  } catch (e) {
    log(`[onboarding] drive failed for '${agent.code_name}': ${(e as Error).message}`);
  }

  agentStates.push({
    agentId: agent.agent_id,
    codeName: agent.code_name,
    status: agent.status,
    charterVersion,
    toolsVersion,
    secretsHash,
    lastRefreshAt: now,
    lastProvisionAt,
    lastDriftCheckAt,
    lastSecretsProvisionAt,
    acpSessions: [],
  });
}

// ---------------------------------------------------------------------------
// Claude Code in-process scheduler
// ---------------------------------------------------------------------------

import {
  syncTasksToScheduler,
  getReadyTasks,
  markTaskFired,
  loadSchedulerState,
  getProjectDir as ccGetProjectDir,
  type SchedulerTaskInput,
  type SchedulerTaskState,
} from './scheduler-engine.js';



// ---------------------------------------------------------------------------
// Persistent session management (session_mode = 'persistent')
// ---------------------------------------------------------------------------

import {
  parseModelPolicy,
  modelPolicyAuthComponent,
  modelPolicyEgressDomains,
  failoverRestartNotice,
  policyResolvedModel,
  type ManagerModelPolicy,
} from './model-policy.js';
import { buildAuthTuple } from './auth-tuple-diff.js';
import {
  startPersistentSession,
  isolationMode,
  effectiveIsolationMode,
  buildEgressAllowlist,
  writeEgressAllowlist,
  reloadEgressSidecar,
  restartEgressSidecar,
  egressMode,
  type EgressToolsFrontmatter,
  stopPersistentSession,
  injectMessage,
  injectMessageWithStatus,
  isSessionHealthy,
  getSessionState,
  readDirectChatSessionState,
  resetRestartCount,
  stopAllSessions,
  stopAllSessionsAndWait,
  getProjectDir as psGetProjectDir,
  resolveClaudeBinary,
  getLastFailureContext,
  prepareForRespawn,
  takeZombieDetection,
  rotateSessionForWedge,
  readPaneLogTail,
  paneLogPath,
  rotatePaneLogForDayRollover,
  CLAUDE_MANAGED_SETTINGS_DIR,
} from './persistent-session.js';
import { resolveContainerMemory, seedContainerMemory, syncContainerMemory } from './container-memory-sync.js';
import {
  BLOCKED_ON_PROMPT_DECISION,
  applyBlockedPromptVerdict,
  isConfirmedRespawn,
  noteSessionBlockedOnPrompt,
  sessionBlockedOnPrompt,
  sessionHealthyAndUnblocked,
  shouldLogBlockedPrompt,
} from './session-blocked-prompt.js';

// ADR-0047: opencode agents are supervised as a headless `opencode serve`
// process driven over HTTP, not a tmux/send-keys claude pane. The launcher
// mirrors the persistent-session surface (start/health/inject/stop) but keeps
// its own tmux namespace (`agt-oc-<code>`), so the two runtimes never collide.
import {
  startOpencodeSession,
  isOpencodeSessionHealthy,
  injectOpencodeMessage,
  stopOpencodeSession,
  readOpencodePaneLogTail,
  readProvisionedOpencodeModel,
  getOpencodeSessionState,
  // ENG-8090: the two separated activity signals — occupancy (utilisation and
  // billing) and turn completion (liveness). See opencode-activity-tracker.ts.
  getOpencodeActivityAgeSeconds,
  getOpencodeTurnHealth,
} from './opencode-session.js';
// ENG-8116: measured occupancy durations. The ledger is shared by both
// producers (opencode turn spans, Claude Code pane-mtime deltas) so the probe
// has one place to drain regardless of runtime.
import { sharedBusyBuckets, type BusyBucket } from './busy-bucket-ledger.js';
import {
  collectQualifyingTurns,
  qualifyBuckets,
  rostersMeasuredZero,
  type OccupancyQualifierMode,
  type QualifyBucketsResult,
} from './occupancy-gate.js';
// Type-only: erased at compile time, so the module stays dynamically imported
// inside pollCycle exactly as before.
import type { ResponsivenessProbePayloadEntry } from './responsiveness-probe.js';
import {
  hasFreshPaneObservation,
  startPaneOccupancySampler,
  stopPaneOccupancySampler,
} from './pane-occupancy-sampler.js';
// ENG-8876: per-agent cgroup PID pressure. `pidPressureFor` is a pure read of
// the sampler's last tick — see the note at each collectDiagnostics call site.
import {
  pidPressureFor,
  startPidPressureSampler,
  stopPidPressureSampler,
} from './pid-pressure-sampler.js';
// ENG-10857: the Claude Code version an ISOLATED agent actually runs, which
// `hosts.framework_version` (the manager's own binary) does not describe.
// `runtimeVersionFor` is a pure read of the sampler's last tick - the exec
// runs once per CONTAINER, never on the heartbeat path.
import {
  runtimeVersionFor,
  startRuntimeVersionSampler,
  stopRuntimeVersionSampler,
} from './runtime-version-sampler.js';
// ENG-10869: the single locator for `.claude.json`. Imported statically so the
// adoption below runs in the manager's own (non-async) startup path.
import {
  CLAUDE_CONFIG_DIR_ENV,
  claudeConfigDir,
  claudeConfigDirIsReady,
  seedClaudeConfigFromLegacy,
} from './claude-config-location.js';
// ADR-0047: opencode has no in-session channel MCP, so the MANAGER runs the
// Slack Socket Mode loop for opencode agents and injects admitted turns over
// HTTP. Claude agents are unaffected - their slack-channel MCP still owns their
// socket, and these are only ever called from the opencode arm.
import {
  ensureOpencodeSlackIngest,
  stopOpencodeSlackIngest,
  stopAllOpencodeSlackIngests,
} from './opencode-slack-ingest.js';
import { syncAndCheckOpencodeScheduler } from './manager/opencode-scheduler.js';
import {
  ensureOpencodeTelegramIngest,
  stopOpencodeTelegramIngest,
  stopAllOpencodeTelegramIngests,
} from './opencode-telegram-ingest.js';
import {
  resolveWedgeConfig,
  decideWedgeRestart,
  isWedgeCandidateCycle,
  wedgeExemptionReason,
  shouldForceFreshOnManualRestart,
} from './wedge-detection.js';
import {
  resolvePoisonCardConfig,
  recordWedgeForCards,
  pruneCardStates,
  partitionActionableByPoison,
  type CardWedgeState,
} from './wedge-poison-card.js';
import {
  noteClaudeAccountChange,
  readUsageCapUntil,
  shouldLogUsageCapDeferral,
} from './agent-serving-probe.js';
import {
  isAgentIdle,
  isStaleForToday,
  minutesSinceLocalMidnight,
  peekCurrentSession,
  subagentActivityAgeSeconds,
  transcriptActivityAgeSeconds,
} from './daily-session.js';
import { processRestartFlags } from './restart-handler.js';
import { refreshSlackRestartContextHints } from './restart-context.js';
import { PersistentSessionStuckTracker } from './persistent-session-stuck-tracker.js';
// ENG-7360: detect a transient LLM-API error in the wedged session's pane.log
// tail so the wedge-respawn path can surface the friendly "please resend" notice.
import { detectTransientApiErrorInLog } from './transient-api-error-log.js';

// ADR-0011 PR-A: track which agents have persistent sessions started.
// Moved into `agentState.persistentSessionAgents` so the future kanban-inject
// gate (PR-C) doesn't have to import from the lifecycle module.

// ENG-5459: per-agent dedupe for the pane.log MCP-failure-banner
// observation. Manager only POSTs when the count differs from the last
// reported value, so a stable healthy agent (banner-not-present →
// count 0) emits at most one POST per fresh worker.
const lastMcpFailedBannerCount = new Map<string, number>();

// ENG-7363: per-agent dedupe for the pane.log model-API-error scrape. The
// error banner persists across frames, so we report only when the observed
// signature (error class + status) changes, so one event per distinct episode
// rather than one per poll. Value is the last-reported signature, or '' when
// the pane was last seen clean.
const lastModelApiErrorSig = new Map<string, string>();

/**
 * ENG-8877: last reported platform-auth verdict per agent, so a session that
 * stays auth-dead logs once per episode instead of once per ~30s poll. Keyed
 * on the full detail string (state + kind), so a transition between kinds
 * re-reports — those have different remedies and collapsing them would
 * reproduce the defect this exists to fix.
 */
const lastSessionAuthDetail = new Map<string, string>();

/**
 * ENG-5116 — discriminator for the *one decision* a persistent-session
 * tick made for a given agent. Every code path inside
 * `ensurePersistentSession` records exactly one of these so the caller
 * can emit a single structured `[persistent-session-decision]` log line
 * even when the path took an early return. Before this, only the happy
 * path logged ("Starting tmux session ...") and silent skips were
 * effectively invisible — see the Vigil incident (2026-05-16).
 */
type PersistentSessionDecision =
  | 'spawn'                       // startPersistentSession() invoked this tick
  | 'healthy'                     // session is up; tasks may have synced
  | 'restart-pending'             // stopped this tick (auth tuple changed); next tick respawns
  | 'day-rollover-restart'        // stopped this tick for date rollover; next tick respawns
  | 'day-rollover-forced'         // ENG-7548: forced rollover after busy-blocking past the grace
  | 'day-rollover-deferred'       // stale but agent still active mid-task; will retry
  | 'circuit-tripped'             // ENG-5441: breaker tripped — no spawn until operator resumes
  | 'skipped-no-api-key'
  | 'skipped-auth-resolve-failed'
  // ENG-8260: the exchange failed with a TRANSIENT platform fault (5xx / 429 /
  // transport) while a healthy session was running, so the session was left
  // untouched and the exchange will be retried on the next poll. Distinct from
  // 'skipped-auth-resolve-failed' precisely so the two are separable in
  // manager.log — the whole incident-analysis gap that produced this ticket was
  // one decision string covering both "we tore down a working agent" and "we
  // correctly declined to spawn".
  | 'skipped-auth-resolve-transient'
  | 'skipped-not-authenticated'
  | 'skipped-api-key-mode-no-key'
  | 'skipped-openrouter-no-key'    // ENG-7152: host in openrouter mode, agent has no key/model
  // ADR-0042 pt3b (ENG-7607 / ENG-8992): minting this agent's own org-scoped key
  // failed, so the spawn was refused rather than fall back to the shared
  // host-wide key. Deliberately DISTINCT from 'skipped-auth-resolve-failed': one
  // string covering both "the exchange is unwell" and "we refused a spawn on a
  // credential-isolation fault" would make this unsearchable in manager.log, and
  // it is the one failure whose whole purpose is to be noticed.
  | 'skipped-per-agent-key-mint-failed'
  // ENG-9125: the agent's .mcp.json points at bundle(s) inside the host-shared
  // `~/.augmented/_mcp` mount that are not on disk yet (the mount is populated
  // by deployMcpAssets(), which can land AFTER containers start). Spawning
  // anyway produces a session with zero MCP tools, which mcp-presence-reaper
  // then tries to fix the only way it can — by restarting — until the crash
  // tally trips the breaker and the agent is paused until a human resumes it.
  // That is what took out 13 of 16 agents on agt-aws-1 on 2026-08-18. Deferring
  // costs one poll interval; spawning costs a human intervention.
  //
  // DISTINCT from 'circuit-tripped' on purpose: that one means "we already
  // burned the restart budget", this one means "we declined to start burning
  // it". Collapsing them would hide exactly the transition this ticket exists
  // to prevent. Critically, this path records NO restart event, so a slow mount
  // can never feed the breaker.
  | 'skipped-mcp-assets-not-ready'
  // ENG-10335: the tick would have reported the session healthy (tmux exists,
  // claude is running), but it is blocked on a prompt nothing can answer (the
  // login picker, an unanswerable usage-limit modal, an unclaimed modal), so it
  // cannot run a tool call. Published as UNHEALTHY with no spawn attempt, which
  // skips bind remediation and stops the console reporting it live, and never
  // restarts it: a restart re-presents the same prompt (ENG-9932). Set only by
  // the ensurePersistentSession wrapper; the underlying decision rides in detail.
  | 'blocked-on-prompt';

// ENG-7548: a perpetually-busy agent never presents a 60s-idle window, so the
// idle-gated day-rollover would defer forever and its context is never reset
// (Paige pinned at 99%). Bound the defer: once the rollover is this many minutes
// past the agent-local day boundary AND still busy-blocked, force the restart
// anyway. 30 min gives a genuine long task room to finish first, while
// guaranteeing "always busy" can't indefinitely defeat the nightly reset.
export const DAY_ROLLOVER_FORCE_GRACE_MIN = 30;

// ENG-8881: hold a FORCED day-rollover restart if the agent had channel inbound
// activity this recently. Covers the window between an agent telling a human
// "on it" and writing the kanban row that makes that promise durable — a restart
// inside it loses accepted work silently, because every recovery path is
// card-based and there is no card yet.
//
// 60s is sized to the observed incident (the kill landed 3s after the reply) with
// generous headroom for a slow card write, while staying far below the poll
// interval multiples that matter to the rollover itself.
export const DAY_ROLLOVER_INBOUND_GRACE_SEC = 60;

// ...and the ceiling on that hold. An agent in continuous conversation keeps the
// inbound signal permanently fresh, which would defer its rollover forever and
// recreate exactly the ENG-7548 defect the forced path exists to fix. After this
// long the restart proceeds regardless.
export const DAY_ROLLOVER_INBOUND_DEFER_MAX_MIN = 10;

interface PersistentSessionResult {
  decision: PersistentSessionDecision;
  /** ENG-10335: only on a `blocked-on-prompt` verdict, the decision the tick really made. */
  underlyingDecision?: string;
  /** True when `startPersistentSession` was called this tick. */
  spawnAttempted: boolean;
  /** Session health observed AFTER the tick's work — what the next tick will see. */
  sessionHealthyAfter: boolean;
  /** Optional human-readable suffix for the decision log line. */
  detail?: string;
}

/**
 * ENG-5116 — once an agent has been unhealthy without a respawn for
 * `threshold` consecutive ticks, emit a loud WARN exactly once until
 * the streak breaks. See `persistent-session-stuck-tracker.ts`.
 */
const persistentSessionStuckTracker = new PersistentSessionStuckTracker();

/**
 * Per-agent fingerprint of the Claude Code auth config used to spawn the
 * current session: `"<mode>:<fingerprint ?? 'none'>"`. When this changes
 * between polls (operator toggled subscription→api_key, or rotated the key),
 * the session is stopped so the next cycle respawns it with fresh auth.
 */
const claudeAuthTupleBySession = new Map<string, string>();

/**
 * ENG-10113: the policy-resolved `ANTHROPIC_MODEL` the current session was
 * launched with, so a later poll can tell a model-CHANGING policy edit from a
 * cosmetic one and announce the first before taking the session away.
 *
 * It has to be recorded, not derived. The auth tuple encodes only
 * `<policyId>@<revision>[/role]` — and must keep doing so, since the byte-for-byte
 * stability of its primary form is what stops a deploy restarting the whole
 * policed fleet — so an operator editing a policy's own model slot produces
 * `<id>@5 → <id>@6`, indistinguishable from a description edit. The model is the
 * only thing that separates them.
 *
 * Its lifecycle is deliberately the auth tuple's, site for site: set where that is
 * set, deleted where that is deleted. A missed delete leaves a stale model behind
 * and the next poll announces a change that did not happen — a notice that is
 * worse than the silence it replaces, because it is confidently wrong.
 *
 * Absent (rather than empty-string) whenever no policy model applies: no policy,
 * a policy that maps no model for its active role, or OpenRouter mode. See
 * `policyResolvedModel`.
 */
const modelPolicyModelBySession = new Map<string, string>();

/**
 * ENG-10156: the model policy id the current session was launched under, so a
 * model-API error reported for this agent can be bound to the policy that was
 * actually in force when it happened.
 *
 * It has to be RECORDED at issuance for the same reason the model above does,
 * and one sharper one. `POST /host/model-api-error-events` accepts a
 * client-supplied `occurred_at`, so an event can be reported minutes after the
 * fact; resolving the policy at report time would attribute a delayed event to
 * whatever policy is current — and the policy change most likely to have
 * happened in between is a failover. The misattribution would land exactly on
 * the events the failover trigger reads.
 *
 * Set only when the policy actually SHAPED the session, which is why it is
 * stamped from `currentPolicyModel`'s presence rather than from
 * `modelPolicy?.policyId` alone. `policyResolvedModel` withholds a model on
 * four conditions the policy map still answers for — no active binding,
 * OpenRouter mode, a `blocked` credential resolution, and a role naming no env
 * slot — and in every one of them the agent is not running on this policy's
 * transport, so its errors are not evidence about that policy.
 */
const modelPolicyIdBySession = new Map<string, string>();

/**
 * Stamp both per-session policy baselines together.
 *
 * They are written as a PAIR, in one function, deliberately. Both have the auth
 * tuple's lifecycle — set where it is set, cleared where it is cleared — and
 * ENG-10113's docstring already warns what a missed site costs: a stale value
 * makes the next poll announce a change that did not happen. Two maps updated
 * at four sites each is eight chances to drift; one function is none, and a new
 * site physically cannot update one without the other.
 *
 * `delete` rather than an empty string when a value does not apply — "no policy
 * model applies" and "the policy applies the empty model" are different states.
 */
function recordSessionPolicyBaseline(
  codeName: string,
  policyModel: string | undefined,
  policyId: string | null,
): void {
  if (policyModel) modelPolicyModelBySession.set(codeName, policyModel);
  else modelPolicyModelBySession.delete(codeName);
  if (policyId) modelPolicyIdBySession.set(codeName, policyId);
  else modelPolicyIdBySession.delete(codeName);
}

/** Clear both baselines. Called wherever the auth tuple is cleared. */
function clearSessionPolicyBaseline(codeName: string): void {
  modelPolicyModelBySession.delete(codeName);
  modelPolicyIdBySession.delete(codeName);
}

// ENG-8208: the ENG-8202 account-change hook moved to `agent-serving-probe.ts` as
// `noteClaudeAccountChange`. It used to clear the usage-limit marker; with the
// marker deleted it records an evidence floor on the transcript probe instead.
// Same rule ("a cap belongs to the account that hit it"), applied to the input
// that replaced the marker — see the comment on the floor map.

/**
 * ENG-8093: the opencode counterpart — a non-secret fingerprint of the PROVIDER
 * credentials the live `opencode serve` was spawned with. When it changes between
 * polls the serve is stopped and respawned so the rotated key actually takes
 * effect; without it a healthy serve holds its original key forever and 401s on
 * every turn while all monitoring stays green.
 *
 * In-memory is sufficient (no disk persistence needed): the boot reaper
 * `killAllAgtTmuxSessions` kills every `agt-*` session — `agt-oc-*` included —
 * before the first poll cycle, so a fresh manager never inherits a live serve
 * whose credentials this map has forgotten.
 */
const opencodeCredentialFingerprintBySession = new Map<string, string>();

/**
 * ENG-6579: last-applied egress allowlist (sorted domains) per session. Lets a
 * healthy tick detect when an agent's trusted allowlist changed (operator edited
 * TOOLS.md allowlist_domains) and apply it live - SIGHUP for a purely additive
 * change (zero disruption), or a full sidecar restart when a domain was removed
 * (SIGHUP would leave an in-flight tunnel to the removed domain alive).
 */
const egressAllowlistBySession = new Map<string, string[]>();

/**
 * ENG-6579: derive the trusted egress allowlist from control-plane tools content.
 *
 * ENG-9483 slice 2: plus the gateway endpoint(s) of the agent's model policy.
 * `policy` is a REQUIRED parameter rather than an optional one because both call
 * sites matter and they are 165 lines apart — the spawn-time write and the
 * live-refresh comparison. An optional parameter would let the live path keep
 * computing a policy-free allowlist, so assigning a policy to a running agent
 * would look applied and then be revoked by the next healthy tick's diff.
 */
function deriveEgressAllowlist(
  toolsRaw: string | undefined,
  policy: ManagerModelPolicy | null,
): string[] {
  return buildEgressAllowlist(
    toolsRaw ? (extractFrontmatter(toolsRaw).frontmatter as EgressToolsFrontmatter) : null,
    modelPolicyEgressDomains(policy),
  );
}

/** Both inputs are sorted (buildEgressAllowlist sorts), so an index compare suffices. */
const egressAllowlistEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((d, i) => d === b[i]);

/**
 * ADR-0047: the opencode-runtime arm of ensurePersistentSession. opencode agents
 * are supervised as a headless `opencode serve` process (opencode-session.ts)
 * driven over HTTP, not a claude tmux pane, so they take an entirely separate
 * path: their own tmux namespace (`agt-oc-<code>`), HTTP health, and a
 * provider-key-only env (no Anthropic subscription credential - the ADR-0046
 * driver).
 *
 * Scope: spawn + health-supervise, plus the Slack + Telegram ingest loops for a
 * live server (see ensureOpencodeSlackIngest / ensureOpencodeTelegramIngest).
 * Scheduled tasks fire from processAgent via syncAndCheckOpencodeScheduler
 * (ENG-7878), which injects due prompts into this server over HTTP. Only
 * whatsapp inbound stays unwired - a spawned opencode agent runs, stays
 * supervised, fires its schedules, and can take Slack, Telegram + direct-chat
 * traffic.
 */
async function ensureOpencodeRuntime(
  agent: { agent_id: string; code_name: string },
  refreshData: Record<string, unknown>,
  agentTimezone: string | null,
): Promise<PersistentSessionResult> {
  const codeName = agent.code_name;

  // Provider key: the per-agent OpenRouter key from /host/refresh is the
  // documented token source for opencode (managed gateway -> opencode, ADR-0046).
  // ENG-7993: an api_key-mode host also delivers its host-level direct-Anthropic
  // key here (refreshData.anthropic), which we inject as ANTHROPIC_API_KEY so an
  // opencode agent can run a claude-* model (buildOpencodeConfig emits
  // {env:ANTHROPIC_API_KEY} for anthropic/*). The launcher also forwards the
  // manager's own process.env into the serve process, so an operator-set
  // host-level key (OPENROUTER_API_KEY / ANTHROPIC_API_KEY /
  // VERCEL_AI_GATEWAY_API_KEY) still flows through even when not named here; a
  // named key from /host/refresh wins (highest-precedence serveEnv layer).
  //
  // ENG-8093: parsed BEFORE the healthy early-return, because the rotation check
  // below needs it. Previously all of this sat after the return, which is exactly
  // why a rotated key never reached a healthy serve.
  const openRouterRaw = (refreshData as {
    openrouter?: { auth_token?: string; fingerprint?: string } | null;
  }).openrouter ?? null;
  const anthropicRaw = (refreshData as {
    anthropic?: { api_key?: string } | null;
  }).anthropic ?? null;

  const credentialFingerprint = opencodeCredentialFingerprint({
    openRouterToken: openRouterRaw?.auth_token,
    openRouterFingerprint: openRouterRaw?.fingerprint,
    anthropicKey: anthropicRaw?.api_key,
  });

  if (isOpencodeSessionHealthy(codeName)) {
    // ENG-8093: a healthy serve holds whatever credential it was SPAWNED with —
    // serveEnv is baked into the tmux process env and never re-read — so a
    // rotation is invisible to it. Symptom: a 401 on every turn while every
    // health signal stays green, because the process is up and only the model
    // call fails.
    //
    // Restart only on a real change. A respawn drops the in-memory conversation
    // map (ENG-7975), so restarting on every refresh that re-delivered the same
    // key would be a steady, silent context loss. Both credential sources are
    // stable reads (a stored OpenRouter key; a decrypt of stored ciphertext), NOT
    // per-poll re-mints like the GitHub App token that ENG-7541 had to special-
    // case, so an unchanged credential genuinely yields an unchanged fingerprint.
    //
    // Only fingerprints are logged — never the values (manager.log is persisted).
    // ENG-8093 / CodeRabbit #3729: NOT a plain inequality. /host/refresh returns
    // `openrouter: null` / `anthropic: null` both when a credential is genuinely
    // absent AND when its resolution threw (both catch blocks log-and-null), so a
    // transient backend fault is indistinguishable from a removal on the wire. A
    // naive compare would respawn a healthy serve on every flap. See
    // shouldRestartForCredentialChange for the per-slot rule.
    const recorded = opencodeCredentialFingerprintBySession.get(codeName);
    if (recorded && shouldRestartForCredentialChange(recorded, credentialFingerprint)) {
      log(
        `[opencode-session] Provider credential rotated for '${codeName}' ` +
          `(${recorded} → ${credentialFingerprint}) — restarting serve`,
      );
      // Teardown via stopOpencodeSession, not a raw tmux kill: it also flips the
      // in-memory session to 'stopped', without which startOpencodeSession's
      // already-running short-circuit refuses to respawn (ENG-8000). Falls through
      // to the spawn path below, so the fresh credential applies this same tick.
      stopOpencodeSession(codeName, log);
      opencodeCredentialFingerprintBySession.delete(codeName);
    } else {
      // Idempotent and cheap when the config is unchanged. This is the ONLY place
      // ingest starts: it needs a live server to inject into, and Socket Mode
      // pre-acks (a ~3s window vs a full agent turn), so an event taken before the
      // server can answer is lost rather than redelivered. Starting only once
      // healthy costs at most one poll interval of delay after a spawn and never
      // opens a socket that can only decline.
      await ensureOpencodeSlackIngest({ codeName, agentId: agent.agent_id, log });
      await ensureOpencodeTelegramIngest({ codeName, agentId: agent.agent_id, log });
      // First observation of an adopted-healthy serve stamps the baseline only.
      // Reachable in practice only if a serve outlives the boot reaper, which
      // kills every `agt-*` session (agt-oc-* included) before the first poll.
      if (!recorded) opencodeCredentialFingerprintBySession.set(codeName, credentialFingerprint);
      return { decision: 'healthy', spawnAttempted: false, sessionHealthyAfter: true };
    }
  }

  // No live server to inject into — either it was never healthy, or ENG-8093 just
  // stopped it for a credential rotation. Tear down any ingest bound to it. A
  // respawn does not need one (injectOpencodeMessage resolves the live port per
  // call), but a crashed / stopped agent must not keep answering Slack or
  // Telegram — and on the rotation path that matters twice over, since the reason
  // we are here is that the old credential no longer works.
  stopOpencodeSlackIngest(codeName, log);
  stopOpencodeTelegramIngest(codeName, log);

  // opencode reads its config from the cwd it serves in. The manager writes every
  // framework's provisioned artifacts (opencode.json, AGENTS.md, CHARTER.md,
  // TOOLS.md) to `~/.augmented/<code>/provision/`, so serve from there. Note this
  // is NOT `<agentDir>/project` (which only the adapter's standalone writeConfig
  // targets, and the manager provisioning path never populates), and NOT the
  // claude-code `psGetProjectDir`. A missing cwd here also surfaces as a
  // misleading `spawn tmux ENOENT` (child_process throws on a nonexistent cwd).
  const opencodeProjectDir = join(getFramework('opencode').getAgentDir(codeName), 'provision');

  const serveEnv: Record<string, string | undefined> = {
    AGT_HOST: requireHost(),
    AGT_API_KEY: getApiKey() ?? undefined,
    AGT_APP_URL: process.env['AGT_APP_URL'],
    // ENG-8344 follow-up: framework parity with the claude-code spawn env.
    // AGT_HOST/AGT_API_KEY are host-wide, so without the agent's own id any
    // program reading `process.env.AGT_AGENT_ID` under opencode sees empty —
    // same failure the GitHub credential shim hit on the bare claude-code path.
    AGT_AGENT_ID: agent.agent_id,
  };
  if (openRouterRaw?.auth_token) serveEnv['OPENROUTER_API_KEY'] = openRouterRaw.auth_token;
  if (anthropicRaw?.api_key) serveEnv['ANTHROPIC_API_KEY'] = anthropicRaw.api_key;

  // ENG-7995: `startOpencodeSession` does not always reach `spawnServe` — the
  // crash-backoff returns the existing session untouched. Both the success and
  // the crash paths re-stamp `startedAt`, so a change in it is the launcher's
  // honest "I re-armed this session" signal.
  const startedAtBefore = getOpencodeSessionState(codeName)?.startedAt ?? null;

  const session = await startOpencodeSession({
    codeName,
    agentId: agent.agent_id,
    projectDir: opencodeProjectDir,
    serveEnv,
    agentTimezone,
    // ADR-0047: the opencode container launcher is not built, so the launcher
    // refuses to spawn under Docker isolation rather than run unisolated on a
    // multi-tenant host. Single-agent / dedicated hosts (isolation off) run bare.
    isolated: isolationMode(codeName) === 'docker',
    log,
  });

  agentState.persistentSessionAgents.add(codeName);

  // Report `spawnAttempted` truthfully. It is not just a log field: the stuck
  // tracker treats it as a recovery signal and RESETS the consecutive-stuck
  // streak on it (persistent-session-stuck-tracker.ts), so hardcoding `true`
  // disarmed the very detector meant to surface a stuck agent — which is how
  // nora's 2026-07-22 crash-loop ran ~10 minutes without a single stuck warning.
  // (An ADR-0047 isolation refusal also stamps `startedAt` and so still reports
  // true; that path logs loudly on every cycle in its own right.)
  const spawnAttempted = session.startedAt !== startedAtBefore;

  if (session.status === 'crashed') {
    // ENG-8093: deliberately do NOT stamp the fingerprint on a crash. The serve
    // is not running these credentials, so recording them would mean a later
    // rotation compares against a baseline that never took effect. Leaving it
    // unset makes the next successful spawn establish the truth.
    const tail = session.lastFailureTail ?? readOpencodePaneLogTail(codeName);
    return {
      decision: 'spawn',
      spawnAttempted,
      sessionHealthyAfter: isOpencodeSessionHealthy(codeName),
      detail: tail ? `opencode spawn failed: ${tail.slice(0, 200)}` : 'opencode spawn failed',
    };
  }

  // ENG-8093: record what this serve is actually running, so the next poll can
  // tell a rotation from a re-delivery of the same key.
  opencodeCredentialFingerprintBySession.set(codeName, credentialFingerprint);

  return {
    decision: 'spawn',
    spawnAttempted,
    sessionHealthyAfter: isOpencodeSessionHealthy(codeName),
    detail: `opencode serve on 127.0.0.1:${session.port ?? '?'}`,
  };
}

/**
 * ENG-10335: the one entry point every caller uses (the slow poll and both fast
 * respawn lanes), so all of them publish a verdict corrected for a blocked
 * startup prompt.
 *
 * `isSessionHealthy()` reads a session parked on the login picker as healthy
 * (tmux exists, claude is alive), and the unchecked function below derives its
 * `sessionHealthyAfter` from it. Correcting here, once, rather than at each of
 * its ~15 return sites, means a new return path cannot forget, and it lands
 * before the decision log line, recordSpawnOutcome and the stuck tracker.
 */
async function ensurePersistentSession(
  ...args: Parameters<typeof ensurePersistentSessionUnchecked>
): Promise<PersistentSessionResult> {
  const result = await ensurePersistentSessionUnchecked(...args);
  // Only a healthy verdict can be wrong in this direction; skip the lookup otherwise.
  const blocked = result.sessionHealthyAfter ? sessionBlockedOnPrompt(args[0].code_name) : null;
  return applyBlockedPromptVerdict(result, blocked);
}

async function ensurePersistentSessionUnchecked(
  agent: { agent_id: string; code_name: string; display_name: string },
  tasks: Array<Record<string, unknown>>,
  // ENG-5769: source coords flow through for the kanban-work nudge.
  boardItems: Array<{ id: string; title: string; status: string; priority: number; estimated_minutes?: number; deliverable?: string; result?: string; source_integration?: string | null; source_external_id?: string | null; source_url?: string | null }>,
  refreshData: Record<string, unknown>,
): Promise<PersistentSessionResult> {
  const codeName = agent.code_name;

  // ENG-7891 / ADR-0049 slice 3: attempt the dark id-keyed migration for this
  // agent BEFORE the spawn cwd is resolved, and ONLY when the session is not
  // running — so the agent's in-session channel MCP writers are quiesced by
  // construction. Ships dark: a no-op for every agent until the per-agent
  // id-keyed-layout-migration flag is armed (after a test-host rehearsal). It is
  // best-effort/non-throwing and never blocks the spawn. Running it here means
  // psGetProjectDir() below resolves through the freshly-created codename symlink
  // to the id-keyed path, and the funnel's later provisionIsolationHook() call
  // (which runs before spawn) lands id-aware hooks in the migrated dir.
  // ADR-0049 §5 scopes the opencode adapter OUT of the id-keyed layout: it keeps
  // its own codename-keyed agentDir (no symlink-aware resolveRealAgentPath) and
  // has no Claude transcript store to move. The opencode fork returns further down
  // in this function, so gate on the cached framework here (populated by
  // processAgent earlier this tick) to keep the migration Claude-Code-only.
  if (
    (agentFrameworkCache.get(codeName) ?? DEFAULT_FRAMEWORK) === 'claude-code' &&
    !isSessionHealthy(codeName)
  ) {
    maybeMigrateAgentToIdKeyedLayout(agent, {
      flagStore: hostFlagStore(),
      channelConfigs:
        (refreshData['channel_configs'] as Record<
          string,
          { config: unknown; status: string }
        > | null) ?? null,
      log,
    });
  }

  const projectDir = psGetProjectDir(codeName);
  const mcpConfigPath = join(projectDir, '.mcp.json');
  const claudeMdPath = join(projectDir, 'CLAUDE.md');

  // ENG-5441: circuit breaker — if this agent's session restart count
  // crossed the threshold inside the sliding window, refuse to spawn
  // until an operator clears the trip (currently: webapp resume / agt
  // agent resume flips the agent's status from paused → active, which
  // the breaker observes on the next poll via clearBreakerOnResume).
  if (restartBreaker.isTripped(codeName)) {
    const trip = restartBreaker.getTrip(codeName)!;
    return {
      decision: 'circuit-tripped',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: trip.statusMessage,
    };
  }

  // ENG-9125: MCP-asset readiness gate. `~/.augmented/_mcp` is a single
  // host-shared directory bind-mounted read-only into every agent container,
  // and it is populated by deployMcpAssets() — which can complete AFTER the
  // containers start. On agt-aws-1 (2026-08-18) containers came up at 08:18 and
  // the bundles did not land until 08:43; every agent that spawned in that
  // window came up with zero MCP tools, mcp-presence-reaper restart-looped them
  // (restarting is its only remedy, and it cannot populate a mount), and the
  // crash tally tripped the breaker on the third restart. 13 of 16 agents were
  // paused across five teams, and a crash-class trip is never auto-resumed, so
  // they stayed paused until a human acted.
  //
  // Deferring here costs one poll interval and is self-healing. Spawning into an
  // empty mount costs a human. So: if any declared `node` bundle inside the
  // shared mount is absent, skip the spawn WITHOUT recording a restart event —
  // the breaker can then never trip on a slow mount.
  //
  // Deliberately narrow, so the normal case is untouched:
  //   • only when the session is NOT already healthy — a running agent is never
  //     disturbed by this gate, matching the spawn path's existing "never brick
  //     a running agent" stance (persistent-session.ts);
  //   • only for claude-code agents — the opencode adapter forks further down
  //     and does not use these bundles;
  //   • only for `node <path-inside-_mcp>` entries — npx and remote servers have
  //     their own failure modes and the reaper's own cold-start grace;
  //   • fail-open on an unreadable/malformed .mcp.json (see mcp-assets-ready.ts).
  if (
    (agentFrameworkCache.get(codeName) ?? DEFAULT_FRAMEWORK) === 'claude-code' &&
    !isSessionHealthy(codeName)
  ) {
    const missingMcpBundles = findMissingMcpBundles(mcpConfigPath);
    if (missingMcpBundles.length > 0) {
      const detail = formatMissingMcpBundles(missingMcpBundles);
      log(
        `[persistent-session] '${codeName}': deferring spawn — ${detail}. ` +
          'Not counted as a restart; will spawn once the host deploys them.',
      );
      return {
        decision: 'skipped-mcp-assets-not-ready',
        spawnAttempted: false,
        sessionHealthyAfter: false,
        detail,
      };
    }
  }

  // ENG-5371 / ENG-6695: resolve the agent's IANA timezone used to scope the
  // daily-session day rollover so a fresh conversation starts at the agent's
  // local midnight, not host UTC. Precedence matches the restart gate:
  // agent.timezone → team (teams.settings.timezone) → org-effective (the cached
  // maintenance-window tz, host → org → region). `undefined` keeps the original
  // ENG-4642 host-local behaviour, preserved here for the case where NO tier is
  // configured so teams/agents that never set a timezone are unchanged.
  const teamSettingsForTz = (
    refreshData.team as { settings?: Record<string, unknown> } | undefined
  )?.settings;
  const agentTimezone = (() => {
    const ownTzRaw = (refreshData.agent as { timezone?: unknown } | undefined)?.timezone;
    const ownTz = typeof ownTzRaw === 'string' && ownTzRaw.trim() !== '' ? ownTzRaw.trim() : null;
    const tz = teamSettingsForTz?.['timezone'];
    const teamTz = typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : null;
    // ENG-8016: a lapsed override must not keep the session's clock pinned.
    const ownTzExpiresAtRaw = (refreshData.agent as { timezone_expires_at?: unknown } | undefined)
      ?.timezone_expires_at;
    const ownTzExpiresAt =
      typeof ownTzExpiresAtRaw === 'string' && ownTzExpiresAtRaw.trim() !== ''
        ? ownTzExpiresAtRaw
        : null;
    // ENG-8461: single resolver shared with the async direct-chat lazy-reset path,
    // so both compute the day boundary against the same clock. Precedence and the
    // host-local (undefined) fallback are unchanged from the prior inline logic.
    return resolveEffectiveAgentTimezone({
      agentTimezone: ownTz,
      teamTimezone: teamTz,
      agentTimezoneExpiresAt: ownTzExpiresAt,
    });
  })();

  // ADR-0047: opencode runtime fork. opencode agents are supervised as a
  // headless `opencode serve` process driven over HTTP, not a claude tmux pane,
  // so they bypass the entire Claude-shaped auth-exchange + tmux-health path
  // below. The circuit breaker and timezone resolution above are
  // framework-agnostic and already applied; everything after this point is
  // Claude Code specific. The framework was cached by processAgent earlier this
  // tick (from the authoritative /host/refresh agent record).
  const frameworkId = agentFrameworkCache.get(codeName) ?? DEFAULT_FRAMEWORK;
  if (frameworkId === 'opencode') {
    return ensureOpencodeRuntime(agent, refreshData, agentTimezone ?? null);
  }

  // Resolve channel plugins from agent's channel configs. ENG-6022: the
  // server-id gating lives in resolveChannelLaunchFlags (pure, unit-tested)
  // — the msteams channel shipped with this list hand-maintained inline and
  // missing the `server:msteams` case, so sessions never subscribed to the
  // channel pipeline even though the MCP was running.
  const channelConfigs = refreshData.channel_configs as Record<string, { config: unknown; status: string }> | null;
  const { devChannels, pluginChannels } = resolveChannelLaunchFlags(
    channelConfigs,
    channelQuarantineStore().getQuarantinedKeys(codeName),
  );
  const channels: string[] = [...pluginChannels];

  // Always add direct-chat channel for persistent sessions.
  // This is provisioned in step 5b of processAgent() into .mcp-channels.json.
  devChannels.push('server:direct-chat');

  // ENG-7152: OpenRouter BYO-model block from /host/refresh (null unless the
  // openrouter-mode flag is on for this org/host AND the agent has a stored
  // OpenRouter key). When present it SUPERSEDES the host's subscription/api_key
  // auth for THIS agent — the launcher points Claude Code at OpenRouter's native
  // Anthropic endpoint instead of the operator subscription. An older API that
  // doesn't send the field → undefined → normal auth (graceful degradation).
  const openRouterRaw = (refreshData as {
    openrouter?: {
      auth_token?: string;
      model?: string;
      small_fast_model?: string | null;
      fingerprint?: string;
    } | null;
  }).openrouter ?? null;
  const openRouterForAgent =
    openRouterRaw && openRouterRaw.auth_token && openRouterRaw.model
      ? {
          authToken: openRouterRaw.auth_token,
          model: openRouterRaw.model,
          smallFastModel: openRouterRaw.small_fast_model ?? null,
          fingerprint: openRouterRaw.fingerprint ?? null,
        }
      : null;

  // ENG-9483 slice 2 (ADR-0074): the resolved model policy, emitted by slice 1.
  //
  // Read from /host/refresh and NOT from the cached exchange, even though both
  // endpoints emit it. Refresh sees the agent, so it answers the full
  // `agent ?? host ?? org` chain; exchange authenticates a HOST and can only
  // answer `host ?? org`. Where the two disagree refresh is authoritative — a
  // per-agent assignment is invisible to exchange and would be silently
  // overridden by the host's policy for the ~50 minutes the exchange JWT is
  // cached. Slice 1's issue comment records this precedence; it is not
  // self-evident from the payloads, which look interchangeable.
  //
  // Null on an older API, a malformed block, or no assignment anywhere — which
  // is every agent in the fleet today — and null means every decision below is
  // byte-for-byte what it was before this change.
  const modelPolicy = parseModelPolicy(
    (refreshData as { model_policy?: unknown }).model_policy,
  );

  // Resolve auth mode + key from the exchange BEFORE any OAuth precheck —
  // otherwise api_key hosts get skipped for never having run `claude /login`
  // even though they don't need OAuth at all. The exchange is bounded rather
  // than cached for the JWT's full ~50m lifetime, so mode/key changes on the
  // webapp are picked up promptly (ENG-4417 rotation acceptance criterion).
  //
  // ENG-10093: `AUTH_EXCHANGE_MAX_AGE_MS`, not `forceRefresh: true`. This call
  // runs once per persistent agent per poll cycle, and the unbounded force made
  // `/host/exchange` the highest-volume endpoint on the whole API — 284,884
  // requests/day on 2026-09-05, 10.3% of everything, against a steady-state
  // need of ~1.2/hour/host. See the constant for what the bound costs.
  //
  // Fail-closed on error: silently falling back to 'subscription' is exactly
  // the confused-deputy path ENG-4417 removes. If /host/exchange rejects with
  // anthropic_key_decrypt_failed, the host is misconfigured — refuse to
  // spawn (or stop a running session) so the operator sees the problem
  // instead of a session quietly booting under the wrong auth.
  let claudeAuthMode: 'subscription' | 'api_key' | 'openrouter';
  let anthropicApiKey: string | null;
  let anthropicApiKeyFingerprint: string | null;
  // ADR-0042 pt3b (ENG-7607 / ENG-8992): the two inputs the SPAWN branch needs to
  // mint this agent's own org-scoped key. Captured here because the exchange is
  // where they are in scope; the mint itself deliberately happens at the spawn,
  // NOT on this per-poll path — see the mint site for why that distinction is
  // load-bearing rather than stylistic.
  let hostKind: string | null = null;
  let rawHostKey: string | null = null;
  try {
    const apiKey = getApiKey();
    if (!apiKey) {
      log(`[persistent-session] Skipping '${codeName}' — AGT_API_KEY not set`);
      return {
        decision: 'skipped-no-api-key',
        spawnAttempted: false,
        sessionHealthyAfter: isSessionHealthy(codeName),
      };
    }
    const exchange = await exchangeApiKey(apiKey, false, {
      maxAgeMs: AUTH_EXCHANGE_MAX_AGE_MS,
    });
    claudeAuthMode = exchange.claudeAuthMode;
    anthropicApiKey = exchange.anthropicApiKey;
    anthropicApiKeyFingerprint = exchange.anthropicApiKeyFingerprint;
    hostKind = exchange.hostKind;
    rawHostKey = apiKey;
  } catch (err) {
    const msg = (err as Error).message;
    // ENG-8260: the failure's CAUSE decides what happens to the session.
    // A 5xx/429/transport fault is "the platform API is unwell", which says
    // nothing about the credentials a healthy session already holds in its own
    // process env — tearing it down destroys in-flight work and buys nothing.
    // A verdict (401/403/revoked) still tears it down: the ENG-4417 fail-closed
    // posture. `decideAuthResolveFailureAction` is conservative — an
    // unclassifiable error is treated as a verdict, so the preserve branch only
    // fires on positive evidence of a transient fault.
    const healthyBefore = isSessionHealthy(codeName);
    const authFailure = decideAuthResolveFailureAction({ error: err, sessionHealthy: healthyBefore });
    const kind = exchangeFailureKind(err);

    if (authFailure.action === 'preserve-session') {
      log(
        `[persistent-session] Failed to resolve auth for '${codeName}': ${msg} — transient (${kind}), ` +
          `leaving the healthy session running; retrying next poll`,
      );
      // ASSERT the no-op actually preserved the session rather than assuming it.
      // A "skip the teardown" branch is precisely the silent-no-op shape that
      // reads as fixed while the session died for some other reason in the same
      // tick, so re-probe and report what is really true. If it did NOT survive,
      // fall through to the skip path so the decision line does not claim a
      // preservation that did not happen.
      const healthyAfter = isSessionHealthy(codeName);
      if (healthyAfter) {
        return {
          decision: 'skipped-auth-resolve-transient',
          spawnAttempted: false,
          sessionHealthyAfter: true,
          detail: `${authFailure.reason} (${kind}): ${msg}`,
        };
      }
      log(
        `[persistent-session] '${codeName}' session did NOT survive the transient auth failure ` +
          `(${kind}) — reporting as a skip, next poll respawns`,
      );
      return {
        decision: 'skipped-auth-resolve-failed',
        spawnAttempted: false,
        sessionHealthyAfter: false,
        detail: `transient-fault-session-lost (${kind}): ${msg}`,
      };
    }

    log(`[persistent-session] Failed to resolve auth for '${codeName}': ${msg} — refusing to spawn`);
    if (authFailure.action === 'stop-session') {
      // Stop any running session: the control plane has rejected these
      // credentials, so continuing is a silent-fallback bug waiting to happen.
      // Next successful poll respawns.
      // ENG-8260: close the interrupted runs as 'failed' with the real cause
      // rather than 'cancelled (deprovision)' — nothing about this teardown is a
      // deprovision, and an infra/credential fault must not be laundered into an
      // ordinary cancellation in the runs table.
      stopPersistentSessionAndForgetMcpBaseline(codeName, undefined, undefined, {
        outcome: 'failed',
        message: `auth resolve failed (${kind})`,
      });
      agentState.persistentSessionAgents.delete(codeName);
      claudeAuthTupleBySession.delete(codeName);
      clearSessionPolicyBaseline(codeName);
    }
    return {
      decision: 'skipped-auth-resolve-failed',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: `${authFailure.reason} (${kind}): ${msg}`,
    };
  }

  // OAuth precheck only applies in subscription mode. api_key hosts have
  // their credentials in the exchange response above — no `claude /login`
  // required — so don't block them on a missing OAuth token. Hosts in
  // api_key mode with no key would already have failed the exchange
  // (503 anthropic_key_decrypt_failed or host_metadata_unavailable).
  // ENG-7152: OpenRouter mode is host-level — when the host is in openrouter mode
  // EVERY agent runs against OpenRouter, so there's no subscription/api_key to
  // check. The per-agent OpenRouter key (delivered as openRouterForAgent) IS the
  // credential. Fail LOUD if the host is in openrouter mode but this agent has no
  // key or no resolvable model — never silently fall back to a subscription the
  // host doesn't have (it would auth-fail in tmux with no clear cause).
  if (claudeAuthMode === 'openrouter' || openRouterForAgent) {
    if (!openRouterForAgent) {
      log(
        `[persistent-session] Skipping '${codeName}' — host is in OpenRouter mode but this agent has no OpenRouter key or resolvable model. Provision one (Agent → OpenRouter key) and set an openrouter/ model.`,
      );
      return {
        decision: 'skipped-openrouter-no-key',
        spawnAttempted: false,
        sessionHealthyAfter: isSessionHealthy(codeName),
      };
    }
  } else if (claudeAuthMode === 'subscription') {
    if (!agentRuntimeAuthenticated) {
      // Re-check in case user logged in since last check
      agentRuntimeAuthenticated = await checkClaudeAuth();
      if (!agentRuntimeAuthenticated) {
        log(`[persistent-session] Skipping '${codeName}' — Claude Code not authenticated (subscription mode)`);
        return {
          decision: 'skipped-not-authenticated',
          spawnAttempted: false,
          sessionHealthyAfter: isSessionHealthy(codeName),
        };
      }
    }
  } else if (!anthropicApiKey) {
    // Belt-and-braces: exchange should have returned a key when mode=api_key.
    // If it didn't, don't spawn — the session would just fail auth in tmux.
    log(`[persistent-session] Skipping '${codeName}' — api_key mode but no key returned from exchange`);
    return {
      decision: 'skipped-api-key-mode-no-key',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
    };
  }

  // ENG-5116: track which reason (if any) triggered a session stop on this
  // tick. Used at the end of the function to distinguish "we stopped and
  // also respawned" (decision=spawn) from the silent-bug case "we stopped
  // but didn't respawn this tick" (decision=restart-pending /
  // day-rollover-restart, with sessionHealthyAfter=true exposing the
  // false-positive that took Vigil down).
  let restartTrigger: 'auth-tuple' | 'day-rollover' | null = null;
  let dayRolloverDeferred = false;
  let dayRolloverForced = false;

  // Detect auth rotation: if the (mode, fingerprint) tuple differs from what
  // we last launched with, stop the session so it respawns with fresh auth
  // on the next poll. Fingerprint captures key rotation without exposing the
  // raw value. First launch (no recorded tuple) stamps the baseline only.
  // ENG-7152: in OpenRouter mode the live credential is the OpenRouter token +
  // model, not the host auth — key it on the non-secret fingerprint (falling back
  // to the model id) so a key rotation, a model change, or toggling the flag
  // on/off changes the tuple and restarts the session on the next poll.
  // ENG-8186: in subscription mode the tuple used to be the CONSTANT
  // `subscription:none` (anthropicApiKeyFingerprint is api_key-mode only), so an
  // operator resolving a Claude usage limit by logging into a different
  // claude.ai account changed nothing the manager watched — the running claude
  // kept the old capped credentials (they're only synced to /root at spawn) and
  // the ENG-7904 usage-limit marker kept every channel MCP answering with the
  // canned "hit its limit" notice instead of dispatching to the tmux pane. Adding
  // the signed-in account's fingerprint makes a re-login trip this existing
  // restart path; it's derived from oauthAccount, not from the tokens, so the
  // ~8-hourly access-token refresh does NOT churn it.
  const claudeAccountFingerprint =
    !openRouterForAgent && claudeAuthMode === 'subscription'
      ? await getClaudeAccountFingerprint()
      : null;
  // ENG-8202: a usage cap belongs to the ACCOUNT that hit it, so the manager's
  // cap gates are told to disregard evidence from before a re-login — exactly
  // here, when the signed-in account changes, and nowhere else. Without it the
  // previous account's refusals are still on disk and inside the probe window
  // after a re-login, and would read as a live cap on the new account.
  noteClaudeAccountChange(codeName, claudeAccountFingerprint, log);
  // ENG-9483 slice 2: the policy's `<id>@<revision>` rides as a NAMED suffix on
  // whichever shape the tuple takes, so a policy edit or reassignment respawns
  // the session on the next poll. `revision` and not a digest of the policy
  // object: the server bumps revision on an edit and only on an edit, so a
  // digest would respawn every agent on the host for an `updated_at` touch that
  // changed nothing an agent can see.
  //
  // The suffix is named rather than a fourth colon segment because
  // `parseAuthTuple` reads the claude shape POSITIONALLY and drops anything past
  // the third field — an appended `:<policy>` would restart the session while the
  // audit row read "no component differs (tuple string re-formatted)", which is
  // the ENG-8293 diagnosis gap reopened and disguised as a benign reformat.
  // Omitted entirely (not written as an empty marker) when there is no policy,
  // so an unpoliced fleet's tuple is unchanged and nothing respawns on deploy.
  //
  // The tuple is assembled by `buildAuthTuple`, not inline, so that the producer
  // and the parser are one testable pair. That is not tidiness: with it inline,
  // mutating this site to use a naive `:` append failed zero tests, because
  // every test built its own tuple string.
  const currentAuthTuple = buildAuthTuple({
    openRouterCredential: openRouterForAgent
      ? (openRouterForAgent.fingerprint ?? openRouterForAgent.model)
      : null,
    claudeAuthMode,
    anthropicApiKeyFingerprint,
    claudeAccountFingerprint,
    modelPolicyComponent: modelPolicyAuthComponent(modelPolicy),
  });
  // ENG-10113: computed from the SAME policy object the tuple was built from, via
  // the shared helper, so the value recorded here cannot drift from the model the
  // spawn env actually applies.
  const currentPolicyModel = policyResolvedModel({
    policy: modelPolicy,
    openRouterMode: Boolean(openRouterForAgent),
    // The same null the launcher config carries — see `modelPolicyGatewayToken`
    // at the spawn call site below, where its absence is deliberate until
    // ENG-9481 decides what a gateway credential is. Both sites must move
    // together: a token here and not there (or the reverse) resolves a gateway
    // binding `blocked` for one and honoured for the other, and the recorded
    // baseline would then describe an agent that does not exist.
    gatewayToken: null,
    codeName,
  });
  /**
   * ENG-10156: the policy id to stamp alongside the model.
   *
   * Gated on `currentPolicyModel` rather than read straight off `modelPolicy`,
   * and that is the whole point. The policy MAP says which policy was chosen;
   * `policyResolvedModel` says whether it actually shaped this session, and it
   * withholds a model on four conditions the map still answers for. On every
   * one of them the agent is running on something other than this policy's
   * transport, so binding its errors to the policy would make the failover
   * trigger read evidence about a configuration the agent never used.
   */
  const currentPolicyId = currentPolicyModel ? (modelPolicy?.policyId ?? null) : null;
  const recordedAuthTuple = claudeAuthTupleBySession.get(codeName);
  const recordedPolicyModel = modelPolicyModelBySession.get(codeName);
  if (recordedAuthTuple && recordedAuthTuple !== currentAuthTuple && isSessionHealthy(codeName)) {
    // ENG-8293: name WHICH component moved, not just that the tuple did. The
    // manager is the only place that holds both sides of the comparison, and it
    // used to discard the answer the instant it acted on it — leaving the
    // `agent.restart` audit row saying `reason: 'auth-tuple-change'` (a
    // restatement of `source`) and this log line as the sole diagnosis. Since
    // manager.log rotates within ~75 minutes, a restart wave older than that was
    // permanently unexplainable. The diff now rides to the audit row, which is
    // queryable, org-walled and retained.
    const authTupleDiff: AuthTupleDiff = diffAuthTuples(recordedAuthTuple, currentAuthTuple);
    log(
      `[persistent-session] Auth config changed for '${codeName}' (${recordedAuthTuple} → ${currentAuthTuple}) — ${authTupleDiff.summary} — restarting session`,
    );
    // ENG-9489: a failover changes which provider and model this agent IS,
    // mid-conversation, and this path has always stopped the session without a
    // word (unlike the model hot-reload a few thousand lines up, which
    // announces itself).
    //
    // ENG-10113 widens it to the case that sits beside a failover: a policy change
    // that holds the ROLE still and swaps the MODEL. That is the same event the
    // hot-reload path announces, so leaving it silent made the policy path the
    // quieter of the two for an identical consequence. Still null for every
    // auth-tuple change that alters neither — a key rotation, an account re-login,
    // a description-only policy edit — so the genuinely quiet cases stay quiet.
    //
    // BEFORE the stop, necessarily: `injectMessage` writes into the running
    // tmux session, so once it is killed there is nothing left to tell. The
    // await is bounded and only taken when the role or the model actually moved,
    // which is an operator-initiated event measured in ones per day, not per poll.
    const policyNotice = failoverRestartNotice({
      components: authTupleDiff.components,
      policy: modelPolicy,
      previousModel: recordedPolicyModel,
      currentModel: currentPolicyModel,
    });
    if (policyNotice && isSessionHealthy(codeName)) {
      // `injectMessageWithStatus`, not the boolean wrapper. Injection is tmux
      // send-keys, so `delivered` is STRUCTURALLY always false since acpx was
      // removed (ENG-6345) — the model hot-reload's `delivered ? 8s : 3s` a few
      // thousand lines up therefore always takes its 3s branch and always logs
      // "unconfirmed". Copying that shape into new code would be writing a
      // branch that cannot be taken. `fallbackUsed` is the real signal: the
      // keystroke landed in the pane.
      const status = await injectMessageWithStatus(
        codeName,
        'system',
        policyNotice.text + BACK_ONLINE_GREETING_GUIDANCE,
        // ENG-10113: the label follows the DECISION, not the call site. One
        // function now produces two events, and filing a model change under
        // `model-policy-failover` would put a wrong word in a record an operator
        // later reads back as evidence of a failover that never happened.
        { task_name: `model-policy-${policyNotice.reason}` },
        log,
      ).catch(() => ({ delivered: false, fallbackUsed: false }));
      if (status.fallbackUsed) {
        // COST, stated because this is a serial loop: `processAgent` is awaited
        // one agent at a time (`for (const agent of processOrder)`), so this
        // pause is paid per NOTIFIED agent and adds ~3s × N to the one cycle
        // that detects the change. Accepted rather than deferred: a
        // send-keys the session never gets to consume is a notice nobody
        // receives, and the alternative — moving this branch onto
        // `scheduleSessionRestart` — swaps the synchronous stop below for a
        // different restart mechanism (MCP baseline, audit payload,
        // `restartTrigger`) on a path every auth change takes. Staggering N
        // agents' respawns by 3s is also not a bad outcome for a fleet that is
        // failing over. Bounded, and only when the role or the model moved —
        // ENG-10113 widened WHICH changes qualify, not how often a poll can pay
        // this, since an unchanged policy still produces no notice at all.
        await new Promise((r) => setTimeout(r, 3_000));
      } else {
        log(
          `[model-policy] ${policyNotice.reason} notice not delivered to '${codeName}' — restarting anyway`,
        );
      }
    }
    // The two `undefined`s are gateReason and runClose. Passing undefined applies
    // their defaults (gateReason falls back to breakerReason, i.e.
    // 'auth-tuple-change'), so the gate and run-close behaviour are byte-for-byte
    // what they were before — only the audit payload is new.
    stopPersistentSessionAndForgetMcpBaseline(codeName, 'auth-tuple-change', undefined, undefined, {
      reason: authTupleDiff.summary,
      detail: {
        auth_tuple: {
          changed: authTupleDiff.changed,
          unchanged: authTupleDiff.unchanged,
          components: authTupleDiff.components,
        },
      },
    });
    agentState.persistentSessionAgents.delete(codeName);
    restartTrigger = 'auth-tuple';
  }

  // ENG-4642: scheduled day rollover. When the daily-session.json's
  // current.date no longer matches today's local date, the running
  // session is on yesterday's UUID. Kill it so the next pass mints a
  // fresh UUID via getOrCreateDailySession — but only when the agent
  // is between turns (JSONL mtime older than IDLE_THRESHOLD_S). If the
  // agent is mid-task, defer to the next tick rather than interrupt.
  // First-spawn agents (no current entry yet) and unhealthy sessions
  // (no tmux to kill) are no-ops.
  const staleForToday = isStaleForToday(codeName, new Date(), agentTimezone);
  // ENG-8461: self-heal — once any respawn (this rollover's lazy reset, or a
  // wedge/auth/self-update restart) has minted today's UUID, isStaleForToday
  // flips false. Drop any lingering defer flag so a later direct-chat/scheduled
  // trigger can't fire a spurious second reset.
  if (!staleForToday) pendingDayRolloverReset.delete(codeName);
  const rolloverSessionHealthy = isSessionHealthy(codeName);
  const rolloverCurrent =
    staleForToday && rolloverSessionHealthy ? peekCurrentSession(codeName) : null;
  // Compute the overdue measure only on the stale+healthy path so the common
  // not-stale poll pays nothing for the Intl format.
  const overdueMin = rolloverCurrent
    ? minutesSinceLocalMidnight(new Date(), agentTimezone)
    : 0;
  // ENG-8881: only consult the inbound signal when a forced restart is actually
  // on the table — this stats a directory per channel, and the overwhelmingly
  // common verdicts ('none', 'defer-idle') need none of it.
  const rolloverMayForce =
    staleForToday && rolloverSessionHealthy && rolloverCurrent !== null && overdueMin >= DAY_ROLLOVER_FORCE_GRACE_MIN;
  const rolloverInboundAgeSec = rolloverMayForce
    ? await channelInboundActivityAgeSecondsFor(codeName)
    : null;

  const rolloverInputs = {
    staleForToday,
    sessionHealthy: rolloverSessionHealthy,
    hasCurrent: rolloverCurrent !== null,
    idle: rolloverCurrent ? isAgentIdle(projectDir, rolloverCurrent.sessionId) : false,
    overdueMin,
    graceMin: DAY_ROLLOVER_FORCE_GRACE_MIN,
    recentInboundAgeSec: rolloverInboundAgeSec,
    inboundGraceSec: DAY_ROLLOVER_INBOUND_GRACE_SEC,
    inboundDeferredMin: inboundHeldMinutesFor(codeName),
    inboundDeferMaxMin: DAY_ROLLOVER_INBOUND_DEFER_MAX_MIN,
  };
  let rolloverDecision = decideDayRolloverAction(rolloverInputs);
  // ENG-10351: ask the device-state gate only when a teardown is actually on the
  // table, so the overwhelmingly common verdicts read no file.
  if (rolloverDecision === 'force-reset' && !deviceStateAllowsRollover(codeName, 'force-reset')) {
    rolloverDecision = decideDayRolloverAction({ ...rolloverInputs, deviceStateReady: false });
  }
  if (rolloverDecision === 'defer-idle' && rolloverCurrent) {
    // ENG-8461: do NOT eagerly tear down a healthy idle session at rollover. That
    // boot loads the full context only to print "Ready." and books the wall-clock
    // as utilization — 100% waste for an agent that never does work. Keep the warm
    // session and DEFER the fresh-session mint to the first real trigger
    // (direct-chat inbound / scheduled firing), which pays the boot alongside
    // actual work (performLazyDayRolloverReset). The 'force-reset' path below
    // (ENG-7548) still resets a bloated busy-yesterday agent; an idle agent has no
    // in-flight turn, and if it never works it correctly never boots. Log ONCE per
    // defer episode (membership-gated) so the every-poll re-evaluation while
    // parked doesn't spam manager.log.
    if (!pendingDayRolloverReset.has(codeName)) {
      pendingDayRolloverReset.add(codeName);
      log(
        `[persistent-session] Day rollover for '${codeName}' (yesterday=${rolloverCurrent.date}) — agent ` +
          `idle; keeping the warm session and deferring the fresh-session mint to the first real ` +
          `trigger (no boot booked as utilization) [ENG-8461]`,
      );
    }
  } else if (rolloverDecision === 'force-reset' && rolloverCurrent) {
    // ENG-7548: don't defer forever. A perpetually-busy agent never goes 60s-idle,
    // so the defer-idle branch would keep deferring and its context would never
    // reset (Paige at 99%). Once we're past the grace window, force the restart
    // even though the agent looks busy — a session pinned near the context limit
    // is worse than one interrupted turn.
    log(
      `[persistent-session] Day rollover FORCED for '${codeName}' (yesterday=${rolloverCurrent.date}) — agent busy-blocked ${overdueMin}m past the day boundary (grace ${DAY_ROLLOVER_FORCE_GRACE_MIN}m), restarting anyway to reset context`,
    );
    stopPersistentSessionAndForgetMcpBaseline(codeName, 'day-rollover');
    agentState.persistentSessionAgents.delete(codeName);
    // ENG-8461: a forced reset supersedes any pending lazy reset for this agent —
    // this teardown mints the fresh session, so drop the flag.
    pendingDayRolloverReset.delete(codeName);
    // ENG-8881: the rollover resolved; the next one starts its own hold episode.
    dayRolloverInboundHold.delete(codeName);
    restartTrigger = 'day-rollover';
    dayRolloverForced = true;
  } else if (rolloverDecision === 'inbound-wait' && rolloverCurrent) {
    // ENG-8881: hold the forced restart briefly — the agent was talking to a
    // human within the last DAY_ROLLOVER_INBOUND_GRACE_SEC, which is the window
    // where accepted-but-not-yet-carded work lives. Bounded by
    // DAY_ROLLOVER_INBOUND_DEFER_MAX_MIN so this cannot become an indefinite
    // defer (the ENG-7548 failure this branch's sibling exists to prevent).
    const priorHold = dayRolloverInboundHold.get(codeName);
    dayRolloverInboundHold.set(codeName, creditInboundHold(priorHold, Date.now()));
    if (!priorHold) {
      log(
        `[persistent-session] Day rollover for '${codeName}' HELD — channel inbound ` +
          `${rolloverInboundAgeSec}s ago (grace ${DAY_ROLLOVER_INBOUND_GRACE_SEC}s); not interrupting an agent ` +
          `mid-handoff, will force within ${DAY_ROLLOVER_INBOUND_DEFER_MAX_MIN}m regardless [ENG-8881]`,
      );
    }
    dayRolloverDeferred = true;
  } else if (rolloverDecision === 'defer-device-state' && rolloverCurrent) {
    // ENG-10351: the working session stays up and nothing is torn down. Logged
    // by deviceStateAllowsRollover once per episode; the heartbeat carries the
    // host-level verdict. Reported as deferred, not restarted.
    dayRolloverDeferred = true;
  } else if (rolloverDecision === 'busy-wait' && rolloverCurrent) {
    log(
      `[persistent-session] Day rollover for '${codeName}' deferred — agent still active on session ${rolloverCurrent.sessionId} (${overdueMin}m past boundary, will force at ${DAY_ROLLOVER_FORCE_GRACE_MIN}m)`,
    );
    dayRolloverDeferred = true;
  }

  // ENG-8881: end the hold episode whenever the gate is no longer the thing
  // holding this agent. Without this the start timestamp survives into the NEXT
  // rollover, where inboundHeldMinutesFor would immediately read hours and the
  // gate would never engage again — a guard that silently stops guarding.
  if (rolloverDecision !== 'inbound-wait') {
    dayRolloverInboundHold.delete(codeName);
  }

  // ENG-8340: rotate pane.log now — after the day-rollover teardown killed tmux
  // (releasing the pipe-pane `cat` fd) and before the respawn below reinstalls
  // the sink via setupPaneLog. This is the ONLY restart reason that rotates.
  // Every other path — auth-tuple, wedge, reaper, hot-reload, operator restart —
  // deliberately keeps appending to one file so the previous session's output
  // survives for the post-mortem tail (see rotatePaneLogForDayRollover).
  //
  // Gated on `restartTrigger` rather than placed inside the two day-rollover
  // branches so a teardown that DIDN'T happen can't rotate a live log: the
  // deferred branch never sets the trigger, and 'auth-tuple' is a different
  // value. Best-effort by construction — the helper swallows its own errors.
  if (restartTrigger === 'day-rollover') {
    rotatePaneLogForDayRollover(codeName, log, agentTimezone);
  }

  // ENG-5441 + CodeRabbit on PR #1302: re-check the breaker after the
  // auth-tuple-change and day-rollover blocks. Either of those can call
  // `recordRestartForBreaker` for the (max+1)th time *inside this tick*,
  // and without a re-gate the spawn block below would still respawn.
  // The initial gate at the top of this function catches *already-tripped*
  // breakers; this second check catches the case where this tick is the
  // one that trips it.
  if (restartBreaker.isTripped(codeName)) {
    const trip = restartBreaker.getTrip(codeName)!;
    return {
      decision: 'circuit-tripped',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: trip.statusMessage,
    };
  }

  // ENG-10001: copy the vendored Obi Wan review poster into the agent's dir
  // (idempotent). Delivery path per ENG-6233/ENG-6268 — a review tool that lives
  // only in a Claude Code plugin reaches no managed agent, because the fleet
  // installs no plugins.
  //
  // DELIBERATELY OUTSIDE the !isSessionHealthy branch below, unlike the hooks,
  // and the difference is not an oversight (CodeRabbit, #5731). A hook is read by
  // Claude Code when a session STARTS, so provisioning it on the spawn path is
  // the correct moment. The poster is invoked by the agent MID-session, so on the
  // spawn path a healthy long-lived agent would never receive it — or a repair to
  // it — until something happened to make its session unhealthy. Placing it here
  // costs one byte-compare of two small files per tick and removes that window.
  //
  // Delivery ONLY. Nothing TRIGGERS a review yet — that seam and the posting
  // gate are the remaining ENG-10001 items. What lands here is the poster.
  //
  // THE REVIEW SKILLS USED TO SHIP HERE TOO, AND NO LONGER DO (ENG-10374).
  // `pr-review` and `pr-review-sweep` were provisioned to every managed agent
  // against the ENG-10006 shape, in which an agent on the fleet ran the sweep.
  // ENG-10296 replaced that with a dedicated runner whose orchestration is code,
  // and `packages/obiwan-runner` carries its own calibrated brief (`brief.ts`)
  // rather than reading a skill body — so the two skills were provisioned
  // fleet-wide and invoked nowhere. ENG-10001's own conclusion: "shipping
  // pr-review into every managed agent, customers' included, is exposure with no
  // purpose."
  //
  // The poster is a DIFFERENT question and deliberately stays. Its justification
  // is in the header of `review-poster-asset.ts` and turns on it granting no
  // authority the agent lacks; that argument is unaffected by the runner, and
  // `scripts/obiwan-post.mjs` imports its primitives from the same asset.
  try {
    provisionReviewPoster(codeName);
  } catch (err) {
    log(`[persistent-session] Failed to provision review poster for '${codeName}': ${(err as Error).message}`);
  }

  // Start or maintain the persistent session
  if (!isSessionHealthy(codeName)) {
    if (agentState.persistentSessionAgents.has(codeName)) {
      // ENG-4659: include the actual claude error from the captured
      // pane log + a restart-cycle counter so silent restart loops are
      // visible at a glance. Run the recovery hook BEFORE the next
      // spawn so e.g. "Session ID already in use" rotates the UUID
      // instead of looping forever.
      //
      // CodeRabbit (PR #618): pane output is untrusted — it can echo
      // user prompts, tool output, secrets, etc. Per CLAUDE.md
      // ("Default logging to hash-only or redacted in prod; never log
      // raw secrets") we only embed the raw tail when the signature
      // is in a strict allowlist of known-safe Claude error messages.
      // For everything else we log only a sha256 prefix of the tail —
      // operators can correlate with the full file at
      // ~/.augmented/<codeName>/pane.log when they need it.
      const ctx = getLastFailureContext(codeName);
      const recovery = prepareForRespawn(codeName);
      const tailSummary = !ctx.tail
        ? ''
        : KNOWN_SAFE_TAIL_SIGNATURES.has(ctx.signature)
          ? `; last pane output (${PANE_TAIL_PREVIEW_LINES} of ~20 lines):\n${truncateForLog(ctx.tail)}`
          : `; pane_tail_hash=sha256:${createHash('sha256').update(ctx.tail).digest('hex').slice(0, 12)} (raw at ~/.augmented/${codeName}/pane.log)`;
      const sigSummary = ctx.signature !== 'unknown' ? `; signature=${ctx.signature}` : '';
      const recoverySummary = recovery ? `; recovery=${recovery}` : '';
      log(
        `[persistent-session] Session for '${codeName}' is unhealthy ` +
          `(restart #${ctx.restartCount}${sigSummary}${recoverySummary}), will restart${tailSummary}`,
      );
    }

    // ENG-5391: if this unhealthy detection was the dead-claude-in-live-tmux
    // case, ship an audit event so we can graph occurrences. Fire-and-forget —
    // a failed POST must not block the respawn that follows.
    const zombie = takeZombieDetection(codeName);
    if (zombie) {
      log(
        `[persistent-session] ZOMBIE DETECTED for '${codeName}' — tmux session ` +
          `${zombie.tmuxSession} alive but no claude process inside; killed shell, will respawn`,
      );
      getHostId()
        .then((hostId) => {
          if (!hostId) return;
          // CodeRabbit: pane output is untrusted (echoed user prompts,
          // tool output, secrets). Same redaction policy as the manager
          // log — ship a sha256 prefix the operator can correlate with
          // ~/.augmented/<codeName>/pane.log instead of the raw text.
          const paneTailHash = zombie.paneTail
            ? `sha256:${createHash('sha256').update(zombie.paneTail).digest('hex').slice(0, 12)}`
            : null;
          return api.post('/host/events', {
            host_id: hostId,
            agent_code_name: codeName,
            event_type: 'manager.agent_zombie_detected',
            payload: {
              agent_code_name: codeName,
              agent_id: agent.agent_id,
              tmux_session: zombie.tmuxSession,
              detected_at: new Date(zombie.detectedAt).toISOString(),
              pane_tail_hash: paneTailHash,
              pane_tail_redacted: zombie.paneTail !== null,
            },
            timestamp: new Date(zombie.detectedAt).toISOString(),
          });
        })
        .catch((err) => {
          log(`[persistent-session] Failed to forward zombie audit event for '${codeName}': ${(err as Error).message}`);
        });
    }

    // Provision Stop hook for result capture (idempotent)
    try {
      provisionStopHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision Stop hook for '${codeName}': ${(err as Error).message}`);
    }

    // Provision isolation hook to prevent cross-agent file access (idempotent).
    // ENG-7891 / ADR-0049: pass agent_id so the deny check allows the agent's
    // own id-keyed dir (~/.augmented/{agent_id}) as well as the codename dir.
    // Safe to pass unconditionally - the id is this agent's own unique UUID, so
    // for a legacy (codename-keyed) agent it names a dir that does not exist and
    // grants nothing extra, while keeping the hook correct after a migration.
    try {
      provisionIsolationHook(codeName, agent.agent_id);
    } catch (err) {
      log(`[persistent-session] Failed to provision isolation hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-5397: provision SessionStart orient hook (idempotent). Replaces
    // --resume's transcript continuity with a fresh-session + injected
    // orientation context model.
    try {
      provisionOrientHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision orient hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-6241: provision the PostToolUse auto-progress hook (idempotent).
    // ENG-6179 shipped this hook only inside claudecode-plugin-augmented, which
    // the managed fleet never installs, so kanban cards stayed frozen on
    // "Waiting for the agent's first update…". Installing it here — alongside
    // the Stop / isolation / orient hooks — is the actual delivery path.
    try {
      provisionAutoKanbanProgressHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision auto-progress hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-6567 Phase 2: provision the PostToolUse channel-progress heartbeat hook
    // (idempotent). Must run AFTER provisionAutoKanbanProgressHook — it appends
    // its command into the PostToolUse group the kanban hook owns. Writes a
    // local heartbeat the channel MCP reads to drive the live in-thread progress
    // indicator (flag-gated, ships dark).
    try {
      provisionChannelProgressHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision channel-progress hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-6233 / ENG-6268: provision the SessionStart session-state recorder
    // (idempotent). ENG-6233 shipped this hook only inside
    // claudecode-plugin-augmented, which the managed fleet never installs, so
    // `/status` replied "session state not recorded yet" on every agent.
    // Installing it here — alongside the Stop / isolation / orient / kanban
    // hooks — is the actual fleet delivery path.
    try {
      provisionSessionStateHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision session-state hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-7339: provision the PreCompact courtesy-notice hook (idempotent).
    // When the session compacts its context it goes quiet for a stretch; this
    // hook lets the channel MCP server tell the user the agent is briefly
    // reorganizing memory (dark behind the compaction-notice flag).
    try {
      provisionPreCompactHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision PreCompact hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-5051: mint a run UUID for the persistent session so Claude
    // Code's `${AGT_RUN_ID}` substitution in .mcp.json resolves to a
    // real value. Without this the cloud-broker MCP boots with the
    // literal `${AGT_RUN_ID}` placeholder, fails its startup guard,
    // and every aws_request_access 400s. Best-effort: if startRun
    // fails, we fall through to the legacy behaviour (placeholder
    // leaks; agent has to mint manually). Tied to the SESSION boot,
    // not per-tool-call — one run UUID covers everything inside this
    // long-running session, which matches the cloud-broker's
    // single-startup-guard semantics.
    const sessionRunResult = await startRun({
      agent_id: agent.agent_id,
      source_type: 'system',
      metadata: { type: 'persistent_session_boot' },
    });

    // ENG-8818: register the handle so the teardown lanes can close it. Before
    // this, nothing ever did: every restart, reboot and manager crash orphaned
    // one, and 73,744 had accumulated (19.3% of `runs`) with the oldest dating
    // to 2026-05-11. They never aged out either — `runs_retention_sweep()` is
    // advertised by the table comment but is not scheduled anywhere.
    //
    // This close is best-effort by construction: it cannot fire when the
    // manager is killed. The server-side backstop is the load-bearing half —
    // see the supersession rule in `stale-run-reaper.ts`, which closes any
    // system run that a later boot has superseded.
    trackSessionRun(codeName, sessionRunResult.run_id);

    // ENG-5631: resolve the agent's model (agent override → org → platform
    // default) and hand it to the launcher so it can pass `--model <alias>`.
    // Same resolution as provisioning/registration above (resolveModelChain).
    const spawnPrimaryModel =
      resolveModelChain(refreshData).primary ??
      ((refreshData.agent as Record<string, unknown> | undefined)?.['primary_model'] as
        | string
        | null
        | undefined) ??
      null;

    // ENG-6579: derive the egress allowlist from the TRUSTED control-plane tools
    // content. Must NOT read the agent's on-disk TOOLS.md - that lives in the
    // agent's read-write mount, so sourcing the containment allowlist from it
    // would let a compromised agent widen its own egress (CodeRabbit, PR #1528).
    const egressAllowlist = deriveEgressAllowlist(
      (refreshData.tools as { raw_content?: string } | undefined)?.raw_content,
      modelPolicy,
    );
    // Seed the live-refresh baseline so the first healthy tick doesn't redundantly
    // reload the sidecar for an allowlist that just got written by this spawn.
    egressAllowlistBySession.set(codeName, egressAllowlist);

    // ENG-10641: the agent's class sets its container memory ceiling (coding
    // agents get 6g). Read once here so the spawn and the live-sync baseline below
    // see the same value.
    const spawnAgentClass = (refreshData.agent as Record<string, unknown> | undefined)?.['agent_class'];

    // ADR-0042 pt3b (ENG-7607 / ENG-8992): mint this agent's OWN org-scoped key
    // and hand it to the launcher as its AGT_API_KEY, so one container's
    // credential cannot act as another agent. On a POOL host that is a cross-org
    // escalation (ENG-7433 attack-2); on a DEDICATED host it is a cross-agent
    // credential read — agent A naming agent B in the body of
    // POST /host/agent-integrations and receiving B's decrypted integration
    // credentials (ENG-8992). The raw host key authenticates the mint.
    //
    // Pool hosts always mint (mandatory, ENG-7489). Dedicated hosts mint only
    // when `per-agent-host-credentials` is armed for this host; unarmed, this
    // resolves null and the shared key is forwarded byte-for-byte as before.
    //
    // WHY HERE AND NOT IN THE PER-POLL AUTH BLOCK ABOVE: `POST /host/agent-key`
    // is revoke-then-create — it revokes every live key for (host, agent) before
    // inserting the new one. `ensurePersistentSession` runs on EVERY poll for
    // every agent (there is no early-return for a healthy session before the
    // exchange), so minting up there would revoke, once per poll, the very key
    // the running container is still holding, and hand the replacement to nobody
    // — the container's next /host/exchange would 401. A manager restart has the
    // same shape: persistent sessions survive it, so a mint on the poll path
    // would revoke out from under a container it never respawns. The key is only
    // ever NEEDED at spawn, so mint at spawn: this branch runs only when the
    // session is unhealthy and we are about to (re)launch it, which is exactly
    // when the container is about to read a fresh AGT_API_KEY.
    //
    // The revoke is scoped `.eq('agent_id', <this agent>)`, so it never touches
    // the manager's own host-wide key (agent_id NULL). That is what lets a
    // dedicated host arm this without the manager losing its own credential —
    // the bootstrap deadlock that blocked the pool rollout (ENG-9087).
    let perAgentApiKey: string | null = null;
    try {
      perAgentApiKey = await resolvePerAgentApiKey({
        hostKind,
        // Non-null on every path that reaches here: the auth block above returns
        // 'skipped-no-api-key' when getApiKey() is empty. `?? ''` keeps the types
        // honest without inventing a fallback credential — an empty key cannot
        // authenticate the mint, so it fails closed like any other bad key.
        rawHostKey: rawHostKey ?? '',
        agentId: agent.agent_id,
        dedicatedEnabled: hostFlagStore().getBoolean('per-agent-host-credentials'),
        mint: mintAgentKey,
      });
    } catch (err) {
      // Fail closed. Never fall back to the shared host-wide key: that is the
      // over-broad credential this slice exists to keep out of containers.
      const msg = err instanceof Error ? err.message : String(err);
      log(
        `[persistent-session] '${codeName}' (host_kind=${hostKind ?? 'dedicated'}): per-agent key mint ` +
          `FAILED (${msg}) — refusing to spawn rather than fall back to the shared host key`,
      );
      // startRun() + trackSessionRun() already ran above (~:14594/:14610), so
      // returning here without closing leaves that run OPEN. Repeated mint
      // failures would accumulate orphaned runs — and because the mint is
      // retried on every poll of an unhealthy session, "repeated" is the
      // expected case for a misconfigured host, not an edge case.
      // No-op when nothing is tracked, so it is safe on every path.
      closeSessionRunForCode(codeName, 'failed', 'per-agent key mint failed');
      return {
        decision: 'skipped-per-agent-key-mint-failed',
        spawnAttempted: false,
        sessionHealthyAfter: isSessionHealthy(codeName),
        detail: msg,
      };
    }
    if (perAgentApiKey) {
      log(
        `[persistent-session] '${codeName}' (host_kind=${hostKind ?? 'dedicated'}): injecting per-agent ` +
          `org-scoped key (ADR-0042 pt3b)`,
      );
    }

    startPersistentSession({
      codeName,
      agentId: agent.agent_id,
      projectDir,
      mcpConfigPath,
      claudeMdPath,
      channels,
      devChannels,
      apiHost: requireHost(),
      claudeAuthMode,
      anthropicApiKey,
      primaryModel: spawnPrimaryModel,
      agentClass: spawnAgentClass,
      // ENG-7152: when set, the launcher points Claude Code at OpenRouter's
      // native Anthropic endpoint for this agent (supersedes claudeAuthMode).
      openRouter: openRouterForAgent
        ? {
            authToken: openRouterForAgent.authToken,
            model: openRouterForAgent.model,
            smallFastModel: openRouterForAgent.smallFastModel,
          }
        : null,
      // ENG-9483 slice 2 (ADR-0074). The SHAPE reaches the launcher; the
      // credential does not. `modelPolicyGatewayToken` is deliberately absent
      // (⇒ null) rather than wired to some plausible existing secret: slice 1's
      // resolver never selects `model_policy_bindings.credential`, and ENG-9481's
      // stored-key-vs-pass-through answer decides whether the credential is a
      // `gateway_token` (needing ANTHROPIC_CUSTOM_HEADERS, an at-rest secret this
      // tree does not have) or a plain `api_key` (which does not). Until then a
      // gateway binding resolves to `blocked` and the agent keeps its existing
      // auth — see resolveBindingCredential, the one function that changes when
      // that question is answered.
      modelPolicy,
      runId: sessionRunResult.run_id,
      agentTimezone,
      // ENG-6476: materialize the host-side reply-routing flags into the agent
      // spawn env so the channel MCP + Stop hook (which can't call the flag
      // evaluator) see the values the operator set. The HostFlagStore resolves
      // them from the flags-cache the manager already maintains; the launcher
      // injects AGT_SLACK_REPLY_BINDING / AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED
      // unless an operator env override is already present (which keeps precedence).
      slackReplyBindingMode: hostFlagStore().getString('slack-reply-binding'),
      blockTurnEndAllMarkers: hostFlagStore().getBoolean('block-turn-end-all-markers'),
      // ENG-7493 (ADR-0044): materialize kanban-waiting-status into the spawn env
      // (AGT_KANBAN_WAITING_ENABLED) so the stdio kanban MCP exposes the `waiting`
      // option. Host-grained (HostFlagStore); the per-org API gate is authoritative.
      kanbanWaitingEnabled: hostFlagStore().getBoolean('kanban-waiting-status'),
      // ENG-7682 (notify Slice 1) + per-agent follow-up: materialize
      // notify-dispatch into the spawn env (AGT_NOTIFY_DISPATCH) so the Slack
      // channel MCP admits non-@mention channel messages in member channels
      // (membership mode). Resolved PER-AGENT: enabling the flag for one agent
      // (via an agent-grained override) no longer fans out to every agent on the
      // host. An agent with no per-agent override inherits the host-wide value;
      // the launcher still lets an operator AGT_NOTIFY_DISPATCH env override win.
      notifyDispatchMode: hostFlagStore().getStringForAgent('notify-dispatch', agent.agent_id),
      // ENG-8269: materialize wedge-transient-notice into the spawn env so the
      // channel MCPs can tell a waiting user their turn died on a transient
      // provider failure. Materialized rather than left to the flags-cache
      // because an isolated agent's container never mounts that cache — without
      // this the flip reaches nothing running under Docker isolation.
      turnFailureNoticeEnabled: hostFlagStore().getBoolean('wedge-transient-notice'),
      egressAllowlist,
      // ADR-0042 pt3b (ENG-7607 / ENG-8992): this agent's own org-scoped key
      // (null when unarmed). The spawn env carries it as this agent's AGT_API_KEY.
      perAgentApiKey,
      // ENG-9350 (epic ENG-9347): when `spawn-inject-channel-secrets` is ON for
      // this agent, deliver its channel bot tokens through the spawn env (`tmux
      // -e`) instead of the on-disk `.env.integrations` file (which the writer
      // above evicted). Undefined ⇒ file-sourced delivery, unchanged. Derived
      // from the same channel_configs the writer used, via the shared core helper
      // so the KEYS/VALUES can't diverge from what would have been persisted.
      channelSpawnSecrets: shouldInjectChannelSecrets(agent.agent_id)
        ? collectChannelSpawnSecrets(channelConfigs)
        : undefined,
      log,
    });
    agentState.persistentSessionAgents.add(codeName);
    // Record the tuple we launched with so future polls can detect rotation.
    claudeAuthTupleBySession.set(codeName, currentAuthTuple);
    // ENG-10113: and the policy model, so a later poll can tell a model-changing
    // policy edit from a cosmetic one. `delete` and not an empty string when there
    // is none — "no policy model applies" and "the policy applies the empty model"
    // are different states, and only the first must stay silent.
    recordSessionPolicyBaseline(codeName, currentPolicyModel, currentPolicyId);
    // ENG-7576 (PR 2): anchor the running-state baselines to the .mcp.json this
    // session was just spawned with (see seedSessionLaunchBaselines docstring).
    seedSessionLaunchBaselines(codeName, projectDir);
    // ENG-10641: the container was just started at this ceiling, so the first
    // healthy tick has nothing to inspect or apply.
    if (effectiveIsolationMode(codeName) === 'docker') {
      seedContainerMemory(codeName, resolveContainerMemory(spawnAgentClass));
    }
    return {
      decision: 'spawn',
      spawnAttempted: true,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: restartTrigger ? `trigger=${restartTrigger}` : undefined,
    };
  }

  // Session is healthy — reset restart counter.
  resetRestartCount(codeName);

  // ENG-6579: live egress refresh. When egress is enabled for this agent,
  // recompute the TRUSTED allowlist; if it changed since the last spawn/refresh
  // (operator edited TOOLS.md allowlist_domains), rewrite the host-only file and
  // SIGHUP the squid sidecar so the running session applies the new policy
  // without a respawn. No-op (and zero cost) when egress is off.
  if (egressMode(codeName) === 'allowlist') {
    const allowlist = deriveEgressAllowlist(
      (refreshData.tools as { raw_content?: string } | undefined)?.raw_content,
      modelPolicy,
    );
    const prev = egressAllowlistBySession.get(codeName);
    if (!prev || !egressAllowlistEqual(prev, allowlist)) {
      try {
        writeEgressAllowlist(codeName, allowlist);
        // If any domain was REMOVED, restart the sidecar so in-flight CONNECT
        // tunnels to the now-removed domain are severed (a SIGHUP reload would
        // leave them alive, defeating an incident-response tightening). A purely
        // additive change can SIGHUP - zero disruption to live tunnels.
        // Unknown previous baseline (e.g. manager restarted while the tmux
        // session survived) is treated as a narrowing: we can't prove nothing
        // was removed, so restart to sever any stale tunnel (deny-by-default).
        const removed = prev ? prev.some((d) => !allowlist.includes(d)) : true;
        const applied = removed ? restartEgressSidecar(codeName) : reloadEgressSidecar(codeName);
        if (applied) {
          // Advance the baseline ONLY when the policy actually reached a running
          // sidecar. If the sidecar isn't up yet (applied=false), the file is
          // written and the (re)spawn path will apply it - leave the baseline so
          // we retry next tick. try/catch keeps an egress hiccup from aborting
          // the rest of the tick (graceful degradation).
          egressAllowlistBySession.set(codeName, allowlist);
          log(
            `[persistent-session] egress allowlist for '${codeName}' changed ` +
              `(${allowlist.length} domains), ${removed ? 'restarted' : 'reloaded'} squid sidecar`,
          );
        } else {
          log(
            `[persistent-session] egress allowlist for '${codeName}' written ` +
              `(${allowlist.length} domains) but sidecar not running; will apply on next tick/spawn`,
          );
        }
      } catch (err) {
        log(
          `[persistent-session] egress allowlist refresh for '${codeName}' failed ` +
            `(will retry next tick): ${(err as Error).message}`,
        );
      }
    }
  }

  // ENG-10641: live memory ceiling. A class change (e.g. promoting an agent to
  // `advanced` so it can run a typecheck) resizes the running container with
  // `docker update` instead of waiting for a respawn that would drop its channels.
  // Raises only, fail-open, logs once per distinct outcome - see
  // syncContainerMemory. After a manager restart the baseline is unknown and is
  // read back from the container, never assumed.
  if (effectiveIsolationMode(codeName) === 'docker') {
    syncContainerMemory(
      codeName,
      (refreshData.agent as Record<string, unknown> | undefined)?.['agent_class'],
      log,
    );
  }

  // Stamp the baseline tuple on first healthy observation (covers the
  // manager-restart case where the tmux session survived but our Map didn't).
  //
  // ENG-10113: the policy model is stamped under the SAME condition — keyed on the
  // tuple map, never on its own `has`. The model map is legitimately absent
  // whenever no policy model applies, so keying on it would re-stamp on every
  // single poll for every unpoliced agent. Stamping here inherits the tuple's
  // assumption that a surviving session was launched with what we resolve now,
  // which is a guess; it is the same guess the tuple already makes, and the
  // alternative — leaving it unset — would keep this agent silent through every
  // subsequent model change until it next happened to respawn.
  if (!claudeAuthTupleBySession.has(codeName)) {
    claudeAuthTupleBySession.set(codeName, currentAuthTuple);
    recordSessionPolicyBaseline(codeName, currentPolicyModel, currentPolicyId);
  }

  // Sync scheduler state from API before checking for ready tasks.
  // This ensures deleted/disabled tasks don't fire and new tasks are picked up.
  // The scheduleChanged guard in syncTasksToScheduler preserves nextFireAt.
  const stableTasksHash = createHash('sha256')
    .update(JSON.stringify(tasks))
    .digest('hex')
    .slice(0, 16);
  const prevHash = agentState.knownTasksHashes.get(agent.agent_id);
  if (stableTasksHash !== prevHash) {
    const taskInputs: SchedulerTaskInput[] = tasks.map((t) => buildSchedulerTaskInput(t));
    const schedulerState = syncTasksToScheduler(codeName, agent.agent_id, taskInputs);
    claudeSchedulerStates.set(codeName, schedulerState);
    agentState.knownTasksHashes.set(agent.agent_id, stableTasksHash);
    log(`[persistent-session] Tasks synced for '${codeName}' (${taskInputs.length} task(s))`);
  } else if (!claudeSchedulerStates.has(codeName)) {
    claudeSchedulerStates.set(codeName, loadSchedulerState(codeName));
  }

  // Inject scheduled tasks that are due
  const state = claudeSchedulerStates.get(codeName);
  if (state) {
    // ENG-4675: pass inFlightClaudeTasks so getReadyTasks filters out
    // tasks whose subprocess is still running. Without this, every
    // supervisor tick logged "N ready task(s)" + "Firing task ..." for
    // the same trigger, even though the in-flight guard below correctly
    // skipped the duplicate spawn.
    const ready = getReadyTasks(state, inFlightClaudeTasks);
    if (ready.length > 0) {
      log(`[persistent-session] ${ready.length} ready task(s) for '${codeName}': ${ready.map(t => `${t.name}(next=${t.nextFireAt ? new Date(t.nextFireAt).toISOString() : 'null'})`).join(', ')}`);
    }

    // ENG-8197: do not fire while the agent is at its Claude usage cap.
    //
    // This is the PERSISTENT-SESSION path — the one the prod fleet runs — and it
    // is the load-bearing half of the fix. Its twin in
    // manager/scheduler/execution.ts carries the same gate; both must have it or
    // the defect simply moves to whichever path is left ungated.
    //
    // Firing here is not merely a wasted turn. `fireScheduledTaskViaKanban` calls
    // `markTaskFired`, which recomputes `nextFireAt` from NOW — so a firing during
    // a cap CONSUMES the occurrence on behalf of an agent that never ran it. Ten
    // agents were capped on 2026-07-27 for ~16h; every occurrence inside that
    // window was spent that way, and ENG-7621 dedup made it look like a clean skip.
    //
    // Returning leaves `nextFireAt` in the past, so the task stays ready and fires
    // on the first tick after the cap lifts. Deliberately NOT a catch-up:
    // `getReadyTasks` yields one entry per task (not per missed occurrence), so a
    // recovering agent gets ONE firing rather than 16 hours of backlog at once.
    //
    // Fails OPEN — an unclassifiable transcript, or a cap with no parseable reset
    // instant, resolves to null and we fire as before.
    //
    // ENG-8208: reads the agent's transcript rather than the deleted pane-scraped
    // marker. The gate stays predictive by necessity — markTaskFired consumes the
    // occurrence at dispatch, so there is nothing left to save by the time a
    // refusal comes back.
    let deferReadyForUsageCap = false;
    if (ready.length > 0) {
      const schedLimitedUntil = readUsageCapUntil({
        codeName,
        projectDir: psGetProjectDir(codeName),
      });
      if (schedLimitedUntil) {
        // Edge-triggered. The deferral deliberately leaves nextFireAt in the
        // past so the occurrence is not consumed, which means getReadyTasks
        // keeps returning this task every tick — logging on the level would
        // repeat for the entire cap window.
        if (shouldLogUsageCapDeferral('persistent-scheduler', codeName, schedLimitedUntil)) {
          log(
            `[persistent-session] Deferring ${ready.length} ready task(s) for '${codeName}' — ` +
              `agent is at its Claude usage limit until ${schedLimitedUntil.toISOString()}; ` +
              `schedule NOT advanced, will fire after the limit resets ` +
              `(further deferrals this window are not logged)`,
          );
        }
        deferReadyForUsageCap = true;
      }
    }

    // ENG-8461: if this agent had an idle day-rollover DEFERRED (warm session
    // kept, fresh mint owed), a due task IS the first real trigger — mint the
    // fresh session now so the task runs on today's UUID rather than yesterday's
    // stale-dated session. Tear down and skip firing this tick; the teardown
    // leaves nextFireAt untouched (no markTaskFired), so the task stays ready and
    // fires on the fresh session the next tick after the ensure-session pass
    // respawns it. Same shape as the usage-cap deferral above (ENG-8197/8208):
    // make progress by respawning, never consume the occurrence. The helper is
    // idle-gated, so a mid-turn agent is not interrupted (it fires normally and
    // resets on a later idle trigger). Skipped when usage-capped — respawning a
    // capped agent to boot is wasteful and the task can't fire anyway; the reset
    // then rides the first firing after the cap lifts. Setting restartTrigger
    // keeps the ENG-5116 decision honest (day-rollover-restart, session down).
    let deferReadyForRollover = false;
    if (
      !deferReadyForUsageCap &&
      ready.length > 0 &&
      performLazyDayRolloverReset(codeName, agentTimezone)
    ) {
      restartTrigger = 'day-rollover';
      deferReadyForRollover = true;
      log(
        `[persistent-session] Deferring ${ready.length} ready task(s) for '${codeName}' — minting a ` +
          `fresh session after an idle day-rollover; schedule NOT advanced, task fires on the fresh ` +
          `session next tick [ENG-8461]`,
      );
    }

    // ENG-10335: do not fire into a session blocked on a prompt nothing can
    // answer (the login picker, an unanswerable usage-limit modal, an unclaimed
    // modal). We are inside the isSessionHealthy() branch, and that check passes
    // for such a session because claude is running. Firing would materialise a
    // card and nudge a session that cannot run a tool call, and markTaskFired
    // would consume the occurrence on its behalf. Same shape as the usage-cap
    // and rollover deferrals above: the schedule is NOT advanced, and because
    // getReadyTasks yields one entry per task, the agent gets one firing once the
    // prompt is answered, not a backlog. Fails open: an unreadable pane is not
    // blocked (see session-blocked-prompt.ts).
    let deferReadyForBlockedPrompt = false;
    if (!deferReadyForUsageCap && !deferReadyForRollover && ready.length > 0) {
      const blockedKind = sessionBlockedOnPrompt(codeName);
      if (blockedKind) {
        deferReadyForBlockedPrompt = true;
        if (shouldLogBlockedPrompt(codeName, 'scheduled-firing')) {
          log(
            `[persistent-session] Deferring ${ready.length} ready task(s) for '${codeName}' — session is ` +
              `blocked on an interactive prompt (${blockedKind}) and cannot run a tool call; schedule NOT ` +
              `advanced, will fire once the prompt is answered (further deferrals this episode are not logged) [ENG-10335]`,
          );
        }
      }
    }

    // Skip the firing loop only — NOT an early return. This function must still
    // fall through to compute and return its PersistentSessionDecision; bailing
    // out here would hand the caller `undefined` and lose the session verdict.
    for (const task of deferReadyForUsageCap || deferReadyForRollover || deferReadyForBlockedPrompt ? [] : ready) {

      // ENG-5662: kanban-work is never fired by the scheduler anymore — the
      // manager-gated hybrid edge-trigger injection (step 7e) owns it. The
      // task list is already filtered to drop kanban-work rows; this is a
      // defensive guard for any that slip through (e.g. a same-tick race).
      if (KANBAN_WORK_TEMPLATES.has(task.templateId)) {
        const updated = markTaskFired(codeName, task.taskId, 'ok');
        claudeSchedulerStates.set(codeName, updated);
        continue;
      }

      // Enrich prompt with board context
      let prompt = task.prompt;
      if (BOARD_INJECT_TEMPLATES.has(task.templateId) && boardItems.length > 0) {
        const template = PLAN_TEMPLATES.has(task.templateId) ? 'morning-plan' : 'follow-up';
        const boardPrefix = formatBoardForPrompt(boardItems, template);
        prompt = boardPrefix + prompt;
      }

      // Guard against duplicate launches (same pattern as oneshot path).
      // ENG-4675: also defends against a stale in-flight set if getReadyTasks'
      // filter is ever bypassed.
      if (inFlightClaudeTasks.has(task.taskId)) {
        continue;
      }
      if ((claudeTaskConcurrency.get(codeName) ?? 0) >= MAX_CLAUDE_CONCURRENCY) {
        break;
      }
      inFlightClaudeTasks.add(task.taskId);
      claudeTaskConcurrency.set(codeName, (claudeTaskConcurrency.get(codeName) ?? 0) + 1);

      // ENG-6849: the kanban todo-card + in-session nudge is the SOLE path for
      // scheduled tasks on a healthy persistent session - the legacy `claude -p`
      // oneshot is retired (Anthropic headless billing deprecation under Max).
      // kanban-work is the only excluded family and is skipped above; every
      // remaining template (plain / standup / task-update / plan) routes here,
      // through the ENG-6839-gated deliverScheduledCardResult so a quiet
      // `conditional` run is suppressed by default. Terminal: no oneshot
      // fallback. The helper advances the schedule and disables a one-off.
      // We're already inside the isSessionHealthy branch, so no extra health
      // check is needed.
      // ENG-8068: resolve the durable-nudge kill switch host-side. When ON, the
      // nudge rides the durable direct-chat rail (ENG-7971); when OFF (default)
      // it stays on tmux send-keys. Org/team overrides are already resolved into
      // the host's heartbeat flag map, so a host-wide read honours them.
      await fireScheduledTaskViaKanban(
        codeName,
        agent.agent_id,
        task,
        prompt,
        hostFlagStore().getBoolean('scheduled-task-nudge-durable'),
      );
      // No long-running subprocess on this path - release the guard.
      inFlightClaudeTasks.delete(task.taskId);
      claudeTaskConcurrency.set(codeName, Math.max(0, (claudeTaskConcurrency.get(codeName) ?? 1) - 1));
      // One task per cycle in persistent mode.
      break;
    }
  }

  // ENG-5116: we reach here only when the spawn branch was NOT entered,
  // i.e. `isSessionHealthy(codeName)` returned true at line ~5719.
  // - restartTrigger set ⇒ we stopped the session earlier on this tick
  //   but somehow it still looks healthy. That's the Vigil silent-bug
  //   signature: surface it explicitly so the caller can warn after N
  //   consecutive ticks instead of waiting hours for a customer to
  //   notice. (Pre-ENG-5116 this collapsed into the same "tasks synced"
  //   log as a normal healthy tick.)
  // - dayRolloverDeferred set ⇒ stale-for-today but agent mid-turn;
  //   benign, will retry next tick.
  // - otherwise ⇒ ordinary healthy steady state.
  const finalDecision: PersistentSessionDecision =
    restartTrigger === 'auth-tuple'
      ? 'restart-pending'
      : restartTrigger === 'day-rollover'
        ? dayRolloverForced
          ? 'day-rollover-forced'
          : 'day-rollover-restart'
        : dayRolloverDeferred
          ? 'day-rollover-deferred'
          : 'healthy';
  return {
    decision: finalDecision,
    spawnAttempted: false,
    sessionHealthyAfter: isSessionHealthy(codeName),
  };
}

// ---------------------------------------------------------------------------
// Supabase Realtime for direct chat (replaces 2s polling)
// ---------------------------------------------------------------------------

import {
  startRealtimeChat,
  startRealtimeDrift,
  startRealtimeAssignments,
  startRealtimeConfig,
  startRealtimeKanban,
  startRealtimeIntegrationContext,
  stopRealtimeIntegrationContext,
  startRealtimeTokenRotation,
  stopRealtimeTokenRotation,
  stopRealtimeChat,
  isRealtimeConnected,
  isRealtimeSocketConnected,
  shouldForcePollFallback,
  updateRealtimeToken,
} from './realtime-chat.js';

let realtimeStarted = false;
let realtimeDriftStarted = false;
let realtimeKanbanStarted = false;
// Snapshot of the agent_ids currently subscribed to the realtime
// `plugin_context` channel. Empty set ⇒ not yet subscribed. Compared
// against the current active-agent set each tick so an agent
// activated after manager startup also gets realtime context updates
// (ENG-4725 — previously this used a one-shot boolean and silently
// missed any agents that came online later).
let subscribedIntegrationContextAgentIds: Set<string> = new Set();

/**
 * ENG-9265 — bound the realtime re-subscribe, and make a stuck one visible.
 *
 * WHAT HAPPENED. dream-host was completely dark 05:03-05:29Z on 2026-08-21:
 * four customer agents unreachable, no heartbeat, SSM timing out. Every
 * `token.rotated` channel timed out ~10s after subscribe, closed, and was
 * rebound on the next tick — eight subscription state changes per cycle, every
 * 10-13 seconds, indefinitely. CPU stepped from 8-13% to 58.8% starting in the
 * same minute as the first timeout. It ended only because an unrelated CLI
 * self-update restarted the manager; nothing else intervened.
 *
 * WHY THE EXISTING MITIGATION DID NOT MITIGATE, which is the part worth
 * keeping. `onSubscriptionLost` already refused to re-subscribe inline, and
 * said why:
 *
 *   "an inline retry would spin; the tick gives it a natural backoff and one
 *    log line per cycle instead of a loop."
 *
 * The reasoning about inline retries is right. The assumption underneath it is
 * not: **a fixed tick is not a backoff.** The manager ticks every 10s and the
 * channel times out at 10s, so "one log line per cycle" became a permanent
 * fixed-rate rebind. The comment names the hazard and prescribes something that
 * does not address it — the retry was bounded in the wrong dimension.
 *
 * HOW THE BOUND WORKS, and why it is a deferral rather than a new gate. The
 * rebind is driven by CLEARING `subscribedIntegrationContextAgentIds`: an empty
 * set makes the equality check in `ensureRealtimeIntegrationContextStarted`
 * fail, which tears down and re-subscribes. So backing off means not clearing
 * it yet. That also gives the "a real change jumps the queue" property for
 * free: if the ACTIVE AGENT SET genuinely changes during a backoff, the
 * equality check fails on its own and the rebind proceeds immediately, without
 * this map being consulted at all.
 *
 * The deadline is therefore checked on the TICK rather than in the loss
 * callback. Once a channel is closed and nothing is rebinding, no further
 * status events arrive — a deadline armed only by the callback would never
 * fire.
 */
interface RealtimeRebindState {
  /** Consecutive losses since the last SUBSCRIBED for this agent. */
  attempts: number;
  /** Epoch ms of the first loss in this streak. */
  since: number;
  /** The status that most recently dropped the channel. */
  lastStatus: string;
  /** Epoch ms before which no rebind is attempted. */
  nextAttemptAt: number;
  /** `attempts` at the last log line, so the summary can be throttled. */
  loggedAtAttempt: number;
}

const realtimeRebindFailures = new Map<string, RealtimeRebindState>();

/**
 * Losses served at full tick rate before the backoff engages.
 *
 * Two, not zero: a single dropped channel on a blip is ordinary and recovers on
 * the next tick, and making that wait would turn a self-healing event into a
 * window where token rotations are missed. Two, not more: the incident produced
 * a loss every single tick, so anything generous here buys minutes of the exact
 * churn being bounded.
 */
export const REALTIME_REBIND_FREE_ATTEMPTS = 2;

/**
 * Backoff ceiling. Five minutes, and NOT a give-up: losing these channels
 * permanently means losing token-rotation invalidation for the life of the
 * process, which is a silent degradation rather than a loud one. The bound is
 * on the RATE; the alert below is what escalates to a human.
 */
export const REALTIME_REBIND_MAX_BACKOFF_MS = 5 * 60_000;

/**
 * How long a streak must survive before the heartbeat reports it.
 *
 * Five minutes, deliberately shorter than ENG-9252's ten: that loop wasted CPU,
 * this one preceded a 26-minute customer blackout. The host went silent about
 * four minutes after the churn began.
 */
export const REALTIME_REBIND_STUCK_AFTER_MS = 5 * 60_000;

/**
 * How long an armed rebind is left alone to resolve before another may be armed.
 *
 * Must exceed the subscribe timeout, or a tick can tear down a subscription
 * that has not finished failing yet. The incident's channels reported
 * TIMED_OUT ~10s after subscribe (Supabase realtime's default) and the manager
 * tick is the same ~10s, so the two race exactly. 15s clears it.
 *
 * The trade this makes, stated rather than hidden: during the free-attempt
 * window a loss is retried on the SECOND tick rather than the first. You
 * cannot both retry every 10s and let a 10s subscribe complete, and retrying
 * on top of an unresolved attempt is what the incident actually looked like.
 */
export const REALTIME_REBIND_IN_FLIGHT_GRACE_MS = 15_000;

/** Delay before the next rebind after `attempts` consecutive losses. */
export function realtimeRebindBackoffMs(attempts: number): number {
  if (attempts <= REALTIME_REBIND_FREE_ATTEMPTS) return 0;
  const step = attempts - REALTIME_REBIND_FREE_ATTEMPTS;
  // 10s, 20s, 40s, 80s, 160s, then the ceiling.
  return Math.min(10_000 * 2 ** (step - 1), REALTIME_REBIND_MAX_BACKOFF_MS);
}

/** Record one lost subscription and return the updated streak. */
export function recordRealtimeRebindFailure(
  failures: Map<string, RealtimeRebindState>,
  agentId: string,
  status: string,
  now: number,
): RealtimeRebindState {
  const prior = failures.get(agentId);
  const attempts = (prior?.attempts ?? 0) + 1;
  const next: RealtimeRebindState = {
    attempts,
    since: prior?.since ?? now,
    lastStatus: status,
    loggedAtAttempt: prior?.loggedAtAttempt ?? 0,
    nextAttemptAt: now + realtimeRebindBackoffMs(attempts),
  };
  failures.set(agentId, next);
  return next;
}

/** True when at least one streak is due for another attempt. */
export function realtimeRebindDue(
  failures: Map<string, RealtimeRebindState>,
  now: number,
): boolean {
  for (const state of failures.values()) if (now >= state.nextAttemptAt) return true;
  return false;
}

/**
 * Agents whose realtime subscription has been failing longer than the
 * threshold, shaped for the heartbeat. Longest-stuck first.
 */
export function stuckRealtimeRebinds(
  failures: Map<string, RealtimeRebindState>,
  now: number,
  thresholdMs: number = REALTIME_REBIND_STUCK_AFTER_MS,
): Array<{ agent_id: string; attempts: number; stuck_for_seconds: number; status: string }> {
  const out: Array<{ agent_id: string; attempts: number; stuck_for_seconds: number; status: string }> = [];
  for (const [agentId, state] of failures) {
    const stuckForMs = now - state.since;
    if (stuckForMs < thresholdMs) continue;
    out.push({
      agent_id: agentId,
      attempts: state.attempts,
      stuck_for_seconds: Math.floor(stuckForMs / 1000),
      status: state.lastStatus,
    });
  }
  out.sort((a, b) => b.stuck_for_seconds - a.stuck_for_seconds);
  return out;
}

/** Teardown, so a revoked or unassigned agent cannot hold an alert open. */
export function forgetRealtimeRebindState(agentId: string): void {
  realtimeRebindFailures.delete(agentId);
}

/**
 * Drop streaks for agents that are no longer active, and return how many went.
 *
 * An entry is otherwise removed only by `onSubscribed` or by
 * `forgetRealtimeRebindState` (which runs from `clearAgentCaches` — revoke,
 * unassign, removed-from-host). Neither covers `paused` or `draft`: a paused
 * agent's channel is never rebuilt, so `onSubscribed` can never fire for it,
 * and its entry survives with `nextAttemptAt` permanently in the past. Every
 * tick then answers `realtimeRebindDue` true and rebinds — the exact fixed-rate
 * churn this module exists to bound, reintroduced by the bookkeeping. The same
 * entry also keeps riding the heartbeat, holding a `realtime_rebind_stuck`
 * alert open for work nobody is attempting.
 */
export function pruneRealtimeRebindState(
  failures: Map<string, RealtimeRebindState>,
  liveAgentIds: Iterable<string>,
): number {
  const live = liveAgentIds instanceof Set ? liveAgentIds : new Set(liveAgentIds);
  let dropped = 0;
  for (const agentId of [...failures.keys()]) {
    if (!live.has(agentId)) {
      failures.delete(agentId);
      dropped++;
    }
  }
  return dropped;
}

/**
 * Hold off the next attempt for agents whose rebind is now in flight.
 *
 * The observed failure resolves ~10s after subscribe and the tick is ~10s, so
 * without this a tick that fires before the status callback lands sees
 * `nextAttemptAt` still in the past, clears the snapshot, and tears down the
 * pending subscription — the attempt never gets to finish and the streak never
 * advances.
 *
 * `Math.max` because a backed-off entry must keep its longer deadline, and
 * during the free-attempt window `realtimeRebindBackoffMs` returns 0, which
 * would advance nothing at all.
 */
export function holdRealtimeRebindInFlight(
  failures: Map<string, RealtimeRebindState>,
  agentIds: Iterable<string>,
  now: number,
  graceMs: number = REALTIME_REBIND_IN_FLIGHT_GRACE_MS,
): void {
  const until = now + graceMs;
  for (const agentId of agentIds) {
    const state = failures.get(agentId);
    if (state) state.nextAttemptAt = Math.max(state.nextAttemptAt, until);
  }
}
let realtimeAssignStarted = false;
let realtimeConfigStarted = false;
let realtimeSubscribedAgentIds = new Set<string>();

/**
 * ENG-5614: reconcile the realtime channel-subscription flag against the
 * actual phoenix socket state. A half-open websocket can leave
 * `isRealtimeConnected()` reporting `true` while the socket is dead and the
 * channel's CLOSED/CHANNEL_ERROR callback never fired — inbound direct-chat
 * delivery silently stops, yet the manager keeps skipping the polling
 * fallback (it only polls `if (!isRealtimeConnected())`). The manager
 * heartbeat stays fresh the whole time (this same poll cycle posts it), so
 * nothing else catches it.
 *
 * On divergence we tear the realtime client down (which clears the flag and
 * nulls the client) and reset the subscription flags so `ensureRealtime
 * Started()` rebuilds a fresh client+subscription next tick. Because the flag
 * is now false, the polling fallback runs THIS cycle — inbound delivery is
 * restored immediately rather than waiting on the ~125-min synthetic-probe
 * backstop. Returns true when a heal was performed (for heartbeat reporting).
 */
function healRealtimeSocketDivergence(): boolean {
  if (!shouldForcePollFallback(isRealtimeConnected(), isRealtimeSocketConnected())) return false;
  log('[realtime] Socket divergence — channel flag connected but socket dead; forcing poll fallback + reconnect');
  stopRealtimeChat();
  realtimeStarted = false;
  realtimeDriftStarted = false;
  realtimeAssignStarted = false;
  realtimeConfigStarted = false;
  realtimeKanbanStarted = false;
  subscribedIntegrationContextAgentIds = new Set();
  return true;
}

function ensureRealtimeStarted(agentStates: AgentState[]): void {
  // Check if new agents have appeared since last subscription
  const currentActiveIds = new Set(
    agentStates.filter((a) => a.status === 'active').map((a) => a.agentId),
  );
  if (realtimeStarted) {
    // Detect any change: additions or removals
    const sameSize = currentActiveIds.size === realtimeSubscribedAgentIds.size;
    const sameMembers = sameSize && [...currentActiveIds].every((id) => realtimeSubscribedAgentIds.has(id));
    if (sameMembers) return;
    // Agent set changed — tear down and reconnect with fresh set
    log('[realtime] Agent set changed — reconnecting subscriptions');
    stopRealtimeChat();
    realtimeStarted = false;
    realtimeDriftStarted = false;
    realtimeAssignStarted = false;
    realtimeConfigStarted = false;
    realtimeKanbanStarted = false;
    subscribedIntegrationContextAgentIds = new Set();
  }

  const activeAgentIds = agentStates
    .filter((a) => a.status === 'active')
    .map((a) => a.agentId);

  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  // Get Supabase config + token from the cached exchange result
  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) {
      log('[realtime-chat] No Supabase URL/key from exchange — staying on polling');
      return;
    }

    startRealtimeChat({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onMessage: (msg) => {
        const agent = agentStates.find((a) => a.agentId === msg.agent_id);
        if (!agent) return;

        if (directChatInFlight.has(msg.id)) return;
        directChatInFlight.add(msg.id);

        processDirectChatMessage(agent, {
          id: msg.id,
          session_id: msg.session_id,
          content: msg.content,
        }).finally(() => {
          directChatInFlight.delete(msg.id);
        });
      },
      onError: (err) => {
        log(`[realtime-chat] Error: ${err.message}`);
      },
      onStatusChange: (status) => {
        if (status === 'disconnected' || status === 'error') {
          log('[realtime] Disconnected — falling back to polling, will reconnect next cycle');
          // Reset all subscription flags so they reconnect with a fresh client
          realtimeStarted = false;
          realtimeDriftStarted = false;
          realtimeAssignStarted = false;
          realtimeConfigStarted = false;
          realtimeKanbanStarted = false;
          subscribedIntegrationContextAgentIds = new Set();
        }
      },
      log,
    });

    realtimeStarted = true;
    realtimeSubscribedAgentIds = new Set(activeAgentIds);
    log(`[realtime-chat] Started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime-chat] Failed to start: ${(err as Error).message}`);
  });
}

function ensureRealtimeDriftStarted(agentStates: AgentState[]): void {
  if (realtimeDriftStarted) return;

  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);
  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) return;

    startRealtimeDrift({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onDrift: (doc) => {
        // Invalidate the known version so next poll cycle re-provisions.
        // ADR-0011 PR-A: local renamed `agentState` → `agent` to disambiguate
        // from the imported `agentState` module namespace.
        const agent = agentStates.find((a) => a.agentId === doc.agent_id);
        if (agent) {
          agentState.knownVersions.delete(doc.agent_id);
          // ENG-9585: and force the next pass to actually FETCH for this agent.
          // Invalidating `knownVersions` alone was sufficient while every pass
          // re-fetched: the next one saw the new charter version and
          // re-provisioned. Under fetch spacing it is not — the next pass can
          // serve the CACHED payload, which still carries the OLD version, so
          // the drift signal would be consumed with no effect and the edit
          // would wait out the TTL. Per-agent, because only this agent drifted.
          refreshFetchGate.forceNext(doc.agent_id);
          log(`[realtime] Drift invalidated for '${agent.codeName}' — will re-provision next cycle`);
        }
      },
      log,
    });

    realtimeDriftStarted = true;
    log(`[realtime] Drift subscription started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime] Drift subscription failed: ${(err as Error).message}`);
  });
}

// ENG-7335: agents currently being serviced by the fast-restart lane. Both the
// lane below and the poll's restart-servicing loop consult this so the two paths
// can't double-kill/double-respawn the same agent. This is the narrow per-agent
// lock the doorbell design uses IN PLACE OF a whole-cycle re-entrancy guard: the
// lane only ever touches ONE agent, so mutual exclusion is per-agent, not global.
const restartInFlight = new Set<string>();

/**
 * ENG-7345: does this restart_reason require .mcp.json to be re-rendered before
 * the respawn? NULL or 'manual' = a pure operator restart (re-launch the
 * already-provisioned agent) -> the doorbell's surgical fast lane is correct.
 * ANY other value = a config-change reload (integration add, and future channel/
 * model changes) whose new .mcp.json only exists after processAgent renders it,
 * so it must go through a full poll. Keyed on "not 'manual'" so new reasons need
 * no manager change. Mirrors host-actions.ts RESTART_REASON_* (the API writer).
 */
function restartReasonNeedsRender(reason: string | null): boolean {
  return reason != null && reason !== 'manual';
}

// ENG-7401: the ONLY render-needed restart reason that currently drives a
// 'new MCP servers' begin() (and therefore a matching pickup= measurement).
// Mirrors host-actions.ts RESTART_REASON_INTEGRATION_CHANGE. Deliberately
// narrower than restartReasonNeedsRender: a future non-MCP render restart
// (channel / model reload) must NOT stamp the restart-timing request anchor,
// or its unconsumed stamp could attach a bogus pickup= to a later, unrelated
// integration add (CodeRabbit on PR #3036). Add a reason here only when it also
// feeds the managed-toolkit MCP-servers-changed block that calls beginRestartTiming.
function restartReasonStampsTiming(reason: string | null): boolean {
  return reason === 'integration-change';
}
/**
 * One-agent `/host/refresh` for the two fast spawn lanes (ENG-9585).
 *
 * The restart lane and the fast-MCP respawn both need the LIVE spawn config —
 * channels, timezone, auth, model chain — for an agent whose provisioning side
 * effects are already on disk from the poll that got them here. Neither needs a
 * body.
 *
 * They shared this call before they shared this function, and that is the point.
 * Both were written as `api.post('/host/refresh', { agent_id })`, the obvious
 * thing to type, and both therefore kept taking the legacy body-inline response
 * long after the poll had moved to manifests — 2.96 MB against 325 KB on the
 * largest agent measured, to read seven small keys, with no error and nothing
 * in `manager.log` to show for it. Two lanes maintained independently drifted
 * identically; one function cannot.
 *
 * SAFE BECAUSE NOTHING DOWNSTREAM READS A BODY LANE. `ensurePersistentSession`
 * is the only thing either caller hands this to, and it reads `agent`, `team`,
 * `channel_configs`, `tools`, `openrouter`, `anthropic` and `model_defaults`;
 * its two whole-object pass-throughs (`ensureOpencodeRuntime`,
 * `resolveModelChain`) stay inside that set. Pinned by
 * `eng-9585-refresh-manifest-flags.test.ts`, so a future read of `knowledge` or
 * a skill lane there fails the build rather than silently seeing hashes.
 *
 * Returns `null` on failure — the caller returns and leaves the agent to the
 * slow poll, which is what both lanes did with their own try/catch. The lane
 * label only picks the log prefix, so the two stay distinguishable in
 * `manager.log`.
 */
async function fetchSpawnRefreshData(
  agentId: string,
  codeName: string,
  lane: 'restart-lane' | 'fast-mcp-respawn',
): Promise<Record<string, unknown> | null> {
  try {
    return await api.post<Record<string, unknown>>('/host/refresh', {
      agent_id: agentId,
      ...REFRESH_MANIFEST_FLAGS,
    });
  } catch (err) {
    log(`[${lane}] refresh failed for '${codeName}', leaving for slow poll: ${(err as Error).message}`);
    return null;
  }
}


/**
 * ENG-7335: fast agent-restart lane. Invoked by the host_agents restart doorbell
 * (a Realtime UPDATE on restart_requested_at) to service a dashboard-issued
 * restart within a few seconds, instead of waiting up to 50-110s for the next
 * full poll cycle to notice it.
 *
 * It reuses the SAME spawn path the poll uses - ensurePersistentSession over the
 * agent's already-provisioned on-disk artifacts plus a fresh single-agent
 * /host/refresh (for live channel/timezone/auth config). A restart re-launches an
 * already-provisioned agent, so the heavier provisioning side effects (.mcp.json,
 * settings, secrets) are already on disk from the last poll; the next full poll
 * reconciles any drift. There is deliberately NO second spawn code path to drift.
 *
 * Advisory + backstopped by design: gated on the restart-doorbell flag; on ANY
 * failure it does NOT ack, so restart_requested_at stays set and
 * lastRestartProcessedAt stays un-advanced, and the slow poll re-services it.
 * Correctness never depends on the doorbell firing - it only collapses latency.
 */
async function handleRestartDoorbell(agentId: string, requestedAt: string, restartReason: string | null): Promise<void> {
  const prev = state.agents.find((a) => a.agentId === agentId);
  const codeName = prev?.codeName;
  // Unknown agent (not assigned to this manager yet, or assigned mid-flight):
  // let the next full poll provision + service it. Never ack what we didn't do.
  if (!codeName) return;

  // Durable dedup - identical semantics to the poll's alreadyServiced guard. If
  // we (or a prior manager process, via persisted state) already serviced this
  // exact request, do nothing.
  const lastProcessed = prev?.lastRestartProcessedAt ?? null;
  if (lastProcessed != null && Date.parse(lastProcessed) >= Date.parse(requestedAt)) return;

  // ENG-7345: a config-change reload (restart_reason present and not 'manual',
  // e.g. an integration add) rewrites .mcp.json with a new MCP server. Only
  // processAgent renders .mcp.json; the surgical fast lane below reads the
  // already-on-disk (now STALE) file, so it would respawn WITHOUT the new server
  // and the next poll would drift-respawn again = double respawn. Route it to a
  // full poll instead: pollCycle's restart-consumption loop (window-exempt) stops
  // the session, then processAgent re-renders .mcp.json and respawns ONCE, then
  // acks. Runs regardless of the restart-doorbell flag (a full poll is safe +
  // cheap, so integration-add is fast on every host); do NOT take the per-agent
  // lock or ack here - the poll owns the stop/render/respawn/ack.
  if (restartReasonNeedsRender(restartReason)) {
    // ENG-7401: anchor the restart-timing clock to the API's restart_requested_at
    // (the modal-clock origin) so the eventual begin()/tools-bound lines can
    // report the true request->tools-live total, matching what the operator sees.
    // Only the integration-change reason feeds a begin(), so scope the stamp to it
    // — a broader (non-manual) stamp would leave stale request anchors that a later
    // MCP add could misread as its own pickup latency (CodeRabbit on PR #3036).
    if (restartReasonStampsTiming(restartReason)) {
      noteRestartRequested(codeName, requestedAt);
    }
    triggerEarlyPoll(`config-change reload for '${codeName}' (reason=${restartReason})`);
    return;
  }

  // Pure operator restart (Live Terminal / slash command): surgical fast lane,
  // flag-gated (ENG-7335) - env override (AGT_RESTART_DOORBELL_ENABLED) >
  // heartbeat flag > compiled default (off), re-read per invocation so a central
  // flip takes effect on the next restart without re-subscribing.
  if (!hostFlagStore().getBoolean('restart-doorbell')) return;

  // Per-agent lock - never run two lane passes for the same agent concurrently.
  if (restartInFlight.has(agentId)) return;

  restartInFlight.add(agentId);
  try {
    log(`[restart-lane] Fast restart for '${codeName}' at ${requestedAt}`);
    const hostId = await getHostId();

    // One-agent refresh for the live spawn config (channels/timezone/auth). The
    // provisioning side effects it would normally drive are already on disk.
    const refreshData = await fetchSpawnRefreshData(agentId, codeName, 'restart-lane');
    if (!refreshData) return;
    const displayName =
      (refreshData.agent as { display_name?: string } | undefined)?.display_name ?? codeName;

    // CS-1680: a restart must be able to CLEAR a wedge. Killing the process is
    // NOT enough — the daily-session UUID pin survives, so
    // resolveSessionSpawnDecision still finds today's transcript and `--resume`s
    // the very transcript that wedged the agent. The Zanda admin restarted
    // 'alfred' twice (04:59 and 08:11) and watched it resume the same card and
    // re-wedge both times.
    //
    // Note this lane never reaches prepareForRespawn's rotation gate (that is on
    // the slow persistent-session path), and that gate could not have helped
    // anyway: it counts `consecutiveSameUuidFailures`, which only increments on
    // SPAWN FAILURE. A wedge spawns fine and then freezes, so it never
    // increments — which is why both restarts logged "#0".
    //
    // Scope deliberately narrow: rotate ONLY when the wedge detector currently
    // reads this agent as wedged. That uses the detector's signal WITHOUT
    // auto-restarting anything, so it respects ENG-7195's shadow soak (shadow
    // keeps the streak — it `continue`s without clearing it), and it leaves
    // ordinary restarts resuming as before, so a routine restart does not
    // silently discard a day's continuity.
    const wedgeStreakAtRestart = consecutiveWedgeCycles.get(codeName) ?? 0;
    if (shouldForceFreshOnManualRestart(wedgeStreakAtRestart, resolveWedgeConfig())) {
      try {
        // Rotates today's pinned UUID so the respawn below cold-starts via
        // `--session-id`. The OLD transcript stays on disk for forensics —
        // rotation only re-pins the UUID (ENG-6153).
        const freshId = rotateSessionForWedge(codeName);
        consecutiveWedgeCycles.delete(codeName);
        // Mirror the enforce path: park the wedged session's undrained markers
        // so the fresh session surfaces them instead of re-reading them into the
        // same wedge (ENG-6289). Without this the loop can re-form on the very
        // next turn and the restart looks like it failed again.
        const { parkPendingInbound: parkForRestart } = await import('./responsiveness-probe.js');
        const { parked, deadLettered } = parkForRestart(codeName, new Date());
        const parkNote = parked > 0 ? `, ${parked} inbound parked` : '';
        const deadNote = deadLettered > 0 ? `, ${deadLettered} undeliverable dead-lettered` : '';
        log(
          `[restart-lane] '${codeName}' reads wedged (cycles=${wedgeStreakAtRestart}) — forcing a FRESH ` +
            `session ${freshId} instead of resuming the wedged transcript ` +
            `(transcript preserved${parkNote}${deadNote}) (CS-1680)`,
        );
      } catch (err) {
        // Never let the force-fresh attempt block the restart the human asked
        // for. Worst case we fall through to the old resume behaviour.
        log(
          `[restart-lane] force-fresh failed for '${codeName}': ${(err as Error).message} — ` +
            `continuing with a normal respawn (CS-1680)`,
        );
      }
    }

    // Kill the current session so ensurePersistentSession cold-starts a fresh
    // PROCESS (same mechanism the poll's servicing loop uses). Note "fresh" here
    // means the process, not the session — the session binding is decided by
    // resolveSessionSpawnDecision from the daily-session pin above.
    //
    // ENG-8000: an opencode agent's serve runs under its own tmux namespace
    // (`agt-oc-<code>`) with a stopOpencodeSession teardown, not the Claude
    // `agt-<code>` pane. The Claude kill below misses it entirely, so the serve
    // stays healthy, ensurePersistentSession -> ensureOpencodeRuntime early-returns
    // 'healthy', and this lane never restarts (decision !== 'spawn' -> no ack ->
    // deferred to the slow poll, which had the same blind spot). Route opencode
    // through stopOpencodeSession, which also marks the in-memory session 'stopped'
    // so the respawn is not short-circuited by startOpencodeSession's
    // already-'running' guard (a raw tmux kill leaves that guard set and wedges the
    // respawn - the nora recovery note on ENG-8000). ensurePersistentSession then
    // dispatches to ensureOpencodeRuntime, which sees an unhealthy session and
    // respawns, so the confirmed-respawn ack gate below fires honestly.
    const framework =
      (refreshData.agent as { framework?: string } | undefined)?.framework ??
      agentFrameworkCache.get(codeName) ??
      DEFAULT_FRAMEWORK;
    if (restartTargetsOpencodeServe(framework)) {
      stopOpencodeSession(codeName, log);
    } else {
      try {
        const { execSync: es } = await import('node:child_process');
        es(`tmux kill-session -t agt-${codeName} 2>/dev/null`, { stdio: 'ignore' });
      } catch { /* no session to kill - the respawn starts fresh */ }
      stopPersistentSessionAndForgetMcpBaseline(codeName);
    }

    // Respawn via the shared spawn path (tasks/boardItems empty - the kanban
    // nudge is a post-spawn concern the next poll handles).
    const result = await ensurePersistentSession(
      { agent_id: agentId, code_name: codeName, display_name: displayName },
      [],
      [],
      refreshData,
    );

    // ENG-7188: this lane bypasses processAgent, the map's other writer, so
    // record ITS outcome — otherwise the flush below publishes whatever the last
    // slow poll left behind (or nothing). Recorded BEFORE the not-confirmed
    // bail-out on purpose: a lane that tried and failed is exactly the fact the
    // console is missing, and leaving the previous tick's entry in place would
    // report the agent healthy after a refused respawn.
    recordSpawnOutcome(codeName, result);

    // ENG-10335: a respawn that landed on an unanswerable prompt still counts as
    // confirmed (see isConfirmedRespawn). Leaving it un-acked would make the slow
    // poll restart the agent again into the same prompt.
    if (!isConfirmedRespawn(result)) {
      // Respawn didn't confirm (circuit-breaker trip, auth, spawn failure). Do
      // NOT ack - leave restart_requested_at set so the slow poll retries and, if
      // it's a persistent condition, surfaces it through the normal path.
      log(`[restart-lane] respawn not confirmed for '${codeName}' (decision=${result.decision}) - leaving for slow poll`);
      return;
    }
    if (result.decision === BLOCKED_ON_PROMPT_DECISION) {
      log(
        `[restart-lane] '${codeName}' respawned but is blocked on an interactive prompt - acking, not retrying: ` +
          `a respawn re-presents the same prompt; published unhealthy [ENG-10335]`,
      );
    }

    // Confirmed respawn. Ack (clears restart_requested_at) AFTER the new session
    // exists, so the dashboard's startedAt>requested signal can't resolve against
    // a not-yet-respawned session. Best-effort; race-safe server-side (only nulls
    // when the stored value still equals requestedAt).
    void api
      .post('/host/restart-ack', { host_id: hostId, agent_id: agentId, restart_requested_at: requestedAt })
      .catch((err) => log(`[restart-lane] ack failed for '${codeName}': ${(err as Error).message}`));

    // ENG-7335: flush a diagnostics-only heartbeat now so the respawned session's
    // fresh startedAt lands within seconds (mirrors the slow path's ENG-7328
    // flush). Without it the dashboard's startedAt>restart_requested_at signal
    // would wait for the next regular heartbeat before confirming "back online".
    void (async () => {
      try {
        const { collectDiagnostics } = await import('./persistent-session.js');
        await api.post('/host/heartbeat', {
          host_id: hostId,
          agent_diagnostics: collectDiagnostics([codeName], quarantineEntriesFor, claudeMdSizeFor, spawnOutcomeForDiagnostics, pidPressureFor, forwardedToolsFor, runtimeVersionFor),
        });
      } catch (err) {
        log(`[restart-lane] post-respawn diagnostics flush failed for '${codeName}': ${(err as Error).message}`);
      }
    })();

    // Stamp the durable dedup so neither the slow poll nor a manager restart
    // re-fires this request. Persist immediately (crash-replay guard, mirroring
    // the poll's applyRestartAcks immediate write).
    prev.lastRestartProcessedAt = requestedAt;
    try {
      atomicWriteFileSync(getStateFile(), JSON.stringify(state, null, 2));
    } catch (err) {
      log(`[restart-lane] failed to persist ack for '${codeName}': ${(err as Error).message}`);
    }
    log(`[restart-lane] '${codeName}' respawned via fast lane`);
  } finally {
    restartInFlight.delete(agentId);
  }
}

/**
 * ENG-7401 Phase 2: respawn one agent immediately after an MCP-bind restart's stop
 * fired, instead of leaving it down until the next full poll cycle's health check
 * (~50-110s later) notices and respawns it. Called (flag-gated) from the
 * scheduleSessionRestart timer right after stopPersistentSession.
 *
 * Reuses the SAME spawn path as the poll and the ENG-7335 fast lane
 * (ensurePersistentSession over the already-provisioned, freshly-rendered
 * .mcp.json + a one-agent /host/refresh for live config) - there is deliberately
 * NO second spawn code path to drift.
 *
 * Advisory + backstopped, exactly like the fast lane: on ANY failure (refresh
 * error, breaker trip, spawn not confirmed) it logs and returns, leaving the
 * session down so the slow poll respawns it on its next cycle - the pre-ENG-7401
 * behaviour. Correctness never depends on this; it only collapses the
 * stop->respawn latency.
 */
async function respawnAgentAfterMcpStop(codeName: string, reason: string): Promise<void> {
  const prev = state.agents.find((a) => a.codeName === codeName);
  if (!prev) return;
  const agentId = prev.agentId;
  // Per-agent mutex shared with the ENG-7335 fast lane - never two lane respawns
  // for one agent at once.
  if (restartInFlight.has(agentId)) return;
  restartInFlight.add(agentId);
  try {
    // A concurrent poll may have already respawned the session between the stop
    // and here - don't stomp a healthy session.
    if (isSessionHealthy(codeName)) return;

    // One-agent refresh for the live spawn config (channels/timezone/auth); the
    // provisioning side effects are already on disk from the poll that rendered
    // the new .mcp.json.
    const refreshData = await fetchSpawnRefreshData(agentId, codeName, 'fast-mcp-respawn');
    if (!refreshData) return;
    const displayName =
      (refreshData.agent as { display_name?: string } | undefined)?.display_name ?? codeName;

    const result = await ensurePersistentSession(
      { agent_id: agentId, code_name: codeName, display_name: displayName },
      [],
      [],
      refreshData,
    );
    // ENG-7188: same reason as the restart lane, and recorded before the same
    // bail-out — this path never reaches processAgent, so nothing else publishes
    // its outcome.
    recordSpawnOutcome(codeName, result);
    // ENG-10335: same rule as the restart lane (isConfirmedRespawn).
    if (!isConfirmedRespawn(result)) {
      log(
        `[fast-mcp-respawn] respawn not confirmed for '${codeName}' (decision=${result.decision}) - leaving for slow poll`,
      );
      return;
    }
    if (result.decision === BLOCKED_ON_PROMPT_DECISION) {
      log(
        `[fast-mcp-respawn] '${codeName}' respawned but is blocked on an interactive prompt - not retrying, ` +
          `a respawn re-presents the same prompt; published unhealthy [ENG-10335]`,
      );
    }

    // Flush a diagnostics-only heartbeat so the respawned session's fresh
    // startedAt lands within seconds (the restart modal's "back online" signal),
    // mirroring the fast lane (ENG-7328/7335). Best-effort.
    try {
      const hostId = await getHostId();
      const { collectDiagnostics } = await import('./persistent-session.js');
      await api.post('/host/heartbeat', {
        host_id: hostId,
        agent_diagnostics: collectDiagnostics([codeName], quarantineEntriesFor, claudeMdSizeFor, spawnOutcomeForDiagnostics, pidPressureFor, forwardedToolsFor, runtimeVersionFor),
      });
    } catch (err) {
      log(`[fast-mcp-respawn] post-respawn diagnostics flush failed for '${codeName}': ${(err as Error).message}`);
    }

    log(
      `[fast-mcp-respawn] '${codeName}' respawned immediately after '${reason}' stop - skipped the ~poll-cycle wait (ENG-7401)`,
    );
  } catch (err) {
    // CodeRabbit: this runs fire-and-forget (void respawnAgentAfterMcpStop), so
    // ANY unhandled throw here - notably from ensurePersistentSession, which is
    // NOT individually try/caught - would surface as an unhandled promise
    // rejection and could take down the whole manager. Backstop the entire body:
    // log and return, leaving the session for the slow poll. This is what makes
    // the "advisory + fully backstopped" contract actually hold.
    log(`[fast-mcp-respawn] unexpected error for '${codeName}', leaving for slow poll: ${(err as Error).message}`);
  } finally {
    restartInFlight.delete(agentId);
  }
}

function ensureRealtimeAssignStarted(agentStates: AgentState[]): void {
  if (realtimeAssignStarted) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey || !exchange.hostId) return;

    startRealtimeAssignments({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      hostId: exchange.hostId,
      // ENG-7335: fast-restart doorbell. Fires the narrow lane; the lane itself is
      // flag-gated and backstopped by the slow poll, so wiring it unconditionally
      // is safe (a no-op when the flag is off).
      onRestartRequested: (payload) => {
        void handleRestartDoorbell(payload.agent_id, payload.restart_requested_at, payload.restart_reason).catch((err) =>
          // Never let a fast-lane failure become an unhandled rejection - the slow
          // poll backstop re-services the (still-set) restart_requested_at.
          log(`[restart-lane] doorbell handler failed for ${payload.agent_id}: ${(err as Error).message}`),
        );
      },
      onAssign: (payload) => {
        // ENG-9585: a newly-assigned agent has no cached payload, so it would
        // fetch anyway — but only because `clearAgentCaches` purged it on the
        // way out. Forcing here states the requirement directly instead of
        // resting on that, and costs one map insert.
        refreshFetchGate.forceNext(payload.agent_id);
        memoriesFetchGate.forceNext(payload.agent_id);
        log(`[realtime] Agent ${payload.agent_id} assigned`);
        // ENG-10760: schedule the pass NOW instead of waiting for the timer.
        //
        // This line used to promise that the next cycle would collect the agent,
        // honest while that cycle was ~10s away. The idle backoff makes it a full
        // 5-minute cap away, and the host receiving its FIRST agent is precisely
        // the host that is backed off — so the assignment lane is the one place
        // that could not keep assuming a short timer. Resetting the counter
        // inside `triggerEarlyPoll` was not enough on its own: nothing on this
        // path called it.
        //
        // Safe to call before its declaration — `triggerEarlyPoll` is a hoisted
        // function declaration lower in this file, the same way the rest of this
        // callback block reaches it. A burst of assignments coalesces through the
        // ENG-9046 single-flight guard and the ENG-9637 rate ceiling, and a
        // throttled early poll still re-arms at the now-reset normal interval, so
        // the worst case is one interval rather than one cap.
        triggerEarlyPoll(`agent ${payload.agent_id} assigned to this host`);
        // ENG-4643: stamp the agent for a fresh memory pull on its next
        // sync tick. Without this, a long-running manager that hosted
        // this agent before its current migration could still have
        // memoryFileHashes / lastDownloadHash entries from the prior
        // assignment and skip the catch-up download (the local-list-
        // hash short-circuit in syncMemories sees "nothing changed"
        // and returns). Clearing the caches + flagging for a forced
        // download-first pass keeps the new host in sync with the DB.
        //
        // Deliberately not invoking syncMemories from here. The
        // realtime payload only carries agent_id; syncMemories needs
        // {agent_id, code_name} plus the supervisor's configDir, none
        // of which are reachable from this callback's closure. The
        // next supervisor tick (interval default 10s) iterates the
        // freshly-loaded agent list and consumes the pending flag,
        // so the latency cost of waiting one tick is bounded and
        // the alternative (extra API call to resolve code_name from
        // here, plus duplicating the sync entrypoint plumbing) is
        // not worth the saved seconds.
        markAgentForFreshMemorySync(payload.agent_id);
      },
      onUnassign: (payload) => {
        log(`[realtime] Agent ${payload.agent_id} unassigned`);
        clearAgentCaches(payload.agent_id, '');
      },
      log,
    });

    realtimeAssignStarted = true;
    log(`[realtime] Assignment subscription started for host ${exchange.hostId}`);
  }).catch((err) => {
    log(`[realtime] Assignment subscription failed: ${(err as Error).message}`);
  });
}

function ensureRealtimeConfigStarted(agentStates: AgentState[]): void {
  if (realtimeConfigStarted) return;

  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);
  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) return;

    startRealtimeConfig({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onConfigChange: (agent) => {
        // Only invalidate status cache — version/provision caches are handled by drift subscription.
        // Don't invalidate agentState.knownVersions here as that triggers re-provision which updates
        // updated_at, creating a feedback loop with this Realtime subscription.
        agentState.knownStatuses.delete(agent.agent_id);
      },
      log,
    });

    realtimeConfigStarted = true;
    log(`[realtime] Config subscription started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime] Config subscription failed: ${(err as Error).message}`);
  });
}

function ensureRealtimeKanbanStarted(agentStates: AgentState[]): void {
  if (realtimeKanbanStarted) return;

  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);
  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) return;

    startRealtimeKanban({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onTodayItem: (item) => {
        // ENG-5599: scheduled-task cards are already nudged by
        // routeScheduledTaskViaKanban with their own per-card run marker. The
        // generic kanban-work trigger here would double-nudge them (and the
        // second nudge carries no marker, so any turns it provokes are
        // unattributed) — skip them.
        if (item.source_type === 'scheduled_task') return;

        // Trigger kanban-work immediately for this agent
        const agent = agentStates.find((a) => a.agentId === item.agent_id);
        if (!agent) return;

        const agentFw = agentFrameworkCache.get(agent.codeName) ?? DEFAULT_FRAMEWORK;

        if (agentFw === 'claude-code') {
          // ENG-8743: durable direct-chat rail, not tmux send-keys — the same
          // migration as the work_trigger_at site above, and for the same two
          // reasons (an unverified Enter strands the nudge in the composer; and
          // send-keys carries no consume-time revalidation, the ENG-7958 class).
          //
          // The card id that used to be embedded here (ENG-5405) is dropped for
          // the reason spelled out at the other site: a durable notice can be
          // consumed long after enqueue, and the server-side gate revalidates the
          // BOARD, not the named card, so an embedded id can go stale unchecked.
          //
          // `isSessionHealthy` is gone too — the notice no longer needs a live
          // pane, so an insert during session boot is delivered late rather than
          // dropped (this callback's old else-branch handed it to the hybrid).
          //
          // This callback is synchronous, so the promise is voided rather than
          // awaited. `.catch()` is belt-and-braces: injectKanbanCheckViaDirectChat
          // documents itself as never-throwing and its internals do all catch, but
          // an unhandled rejection escaping a realtime handler is not a bet worth
          // taking for one line.
          void injectKanbanCheckViaDirectChat(agent.codeName, agent.agentId, 1)
            .then((enqueued) => {
              if (enqueued) {
                log(`[realtime] Enqueued kanban-work trigger for '${agent.codeName}' item_id=${item.id}`);
              } else {
                // No send-keys fallback (ENG-7967 contract). The card is durable on
                // the board, so the hybrid edge-trigger re-nudges on its next tick.
                log(
                  `[realtime] Work trigger enqueue FAILED for '${agent.codeName}' item_id=${item.id} — hybrid nudge will retry`,
                );
              }
            })
            .catch((err) => {
              // Hash the error text rather than logging it: an enqueue fault can
              // carry response data and manager.log is durable. Same 12-char
              // sha256 shape as startRun() and enqueueKanbanNotice(), both of
              // which cite the CLAUDE.md log policy for exactly this.
              // (CodeRabbit, PR #4385.)
              const errId = createHash('sha256')
                .update(err instanceof Error ? err.message : String(err))
                .digest('hex')
                .slice(0, 12);
              log(
                `[realtime] Work trigger enqueue threw for '${agent.codeName}' item_id=${item.id} error_id=${errId} — hybrid nudge will retry`,
              );
            });
        }
      },
      // ENG-4507: kanban completion notification — surfaces user-driven
      // closures to the daemon log + telemetry surface. The agent-runtime
      // ingestion path is the kanban_list `closed_by` annotation (no live
      // injection in this slice — the agent reads its board between turns
      // and sees the new state naturally).
      onCompletion: (event) => {
        const agent = agentStates.find((a) => a.agentId === event.agent_id);
        const codeName = agent?.codeName ?? event.agent_id;
        log(
          `[realtime] Kanban completion forwarded for '${codeName}': ` +
          `item=${event.item_id} status=${event.status} actor=${event.last_actor_id ?? 'unknown'}`,
        );
        // ENG-5601: secondary delivery path, for a HUMAN completing the agent's
        // scheduled-task card from the console. Two gates, both load-bearing:
        //   1. `user:` actor only. Agent self-completion (the in-session happy
        //      path) is suppressed upstream by isSelfCompletion and handled by
        //      the poll path.
        //   2. Manager-ROUTED cards only (isScheduledCardTracked). Restricting to
        //      tracked cards matches the poll path, which only ever iterates
        //      tracked cards.
        // deliverScheduledCardResult shares the idempotency claim with the poll
        // path, so a human closing a card the poll already delivered is a no-op.
        // ENG-6849: the AGT_SCHEDULED_VIA_KANBAN gate is retired (the claude -p
        // oneshot it guarded is gone); manager-side delivery is the only model.
        if (
          event.status === 'done' &&
          classifyActor(event.last_actor_id, event.agent_id) === 'user' &&
          isScheduledCardTracked(event.item_id)
        ) {
          // ENG-6546: explicitly 'user' — a human closed this card from the
          // console, so deliverScheduledCardResult must NOT parse their free text
          // as a standup / task-update side effect into /host/agent-status. Only
          // the self-completion poll path (completedBy='self') runs side effects.
          void deliverScheduledCardResult(codeName, event.agent_id, event.item_id, 'user');
        }
      },
      log,
    });

    realtimeKanbanStarted = true;
    log(`[realtime] Kanban subscription started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime] Kanban subscription failed: ${(err as Error).message}`);
  });
}

// ---------------------------------------------------------------------------
// Integration context realtime (ENG-4342)
//
// Subscribes to plugin_context table changes for the manager's agents and
// triggers an early poll cycle whenever a context row is inserted or
// updated. The early poll is what actually re-renders SKILL.md — this
// realtime channel just collapses the latency from "next poll cycle"
// (~60s) to "next event loop tick after the change" (~100ms + RTT).
// ---------------------------------------------------------------------------

function activeAgentIdSetsEqual(current: string[], previous: Set<string>): boolean {
  if (current.length !== previous.size) return false;
  for (const id of current) if (!previous.has(id)) return false;
  return true;
}

function ensureRealtimeIntegrationContextStarted(agentStates: AgentState[]): void {
  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);

  // ENG-9265 (CodeRabbit on #4861): reconcile the streak map against the live
  // active set BEFORE anything else, including the no-active-agents return
  // below.
  //
  // An entry can only be removed by `onSubscribed` or by
  // `forgetRealtimeRebindState`, and the latter runs from `clearAgentCaches` —
  // revoke, unassign, removed-from-host. Neither covers `paused` or `draft`.
  // A paused agent's channel is never rebuilt, so `onSubscribed` can never fire
  // for it, so its entry survives with `nextAttemptAt` permanently in the past.
  // `realtimeRebindDue` then answers true on every tick forever, the gate below
  // clears the snapshot every tick, and the equality check tears down and
  // re-subscribes BOTH channels every tick. That is precisely the fixed-rate
  // churn this change exists to remove, reintroduced by the bookkeeping.
  //
  // It also keeps `realtime_rebind_stuck` naming the agent in every heartbeat,
  // so the API holds an alert open with a growing duration for work nobody is
  // attempting — the same defect ENG-9252 fixed for the channel-sync twin,
  // which purges explicitly in the draft/paused branch of `processAgent`.
  //
  // Reconciling against the ACTIVE set rather than branching on status covers
  // one case that branch does not: an agent that has left `agentStates`
  // entirely. The manager will never rebind a channel for an agent it no longer
  // lists, so a surviving streak there has no remover either. The cost is that
  // an agent transiently missing from one cycle loses its streak and gets a
  // fresh free attempt — a reset backoff, not a return to churn.
  if (realtimeRebindFailures.size > 0) {
    const dropped = pruneRealtimeRebindState(realtimeRebindFailures, activeAgentIds);
    if (dropped > 0) {
      log(`[realtime] Dropped ${dropped} token-rotation rebind streak(s) for no-longer-active agent(s) (ENG-9265)`);
    }
  }

  // ENG-9346: the same reconcile for streaks that came back from the sidecar.
  // The block above covers the rebind Map in-process; this one exists because
  // persisted state can outlive the agent that owned it — an agent unassigned
  // while the manager was DOWN reaches no purge path at all, and its entry
  // would otherwise be reported on every heartbeat and rejected by the API's
  // assignment check forever.
  pruneStuckStreaksToActiveAgents(activeAgentIds);

  // No active agents → tear down any existing subscription so we don't
  // hold a channel filter for agents we no longer manage.
  if (activeAgentIds.length === 0) {
    if (subscribedIntegrationContextAgentIds.size > 0) {
      stopRealtimeIntegrationContext();
      // ENG-4536: torn down together, because they are subscribed together and
      // track the same active-agent set. Two lifecycles over one set drift.
      stopRealtimeTokenRotation();
      subscribedIntegrationContextAgentIds = new Set();
      log('[realtime] Integration context + token rotation subscriptions torn down (no active agents)');
    }
    return;
  }

  // ENG-9265: a backed-off rebind is armed HERE, not in the loss callback.
  // Once a channel is closed and nothing is rebinding, no further status events
  // arrive — a deadline armed only by the callback would never fire. Clearing
  // the snapshot is what makes the equality check below fail and the rebind
  // happen.
  if (
    realtimeRebindFailures.size > 0
    && realtimeRebindDue(realtimeRebindFailures, Date.now())
    && subscribedIntegrationContextAgentIds.size > 0
  ) {
    subscribedIntegrationContextAgentIds = new Set();
  }

  // Already subscribed to exactly this set → nothing to do.
  if (activeAgentIdSetsEqual(activeAgentIds, subscribedIntegrationContextAgentIds)) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  // Active set changed (agent activated, deactivated, reassigned, etc.).
  // Tear down the existing channel so we can re-subscribe with the
  // updated `agent_id=in.(...)` filter — Supabase realtime filters are
  // immutable per channel, so a re-bind is the only path.
  const isResubscribe = subscribedIntegrationContextAgentIds.size > 0;
  if (isResubscribe) {
    stopRealtimeIntegrationContext();
    // ENG-4536: the token-rotation topic is per-agent, so a changed set means a
    // different set of channels, not a different filter on one channel.
    stopRealtimeTokenRotation();
  }

  // Snapshot the set we're about to subscribe to before the async work
  // so a concurrent tick sees the in-flight subscription and skips.
  // exchangeApiKey resolution happens off-tick; if it fails, we reset
  // below so the next tick retries.
  const targetSet = new Set(activeAgentIds);
  subscribedIntegrationContextAgentIds = targetSet;

  // ENG-9265 (CodeRabbit on #4861): an attempt is now IN FLIGHT for this set,
  // so hold off arming another until it has had time to resolve.
  //
  // The observed failure resolves ~10s after subscribe, and the tick is ~10s.
  // Without this, a tick that fires before the status callback lands sees
  // `nextAttemptAt` still in the past, clears the snapshot, and tears down the
  // pending subscription — so the attempt never gets to finish and the streak
  // never advances.
  //
  // Deliberately placed HERE and not in the gate above, which is where the
  // review suggested it. The gate is only one of the two ways a rebind starts:
  // when `onSubscriptionLost` fires inside the free-attempt window it clears
  // the snapshot itself, and the rebind then happens via the equality check
  // with the gate skipped entirely (it requires a non-empty snapshot). An
  // advance in the gate would miss exactly the free-window case the finding
  // describes. This line runs on every rebind, whichever path armed it.
  //
  // `Math.max` for the same reason: a backed-off entry must keep its longer
  // deadline, and during the free window `realtimeRebindBackoffMs` returns 0,
  // which would advance nothing.
  if (realtimeRebindFailures.size > 0) {
    holdRealtimeRebindInFlight(realtimeRebindFailures, targetSet, Date.now());
  }

  void exchangeApiKey(apiKey).then((exchange) => {
    // A newer tick may have replaced the snapshot (e.g. agent set changed
    // again, or the broader chat client disconnected and reset all flags).
    // Bail without mutating state or starting a now-stale subscription —
    // identity comparison is enough because we only ever assign fresh
    // `Set` instances to the module-level snapshot.
    if (subscribedIntegrationContextAgentIds !== targetSet) return;

    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) {
      subscribedIntegrationContextAgentIds = new Set();
      return;
    }

    startRealtimeIntegrationContext({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: [...targetSet],
      onContextChange: (payload) => {
        // Invalidate the affected agent's cached plugin-skill hashes so the
        // next poll definitely re-renders SKILL.md (the hash check would
        // otherwise be a no-op if the LLM-rendered output happens to be
        // identical, which is rare but possible). Then trigger an early poll.
        const agent = agentStates.find((a) => a.agentId === payload.agent_id);
        if (agent) {
          // Match the cache key shape used when hashes are written in the
          // refresh loop: `plugin-skill:${agent.agent_id}:...`. Using
          // codeName here (as the original version did) silently failed to
          // invalidate anything, so plugin-context changes could be skipped
          // until some unrelated bundle change happened or the manager
          // restarted.
          for (const key of agentState.knownSkillHashes.keys()) {
            if (key.startsWith(`plugin-skill:${agent.agentId}:`)) {
              agentState.knownSkillHashes.delete(key);
            }
          }
        }
        triggerEarlyPoll(`integration context changed for agent ${payload.agent_id}`);
      },
      log,
    });

    // ENG-4536: subscribed HERE rather than in its own ensure* function, on
    // purpose. It tracks exactly the same active-agent set, needs exactly the
    // same exchange, and re-binds on exactly the same trigger. A parallel
    // lifecycle over one set is a drift hazard: the two would eventually
    // disagree about which agents are subscribed, and the failure would be a
    // silently missing invalidation rather than an error.
    startRealtimeTokenRotation({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: [...targetSet],
      onTokenRotated: (agentId, event) => {
        // An early poll IS the whole reaction, and that is not a shortcut.
        //
        // Unlike the integration-context path above, nothing needs invalidating
        // first. That path has to clear `knownSkillHashes` because a re-render
        // can produce byte-identical output, so its hash gate would swallow the
        // change. Here the gate is `intHash` over
        // `definition_id + credentials + config` (ENG-4911), and a rotated token
        // is a changed credential BY DEFINITION — the gate opens by itself, and
        // the poll re-reads credentials from /host/agent-integrations rather
        // than from anything cached locally. So the poll rewrites
        // `.env.integrations` and the existing env-aware MCP child reap
        // (ENG-6062) applies it.
        //
        // What this changes is only WHEN that happens: on the rotation instead
        // of on the next scheduled poll, which is the 401 this ticket removes.
        triggerEarlyPoll(
          `integration token rotated for agent ${agentId} (integration ${event.integration_id}, ${event.definition_id})`,
        );
      },
      onSubscribed: (agentId) => {
        // ENG-9265: a streak that ends must say so. With the retry bounded,
        // silence no longer distinguishes "recovered" from "still backing off".
        const ended = realtimeRebindFailures.get(agentId);
        if (!ended) return;
        realtimeRebindFailures.delete(agentId);
        const stuckForSeconds = Math.floor((Date.now() - ended.since) / 1000);
        log(
          `[realtime] Token rotation subscription for agent ${agentId} recovered after `
          + `${ended.attempts} failed attempt(s) over ${stuckForSeconds}s (ENG-9265)`,
        );
      },
      onSubscriptionLost: (agentId, status) => {
        // Clear the snapshot so the NEXT tick rebinds. Without this the early
        // return at the top of this function (`activeAgentIdSetsEqual`) sees a
        // snapshot still equal to the active set and skips forever, so losing
        // these channels once loses invalidation for the life of the process.
        //
        // Clearing is safe to do more than once: the next tick re-subscribes
        // BOTH the integration-context and token-rotation channels together,
        // which is the pairing this function already maintains, and
        // `startRealtimeTokenRotation` tears down before rebuilding.
        //
        // Deliberately NOT re-subscribing inline. A channel that was refused
        // (the private topic denied by RLS, say) would be re-refused
        // immediately, and an inline retry would spin; the tick gives it a
        // natural backoff and one log line per cycle instead of a loop.
        // ENG-9265: record the streak and DEFER the clear. Clearing the
        // snapshot is what forces a rebind on the next tick, so not clearing it
        // is the backoff — and a genuine change to the active agent set still
        // rebinds immediately, because the equality check fails on its own
        // without consulting this map.
        const state = recordRealtimeRebindFailure(
          realtimeRebindFailures, agentId, status, Date.now(),
        );
        const waitMs = realtimeRebindBackoffMs(state.attempts);
        if (state.attempts === 1 || state.attempts > state.loggedAtAttempt) {
          state.loggedAtAttempt = state.attempts;
          const stuckForSeconds = Math.floor((Date.now() - state.since) / 1000);
          log(
            `[realtime] Token rotation subscription lost for agent ${agentId} (${status}) — `
            + `attempt ${state.attempts}, stuck ${stuckForSeconds}s, next rebind in `
            + `${Math.round(waitMs / 1000)}s (ENG-9265)`,
          );
        }
        if (waitMs === 0) subscribedIntegrationContextAgentIds = new Set();
      },
      log,
    });

    log(
      `[realtime] Integration context + token rotation subscriptions ${isResubscribe ? 're-bound' : 'started'} for ${targetSet.size} agent(s)`,
    );
  }).catch((err) => {
    // Reset so the next tick retries — but only if we're still the
    // owning snapshot. Otherwise a newer tick has already taken over
    // and clearing here would silently undo its in-flight work.
    if (subscribedIntegrationContextAgentIds === targetSet) {
      subscribedIntegrationContextAgentIds = new Set();
    }
    log(`[realtime] Integration context subscription failed: ${(err as Error).message}`);
  });
}

/**
 * Cancel the scheduled poll and run a new poll cycle on the next event-loop
 * tick. Used by realtime callbacks (plugin context, etc.) to collapse the
 * latency between an external change and the manager observing it.
 *
 * Idempotent — multiple triggers within the same tick coalesce into one
 * poll because pollCycle() is awaited and scheduleNext() only fires after
 * it completes.
 */
function triggerEarlyPoll(reason: string): void {
  if (!running) return;
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  log(`[realtime] Triggering early poll: ${reason}`);
  // ENG-10760: clear the idle backoff, not just the pending timer. Every caller
  // is a Realtime lane reporting that something CHANGED, and an assignment is
  // one of the things it can be — so the pass this schedules must be followed by
  // a normal-cadence one rather than dropping straight back to the cap.
  consecutiveEmptyAgentPolls = 0;
  // ENG-9585: a push always beats the spacing. Every caller of this function is
  // a Realtime lane reporting that desired state CHANGED, so the one pass this
  // schedules must actually go to the network — otherwise the early poll would
  // re-serve the cached payload and the event would be silently swallowed until
  // the TTL expired, which is strictly worse than not having subscribed.
  forceRefreshFetch();
  // Schedule on the next tick so we don't run reentrantly inside a Realtime
  // callback. scheduleNext() will fire on completion.
  pollTimer = setTimeout(() => {
    // ENG-9068: scheduleNext() is the normal post-poll re-arm. It used to live
    // inside a bare `.then()`, so a rejected cycle skipped it and the poll
    // loop stopped for good. `pollCycleAndReschedule` re-arms on both paths.
    // Forward declared at the bottom of this file; safe to reference here
    // because the function declaration is hoisted.
    void pollCycleAndReschedule(pollCycle, {
      onError: (err) => log(`[poll-loop] early-poll cycle rejected: ${(err as Error)?.message ?? String(err)}`),
      reschedule: () => scheduleNext(),
    });
  }, 0);
}

// ---------------------------------------------------------------------------
// Direct chat — poll for pending messages (fallback when Realtime is down)
// ---------------------------------------------------------------------------

// Track in-flight direct chat messages to avoid double-processing
const directChatInFlight = new Set<string>();

async function pollDirectChatMessages(agentStates: AgentState[]): Promise<void> {
  for (const agent of agentStates) {
    if (agent.status !== 'active') continue;

    try {
      const data = await api.post<{
        messages: Array<{
          id: string;
          session_id: string;
          content: string;
          created_at: string;
        }>;
      }>('/host/direct-chat/poll', { agent_id: agent.agentId });

      for (const msg of data.messages) {
        if (directChatInFlight.has(msg.id)) continue;
        directChatInFlight.add(msg.id);

        // Process async so we don't block the poll cycle
        processDirectChatMessage(agent, msg).finally(() => {
          directChatInFlight.delete(msg.id);
        });
      }
    } catch (err) {
      log(`Direct chat poll failed for '${agent.codeName}': ${(err as Error).message}`);
    }
  }
}

/**
 * ADR-0047: the opencode arm of direct-chat delivery. Unlike Claude Code (whose
 * in-session direct-chat MCP pulls the message and calls `direct_chat.reply`),
 * an opencode agent is driven entirely from the manager over HTTP: inject the
 * message, wait for the reply, and POST it back through /host/direct-chat/reply
 * (with `message_ids`, which advances the durable-queue cursor exactly as the
 * MCP's reply does). A THROWN failure leaves the row claimed so the next poll
 * redelivers it.
 *
 * ENG-8346: this docblock used to end "never a silent loss", and that claim was
 * false. `api.post` throws only on a non-2xx; a 200 that advanced ZERO rows
 * returned normally and the result was discarded without being inspected at all
 * — the single loudest instance of the pattern this ticket exists to close. The
 * shared verdict below now reads `consumed`, so a cursor that did not move is
 * logged and counted. It is still not RETRIED here (ENG-8346 deliberately
 * changes no control flow); the follow-up that decides whether it should be is
 * ENG-8347.
 */
async function processDirectChatMessageOpencode(
  agent: AgentState,
  msg: { id: string; session_id: string; content: string },
): Promise<void> {
  if (!isOpencodeSessionHealthy(agent.codeName)) {
    // The server is spawned on the session tick (ensureOpencodeRuntime); until
    // it is up, leave the claimed row for the next poll to redeliver.
    log(`[direct-chat] opencode server not running for '${agent.codeName}' (msg=${msg.id}) — leaving for redelivery`);
    return;
  }

  let result: Awaited<ReturnType<typeof injectOpencodeMessage>>;
  try {
    result = await injectOpencodeMessage(agent.codeName, {
      channelId: 'direct-chat',
      conversationKey: msg.session_id,
      senderId: 'webapp',
      text: msg.content,
      // Provenance parity with the Claude Code <channel> envelope: source +
      // reply-expected, folded into the prompt by the bridge.
      meta: { source: 'direct-chat', user: 'webapp', lane: 'conversational', requires_reply: 'true' },
    });
  } catch (err) {
    log(`[direct-chat] opencode inject failed for '${agent.codeName}' (msg=${msg.id}): ${(err as Error).message} — leaving for redelivery`);
    return;
  }

  if (result.status === 'replied' && result.reply) {
    // ENG-8393 (CodeRabbit, PR #4023): `api.post` THROWS on a non-2xx, so the
    // record below is only reached when the status was 2xx. A host whose reply
    // POSTs are all rejected — the exact case the `failed` key exists to make
    // visible — would otherwise emit no telemetry at all, which is the
    // unreadable zero this ticket is about. The catch records it instead.
    //
    // The flag is not decoration: everything after the record in the try (the
    // log calls, `formatCursorAdvanceShortfall`) can throw, and recording again
    // in the catch would double-count one advance as both success and failure.
    let recorded = false;
    try {
      // ENG-8346: `api.post` throws on a non-2xx (handleResponse), so reaching
      // here means the STATUS was 2xx — hence `httpOk: true`, rather than
      // pretending this site evaluated a status it never sees. It does NOT mean
      // the reply succeeded: `handleResponse` returns a 2xx body unchanged, so a
      // `200 {error: ...}` arrives here and the verdict below is what catches it
      // (CodeRabbit, PR #3995 — an earlier revision of this comment claimed the
      // only open question was whether the cursor moved, which was wrong).
      const body = (await api.post('/host/direct-chat/reply', {
        agent_id: agent.agentId,
        session_id: msg.session_id,
        content: result.reply,
        message_ids: [msg.id],
      })) as CursorAdvanceResponseBody;
      const verdict = classifyCursorAdvance({
        messageIds: [msg.id],
        httpOk: true,
        body,
      });
      // ENG-8393: record EVERY verdict, and do it here — before the branches —
      // rather than inside the shortfall arm below. Recording only the shortfall
      // is what left the metric with no denominator: a fleet-wide zero could not
      // be told apart from a host that never ingested, which is unreadable for a
      // metric whose whole job is to gate the ENG-8347 decision. Placed above
      // the `failed` early-return so that arm is counted too.
      //
      // The counter dir is derived from `paneLogPath` — the SAME helper the
      // responsiveness probe's reader goes through (`attachSupplementalCounters`
      // is handed `dirname(panePath)`). Re-joining `~/.augmented/<codeName>`
      // inline would work today and silently stop working the moment the two
      // sides disagree about the layout, which under ADR-0049 (agent_id-keyed
      // dirs, codename kept as a compatibility symlink) is a live risk — the
      // migration engine landed in this very file. A counter written where the
      // probe does not look reads as a clean zero meaning "never ingested",
      // which is the precise failure this counter exists to prevent. Deriving
      // from the reader's own helper makes the two move together.
      recordCursorAdvanceOutcome(
        dirname(paneLogPath(agent.codeName)),
        'direct-chat-manager',
        'reply',
        verdict,
      );
      recorded = true;
      if (verdict.outcome === 'failed') {
        // A `200 {error: ...}`. Nothing local is cleared on this arm, so the row
        // stays claimed and the next poll redelivers it either way — but saying
        // "opencode replied" for a reply the API refused is the same
        // confidently-wrong report this whole ticket exists to remove, one line
        // below the verdict that knows better.
        log(
          `[direct-chat] opencode reply REFUSED for '${agent.codeName}' (msg=${msg.id}): ` +
            `${verdict.error} — leaving for redelivery`,
        );
        return;
      }
      if (verdict.outcome === 'shortfall') {
        log(
          formatCursorAdvanceShortfall(verdict, {
            route: 'reply',
            site: 'opencode-reply',
            sessionId: msg.session_id,
            // Nothing local to clear on this arm — the manager holds no claim
            // tracker and writes no pending marker for an opencode agent. The
            // field is still emitted (as `none`) so a fleet-wide grep for
            // `cleared_anyway=` does not silently skip this site.
            cleared: [],
          }),
        );
      }
      log(`[direct-chat] opencode replied for '${agent.codeName}' (msg=${msg.id}, ${result.reply.length} chars)`);
    } catch (err) {
      if (!recorded) {
        recordCursorAdvanceOutcome(
          dirname(paneLogPath(agent.codeName)),
          'direct-chat-manager',
          'reply',
          { outcome: 'failed', error: (err as Error).message, expected: 1 },
        );
      }
      log(`[direct-chat] opencode reply POST failed for '${agent.codeName}' (msg=${msg.id}): ${(err as Error).message} — leaving for redelivery`);
    }
    return;
  }

  if (result.status === 'declined') {
    log(`[direct-chat] opencode gate declined message for '${agent.codeName}' (msg=${msg.id}): ${result.reason}`);
    return;
  }

  // Admitted but produced no reply text — surface it and leave the row for the
  // next poll rather than marking it delivered with an empty answer.
  log(`[direct-chat] opencode produced no reply for '${agent.codeName}' (msg=${msg.id}) — leaving for redelivery`);
}

async function processDirectChatMessage(
  agent: AgentState,
  // `created_at` is present on the poll/backlog path (the replay path) and
  // absent on the Realtime path (those messages are by definition fresh). It
  // drives the backlog-staleness expiry below.
  msg: { id: string; session_id: string; content: string; created_at?: string },
): Promise<void> {
  // ENG-5963: record inbound activity for the defer-until-idle restart gate —
  // stamped before the expiry/delivery branches so even a drained backlog
  // message counts as "the agent was just addressed".
  noteInbound(agent.codeName);
  const fw = agentFrameworkCache.get(agent.codeName) ?? DEFAULT_FRAMEWORK;
  log(`[direct-chat] Processing message for '${agent.codeName}' (fw=${fw}): id=${msg.id} len=${msg.content.length}`);

  // Backlog-staleness expiry (checked BEFORE any delivery attempt). A backlog
  // message that replayed long after the user gave up isn't worth delivering at
  // all. Drain it with a terminal notice (visible degradation, not silent loss)
  // so it leaves the pending set and never replays again. Realtime messages
  // carry no `created_at` and so never expire here; expiry is disabled entirely
  // when the window is 0.
  if (isDirectChatMessageExpired(msg.created_at, Date.now(), directChatMaxAgeMs())) {
    log(`[direct-chat] Expired backlog message for '${agent.codeName}' (msg=${msg.id}, created_at=${msg.created_at}) — draining without delivery`);
    try {
      await api.post('/host/direct-chat/reply', {
        agent_id: agent.agentId,
        session_id: msg.session_id,
        content: '[Expired] This message timed out before it could be processed. Please resend it.',
        // ENG-8274: kind:'notice' — the manager is draining a stale backlog
        // message. That is the system reporting a delivery outcome, not the agent
        // answering, and it owes no reply. Without this it rendered as an agent
        // bubble, so a timeout the agent never even saw read as the agent's words.
        kind: 'notice',
      });
    } catch {
      // Best-effort drain — if the reply post fails the poll will retry the
      // message on the next cycle.
    }
    return;
  }

  // ADR-0047: opencode agents have no in-session direct-chat MCP to pull the
  // message and call `direct_chat.reply`. The manager drives the whole exchange
  // over HTTP instead: inject into the agent's `opencode serve`, wait for the
  // reply, and post it back through the same /host/direct-chat/reply endpoint the
  // Claude Code MCP uses (message_ids advances the queue cursor). This is the
  // first real inbound channel wired for the opencode runtime.
  if (fw === 'opencode') {
    await processDirectChatMessageOpencode(agent, msg);
    return;
  }

  // ENG-8461: if this agent had an idle day-rollover DEFERRED (warm session kept,
  // fresh mint owed), THIS inbound is the first real trigger — mint the fresh
  // session now so the message is answered on today's UUID, and the boot is paid
  // alongside real work rather than at an idle midnight for nothing. The helper is
  // idle-gated, staleness-gated (PR #4126) and idempotent; on a successful reset
  // the session is torn down, so we leave this message PENDING (never delivered,
  // cursor not advanced) and the poll respawns a fresh session that redelivers it
  // via ENG-5927 durable replay — exactly the existing "no healthy session" path,
  // only triggered deliberately. The agent's effective timezone comes from the
  // cached poll inputs (cachedAgentTimezoneFor), so the staleness check here uses
  // the SAME day-boundary clock as the poll block — no host-local drift for a
  // tz-mismatched agent.
  if (performLazyDayRolloverReset(agent.codeName, cachedAgentTimezoneFor(agent.codeName))) {
    log(
      `[direct-chat] Reset '${agent.codeName}' to a fresh session on first inbound after an idle ` +
        `day-rollover (msg=${msg.id}) — leaving pending for durable replay on the fresh session [ENG-8461]`,
    );
    return;
  }

  // ENG-4778: when a persistent session is running for this agent, deliver the
  // direct-chat message into it (doorbell ring, or send-keys inject as a
  // fallback) so the agent answers with full conversation history. Inject as a
  // `<channel>` tag so the agent's existing system-prompt instructions for
  // direct-chat (call `direct_chat.reply` with the session_id) still apply.
  //
  // ENG-6850: there is no `claude -p` one-shot fallback anymore. A claude-code
  // agent with no healthy session (or an unverified inject) leaves the message
  // PENDING on the durable queue (ENG-5927) so it redelivers on the next poll /
  // session recovery — see the early-return at the end of this function.
  // ENG-10335: a session blocked on a prompt nothing can answer passes
  // isSessionHealthy() (claude is running) but has no in-session MCP to pull a
  // doorbell, and a send-keys inject would type into the prompt. Treat it like an
  // unhealthy session: fall through to the leave-pending path below, so durable
  // replay delivers the message once the prompt is answered.
  const blockedOnPrompt = sessionBlockedOnPrompt(agent.codeName);
  if (blockedOnPrompt && shouldLogBlockedPrompt(agent.codeName, 'direct-chat')) {
    log(
      `[direct-chat] Not delivering to '${agent.codeName}' (msg=${msg.id}) — session is blocked on an ` +
        `interactive prompt (${blockedOnPrompt}), so nothing in-session can pull it; leaving pending for ` +
        `durable replay once the prompt is answered (further messages this episode are not logged) [ENG-10335]`,
    );
  }
  // ENG-10797: AND TELL THE SENDER. The log line above goes to manager.log,
  // which the person who just typed a message cannot see. Until now their
  // message simply sat there: no reply, no error, no indication it had arrived
  // somewhere that cannot answer. Seven hosts were in this state at once during
  // the weekend outage.
  //
  // `login-picker` ONLY, and the other two kinds are excluded for opposite
  // reasons rather than out of caution:
  //   usage-limit-unanswerable - ENG-10497 / PR #6038 already owns that wording,
  //     and two systems apologising for one episode is worse than one.
  //   unclaimed-modal - a corrupted or unreadable ~/.claude.json. Telling that
  //     sender to re-authenticate points them at a credential that is fine;
  //     ENG-10140 established the remedy there is usually a container recycle.
  //     No notice is worse than a wrong one, and a right one needs its own copy.
  //
  // ITS OWN SURFACE, not 'direct-chat'. `shouldLogBlockedPrompt` CONSUMES the
  // token it returns (`loggedSurfaces.add(surface)`), so reusing 'direct-chat'
  // here would find it already spent by the log above and this would never fire
  // - silently, and only in production, because a test that stubs the log away
  // would pass.
  if (blockedOnPrompt === 'login-picker' && shouldLogBlockedPrompt(agent.codeName, 'direct-chat-notice')) {
    try {
      await api.post('/host/direct-chat/reply', {
        agent_id: agent.agentId,
        session_id: msg.session_id,
        // ENG-9592: `kind: 'notice'` is MANDATORY and is the whole reason this
        // is safe to send. The column DEFAULTS to 'message', and a 'message'
        // row is a reply-EXPECTING human turn - so omitting this would hand a
        // blocked agent a question about its own broken sign-in, which it
        // cannot answer because it is not running. Push-and-consumed only.
        kind: 'notice',
        content:
          `'${agent.codeName}' received your message but cannot start: it is stuck on the Claude ` +
          `login screen, which needs an operator to complete sign-in on its host. Your message is ` +
          `saved and will be delivered automatically once that is done - you do not need to resend it.`,
      });
    } catch {
      // Best-effort. The message itself stays pending either way, so a failed
      // notice costs the sender an explanation, not their message.
    }
  }
  if (!blockedOnPrompt && isSessionHealthy(agent.codeName)) {
    // ENG-5927 (PR-2): doorbell + pull-cursor rail (ADR-0020 §Decision 4),
    // gated default-OFF behind AGT_DIRECT_CHAT_DOORBELL_ENABLED. When enabled we
    // ring a *content-free doorbell* — touch <agentDir>/direct-chat-doorbell —
    // instead of send-keys injecting the content. The agent's in-session
    // direct-chat MCP watches that file, PULLS via the capped
    // /host/direct-chat/poll claim, and pushes the message as a native
    // notifications/claude/channel (no keystrokes). The durable queue is the
    // cursor: the row advances pending→processing on the MCP's claim and
    // →delivered when the agent replies. A dropped ring costs only latency (the
    // MCP's safety-net poll drains it), so the ring needs no ack. On a ring
    // failure we fall through to the existing send-keys path — silent loss is
    // worse than a possible duplicate.
    // ENG-6818: isolated agents take the doorbell rail. The rail is right; the
    // reason this comment gave for it was not, and ENG-10688 measured it.
    //
    // It used to say that under Docker isolation `tmux send-keys` "lands in the
    // host pane and never reaches the in-container claude". It does reach it.
    // Measured on `agt-aws-1`, 2026-09-19: a synthetic tmux session running
    // `docker run --rm -it` on `agt-runtime:latest` over a line-echo process,
    // driven by this file's own two-step send (`send-keys -l <text>`, then
    // `send-keys Enter`), printed the text back FROM INSIDE the container.
    // `docker run -it` wires the container's stdin to the pane's pty, so the
    // bytes cross. No agent pane was touched by that probe.
    //
    // The symptom ENG-6818 saw — text visible in the input buffer, no turn — is
    // the signature of the bracketed-paste / CSI-u defect diagnosed and fixed
    // AFTERWARDS at `persistent-session.ts:4070-4103`, which drops the encoded
    // carriage return regardless of isolation. That is the likelier cause, and
    // the container boundary took the blame for it.
    //
    // So this branch is a PREFERENCE, not a necessity, and it stays one: the
    // doorbell+pull rail is a durable queue with a cursor (pending→processing→
    // delivered) rather than keystrokes into a TUI, and the in-container MCP has
    // it force-enabled via AGT_DIRECT_CHAT_DOORBELL_ENABLED
    // (buildDockerRunCommand). A failed ring here is not fatal: the MCP's 30s
    // safety-net poll still drains the pending row. Do not re-derive the old
    // claim from this branch's existence — if you need send-keys to reach an
    // isolated agent it works; the question is whether the durable rail is
    // better, and that is what this branch answers.
    const useDoorbell =
      hostFlagStore().getBoolean('direct-chat-doorbell') ||
      isolationMode(agent.codeName) === 'docker';
    if (useDoorbell) {
      try {
        const doorbell = directChatDoorbellPath(agent.agentId, homedir());
        mkdirSync(dirname(doorbell), { recursive: true });
        writeFileSync(doorbell, String(Date.now()));
        log(`[direct-chat] Doorbell rung for '${agent.codeName}' (msg=${msg.id}) — in-session MCP will pull via the cursor`);
        return;
      } catch (err) {
        // For an isolated agent, stay on the durable rail rather than falling
        // through: the row stays pending and the MCP safety-net poll drains it.
        // Note this is a CHOICE, not a forced hand — ENG-10688 measured that
        // send-keys does cross into the container (see above). Keeping the
        // message on the queue preserves its cursor and its delivery record,
        // where a keystroke fallback would deliver it with neither. For host
        // agents, fall through to send-keys as before.
        if (isolationMode(agent.codeName) === 'docker') {
          log(`[direct-chat] Doorbell ring failed for isolated '${agent.codeName}' (msg=${msg.id}): ${(err as Error).message} — leaving pending for the in-session safety-net poll`);
          return;
        }
        log(`[direct-chat] Doorbell ring failed for '${agent.codeName}' (msg=${msg.id}): ${(err as Error).message} — falling back to send-keys inject`);
        // fall through to the send-keys path below
      }
    }
    // CodeRabbit (PR #748): escape session_id + content before splicing
    // into the XML-shaped envelope so a payload containing quotes,
    // angle brackets, or a literal `</channel>` can't break the
    // envelope structure (and possibly steer the agent's downstream
    // routing). Apply to both attribute values and body for safety —
    // escaped < / > in the body still render legibly to the agent.
    const escapeXml = (value: string): string =>
      value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&apos;');
    // ENG-6407 / ADR-0024 Slice 1: stamp provenance + reply-obligation on the
    // tag at the source. A manager-injected direct-chat message is conversational
    // and reply-expected. The fragment is a closed enum / boolean, so it needs no
    // escaping. Additive only - no consumer gates on these yet (Slice 2).
    const channelEnvelope =
      `<channel source="direct-chat" session_id="${escapeXml(msg.session_id)}" user="webapp" ${laneTagFragment('conversational', true)}>\n` +
      `${escapeXml(msg.content)}\n` +
      `</channel>`;
    const delivered = await injectMessage(
      agent.codeName,
      'chat',
      channelEnvelope,
      { task_name: 'direct-chat' },
      log,
    );
    if (delivered) {
      // agent-tool-name-allow: a manager log line read by operators, not by the agent —
      // naming the tool as the SERVER registers it is what matches the dispatch here.
      log(`[direct-chat] Injected into persistent session for '${agent.codeName}' (msg=${msg.id}); agent will reply via direct_chat.reply tool`);
      return;
    }
    // ENG-6850: injectMessage returned false — in-session delivery is
    // `tmux send-keys`, which doesn't guarantee submission. There is no
    // `claude -p` one-shot fallback anymore, so don't strand or duplicate:
    // leave the message PENDING (the claude-code early-return below) and let
    // ENG-5927 durable replay redeliver it on the next poll / session recovery.
    // The backlog-staleness expiry above is the terminal backstop.
    log(`[direct-chat] Inject reported unverified for '${agent.codeName}' (msg=${msg.id}) — leaving pending for durable replay`);
  }

  // ENG-6850: claude-code agents have no one-shot `claude -p` fallback. Reaching
  // here means the message wasn't delivered in-session (no healthy session, a
  // doorbell ring that failed on a host agent, or an unverified send-keys
  // inject) — leave it PENDING so ENG-5927 durable replay redelivers on the next
  // poll / session recovery. A message that goes stale is drained by the expiry
  // check above with a terminal notice (visible degradation, never silent loss).
  log(`[direct-chat] No in-session delivery for '${agent.codeName}' (msg=${msg.id}) — leaving pending for ENG-5927 durable replay`);
}

// ---------------------------------------------------------------------------
// Cron result harvesting — polls cron run history and updates agent status
// ---------------------------------------------------------------------------

// Template-id classification, the hybrid-actionable predicate, and the kanban
// LLM-output parsers now live in ./manager/kanban/parsers.js (ADR-0011 leaf
// extraction); the stateful inject/harvest code below imports from there.

// Module-level debounce state. See design-doc Q3 for the
// "no manager-state.json persistence" rationale.
const lastKanbanInjectAt = new Map<string, number>();

// ENG-7704: per-agent last-nudged actionable-board signature + timestamp. Backs
// off the fast KANBAN_HYBRID_DEBOUNCE_MS cadence to the slow full cadence while
// the actionable board is UNCHANGED (nothing new to self-action), stopping the
// ~60-75s re-nudge loop that fires when the durable enqueue keeps failing and
// send-keys (no dedup) re-fires every window. Cleared on board drain.
// ENG-7798: also carries nudgeCount (consecutive no-op nudges) driving the
// escalating cadence, and round-trips through kanban-nudge-state.json so a
// manager restart doesn't reset every blocked agent to the fast cadence.
// Mutate ONLY via the setKanbanNudgeState/clearKanbanNudgeState helpers so
// every change persists.
//
// ENG-7891 slice 5 (ADR-0049 durable-state rule): this sidecar is DURABLE state
// keyed per-agent, so it converges on the rename-stable runtime key. Every
// access goes through kanbanNudgeStateKey(), which returns the agent_id on the
// id-keyed layout and the codeName on legacy - so on the pre-arming fleet the
// map and its kanban-nudge-state.json file are keyed exactly as before (a
// no-op). Because load/save just serialise whatever keys the map holds, keying
// consistently at every access point keeps the in-memory map and the on-disk
// file in the same key-space with no reverse lookup. When an agent is migrated
// to the id-keyed layout, the file it loaded still holds a codeName-keyed entry;
// getKanbanNudgeState() dual-reads and rekeys that legacy entry onto the runtime
// key on first touch, so the backoff cadence is preserved across the flip and no
// stale codename entry is orphaned in the file. On legacy hosts the runtime key
// equals the codeName, so the fallback never fires.
const kanbanNudgeStateByCode = new Map<string, KanbanNudgeBackoffState>();

/** ADR-0049 durable-state key for the nudge sidecar (agent_id when id-keyed). */
function kanbanNudgeStateKey(codeName: string): string {
  return agentRuntimeKey(codeName);
}

/**
 * Read the nudge state, migrating a surviving legacy codeName-keyed entry onto
 * the runtime key on first touch after the id-keyed layout is armed (dual-read +
 * rekey + persist). Without this the post-arming lookup would miss the entry the
 * previous codeName-keyed manager persisted, resetting the escalating cadence
 * once AND orphaning the codeName key in the file. No-op on legacy hosts
 * (runtime key === codeName, so the fallback branch is skipped).
 */
function getKanbanNudgeState(codeName: string): KanbanNudgeBackoffState | undefined {
  const key = kanbanNudgeStateKey(codeName);
  const hit = kanbanNudgeStateByCode.get(key);
  if (hit || key === codeName) return hit;
  const legacy = kanbanNudgeStateByCode.get(codeName);
  if (!legacy) return undefined;
  kanbanNudgeStateByCode.delete(codeName);
  kanbanNudgeStateByCode.set(key, legacy);
  saveKanbanNudgeState(kanbanNudgeStateByCode, channelHashCacheDir());
  return legacy;
}

function loadKanbanNudgeStateFromDisk(): void {
  loadKanbanNudgeState(kanbanNudgeStateByCode, channelHashCacheDir());
}

function setKanbanNudgeState(codeName: string, state: KanbanNudgeBackoffState): void {
  const key = kanbanNudgeStateKey(codeName);
  // Drop any surviving legacy codeName entry so the flip leaves nothing stale.
  if (key !== codeName) kanbanNudgeStateByCode.delete(codeName);
  kanbanNudgeStateByCode.set(key, state);
  saveKanbanNudgeState(kanbanNudgeStateByCode, channelHashCacheDir());
}

function clearKanbanNudgeState(codeName: string): void {
  const key = kanbanNudgeStateKey(codeName);
  let removed = kanbanNudgeStateByCode.delete(key);
  // Also clear a legacy codeName entry that predates the key flip.
  if (key !== codeName && kanbanNudgeStateByCode.delete(codeName)) removed = true;
  if (!removed) return;
  saveKanbanNudgeState(kanbanNudgeStateByCode, channelHashCacheDir());
}

/**
 * ENG-7798: the nudgeCount for the nudge being delivered NOW — one more
 * consecutive no-op if the signature is unchanged, else a fresh count of 1.
 */
function nextKanbanNudgeCount(codeName: string, signature: string): number {
  const prev = getKanbanNudgeState(codeName);
  return prev && prev.signature === signature ? (prev.nudgeCount ?? 1) + 1 : 1;
}

// ENG-5565: the currently-open injected `runs` row per agent (codeName →
// run_id). Each manager-injected kanban nudge opens a run; the next inject
// (the agent is idle again, so the prior work is done) closes it. The run_id
// is also written into the injected prompt as a marker (see formatRunMarker)
// so the per-turn transcript parser can attribute that run's token usage.
// At most one open injected run per agent; the last one stays open until the
// next inject (or session end) — fine, since attribution is marker-bounded.
const openInjectedRunByCode = new Map<string, string>();

// ENG-5565: close an agent's open injected run if one exists. Fire-and-forget
// (the map is drained synchronously; finishRun POSTs in the background). Called
// when the board drains (kanban work done), the next inject supersedes it, or
// the session is torn down — so a run never hangs open past its real
// prompt-ready boundary, which would mis-attribute later turns. A manager
// restart that loses this in-memory map can't close in-process; a server-side
// stale-run reaper is the backstop there (tracked for B3/hardening).
function closeInjectedRunIfOpen(
  codeName: string,
  outcome: 'completed' | 'failed' | 'cancelled' | 'timeout',
  outcomeMessage: string,
): void {
  const runId = openInjectedRunByCode.get(codeName);
  if (!runId) return;
  openInjectedRunByCode.delete(codeName);
  void finishRun(runId, outcome, { outcomeMessage });
}

// ENG-8818: the persistent-session handle's tracking + close live in
// manager/scheduler/state.ts alongside closeScheduledRunsForCode — same shape,
// same teardown callers, and testable there without importing this module's
// 13k-line graph. See that file for why it is a separate map from
// openInjectedRunByCode above.
//
// Outcome discriminates the three ways a session can end, which is what makes
// the row worth reading afterwards:
//   - 'completed' — a clean stop (deprovision, routine restart, day-rollover).
//   - 'failed' / 'timeout' — the caller identified a platform or credential
//     fault (ENG-8260's runClose), so the row records the infra kill rather
//     than laundering it into an ordinary stop.
//   - reaped as 'timeout' server-side — the manager died without running the
//     close at all, which is precisely the case a host-side close can't cover.

// ENG-7557: board-drained handling for an agent with an open nudge cycle. The
// caller has already drained the openInjectedRunByCode entry (synchronously,
// so a concurrent tick can't double-close) and hands us its run id. Cancel any
// still-pending kanban-check notice server-side, then finish the run:
//   - cancelled > 0  → the nudge never reached the agent; 'cancelled' with a
//     distinct message keeps it out of real-kanban-work telemetry. The cancel
//     route also closes the run from the notice payload; /host/runs/finish is
//     first-write-wins, so the double finish is a harmless no-op.
//   - otherwise      → the nudge was delivered and the work is done; 'completed'
//     with the pre-ENG-7557 message (same as closeInjectedRunIfOpen did here).
export async function cancelKanbanNoticeOnDrain(
  codeName: string,
  agentId: string,
  openRunId: string,
): Promise<void> {
  const cancelled = await cancelKanbanNotice(agentId);
  if (cancelled != null && cancelled > 0) {
    log(
      `[manager-worker] cancelled ${cancelled} pending kanban nudge(s) for '${codeName}' - board drained before delivery`,
    );
    void finishRun(openRunId, 'cancelled', {
      outcomeMessage: 'kanban nudge cancelled - board drained before delivery',
    });
  } else {
    void finishRun(openRunId, 'completed', { outcomeMessage: 'kanban board drained' });
  }
}

// Floor at 10s — anything tighter risks pummelling the REPL when the
// agent's first response itself adds new actionable items.
// CR (CodeRabbit on PR #1293): clamp sub-10 overrides to 10s rather
// than discarding them and falling back to the 60s default. An operator
// who sets `=5` is asking for "as fast as you can safely go", not "give
// me a slow default."
const KANBAN_HYBRID_DEBOUNCE_MS = (() => {
  const raw = process.env['AGT_KANBAN_HYBRID_DEBOUNCE_SECONDS'];
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return Math.max(parsed, 10) * 1000;
  return 60_000;
})();

// ENG-7704: the slow "full cadence" the nudge backs off to while the actionable
// board is unchanged (nothing new to self-action). New/changed cards still
// nudge at the fast KANBAN_HYBRID_DEBOUNCE_MS. Default 5 min; env-overridable
// (clamped to at least the fast debounce so it can never be faster than it).
const KANBAN_HYBRID_FULL_CADENCE_MS = (() => {
  const raw = process.env['AGT_KANBAN_HYBRID_FULL_CADENCE_SECONDS'];
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return Math.max(parsed * 1000, KANBAN_HYBRID_DEBOUNCE_MS);
  return Math.max(5 * 60_000, KANBAN_HYBRID_DEBOUNCE_MS);
})();

// ENG-7798: the ceiling the escalating unchanged-board cadence backs off to
// (full, 3x, 9x, ... capped here — default 5m -> 15m -> 45m -> 60m). A board
// whose only actionable cards are blocked stops burning a no-op turn every
// full-cadence window forever; a signature change (card added/moved) still
// nudges at the fast debounce. Env-overridable tunable, clamped to at least
// the full cadence so escalation can never undercut it.
const KANBAN_HYBRID_MAX_CADENCE_MS = (() => {
  const raw = process.env['AGT_KANBAN_HYBRID_MAX_CADENCE_SECONDS'];
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return Math.max(parsed * 1000, KANBAN_HYBRID_FULL_CADENCE_MS);
  return Math.max(60 * 60_000, KANBAN_HYBRID_FULL_CADENCE_MS);
})();

/**
 * ENG-6785: deliver the kanban check over the direct-chat durable rail. Opens
 * an attribution run (same model as the send-keys path — the run marker rides
 * the notice content so the transcript parser still attributes the work), then
 * enqueues a kind:'notice' direct_chat_messages row via /host/kanban/notify and
 * rings the existing direct-chat doorbell so the in-session MCP pulls promptly.
 * Returns true when the rail accepted the nudge. Never throws.
 *
 * ENG-8743: `false` does NOT mean "fall back to send-keys" — that fallback was
 * removed by ENG-7967 and must not come back (send-keys has no consume-time
 * revalidation, so it re-introduces ENG-7958 exactly when the durable rail is
 * unhealthy). It means: log it, and let the recurring hybrid nudge retry. All
 * three callers — the hybrid tick and the two ENG-8743 work-trigger sites — take
 * that path.
 */
async function injectKanbanCheckViaDirectChat(
  codeName: string,
  agentId: string,
  actionableCount: number,
): Promise<boolean> {
  if (isKanbanHybridDryRun()) {
    log(
      `[manager-worker] kanban inject DRY-RUN (direct-chat) — would enqueue for '${codeName}' (${actionableCount} actionable items)`,
    );
    // Roll back the stamp so the next tick can DRY-RUN-log again immediately.
    lastKanbanInjectAt.delete(codeName);
    return true;
  }

  // The agent is being nudged again, so any previously-injected run is finished
  // — close it before opening the next (marker-bounded attribution).
  closeInjectedRunIfOpen(codeName, 'completed', 'kanban inject superseded by next inject');

  const { run_id } = await startRun({
    agent_id: agentId,
    source_type: 'kanban',
    metadata: { injected: 'kanban-check', actionable_count: actionableCount, delivery: 'direct-chat' },
  });

  const content = run_id
    ? `${KANBAN_CHECK_COMMAND}\n${formatRunMarker(run_id)}`
    : KANBAN_CHECK_COMMAND;

  const ok = await enqueueKanbanNotice({ agentId, content, runId: run_id, actionableCount });

  if (!ok) {
    // Enqueue failed — don't leave a phantom open run for a nudge that never
    // landed. Close it and let the caller fall back to send-keys.
    if (run_id) void finishRun(run_id, 'failed', { outcomeMessage: 'kanban notice enqueue failed' });
    return false;
  }

  if (run_id) openInjectedRunByCode.set(codeName, run_id);

  // Ring the existing direct-chat doorbell so the in-session MCP pulls the notice
  // promptly rather than waiting for its safety-net poll. Best-effort: a missed
  // ring only costs latency (the notice is durable in the queue).
  try {
    const doorbell = directChatDoorbellPath(agentId, homedir());
    mkdirSync(dirname(doorbell), { recursive: true });
    writeFileSync(doorbell, String(Date.now()));
  } catch (err) {
    log(`[kanban] doorbell ring failed for '${codeName}': ${(err as Error).message} (notice still queued)`);
  }

  log(
    `[manager-worker] Enqueued kanban check via direct-chat for '${codeName}' (${actionableCount} actionable items)${run_id ? ` run_id=${run_id}` : ''}`,
  );
  return true;
}

/**
 * Per-agent inject decision: debounce-then-idle-then-send. Stamps the
 * debounce BEFORE the idle check (design-doc Q3 mitigation) so two
 * back-to-back ticks don't both proceed into a double send. Idle skip
 * is a soft-fail: the agent gets another shot on the next tick.
 *
 * Fire-and-forget — caller MUST NOT await the outer promise, or a
 * busy agent's 10s `waitForPromptReady` cap serialises every later
 * agent in the same poll cycle.
 */
export async function maybeInjectKanbanCheck(
  codeName: string,
  agentId: string,
  actionableCount: number,
  // ENG-7704: stable signature of the actionable ('allowed') cards this tick.
  signature: string,
): Promise<void> {
  // ENG-6876: back off on consecutive DELIVERY failures, before anything else.
  // This gate is deliberately independent of the board signature — a dead rail
  // is not an idle board, and on an active agent the signature changes
  // constantly, so folding this into the unchanged-board gate below would let
  // every card move reset it and re-establish the every-debounce loop.
  //
  // Until this existed the escalation was wired to the SUCCESS path only
  // (setKanbanNudgeState lives inside `if (ok)`), so a persistently failing
  // enqueue never advanced nudgeCount, never escalated, and re-fired every
  // KANBAN_HYBRID_DEBOUNCE_MS (default 60s) per agent indefinitely — each
  // attempt opening and closing a `runs` row for a nudge that never landed.
  const failureGate = shouldAttemptNoticeEnqueue(
    getKanbanNudgeState(codeName),
    Date.now(),
    KANBAN_HYBRID_DEBOUNCE_MS,
    KANBAN_HYBRID_MAX_CADENCE_MS,
  );
  if (!failureGate.attempt) return;

  // ENG-7704: back off the fast cadence while the actionable board is UNCHANGED
  // (nothing new to self-action). A changed signature (new/changed card) passes
  // straight through to the fast debounce below; an unchanged board re-nudges
  // only after the full cadence.
  //
  // ENG-6876 correction to this comment: it used to claim this "kills the
  // ~60-75s send-keys retry-loop that fires when the durable enqueue keeps
  // failing". That was true while the send-keys fallback existed. Once ENG-7967
  // made the durable rail the sole path, the failure branch below BECAME the
  // loop, and this gate never saw it — the failure gate above is what covers it.
  if (
    !shouldNudgeUnchangedBoard(
      signature,
      getKanbanNudgeState(codeName),
      Date.now(),
      KANBAN_HYBRID_FULL_CADENCE_MS,
      KANBAN_HYBRID_MAX_CADENCE_MS,
    )
  ) {
    return;
  }

  const lastAt = lastKanbanInjectAt.get(codeName) ?? 0;
  if (Date.now() - lastAt < KANBAN_HYBRID_DEBOUNCE_MS) {
    // Quiet log — every 5s poll tick on a chatty board would otherwise
    // emit a debounce line per agent. Only log every ~10th skip via a
    // counter would be nicer; for v1 we just log nothing on debounce-
    // skip and let the next successful inject mark the cadence.
    return;
  }
  // Stamp NOW so a concurrent tick can't race past the debounce. A
  // failed send (idle skip, tmux gone) leaves the stamp in place for
  // one debounce window — acceptable: the next inject just waits one
  // window before retrying.
  lastKanbanInjectAt.set(codeName, Date.now());

  // ENG-7967: durable kanban delivery is the SOLE path. The nudge is enqueued
  // onto the verified direct-chat notice rail (ENG-6785), whose consume-time
  // gate (agentHasActionableKanban, ENG-7557/ENG-7958) re-checks the REAL board
  // before delivery. The board state is already durable (ADR-0020 T1), so the
  // in-session MCP pulls the notice when the agent is free and no prompt-ready
  // gate is needed. The legacy tmux send-keys path was removed with the
  // kanban-doorbell flag (ENG-7967): it had NO consume-time revalidation, so it
  // typed a nudge straight into the pane off the manager's own (possibly stale)
  // board belief - exactly the bug class behind ENG-7958.
  const priorState = getKanbanNudgeState(codeName);
  const ok = await injectKanbanCheckViaDirectChat(codeName, agentId, actionableCount);
  if (ok) {
    // ENG-7704: record the signature we successfully delivered for.
    // ENG-7798: count consecutive same-signature nudges (each is a prior no-op)
    // so the unchanged-board cadence escalates; persisted to disk.
    // ENG-6876: a success is the ONLY thing that clears the failure state —
    // note failureCount/failedAt are simply not carried over here.
    if ((priorState?.failureCount ?? 0) > 0) {
      log(
        `[manager-worker] kanban notice rail RECOVERED for '${codeName}' after ${priorState?.failureCount} consecutive failures`,
      );
    }
    setKanbanNudgeState(codeName, {
      signature,
      nudgedAt: Date.now(),
      nudgeCount: nextKanbanNudgeCount(codeName, signature),
    });
    return;
  }

  // ENG-6876: enqueue failed (transport/route error). Record it so the retry
  // interval escalates (60s -> 3m -> 9m -> 27m -> capped) instead of re-firing
  // every debounce window forever. `nudgedAt`/`nudgeCount` are carried through
  // UNCHANGED on purpose: they anchor the ENG-7798 unchanged-board ladder, which
  // is about delivered no-op nudges and must not be perturbed by a transport
  // fault it knows nothing about.
  const failureCount = (priorState?.failureCount ?? 0) + 1;
  setKanbanNudgeState(codeName, {
    signature: priorState?.signature ?? signature,
    nudgedAt: priorState?.nudgedAt ?? 0,
    ...(priorState?.nudgeCount !== undefined ? { nudgeCount: priorState.nudgeCount } : {}),
    failureCount,
    failedAt: Date.now(),
  });

  // ENG-6876: ONE event when the breaker opens, not a log line per tick. The
  // equality check makes this fire on the crossing only — a still-open breaker
  // re-firing at the capped interval stays quiet until it recovers.
  if (failureCount === KANBAN_NOTICE_BREAKER_THRESHOLD) {
    log(
      `[manager-worker] kanban notice rail BREAKER OPEN for '${codeName}' — ${failureCount} consecutive enqueue failures; retries continue at the capped interval`,
    );
    getHostId()
      .then((hostId) => {
        if (!hostId) return;
        return api.post('/host/events', {
          host_id: hostId,
          agent_code_name: codeName,
          event_type: 'manager.kanban_notice_breaker_open',
          payload: {
            agent_code_name: codeName,
            agent_id: agentId,
            consecutive_failures: failureCount,
            retry_interval_ms: KANBAN_HYBRID_MAX_CADENCE_MS,
          },
          timestamp: new Date().toISOString(),
        });
      })
      .catch((err) => {
        log(
          `[manager-worker] failed to forward kanban breaker event for '${codeName}': ${(err as Error).message}`,
        );
      });
    return;
  }
  log(
    `[manager-worker] kanban direct-chat enqueue failed for '${codeName}' (${failureCount} consecutive) - backing off before the next attempt`,
  );
}

// Throttle harvest — no need to check cron results every 30s cycle
const lastHarvestAt = new Map<string, number>();
const HARVEST_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

// Cache kanban board per agent per poll cycle. BoardItem is defined in
// ./manager/kanban/parsers.js (ADR-0011) and imported above.
const kanbanBoardCache = new Map<string, BoardItem[]>();

/**
 * ENG-9584: the last board we hold per agent, with the server token that
 * addresses it. Distinct from `kanbanBoardCache` above, which is a
 * deliberately-STALE fallback for a FAILED fetch and drives `boardItemsStale`;
 * this one only ever holds a board the server confirmed current this tick.
 */
const boardTokenCache = new BoardTokenCache<BoardItem>();
// Separate cache for notification board diff — not pre-populated by step 7d
const notifyBoardCache = new Map<string, Set<string>>();
// ENG-7643 (ADR-0044 P5a): per-code_name set of waiting(human) card ids we've
// already DM'd the requester about. Mirrors notifyBoardCache's seed-on-first-
// cycle one-shot, so a card already parked at manager boot is seeded (not
// re-DM'd) and only a fresh in_progress->waiting(human) transition fires.
const notifiedWaitingHumanIds = new Map<string, Set<string>>();

// ADR-0011 PR-4: the low-level channel senders (telegramApiCall,
// sendSlackWebhookMessage, sendSlackChannelMessage, postSlackChannelMessage,
// maybePostSlackThreadHint, maybeSendTelegramFollowUpHint) moved into
// ./manager/channels/{slack,telegram}.ts and are imported above.


/** ENG-4581: dispatch any pending Claude Code OAuth pairing sessions
 *  across this host's agents. Runs once per refresh cycle.
 *
 *  Two states drive work:
 *    - 'initiating'     → run startClaudePair, report URL or error
 *    - 'code_submitted' → run submitClaudePairCode, report success/failure
 *
 *  Best-effort: a single bad session shouldn't block the others, and a
 *  transient API failure just means we retry on the next refresh.
 */
interface PairableAgent { agentId: string; codeName: string }

// Pair_ids the manager has spawned a tmux session for. Used to ask the
// server "of these, which are now terminal?" so we can kill orphan
// `agt-pair-<id>` tmux sessions left over from operator cancels or
// connection drops.
const spawnedPairIds = new Set<string>();

async function processClaudePairSessions(agents: PairableAgent[]): Promise<void> {
  // Keep polling whenever we have orphan pair-tmux sessions to clean
  // up — even if the host temporarily has zero assigned agents.
  // Otherwise stale agt-pair-* sessions could linger until the manager
  // restarts.
  if (agents.length === 0 && spawnedPairIds.size === 0) return;
  const agentIds = agents.map((a) => a.agentId);
  const codeNameByAgentId = new Map(agents.map((a) => [a.agentId, a.codeName]));

  const pendingResp = await api.post<{
    pending: Array<{
      pair_id: string;
      agent_id: string;
      status: 'initiating' | 'code_submitted';
      code: string | null;
      created_at: string;
      updated_at: string;
    }>;
    cancelled_pair_ids?: string[];
  }>('/host/claude-pair/pending', {
    agent_ids: agentIds,
    spawned_pair_ids: Array.from(spawnedPairIds),
  });

  const {
    startClaudePair,
    submitClaudePairCode,
    spawnPairSession,
    killPairSession,
    pairTmuxSession,
    finalizeClaudePairOnboarding,
    // ENG-10862: the failure vocabulary lives beside the loop that produces it,
    // so the code the console stores and the message the operator reads cannot
    // drift from the reason finalize actually returned.
    PAIR_FINALIZE_ERROR_CODES,
    describePairFinalizeFailure,
    // ENG-10862: the last-chance re-read before a failure is reported.
    confirmPairFailure,
    // ENG-10862: the pre-submit mtime baseline. Taken here because finalize
    // cannot take it for itself — by the time it runs, the write it is looking
    // for may already have happened.
    snapshotPairConfig,
  } = await import('./claude-pair-runtime.js');

  // Sweep orphans the server flagged as terminal but still in our
  // spawned set (operator cancel, status reported via a different code
  // path, etc.). Kill the tmux session and forget about the id.
  for (const pairId of pendingResp.cancelled_pair_ids ?? []) {
    log(`[claude-pair] sweeping orphan tmux session for pair ${pairId.slice(0, 8)}`);
    const killed = await killPairSession(pairTmuxSession(pairId));
    if (killed) {
      spawnedPairIds.delete(pairId);
    } else {
      // Kill failed for a non-missing-session reason — leave the id in
      // spawnedPairIds so the next poll retries.
      log(`[claude-pair] kill-session failed for pair ${pairId.slice(0, 8)} — will retry on next poll`);
    }
  }

  if (!pendingResp.pending || pendingResp.pending.length === 0) return;

  // Terminal states cleared from the manager's perspective — once we
  // report any of these, the pair-scoped tmux session is no longer needed
  // and should be torn down.
  const TERMINAL: ReadonlySet<string> = new Set([
    'success',
    'failure',
    'session_missing',
    'timeout',
  ]);

  async function reportAndCleanup(
    pairId: string,
    body: Record<string, unknown>,
  ): Promise<void> {
    await api.post('/host/claude-pair/result', { pair_id: pairId, ...body });
    if (typeof body.status === 'string' && TERMINAL.has(body.status)) {
      // On success, give claude a beat to persist credentials to
      // ~/.claude/.credentials.json before we kill the tmux session.
      // Killing during the write window can leave the host with an
      // empty ~/.claude (claude shows the theme picker on next launch
      // as if no auth had ever happened).
      // ENG-10862: the beat covers an onboarding failure too. Its purpose is to
      // let claude finish persisting `~/.claude/.credentials.json` before the
      // pane is killed, and that is not a property of the pairing having
      // SUCCEEDED — the `send-failed` and `pane-gone` verdicts can be returned
      // about 1.5s after the success marker, so killing immediately would land
      // inside the same write window this pause exists to clear.
      const isOnboardingFailure =
        typeof body.error_code === 'string' && body.error_code.startsWith('onboarding_');
      if (body.status === 'success' || isOnboardingFailure) {
        await new Promise<void>((r) => setTimeout(r, 3_000));
      }
      const killed = await killPairSession(pairTmuxSession(pairId));
      if (killed) {
        spawnedPairIds.delete(pairId);
      }
      // If kill failed, the next poll's cancelled_pair_ids sweep will
      // retry — the row is now terminal so it'll be returned again.
    }
  }

  for (const session of pendingResp.pending) {
    // codeName is informational only — handy for log lines. The pair
    // flow drives a throwaway tmux session, NOT the agent's persistent
    // session, so we no longer require an entry in codeNameByAgentId.
    const codeName = codeNameByAgentId.get(session.agent_id) ?? '<unassigned>';
    const pairSession = pairTmuxSession(session.pair_id);

    try {
      if (session.status === 'initiating') {
        log(`[claude-pair] spawning pair session ${pairSession} for '${codeName}'`);
        const spawn = await spawnPairSession(pairSession);
        if (!spawn.ok) {
          await reportAndCleanup(session.pair_id, {
            status: 'failure',
            error_code: spawn.error.kind,
            // ENG-10617: forward any message (incl. `claude-exited`, which
            // carries claude's exit status + last output), not just unknown.
            error_message: 'message' in spawn.error ? spawn.error.message : undefined,
          });
          continue;
        }
        spawnedPairIds.add(session.pair_id);

        log(`[claude-pair] dispatching /login (pair ${session.pair_id.slice(0, 8)})`);
        const result = await startClaudePair({ session: pairSession });
        if (result.kind === 'url') {
          await api.post('/host/claude-pair/result', {
            pair_id: session.pair_id,
            status: 'awaiting_code',
            url: result.url,
          });
        } else if (result.kind === 'timeout') {
          await reportAndCleanup(session.pair_id, {
            status: 'timeout',
            error_code: 'url_prompt_not_seen',
            error_message: 'Claude Code did not print the URL prompt within the deadline',
          });
        } else {
          const errKind = result.error.kind;
          // Forward the message for any kind that carries one. `oauth-retry-stuck`
          // ships actionable remediation text ("clear ~/.claude/.credentials.json...")
          // that operators need; the previous unknown-only filter dropped that
          // guidance and reduced the failure to a bare error_code.
          const errMessage =
            'message' in result.error ? result.error.message : undefined;
          await reportAndCleanup(session.pair_id, {
            status: errKind === 'no-session' ? 'session_missing' : 'failure',
            error_code: errKind,
            error_message: errMessage,
          });
        }
      } else if (session.status === 'code_submitted' && session.code) {
        log(`[claude-pair] submitting code (pair ${session.pair_id.slice(0, 8)})`);
        // ENG-10862: snapshot the config's mtime BEFORE the code goes in.
        // Claude Code can write it between printing the success marker and
        // finalize starting — concurrent events, not ordered ones — and a
        // snapshot taken inside finalize would then measure against the
        // post-write value and report a working pairing as failed.
        const configBaseline = snapshotPairConfig();
        const result = await submitClaudePairCode({
          session: pairSession,
          code: session.code,
        });
        if (result.kind === 'success') {
          // ENG-4633: drive Claude Code through its post-OAuth dialogs
          // so it persists ~/.claude.json before we kill the pair tmux
          // session. Without this step claude only wrote
          // ~/.claude/.credentials.json — the next agent launch hit
          // the login picker again.
          //
          // ENG-10862: this result now GATES the report, where it used to be
          // logged and discarded. The old comment here argued an un-finalized
          // pair was still a success because the OAuth tokens work — true, and
          // beside the point: on alyve-host-1 that "worst-case one-time login
          // picker" was four agents latched for up to 9h50m (the reaper will
          // not restart a session blocked on a prompt, ENG-10825), against a
          // pairing the console showed as green. A pairing that leaves the
          // host unable to start an agent is not a successful pairing.
          //
          // Reported as `failure` rather than a new status: `failure` is
          // already in the table's CHECK constraint and the route's allowlist,
          // and the console's failure branch already renders `error_message`
          // with a Start over button — which is the retry. The failure KIND
          // travels in `error_code`, and every message states that the tokens
          // are valid so nobody chases a credential fault.
          const finalize = await finalizeClaudePairOnboarding(pairSession, log, {
            baseline: configBaseline,
          });
          const pairShort = session.pair_id.slice(0, 8);
          // ENG-10862: `send-failed` and `pane-gone` abort on a tmux error
          // rather than falling through to the grace pass, so they have looked
          // at the config exactly once, milliseconds earlier. A write already
          // in flight lands after that verdict — and since the report is POSTed
          // BEFORE `reportAndCleanup`'s beat, a working pairing would raise an
          // alert and tell the operator to start over. Re-ask before reporting.
          const confirmed =
            finalize.finalized
              ? 'landed-after-all'
              : await confirmPairFailure({
                  configPath: finalize.configPath,
                  sinceMtimeMs: configBaseline.mtimeMs,
                  log,
                });
          if (!finalize.finalized && confirmed === 'confirmed') {
            // `reason` is set whenever finalized is false; the fallback keeps
            // this honest rather than asserting a timeout we did not observe.
            const reason = finalize.reason ?? 'not-flushed';
            const errorCode = PAIR_FINALIZE_ERROR_CODES[reason];
            log(
              `[claude-pair] onboarding did NOT persist (pair ${pairShort}) reason=${reason} ` +
                `code=${errorCode} iterations=${finalize.iterations} graceWaits=${finalize.graceWaits} ` +
                `consumerTerms=${finalize.counts.consumerTerms} dialogEnter=${finalize.counts.dialogEnter} ` +
                `noMatch=${finalize.counts.noMatch} — reporting the pairing as FAILED`,
            );
            await reportAndCleanup(session.pair_id, {
              status: 'failure',
              error_code: errorCode,
              // The path finalize actually watched, returned by it rather than
              // recomputed here — two copies of that rule is how the operator
              // ends up reading about a file the flow never looked at.
              error_message: describePairFinalizeFailure(reason, finalize.configPath),
              // AC5: the loop's own accounting, so "do real pairings need more
              // than 10 dismissals?" stops being unanswerable. Counts only —
              // never pane text, which carries `Logged in as <email>`. These
              // are NOT stored on the pair row (no column, by decision); the
              // route forwards them into the alert payload, which is jsonb.
              finalize_iterations: finalize.iterations,
              finalize_grace_waits: finalize.graceWaits,
              finalize_dialog_counts: finalize.counts,
            });
          } else {
            // `finalized` OR a config that landed inside the confirm window.
            // The distinction is logged rather than flattened: a late landing
            // means the dialog loop's verdict was wrong, which is exactly the
            // signal that would tell us the budget or the matchers need work.
            const lateness = finalize.finalized
              ? ''
              : ` (late: finalize said ${finalize.reason}, config landed during the confirm window)`;
            log(
              `[claude-pair] onboarding persisted (pair ${pairShort}) iterations=${finalize.iterations} ` +
                `graceWaits=${finalize.graceWaits} consumerTerms=${finalize.counts.consumerTerms} ` +
                `dialogEnter=${finalize.counts.dialogEnter} noMatch=${finalize.counts.noMatch}${lateness}`,
            );
            // Nothing extra on the wire here: the success path raises no
            // alert, so the counts would be a payload no reader consumes. They
            // are in the log line above, which is where AC5 asked for them.
            await reportAndCleanup(session.pair_id, { status: 'success' });
          }
        } else if (result.kind === 'failure') {
          await reportAndCleanup(session.pair_id, {
            status: 'failure',
            error_code: 'invalid_code',
            error_message: result.rawMatch,
          });
        } else if (result.kind === 'timeout') {
          await reportAndCleanup(session.pair_id, {
            status: 'timeout',
            error_code: 'outcome_not_seen',
            error_message: 'Claude Code did not show a success/failure marker after submitting the code',
          });
        } else {
          const errKind = result.error.kind;
          // Forward the message for any kind that carries one. `oauth-retry-stuck`
          // ships actionable remediation text ("clear ~/.claude/.credentials.json...")
          // that operators need; the previous unknown-only filter dropped that
          // guidance and reduced the failure to a bare error_code.
          const errMessage =
            'message' in result.error ? result.error.message : undefined;
          await reportAndCleanup(session.pair_id, {
            status: errKind === 'no-session' ? 'session_missing' : 'failure',
            error_code: errKind,
            error_message: errMessage,
          });
        }
      }
    } catch (err) {
      // Don't poison the loop. Log and let the next refresh retry —
      // pending rows stay in 'initiating' / 'code_submitted' until we
      // succeed or the operator gives up.
      log(`[claude-pair] dispatch failed for pair ${session.pair_id.slice(0, 8)}: ${(err as Error).message}`);
    }
  }
}


/**
 * Generate provision artifacts without writing to disk.
 * Returns the list of artifacts (relativePath + content) for comparison.
 */
function generateArtifacts(
  agent: { agent_id: string; code_name: string; display_name: string; status: string; environment: string },
  refreshData: {
    agent: Record<string, unknown>;
    charter: { raw_content: string; version: string } | null;
    tools: { raw_content: string; version: string } | null;
    channel_configs: Record<string, { config: unknown; status: string }> | null;
    team_channel_policy: {
      team_id: string;
      allowed_channels: string[];
      denied_channels: string[];
      require_elevated_for_pii: boolean;
    } | null;
    team: { name: string; description: string | null; settings?: Record<string, unknown> } | null;
    scheduled_tasks?: unknown;
    knowledge?: Array<{ title: string; slug: string; content: string | null; scope: 'org' | 'team' }>;
    team_members?: Array<{ display_name: string; email?: string; role: string; title?: string; contact_channel?: string }>;
    people?: Array<{ display_name: string; email?: string; title?: string; department?: string; relationship?: string; contact_channel?: string }>;
    guardrails?: ProvisionInput['guardrails'];
    // ENG-9862: standing rules, rendered into CLAUDE.md by the adapter.
    agent_rules?: ProvisionInput['agentRules'];
    // ADR-0012 / ENG-6352: down-synced dynamic workflows (flag-gated, resolved
    // server-side). Threaded into provisionInput.workflows for the adapter.
    workflows?: ProvisionInput['workflows'];
    // ENG-5380: active kanban tasks payload from /host/refresh. The API
    // caps at 10 and filters to todo/in_progress; here we just thread it
    // through to the adapter behind the feature flag.
    active_tasks?: ProvisionInput['activeTasks'];
  },
  adapter: FrameworkAdapter,
  // ENG-8174: resolved ONCE by the caller (processAgent) and passed in, rather
  // than re-read here. The `writeIntegrations` calls later in the same tick sit
  // behind several awaits, and `hostFlagStore()` can be refreshed by the
  // heartbeat in between — re-reading per call site would let the generator and
  // the side-effect writer resolve different values for one cycle and fight over
  // the sentinel range, which is precisely what this gate exists to prevent.
  renderIntegrationsSection: boolean,
): { relativePath: string; content: string }[] {
  if (!refreshData.charter || !refreshData.tools) {
    throw new Error('No charter/tools available');
  }

  const charterContent = refreshData.charter.raw_content;
  const toolsContent = refreshData.tools.raw_content;

  const charterParsed = extractFrontmatter(charterContent);
  if (!charterParsed.frontmatter) {
    throw new Error(`Failed to parse CHARTER.md frontmatter: ${charterParsed.error ?? 'unknown'}`);
  }

  const toolsParsed = extractFrontmatter(toolsContent);
  if (!toolsParsed.frontmatter) {
    throw new Error(`Failed to parse TOOLS.md frontmatter: ${toolsParsed.error ?? 'unknown'}`);
  }

  const charterFrontmatter = charterParsed.frontmatter as unknown as CharterFrontmatter;
  const toolsFrontmatter = toolsParsed.frontmatter as unknown as ToolsFrontmatter;

  const configuredChannels = Object.keys(refreshData.channel_configs ?? {}) as ChannelId[];

  const agentChannelPolicy: ChannelPolicy = {
    policy: 'allowlist',
    allowed: configuredChannels,
    denied: [],
    require_approval_to_change: true,
  };

  let orgChannelPolicy: OrgChannelPolicy | undefined;
  const policyData = refreshData.team_channel_policy;

  if (policyData) {
    orgChannelPolicy = {
      organization_id: policyData.team_id,
      allowed_channels: (policyData.allowed_channels ?? []) as ChannelId[],
      denied_channels: (policyData.denied_channels ?? []) as ChannelId[],
      require_elevated_for_pii: policyData.require_elevated_for_pii ?? false,
    };
  }

  const resolvedChannels = resolveChannels(agentChannelPolicy, orgChannelPolicy);
  const effectiveChannels = agent.status === 'paused' ? [] : resolvedChannels;

  // Resolve timezone deterministically — use unanimous task timezone, then team, then UTC
  const tasks = (refreshData as Record<string, unknown>).scheduled_tasks as Array<{ timezone?: string | null }> | null | undefined;
  const taskTimezones = [...new Set(
    (tasks ?? [])
      .map((t) => (typeof t.timezone === 'string' ? t.timezone.trim() : ''))
      .filter((tz): tz is string => tz.length > 0),
  )];
  const teamTimezoneRaw = (refreshData.team as { timezone?: unknown } | null | undefined)?.timezone;
  const teamTimezone = typeof teamTimezoneRaw === 'string' && teamTimezoneRaw.trim().length > 0 ? teamTimezoneRaw.trim() : undefined;
  const agentTimezone = (taskTimezones.length === 1 ? taskTimezones[0] : undefined) ?? teamTimezone ?? 'UTC';

  // Resolve personality seed: agent-level overrides org-level
  const agentPersonalitySeed = (refreshData.agent as Record<string, unknown>).personality_seed as string | undefined;
  const orgDefaults = (refreshData as Record<string, unknown>).model_defaults as { org?: { settings?: Record<string, unknown> } } | undefined;
  const personalitySeed = agentPersonalitySeed || orgDefaults?.org?.settings?.personality_seed as string | undefined;

  // Resolve reports_to reference (person or agent name)
  let reportsTo: ProvisionInput['reportsTo'];
  const agentData = refreshData.agent as Record<string, unknown>;
  const reportsToId = agentData.reports_to as string | null | undefined;
  const reportsToType = (agentData.reports_to_type as string | null | undefined) ?? 'agent';

  if (reportsToId) {
    const reportsToName = agentData.reports_to_name as string | undefined;
    const reportsToTitle = agentData.reports_to_title as string | undefined;
    const reportsToDesc = agentData.reports_to_description as string | undefined;
    if (reportsToName) {
      reportsTo = {
        name: reportsToName,
        type: reportsToType as 'agent' | 'person',
        title: reportsToTitle,
        description: reportsToDesc,
      };
    }
  }

  // ENG-5769: session-start active-tasks injection is now always on. The
  // ENG-5380 flag (`AGT_ACTIVE_TASKS_INJECT`) was default-off, which meant
  // Slack-thread continuity silently broke on every kanban resume — the
  // agent picked the card back up via the kanban-work nudge but had no
  // thread coordinates in its context (the nudge's `formatBoardForPrompt`
  // didn't carry them either, fixed in this same commit). The Active Tasks
  // section is hard-capped at ~200 tokens (`ACTIVE_TASKS_MAX_CHARS`) so
  // the always-on default is bounded and safe.
  //
  // Logged unconditionally so we can monitor the steady-state cost in
  // prod and catch any regression in the cap.
  const activeTasksFromRefresh = refreshData.active_tasks ?? [];
  const activeTasksTokens = estimateActiveTasksTokens(activeTasksFromRefresh);
  log(
    `[provision/active-tasks] agent=${agent.code_name} count=${activeTasksFromRefresh.length} estimated_tokens=${activeTasksTokens}`,
  );

  // ENG-7876: opencode reads its model straight from the generated
  // opencode.json (there is no separate `--model` launch flag like claude's),
  // so the ProvisionInput's primary_model must carry the RESOLVED model — agent
  // override -> org default -> platform default (resolveModelChain) — with a
  // host env override (AGT_OPENCODE_MODEL) winning for ops/testing. Passing the
  // raw field made an opencode agent with no explicit model fall to the
  // adapter's hardcoded claude-opus-4-7 default instead of the org/platform
  // default (the "asked haiku, got opus" gap). Scoped to opencode so the
  // claude-code path (which resolves its launch model separately) is untouched.
  const provisionAgent = (() => {
    const rawAgent = refreshData.agent as Record<string, unknown>;
    if (rawAgent?.['framework'] !== 'opencode') return rawAgent;
    // feature-gate-allow: AGT_OPENCODE_MODEL is a model-id tunable, not a gate
    const hostOverride = process.env['AGT_OPENCODE_MODEL']?.trim();
    const resolved =
      hostOverride ||
      resolveModelChain(refreshData).primary ||
      (rawAgent?.['primary_model'] as string | undefined);
    return resolved ? { ...rawAgent, primary_model: resolved } : rawAgent;
  })();

  const provisionInput: ProvisionInput = {
    agent: provisionAgent as any,
    charterFrontmatter,
    charterContent,
    toolsFrontmatter,
    toolsContent,
    resolvedChannels: effectiveChannels,
    deploymentTarget: 'local_docker' as DeploymentTarget,
    team: refreshData.team ?? undefined,
    activeTasks: activeTasksFromRefresh,
    // ENG-5009: org name for the identity-preamble. Only present when
    // /host/refresh ships the field — falls through cleanly for older
    // API payloads that don't carry it.
    organization: (() => {
      // CodeRabbit on PR #941: trim before the length check so a
      // whitespace-only value (`'   '`) doesn't render a broken
      // "in the X team at **   **" scope in CLAUDE.md.
      const org = (refreshData as unknown as { organization?: { name?: unknown } | null })
        .organization;
      if (typeof org?.name !== 'string') return undefined;
      const name = org.name.trim();
      return name.length > 0 ? { name } : undefined;
    })(),
    timezone: agentTimezone,
    reportsTo,
    personalitySeed,
    knowledge: (refreshData.knowledge ?? []).filter((k): k is { title: string; slug: string; content: string; scope: 'org' | 'team' } => !!k.content),
    knowledgeDelivery: ((refreshData.agent as Record<string, unknown>).knowledge_delivery as 'search' | 'files' | 'both' | undefined) ?? 'both',
    teamMembers: refreshData.team_members ?? undefined,
    people: refreshData.people ?? undefined,
    guardrails: refreshData.guardrails,
    // ENG-9862: standing rules (ENG-9789), threaded exactly like guardrails so
    // the claude-code adapter can render them into CLAUDE.md. Absent on an
    // older API renders nothing, which is the documented fail-direction.
    agentRules: refreshData.agent_rules,
    // ADR-0012 / ENG-6352: down-synced dynamic workflows (flag-gated, resolved
    // server-side). The claude-code adapter renders each as
    // `.claude/workflows/<name>.js`; undefined/empty no-ops.
    workflows: refreshData.workflows,
    // ENG-8174: the `## Integrations` gate, resolved once per tick by
    // processAgent so the generator and the side-effect writer cannot disagree
    // about the sentinel range.
    renderIntegrationsSection,
  };

  const provisionOutput = provision(provisionInput, adapter.id);
  return provisionOutput.artifacts;
}

// ---------------------------------------------------------------------------
// Memory sync — bidirectional sync between local files and agent_memories DB
// ---------------------------------------------------------------------------

/** Per-file content hashes to detect individual memory changes */
const memoryFileHashes = new Map<string, Map<string, string>>(); // agentId → (fileName → hash)
/** Hash of DB memories response to skip redundant downloads */
const lastDownloadHash = new Map<string, string>(); // agentId → hash
/** Hash of local file listing to skip redundant download checks */
const lastLocalFileHash = new Map<string, string>(); // agentId → hash

/**
 * ENG-9584: the last `/host/memories` body and the SERVER's token for it.
 *
 * The token is opaque here by design — the manager never computes it, it echoes
 * back whatever `payload_token` the API last handed it. That is what keeps a
 * second hashing scheme from existing at all (see the API's
 * `lib/conditional-payload.ts` header). When the server answers
 * `{ unchanged: true }` it has just recomputed the payload and found it
 * byte-identical to the one this body came from, so the cached body is
 * provably current — not stale-but-probably-fine.
 *
 * INVARIANT: nothing downstream may mutate a cached body, or the stored token
 * stops describing it and the manager acts on a corpus the server never sent.
 * Only `tombstones` ever escapes this function, into `applyMemoryTombstones`,
 * which reads it and never writes — pinned by a test, because "it doesn't
 * mutate today" is exactly the kind of fact that stops being true quietly.
 *
 * Bounded by ENG-9585's 256 KB payload budget plus the 200-entry tombstone cap,
 * so this is ~270 KB of JSON per assigned agent at the absolute worst.
 */
const lastMemoriesBody = new Map<string, { hash: string; body: MemoriesResponseBody }>();

interface MemoriesResponseBody {
  // Optional to match the wire rather than the happy path: every downstream
  // read is already `?? []` / `?.length`, and asserting presence here would
  // only move a real absence past the type checker.
  memories?: Array<{ name: string; type: string; content: string }>;
  // ENG-9678: a DISTINCT key, never a second shape of `memories`. An API that
  // predates manifest mode ignores the request flag and answers with
  // `memories`, and that has to stay detectable by a property check rather than
  // inferred from whether `content` happens to be present.
  memories_manifest?: MemoryContentManifestEntry[];
  /**
   * ENG-9678: set only when the server's runaway ceiling clipped the live read,
   * so a partial manifest is never indistinguishable from a complete one.
   * Absent — not `false` — when the list is whole.
   */
  memories_manifest_truncated?: boolean;
  tombstones?: MemoryTombstone[];
  tombstones_truncated?: boolean;
}

/** Wire shape: either the full body, or the unchanged marker. */
type MemoriesWireBody = Partial<MemoriesResponseBody> & {
  unchanged?: boolean;
  payload_token?: string;
};

// ---------------------------------------------------------------------------
// ENG-9257: the write-time manifest — what THIS manager wrote, and what it
// looked like when it wrote it.
//
// This is the whole delete gate. A tombstone says a memory is retired; the
// manifest says whether the file on disk is still the one we put there. Only
// then is it ours to move.
//
// WHY A RECORD AND NOT A RECOMPUTE. The obvious gate is "re-render the
// canonical body from the row and compare bytes", and it is wrong in a way that
// is invisible until it destroys something. Upload runs BEFORE download on
// every tick, so an agent's edit to a memory file is copied into the row and
// the next tick's recompute MATCHES — deleting the very edit the gate exists to
// protect. (ENG-9229 now refuses writes onto retired rows, closing that
// particular door, but the recompute is also defeated by parseMemoryFile's
// .trim(), by any DB-side content change before retirement, and by any future
// change to the frontmatter rendering.) A recorded sha is immune to all of it:
// it answers "did WE write this exact file", which is the actual question.
//
// It also means agent-authored files are handled correctly rather than by
// accident. Those have no frontmatter at all, so they could never match a
// recomputed body — a permanent silent blind spot over the most common file
// type. They simply have no manifest entry, and are retained explicitly.
//
// KEYED BY MEMORY NAME, NOT SLUG. The tombstone carries the name, so the delete
// path never re-derives a slug — which retires the whole lossy-slug problem
// (`.slice(0, 60)` collisions, and the `memory-${i}` fallback whose filename
// depends on a position in an array whose ordering shifts). If two names do
// collide onto one file, only the entry whose sha is actually on disk matches;
// the other is retained.
//
// Persisted because it must survive a restart: everything else here is
// in-memory, and a manifest that empties on restart can never delete anything.
// It lives OUTSIDE project/ so the agent has no reason to read it.
// ---------------------------------------------------------------------------

interface MemoryManifestEntry {
  /** Filename within the memory dir, as actually written. */
  file: string;
  /** sha256 of the exact bytes written. */
  sha: string;
}
type MemoryManifest = Record<string, MemoryManifestEntry>;

/** agentId → manifest, hydrated from disk on first use per process. */
export const memoryManifests = new Map<string, MemoryManifest>();

/**
 * Host-only root for the retirement machinery, under `~/.augmented/_memory-
 * retirement/<codeName>/`.
 *
 * THE UNDERSCORE PREFIX IS LOad-BEARING. `buildDockerRunCommand` bind-mounts the
 * agent's whole directory — `-v ~/.augmented/<codeName>:~/.augmented/<codeName>`
 * (persistent-session.ts) — so ANY path under `<codeName>/` is readable and
 * writable by the agent itself. Putting the retired copies at
 * `<codeName>/retired-memory` therefore achieved nothing: moving them out of
 * `project/memory/` was supposed to put them beyond a `grep` of the memory
 * directory, and a sibling inside the same mount is still one `ls ..` away.
 *
 * The mount set is exactly five paths (`<codeName>`, `<agentId>`, `_mcp`,
 * `~/.claude`, `~/.claude.json`), so a sibling under `~/.augmented/` with an
 * underscore prefix is host-only. Same reasoning and same convention as
 * `~/.augmented/_egress/` (ENG-6579), whose comment says so explicitly.
 *
 * The MANIFEST moves here too, not just the retired copies. It is the control
 * data for a deletion decision — if the agent could rewrite it, it could point
 * an entry at a file it wants removed. (Corrupting it is harmless: that degrades
 * to "we wrote nothing", which retains. Forging is the direction that matters.)
 */
function memoryRetirementDir(configDir: string, codeName: string): string {
  return join(configDir, '_memory-retirement', codeName);
}

export function memoryManifestPath(configDir: string, codeName: string): string {
  return join(memoryRetirementDir(configDir, codeName), 'manifest.json');
}

function loadMemoryManifest(agentId: string, configDir: string, codeName: string): MemoryManifest {
  const cached = memoryManifests.get(agentId);
  if (cached) return cached;

  let manifest: MemoryManifest = {};
  try {
    const raw = readFileSync(memoryManifestPath(configDir, codeName), 'utf-8');
    const parsed: unknown = JSON.parse(raw);
    // Shape-check rather than trust: a corrupt manifest must degrade to "we
    // wrote nothing", which retains every file. The opposite failure would let
    // a garbage entry authorise a deletion.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [name, entry] of Object.entries(parsed as Record<string, unknown>)) {
        if (
          entry && typeof entry === 'object' &&
          typeof (entry as MemoryManifestEntry).file === 'string' &&
          typeof (entry as MemoryManifestEntry).sha === 'string'
        ) {
          manifest[name] = { file: (entry as MemoryManifestEntry).file, sha: (entry as MemoryManifestEntry).sha };
        }
      }
    }
  } catch {
    manifest = {};
  }

  memoryManifests.set(agentId, manifest);
  return manifest;
}

function saveMemoryManifest(agentId: string, configDir: string, codeName: string): void {
  const manifest = memoryManifests.get(agentId);
  if (!manifest) return;
  try {
    const path = memoryManifestPath(configDir, codeName);
    // The host-only retirement dir, NOT the agent's own directory — creating the
    // latter here would have been a no-op anyway, but pointing at it invites the
    // manifest to drift back inside the bind mount.
    mkdirSync(memoryRetirementDir(configDir, codeName), { recursive: true });
    // Write-then-rename so a crash mid-write cannot leave a truncated manifest,
    // which would silently downgrade to "we wrote nothing" on next boot.
    const tmp = `${path}.tmp`;
    writeFileSync(tmp, JSON.stringify(manifest, null, 2));
    renameSync(tmp, path);
  } catch {
    /* best-effort: a manifest we cannot persist costs a retain, never a delete */
  }
}

/**
 * Where a removed memory file goes: outside the agent's bind mount entirely, so
 * the agent cannot read it back. See memoryRetirementDir for why "outside
 * project/" was not far enough.
 */
export function retiredMemoryDir(configDir: string, codeName: string): string {
  return join(memoryRetirementDir(configDir, codeName), 'retired');
}

/** How long a moved file is kept before the reaper drops it. */
const RETIRED_MEMORY_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * ENG-9257: fingerprint of the last unapplied set we reported per agent, so a
 * steady state does not POST every tick.
 *
 * In-memory on purpose. A restart clears it, so the first tick after any restart
 * re-reports — which is the self-healing direction: the control plane's picture
 * is refreshed rather than left to drift behind a cache we cannot inspect.
 */
const lastRetirementReport = new Map<string, string>(); // agentId → fingerprint

/**
 * Tell the control plane which retired memories are still on this host.
 *
 * Best-effort and non-throwing, like every other host→control-plane report: a
 * reporting outage must never stop the removal work itself. But the fingerprint
 * is only recorded on SUCCESS, so a failed report retries next tick instead of
 * being silently swallowed for the life of the process.
 */
async function reportMemoryRetirement(
  agent: { agent_id: string; code_name: string },
  outcomes: MemoryTombstoneOutcomes,
  log: (msg: string) => void,
): Promise<void> {
  // An empty `unapplied` is meaningful — it CLEARS the agent's rows, which is
  // how a memory that later becomes removable stops being reported. So this
  // sends on transition to empty, not only when there is something to say.
  const fingerprint = outcomes.unapplied
    .map((u) => `${u.reason}:${u.name}`)
    .sort()
    .join('\u0000');
  if (lastRetirementReport.get(agent.agent_id) === fingerprint) return;

  try {
    await api.post('/host/memory-retirement-report', {
      agent_id: agent.agent_id,
      unapplied: outcomes.unapplied,
    });
    lastRetirementReport.set(agent.agent_id, fingerprint);
    if (outcomes.unapplied.length > 0) {
      log(
        `[memory-retire] ${agent.code_name}: reported ${outcomes.unapplied.length} retired ` +
          `memory file(s) still present on this host`,
      );
    }
  } catch (err) {
    log(`[memory-retire] ${agent.code_name}: retirement report failed: ${(err as Error).message}`);
  }
}

/**
 * ENG-4643: agents that have just been assigned to this host (or have
 * otherwise been flagged as needing a clean re-pull from the API).
 * Set populated by the onAssign realtime callback; consumed at the
 * top of the next syncMemories run for that agent. The pending tick
 * skips the local-list-hash short-circuit, downloads first (so DB is
 * authoritative on the migration boundary, never overwritten by stale
 * local files), and clears the per-agent in-memory caches so a long-
 * running manager that previously hosted this agent doesn't carry
 * stale hashes that would suppress fresh writes.
 */
const pendingFreshMemorySync = new Set<string>(); // agent_id

export function markAgentForFreshMemorySync(agentId: string): void {
  pendingFreshMemorySync.add(agentId);
  memoryFileHashes.delete(agentId);
  lastDownloadHash.delete(agentId);
  lastLocalFileHash.delete(agentId);
  // ENG-9584: same invariant as clearAgentCaches. A fresh sync would in fact be
  // correct against a cached body — an `unchanged` answer proves it is current,
  // and `force` re-renders from it regardless — but the migration boundary is
  // the last place to be clever, and one full download once is the price.
  lastMemoriesBody.delete(agentId);
}

function parseMemoryFile(raw: string, fallbackName: string): { name: string; content: string; type: string } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const fmMatch = trimmed.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);

  if (fmMatch) {
    // Has YAML frontmatter — parse structured fields
    const frontmatter = fmMatch[1] ?? '';
    const body = (fmMatch[2] ?? '').trim();
    if (!body) return null;

    const nameMatch = frontmatter.match(/^name:\s*(.+)$/m);
    const typeMatch = frontmatter.match(/^type:\s*(.+)$/m);
    const name = nameMatch?.[1]?.trim().replace(/^["']|["']$/g, '') ?? fallbackName;
    const rawType = typeMatch?.[1]?.trim().replace(/^["']|["']$/g, '') ?? 'user';

    const typeMap: Record<string, string> = { user: 'user', feedback: 'feedback', project: 'project', reference: 'reference', daily: 'daily' };
    const type = typeMap[rawType] ?? 'user';

    return { name, content: body, type };
  }

  // No frontmatter — treat the entire file as content.
  // Derive type from filename pattern: date-like names (YYYY-MM-DD) → daily, else project.
  const isDaily = /^\d{4}-\d{2}-\d{2}$/.test(fallbackName);
  return {
    name: fallbackName,
    content: trimmed,
    type: isDaily ? 'daily' : 'project',
  };
}

/** One tombstone as the control plane sends it. */
export interface MemoryTombstone {
  name: string;
  type: string;
}

/**
 * Per-tick tally. Every branch is counted, including the two that do nothing,
 * because a single "removed" scalar reads 0 for at least five distinct states —
 * nothing retired, flag off, all retained on hash, all already gone, and a CLI
 * with no tombstone code at all — and an operator cannot act on that.
 */
export interface MemoryTombstoneOutcomes {
  removed: number;
  kept_unknown: number;
  kept_modified: number;
  already_gone: number;
  failed: number;
  skipped_disabled: number;
  /**
   * The retired memories whose FILE IS STILL THERE, with why. Reported to the
   * control plane so the refusal is visible from outside a customer-owned host
   * — otherwise it lives only in manager.log and "worked perfectly" and "never
   * removed anything" are the same observation from every surface an operator
   * can reach.
   */
  unapplied: Array<{ name: string; reason: 'kept_unknown' | 'kept_modified' | 'failed' }>;
}

/**
 * ENG-9257: `downloadMemories` used to return a bare boolean, and `true` meant
 * three different things — "I short-circuited", "the DB says this agent has no
 * live memories", and "I have a fresh complete view". Any precondition written
 * against that bit ("apply tombstones only after a good download") was really
 * applying them after "I skipped" as well.
 *
 * `tombstones: null` is load-bearing and distinct from `[]`: null means the
 * server told us nothing about tombstones this tick (old API, or its tombstone
 * read failed), which is never a licence to remove anything.
 */
interface DownloadMemoriesResult {
  /**
   * `fetched`  — asked, and wrote files.
   * `skipped`  — asked, and the content short-circuit said nothing to write.
   *              STILL CARRIES TOMBSTONES: "nothing to write" is not "nothing
   *              to remove" (ENG-9257).
   * `deferred` — did NOT ask; the ENG-9585 fetch spacing was still closed.
   *              Tombstones are `null` because there is no information, not
   *              because nothing is retired.
   * `failed`   — asked, and the request threw.
   */
  status: 'fetched' | 'skipped' | 'deferred' | 'failed';
  tombstones: MemoryTombstone[] | null;
  truncated: boolean;
}

/**
 * ENG-9257: act on the tombstone list.
 *
 * Every outcome is counted and every non-removal is logged with its reason.
 * That is not decoration — before this, "removed it", "retained because the
 * bytes differ", "the flag is off" and "this CLI has no tombstone code at all"
 * were a single observable: silence. An operator reading a quiet fleet cannot
 * tell a working feature from one that has never once acted, and the retirement
 * UI asserts success either way.
 */
export function applyMemoryTombstones(opts: {
  agent: { agent_id: string; code_name: string };
  configDir: string;
  memoryDir: string;
  tombstones: MemoryTombstone[];
  truncated: boolean;
  /**
   * Resolved by the CALLER, so the flag read stays at the call site where it is
   * greppable — and so this stays a pure-ish function the tests can drive
   * without standing up a flag store.
   */
  enabled: boolean;
  log: (msg: string) => void;
}): MemoryTombstoneOutcomes {
  const { agent, configDir, memoryDir, tombstones, truncated, enabled, log } = opts;
  const outcomes: MemoryTombstoneOutcomes = {
    removed: 0, kept_unknown: 0, kept_modified: 0, already_gone: 0, failed: 0, skipped_disabled: 0,
    unapplied: [],
  };

  if (tombstones.length === 0) return outcomes;

  if (!enabled) {
    // Counted rather than silently returned: "the flag is off" and "there was
    // nothing to do" must not be the same observation.
    outcomes.skipped_disabled = tombstones.length;
    return outcomes;
  }

  const manifest = loadMemoryManifest(agent.agent_id, configDir, agent.code_name);
  let manifestDirty = false;

  for (const tomb of tombstones) {
    // Deliberately NO `type === 'daily'` check here. The server excludes dailies
    // from the list, and that decision is owned by exactly one side. Re-checking
    // here would decide it from the FILE's frontmatter instead — a different
    // source of truth — so a one-word frontmatter edit could push a memory into
    // a state neither side will ever speak about.
    const entry = manifest[tomb.name];
    if (!entry) {
      // No record of writing it: an agent-authored file, a file from before the
      // manifest existed, or one we never had. Not ours to remove.
      outcomes.kept_unknown++;
      outcomes.unapplied.push({ name: tomb.name, reason: 'kept_unknown' });
      log(`[memory-retire] ${agent.code_name}: KEPT '${tomb.name}' — no write record, not ours to remove`);
      continue;
    }

    const filePath = join(memoryDir, entry.file);
    if (!existsSync(filePath)) {
      outcomes.already_gone++;
      delete manifest[tomb.name];
      manifestDirty = true;
      continue;
    }

    try {
      const actual = createHash('sha256').update(readFileSync(filePath)).digest('hex');
      if (actual !== entry.sha) {
        // Edited since we wrote it. Retain — and SAY SO, because this is the
        // case where the control plane believes the memory is gone and the
        // agent is still reading it.
        outcomes.kept_modified++;
        outcomes.unapplied.push({ name: tomb.name, reason: 'kept_modified' });
        log(
          `[memory-retire] ${agent.code_name}: KEPT '${tomb.name}' (${entry.file}) — ` +
            `edited since we wrote it (recorded ${entry.sha.slice(0, 12)}, on disk ${actual.slice(0, 12)})`,
        );
        continue;
      }

      const destDir = retiredMemoryDir(configDir, agent.code_name);
      mkdirSync(destDir, { recursive: true });
      // Timestamped so a retire → revive → retire cycle cannot silently
      // overwrite the earlier copy (memory_save can revive a retired row).
      //
      // The timestamp ALONE is not enough: Date.now() is millisecond-resolution,
      // and two retirements of the same file inside one tick land on the same
      // suffix — renameSync then overwrites on POSIX with no error, destroying
      // the very forensic copy this move exists to preserve. Probe for a free
      // name rather than assuming the clock separates them.
      const stamp = Date.now();
      let dest = join(destDir, `${entry.file}.${stamp}.retired`);
      for (let n = 1; existsSync(dest); n++) {
        dest = join(destDir, `${entry.file}.${stamp}-${n}.retired`);
      }
      renameSync(filePath, dest);
      // Stamp the retirement time. `renameSync` PRESERVES the source mtime, so
      // without this the reaper ages the copy from when the memory file was last
      // WRITTEN, not from when it was retired -- and a long-stable memory retires
      // straight into an already-expired copy that the very next pass deletes.
      // The forensic window would silently be zero for exactly the memories most
      // likely to matter.
      try {
        const now = Date.now() / 1000;
        utimesSync(dest, now, now);
      } catch {
        /* the copy is still there; it just ages from its old mtime */
      }

      delete manifest[tomb.name];
      manifestDirty = true;
      // Drop the upload-side hash too, so the next tick does not treat the now-
      // absent file as anything at all.
      memoryFileHashes.get(agent.agent_id)?.delete(entry.file);
      outcomes.removed++;
      log(`[memory-retire] ${agent.code_name}: removed '${tomb.name}' (${entry.file})`);
    } catch (err) {
      outcomes.failed++;
      outcomes.unapplied.push({ name: tomb.name, reason: 'failed' });
      log(`[memory-retire] ${agent.code_name}: FAILED '${tomb.name}': ${(err as Error).message}`);
    }
  }

  if (manifestDirty) saveMemoryManifest(agent.agent_id, configDir, agent.code_name);

  // Emitted whenever there were tombstones at all, INCLUDING the all-retained
  // tick. The pre-existing download log is gated on `written > 0 ||
  // overwritten > 0`, so a tick that changed nothing said nothing — exactly the
  // gap that makes "never ran" and "ran and did nothing" indistinguishable.
  log(
    `[memory-retire] ${agent.code_name}: ${tombstones.length} tombstone(s) — ` +
      `${outcomes.removed} removed, ${outcomes.kept_modified} kept (edited), ` +
      `${outcomes.kept_unknown} kept (not ours), ${outcomes.already_gone} already gone, ` +
      `${outcomes.failed} failed`,
  );

  if (truncated) {
    // Not a retry: the list is ordered most-recently-retired first, so anything
    // past the server's cap is never served again as the set grows.
    log(
      `[memory-retire] ${agent.code_name}: tombstone list was TRUNCATED by the server — ` +
        `older retirements are permanently absent from it and their files cannot be reached`,
    );
  }

  reapRetiredMemories(configDir, agent.code_name, log);
  return outcomes;
}

/**
 * Drop moved files once they are past their forensic value. Without this the
 * retired dir grows forever, holding exactly the content we set out to remove.
 */
function reapRetiredMemories(configDir: string, codeName: string, log: (msg: string) => void): void {
  const dir = retiredMemoryDir(configDir, codeName);
  if (!existsSync(dir)) return;
  const cutoff = Date.now() - RETIRED_MEMORY_TTL_MS;
  let reaped = 0;
  try {
    for (const file of readdirSync(dir)) {
      if (!file.endsWith('.retired')) continue;
      const path = join(dir, file);
      try {
        if (statSync(path).mtimeMs < cutoff) { unlinkSync(path); reaped++; }
      } catch { /* skip */ }
    }
  } catch { return; }
  if (reaped > 0) log(`[memory-retire] ${codeName}: reaped ${reaped} retired memory file(s) past TTL`);
}

async function syncMemories(
  agent: { agent_id: string; code_name: string },
  configDir: string,
  log: (msg: string) => void,
): Promise<void> {
  const projectDir = join(configDir, agent.code_name, 'project');
  const memoryDir = join(projectDir, 'memory');

  // ENG-4643: on a fresh-sync tick (post-migration / post-assignment) we
  // download first and let the DB be authoritative. Any stale local
  // file from a prior provision gets overwritten when its DB twin has
  // different content. Subsequent ticks fall through to the regular
  // upload-then-download bidirectional flow.
  //
  // Skip the upload path entirely when the download fails — without
  // a confirmed catch-up pull, any local .md files we'd upload risk
  // overwriting fresher DB content with stale local copies (the
  // exact scenario we're guarding against). Leave the pending flag
  // set so the next supervisor tick retries the download.
  const isFreshSync = pendingFreshMemorySync.has(agent.agent_id);
  if (isFreshSync) {
    log(`[memory-sync] Fresh-sync requested for '${agent.code_name}' — pulling DB first`);
    const result = await downloadMemories(agent, memoryDir, log, { force: true, configDir });
    if (result.status === 'failed') {
      log(`[memory-sync] Fresh-sync download failed for '${agent.code_name}' — skipping upload, will retry next tick`);
      return;
    }
    // ENG-9257: apply BEFORE the upload block reads the directory, so a removed
    // file is never re-uploaded on this tick. This ordering is why the fresh-sync
    // path is the safe one; the regular path below has to compensate.
    if (result.tombstones) {
      const tombEnabled = hostFlagStore().getBooleanForAgent('memory-file-tombstones', agent.agent_id);
      const tombOutcomes = applyMemoryTombstones({
        agent, configDir, memoryDir,
        tombstones: result.tombstones,
        truncated: result.truncated,
        enabled: tombEnabled,
        log,
      });
      // Only when the gate is ON. Reporting an empty set while disabled would
      // CLEAR the agent's rows and assert "nothing outstanding" about a pass
      // that never ran.
      if (tombEnabled) await reportMemoryRetirement(agent, tombOutcomes, log);
    }
    pendingFreshMemorySync.delete(agent.agent_id);
  }

  // --- Upload: local → DB (only changed files) ---
  if (existsSync(memoryDir)) {
    const prevHashes = memoryFileHashes.get(agent.agent_id) ?? new Map<string, string>();
    const currentHashes = new Map<string, string>();
    const changedMemories: Array<{ name: string; content: string; type: string; expires_at?: string }> = [];

    for (const file of readdirSync(memoryDir)) {
      if (!file.endsWith('.md')) continue;
      try {
        const raw = readFileSync(join(memoryDir, file), 'utf-8');
        const fileHash = createHash('sha256').update(raw).digest('hex').slice(0, 16);
        currentHashes.set(file, fileHash);

        // Only parse + upload if file content changed since last cycle
        if (prevHashes.get(file) === fileHash) continue;

        const parsed = parseMemoryFile(raw, file.replace(/\.md$/, ''));
        if (parsed) {
          // Daily memories require expires_at (DB constraint)
          if (parsed.type === 'daily') {
            (parsed as { expires_at?: string }).expires_at = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
          }
          changedMemories.push(parsed);
        }
      } catch { /* skip unreadable */ }
    }

    memoryFileHashes.set(agent.agent_id, currentHashes);

    if (changedMemories.length > 0) {
      try {
        await api.post('/host/sync-memories', {
          agent_id: agent.agent_id,
          memories: changedMemories,
        });
        log(`Synced ${changedMemories.length} changed memories to DB for '${agent.code_name}'`);
      } catch (err) {
        // Reset hashes for changed files so they retry next cycle
        for (const mem of changedMemories) {
          for (const [file] of currentHashes) {
            const parsed = parseMemoryFile(readFileSync(join(memoryDir, file), 'utf-8'), file.replace(/\.md$/, ''));
            if (parsed?.name === mem.name) currentHashes.delete(file);
          }
        }
        log(`Memory upload failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }
  }

  // --- Download: DB → local (skip if local files unchanged) ---
  // ENG-4643: skipped when isFreshSync ran above (we already pulled).
  if (!isFreshSync) {
    const result = await downloadMemories(agent, memoryDir, log, { force: false, configDir });
    // ENG-9257: applied on BOTH 'fetched' and 'skipped'. The short-circuit means
    // "nothing to write", not "nothing to remove" — and gating removal on it
    // would make the pass unreachable, since a retained file changes no hash.
    // Never on 'failed': tombstones is null there anyway, but the status check
    // states the precondition rather than relying on that.
    // ENG-9585: nor on 'deferred', for the same reason and a different cause —
    // that pass never asked, so its null means "no information" rather than
    // "nothing is retired". Retirement resumes on the next pass that fetches,
    // at most one TTL later.
    if (result.status !== 'failed' && result.tombstones) {
      const tombEnabled = hostFlagStore().getBooleanForAgent('memory-file-tombstones', agent.agent_id);
      const tombOutcomes = applyMemoryTombstones({
        agent, configDir, memoryDir,
        tombstones: result.tombstones,
        truncated: result.truncated,
        enabled: tombEnabled,
        log,
      });
      // Only when the gate is ON. Reporting an empty set while disabled would
      // CLEAR the agent's rows and assert "nothing outstanding" about a pass
      // that never ran.
      if (tombEnabled) await reportMemoryRetirement(agent, tombOutcomes, log);
    }
  }
}

/**
 * Pull DB memories and write them to the local memory dir, reconciling
 * against existing files by CONTENT hash instead of slug presence.
 *
 * ENG-4643:
 *   - Old behaviour: skip the DB version whenever a same-slug local
 *     file existed, regardless of whether the local content matched.
 *     Stale provision leftovers permanently shadowed DB updates.
 *   - New behaviour: derive slug + render the canonical file body for
 *     each DB memory; if the existing file's bytes match exactly,
 *     skip; otherwise overwrite. Files with no DB twin are left alone
 *     (the upload path handles those on the next pass).
 *   - When `force` is true, the local-listing-hash short-circuit is
 *     bypassed so the migration / fresh-assignment first sync always
 *     reaches the network call.
 */
async function downloadMemories(
  agent: { agent_id: string; code_name: string },
  memoryDir: string,
  log: (msg: string) => void,
  { force, configDir }: { force: boolean; configDir: string },
): Promise<DownloadMemoriesResult> {
  const localFiles = existsSync(memoryDir)
    ? readdirSync(memoryDir).filter((f) => f.endsWith('.md')).sort()
    : [];
  const localListHash = createHash('sha256').update(localFiles.join(',')).digest('hex').slice(0, 16);
  const prevLocalHash = lastLocalFileHash.get(agent.agent_id);
  const prevDownload = lastDownloadHash.get(agent.agent_id);

  // ENG-9585: `/host/memories` was the single largest consumer of prod egress —
  // 44.7% of it, at a 61 KB mean, fetched on EVERY pass for EVERY agent. The
  // short-circuit below (`prevLocalHash === localListHash && ...responseHash`)
  // already discards the response when nothing changed, so those bytes were
  // provably wasted: the corpus was downloaded in full purely to hash it and
  // conclude there was nothing to write.
  //
  // `force` bypasses the spacing, so the fresh-assignment / migration first
  // sync still always reaches the network — the same exemption that already
  // governs the local-listing-hash short-circuit.
  // Taken BEFORE the request, for the same reason as the refresh lane.
  const memoriesToken = memoriesFetchGate.beginFetch(agent.agent_id);
  if (!force && !memoriesFetchGate.shouldFetch(agent.agent_id)) {
    // DELIBERATELY NOT 'skipped'. That value already means "we asked, and there
    // was nothing to write" and it carries a real tombstone list; this means "we
    // did not ask", and its tombstones are null for want of information. The
    // caller's ENG-9257 rule is "apply on both fetched and skipped", so folding
    // this case into 'skipped' would invite exactly the simplification that
    // breaks it — dropping the `&& result.tombstones` guard would then hand the
    // tombstone pass a null on every deferred pass.
    return { status: 'deferred', tombstones: null, truncated: false };
  }

  try {
    // ENG-9584: send the server's own token for the body we already hold, so a
    // corpus that has not changed costs ~90 bytes instead of 45.5 KB. Omitted
    // when we hold nothing, which is also what an older API's response leaves
    // us with — either way the request is exactly the pre-ENG-9584 one.
    const quoted = lastMemoriesBody.get(agent.agent_id);
    // ENG-9678 + ENG-9584 compose: the manifest shrinks a CHANGED corpus, the
    // token elides an UNCHANGED one. Both flags ride on the same request, and
    // the manifest flag must be on EVERY call to this route (see the re-fetch
    // below) — dropping it on one path silently returns that path to full
    // bodies, which costs nothing observable and undoes the saving.
    let wire = await api.post<MemoriesWireBody>('/host/memories', {
      agent_id: agent.agent_id,
      memories_manifest: true,
      // ENG-9698 renamed this field from `known_hash`. Emitting the NEW name
      // only: the route reads `known_token ?? known_hash`, so a new host against
      // an old API degrades to a full response rather than breaking.
      ...(quoted ? { known_token: quoted.hash } : {}),
    });
    // Success only — a throw skips this and the next pass retries immediately.
    memoriesFetchGate.markFetched(agent.agent_id, memoriesToken);

    // RE-READ the cache; never trust the reference captured before the await.
    // `clearAgentCaches` and `markAgentForFreshMemorySync` both run off Realtime
    // events and can purge the quoted entry while this request is in flight —
    // and a purge does not touch `quoted`, which stays perfectly non-null. A
    // caller testing its own captured value would conclude it still holds a body
    // and apply an `unchanged` answer to state the purge deliberately retired,
    // making the purge inauthoritative exactly when it matters. `stillHeld` also
    // requires the token to match, so a purge-then-repopulate by a concurrent
    // pass is not mistaken for the entry we quoted.
    let held = stillHeld(quoted, lastMemoriesBody.get(agent.agent_id));

    // The marker with no body to apply it to. An empty corpus is NOT a safe
    // stand-in — so ask again, without a token, and take the full response.
    if (resolveConditionalResponse(wire, held !== undefined).use === 'no-body') {
      log(
        `Memory download for '${agent.code_name}': unchanged marker with no cached body ` +
          `(cache purged mid-flight?) — re-fetching in full`,
      );
      // Drops `known_hash` ONLY. Keeping `memories_manifest` matters: this is
      // the recovery path, so a silent fall back to full bodies here would be
      // invisible except as a bandwidth regression on exactly the ticks where
      // the cache was purged mid-flight.
      wire = await api.post<MemoriesWireBody>('/host/memories', {
        agent_id: agent.agent_id,
        memories_manifest: true,
      });
      // The re-fetch quoted NO token, so nothing may be reused off the back of
      // it — including an entry that reappeared while it was in flight.
      held = undefined;
    }

    let dbMemories: MemoriesResponseBody;
    const decision = resolveConditionalResponse(wire, held !== undefined);
    if (decision.use === 'cached') {
      dbMemories = held!.body;
      pollCycleMemoriesUnchanged += 1;
    } else if (decision.use === 'no-body') {
      // The re-fetch above already sent no token, so a contract-honouring API
      // cannot reach here. Fail the pass rather than invent a corpus: 'failed'
      // retries on the next tick and touches nothing.
      log(`Memory download for '${agent.code_name}': server sent 'unchanged' for an untokened request`);
      return { status: 'failed', tombstones: null, truncated: false };
    } else {
      // The WHOLE wire object, not a reconstruction of the fields used today.
      // A field-by-field copy is lossy in a way that only shows up later and
      // only on the cached path: add a reader for some key the copy forgot, and
      // it works on every full response and reads `undefined` on every
      // unchanged one. Keeping the object whole means the cached body and a
      // fresh one are the same thing.
      dbMemories = wire;
      if (decision.token) {
        lastMemoriesBody.set(agent.agent_id, { hash: decision.token, body: dbMemories });
      } else {
        lastMemoriesBody.delete(agent.agent_id);
      }
      pollCycleMemoriesFull += 1;
    }

    // ENG-9257: `undefined` and `[]` mean OPPOSITE things and must not be
    // collapsed with `?? []`. Absent = this server has no tombstone concept (or
    // its tombstone read failed) = we have no information and must not remove
    // anything. Empty = it has one and nothing is retired.
    const tombstones = Array.isArray(dbMemories.tombstones) ? dbMemories.tombstones : null;

    // ENG-9257: the tombstone list is part of the response for change-detection
    // purposes. Hashing only `.memories` meant retiring one memory from an
    // otherwise-stable corpus changed nothing observable, so the tick
    // short-circuited below and the retirement was never acted on — for the life
    // of the manager process.
    // ENG-9678: normalise the manifest back into the full-content shape RIGHT
    // HERE, at the wire boundary and BEFORE anything hashes it. Everything
    // downstream — the response hash, the short-circuit, the write loop — is
    // untouched, which is what keeps this a transport change rather than a
    // behaviour change.
    //
    // Ordering is the server's and is load-bearing: the resolver preserves
    // manifest order, and the manifest is emitted under a total ORDER BY, so
    // two polls a second apart hash identically and the short-circuit below
    // still fires. Resolve failures perturb the hash, which is correct — it
    // means the next tick retries rather than skipping.
    let memories = dbMemories.memories;
    if (dbMemories.memories_manifest) {
      const resolved = await resolveMemoriesFromManifest({
        manifest: dbMemories.memories_manifest,
        // Per-agent, not host-wide: memories are agent-private, so a shared
        // store would buy almost no dedupe while exposing one agent's content
        // to every other agent on the host. See memory-cache.ts's header.
        cacheDir: join(configDir, agent.code_name, '_memory_bodies'),
        fetchContents: async (hashes) => {
          const res = await api.post<{ contents?: Array<{ hash: string; content: string }> }>(
            '/host/memories/contents',
            { agent_id: agent.agent_id, hashes },
          );
          return res.contents ?? [];
        },
        log,
      });
      memories = resolved.memories;
      if (dbMemories.memories_manifest_truncated) {
        // The server hit its runaway ceiling, so this corpus is PARTIAL by the
        // server's own admission — distinct from `unresolved` below, which is a
        // local cache/fetch miss over a complete list. Absence from a clipped
        // manifest must never be read as retirement; the ENG-9257 contract
        // already says only an explicit tombstone removes a file, and this log
        // exists so a shrinking memory dir has a visible cause.
        log(
          `[memory-cache] ${agent.code_name}: server returned a TRUNCATED manifest ` +
            `(${dbMemories.memories_manifest.length} entries, ceiling reached) — ` +
            `corpus is partial this tick`,
        );
      }
      if (resolved.unresolved > 0) {
        // Counts only, never a memory name or body. Fails soft: the files
        // already on disk are untouched and this self-heals next poll.
        log(
          `[memory-cache] ${agent.code_name}: ${resolved.unresolved} of ` +
            `${dbMemories.memories_manifest.length} memory bodies unresolved this tick`,
        );
      }
    }

    const responseHash = createHash('sha256')
      .update(JSON.stringify({ m: memories ?? [], t: tombstones }))
      .digest('hex').slice(0, 16);

    // Skip processing if nothing changed (local files same + DB response same).
    // `force=true` callers (post-migration first sync) ignore this guard.
    //
    // ENG-9257: tombstones are returned even on this path, and applied by the
    // CALLER. Gating tombstone work on this short-circuit would make it
    // unreachable: a retained file renames nothing, so neither hash moves and
    // the next tick skips again — and arming the flag perturbs no hash at all,
    // so the flip would be inert on exactly the quiet agents that have the
    // largest backlogs.
    if (
      !force &&
      prevDownload &&
      prevLocalHash === localListHash &&
      lastDownloadHash.get(agent.agent_id) === responseHash
    ) {
      return { status: 'skipped', tombstones, truncated: dbMemories.tombstones_truncated === true };
    }

    lastDownloadHash.set(agent.agent_id, responseHash);
    lastLocalFileHash.set(agent.agent_id, localListHash);

    if (memories?.length) {
      mkdirSync(memoryDir, { recursive: true });
      const manifest = loadMemoryManifest(agent.agent_id, configDir, agent.code_name);

      let written = 0;
      let overwritten = 0;
      for (let i = 0; i < memories.length; i++) {
        const mem = memories[i]!;
        const rawSlug = mem.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 60);
        const slug = rawSlug || `memory-${i}`;
        const fileName = `${slug}.md`;
        const filePath = join(memoryDir, fileName);
        const desired = `---\nname: ${JSON.stringify(mem.name)}\ntype: ${mem.type}\ndescription: ${JSON.stringify(mem.content.slice(0, 200))}\n---\n\n${mem.content}\n`;
        // ENG-9257: record what we wrote, keyed by NAME (which is what a
        // tombstone carries) and holding the filename we actually used — so the
        // delete path never re-derives the lossy slug, and the index-dependent
        // `memory-${i}` fallback above cannot mislead it.
        const record = () => {
          const sha = createHash('sha256').update(desired).digest('hex');
          const prev = manifest[mem.name];
          if (prev?.file === fileName && prev.sha === sha) return false;
          manifest[mem.name] = { file: fileName, sha };
          return true;
        };
        let manifestDirty = false;

        if (existsSync(filePath)) {
          // Content compare — DB wins on mismatch (the local file is
          // either a stale provision leftover or out-of-band edited
          // copy that the upload path didn't catch).
          let existing = '';
          try { existing = readFileSync(filePath, 'utf-8'); } catch { /* unreadable → overwrite */ }
          if (existing === desired) {
            // Already correct. Still record it — this file IS ours, and without
            // an entry it could never be removed after a restart.
            if (record()) manifestDirty = true;
            if (manifestDirty) saveMemoryManifest(agent.agent_id, configDir, agent.code_name);
            continue;
          }
          writeFileSync(filePath, desired);
          overwritten++;
        } else {
          writeFileSync(filePath, desired);
          written++;
        }
        if (record()) manifestDirty = true;
        if (manifestDirty) saveMemoryManifest(agent.agent_id, configDir, agent.code_name);
      }

      if (written > 0 || overwritten > 0) {
        // Update local file hash since we just wrote new/changed files
        const updatedFiles = readdirSync(memoryDir).filter((f) => f.endsWith('.md')).sort();
        lastLocalFileHash.set(agent.agent_id, createHash('sha256').update(updatedFiles.join(',')).digest('hex').slice(0, 16));
        log(`Memory download for '${agent.code_name}': wrote ${written} new, overwrote ${overwritten} stale`);
      }
    }
    return { status: 'fetched', tombstones, truncated: dbMemories.tombstones_truncated === true };
  } catch (err) {
    log(`Memory download failed for '${agent.code_name}': ${(err as Error).message}`);
    return { status: 'failed', tombstones: null, truncated: false };
  }
}

// ---------------------------------------------------------------------------
// Cleanup helper (for revoked agents)
// ---------------------------------------------------------------------------

async function cleanupAgentFiles(codeName: string, agentDir: string): Promise<void> {
  // Remove the provision directory
  if (existsSync(agentDir)) {
    try {
      rmSync(agentDir, { recursive: true, force: true });
      log(`Removed provision directory for '${codeName}'`);
    } catch (err) {
      log(`Failed to remove provision dir for '${codeName}': ${(err as Error).message}`);
    }
  }

  // Deregister from the framework runtime
  try {
    const adapter = resolveAgentFramework(codeName);
    const registered = await getOrCacheRegisteredAgents(adapter, codeName);
    if (registered.has(codeName)) {
      await adapter.deregisterAgent(codeName);
      registered.delete(codeName);
      log(`Deregistered '${codeName}' from ${adapter.label}`);
    }
  } catch (err) {
    log(`Failed to deregister '${codeName}': ${(err as Error).message}`);
  }
}

// ---------------------------------------------------------------------------
// IPC + lifecycle
// ---------------------------------------------------------------------------

// Caffeinate subprocess — prevents macOS sleep while manager is running
let caffeinateProc: import('node:child_process').ChildProcess | null = null;

async function startCaffeinate(): Promise<void> {
  if (process.platform !== 'darwin') return;
  try {
    const { spawn } = await import('node:child_process');
    caffeinateProc = spawn('caffeinate', ['-dims'], {
      stdio: 'ignore',
      detached: false,
    });
    caffeinateProc.on('error', (err) => {
      log(`caffeinate failed: ${err.message} — Mac may sleep during operation`);
      caffeinateProc = null;
    });
    caffeinateProc.on('exit', () => { caffeinateProc = null; });
    if (caffeinateProc.pid) {
      log(`caffeinate started (PID ${caffeinateProc.pid}) — Mac sleep prevented while manager is running`);
    }
  } catch (err) {
    log(`caffeinate not available: ${(err as Error).message} — Mac may sleep during operation`);
  }
}

function stopCaffeinate(): void {
  if (caffeinateProc) {
    caffeinateProc.kill();
    caffeinateProc = null;
    log('caffeinate stopped');
  }
}

// ENG-6257 (Augmented Live Phase 2D): one streaming scanner per live agent,
// driven once per poll cycle (no dedicated timer — reuses the poll cadence).
// Self-gating: a no-op for agents with no artefacts dir; best-effort and
// isolated so a streaming hiccup never affects the poll loop.
const artifactScanners = new Map<string, ArtifactStreamScanner>();
let artifactStreamingInFlight = false;

async function driveArtifactStreaming(): Promise<void> {
  // The two call sites fire-and-forget after each pollCycle; if a scan outlasts
  // the poll interval, a second run would race the shared scanner map. Skip an
  // overlapping run rather than mutate the map concurrently.
  if (artifactStreamingInFlight) return;
  artifactStreamingInFlight = true;
  try {
    await driveArtifactStreamingInner();
  } finally {
    artifactStreamingInFlight = false;
  }
}

async function driveArtifactStreamingInner(): Promise<void> {
  const liveIds = new Set<string>();
  for (const agent of state.agents) {
    if (!agent.agentId || !agent.codeName || agent.status !== 'active') continue;
    liveIds.add(agent.agentId);
    let scanner = artifactScanners.get(agent.agentId);
    if (!scanner) {
      scanner = new ArtifactStreamScanner(
        agent.agentId,
        artifactsDirFor(agent.codeName),
        nodeArtifactFs,
        { api, log },
      );
      artifactScanners.set(agent.agentId, scanner);
    }
    try {
      await scanner.scanOnce();
    } catch (err) {
      log(`[artifact-stream] scan failed for ${agent.codeName}: ${(err as Error).message}`);
    }
  }
  // Drop scanners for agents no longer active so their encoder/draft state is released.
  for (const id of artifactScanners.keys()) {
    if (!liveIds.has(id)) artifactScanners.delete(id);
  }
}

/**
 * ENG-7273: the fast, independent liveness heartbeat. The main poll cycle stamps
 * hosts.last_seen_at + agents.last_heartbeat_at only once per iteration (section
 * 1c), but a busy host's cycle wall-clock - per-agent provisioning and
 * connectivity probes, and the multi-minute re-provision after a restart - can
 * exceed the webapp's 2-minute offline threshold, flipping every agent to
 * "offline" (and blocking Direct Chat) while they are alive and replying. This
 * posts a MINIMAL /host/heartbeat on its own ~30s interval so the liveness
 * signal can never be starved by cycle duration.
 *
 * The body is intentionally minimal: the route requires only host_id and writes
 * every other column conditionally, so this bumps last_seen_at (and, for the
 * host's assigned agents, last_heartbeat_at) without touching
 * framework_version/host_security/diagnostics - those stay on the slow cycle.
 * Best-effort and non-throwing; the reentrancy guard stops a slow POST stacking.
 */
async function postLivenessHeartbeat(): Promise<void> {
  if (!running || !config || restartAfterUpgrade) return;
  if (livenessInFlight) return;
  livenessInFlight = true;
  try {
    const hostId = await getHostId();
    // host id is null until the first key exchange resolves; an early tick
    // before the first poll cycle would otherwise POST host_id:null -> 400.
    // Skip and let the next tick (post-exchange) refresh liveness.
    if (!hostId) return;
    // ENG-9174: carry poll-loop liveness on THIS path specifically.
    //
    // The body above is otherwise deliberately minimal, and these two fields
    // are the one justified exception. This heartbeat runs on its own interval
    // outside the poll loop — which is exactly why `last_seen_at` stays fresh
    // when the loop has stopped, and therefore why it is the only channel that
    // can still report the stoppage. Putting this on the slow in-cycle
    // heartbeat instead would go silent at precisely the moment it mattered.
    //
    // `state.lastPollAt` is already maintained for `agt manager status`; this
    // adds no measurement, only transport. Null until the first cycle
    // completes, and the server treats absent/null as NO SIGNAL rather than as
    // a stopped loop.
    await api.post('/host/heartbeat', {
      host_id: hostId,
      agt_version: agtCliVersion,
      last_poll_at: state.lastPollAt ?? undefined,
      // The delay actually in force, so the monitor compares against reality
      // rather than a constant — a manager riding out an API outage under the
      // ENG-5041 backoff must not be paged as stopped.
      //
      // ENG-10760 made this load-bearing rather than merely tidy. It now also
      // carries the idle backoff, and `poll-loop-liveness.ts` allows
      // `poll_backoff_ms * 30` of silence: at the default 10s interval that is
      // exactly 300,000ms, so an agent-less host sleeping for its 5-minute cap
      // would sit precisely on the `poll_loop_stopped` boundary if this reported
      // the interval instead. Reporting the real delay is what keeps a
      // backed-off host distinguishable from a dead one.
      //
      // THE TRADE THIS MAKES, STATED PLAINLY. The allowance scales with what we
      // report, so an idle host at the 5-minute cap widens its own
      // `poll_loop_stopped` latency from 5 minutes to 2.5 hours. That is
      // accepted rather than overlooked: a wedged poll loop on a host with no
      // agents starves nobody, a genuinely dead HOST is still caught in ~2
      // minutes by `host_down` off the untouched 30s liveness heartbeat, and the
      // first thing anyone does with such a host — assign it an agent — surfaces
      // the wedge immediately. A host with agents is unaffected, because the
      // idle term is 0 for it.
      poll_backoff_ms: currentEffectivePollDelayMs(),
    });
  } catch (err) {
    // A transient API/exchange failure must never crash the manager; the next
    // tick (and the slow cycle's own heartbeat) retry.
    log(`[liveness] fast heartbeat failed: ${(err as Error).message}`);
  } finally {
    livenessInFlight = false;
  }
}

function startPolling(): void {
  if (!config || running) return;
  running = true;

  // ENG-7273: arm the fast liveness heartbeat immediately, before the first
  // (and every subsequent) heavy poll cycle, so last_seen_at stays fresh even
  // while a long cycle or a post-restart re-provision is in flight. unref() so
  // it never holds the process open on shutdown.
  livenessTimer = setInterval(() => {
    void postLivenessHeartbeat();
  }, LIVENESS_HEARTBEAT_INTERVAL_MS);
  livenessTimer.unref();

  // ENG-8116: Claude Code occupancy is reconstructed from pane.log mtime, and
  // it cannot ride pollCycle — measured poll spacing is 29s at the fleet median
  // and ~180s at p90, which cannot resolve per-minute durations. This is a
  // statSync-only timer feeding an in-memory ledger: no destructive drains, no
  // iteration of mutable manager state beyond a snapshot read, so unlike moving
  // the probe itself it cannot lose counters or skip an agent's telemetry. Its
  // worst failure is one missed 10s sample, which the next tick corrects.
  // opencode agents have no pane.log and are skipped here — they report exact
  // spans from their turn lifecycle instead.
  startPaneOccupancySampler(() => agentState.persistentSessionAgents);

  // ENG-8876: PID pressure on its own timer for the same reason occupancy is —
  // the poll cycle's real spacing is ~29s median / ~180s p90, and a resource
  // LEVEL sampled at 180s resolution is a level you find out about late. The
  // difference from occupancy is that nothing accrues here, so a missed tick
  // costs staleness rather than data. Container-id resolution is cached, so the
  // steady-state cost of a tick is a handful of readFileSync calls.
  startPidPressureSampler(() => agentState.persistentSessionAgents);
  // ENG-10857: same live-agent source, a slower cadence. This one measures a
  // CONSTANT (the image's baked claude), so the interval governs only how
  // quickly a NEW container is noticed, not the resolution of a moving value.
  startRuntimeVersionSampler(() => agentState.persistentSessionAgents);

  // ENG-10869: adopt CLAUDE_CONFIG_DIR process-wide, and seed the file into it.
  //
  // Process-wide so the MANAGER'S OWN in-process reads and writes agree with
  // what the fleet reads — the account fingerprint, the device-state probe, the
  // config-health check and `ensureWorkspaceTrusted` all run here.
  //
  // IT DOES NOT REACH A PANE OF AN ALREADY-RUNNING TMUX SERVER — and that
  // qualifier is load-bearing, because an earlier version of this comment
  // asserted the unqualified version and a reviewer refuted it: on a host's
  // FIRST spawn the bootstrap `new-session` becomes the server, which inherits
  // `process.env`, and every later pane then inherits the server's global
  // environment. That is why the adoption below is gated on readiness rather
  // than relying on this paragraph.
  //
  // An earlier version also claimed the opposite direction ("spawnPairSession
  // copies process.env into the pair tmux", offered as a reason the adoption
  // was sufficient on its own). That is false too, and the refutation was
  // already in this repo: the ENG-8800
  // note in persistent-session.ts records, measured on a live host, that a pane
  // gets the SERVER's global environment merged with `-e` entries plus the
  // `update-environment` names, and that a variable present only in the client
  // env never reaches the pane at all. `spawnPairSession` passed `{ env }` and
  // no `-e`, so the pair pane kept writing `<home>/.claude.json` while
  // `finalizeClaudePairOnboarding` watched `<configDir>/.claude.json` — an
  // mtime that could never move, i.e. every pairing timing out, which under
  // ENG-10862 becomes a reported FAILURE for a pairing that worked.
  //
  // Delivery to panes is by `-e` at the two spawn sites: `spawnPairSession`'s
  // `new-session` argv, and `tmuxSessionEnvArgs` for host-mode agents. This
  // block is not a substitute for either.
  //
  // Seeding here too, not just at spawn: the pair flow can run before any agent
  // spawns on a fresh host, and it polls the config's mtime to decide whether
  // onboarding took. Pointed at a path that does not exist yet it would time
  // out and emit the ENG-10862 warning for the wrong reason.
  {
    const seedOutcome = seedClaudeConfigFromLegacy({ log });
    // GATED ON READINESS, and that is not belt-and-braces — it closes a hole a
    // reviewer found in the paragraph above.
    //
    // The claim that this "does not reach any tmux pane" holds for a server
    // that is ALREADY RUNNING. `tmuxEnv` is built with `base: process.env`, and
    // the bootstrap `new-session` on a host's first spawn BECOMES the tmux
    // server (ENG-10092) — after which every pane inherits the server's global
    // environment (the ENG-8800 note). `spawnPairSession` spreads `process.env`
    // the same way. So an ungated adoption would leak CLAUDE_CONFIG_DIR into
    // every pane on such a host regardless of `claudeConfigDirIsReady`, and the
    // `-e` gate — which exists because pointing an agent at a config dir that
    // holds nothing usable IS the login picker — would be inert.
    //
    // Concretely: an unmigrated host running host-mode agents would point
    // `claude` at an empty `~/.claude`, it would create a fresh account-less
    // config, and the agent would park on the picker where it previously read
    // the legacy file.
    const configDirUsable = claudeConfigDirIsReady();
    if (configDirUsable && !process.env[CLAUDE_CONFIG_DIR_ENV]?.trim()) {
      process.env[CLAUDE_CONFIG_DIR_ENV] = claudeConfigDir();
    }
    log(
      `[claude-config] ${CLAUDE_CONFIG_DIR_ENV}=${process.env[CLAUDE_CONFIG_DIR_ENV] ?? '<not adopted>'} ` +
        `configDirUsable=${configDirUsable} seed=${seedOutcome}`,
    );
  }

  void startCaffeinate();
  // ENG-4712: rehydrate the channel-config hash cache from disk so a
  // fresh manager process recognises credentials it (or a sibling
  // manager) already wrote on a previous run, and skips the noisy
  // `reason=first-write` rewrite for unchanged configs.
  loadChannelHashCache();
  // ENG-7771: rehydrate the delivery baselines (sender_policy + the
  // slack/msteams behaviour subsets, ENG-7779) so a manager restart is not
  // mistaken for a first poll. Without this, every deploy wiped the
  // baselines and the first-poll branch advanced them WITHOUT restarting the
  // surviving tmux session — a sender_policy or behaviour change (incl.
  // SLACK_ALLOWED_USERS, an access-control gate) made across a manager
  // restart was never delivered to the running channel MCPs and never
  // self-healed (security control silently decorative).
  loadSenderPolicyBaseline();
  // ENG-9346: rehydrate the channel-sync and realtime-rebind streak counters
  // BEFORE the first poll. Both ride the heartbeat as arrays whose ALWAYS-SENT
  // empty form means "nothing is stuck", so a manager that restarts with them
  // wiped tells the control plane every stuck condition on the host just
  // recovered — and the replacement alert then reports an age measured from the
  // restart rather than from the failure. Two customer agents spent 50+ hours
  // with a dead channel being declared recovered once a day on exactly this.
  loadStuckStreaks();
  // ENG-7798: rehydrate the kanban-nudge backoff state so a manager restart
  // doesn't reset every blocked agent to the fast cadence (and the bottom of
  // the escalation ladder) — the same wipe-on-restart pathology as the
  // delivery baselines above, in a token-burn rather than security shape.
  loadKanbanNudgeStateFromDisk();
  // ENG-6251: hydrate the host flag store from last-known-good cache and log
  // its age, so a stale flags-cache.json is visible at boot. Cached values are
  // used only until the first heartbeat refreshes the map this same poll cycle.
  hostFlagStore().init();
  log(`Starting poll loop (interval=${config.intervalMs}ms, configDir=${config.configDir})`);

  // ENG-9129: report how long the whole boot took, and warn when it is slow.
  // process.uptime() is measured from process spawn by the runtime, so it
  // covers everything BEFORE our first log statement too — which is exactly the
  // window that was invisible during the 2026-08-18 outage. Nothing has to be
  // threaded through, and it cannot drift out of sync with a hand-captured
  // timestamp. Healthy respawn on that host measured 11.3s; the incident was
  // ~1,495s. This only ever changes what is logged: a slow boot is never fatal,
  // because a manager recovering late still beats one that refuses to start.
  {
    const startupOutcome = describeStartupCompletion(process.uptime() * 1000);
    log(startupOutcome.message);
  }

  // Kill any orphaned agt-* tmux sessions from a previous manager run,
  // then start the poll loop. Must await to avoid race with session spawn.
  // ENG-9068: the startup lane had the same shape as the other two — the very
  // first cycle rejecting meant the loop never started at all, which is the
  // worst instance of this bug rather than a lesser one.
  void killAllAgtTmuxSessions().catch(() => {}).then(() => {
    return pollCycleAndReschedule(pollCycle, {
      onError: (err) => log(`[poll-loop] startup cycle rejected: ${(err as Error)?.message ?? String(err)}`),
      reschedule: () => {
        void driveArtifactStreaming();
        scheduleNext();
      },
    });
  });
}

/**
 * ENG-6399: report a self-update-induced restart to audit_log for each agent
 * with a live persistent session, via POST /host/restart-event (source
 * 'self-update'). Called on the upgrade-restart path right before the manager
 * exits. Bounded by a short deadline so a slow/down API can't stall the restart
 * — any POSTs still in flight when the deadline wins are dropped (best-effort,
 * the same contract as the other restart-event reports).
 */
async function reportSelfUpdateRestarts(): Promise<void> {
  const liveAgents = [...agentState.persistentSessionAgents];
  if (liveAgents.length === 0) return;
  const reason = `CLI self-update${pendingUpgradeVersion ? ` to ${pendingUpgradeVersion}` : ''}`;
  const posts = Promise.allSettled(
    liveAgents.map((codeName) => {
      const agentId = agentState.codeNameToAgentId.get(codeName);
      if (!agentId) return Promise.resolve();
      return api.post('/host/restart-event', {
        agent_id: agentId,
        source: 'self-update',
        reason,
      });
    }),
  ).then(() => undefined);
  const deadline = new Promise<void>((resolve) => setTimeout(resolve, 3_000));
  await Promise.race([posts, deadline]);
}

function scheduleNext(): void {
  if (!running || !config) return;

  // ENG-4488: if the self-update flipped this during the poll we just
  // finished, exit cleanly instead of scheduling another cycle. Also
  // re-check inside the timer callback — `checkAndUpdateCli()` is kicked
  // off without await, so the flag can flip between scheduleNext() and
  // the next fire. Without the second check we'd run one more pollCycle
  // on the old Cellar path, exactly the failure this is meant to avoid.
  // forcedExitCode: 0 so the supervisor treats this as a clean respawn
  // even if graceful cleanup hits its 15s backstop.
  const restartNow = (): void => {
    // ENG-9137: at most one upgrade-restart sequence per process. Checked and set
    // SYNCHRONOUSLY, before any await or I/O, so two re-entries cannot both pass
    // the check. A later entry returns without logging or posting: the first
    // entry already owns the exit, and its `reportSelfUpdateRestarts()` already
    // covered every agent in `persistentSessionAgents`, so a second batch adds no
    // information - only duplicate rows.
    if (upgradeRestartInitiated) return;
    upgradeRestartInitiated = true;
    log(`[self-update] Restarting manager to load upgraded binary (${pendingUpgradeVersion ?? 'unknown version'})`);
    // ENG-6399: audit the self-update-induced restart per running agent BEFORE
    // we exit — this is the one point where we still know both the agents we're
    // about to bounce and the version delta. The new manager cold-starts those
    // sessions on its first poll but can't cleanly attribute the cold-start to a
    // self-update, so the honest signal lives here. Bounded + best-effort: it
    // must never delay or block the upgrade restart.
    void reportSelfUpdateRestarts()
      .catch((err) => log(`[self-update] restart-event audit failed: ${(err as Error).message}`))
      .finally(() => {
        // Exit with the dedicated restart code so the supervisor distinguishes
        // "please respawn me" from "the operator asked me to stop" — overloading
        // exit code 0 would make `agt manager stop` unable to actually stop
        // a supervised manager.
        void stopPolling({ forcedExitCode: SUPERVISOR_RESTART_EXIT_CODE }).then(() => {
          process.exit(SUPERVISOR_RESTART_EXIT_CODE);
        });
      });
  };

  if (restartAfterUpgrade) {
    restartNow();
    return;
  }
  // The widest of three: the configured interval, ENG-5041's failure backoff
  // (so a sustained API outage doesn't hammer the cloud every few seconds), and
  // ENG-10760's idle backoff (so a host with no agents stops paying to re-learn
  // an empty roster). Each of the latter two returns 0 in its healthy case, so a
  // busy host on a healthy API is timed exactly as it was before either existed.
  const delayMs = currentEffectivePollDelayMs();
  pollTimer = setTimeout(() => {
    if (restartAfterUpgrade) {
      restartNow();
      return;
    }
    // ENG-9068: re-arm on both paths. This is the steady-state lane, so a
    // rejection here is the one that strands a long-running healthy manager.
    void pollCycleAndReschedule(pollCycle, {
      onError: (err) => log(`[poll-loop] scheduled cycle rejected: ${(err as Error)?.message ?? String(err)}`),
      reschedule: () => {
        void driveArtifactStreaming();
        scheduleNext();
      },
    });
  }, delayMs);
}

async function killAllAgtTmuxSessions(): Promise<void> {
  try {
    const { stdout: output } = await execFilePromiseLong(
      'tmux',
      ['list-sessions', '-F', '#{session_name}'],
      { timeout: 2_000, stdin: 'ignore' },
    );
    const sessions = output.trim().split('\n').filter((s) => s.startsWith('agt-'));
    // ENG-10367: CONCURRENT, not a serial `for ... await`. Each kill carries its
    // own 2s timeout, so serially N sessions cost 2s x N of a 15s shutdown
    // budget that also has to fund the card release (3s) and
    // stopAllSessionsAndWait (4s). At six sessions the backstop fired before the
    // graceful stop began. The kills are independent calls against different
    // sessions, so there is no ordering to preserve. See drain-session-kill.ts
    // for the arithmetic and the test that holds this parallel.
    const { killed, failed } = await killSessionsConcurrently(sessions, async (session) => {
      await execFilePromiseLong('tmux', ['kill-session', '-t', session], {
        timeout: 2_000,
        stdin: 'ignore',
      });
    });
    for (const session of killed) log(`Killed tmux session '${session}'`);
    if (sessions.length > 0) {
      log(
        `Cleaned up ${killed.length} agt-* tmux session(s)` +
          (failed.length > 0 ? ` (${failed.length} already gone or unkillable)` : ''),
      );
    }
  } catch {
    // tmux not installed or no server running — nothing to clean up
  }
}

/**
 * ENG-10367 — release the `in_progress` kanban cards this drain is about to
 * orphan, before `killAllAgtTmuxSessions()` destroys the turns holding them.
 *
 * Runs on EVERY drain path, not just self-update. `stopPolling()` is reached
 * from the upgrade restart, from `agt manager stop`, and from the signal
 * handlers, and all three kill the same tmux sessions and orphan the same
 * cards. Scoping this to the self-update lane would fix the reported instance
 * and leave the identical bug on two other paths.
 *
 * SHAPE COPIED FROM `reportSelfUpdateRestarts()` rather than from the
 * `closeScheduledRunsForCode` loop above, deliberately. Those closers are
 * fire-and-forget and say so, justified by "the server-side reaper (S2) is the
 * durable backstop". For kanban cards there IS no backstop — both reapers share
 * `livenessDefersReap` and decline forever while the agent is healthy (see
 * drain-card-release.ts) — so this one is AWAITED under its own deadline.
 *
 * Bounded at 3s, well inside the 15s shutdown backstop, and it cannot fail the
 * drain: a slow or down API loses the release rather than delaying the restart.
 * Nothing here throws.
 *
 * The server enumerates the cards, not us. `kanbanBoardCache` is per-poll, so a
 * card moved to `in_progress` since the last tick is invisible to it — which is
 * precisely the mid-turn card most at risk. We send the agent and let the route
 * query `status = 'in_progress'` itself, which is also the ENG-7767 shape: the
 * client reports, the server decides.
 */
interface DrainReleaseSnapshot {
  enumerated: boolean;
  liveAgents: string[];
}

/**
 * ENG-10367 — capture WHICH agents hold live persistent sessions, taken BEFORE
 * the tmux kill and handed to the release afterwards.
 *
 * The release now runs after `killAllAgtTmuxSessions()` (see `stopPolling`), and
 * `agentState.persistentSessionAgents` is a mutable set with five deleters in
 * this file. None of them sits on the tmux-kill path today, but "today" is the
 * whole of that guarantee: a later teardown hooked into the kill would empty the
 * set and the release would silently find nothing to release, reporting
 * `scanned=0 acted=0` — the same false all-clear this ticket exists to remove.
 * Snapshotting makes the release independent of the order of side effects
 * instead of dependent on an invariant nobody is enforcing.
 */
function snapshotDrainReleaseTargets(): DrainReleaseSnapshot {
  try {
    return { enumerated: true, liveAgents: [...agentState.persistentSessionAgents] };
  } catch {
    // Enumeration itself failed. Report `not-scanned` rather than an all-clear —
    // knowing nothing is not the same as finding nothing.
    return { enumerated: false, liveAgents: [] };
  }
}

async function releaseInterruptedCardsOnDrain(snapshot: DrainReleaseSnapshot): Promise<void> {
  const startedAt = Date.now();
  const { enumerated, liveAgents } = snapshot;

  const { targets, unresolved } = decideDrainReleaseTargets(
    liveAgents,
    (codeName) => agentState.codeNameToAgentId.get(codeName),
  );

  let released = 0;
  let failed = 0;
  if (targets.length > 0) {
    const reason = restartAfterUpgrade
      ? `manager drain: CLI self-update${pendingUpgradeVersion ? ` to ${pendingUpgradeVersion}` : ''}`
      : 'manager drain: manager shutdown';
    const posts = Promise.allSettled(
      targets.map((t) =>
        api.post<{ released?: number; failed?: number }>('/host/kanban/release-interrupted', {
          agent_id: t.agentId,
          reason,
        }),
      ),
    );
    const deadline = new Promise<
      PromiseSettledResult<{ released?: number; failed?: number } | undefined>[]
    >((resolve) => setTimeout(() => resolve([]), 3_000));
    const settled = await Promise.race([posts, deadline]);
    // An empty array means the deadline won, so nothing is confirmed released
    // and every target is unaccounted for. Count them as failed, not as zero.
    if (settled.length === 0 && targets.length > 0) {
      failed = targets.length;
    } else {
      const tally = tallyDrainReleases(settled);
      released = tally.released;
      failed = tally.failed;
    }
  }

  const noteParts: string[] = [];
  if (unresolved.length > 0) noteParts.push(`unresolved_agent_ids=${unresolved.length}`);
  if (failed > 0) noteParts.push(`post_failures=${failed}`);
  log(
    formatDrainLine({
      step: 'release-interrupted-cards',
      elapsedMs: Date.now() - startedAt,
      scanned: drainReleaseScanned(enumerated, liveAgents.length),
      acted: released,
      note: noteParts.length > 0 ? noteParts.join(' ') : undefined,
    }),
  );
}

async function stopPolling(opts: { forcedExitCode?: number } = {}): Promise<void> {
  running = false;
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  // ENG-7273: stop the fast liveness heartbeat alongside the poll loop.
  if (livenessTimer) {
    clearInterval(livenessTimer);
    livenessTimer = null;
  }
  // ENG-8116: and the occupancy sampler. Any buckets it has already accrued but
  // not yet reported are lost on shutdown — in-memory and per-manager-generation
  // by design, matching the occupancy tracker it feeds. A restart also respawns
  // the sessions, so there is no work still in flight to attribute.
  stopPaneOccupancySampler();
  // ENG-8876: same lifetime as the occupancy sampler. The last reading dies with
  // the manager generation on purpose — a restart respawns the containers, so a
  // carried-over sample would describe a container that no longer exists.
  stopPidPressureSampler();
  stopRuntimeVersionSampler();

  // Hard backstop: force exit after 15s if graceful cleanup hangs. Default
  // exit code is 1 (crash-signal) so a SIGTERM handler that can't drain
  // surfaces as a failure to any supervisor. The self-update restart path
  // passes forcedExitCode: 0 so a stuck cleanup after `brew upgrade`
  // doesn't poison the respawn — the supervisor still sees clean exit and
  // re-spawns onto the new binary.
  const shutdownTimer = setTimeout(() => {
    log('Shutdown timeout exceeded (15s), forcing exit');
    process.exit(opts.forcedExitCode ?? 1);
  }, 15000);
  shutdownTimer.unref();

  // ENG-5865 — drain phase. Structured `[drain] ...` lines on every step
  // give operators a single greppable signal for the shutdown outcome.
  // sigtermClaudePids is the single load-bearing new behaviour: SIGTERM every
  // tracked claude -p child (the conv-eval backend) BEFORE we tear down tmux.
  // Without this they get re-parented to init and keep burning tokens; with
  // it, the boot-side reaper on the next manager start (startManager →
  // reapOrphanedClaudePids) SIGKILLs anything that didn't honour the SIGTERM.
  const drainStartedAt = Date.now();
  const livePids = [...inFlightClaudePids.keys()];
  if (livePids.length > 0) {
    for (const pid of livePids) {
      try { process.kill(pid, 'SIGTERM'); } catch { /* already exited */ }
    }
    log(formatDrainLine({
      step: 'sigterm-claude-pids',
      elapsedMs: Date.now() - drainStartedAt,
      killedPids: livePids,
      scanned: livePids.length,
      acted: livePids.length,
    }));
  } else {
    // ENG-10367: the note now names the class it scanned. The old wording,
    // "no in-flight claude -p children", was read as an all-clear for the
    // drain as a whole 62ms before a mid-turn session was killed.
    log(formatDrainLine({
      step: 'sigterm-claude-pids',
      elapsedMs: 0,
      scanned: 0,
      acted: 0,
      note: 'no in-flight claude -p children (this step scans claude -p children only)',
    }));
  }

  const subsysStartedAt = Date.now();
  stopCaffeinate();
  stopRealtimeChat();
  // ADR-0047: close every opencode agent's Slack socket + Telegram poller. Same
  // rationale as stopRealtimeChat - these are manager-owned loops, so nothing
  // else in the drain (tmux kill, session stop) reaps them.
  stopAllOpencodeSlackIngests(log);
  stopAllOpencodeTelegramIngests(log);
  log(formatDrainLine({ step: 'stop-subsystems', elapsedMs: Date.now() - subsysStartedAt }));

  // ENG-5599: close any in-session scheduled-task runs still tracked in memory.
  // The per-agent stop paths (stopPersistentSessionAndForgetMcpBaseline) cover
  // pause/revoke/restart, but a full manager shutdown/restart can exit without
  // hitting those — without this drain those runs would be left open forever
  // once scheduledRunsByCode is lost. Best-effort (finishRun POSTs in the
  // background under the 15s shutdown budget); the server-side reaper (S2) is
  // the durable backstop.
  for (const codeName of [...scheduledRunsByCode.keys()]) {
    closeScheduledRunsForCode(codeName, 'cancelled', 'manager shutdown');
  }

  // ENG-8818: and the persistent-session handles, for exactly the reason above.
  // This is the THIRD teardown site — the two per-agent lanes cover
  // pause/revoke/restart, and a clean manager shutdown reaches neither. It is
  // also the highest-volume orphan source in normal operation: one row per
  // agent per manager restart, on every host, forever.
  //
  // 'completed' rather than the 'cancelled' above: a scheduled-task run is work
  // being interrupted mid-flight, whereas the session handle has no work to
  // interrupt — it lived exactly as long as the session and is ending with it.
  for (const codeName of [...sessionRunsByCode.keys()]) {
    closeSessionRunForCode(codeName, 'completed', 'manager shutdown');
  }

  // ENG-10367: snapshot the agents holding live persistent sessions BEFORE the
  // kill, so the release below cannot be emptied by anything the kill path
  // touches. See snapshotDrainReleaseTargets().
  const drainReleaseSnapshot = snapshotDrainReleaseTargets();

  // Kill all agt-* tmux sessions FIRST — this is the most critical cleanup
  // Do it before graceful session stop to ensure sessions are killed even if
  // the graceful path hangs or times out.
  log('Killing tmux sessions...');
  try {
    await killAllAgtTmuxSessions();
  } finally {
    // ENG-10367: release the in_progress cards the kill above just orphaned.
    //
    // AFTER the kill, not before, and the ordering is a correctness choice
    // rather than a convenience. Releasing first put the card back in `todo`
    // while its turn was still running, so for the width of the kill the old
    // turn could still call kanban_done and write a stale result onto a card
    // another worker may already have claimed. `kanban_move`/`kanban_done`
    // authorise on agent + team + card id, so nothing downstream would reject
    // that write. Killing first closes the window without threading a per-turn
    // token through every kanban write path.
    //
    // What the kill actually guarantees, stated honestly: `tmux kill-session`
    // has delivered SIGHUP to the pane. It is NOT a wait-for-exit. A turn that
    // outlives SIGHUP long enough to complete an HTTP round-trip is an orphan,
    // and the boot-side reaper on the next manager start is what covers that —
    // the same backstop the claude -p children rely on.
    //
    // In a `finally` so a throwing kill cannot skip it. There is no server-side
    // backstop for these cards (both reapers share livenessDefersReap and
    // decline while the agent is healthy), so a dropped release is a card
    // orphaned forever. Awaited, bounded at 3s, never throws.
    await releaseInterruptedCardsOnDrain(drainReleaseSnapshot);
  }

  // Then gracefully stop persistent sessions (no-op if already killed)
  log('Stopping persistent sessions...');
  await stopAllSessionsAndWait(log, { timeoutMs: 4_000 });

  clearTimeout(shutdownTimer);
}

// ---------------------------------------------------------------------------
// Public API — called directly by the CLI command (no fork/IPC)
// ---------------------------------------------------------------------------

/**
 * Start the manager poll loop. Call this directly from the CLI command.
 * The process stays alive running the poll loop until stopManager() is called
 * or a signal is received.
 */
export function startManager(opts: { intervalMs: number; configDir: string }): void {
  // ENG-9129: FIRST statement, before any work. On agt-aws-1 (2026-08-18) the
  // manager printed "✔ Manager started (PID 560977)" and then emitted nothing
  // for 25 minutes, reaching the rehydrate below at 08:43. Because that
  // rehydrate was the first log, "blocked before startManager()" and "blocked
  // inside the state read" produced byte-identical evidence — silence — and the
  // root cause still has three candidates instead of one.
  //
  // This line is the bisector: if it appears at spawn and the rehydrate is
  // still minutes later, the block is below. If it never appears, the block is
  // above (the CLI command path, or startWatchdog's findOtherManagerPids
  // process scan). Keep it first; its whole value is that nothing precedes it.
  log(`[startup] begin (pid=${process.pid}, node=${process.version}, interval=${opts.intervalMs}ms)`);
  // ENG-9577: disable Claude Code's OWN autoupdater for this process and every
  // child it spawns. Set here, once, rather than at each launch site.
  //
  // The wrapper (persistent-claude.sh) covers agent sessions, but it is not the
  // only way a claude process starts on a host. There are at least four others,
  // each of which runs the same global binary and can therefore move it: the
  // pair/impersonate launch (claude-pair-runtime.ts), the runtime auth probe,
  // the `claude -p` conversation-eval runner, and the usage poller. Any one of
  // them self-updating drifts the host off `desired_claude_code_version`, and
  // the server-side pin has no way to notice — it converges on its own
  // schedule and the binary moves in between.
  //
  // Patching those four sites would fix the four that exist today and silently
  // miss the fifth. Every one of them inherits this process's environment, so
  // setting it on the manager covers the launch paths nobody has written yet.
  // The wrapper export stays as well: it is re-applied AFTER
  // `.env.integrations` is sourced, which is the one place an inherited value
  // can be overwritten (that file is sourced under `set -a`).
  //
  // Deliberately NOT conditional on the pin being set. Claude moving its own
  // binary underneath us is unwanted whether or not a version is pinned today
  // — an operator who has not yet chosen a version still should not have one
  // chosen for them mid-session.
  process.env['DISABLE_AUTOUPDATER'] = '1';
  config = opts;
  // ENG-4947: rehydrate AgentState[] from disk so dashboard-issued restart
  // acks (lastRestartProcessedAt) survive a manager process restart. We
  // only carry forward `agents`; pid/startedAt/pollCount stay fresh.
  // Errors are logged and ignored — first-run with no state file is the
  // normal case and a corrupt file shouldn't block startup.
  try {
    const stateFile = getStateFile();
    if (existsSync(stateFile)) {
      const raw = readFileSync(stateFile, 'utf-8');
      const parsed = JSON.parse(raw) as Partial<ManagerState>;
      if (Array.isArray(parsed.agents)) {
        state.agents = parsed.agents as AgentState[];
        log(`[startup] rehydrated ${state.agents.length} agent state(s) from ${stateFile}`);
      }
      // ENG-5441: rehydrate circuit-breaker trips so a manager restart
      // can't accidentally clear a tripped breaker. An operator who
      // intentionally stopped the loop must be the one to clear it.
      if (parsed.circuitBreakerTrips && typeof parsed.circuitBreakerTrips === 'object') {
        restartBreaker.hydrate(parsed.circuitBreakerTrips);
        const n = Object.keys(parsed.circuitBreakerTrips).length;
        if (n > 0) log(`[startup] rehydrated ${n} circuit-breaker trip(s) — agents will remain paused until manually resumed`);
      }
      // ENG-6088: rehydrate spent auto-resume credits so a manager restart
      // can't re-arm a re-tripped agent's auto-resume.
      if (parsed.circuitBreakerAutoResumes && typeof parsed.circuitBreakerAutoResumes === 'object') {
        autoResumeMarkers = hydrateAutoResumeMarkers(parsed.circuitBreakerAutoResumes);
        const n = autoResumeMarkers.size;
        if (n > 0) log(`[startup] rehydrated ${n} auto-resume marker(s) (ENG-6088)`);
      }
      // ENG-6692: rehydrate the forced-update dedup marker. The self-update
      // RESTARTS the manager, so this marker only does its job (fire once per
      // distinct "Update CLI now" timestamp) if it survives that restart — and
      // the heartbeat ack that clears hosts.update_requested_at goes out only on
      // the NEXT heartbeat, so the DB still carries the timestamp on the first
      // post-restart cycle. Without this, the fresh worker would re-consume it.
      if (
        typeof parsed.lastUpdateRequestProcessedAt === 'string' ||
        parsed.lastUpdateRequestProcessedAt === null
      ) {
        state.lastUpdateRequestProcessedAt = parsed.lastUpdateRequestProcessedAt;
        if (state.lastUpdateRequestProcessedAt) {
          log(`[startup] rehydrated update-request ack at ${state.lastUpdateRequestProcessedAt} (ENG-6692)`);
        }
      }
    }
  } catch (err) {
    log(`[startup] state rehydration failed (continuing with empty state): ${(err as Error).message}`);
  }
  // ENG-4658: the "manager process boundary" marker. Two purposes:
  //   1. Confirms the on-disk log destination is writable from this worker
  //      (the bug was a silent write to a stdio fd that pointed nowhere).
  //   2. Gives operators a per-worker segment boundary, so a long
  //      manager.log can be split by grepping for `[startup] worker pid=`.
  //
  // ENG-9129: this is NO LONGER the first log line on a fresh worker —
  // `[startup] begin` at the top of startManager() now precedes it, and THAT
  // is the line to use when bisecting a boot stall, because nothing runs
  // before it. Do not reintroduce a "first line" claim here: an operator
  // chasing a silent boot needs the earliest marker, and pointing them at
  // this one would skip the entire state-rehydration window (~25 min on
  // agt-aws-1, 2026-08-18).
  //
  // For a writability check either line serves, since a worker that cannot
  // write the file emits neither. If NEITHER appears shortly after a fresh
  // spawn, the worker can't write to manager.log and the operator should
  // check ~/.augmented permissions. If `begin` appears and this one does not,
  // the block is in the state rehydration above, not in logging.
  log(
    `[startup] worker pid=${process.pid} ppid=${process.ppid} ` +
      `node=${process.version} log=${join(homedir(), '.augmented', 'manager.log')}`,
  );
  deployMcpAssets();
  // ENG-4808: reap any channel-MCP processes that were left orphaned by
  // a prior manager crash or unclean exit. The per-restart reap inside
  // stopPersistentSession handles the in-process case; this catches the
  // pre-existing-orphan case where the manager process itself died with
  // children still attached. Idempotent: a clean restart with no orphans
  // is a no-op.
  reapOrphanChannelMcps({ log });
  // ENG-5865 — boot-side reaper for claude -p processes orphaned by a
  // previous manager death. Must complete BEFORE startPolling() so
  // processAgent can't observe (and re-pick) the same kanban card the
  // reaper is about to reset to backlog. CodeRabbit (PR #1631) caught
  // that the previous `void reapOrphanedClaudePids()` was fire-and-forget
  // and racing the very window it was meant to close. Sequenced now via
  // an IIFE: reaper → ensureHostFrameworkBinaries → startPolling. A
  // reaper failure logs and continues; the poll loop still must start
  // regardless (orphaned billing is better than a host that never polls).
  void (async (): Promise<void> => {
    try { await reapOrphanedClaudePids(); }
    catch (err) { log(`[drain] boot reaper failed: ${(err as Error).message}`); }
    // ENG-8022 - before agents respawn (and their channel MCPs reconnect and
    // post the "I was restarted" stranded-inbound notice), reconstruct each
    // rehydrated agent's last thread topic from its LOCAL transcripts and drop
    // a per-marker hint the MCP reads to make that notice context-carrying.
    // Synchronous fs work; non-throwing so a fault just yields the bare notice.
    try {
      refreshSlackRestartContextHints(
        state.agents.map((a) => a.codeName),
        { log },
      );
    } catch (err) {
      log(`[restart-context] boot refresh failed (continuing): ${(err as Error).message}`);
    }
    // Fetch the host's own framework from /host/exchange and ensure the
    // framework's binaries are installed. Runs async so startup isn't blocked
    // on a brew install — the binary is required only when agents are launched,
    // not for the polling loop itself. Failures are logged, never fatal.
    void ensureHostFrameworkBinaries();
    startPolling();
  })();
}

/**
 * ENG-5865 — boot-side reaper. On manager startup, read the host-wide
 * claude-pid file written by the previous instance and SIGKILL any
 * survivors (orphaned conversation-eval `claude -p` children, the sole
 * tracked spawn since ENG-6347/ENG-6851). The pid file is truncated at the
 * end regardless of success — leaving stale records would cause the next
 * reaper run to try to SIGKILL random pids.
 */
async function reapOrphanedClaudePids(): Promise<void> {
  const path = claudePidFilePath();
  const file = readClaudePidFile(path);
  if (file.spawns.length === 0) return;

  // CodeRabbit (PR #1631) — PID-reuse defense. Linux `/proc/<pid>/comm` is
  // the kernel's view of the process's binary name (15 bytes max); if a
  // recorded PID has been recycled to an unrelated process between our
  // SIGTERM and this probe, that line will not contain 'claude'. On
  // non-Linux (macOS dev hosts), no /proc → degrade to "trust the PID",
  // matching the pre-fix behaviour; macOS PID recycling is sufficiently
  // slower in practice that the SIGTERM→boot-reaper race is much tighter.
  const looksLikeClaude = (pid: number): boolean => {
    if (process.platform !== 'linux') return true;
    try {
      const comm = readFileSync(`/proc/${pid}/comm`, 'utf-8').trim().toLowerCase();
      return comm.includes('claude');
    } catch {
      // No /proc entry typically means the process is already gone — but
      // isAlive caught that case. A read failure here is unusual; safer
      // to skip the kill than to gamble on identity.
      return false;
    }
  };

  const decision = decideClaudeReaperActions(
    file,
    (pid) => {
      try { process.kill(pid, 0); return true; }
      catch { return false; }
    },
    looksLikeClaude,
  );

  for (const spawn of decision.toKill) {
    try { process.kill(spawn.pid, 'SIGKILL'); }
    catch (err) { log(`[drain] reaper SIGKILL pid=${spawn.pid} failed: ${(err as Error).message}`); }
  }

  // Log the skipped-PID-reuse cases so operators have a paper trail — the
  // SIGKILL was skipped intentionally because the live PID no longer looks
  // like a claude process (it was reused by something unrelated).
  for (const spawn of decision.pidReusedSkipped) {
    log(
      `[drain] reaper skipped pid=${spawn.pid} — PID reuse suspected (identity probe rejected)`,
    );
  }

  log(formatReaperLine({
    totalRecorded: file.spawns.length,
    killed: decision.toKill.length,
    alreadyDead: decision.alreadyDead.length,
    pidReusedSkipped: decision.pidReusedSkipped.length,
  }));

  // Truncate the file — anything left from the previous instance is now
  // either reaped or already dead. Live spawns from THIS instance will
  // re-register themselves via the onSpawn hook on the next execFile call.
  try { writeClaudePidFile(path, { version: 1, spawns: [] }); }
  catch (err) { log(`[drain] reaper file truncate failed: ${(err as Error).message}`); }
}

async function ensureHostFrameworkBinaries(): Promise<void> {
  const apiKey = getApiKey();
  if (!apiKey) {
    log('[framework-install] AGT_API_KEY not set — skipping host framework binary check');
    return;
  }
  try {
    const exchange = await exchangeApiKey(apiKey);
    if (!exchange.framework) {
      log('[framework-install] host has no framework set — skipping');
      return;
    }
    log(`[framework-install] host framework = ${exchange.framework}`);
    await ensureFrameworkBinary(exchange.framework);
  } catch (err) {
    log(`[framework-install] failed: ${(err as Error).message}`);
  }
}

/**
 * Deploy bundled MCP server files to ~/.augmented/_mcp/ if they don't exist
 * or are outdated. This ensures prod hosts have the Slack channel server
 * and augmented MCP server without manual file copying.
 */
/**
 * SIGTERM every running instance of the given channel MCP basenames so they
 * respawn with the newly-deployed bundle on next message.
 *
 * Channel MCPs are stdio children of each agent's Claude Code tmux session —
 * when they exit, Claude Code auto-respawns them via the entry in .mcp.json.
 * That respawn reads the freshly-written bundle from ~/.augmented/_mcp/, so
 * all we need is a death signal.
 *
 * Matches by command line containing the basename — `ps` output on macOS +
 * Linux both include the full path, so `/path/to/slack-channel.js` grep-hits
 * reliably. Not SIGKILL: the channel servers already handle SIGTERM cleanly
 * (ENG-4452) to drain in-flight Socket Mode events.
 */
function restartRunningChannelMcps(basenames: string[]): void {
  try {
    const psOutput = syncExecFile(
      'ps',
      ['-eo', 'pid=,command='],
      { encoding: 'utf-8', timeout: 5_000 },
    );
    const lines = psOutput.split('\n').filter((l) => l.trim());
    const selfPid = process.pid;
    let signalled = 0;
    for (const line of lines) {
      const match = line.match(/^\s*(\d+)\s+(.*)$/);
      if (!match) continue;
      const pid = Number(match[1]);
      const command = match[2]!;
      if (pid === selfPid) continue;
      // Only match JS basenames in the command path; env-var substrings like
      // SOMEPATH=/fake/slack-channel.js don't count because the basename
      // token is followed by whitespace or end-of-line when it's the actual
      // script being executed.
      const hit = basenames.find((b) => new RegExp(`/${b}\\.js(\\s|$)`).test(command));
      if (!hit) continue;
      try {
        process.kill(pid, 'SIGTERM');
        signalled++;
        log(`[manager]   sent SIGTERM to pid=${pid} (${hit}.js) — will respawn on next message`);
      } catch (err) {
        // ESRCH = already dead; others are "not our process" (EPERM). Ignore.
        const code = (err as NodeJS.ErrnoException).code;
        if (code && code !== 'ESRCH') {
          log(`[manager]   failed to signal pid=${pid}: ${code}`);
        }
      }
    }
    if (signalled === 0) {
      log(`[manager]   no running instances to restart (deploy still applies to future spawns)`);
    }
  } catch (err) {
    log(`[manager] restartRunningChannelMcps failed: ${(err as Error).message}`);
  }
}

function deployMcpAssets(): void {
  const targetDir = join(homedir(), '.augmented', '_mcp');
  mkdirSync(targetDir, { recursive: true });

  // Resolve MCP files from the CLI package's dist/mcp/ directory (ENG-5410).
  // The tarball is restricted to dist/ only, so the bundled MCP assets are
  // copied under dist/mcp/ by the build:mcp-assets script.
  const moduleDir = dirname(fileURLToPath(import.meta.url));
  // Dev: src/lib → ../../dist/mcp, Built: dist/lib → ../dist/mcp, npm global: dist/lib → ../dist/mcp
  let mcpSourceDir = '';
  let dir = moduleDir;
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, 'dist', 'mcp');
    if (existsSync(join(candidate, 'index.js'))) {
      mcpSourceDir = candidate;
      break;
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }

  if (!mcpSourceDir) {
    log('[manager] MCP assets not found in CLI package — skipping deployment');
    return;
  }

  // Track which channel basenames got a new bundle so we can signal running
  // instances to restart. Without this, already-spawned channel MCP children
  // (parented to each agent's Claude Code tmux session) keep running the
  // previous bundle from memory — `deployMcpAssets` only updates the file
  // on disk. That's exactly the scenario where a merged branch like ENG-4503
  // (Slack image auto-download) or ENG-4504 (Telegram image auto-download)
  // silently "doesn't work" until the operator manually restarts every agent.
  const changedBasenames: string[] = [];
  // ENG-9125: track copy outcomes so the completion log can tell the truth
  // instead of asserting success unconditionally. See the summary log below.
  const attemptedFiles: string[] = [];
  const failedFiles: string[] = [];
  const fileHash = (p: string): string | null => {
    try {
      if (!existsSync(p)) return null;
      return createHash('sha256').update(readFileSync(p)).digest('hex');
    } catch {
      return null;
    }
  };

  // Only these basenames are eligible for deploy-triggered SIGTERM. `index.js`
  // is deployed alongside the channels but must NEVER trigger the restart path
  // because countless unrelated Node processes on the host have an `index.js`
  // entrypoint — matching on `/index.js` would SIGTERM any of them. The list
  // below is the closed set of channel bundles whose process commands reliably
  // end in `/<basename>.js` with no collisions.
  const RESTARTABLE_CHANNEL_FILES = new Set([
    'slack-channel.js',
    'direct-chat-channel.js',
    'telegram-channel.js',
    // ENG-6019: teams-channel was provisioned into .mcp.json but never shipped
    // in the bundle — the file below has never existed on hosts until now.
    'teams-channel.js',
    'whatsapp-channel.js', // ENG-6812
  ]);

  for (const file of [
    'index.js',
    'slack-channel.js',
    'direct-chat-channel.js',
    'telegram-channel.js',
    'teams-channel.js', // ENG-6019
    'whatsapp-channel.js', // ENG-6812
    // ENG-6827: the one-time WhatsApp/Baileys QR-link tool. Deployed so an
    // operator can run it on the host, but NOT in RESTARTABLE_CHANNEL_FILES —
    // it's an interactive tool, not a per-session channel server.
    'whatsapp-link.js',
    // ENG-6195: the staff-only admin-debug broker, bundled with the CLI (never
    // published to npm). NOT in RESTARTABLE_CHANNEL_FILES — it's not a channel,
    // so it doesn't drive the channel-restart path; the mcp-presence-reaper
    // (re)starts it with the session, and a bundle update applies on the next
    // natural session restart.
    'augmented-admin.js',
    // ENG-7023: the per-org self-troubleshoot broker for the system_support
    // concierge agent, bundled with the CLI (never published to npm). Same
    // treatment as augmented-admin - NOT a channel, so not in
    // RESTARTABLE_CHANNEL_FILES; the reaper (re)starts it with the session and a
    // bundle update applies on the next natural session restart.
    'augmented-support.js',
    // ENG-8332: the help-knowledge-base broker, split out of augmented-support
    // so the docs can be granted without its ticket-filing and org-state
    // surface. Bundled with the CLI (never published to npm). Same treatment as
    // augmented-support - NOT a channel, so not in RESTARTABLE_CHANNEL_FILES;
    // the reaper (re)starts it with the session and a bundle update applies on
    // the next natural session restart.
    'augmented-help-kb.js',
    // ENG-6859: the remote OAuth MCP proxy (brand-ninja, granola). NOT in
    // RESTARTABLE_CHANNEL_FILES - it's not a channel; a bundle update applies on
    // the next natural session restart. It re-reads the token from the secrets
    // file per request, so it never needs restarting just to pick up a rotation.
    'remote-oauth-proxy.js',
    // ENG-7358: origami's dedicated stdio MCP server, bundled with the CLI
    // (never published to npm). Same treatment as augmented-admin - NOT a
    // channel, so not in RESTARTABLE_CHANNEL_FILES; the reaper (re)starts it
    // with the session and a bundle update applies on the next natural
    // session restart. It fetches its credential + identity per call, so it
    // never needs restarting to pick up a key rotation either.
    'origami.js',
    // ENG-7579: the Xero vendor MCP (our @integrity-labs/xero-mcp-server fork),
    // now bundled with the CLI instead of resolved at runtime via
    // `npx @integrity-labs/xero-mcp-server@latest`. The npx form required a
    // manual npm publish on every change and silently stranded the fleet on the
    // last-published version (CS-1440's attachment tool shipped in 0.0.21 but
    // npm @latest stayed at 0.0.20). Same treatment as origami - NOT a channel,
    // so not in RESTARTABLE_CHANNEL_FILES; the reaper (re)starts it with the
    // session and a bundle update applies on the next natural session restart.
    // In broker mode it fetches the freshest access_token per call, so it never
    // needs restarting to pick up a token rotation.
    'xero.js',
    // ENG-10386: the open-computer-use proxy. ENG-10131 wired it into the tsup
    // entry, the build:mcp-assets cp line and the publish paths filter — and
    // called those "all three delivery lists". This is a FOURTH: the CLI shipped
    // the bundle and the manager never materialised it, so every install sat at
    // `deferring spawn — MCP bundle(s) not yet on disk` forever. The readiness
    // gate was right to defer and nothing could ever end the wait, so the
    // integration was inert with no error anywhere the operator looks.
    // NOT in RESTARTABLE_CHANNEL_FILES: it is not a channel, so the reaper
    // (re)starts it with the session and a bundle update applies on the next
    // natural session restart.
    'computer-use-proxy.js',
  ]) {
    const src = join(mcpSourceDir, file);
    const dst = join(targetDir, file);
    if (!existsSync(src)) continue;
    attemptedFiles.push(file);
    const before = fileHash(dst);
    try {
      // ENG-9128: ATOMIC replace, not a truncating overwrite.
      //
      // `copyFileSync(src, dst)` truncates `dst` and then writes it, and this
      // directory is bind-mounted read-only into every agent container. An
      // agent spawning inside that window loads an empty or partial bundle,
      // its MCP servers fail to start, and mcp-presence-reaper restarts the
      // session — three of which trip the circuit breaker. That is the shape
      // of the 2026-08-19 clara / razorclaw-ea pauses.
      //
      // ENG-9125's readiness gate does not cover this: a half-written file
      // EXISTS, so `findMissingMcpBundles` reports nothing missing and the
      // spawn proceeds. That gate closed the absent case; this closes the
      // truncated one.
      atomicCopyFileSync(src, dst);
      const after = fileHash(dst);
      if (before !== after && RESTARTABLE_CHANNEL_FILES.has(file)) {
        // file is 'slack-channel.js' → basename 'slack-channel' (matches the
        // ChannelType enum in channel-sweep.ts)
        changedBasenames.push(file.replace(/\.js$/, ''));
      }
    } catch (err) {
      failedFiles.push(file);
      log(`[manager] Failed to deploy ${file}: ${(err as Error).message}`);
    }
  }
  // ENG-9125: this used to log unconditional success even when every single
  // copy above had thrown, because each failure is caught and swallowed
  // per-file. On agt-aws-1 (2026-08-18) that meant manager.log asserted "MCP
  // assets deployed" for a directory that was empty, while 13 of 16 agents were
  // being restart-looped into the circuit breaker for lack of those very
  // bundles. A success line that cannot fail is not evidence of anything, and
  // this one actively misled the incident response. Say what actually happened.
  if (failedFiles.length > 0) {
    log(
      `[manager] MCP asset deployment INCOMPLETE for ${targetDir} — ` +
        `${failedFiles.length}/${attemptedFiles.length} bundle(s) failed to copy: ${failedFiles.join(', ')}. ` +
        'Agents declaring these bundles will be held out of spawn (ENG-9125) until this succeeds. ' +
        'A read-only target directory is the usual cause.',
    );
  } else {
    log(`[manager] MCP assets deployed to ${targetDir} (${attemptedFiles.length} bundle(s))`);
  }

  if (changedBasenames.length > 0) {
    log(`[manager] Bundle(s) updated: ${changedBasenames.join(', ')} — signalling running instances to restart`);
    restartRunningChannelMcps(changedBasenames);
  }

  // Patch any agent .mcp.json files that still reference the npx fallback
  // (which was never published). Replace with the local node path.
  const localMcpPath = join(targetDir, 'index.js');
  try {
    const agentsDir = join(homedir(), '.augmented', 'agents');
    if (existsSync(agentsDir)) {
      for (const entry of readdirSync(agentsDir, { withFileTypes: true })) {
        if (!entry.isDirectory()) continue;
        // Check both provision and project dirs
        for (const subdir of ['provision', 'project']) {
          const mcpJsonPath = join(agentsDir, entry.name, subdir, '.mcp.json');
          try {
            const raw = readFileSync(mcpJsonPath, 'utf-8');
            if (!raw.includes('@integrity-labs/augmented-mcp')) continue;
            const mcpConfig = JSON.parse(raw) as { mcpServers?: Record<string, Record<string, unknown>> };
            const augServer = mcpConfig.mcpServers?.['augmented'];
            if (!augServer) continue;
            augServer.command = 'node';
            augServer.args = [localMcpPath];
            writeFileSync(mcpJsonPath, JSON.stringify(mcpConfig, null, 2));
            log(`[manager] Patched ${entry.name}/${subdir}/.mcp.json: npx → node`);
          } catch {
            // File doesn't exist or isn't valid JSON — skip
          }
        }
      }
    }
  } catch (err) {
    log(`[manager] Failed to patch agent .mcp.json files: ${(err as Error).message}`);
  }
}

/**
 * Stop the manager poll loop gracefully.
 */
export async function stopManager(): Promise<void> {
  await stopPolling();
}

// Graceful shutdown on signals
let shuttingDown = false;
for (const sig of ['SIGTERM', 'SIGINT'] as const) {
  process.on(sig, () => {
    if (shuttingDown) {
      log(`Received ${sig} again during shutdown — ignoring (cleanup in progress)`);
      return;
    }
    shuttingDown = true;
    log(`Received ${sig}, shutting down`);
    void stopPolling().then(() => {
      process.exit(0);
    });
  });
}

// If parent disconnects, exit
process.on('disconnect', () => {
  log('Parent disconnected, exiting');
  void stopPolling().then(() => {
    process.exit(0);
  });
});
